const { useState, useRef, useCallback, useEffect, useMemo } = React;

const SUPABASE_URL = 'https://ymwoabgesjqrojurdxmv.supabase.co';
const SUPABASE_KEY = 'sb_publishable_8z6jTCr6BPKmltRnNvEVzA_do7BmXKe';
const sb = (window.supabase && window.supabase.createClient) ? window.supabase.createClient(SUPABASE_URL, SUPABASE_KEY) : null;

const DEFAULT_SETTINGS = { format:'Superflex', tep:0.5, ppr:1.0, passTd:5, ppc:0 };
const FORMAT_CHOICES=['Superflex'];
const TEP_CHOICES=[0.5,0.75,1.0], PPR_CHOICES=[1.0], PTD_CHOICES=[4,5,6], PPC_CHOICES=[0];

const sameSettings = (a,b)=> a && b && (a.format||'Superflex')===(b.format||'Superflex') && a.tep===b.tep && a.ppr===b.ppr && a.passTd===b.passTd && a.ppc===b.ppc;

// Per-combo rankings rows in pfk_rankings:
//   - settings = {format, tep, ppr, passTd, ppc}                                   → PUBLISHED (prod)
//   - settings = {format, tep, ppr, passTd, ppc, kind: 'rankings_dev_draft'}       → DEV DRAFT (dev URL only)
//   - settings = {kind: 'rookie_model'} | {kind: 'rookie_model_draft'}             → model rows (separate)
const RANKINGS_DEV_KIND = 'rankings_dev_draft';
const fetchOfficialRankings = async (wanted) => {
  if(!sb) return null;
  try{
    const { data, error } = await sb.from('pfk_rankings').select('*').order('updated_at',{ascending:false});
    if(error||!data?.length) return null;
    // Only return PUBLISHED rows (no kind sentinel) and ONLY ones that exactly match the requested
    // combo. No fallback — non-matching combos must return null so derivation logic can kick in.
    if(wanted) return data.find(r=>!r.settings?.kind && sameSettings(r.settings,wanted)) || null;
    // Wanted not specified: return any published row.
    return data.find(r=>!r.settings?.kind) || null;
  }catch{ return null; }
};
// Fetch the dev-draft rankings row for a given combo (used on dev URL only).
const fetchDevDraftRankings = async (wanted) => {
  if(!sb) return null;
  try{
    const { data } = await sb.from('pfk_rankings').select('*').order('updated_at',{ascending:false});
    if(!data?.length) return null;
    return data.find(r=>r.settings?.kind===RANKINGS_DEV_KIND && sameSettings(r.settings,wanted)) || null;
  }catch{ return null; }
};
// Save the dev-draft rankings row for a combo (overwrites the existing draft for that combo).
const saveDevDraftRankings = async (items, settings) => {
  if(!sb) return { error:'Supabase not loaded' };
  const { data:userData } = await sb.auth.getUser();
  const email = userData?.user?.email || 'PFK Staff';
  const sigSettings = { ...settings, kind: RANKINGS_DEV_KIND };
  const { data:existing } = await sb.from('pfk_rankings').select('id,settings').order('updated_at',{ascending:false});
  const match = (existing||[]).find(r=>r.settings?.kind===RANKINGS_DEV_KIND && sameSettings(r.settings,settings));
  if(match){
    const { error } = await sb.from('pfk_rankings').update({ data:items, updated_by:email, updated_at:new Date().toISOString(), settings:sigSettings }).eq('id',match.id);
    return { error };
  }
  const { error } = await sb.from('pfk_rankings').insert({ data:items, updated_by:email, settings:sigSettings });
  return { error };
};
// Publish current items to PROD for the given combo (writes the no-kind row that prod reads).
const publishToProdRankings = async (items, settings) => {
  // settings already passed without 'kind'; reuse publishOfficialRankings which writes the no-kind row
  return publishOfficialRankings(items, settings);
};
// Dev URL gate: only Evan's email can access dev preview.
const EVAN_EMAIL = 'ejohnson2621@gmail.com';

const publishOfficialRankings = async (items, settings) => {
  if(!sb) return { error: 'Supabase not loaded' };
  const { data:userData } = await sb.auth.getUser();
  const email = userData?.user?.email || 'PFK Staff';
  const { data:existing } = await sb.from('pfk_rankings').select('id,settings').order('updated_at',{ascending:false});
  // Match the published row only (no kind sentinel) — without this, sameSettings could pick
  // an archived or per-combo row and "promote" it back to master, leaving duplicate masters.
  const match = (existing||[]).find(r=>!r.settings?.kind && sameSettings(r.settings,settings));
  if(match){
    const { error } = await sb.from('pfk_rankings').update({ data:items, updated_by:email, updated_at:new Date().toISOString(), settings }).eq('id',match.id);
    return { error };
  }
  const { error } = await sb.from('pfk_rankings').insert({ data:items, updated_by:email, settings });
  return { error };
};
// Rookie model storage — TWO sentinel rows in pfk_rankings:
//   kind='rookie_model'        → published (read by PROD public site, written by PUBLISH MODEL button)
//   kind='rookie_model_draft'  → draft     (read by DEV public site & admin, written by every admin auto-save)
const MODEL_SETTINGS_PUB   = { kind:'rookie_model' };
const MODEL_SETTINGS_DRAFT = { kind:'rookie_model_draft' };
const isDevHost = () => typeof window!=='undefined' && /^(dev\.|localhost|127\.|192\.168\.)/.test(window.location.hostname);
const _kind = s => (s && typeof s==='object') ? s.kind : null;
// Public reader: route by hostname. Dev reads draft (fallback to published if draft missing); prod reads published only.
const fetchModelData = async () => {
  if(!sb) return null;
  try{
    const { data } = await sb.from('pfk_rankings').select('*').order('updated_at',{ascending:false});
    if(!data?.length) return null;
    const pub   = data.find(r=>_kind(r.settings)==='rookie_model');
    const draft = data.find(r=>_kind(r.settings)==='rookie_model_draft');
    return isDevHost() ? (draft || pub) : pub;
  }catch{ return null; }
};
// Admin reader: always draft, fallback to published on first load.
const fetchModelDraft = async () => {
  if(!sb) return null;
  try{
    const { data } = await sb.from('pfk_rankings').select('*').order('updated_at',{ascending:false});
    if(!data?.length) return null;
    return data.find(r=>_kind(r.settings)==='rookie_model_draft') || data.find(r=>_kind(r.settings)==='rookie_model') || null;
  }catch{ return null; }
};
// Admin auto-save: writes the DRAFT row only.
const saveModelData = async (items) => {
  if(!sb) return { error:'Supabase not loaded' };
  const { data:userData } = await sb.auth.getUser();
  const email = userData?.user?.email || 'PFK Staff';
  const { data:existing } = await sb.from('pfk_rankings').select('id,settings').order('updated_at',{ascending:false});
  const match = (existing||[]).find(r=>_kind(r.settings)==='rookie_model_draft');
  if(match){
    const { error } = await sb.from('pfk_rankings').update({ data:items, updated_by:email, updated_at:new Date().toISOString(), settings:MODEL_SETTINGS_DRAFT }).eq('id',match.id);
    return { error };
  }
  const { error } = await sb.from('pfk_rankings').insert({ data:items, updated_by:email, settings:MODEL_SETTINGS_DRAFT });
  return { error };
};
// PUBLISH MODEL button: copies the current draft items into the PUBLISHED row.
const publishModel = async (items) => {
  if(!sb) return { error:'Supabase not loaded' };
  const { data:userData } = await sb.auth.getUser();
  const email = userData?.user?.email || 'PFK Staff';
  const { data:existing } = await sb.from('pfk_rankings').select('id,settings').order('updated_at',{ascending:false});
  const match = (existing||[]).find(r=>_kind(r.settings)==='rookie_model');
  if(match){
    const { error } = await sb.from('pfk_rankings').update({ data:items, updated_by:email, updated_at:new Date().toISOString(), settings:MODEL_SETTINGS_PUB }).eq('id',match.id);
    return { error };
  }
  const { error } = await sb.from('pfk_rankings').insert({ data:items, updated_by:email, settings:MODEL_SETTINGS_PUB });
  return { error };
};

function SettingsToggleBar({value,onChange,compact}){
  const Group = ({label,choices,suffix,current,field})=>(
    <div style={{display:'flex',flexDirection:'column',gap:3,alignItems:'center'}}>
      <div style={{fontSize:11,color:'var(--text-secondary)',letterSpacing:1,fontWeight:700,textAlign:'center'}}>{label}</div>
      <div style={{display:'flex',gap:2,background:'var(--bg-elevated)',border:'1px solid var(--bg-pill)',borderRadius:6,padding:2}}>
        {choices.map(c=>(
          <button key={c} onClick={()=>onChange({...value,[field]:c})} style={{padding:compact?'3px 8px':'5px 10px',background:current===c?'var(--accent-gold)':'transparent',color:current===c?'#000':'var(--text-muted)',border:'none',borderRadius:4,cursor:'pointer',fontSize:13,fontWeight:700}}>{c}{suffix}</button>
        ))}
      </div>
    </div>
  );
  return (
    <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
      {FORMAT_CHOICES.length>1 && <Group label="FORMAT" choices={FORMAT_CHOICES} suffix="" current={value.format||'Superflex'} field="format"/>}
      <Group label="TEP" choices={TEP_CHOICES} suffix="" current={value.tep} field="tep"/>
      <Group label="PASS TD" choices={PTD_CHOICES} suffix="pt" current={value.passTd} field="passTd"/>
    </div>
  );
}

const TIER_COLORS = ["var(--accent-gold)","var(--accent-gold)","#FFAA00","#E09000","var(--warning-strong)","#c2840a","#a37820","#8B6914","#a3a3a3","#7c8896"];
const POS_COLORS = { WR:"var(--pos-wr)", RB:"var(--pos-rb)", TE:"var(--pos-te)", QB:"var(--pos-qb)" };

// Player headshot — small circular avatar next to player names across the
// dispersal draft, Trade Finder, and Rookie Ranks.
//
// Source priority:
//   1. props.src      — direct URL (used for rookie prospect headshots from
//                       NFL.com, already in prospects-2026.json)
//   2. props.sleeperId — proxied through /api/player/{id} so html2canvas
//                       captures roster/share screenshots without CORS taint
//   3. Fallback       — position-colored initial circle (first letter of name)
//
// `loading="lazy"` so a dispersal pool with 200 players doesn't spam the
// browser with image requests on page load; the browser fetches each one
// only as it scrolls into view.
function PlayerHeadshot({ src, sleeperId, name, pos, size = 28, style }) {
  const [errored, setErrored] = useState(false);
  const initial = (name || '?').trim().charAt(0).toUpperCase();
  const posColor = POS_COLORS[(pos || '').toUpperCase()] || 'var(--pos-other, var(--border-strong))';
  const fallback = (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      background: `color-mix(in srgb, ${posColor} 18%, var(--bg-elevated))`,
      color: posColor,
      border: `1px solid color-mix(in srgb, ${posColor} 40%, transparent)`,
      fontSize: Math.max(10, Math.floor(size * 0.45)), fontWeight: 900,
      letterSpacing: 0,
      ...style,
    }}>{initial}</div>
  );
  const url = src || (sleeperId ? `/api/player/${encodeURIComponent(String(sleeperId))}` : null);
  if (!url || errored) return fallback;
  return (
    <img
      src={url}
      alt=""
      loading="lazy"
      onError={() => setErrored(true)}
      style={{
        width: size, height: size, borderRadius: '50%', objectFit: 'cover',
        flexShrink: 0,
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        ...style,
      }}
    />
  );
}
const INITIAL_TIERS = ["Untouchable","X-Factor","Super-Star","Star","Starter","Good Depth","Bench Player","Roster Clogger","Taxi Squad","Waivers"];
// Dynamic per-class auto-tier algorithm.
// PFK_TIER_TARGETS[i] = the 0-indexed position where tier (i+2) begins in a sorted-desc PFK list.
// PFK_TIER_WINDOWS[i] = how far to look around that target for the largest natural PFK gap to snap to.
// Top tiers are precise (small windows, small target sizes). Bottom tiers are broader.
// At call time: scoresDesc is the array of PFK scores sorted desc, returns 9 boundary positions
// for 10 tiers. Tier names come from src in order.
const PFK_TIER_TARGETS = [1, 2, 4, 7, 14, 22, 33, 47, 62];
const PFK_TIER_WINDOWS = [0, 1, 1, 1, 2, 2, 2, 2, 2];
const autoTierBoundaries = (scoresDesc, tierCount = 10) => {
  const n = scoresDesc.length;
  if(n === 0) return [];
  const numBoundaries = Math.min(tierCount - 1, PFK_TIER_TARGETS.length);
  const out = [];
  let prev = 0;
  for(let i = 0; i < numBoundaries; i++){
    const target = Math.min(PFK_TIER_TARGETS[i], n);
    const w = PFK_TIER_WINDOWS[i];
    if(w === 0){ out.push(target); prev = target; continue; }
    const lo = Math.max(prev + 1, target - w);
    const hi = Math.min(target + w, n - 1);
    if(hi < lo){ out.push(target); prev = target; continue; }
    let bestPos = lo, bestGap = -Infinity;
    for(let pos = lo; pos <= hi; pos++){
      const gap = (scoresDesc[pos - 1] ?? 0) - (scoresDesc[pos] ?? 0);
      if(gap > bestGap){ bestGap = gap; bestPos = pos; }
    }
    out.push(bestPos);
    prev = bestPos;
  }
  return out;
};

const buildInitialList = () => {
  const players = [
    {id:"p1",name:"Jeremiyah Love",pos:"RB",age:21.3,college:"Notre Dame",nflTeam:"ARI",pick:"1.03"},
    {id:"p2",name:"Fernando Mendoza",pos:"QB",age:22.9,college:"Indiana",nflTeam:"LV",pick:"1.01"},
    {id:"p3",name:"Carnell Tate",pos:"WR",age:21.6,college:"Ohio State",nflTeam:"TEN",pick:"1.04"},
    {id:"p4",name:"Makai Lemon",pos:"WR",age:22.3,college:"USC",nflTeam:"PHI",pick:"1.20"},
    {id:"p5",name:"KC Concepcion",pos:"WR",age:21.9,college:"Texas A&M",nflTeam:"CLE",pick:"1.24"},
    {id:"p6",name:"Kenyon Sadiq",pos:"TE",age:21.5,college:"Oregon",nflTeam:"NYJ",pick:"1.16"},
    {id:"p7",name:"Denzel Boston",pos:"WR",age:22.7,college:"Washington",nflTeam:"NYG"},
    {id:"p8",name:"Jordyn Tyson",pos:"WR",age:22.1,college:"Arizona State",nflTeam:"NO",pick:"1.08"},
    {id:"p9",name:"Omar Cooper Jr.",pos:"WR",age:22.7,college:"Indiana",nflTeam:"NYJ",pick:"1.30"},
    {id:"p10",name:"Jadarian Price",pos:"RB",age:22.9,college:"Notre Dame",nflTeam:"SEA",pick:"1.32"},
    {id:"p11",name:"Zachariah Branch",pos:"WR",age:22.4,college:"Georgia",nflTeam:"ATL"},
    {id:"p12",name:"Jonah Coleman",pos:"RB",age:22.9,college:"Washington",nflTeam:"DEN"},
    {id:"p13",name:"Kaytron Allen",pos:"RB",age:23.7,college:"Penn State",nflTeam:"IND"},
    {id:"p14",name:"Ty Simpson",pos:"QB",age:23.0,college:"Alabama",nflTeam:"LAR",pick:"1.13"},
    {id:"p15",name:"Elijah Sarratt",pos:"WR",age:23.3,college:"Indiana",nflTeam:"IND"},
    {id:"p16",name:"Michael Trigg",pos:"TE",age:24.2,college:"Baylor",nflTeam:"MIN"},
    {id:"p17",name:"Eli Stowers",pos:"TE",age:23.3,college:"Vanderbilt",nflTeam:"ATL"},
    {id:"p18",name:"Max Klare",pos:"TE",age:23.2,college:"Ohio State",nflTeam:"PIT"},
    {id:"p19",name:"Emmett Johnson",pos:"RB",age:22.9,college:"Nebraska",nflTeam:"NO"},
    {id:"p20",name:"Chris Bell",pos:"WR",age:22.3,college:"Louisville",nflTeam:"MIA"},
    {id:"p21",name:"Chris Brazzell II",pos:"WR",age:22.3,college:"Tennessee",nflTeam:"CAR"},
    {id:"p22",name:"Nicholas Singleton",pos:"RB",age:22.7,college:"Penn State",nflTeam:"PHI"},
    {id:"p23",name:"Antonio Williams",pos:"WR",age:22.1,college:"Clemson",nflTeam:"WSH"},
    {id:"p24",name:"Germie Bernard",pos:"WR",age:22.8,college:"Alabama",nflTeam:"SEA"},
    {id:"p25",name:"Garrett Nussmeier",pos:"QB",age:24.5,college:"LSU",nflTeam:"DET"},
    {id:"p26",name:"Carson Beck",pos:"QB",age:23.8,college:"Miami",nflTeam:"ARI"},
    {id:"p27",name:"Eli Heidenreich",pos:"RB",age:22.5,college:"Navy",nflTeam:"UDFA"},
    {id:"p28",name:"Malachi Fields",pos:"WR",age:22.3,college:"Notre Dame",nflTeam:"NYG"},
    {id:"p29",name:"Drew Allar",pos:"QB",age:null,college:"Penn State",nflTeam:"PIT"},
    {id:"p30",name:"Justin Joly",pos:"TE",age:23.5,college:"NC State",nflTeam:"TBD"},
    {id:"p31",name:"Eric McCalister",pos:"WR",age:23,college:"TCU",nflTeam:"TBD"},
    {id:"p32",name:"Bryce Lance",pos:"WR",age:null,college:"N. Dakota St",nflTeam:"TBD"},
    {id:"p33",name:"CJ Daniels",pos:"WR",age:24.3,college:"Miami",nflTeam:"TBD"},
    {id:"p34",name:"Ja'Kobi Lane",pos:"WR",age:22.1,college:"USC",nflTeam:"BAL"},
    {id:"p35",name:"Ted Hurst",pos:"WR",age:null,college:"Georgia State",nflTeam:"TB"},
    {id:"p36",name:"Tanner Koziol",pos:"TE",age:null,college:"Houston",nflTeam:"TBD"},
    {id:"p37",name:"Kevin Coleman Jr.",pos:"WR",age:null,college:"Missouri",nflTeam:"TBD"},
    {id:"p38",name:"Seth McGowan",pos:"RB",age:null,college:"Kentucky",nflTeam:"TBD"},
    {id:"p39",name:"Barion Brown",pos:"WR",age:null,college:"LSU",nflTeam:"TBD"},
    {id:"p40",name:"Jadyn Ott",pos:"RB",age:null,college:"Oklahoma",nflTeam:"TBD"},
    {id:"p41",name:"Brenen Thompson",pos:"WR",age:23.1,college:"Miss St.",nflTeam:"TBD"},
    {id:"p42",name:"Rahsul Faison",pos:"RB",age:null,college:"S. Carolina",nflTeam:"TBD"},
    {id:"p43",name:"Jack Endries",pos:"TE",age:null,college:"Texas",nflTeam:"TBD"},
    {id:"p44",name:"Josh Cameron",pos:"WR",age:null,college:"Baylor",nflTeam:"TBD"},
    {id:"p45",name:"Skyler Bell",pos:"WR",age:24.2,college:"UCONN",nflTeam:"TBD"},
    {id:"p46",name:"J'Mari Taylor",pos:"RB",age:null,college:"Virginia",nflTeam:"TBD"},
    {id:"p47",name:"Demond Claiborne",pos:"RB",age:22.9,college:"Wake Forest",nflTeam:"TBD"},
    {id:"p48",name:"Desmond Reid",pos:"RB",age:null,college:"Pittsburgh",nflTeam:"TBD"},
    {id:"p49",name:"Oscar Delp",pos:"TE",age:null,college:"Georgia",nflTeam:"NO"},
    {id:"p50",name:"Adam Randall",pos:"RB",age:22.1,college:"Clemson",nflTeam:"TBD"},
    {id:"p51",name:"Deion Burks",pos:"WR",age:23.7,college:"Oklahoma",nflTeam:"TBD"},
    {id:"p52",name:"Le'Veon Moss",pos:"RB",age:null,college:"Texas A&M",nflTeam:"TBD"},
    {id:"p53",name:"Mike Washington",pos:"RB",age:24.5,college:"Arkansas",nflTeam:"TBD"},
    {id:"p54",name:"Jamarion Miller",pos:"RB",age:22.4,college:"Alabama",nflTeam:"TBD"},
    {id:"p55",name:"De'Zhaun Stribling",pos:"WR",age:null,college:"Mississippi",nflTeam:"SF"},
    {id:"p56",name:"Nate Boerkircher",pos:"TE",age:null,college:"Texas A&M",nflTeam:"JAC"},
    {id:"p57",name:"Marlin Klein",pos:"TE",age:null,college:"Michigan",nflTeam:"HOU"},
    {id:"p58",name:"Sam Roush",pos:"TE",age:null,college:"Stanford",nflTeam:"CHI"},
    {id:"p59",name:"Caleb Douglas",pos:"WR",age:null,college:"Texas Tech",nflTeam:"MIA"},
    {id:"p60",name:"Will Kacmarek",pos:"TE",age:null,college:"Ohio State",nflTeam:"MIA"},
    {id:"p61",name:"Zavion Thomas",pos:"WR",age:null,college:"LSU",nflTeam:"CHI"},
    {id:"p62",name:"Kaelon Black",pos:"RB",age:null,college:"Indiana",nflTeam:"SF"},
    {id:"p63",name:"Eli Raridon",pos:"TE",age:null,college:"Notre Dame",nflTeam:"NE"},
  ];
  const tierBreaks = {0:0,1:1,2:2,3:3,5:4,9:5,11:6,15:7,22:8,36:9};
  const list = [];
  players.forEach((p,i) => {
    if(tierBreaks[i]!==undefined) list.push({type:"tier",id:"t_"+INITIAL_TIERS[tierBreaks[i]],name:INITIAL_TIERS[tierBreaks[i]]});
    list.push({type:"player",...p});
  });
  return list;
};

const slotLabel = i => i>=48?"FAAB":((Math.floor(i/12)+1)+"."+String((i%12)+1).padStart(2,"0"));
const getTierColor = (name,list) => { const t=list.filter(x=>x.type==="tier"); const i=t.findIndex(x=>x.name===name); return TIER_COLORS[i%TIER_COLORS.length]||"var(--border-outline)"; };
const getPlayerTier = (pid,list) => { let cur="Unranked"; for(const x of list){if(x.type==="tier")cur=x.name;if(x.type==="player"&&x.id===pid)return cur;} return cur; };
const getPlayerIndex = (id,l) => { let c=0; for(const x of l){if(x.type==="player"){if(x.id===id)return c;c++;}} return c; };
const PFK_LIST = buildInitialList();

const loadStorage = (key, fallback) => {
  try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fallback; } catch { return fallback; }
};

const classifyTeam = (dPct, rPct) => {
  if (dPct >= 67 && rPct >= 67)  return {label:'Dynasty',        emoji:'👑', color:'var(--accent-gold-text)', desc:'Dominant now and built to stay that way'};
  if (rPct >= 67 && dPct < 40)   return {label:'Aging Contender', emoji:'⏰', color:'var(--danger)', desc:'Championship window is closing fast'};
  if (dPct >= 67 && rPct < 40)   return {label:'Future Dynasty',  emoji:'🔮', color:'var(--info)', desc:'Young and loaded — not competing yet'};
  if (dPct >= 50 && rPct >= 50)  return {label:'Contender',       emoji:'💪', color:'var(--accent-gold-text)', desc:'Real title threat with a solid future'};
  if (rPct >= 50)                return {label:'Win-Now',          emoji:'🏈', color:'var(--warning)', desc:'Competitive now, uncertain future'};
  if (dPct >= 40)                return {label:'Rebuilding',       emoji:'🔨', color:'var(--warning-strong)', desc:'Accumulating assets for the future'};
  return                          {label:'Full Rebuild',           emoji:'🏗️', color:'#94a3b8', desc:'Tearing it down to build it right'};
};

// Team Analyzer thresholds — see project_team_analyzer_spec memory for derivation
const AGE_RISK_AGE = { RB: 29, WR: 29, TE: 27, QB: 31 };
const AGE_TIERS = [
  {max:25.0, label:'Young',  desc:'Dynasty upside',   color:'var(--info)'},
  {max:26.5, label:'Prime',  desc:'Prime window',     color:'var(--success)'},
  {max:27.5, label:'Mature', desc:'Contender window', color:'var(--accent-gold-text)'},
  {max:28.5, label:'Aging',  desc:'Window closing',   color:'var(--warning)'},
  {max:999,  label:'Old',    desc:'Rebuild risk',     color:'var(--danger)'},
];
const ageTier = a => AGE_TIERS.find(t=>a<=t.max) || AGE_TIERS[AGE_TIERS.length-1];
// 2026 NFL Draft round 1 overlay (skill positions only). Keyed by normalized name.
// Sourced from nfl.com draft tracker; updated 2026-04-23.
const DRAFT_2026 = {
  'fernandomendoza': {team:'LV',  pick:'1.01'},
  'jeremiyahlove':   {team:'ARI', pick:'1.03'},
  'carnelltate':     {team:'TEN', pick:'1.04'},
  'jordyntyson':     {team:'NO',  pick:'1.08'},
  'tysimpson':       {team:'LAR', pick:'1.13'},
  'kenyonsadiq':     {team:'NYJ', pick:'1.16'},
  'makailemon':      {team:'PHI', pick:'1.20'},
  'kcconcepcion':    {team:'CLE', pick:'1.24'},
  'omarcooperjr':    {team:'NYJ', pick:'1.30'},
  'jadarianprice':   {team:'SEA', pick:'1.32'},
  'dezhaunstribling':{team:'SF',  pick:'2.01'},
  'denzelboston':    {team:'CLE', pick:'2.07'},
  'germiebernard':   {team:'PIT', pick:'2.15'},
  'elistowers':      {team:'PHI', pick:'2.22'},
  'nateboerkircher': {team:'JAC', pick:'2.24'},
  'marlinklein':     {team:'HOU', pick:'2.27'},
  'maxklare':        {team:'LAR', pick:'2.29'},
  'carsonbeck':      {team:'ARI', pick:'3.01'},
  'samroush':        {team:'CHI', pick:'3.05'},
  'antoniowilliams': {team:'WSH', pick:'3.07'},
  'oscardelp':       {team:'NO',  pick:'3.09'},
  'malachifields':   {team:'NYG', pick:'3.10'},
  'calebdouglas':    {team:'MIA', pick:'3.11'},
  'drewallar':       {team:'PIT', pick:'3.12'},
  'zachariahbranch': {team:'ATL', pick:'3.15'},
  'jakobilane':      {team:'BAL', pick:'3.16'},
  'chrisbrazzellii': {team:'CAR', pick:'3.19'},
  'tedhurst':        {team:'TB',  pick:'3.20'},
  'willkacmarek':    {team:'MIA', pick:'3.23'},
  'zavionthomas':    {team:'CHI', pick:'3.25'},
  'kaelonblack':     {team:'SF',  pick:'3.26'},
  'chrisbell':       {team:'MIA', pick:'3.30'},
  'eliraridon':      {team:'NE',  pick:'3.31'},
  // Round 4 (NFL.com 2026 draft tracker)
  'brenenthompson':  {team:'LAC', pick:'4.05'},
  'jonahcoleman':    {team:'DEN', pick:'4.08'},
  'cadeklubnik':     {team:'NYJ', pick:'4.10'},
  'elijahsarratt':   {team:'BAL', pick:'4.15'},
  'kadenwetjen':     {team:'PIT', pick:'4.21'},
  'mikewashington':  {team:'LV',  pick:'4.22'},
  'skylerbell':      {team:'BUF', pick:'4.25'},
  'matthewhibner':   {team:'BAL', pick:'4.33'},
  'brycelance':      {team:'NO',  pick:'4.36'},
  'colbieyoung':     {team:'CIN', pick:'4.40'},
  // Round 5 (final results — Denver traded up to 152 for Joly, sending picks 170 and 182 to Cleveland)
  'reggievirgil':    {team:'ARI', pick:'5.03'},
  'justinjoly':      {team:'DEN', pick:'5.12'},
  'emmettjohnson':   {team:'KC',  pick:'5.21'},
  'tannerkoziol':    {team:'JAC', pick:'5.24'},
  'nicholassingleton':{team:'TEN',pick:'5.25'},
  'kendricklaw':     {team:'DET', pick:'5.28'},
  'joeroyer':        {team:'CLE', pick:'5.30'},
  'adamrandall':     {team:'BAL', pick:'5.34'},
  'cyrusallen':      {team:'KC',  pick:'5.36'},
  'kevincolemanjr':  {team:'MIA', pick:'5.37'},
  'colepayton':      {team:'PHI', pick:'5.38'},
  // Round 6
  'taylengreen':     {team:'CLE', pick:'6.01'},
  'kaytronallen':    {team:'WSH', pick:'6.06'},
  'barionbrown':     {team:'NO',  pick:'6.09'},
  'joshcameron':     {team:'JAC', pick:'6.10'},
  'malikbenson':     {team:'LV',  pick:'6.14'},
  'cjdaniels':       {team:'LAR', pick:'6.16'},
  'demondclaiborne': {team:'MIN', pick:'6.17'},
  'emmanuelhendersonjr':{team:'SEA',pick:'6.18'},
  // Round 7
  'jackendries':     {team:'CIN', pick:'7.05'},
  'athankaliakmanis':{team:'WSH', pick:'7.07'},
  'eliheidenreich':  {team:'PIT', pick:'7.14'},
  'behrenmorton':    {team:'NE',  pick:'7.18'},
  'sethmcgowan':     {team:'IND', pick:'7.21'},
  'jammiller':       {team:'NE',  pick:'7.29'},
  'jamarionmiller':  {team:'NE',  pick:'7.29'},
  'carsenryan':      {team:'CLE', pick:'7.32'},
  'garrettnussmeier':{team:'KC',  pick:'7.33'},
  'deionburks':      {team:'IND', pick:'7.38'},
  'dallenbentley':   {team:'DEN', pick:'7.40'},
  // 2026 UDFA signings (CBS Sports tracker + team-specific reports)
  'michaeltrigg':         {team:'DAL', pick:'UDFA'},
  'johnmichaelgyllenborg':{team:'KC',  pick:'UDFA'},
  'romanhemby':           {team:'LV',  pick:'UDFA'},
  'jeffcaldwell':         {team:'KC',  pick:'UDFA'},
  'desmondreid':          {team:'BUF', pick:'UDFA'},
  'jadynott':             {team:'KC',  pick:'UDFA'},
  'aaronanderson':        {team:'CLE', pick:'UDFA'},
  'deamontetrayanum':     {team:'PHI', pick:'UDFA'},
  'jmaritaylor':          {team:'JAC', pick:'UDFA'},
  'terionstewart':        {team:'KC',  pick:'UDFA'},
  'roberthenryjr':        {team:'WSH', pick:'UDFA'},
  'leveonmoss':           {team:'MIA', pick:'UDFA'},
};
const normDraftName = s=>(s||'').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g,'').replace(/[^a-z0-9]/g,'');
// Zoltan's 2026 dynasty rookie rankings + ADP. Static snapshot dated 2026-04-26.
// Used by the admin "PFK vs Zolty" tab to compare Evan's PFK rankings against Zolty's
// rankings + the consensus ADP that Zolty publishes alongside.
const ZOLTY_2026 = [{"name":"Jeremiyah Love","pos":"RB","rank":1,"adp":1},{"name":"Carnell Tate","pos":"WR","rank":2,"adp":2},{"name":"Makai Lemon","pos":"WR","rank":3,"adp":5},{"name":"KC Concepcion","pos":"WR","rank":4,"adp":8},{"name":"Fernando Mendoza","pos":"QB","rank":5,"adp":3},{"name":"Jordyn Tyson","pos":"WR","rank":6,"adp":4},{"name":"Omar Cooper","pos":"WR","rank":7,"adp":9},{"name":"Kenyon Sadiq","pos":"TE","rank":8,"adp":7},{"name":"Jadarian Price","pos":"RB","rank":9,"adp":6},{"name":"Ty Simpson","pos":"QB","rank":10,"adp":10},{"name":"Denzel Boston","pos":"WR","rank":11,"adp":12},{"name":"Eli Stowers","pos":"TE","rank":12,"adp":11},{"name":"Chris Brazzell","pos":"WR","rank":13,"adp":21},{"name":"Emmett Johnson","pos":"RB","rank":14,"adp":22},{"name":"Jonah Coleman","pos":"RB","rank":15,"adp":13},{"name":"Zachariah Branch","pos":"WR","rank":16,"adp":19},{"name":"Nicholas Singleton","pos":"RB","rank":17,"adp":15},{"name":"Antonio Williams","pos":"WR","rank":18,"adp":17},{"name":"Chris Bell","pos":"WR","rank":19,"adp":14},{"name":"Eli Raridon","pos":"TE","rank":20,"adp":37},{"name":"De'Zhaun Stribling","pos":"WR","rank":21,"adp":23},{"name":"Drew Allar","pos":"QB","rank":22,"adp":31},{"name":"Carson Beck","pos":"QB","rank":23,"adp":25},{"name":"Brenen Thompson","pos":"WR","rank":24,"adp":47},{"name":"Max Klare","pos":"TE","rank":25,"adp":28},{"name":"Germie Bernard","pos":"WR","rank":26,"adp":18},{"name":"Kaytron Allen","pos":"RB","rank":27,"adp":27},{"name":"Elijah Sarratt","pos":"WR","rank":28,"adp":24},{"name":"Ja'Kobi Lane","pos":"WR","rank":29,"adp":60},{"name":"Kevin Coleman","pos":"WR","rank":30,"adp":41},{"name":"Ted Hurst","pos":"WR","rank":31,"adp":30},{"name":"Oscar Delp","pos":"TE","rank":32,"adp":35},{"name":"Malachi Fields","pos":"WR","rank":33,"adp":20},{"name":"Cade Klubnik","pos":"QB","rank":34,"adp":34},{"name":"Bryce Lance","pos":"WR","rank":35,"adp":46},{"name":"Cyrus Allen","pos":"WR","rank":36,"adp":58},{"name":"Demond Claiborne","pos":"RB","rank":37,"adp":32},{"name":"Skyler Bell","pos":"WR","rank":38,"adp":29},{"name":"Mike Washington","pos":"RB","rank":39,"adp":16},{"name":"Adam Randall","pos":"RB","rank":40,"adp":45},{"name":"Michael Trigg","pos":"TE","rank":41,"adp":42},{"name":"Justin Joly","pos":"TE","rank":42,"adp":40},{"name":"Sam Roush","pos":"TE","rank":43,"adp":60},{"name":"Tanner Koziol","pos":"TE","rank":44,"adp":55},{"name":"Jack Endries","pos":"TE","rank":45,"adp":52},{"name":"Cole Payton","pos":"QB","rank":46,"adp":49},{"name":"Zavion Thomas","pos":"WR","rank":47,"adp":50},{"name":"Caleb Douglas","pos":"WR","rank":48,"adp":43},{"name":"Marlin Klein","pos":"TE","rank":49,"adp":56},{"name":"Kaelon Black","pos":"RB","rank":50,"adp":33},{"name":"Joe Royer","pos":"TE","rank":51,"adp":60},{"name":"Seth Mcgowan","pos":"RB","rank":52,"adp":48},{"name":"Eli Heidenreich","pos":"RB","rank":53,"adp":38},{"name":"CJ Daniels","pos":"WR","rank":54,"adp":60},{"name":"Deion Burks","pos":"WR","rank":55,"adp":60},{"name":"Taylen Green","pos":"QB","rank":56,"adp":60},{"name":"Malik Benson","pos":"WR","rank":57,"adp":53}];

// PFK Rookie Model — admin-only, dev-page tab.
// Composite score: 0.35*stats + 0.50*draftCapital + 0.15*film, all on 0-100 scale.
const PFK_WEIGHTS = { stats:0.35, dc:0.50, film:0.15 };
// Per-position draft capital tier anchors. abs = (round-1)*32 + slot (compensatory clamped to 32).
// Linear interpolation between adjacent anchors. udfa = score for undrafted players.
const DC_TIERS = {
  QB: { udfa:2, anchors:[
    {abs:1,score:100},{abs:6,score:85},{abs:16,score:70},
    {abs:33,score:45},{abs:65,score:25},{abs:97,score:18},
    {abs:129,score:12},{abs:161,score:8},{abs:193,score:5},
  ]},
  RB: { udfa:6, anchors:[
    {abs:1,score:100},{abs:11,score:92},{abs:21,score:82},
    {abs:33,score:70},{abs:49,score:60},{abs:65,score:52},
    {abs:81,score:42},{abs:97,score:30},{abs:129,score:20},
    {abs:161,score:14},{abs:193,score:9},
  ]},
  WR: { udfa:4, anchors:[
    {abs:1,score:100},{abs:6,score:92},{abs:11,score:82},
    {abs:21,score:72},{abs:33,score:60},{abs:49,score:52},
    {abs:65,score:40},{abs:81,score:32},{abs:97,score:18},
    {abs:129,score:13},{abs:161,score:9},{abs:193,score:6},
  ]},
  TE: { udfa:2, anchors:[
    {abs:1,score:100},{abs:16,score:88},{abs:33,score:52},
    {abs:49,score:42},{abs:65,score:35},{abs:81,score:27},
    {abs:97,score:17},{abs:129,score:12},{abs:161,score:8},{abs:193,score:5},
  ]},
};
const pickToAbs = pickStr => {
  if(!pickStr || pickStr==='UDFA') return null;
  const m = String(pickStr).match(/^(\d+)\.(\d+)/);
  if(!m) return null;
  const r = +m[1], s = Math.min(+m[2], 32);
  return (r-1)*32 + s;
};
// FF "hit rate" = % chance of a fantasy-startable season at SOME POINT in a player's career.
// Career-based, not first-3-years — aligns with the popular "only 6 WR busts in top-10 over
// 20 years" framing where "hit" means "ever became a fantasy starter," not "starter as a rookie."
// Pick-bracket anchors with linear interpolation between them — mirrors dcScoreFromPick.
// Sources: Fantasy Footballers (2000-2018 study, n=1349), fantasyclassroom.org (QB tiers),
// Yahoo/ESPN/PFF top-10-pick analyses, and a 20-year manual review of top-5 picks per position.
//   RB metric: any Top-24 RB (RB2) season — career
//   WR metric: any Top-24 WR (WR2) season — career
//   TE metric: any Top-12 TE season — career
//   QB metric: any Top-12 QB (QB1) season — career
const FF_HIT_RATE = {
  RB: { metric:'Career Top-24 RB (RB2) season', udfa:2, anchors:[
    {abs:1,rate:95}, {abs:5,rate:92}, {abs:10,rate:88}, {abs:20,rate:75}, {abs:32,rate:62},
    {abs:48,rate:45}, {abs:64,rate:38}, {abs:96,rate:22},
    {abs:128,rate:12}, {abs:160,rate:7}, {abs:192,rate:4}, {abs:224,rate:3},
  ]},
  WR: { metric:'Career Top-24 WR (WR2) season', udfa:1, anchors:[
    {abs:1,rate:92}, {abs:5,rate:88}, {abs:10,rate:80}, {abs:20,rate:60}, {abs:32,rate:45},
    {abs:48,rate:28}, {abs:64,rate:20}, {abs:96,rate:10},
    {abs:128,rate:5}, {abs:160,rate:3}, {abs:192,rate:2}, {abs:224,rate:1},
  ]},
  TE: { metric:'Career Top-12 TE season', udfa:1, anchors:[
    {abs:1,rate:75}, {abs:5,rate:70}, {abs:10,rate:65}, {abs:20,rate:48}, {abs:32,rate:35},
    {abs:64,rate:15}, {abs:96,rate:12},
    {abs:128,rate:6}, {abs:160,rate:4}, {abs:192,rate:3}, {abs:224,rate:2},
  ]},
  QB: { metric:'Career Top-12 QB (QB1) season', udfa:1, anchors:[
    {abs:1,rate:88}, {abs:5,rate:82}, {abs:10,rate:72}, {abs:20,rate:62}, {abs:32,rate:52},
    {abs:48,rate:28}, {abs:64,rate:18},     // R2 — Hurts/Kaepernick/Geno/Carr tier
    {abs:80,rate:13}, {abs:96,rate:8},      // R3 — Wilson/Schaub/Foles outliers, mostly busts
    {abs:128,rate:5}, {abs:160,rate:3}, {abs:192,rate:2}, {abs:224,rate:1},
  ]},
};
const ffHitRate = (pos, pickStr) => {
  const tier = FF_HIT_RATE[pos]; if(!tier) return null;
  const abs = pickToAbs(pickStr);
  if(abs===null) return tier.udfa;
  const a = tier.anchors;
  if(abs <= a[0].abs) return Math.round(a[0].rate);
  for(let i=0; i<a.length-1; i++){
    if(abs >= a[i].abs && abs < a[i+1].abs){
      const t = (abs - a[i].abs) / (a[i+1].abs - a[i].abs);
      return Math.round(a[i].rate + t*(a[i+1].rate - a[i].rate));
    }
  }
  return Math.round(a[a.length-1].rate);
};
const dcScoreFromPick = (pos, pickStr) => {
  const tier = DC_TIERS[pos]; if(!tier) return 0;
  const abs = pickToAbs(pickStr);
  if(abs===null) return tier.udfa;
  const a = tier.anchors;
  if(abs <= a[0].abs) return a[0].score;
  for(let i=0; i<a.length-1; i++){
    if(abs >= a[i].abs && abs < a[i+1].abs){
      const t = (abs - a[i].abs) / (a[i+1].abs - a[i].abs);
      return Math.round((a[i].score + t*(a[i+1].score - a[i].score))*10)/10;
    }
  }
  return a[a.length-1].score;
};
const pfkScore = (stats, dc, film) => {
  const s = +stats||0, d = +dc||0, f = +film||0;
  return Math.round((PFK_WEIGHTS.stats*s + PFK_WEIGHTS.dc*d + PFK_WEIGHTS.film*f)*10)/10;
};
// Per-combo position multipliers. Baseline = Superflex / TEP 0.5 / 6 PTD (DEFAULT_SETTINGS).
// Source: pfk_research/MULTIPLIER_AUDIT_2026-05-13.md (full provenance audit).
//   Derivation: FantasyCalc API comparison numQbs=1 vs numQbs=2 per position
//   (calibration sheet not in pfk_research — reproducible but undocumented).
// Status: FC-API DERIVED (1QB row), ANCHOR (Superflex row).
// Last reviewed: 2026-05-13
const FORMAT_MULT = {
  'Superflex': {QB:1.00, RB:1.00, WR:1.00, TE:1.00}, // ANCHOR — baseline format
  '1QB':       {QB:0.54, RB:1.10, WR:1.01, TE:0.93}, // FC-API DERIVED 2026-04-25
};
// TEP only affects TE per Evan's preference (cross-position effects ignored despite research showing slight squeeze on RB/WR).
// Source: pfk_research/MULTIPLIER_AUDIT_2026-05-13.md (full provenance audit).
//   Empirical support for TEP impact lives in CHAMPION_ANATOMY_HOP2_UPDATE.md:45-53
//   (TE-in-FLEX% by TEP tier) + output/summary.csv (avg_starter_TE by bucket).
//   The 1.0 → 1.06 anchor was imported from rookie ranks, not freshly derived.
// Status: MIXED — see per-row inline status.
// Last reviewed: 2026-05-13
const TEP_MULT = {
  0:    {QB:1.00, RB:1.00, WR:1.00, TE:0.94},  // EXTRAPOLATED — symmetric reflection of 1.0 around 0.5 baseline (added 2026-05-13)
  0.25: {QB:1.00, RB:1.00, WR:1.00, TE:0.97},  // EXTRAPOLATED — linear midpoint of 0 and 0.5; no empirical 0.25 data in pfk_research (added 2026-05-13)
  0.5:  {QB:1.00, RB:1.00, WR:1.00, TE:1.00},  // ANCHOR — DEFAULT_SETTINGS.tep, all other entries scale from here
  0.75: {QB:1.00, RB:1.00, WR:1.00, TE:1.025}, // EXTRAPOLATED — linear midpoint of 0.5 and 1.0 (champion data suggests ~1.044 may be more accurate)
  1.0:  {QB:1.00, RB:1.00, WR:1.00, TE:1.06},  // EVAN-TUNED — imported from rookie ranks; no formal derivation in pfk_research
  1:    {QB:1.00, RB:1.00, WR:1.00, TE:1.06},  // alias for tep stored as integer 1
};
// Exposed on window so Playwright can verify the table + the per-league
// TE adjustment without needing a real Sleeper league fixture.
if(typeof window !== 'undefined'){
  window.PFK_TEP_MULT = TEP_MULT;
  window.PFK_TF_TE_VALUE_FOR_TEP = (rawValue, tep) => rawValue * (TEP_MULT[tep]?.TE ?? 1);
}
// Source: pfk_research/MULTIPLIER_AUDIT_2026-05-13.md (full provenance audit).
//   PROJECT_STATUS.md:253 lists these as "calibration anchors already in code
//   from rookie rankings" — direction is uncontroversial, magnitude is a
//   chosen 5% with no NFL-stats derivation in pfk_research.
// Status: EVAN-TUNED (non-baseline rows), ANCHOR (5 PTD baseline).
// Last reviewed: 2026-05-13
const PTD_MULT = {
  4: {QB:0.95, RB:1.00, WR:1.00, TE:1.00}, // EVAN-TUNED 2026-04-25 — 4PT nerfs QBs 5%
  5: {QB:1.00, RB:1.00, WR:1.00, TE:1.00}, // ANCHOR — standard pass-TD baseline
  6: {QB:1.05, RB:1.00, WR:1.00, TE:1.00}, // EVAN-TUNED 2026-04-25 — 6PT bumps QBs 5%
};
const comboMultiplier = (pos, settings) => {
  if(!pos || !settings) return 1.00;
  const f = FORMAT_MULT[settings.format||'Superflex']?.[pos] ?? 1.00;
  const t = TEP_MULT[settings.tep]?.[pos] ?? 1.00;
  const p = PTD_MULT[settings.passTd]?.[pos] ?? 1.00;
  return f * t * p;
};
// Stable signature for a settings combo, matches sigOf() inside AdminApp for cross-component lookup.
const settingsSig = s => `${s?.format||'Superflex'}|${s?.tep}|${s?.ppr}|${s?.passTd}|${s?.ppc}`;
// Ranking Score system. Each tier gets a 10-point base score (tier 0 = top = 100, tier 1 = 90, ...).
// Within a tier, each rank decays by RANKING_SCORE_DECAY[tierIdx]. Top tiers have a steeper within-tier
// drop (rank matters more); bottom tiers nearly flat (decay 0.2). Doubled from the original spec
// per Evan's preference for stronger top-tier separation.
const RANKING_SCORE_DECAY = [2.0, 1.5, 1.0, 0.5, 0.2, 0.2, 0.2, 0.2, 0.2, 0.2];
// Walk the master list (with tier markers in order) and find the player's tier index + rank within tier.
// Returns { tierIdx, rankInTier } or null if not found.
const findPlayerInMaster = (masterList, id) => {
  let tierIdx = -1; let rankInTier = 0;
  for(const it of (masterList||[])){
    if(it?.type === 'tier'){ tierIdx++; rankInTier = 0; }
    else if(it?.type === 'player'){
      rankInTier++;
      if(it.id === id) return { tierIdx: Math.max(0, tierIdx), rankInTier };
    }
  }
  return null;
};
// Auto ranking score from rank+tier per Evan's spec.
const getAutoRankingScore = (item, masterList) => {
  if(!item?.id) return null;
  const pos = findPlayerInMaster(masterList, item.id);
  if(!pos) return null;
  const { tierIdx, rankInTier } = pos;
  const tierBase = 100 - tierIdx * 10;
  const decay = RANKING_SCORE_DECAY[Math.min(tierIdx, RANKING_SCORE_DECAY.length-1)];
  return Math.round((tierBase - (rankInTier - 1) * decay) * 100) / 100; // 2 decimal precision
};
// Effective = override if set, else auto.
const getEffectiveRankingScore = (item, masterList) => {
  if(item?.rankingScoreOverride != null && !isNaN(+item.rankingScoreOverride)){
    return Math.round(+item.rankingScoreOverride * 100) / 100;
  }
  return getAutoRankingScore(item, masterList);
};
// Derive a per-combo list from the master list. WR/RB are ANCHORS — they keep their
// exact master order and tier placement regardless of combo. Only QB/TE move: each is
// re-inserted based on its adjusted score (ranking score × combo multiplier) relative
// to the nearest-lower WR/RB anchor. Empty tier markers (e.g. a master tier that held
// only QBs that all jumped out) are dropped.
const deriveListForCombo = (masterList, combo) => {
  if(!masterList || !masterList.length) return null;
  if(!masterList.some(it => it?.type === 'player')) return null;
  const adjFor = (p) => {
    const rs = getEffectiveRankingScore(p, masterList) ?? 0;
    return rs * comboMultiplier(p.pos, combo);
  };
  // Skeleton: tier markers + WR/RB players in original master order.
  const skeleton = [];
  const movables = [];
  for(const it of masterList){
    if(it?.type === 'tier'){ skeleton.push(it); }
    else if(it?.type === 'player'){
      if(it.pos === 'QB' || it.pos === 'TE'){ movables.push({p: it, adj: adjFor(it)}); }
      else { skeleton.push(it); }
    }
  }
  // Insert highest-adj movables first so their relative order is preserved within a tier.
  movables.sort((a,b) => b.adj - a.adj);
  const result = skeleton.slice();
  for(const m of movables){
    let insertAt = result.length;
    for(let i=0; i<result.length; i++){
      const it = result[i];
      if(it?.type === 'player' && (it.pos === 'WR' || it.pos === 'RB')){
        if(adjFor(it) < m.adj){ insertAt = i; break; }
      }
    }
    result.splice(insertAt, 0, m.p);
  }
  // Drop tier markers that have no following players before the next tier marker / end.
  const cleaned = [];
  for(let i=0; i<result.length; i++){
    const it = result[i];
    if(it?.type === 'tier'){
      let hasPlayer = false;
      for(let j=i+1; j<result.length; j++){
        if(result[j]?.type === 'tier') break;
        if(result[j]?.type === 'player'){ hasPlayer = true; break; }
      }
      if(hasPlayer) cleaned.push(it);
    } else {
      cleaned.push(it);
    }
  }
  return cleaned;
};
// Fetch the master list for derivation. Dev URL prefers the master combo's dev draft (most recent
// edits); prod URL goes straight to the published default combo. Returns the items array or null.
const fetchMasterListForDerivation = async () => {
  if(isDevHost()){
    const draft = await fetchDevDraftRankings(DEFAULT_SETTINGS);
    if(draft?.data && Array.isArray(draft.data)) return draft.data;
  }
  const pub = await fetchOfficialRankings(DEFAULT_SETTINGS);
  return (pub?.data && Array.isArray(pub.data)) ? pub.data : null;
};
// Build a per-combo PFK score for a model entry (with override + landing + combo multiplier).
const computeComboPfk = (m, settings) => {
  const pos = m.pos;
  const dc = m.dcOverride!=null ? +m.dcOverride : dcScoreFromPick(pos, m.pick);
  const film = filmAvgPct(m.filmScores);
  const base = pfkScore(m.stats, dc, film);
  const baseline = Math.round(base * landingMultiplier(m.landing) * 10) / 10;
  const sig = settingsSig(settings);
  const ov = m.comboOverrides?.[sig];
  if(ov!=null && !isNaN(+ov)) return Math.round(+ov*10)/10;
  return Math.round(baseline * comboMultiplier(pos, settings) * 10) / 10;
};
// Build a fully-seeded items array from the rookie model + settings + (optional) existing list.
// Returns null if model is empty. Used by both the admin RANKINGS auto-seed-on-first-load and the
// 🌱 Seed from Model button. Players sorted by combo-adjusted PFK desc, bucketed via the dynamic
// per-class auto-tier algorithm using tier names from the existing list (or INITIAL_TIERS).
// College/age preserved from existing list when names match.
const buildSeedFromModel = (modelByName, settings, existingList) => {
  const modelEntries = Object.values(modelByName||{}).filter(p=>['QB','RB','WR','TE'].includes(p.pos));
  if(!modelEntries.length) return null;
  const scored = modelEntries.map(p=>({p, pfk: computeComboPfk(p, settings)}));
  scored.sort((a,b)=>b.pfk - a.pfk);
  const existingTiers = (existingList||[]).filter(it=>it.type==='tier');
  const tierNames = existingTiers.length ? existingTiers.map(t=>t.name) : INITIAL_TIERS.slice();
  const usableNames = tierNames.slice(0, PFK_TIER_TARGETS.length+1);
  const existingByName = new Map((existingList||[]).filter(it=>it.type==='player').map(it=>[normDraftName(it.name), it]));
  const scores = scored.map(x=>x.pfk);
  const boundaries = autoTierBoundaries(scores, usableNames.length);
  const out = []; let cur = 0;
  const ts = Date.now();
  for(let i=0; i<usableNames.length; i++){
    const end = (i<boundaries.length) ? boundaries[i] : scored.length;
    const slice = scored.slice(cur, end);
    if(slice.length){
      out.push({type:'tier', id:'tier_seed_'+i+'_'+ts, name:usableNames[i]});
      for(const {p} of slice){
        const ex = existingByName.get(normDraftName(p.name));
        out.push({
          type:'player',
          id: p.id || ('p_'+ts+'_'+Math.random().toString(36).slice(2,6)),
          name: p.name, pos: p.pos,
          pick: p.pick || null,
          nflTeam: p.team || (ex?.nflTeam) || 'TBD',
          college: ex?.college || '',
          age: ex?.age ?? null,
        });
      }
    }
    cur = end;
  }
  return out;
};
// Pre-Draft percentile × 100 from 2026 Data Models (QB/RB/WR/TE xlsx, 2026 only).
const BAKED_STATS_PCT = {
  // QB
  fernandomendoza:70.0, carsonbeck:60.6, trinidadchambliss:64.3,
  drewallar:39.0, garrettnussmeier:37.1, tysimpson:30.5,
  // RB
  jeremiyahlove:99.4, nicholassingleton:87.6, jonahcoleman:80.1,
  kaytronallen:56.5, emmettjohnson:55.9, jadarianprice:39.8,
  demondclaiborne:27.3, adamrandall:7.5, mikewashington:5.0, jamarionmiller:3.7,
  // WR
  jordyntyson:96.5, makailemon:89.0, carnelltate:88.0, kcconcepcion:87.0,
  denzelboston:77.5, elijahsarratt:72.0, antoniowilliams:64.5, zachariahbranch:64.0,
  chrisbrazzellii:58.5, chrisbell:59.0, omarcooperjr:57.5, germiebernard:51.0,
  cjdaniels:46.5, malachifields:39.5, jakobilane:34.0, brenenthompson:22.0,
  deionburks:15.5, skylerbell:15.0,
  // TE
  maxklare:76.2, kenyonsadiq:74.3, jackendries:70.5, elistowers:69.5,
  joeroyer:47.6, dallenbentley:41.0, samroush:36.2, oscardelp:21.0,
};
// Film source registry — multi-source film grading. Each source defines:
//   weight    — relative weight in the composite Film score (any positive number; renormalized when sources missing)
//   kind      — 'numeric' or 'categorical' (drives UI editor)
//   scaleHint — human label for the raw scale
//   toPct     — converts raw value to 0-100 percentile
const FILM_SOURCES = {
  zoltan:   { label:'Zoltan',   weight:50, kind:'numeric',     scaleHint:'0-100',
              toPct: v => v==null?null:Math.max(0,Math.min(100,+v)) },
  evan:     { label:'You',      weight:30, kind:'categorical', scaleHint:'In/Neutral/Out',
              options:[{key:'in',label:'IN',pct:100,color:'var(--success)'},
                       {key:'neutral',label:'NTL',pct:50,color:'var(--accent-gold-text)'},
                       {key:'out',label:'OUT',pct:0,color:'var(--danger)'}],
              toPct: v => { if(v==null) return null; const o=FILM_SOURCES.evan.options.find(x=>x.key===v); return o?o.pct:null; } },
  zierlein: { label:'Zierlein', weight:20, kind:'numeric',     scaleHint:'5.0-7.5',
              toPct: v => v==null?null:Math.max(0,Math.min(100,(+v-5.0)/2.5*100)) },
};
// Landing-spot multiplier applied AFTER the weighted PFK score. Default unrated = 1.00 (no impact).
// 1=worst → 0.92×, 5=best → 1.08×. Symmetric ±8% max swing.
const LANDING_OPTIONS = [
  {key:5, label:'5', mult:1.08, color:'var(--success)'},
  {key:4, label:'4', mult:1.04, color:'#84cc16'},
  {key:3, label:'3', mult:1.00, color:'#94a3b8'},
  {key:2, label:'2', mult:0.96, color:'var(--warning)'},
  {key:1, label:'1', mult:0.92, color:'var(--danger)'},
];
const landingMultiplier = (v) => {
  if(v==null) return 1.00;
  const opt = LANDING_OPTIONS.find(o=>o.key===+v);
  return opt ? opt.mult : 1.00;
};
// Weighted average across populated sources; missing sources drop and weights re-normalize.
const filmAvgPct = (filmScores) => {
  if(!filmScores || typeof filmScores!=='object') return null;
  let totalW=0, totalV=0;
  for(const [k,src] of Object.entries(FILM_SOURCES)){
    const p = src.toPct(filmScores[k]);
    if(p==null || isNaN(p)) continue;
    totalW += src.weight;
    totalV += src.weight * p;
  }
  return totalW>0 ? Math.round(totalV/totalW*10)/10 : null;
};
// Film percentiles, baked seed values per source.
// Zoltan: Dynasty Zoltan Premium screenshots, 2026-04-25 (0-100 scale, raw value used directly).
// Zierlein: NFL.com prospect grade screenshots, 2026-04-25 (5.0-7.5 scale, converted via FILM_SOURCES.zierlein.toPct).
const BAKED_FILM_ZOLTAN = {
  jeremiyahlove:91, makailemon:91, kcconcepcion:93, carnelltate:88,
  omarcooperjr:92, fernandomendoza:57, jordyntyson:77, denzelboston:85,
  kenyonsadiq:91, emmettjohnson:88, chrisbrazzellii:75, elistowers:57,
  zachariahbranch:81, jonahcoleman:45, jadarianprice:28, tysimpson:43,
  kaytronallen:61, michaeltrigg:75, elijahsarratt:51, chrisbell:45,
  antoniowilliams:49, jakobilane:53, nicholassingleton:35, germiebernard:47,
  mikewashington:24, maxklare:40, brycelance:42, tedhurst:43,
  demondclaiborne:13, roberthenryjr:19, skylerbell:19,
};
// All Zierlein grades from nfl.com/draft/tracker/2026/prospects/{pos}_all (2026-04-25).
// Aliases included for known PFK-board name variants (e.g. "Jam Miller" → "Jamarion Miller").
const BAKED_FILM_ZIERLEIN = {
  // QB
  fernandomendoza:6.73, tysimpson:6.30, carsonbeck:6.14, garrettnussmeier:6.00,
  taylengreen:6.00, drewallar:5.98, cadeklubnik:5.96, diegopavia:5.95,
  joeyaguilar:5.95, colepayton:5.91, sawyerrobertson:5.85, haynesking:5.80,
  behrenmorton:5.68, lukealtmyer:5.68, jalondaniels:5.66, joefagnano:5.66,
  // RB
  jeremiyahlove:6.73, jadarianprice:6.38, mikewashington:6.24, mikewashingtonjr:6.24,
  adamrandall:6.13, demondclaiborne:6.10, leveonmoss:6.10, emmettjohnson:6.00,
  nicholassingleton:6.00, rahsulfaison:6.00, jmaritaylor:5.99, romanhemby:5.98,
  kaytronallen:5.97, sethmcgowan:5.97, jonahcoleman:5.94, desmondreid:5.92,
  eliheidenreich:5.86, chiptrayanum:5.85, deamontetrayanum:5.85, cjdonaldson:5.82,
  aljayhenderson:5.69, davonbooth:5.69, jammiller:5.69, jamarionmiller:5.69,
  jaydnott:5.69, jadynott:5.69, kaelonblack:5.69, noahwhittington:5.69, roberthenryjr:5.69,
  barikakpeenu:5.68, deanconnors:5.67, kentrelbullock:5.67, jamalhaynes:5.65, samscott:5.65,
  // WR
  carnelltate:6.71, makailemon:6.47, jordyntyson:6.43, kcconcepcion:6.42,
  denzelboston:6.40, omarcooperjr:6.39, chrisbrazzellii:6.36, zachariahbranch:6.32,
  skylerbell:6.31, germiebernard:6.29, dezhaunstribling:6.28, malachifields:6.27,
  antoniowilliams:6.26, chrisbell:6.24, jakobilane:6.20, elijahsarratt:6.19,
  tedhurst:6.19, cyrusallen:6.18, deionburks:6.18, brycelance:6.17,
  jmichaelsturdivant:6.10, jeffcaldwell:6.00, joshcameron:6.00, malikbenson:6.00,
  kevincolemanjr:5.99, reggievirgil:5.99, kadenwetjen:5.98,
  ericmcalister:5.97, ericmccalister:5.97,
  colbieyoung:5.96, calebdouglas:5.95, kendricklaw:5.95, dillonbell:5.94,
  brenenthompson:5.89, emmanuelhendersonjr:5.88, zavionthomas:5.87, barionbrown:5.86,
  ericrivers:5.86, harrisonwallaceiii:5.85, cjdaniels:5.84, jalenwalthall:5.83,
  jordanhudson:5.81, camdenbrown:5.80, chaseroberts:5.80, danielsobkowicz:5.80,
  dtsheffield:5.69, tyrenmontgomery:5.69, vinnyanthonyii:5.69,
  bradyboyd:5.68, caullinlacy:5.68, chrishiltonjr:5.68, aaronanderson:5.67,
  // TE
  kenyonsadiq:6.46, maxklare:6.30, samroush:6.26, elistowers:6.24,
  nateboerkircher:6.17, justinjoly:6.16, eliraridon:6.14, jackendries:6.13,
  oscardelp:6.13, joeroyer:6.12, willkacmarek:6.12, marlinklein:6.10,
  michaeltrigg:6.00, tannerkoziol:5.95, johnmichaelgyllenborg:5.90, carsenryan:5.84,
};
const POS_TIER_BANDS = [
  {max:5,   key:'Elite', color:'var(--accent-gold-text)'},
  {max:12,  key:'T1',    color:'var(--accent-violet)'},
  {max:24,  key:'T2',    color:'var(--info)'},
  {max:36,  key:'Flex',  color:'var(--success)'},
  {max:999, key:'Depth', color:'var(--text-secondary)'},
];
const posTier = rank => rank ? (POS_TIER_BANDS.find(b=>rank<=b.max) || POS_TIER_BANDS[POS_TIER_BANDS.length-1]) : null;
const healthGrade = (pos, b) => {
  const e=b.Elite||0, t1=b.T1||0, t2=b.T2||0, fx=b.Flex||0;
  if (pos==='QB'){
    if ((e>=1 && (e+t1+t2)>=2) || t1>=2) return 'Strong';
    if (t1>=1 && t2>=1)                  return 'Adequate';
    if ((e+t1+t2)>=1 && fx>=1)           return 'Thin';
    return 'Critical';
  }
  if (pos==='RB'){
    if ((e+t1)>=2 || (e>=1 && t2>=2))    return 'Strong';
    if (t1>=1 && t2>=1 && fx>=1)         return 'Adequate';
    if ((e+t1+t2)>=1)                    return 'Thin';
    return 'Critical';
  }
  if (pos==='WR'){
    if ((e>=1 && (t1+t2)>=2) || t1>=3)   return 'Strong';
    if (t1>=2 || (t1>=1 && t2>=2))       return 'Adequate';
    if ((e+t1+t2)>=1)                    return 'Thin';
    return 'Critical';
  }
  if (pos==='TE'){
    if (e>=1 || t1>=2)                   return 'Strong';
    if (t1>=1)                           return 'Adequate';
    if (t2>=1)                           return 'Thin';
    return 'Critical';
  }
  return 'Critical';
};
const GRADE_COLOR = {Strong:'var(--success)', Adequate:'var(--accent-gold)', Thin:'var(--warning)', Critical:'var(--danger)'};
const pickTierFromStand = (standPos, n=12) => {
  const third = Math.max(1, Math.round(n/3));
  if (standPos <= third)   return {key:'Early', color:'var(--accent-gold-text)'};
  if (standPos <= third*2) return {key:'Mid',   color:'var(--info)'};
  return                        {key:'Late',  color:'var(--text-secondary)'};
};

const SLEEPER = 'https://api.sleeper.app/v1';
const FC      = 'https://api.fantasycalc.com';

// ============================================================================
// Sharp Trade Tracker — pool ingestion helper (Phase 0a)
// ============================================================================
// Called fire-and-forget at every `/v1/user/{id}/leagues/nfl/{year}` callsite.
// Routes through the SECURITY DEFINER `record_user_leagues` RPC in Supabase
// so the client never holds raw INSERT/UPDATE on the pool tables.
//
// - Dynasty-only at intake (settings.type === 2). Redraft leagues stay out
//   of the pool — the Sharp Tracker tool is dynasty-only and we don't want
//   to dilute the trade scan budget.
// - Hard cap at 200 leagues per call so a mega-account doesn't blow the
//   RPC payload.
// - Silent failure path. The hook must NEVER surface an error to the UI;
//   if Supabase is down or the RPC rejects, the user's session continues
//   normally.
// ============================================================================
const pfkRecordUserLeagues = (userObj, leagues) => {
  if(!sb || !userObj || !userObj.user_id || !Array.isArray(leagues)) return;
  const dynasty = leagues
    .filter(l => l && l.settings && l.settings.type === 2)
    .slice(0, 200);
  if(dynasty.length === 0) return;
  try {
    sb.rpc('record_user_leagues', {
      p_user_id:      String(userObj.user_id),
      p_username:     userObj.username || null,
      p_display_name: userObj.display_name || null,
      p_avatar:       userObj.avatar || null,
      p_leagues:      dynasty,
    }).then(() => {}, () => {}); // fire-and-forget
  } catch(_) { /* swallow */ }
};

// fetchSleeperDraftSlots — for each requested year, look up the league's
// startup/upcoming rookie draft on Sleeper and return a roster_id → { slot, pad }
// map so dispersal pick names can render as "2026 1.02" instead of the
// round-only fallback. Returns Map<yearStr, Map<rosterId, {slot, pad}> | null>.
// A year maps to null when (a) the season has no draft, or (b) the
// commissioner hasn't set the draft order yet (slot_to_roster_id is empty).
const fetchSleeperDraftSlots = async (leagueId, years) => {
  const out = new Map();
  if(!leagueId || !years || !years.length) return out;
  let drafts;
  try{ drafts = await fetch(`${SLEEPER}/league/${leagueId}/drafts`, {cache:'no-store'}).then(r=>r.ok?r.json():[]); }
  catch(e){ drafts = []; }
  for(const y of years){
    const yStr = String(y);
    const d = (drafts||[]).find(x => String(x.season) === yStr);
    if(!d?.draft_id){ out.set(yStr, null); continue; }
    let full;
    try{ full = await fetch(`${SLEEPER}/draft/${d.draft_id}`, {cache:'no-store'}).then(r=>r.ok?r.json():null); }
    catch(e){ full = null; }
    const s2r = full?.slot_to_roster_id;
    if(!s2r || !Object.values(s2r).some(v => v != null)){ out.set(yStr, null); continue; }
    const teamCount = full?.settings?.teams || Object.keys(s2r).length || 12;
    const pad = String(teamCount).length;
    const r2s = new Map();
    Object.entries(s2r).forEach(([slot, rid]) => {
      if(rid != null) r2s.set(rid, { slot: +slot, pad });
    });
    out.set(yStr, r2s);
  }
  return out;
};
const formatPickSlot = (year, round, slotInfo) =>
  `${year} ${round}.${String(slotInfo.slot).padStart(slotInfo.pad || 2, '0')}`;
const POS_ORDER = ['QB','RB','WR','TE'];
const ORDINALS  = ['1st','2nd','3rd','4th'];

const _fcCache = {};
const fetchFcCached = async (url) => {
  if(_fcCache[url]) return _fcCache[url];
  const p = fetch(url).then(r=>r.json()).catch(()=>[]);
  _fcCache[url] = p;
  return p;
};

const computeArcForLeague = async (lg, userId) => {
  try {
    const [rs, tp] = await Promise.all([
      fetch(`${SLEEPER}/league/${lg.league_id}/rosters`).then(r=>r.json()).catch(()=>[]),
      fetch(`${SLEEPER}/league/${lg.league_id}/traded_picks`).then(r=>r.json()).catch(()=>[])
    ]);
    if(!Array.isArray(rs) || !rs.length) return null;
    const teams = rs.length;
    const rp = lg.roster_positions||[];
    const numQbs = rp.includes('SUPER_FLEX') ? 2 : 1;
    const ppr = lg.scoring_settings?.rec ?? 1;
    const fcUrl = d=>`${FC}/values/current?isDynasty=${d}&numQbs=${numQbs}&ppr=${ppr}&numTeams=${teams}`;
    const [dyn, red] = await Promise.all([fetchFcCached(fcUrl(true)), fetchFcCached(fcUrl(false))]);
    if(!Array.isArray(dyn) || !dyn.length) return null;
    const rdftById = {};
    (Array.isArray(red)?red:[]).forEach(v=>{ const sid=v.player?.sleeperId; if(sid) rdftById[String(sid)]=v.redraftValue||v.value||0; });
    const fcMap = {}, pickRound = {}, pickFuture = {};
    dyn.forEach(v=>{
      const sid=v.player?.sleeperId; if(!sid) return;
      const merged={...v, redraftValue: rdftById[String(sid)] ?? v.redraftValue ?? 0};
      fcMap[String(sid)] = merged;
      if(v.player?.position==='PICK'){
        const dpM=sid.match(/^DP_(\d+)_(\d+)$/);
        if(dpM){ const rd=Number(dpM[1])+1, sl=Number(dpM[2])+1; pickRound[rd]=pickRound[rd]||{}; pickRound[rd][sl]=v.value||0; return; }
        const fpM=sid.match(/^FP_(\d{4})_(\d+)$/);
        if(fpM) pickFuture[`${fpM[1]}_${fpM[2]}`]=v.value||0;
      }
    });
    const now=new Date(); const startYr=now.getFullYear()+(now.getMonth()>=8?1:0);
    const yrs=[startYr,startYr+1,startYr+2];
    const standingsOrder=[...rs].sort((a,b)=>(a.settings?.wins||0)-(b.settings?.wins||0)||(b.settings?.losses||0)-(a.settings?.losses||0)||a.roster_id-b.roster_id).map(r=>r.roster_id);
    const own={};
    rs.forEach(r=>{ yrs.forEach(y=>{ [1,2,3,4].forEach(rd=>{ own[`${y}_${rd}_${r.roster_id}`]=r.roster_id; }); }); });
    (Array.isArray(tp)?tp:[]).forEach(t=>{ const y=Number(t.season); if(yrs.includes(y)) own[`${y}_${t.round}_${t.roster_id}`]=t.owner_id; });
    const avgRd = rd=>{ const vs=Object.values(pickRound[rd]||{}); return vs.length?Math.round(vs.reduce((s,v)=>s+v,0)/vs.length):0; };
    const pickDVal={};
    Object.entries(own).forEach(([k,o])=>{
      const [yr,rd,orig]=k.split('_').map(Number); if(rd>4) return;
      let val=0;
      if(yr===startYr){
        const slot=standingsOrder.indexOf(orig)+1||null;
        val=(slot && pickRound[rd]?.[slot]) || avgRd(rd);
      } else val=pickFuture[`${yr}_${rd}`]||0;
      pickDVal[o]=(pickDVal[o]||0)+val;
    });
    const stats = rs.map(r=>{
      const active=r.players||[]; const allP=[...active,...(r.taxi||[])];
      let dVal=0, rVal=0;
      allP.forEach(pid=>{ const v=fcMap[String(pid)]; if(v) dVal+=v.value||0; });
      dVal+=(pickDVal[r.roster_id]||0);
      active.forEach(pid=>{ const v=fcMap[String(pid)]; if(v) rVal+=v.redraftValue||0; });
      return {roster_id:r.roster_id, owner_id:r.owner_id, dVal, rVal};
    });
    const byD=[...stats].sort((a,b)=>b.dVal-a.dVal);
    const byR=[...stats].sort((a,b)=>b.rVal-a.rVal);
    const n=stats.length;
    const di=byD.findIndex(x=>x.owner_id===userId);
    const ri=byR.findIndex(x=>x.owner_id===userId);
    if(di<0||ri<0) return null;
    const dPct=n===1?100:((n-1-di)/(n-1))*100;
    const rPct=n===1?100:((n-1-ri)/(n-1))*100;
    return classifyTeam(dPct,rPct);
  } catch { return null; }
};

const fetchLeagueChampionships = async (lg) => {
  const history = [];
  try {
    const seasons = [];
    let cur = lg;
    for (let i = 0; i < 10; i++) {
      if (!cur.previous_league_id) break;
      cur = await fetch(`${SLEEPER}/league/${cur.previous_league_id}`).then(r=>r.json()).catch(()=>null);
      if (!cur) break;
      seasons.push(cur);
    }
    if (!seasons.length) return history;
    await Promise.all(seasons.map(async season => {
      try {
        const [bracket, pastRosters, pastUsers] = await Promise.all([
          fetch(`${SLEEPER}/league/${season.league_id}/winners_bracket`).then(r=>r.json()).catch(()=>[]),
          fetch(`${SLEEPER}/league/${season.league_id}/rosters`).then(r=>r.json()).catch(()=>[]),
          fetch(`${SLEEPER}/league/${season.league_id}/users`).then(r=>r.json()).catch(()=>[]),
        ]);
        if (!Array.isArray(bracket) || !bracket.length) return;
        const maxR = Math.max(...bracket.map(g=>g.r));
        const champGame = bracket.filter(g=>g.r===maxR).find(g=>g.p==null)
                       || bracket.filter(g=>g.r===maxR)[0];
        if (!champGame?.w) return;
        const winner = Array.isArray(pastRosters) && pastRosters.find(r=>r.roster_id===champGame.w);
        if (!winner?.owner_id) return;
        const uMap = {};
        if (Array.isArray(pastUsers)) pastUsers.forEach(u=>{ uMap[u.user_id]=u; });
        const owner = uMap[winner.owner_id];
        history.push({
          year: season.season||'?',
          ownerId: winner.owner_id,
          teamName: owner?.metadata?.team_name||owner?.display_name||`Team ${winner.roster_id}`,
          ownerName: owner?.display_name||'',
        });
      } catch {}
    }));
  } catch {}
  return history.sort((a,b)=>Number(b.year)-Number(a.year));
};

function TeamTab() {
  // 'pfk_team_username' was previously 'pfk_sleeper_user', which collided
  // with the global useSleeperUser hook's JSON-object storage at the same
  // key. The collision corrupted the JSON, making users get re-prompted to
  // link Sleeper on every visit. Renamed 2026-05-02.
  const [username, setUsername]           = useState(()=>loadStorage('pfk_team_username',''));
  useEffect(()=>{
    if(username||!sb) return;
    sb.auth.getSession().then(({data})=>{
      const uid=data.session?.user?.id; if(!uid) return;
      sb.from('users').select('sleeper_username').eq('id',uid).maybeSingle().then(({data:u})=>{
        if(u?.sleeper_username) setUsername(u.sleeper_username);
      });
    });
  },[]);
  const [sleeperUser, setSleeperUser]     = useState(()=>loadStorage('pfk_sleeper_obj',null));
  const [leagues, setLeagues]             = useState([]);
  const [league, setLeague]               = useState(null);
  const [rosters, setRosters]             = useState([]);
  const [users, setUsers]                 = useState([]);
  const [fcValues, setFcValues]           = useState([]);
  const [tradedPicks, setTradedPicks]     = useState([]);
  const [championships, setChampionships] = useState([]);
  const champCounts = useMemo(()=>{
    const m={};
    championships.forEach(c=>{m[c.ownerId]=(m[c.ownerId]||0)+1;});
    return m;
  },[championships]);
  const [draftSlots, setDraftSlots]       = useState({}); // roster_id → draft slot# for current rookie draft
  const [loading, setLoading]             = useState('');
  const [error, setError]                 = useState('');
  const [fcError, setFcError]             = useState(false);
  const [lastFetched, setLastFetched]     = useState(null);
  const [viewMode, setViewMode]           = useState('dynasty');
  const [valueMode, setValueMode]         = useState(()=>localStorage.getItem('pfk_value_mode')||'starter');
  useEffect(()=>{ localStorage.setItem('pfk_value_mode', valueMode); },[valueMode]);
  const [selectedOtherRid, setSelectedOtherRid] = useState(null);
  const [leagueArcs, setLeagueArcs] = useState({});
  const [analyzerRid, setAnalyzerRid] = useState(null);
  const [suggestionMode, setSuggestionMode] = useState(null); // null = auto by arc
  const [pendingTrades, setPendingTrades] = useState(0);
  const inp2 = ex=>({padding:'7px 10px',background:'var(--bg-card)',border:'1px solid var(--border-strong)',borderRadius:7,color:'var(--text-bright)',fontSize:13,fontFamily:'inherit',...ex});

  const connectUser = async () => {
    if (!username.trim()) return;
    setLoading('user'); setError(''); setLeagues([]); setLeague(null); setRosters([]); setUsers([]);
    try {
      const u = await fetch(`${SLEEPER}/user/${encodeURIComponent(username.trim())}`).then(r=>{
        if(!r.ok) throw new Error('Username not found on Sleeper');
        return r.json();
      });
      if (!u?.user_id) throw new Error('Username not found on Sleeper');
      setSleeperUser(u);
      localStorage.setItem('pfk_sleeper_obj', JSON.stringify(u));
      localStorage.setItem('pfk_team_username', JSON.stringify(username.trim()));
      setLoading('leagues');
      // Try current year first, then fall back — dynasty leagues roll over each season
      const curYear = new Date().getFullYear();
      let ls = [];
      for (const yr of [curYear, curYear-1, curYear-2]) {
        const res = await fetch(`${SLEEPER}/user/${u.user_id}/leagues/nfl/${yr}`, {cache:'no-store'}).then(r=>r.json()).catch(()=>[]);
        if (Array.isArray(res) && res.length) { ls = res; break; }
      }
      pfkRecordUserLeagues(u, ls); // Sharp Tracker pool ingestion (Phase 0a)
      setLeagues(ls);
      setLoading('');
    } catch(e){ setError(e.message); setLoading(''); }
  };

  useEffect(()=>{
    if(username) localStorage.setItem('pfk_team_username', JSON.stringify(username));
  },[username]);

  const autoTriedRef = useRef(false);
  useEffect(()=>{
    if(autoTriedRef.current) return;
    if(username && !sleeperUser && !loading){
      autoTriedRef.current = true;
      connectUser();
    }
  },[username,sleeperUser,loading]);

  useEffect(()=>{
    if(!sleeperUser||!leagues.length) return;
    let cancelled=false;
    const uid=sleeperUser.user_id;
    (async()=>{
      for(const lg of leagues){
        if(cancelled) return;
        if(leagueArcs[lg.league_id]) continue;
        const arc = await computeArcForLeague(lg, uid);
        if(cancelled) return;
        if(arc) setLeagueArcs(prev=>({...prev,[lg.league_id]:arc}));
      }
    })();
    return ()=>{ cancelled=true; };
  },[leagues,sleeperUser]);

  const NO_CACHE = {cache:'no-store'};

  const selectLeague = async (lg) => {
    setLeague(lg); setLoading('data'); setError('');
    setRosters([]); setUsers([]); setFcValues([]); setTradedPicks([]); setChampionships([]); setDraftSlots({}); setFcError(false); setLastFetched(null); setPendingTrades(0);
    try {
      const [rs, us, tp, drafts] = await Promise.all([
        fetch(`${SLEEPER}/league/${lg.league_id}/rosters`, NO_CACHE).then(r=>r.json()).catch(()=>[]),
        fetch(`${SLEEPER}/league/${lg.league_id}/users`, NO_CACHE).then(r=>r.json()).catch(()=>[]),
        fetch(`${SLEEPER}/league/${lg.league_id}/traded_picks`, NO_CACHE).then(r=>r.json()).catch(()=>[]),
        fetch(`${SLEEPER}/league/${lg.league_id}/drafts`, NO_CACHE).then(r=>r.json()).catch(()=>[]),
      ]);
      setRosters(Array.isArray(rs)?rs:[]);
      setUsers(Array.isArray(us)?us:[]);
      setTradedPicks(Array.isArray(tp)?tp:[]);
      // Rookie-draft slot order: always pull from Sleeper.
      // Priority:
      //   1) Current startYr rookie draft's slot_to_roster_id (set by commish — source of truth).
      //   2) If that draft exists but slots not yet populated, try draft_order (user_id→slot).
      //   3) Final fallback: previous_league_id rosters sorted worst-first (pre-commish setup).
      const allDrafts = Array.isArray(drafts)?drafts:[];
      const nowDt=new Date(); const startYr=nowDt.getFullYear()+(nowDt.getMonth()>=8?1:0);
      const currentDraft = allDrafts
        .filter(d=>d.type!=='auction' && Number(d.season||0)===startYr)
        .sort((a,b)=>(b.created||0)-(a.created||0))[0];
      const rosterToSlot = {};
      const slotMap = currentDraft?.slot_to_roster_id;
      const orderMap = currentDraft?.draft_order; // user_id → slot
      if(slotMap && Object.keys(slotMap).length){
        Object.entries(slotMap).forEach(([slot,rid])=>{
          if(rid) rosterToSlot[Number(rid)]=Number(slot);
        });
      } else if(orderMap && Object.keys(orderMap).length && Array.isArray(rs)){
        rs.forEach(r=>{
          const slot = orderMap[r.owner_id];
          if(slot) rosterToSlot[r.roster_id]=Number(slot);
        });
      }
      if(!Object.keys(rosterToSlot).length){
        const prevId = lg.previous_league_id;
        const prevRosters = prevId
          ? await fetch(`${SLEEPER}/league/${prevId}/rosters`,NO_CACHE).then(r=>r.json()).catch(()=>[])
          : (Array.isArray(rs)?rs:[]);
        const source = Array.isArray(prevRosters)&&prevRosters.length ? prevRosters : (Array.isArray(rs)?rs:[]);
        [...source].sort((a,b)=>
          (a.settings?.wins||0)-(b.settings?.wins||0)||
          (a.settings?.fpts||0)-(b.settings?.fpts||0)||
          a.roster_id-b.roster_id
        ).forEach((r,i)=>{ rosterToSlot[r.roster_id]=i+1; });
      }
      setDraftSlots(rosterToSlot);
      fetchLeagueChampionships(lg).then(c=>setChampionships(c)).catch(()=>{});
      // Incoming trade request count — fetch transactions across recent NFL weeks and
      // count pending trades that involve the user's roster_id.
      (async()=>{
        try {
          const myRoster = (Array.isArray(rs)?rs:[]).find(r=>r.owner_id===sleeperUser?.user_id);
          if(!myRoster) return;
          // Sleeper stores trades under the current "leg" week; during offseason that's often 1.
          // We fan out across all possible legs so off-season and in-season both work.
          const weeks=[0,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18];
          const results = await Promise.all(weeks.map(w=>
            fetch(`${SLEEPER}/league/${lg.league_id}/transactions/${w}`,NO_CACHE).then(r=>r.ok?r.json():[]).catch(()=>[])
          ));
          // De-dupe by transaction_id since the same trade can show in multiple leg queries
          const seen=new Set();
          const txns = results.flat().filter(t=>{
            if(!t||t.type!=='trade'||t.status!=='pending') return false;
            if(seen.has(t.transaction_id)) return false;
            seen.add(t.transaction_id); return true;
          });
          const mine = txns.filter(t=>Array.isArray(t.roster_ids)&&t.roster_ids.includes(myRoster.roster_id));
          setPendingTrades(mine.length);
        } catch {}
      })();
      setLoading('fc');
      const isSF  = lg.roster_positions?.includes('SUPER_FLEX')||false;
      const ppr   = (lg.scoring_settings?.rec??1)>=1?1:(lg.scoring_settings?.rec??1)>=0.5?0.5:0;
      const teams = lg.total_rosters||12;
      const numQbs= isSF?2:1;
      const ts    = Date.now();
      const fcUrl = (dyn) => `${FC}/values/current?isDynasty=${dyn}&numQbs=${numQbs}&ppr=${ppr}&numTeams=${teams}&_t=${ts}`;
      try {
        // Fetch dynasty + redraft in parallel so each uses its own context
        const [fcDyn, fcRdft] = await Promise.all([
          fetch(fcUrl('true'),  NO_CACHE).then(r=>{ if(!r.ok) throw new Error(); return r.json(); }),
          fetch(fcUrl('false'), NO_CACHE).then(r=>{ if(!r.ok) throw new Error(); return r.json(); }),
        ]);
        // Build redraft lookup by sleeperId (value + positionRank)
        const rdftById = {};
        (Array.isArray(fcRdft)?fcRdft:[]).forEach(v=>{
          const sid = v.player?.sleeperId||v.player?.maybeSleeperId;
          if(sid) rdftById[String(sid)] = {val:v.value||0, posRank:v.positionRank||null};
        });
        // Merge: use dynasty entries but override redraftValue + store redraft position rank
        const merged = (Array.isArray(fcDyn)?fcDyn:[]).map(v=>{
          const sid = v.player?.sleeperId||v.player?.maybeSleeperId;
          const rm  = sid ? rdftById[String(sid)] : null;
          return {
            ...v,
            redraftValue: rm ? rm.val : (v.redraftValue||0),
            redraftPositionRank: rm ? rm.posRank : null,
          };
        });
        setFcValues(merged);
        setLastFetched(new Date());
      } catch { setFcError(true); }
      setLoading('');
    } catch(e){ setError(e.message); setLoading(''); }
  };

  const refreshLeague = () => { if (league) selectLeague(league); };

  const fcMap = useMemo(()=>{
    const m={};
    fcValues.forEach(v=>{
      const sid = v.player?.sleeperId||v.player?.maybeSleeperId||v.player?.sleeperBotId;
      if(sid) m[String(sid)]=v;
    });
    return m;
  },[fcValues]);

  const fcPickMap = useMemo(()=>{
    // slotValues[round][slot] = value. Rounds and slots are 1-indexed.
    //   Source sleeperId: "DP_<roundIdx>_<slotIdx>" (both 0-based) for current-year rookie picks.
    const slotValues={};
    // futureValues["YYYY_R"] = generic pick value for future year YYYY, round R.
    //   Source sleeperId: "FP_YYYY_R".
    const futureValues={};
    fcValues.forEach(v=>{
      if(v.player?.position!=='PICK') return;
      const sid = v.player?.sleeperId||'';
      const val = v.value||0;
      const dpM = sid.match(/^DP_(\d+)_(\d+)$/);
      if(dpM){
        const round=Number(dpM[1])+1, slot=Number(dpM[2])+1;
        slotValues[round]=slotValues[round]||{};
        slotValues[round][slot]=val;
        return;
      }
      const fpM = sid.match(/^FP_(\d{4})_(\d+)$/);
      if(fpM){ futureValues[`${fpM[1]}_${fpM[2]}`]=val; }
    });
    return {slotValues, futureValues};
  },[fcValues]);

  const userMap = useMemo(()=>{
    const m={};
    users.forEach(u=>{ m[u.user_id]=u; });
    return m;
  },[users]);

  const rosterNameMap = useMemo(()=>{
    const m={};
    rosters.forEach(r=>{
      const o=userMap[r.owner_id];
      m[r.roster_id]=o?.metadata?.team_name||o?.display_name||`Team ${r.roster_id}`;
    });
    return m;
  },[rosters,userMap]);

  const ranked = useMemo(()=>{
    if(!rosters.length) return [];
    // Rookie draft year = current calendar year (drafts happen May/June; after August it's next year)
    const now=new Date(); const startYr=now.getFullYear()+(now.getMonth()>=8?1:0);
    const yrs=[startYr,startYr+1,startYr+2];
    // Build pick dynasty value per roster_id using FC slot/future values
    const pickDVal={};
    const {slotValues={},futureValues={}}=fcPickMap;
    const hasFcPicks=Object.keys(slotValues).length||Object.keys(futureValues).length;
    if(hasFcPicks){
      const own={};
      rosters.forEach(r=>{ yrs.forEach(y=>{ [1,2,3,4].forEach(rd=>{ own[`${y}_${rd}_${r.roster_id}`]=r.roster_id; }); }); });
      tradedPicks.forEach(tp=>{ const y=Number(tp.season); if(yrs.includes(y)) own[`${y}_${tp.round}_${tp.roster_id}`]=tp.owner_id; });
      const standingsOrder=[...rosters]
        .sort((a,b)=>(a.settings?.wins||0)-(b.settings?.wins||0)||(b.settings?.losses||0)-(a.settings?.losses||0)||a.roster_id-b.roster_id)
        .map(r=>r.roster_id);
      const avgRoundVal = rd => {
        const vals=Object.values(slotValues[rd]||{});
        return vals.length?Math.round(vals.reduce((s,v)=>s+v,0)/vals.length):0;
      };
      Object.entries(own).forEach(([key,ownRid])=>{
        const [yr,rd,origRid]=key.split('_').map(Number);
        if(rd>4) return;
        let val=0;
        if(yr===startYr){
          const slot = draftSlots[origRid] || (standingsOrder.indexOf(origRid)+1) || null;
          val = (slot && slotValues[rd]?.[slot]) || avgRoundVal(rd);
        } else {
          val = futureValues[`${yr}_${rd}`] || 0;
        }
        pickDVal[ownRid]=(pickDVal[ownRid]||0)+val;
      });
    }
    const rp=league?.roster_positions||[];
    const tepBonus=league?.scoring_settings?.bonus_rec_te||0;
    const teMult=1+tepBonus*0.30;
    const BENCH_MULT=valueMode==='starter'?0:1;
    const slots=[];
    rp.forEach(s=>{
      if(s==='QB'||s==='RB'||s==='WR'||s==='TE') slots.push({strict:true,elig:[s]});
      else if(s==='SUPER_FLEX') slots.push({strict:false,elig:['QB','RB','WR','TE']});
      else if(s==='FLEX') slots.push({strict:false,elig:['RB','WR','TE']});
      else if(s==='REC_FLEX') slots.push({strict:false,elig:['WR','TE']});
      else if(s==='WRRB_FLEX'||s==='RB_WR_FLEX') slots.push({strict:false,elig:['RB','WR']});
    });
    slots.sort((a,b)=>(a.strict?0:1)-(b.strict?0:1));
    const lineupValue=(playerIds,valKey)=>{
      const pool=playerIds.map(pid=>{
        const fc=fcMap[pid]; if(!fc) return null;
        const pos=fc.player?.position; const base=fc[valKey]||0;
        const adj=pos==='TE'?base*teMult:base;
        return {pid,pos,val:adj};
      }).filter(x=>x&&x.val>0).sort((a,b)=>b.val-a.val);
      const used=new Set(); let starter=0;
      slots.forEach(slot=>{
        const pick=pool.find(p=>!used.has(p.pid)&&slot.elig.includes(p.pos));
        if(pick){ used.add(pick.pid); starter+=pick.val; }
      });
      let bench=0; pool.forEach(p=>{ if(!used.has(p.pid)) bench+=p.val; });
      return Math.round(starter+bench*BENCH_MULT);
    };
    const stats=rosters.map(r=>{
      const active=r.players||[];
      const allP=[...active,...(r.taxi||[])];
      // Dynasty: starter-usable lineup + discounted bench from full roster, plus picks (full)
      const dVal=lineupValue(allP,'value')+(pickDVal[r.roster_id]||0);
      // Redraft: starter-usable lineup + discounted bench from active only
      const rVal=lineupValue(active,'redraftValue');
      const owner=userMap[r.owner_id];
      return {...r,dVal,rVal,teamName:owner?.metadata?.team_name||owner?.display_name||`Team ${r.roster_id}`,username:owner?.display_name||`Team ${r.roster_id}`,allPlayers:allP};
    });
    const byD=[...stats].sort((a,b)=>b.dVal-a.dVal);
    const byR=[...stats].sort((a,b)=>b.rVal-a.rVal);
    const n=stats.length;
    return byD.map((t,di)=>{
      const ri=byR.findIndex(x=>x.roster_id===t.roster_id);
      const dPct=Math.round((1-di/(n-1||1))*100);
      const rPct=Math.round((1-ri/(n-1||1))*100);
      return {...t,dRank:di+1,rRank:ri+1,dPct,rPct,arc:classifyTeam(dPct,rPct)};
    });
  },[rosters,fcMap,userMap,tradedPicks,fcPickMap,draftSlots,league,valueMode]);

  const myTeam = ranked.find(r=>r.owner_id===sleeperUser?.user_id);

  useEffect(()=>{
    if(myTeam) setAnalyzerRid(myTeam.roster_id);
    setSuggestionMode(null);
  },[league?.league_id, myTeam?.roster_id]);

  const posRanks = useMemo(()=>{
    if(!ranked.length) return {};
    const totals = {};
    ranked.forEach(t=>{
      const row={}; POS_ORDER.forEach(p=>row[p]={d:0,r:0});
      t.allPlayers.forEach(pid=>{
        const fc=fcMap[pid]; const pos=fc?.player?.position;
        if(pos&&row[pos]){ row[pos].d+=fc.value||0; row[pos].r+=fc.redraftValue||0; }
      });
      totals[t.roster_id]=row;
    });
    const rankMap={};
    ranked.forEach(t=>{ rankMap[t.roster_id]={}; POS_ORDER.forEach(p=>rankMap[t.roster_id][p]={}); });
    POS_ORDER.forEach(p=>{
      const byD=[...ranked].sort((a,b)=>totals[b.roster_id][p].d-totals[a.roster_id][p].d);
      const byR=[...ranked].sort((a,b)=>totals[b.roster_id][p].r-totals[a.roster_id][p].r);
      byD.forEach((t,i)=>{ rankMap[t.roster_id][p].dRank=i+1; });
      byR.forEach((t,i)=>{ rankMap[t.roster_id][p].rRank=i+1; });
    });
    return rankMap;
  },[ranked,fcMap]);

  const picksByRoster = useMemo(()=>{
    if(!rosters.length) return {};
    const now2=new Date(); const startYr=now2.getFullYear()+(now2.getMonth()>=8?1:0);
    const yrs=[startYr,startYr+1,startYr+2];
    const own={};
    rosters.forEach(r=>{ yrs.forEach(y=>{ [1,2,3,4].forEach(rd=>{ own[`${y}_${rd}_${r.roster_id}`]=r.roster_id; }); }); });
    tradedPicks.forEach(tp=>{
      const y=Number(tp.season);
      if(!yrs.includes(y)) return;
      own[`${y}_${tp.round}_${tp.roster_id}`]=tp.owner_id;
    });
    const n=rosters.length||1;
    const standingsOrder=[...rosters]
      .sort((a,b)=>(a.settings?.wins||0)-(b.settings?.wins||0)||(b.settings?.losses||0)-(a.settings?.losses||0)||a.roster_id-b.roster_id)
      .map(r=>r.roster_id);
    const byRid={};
    rosters.forEach(r=>{ byRid[r.roster_id]=[]; });
    Object.entries(own).forEach(([key,ownRid])=>{
      const [year,round,origRid]=key.split('_').map(Number);
      const isCurrent=year===startYr;
      const slotNum=isCurrent?(draftSlots[origRid]||standingsOrder.indexOf(origRid)+1||null):null;
      const slotStr=slotNum?String(slotNum).padStart(2,'0'):null;
      const standPos=standingsOrder.indexOf(origRid)+1||Math.ceil(n/2);
      const tier=standPos<=Math.ceil(n/3)?'early':standPos<=Math.ceil(2*n/3)?'mid':'late';
      (byRid[ownRid]=byRid[ownRid]||[]).push({year,round,origRid,isOwn:origRid===ownRid,isCurrent,slotNum,slotStr,tier});
    });
    Object.values(byRid).forEach(arr=>arr.sort((a,b)=>a.year-b.year||a.round-b.round||(a.slotNum||99)-(b.slotNum||99)));
    return byRid;
  },[rosters,tradedPicks,draftSlots]);

  useEffect(()=>{
    if(ranked.length&&sleeperUser){
      const slim=ranked.map(({roster_id,owner_id,teamName,dVal,rVal,dRank,rRank,dPct,rPct,arc})=>({roster_id,owner_id,teamName,dVal,rVal,dRank,rRank,dPct,rPct,arc}));
      localStorage.setItem('pfk_ranked',JSON.stringify(slim));
      localStorage.setItem('pfk_league_name',league?.name||'');
    }
  },[ranked]);

  // ── Team Analyzer data builder ──
  const analyzeTeam = (team) => {
    if(!team) return null;
    const hasFc = fcValues.length>0;
    // Build per-position lists with tier info
    const byPos = {QB:[],RB:[],WR:[],TE:[]};
    team.allPlayers.forEach(pid=>{
      const fc=fcMap[pid]; if(!fc) return;
      const pos=fc.player?.position; if(!byPos[pos]) return;
      byPos[pos].push({
        pid, name:fc.player?.name||pid,
        age:fc.player?.maybeAge??null,
        value:fc.value||0,
        dynPosRank: fc.positionRank||null,
        rdftPosRank: fc.redraftPositionRank||null,
        isElite: (fc.positionRank&&fc.positionRank<=10)&&(fc.redraftPositionRank&&fc.redraftPositionRank<=10),
        tier:posTier(fc.positionRank)
      });
    });
    POS_ORDER.forEach(p=>byPos[p].sort((a,b)=>b.value-a.value));
    // Tier buckets per position for health grade
    const buckets = {};
    const grades = {};
    POS_ORDER.forEach(p=>{
      const b={Elite:0,T1:0,T2:0,Flex:0,Depth:0};
      byPos[p].forEach(x=>{ if(x.tier) b[x.tier.key]++; });
      buckets[p]=b;
      grades[p]=healthGrade(p,b);
    });
    // Starter lineup (re-run slot fill to get the starting ~9 players and their avg age)
    const rp=league?.roster_positions||[];
    const tepBonus=league?.scoring_settings?.bonus_rec_te||0;
    const teMult=1+tepBonus*0.30;
    const slots=[];
    rp.forEach(s=>{
      if(s==='QB'||s==='RB'||s==='WR'||s==='TE') slots.push({strict:true,elig:[s]});
      else if(s==='SUPER_FLEX') slots.push({strict:false,elig:['QB','RB','WR','TE']});
      else if(s==='FLEX') slots.push({strict:false,elig:['RB','WR','TE']});
      else if(s==='REC_FLEX') slots.push({strict:false,elig:['WR','TE']});
      else if(s==='WRRB_FLEX'||s==='RB_WR_FLEX') slots.push({strict:false,elig:['RB','WR']});
    });
    slots.sort((a,b)=>(a.strict?0:1)-(b.strict?0:1));
    const pool=team.allPlayers.map(pid=>{
      const fc=fcMap[pid]; if(!fc) return null;
      const pos=fc.player?.position;
      const base=fc.value||0;
      const adj=pos==='TE'?base*teMult:base;
      return {pid,pos,val:adj,age:fc.player?.maybeAge??null};
    }).filter(x=>x&&x.val>0).sort((a,b)=>b.val-a.val);
    const usedIds=new Set(); const starters=[];
    slots.forEach(slot=>{
      const pick=pool.find(p=>!usedIds.has(p.pid)&&slot.elig.includes(p.pos));
      if(pick){ usedIds.add(pick.pid); starters.push(pick); }
    });
    const agesWithValues=starters.filter(s=>s.age!=null);
    const avgAge = agesWithValues.length
      ? +(agesWithValues.reduce((s,x)=>s+x.age,0)/agesWithValues.length).toFixed(1)
      : null;
    const ageTierInfo = avgAge!=null ? ageTier(avgAge) : null;
    // Age-cliff red flags — per-position dyn rank cutoffs (WR/RB top 50, QB/TE top 28).
    // Sorted by dynasty value desc so biggest assets surface first.
    const AGE_FLAG_CUTOFF = { WR:50, RB:50, QB:28, TE:28 };
    const flags=[];
    POS_ORDER.forEach(p=>{
      byPos[p].forEach(x=>{
        if(x.age==null) return;
        const thr=AGE_RISK_AGE[p];
        if(!thr || x.age<thr) return;
        const cutoff = AGE_FLAG_CUTOFF[p];
        if(!x.dynPosRank || x.dynPosRank>cutoff) return;
        flags.push({...x, pos:p});
      });
    });
    flags.sort((a,b)=>b.value-a.value);
    // Pick capital
    const picks = picksByRoster[team.roster_id]||[];
    const now=new Date(); const startYr=now.getFullYear()+(now.getMonth()>=8?1:0);
    const futureFirsts = picks.filter(p=>p.round===1 && p.year>startYr).length;
    // Window assessment
    let windowText='';
    if(avgAge!=null){
      if(team.arc.label==='Dynasty') windowText='Built for sustained contention';
      else if(team.arc.label==='Aging Contender') windowText='Window closing fast — push now';
      else if(team.arc.label==='Future Dynasty') windowText='Young core still cooking';
      else if(team.arc.label==='Contender') windowText=avgAge>=27.5?'Contender window open but closing':'Contender with runway';
      else if(team.arc.label==='Win-Now') windowText='Win now, figure out tomorrow later';
      else if(team.arc.label==='Rebuilding') windowText='Stockpiling for the next push';
      else windowText='Full rebuild mode';
    }
    // Young talent detection for tanking advice
    const youngCore=[];
    POS_ORDER.forEach(p=>{
      byPos[p].forEach(x=>{
        if(x.age!=null && x.age<=24.5 && (x.tier?.key==='Elite'||x.tier?.key==='T1')){
          youngCore.push({...x, pos:p});
        }
      });
    });
    // CONTENDING suggestions
    const sugsContending=[];
    const isAging = team.arc.label==='Aging Contender';
    if(isAging){
      // Aging Contender: window is closing — don't push youth, push vets.
      // Identify likely-late future 1sts the team owns (their own picks where they're projected early in standings)
      const myFutureFirsts = picks.filter(p=>p.round===1 && p.year>startYr);
      sugsContending.push({type:'window',text:`Window is closing (avg age ${avgAge??'—'}). Don't chase youth — convert picks into proven win-now producers.`});
      if(myFutureFirsts.length>0)
        sugsContending.push({type:'trade',text:`Package future 1sts — especially any that project late in their year — for established starters at your weakest position.`});
      POS_ORDER.forEach(p=>{
        const g=grades[p];
        if(g==='Critical'||g==='Thin') sugsContending.push({type:'thin',text:`${p} is ${g.toLowerCase()} — target a proven veteran starter at ${p} using picks as currency.`});
      });
    } else {
      POS_ORDER.forEach(p=>{
        const g=grades[p], b=buckets[p];
        if(g==='Critical') sugsContending.push({type:'critical', text:`Your ${p} room is critical — 0 players inside the top 24. Prioritize a ${p} upgrade above all else.`});
        else if(g==='Thin') sugsContending.push({type:'thin', text:`${p} is thin — only ${b.Elite+b.T1+b.T2} ${p} inside the top 24. Consider consolidating depth into a ${p} upgrade.`});
        else if(b.Elite===0 && b.T1>=3 && (p==='WR'||p==='RB')){
          sugsContending.push({type:'consolidate', text:`You have ${b.T1}+ tier-1 ${p}s but no Elite — consider consolidating into a top-5 ${p}.`});
        }
      });
      const oldRBStarters = starters.filter(s=>s.pos==='RB' && s.age!=null && s.age>=AGE_RISK_AGE.RB).length;
      if(oldRBStarters>=2) sugsContending.push({type:'age',text:`${oldRBStarters} of your starting RBs are 29+ — target a young RB in the next rookie draft.`});
      const oldTEStarters = starters.filter(s=>s.pos==='TE' && s.age!=null && s.age>=AGE_RISK_AGE.TE).length;
      if(oldTEStarters>=1 && starters.filter(s=>s.pos==='TE').length>0) sugsContending.push({type:'age',text:`Your starting TE is 27+ — TE decline starts here. Look at young TEs if you're not already elite.`});
      if(avgAge!=null && avgAge>=28.5)
        sugsContending.push({type:'window',text:`Roster trends old (${avgAge} avg) — push now. Don't hoard picks while your window is open.`});
    }
    if(futureFirsts===0 && team.arc.label!=='Dynasty' && !isAging)
      sugsContending.push({type:'critical',text:`Zero future 1st-round picks. Red flag unless you're in true Dynasty status.`});
    if(sugsContending.length===0)
      sugsContending.push({type:'good',text:`Roster looks balanced for your arc. Stay patient and attack trade markets opportunistically.`});

    // TANKING suggestions
    const sugsTanking=[];
    // Old vets to move (defined up top so we can gate the 1.01 tip on sellable assets)
    const oldVets=[];
    POS_ORDER.forEach(p=>{
      byPos[p].forEach(x=>{
        if(x.age!=null && x.age>=AGE_RISK_AGE[p] && x.tier && ['Elite','T1','T2'].includes(x.tier.key)){
          oldVets.push({...x, pos:p});
        }
      });
    });
    // Only surface the 1.01 tidbit when moving a few vets would realistically flip the tank.
    // Gate: at least 2 sellable aging vets AND team isn't already a Full Rebuild (no one to sell there).
    const canFlipTank = oldVets.length>=2 && team.arc.label!=='Full Rebuild';
    if(canFlipTank)
      sugsTanking.push({type:'tank',text:`Selling a few vets could flip you into tank mode. The 1.01 is ~50% more valuable than the 1.02 — commit fully rather than land mid-draft.`});
    if(oldVets.length>0)
      sugsTanking.push({type:'tank',text:`Aging vets still holding value: ${oldVets.slice(0,4).map(v=>`${v.name} (${v.pos} ${v.age.toFixed(1)})`).join(', ')}${oldVets.length>4?'…':''}. Cash them in for picks while the market still pays.`});
    if(youngCore.length>0)
      sugsTanking.push({type:'build',text:`Young core to build around: ${youngCore.slice(0,4).map(v=>v.name).join(', ')}${youngCore.length>4?'…':''}. Don't trade these — they're your next contention window.`});
    POS_ORDER.forEach(p=>{
      const g=grades[p];
      if(g==='Critical') sugsTanking.push({type:'tank',text:`${p} is barren — that's fine for a tank. Target rookie ${p}s in the upcoming draft.`});
    });
    if(avgAge!=null && avgAge>=27.5)
      sugsTanking.push({type:'tank',text:`Roster is still old (${avgAge} avg). A true tank means turning these vets into youth + picks, not just losing.`});
    if(avgAge!=null && avgAge<=24.5 && youngCore.length>=3)
      sugsTanking.push({type:'build',text:`Young core is already strong (${avgAge} avg, ${youngCore.length} young studs). You may be closer to contending than you think — consider a 'soft tank' this year then push next.`});
    sugsTanking.push({type:'rule',text:`Do not trade future 1sts. Acquiring future 1sts is the best course of action.`});

    return {byPos, buckets, grades, starters, avgAge, ageTierInfo, flags, picks, futureFirsts, windowText, sugsContending, sugsTanking, youngCore, hasFc};
  };

  // ── Grouped roster renderer ──
  const RosterSection = ({ team }) => {
    const picks = picksByRoster[team?.roster_id]||[];
    if (!team) return null;
    const hasFc = fcValues.length > 0;
    const byPos = {};
    POS_ORDER.forEach(p=>{ byPos[p]=[]; });
    team.allPlayers.forEach(pid=>{
      const fc=fcMap[pid];
      const pos=fc?.player?.position;
      if(pos&&byPos[pos]) byPos[pos].push({pid,fc});
      else if(fc) byPos['WR']?.push({pid,fc}); // fallback for flex/unknown
    });
    // Sort each group by dynasty value desc (always — Evan wants best dynasty asset first)
    POS_ORDER.forEach(p=>{ byPos[p].sort((a,b)=>(b.fc?.value||0)-(a.fc?.value||0)); });

    const teamChamps = champCounts[team.owner_id]||0;

    return (
      <div>
        {/* Roster header */}
        <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:14,flexWrap:'wrap'}}>
          <span style={{fontSize:13,fontWeight:700,color:'var(--text-secondary)',letterSpacing:1}}>ROSTER · {team.allPlayers.length} PLAYERS</span>
          {teamChamps>0&&(
            <span style={{display:'flex',alignItems:'center',gap:5,padding:'3px 10px',background:'var(--bg-divider)',border:'1px solid var(--accent-gold)',borderRadius:20,fontSize:13,fontWeight:800,color:'var(--accent-gold-text)'}}>
              {'🏆'.repeat(Math.min(teamChamps,5))} {teamChamps} LEAGUE TITLE{teamChamps>1?'S':''}
            </span>
          )}
          {hasFc&&(
            <div style={{marginLeft:'auto',display:'flex',alignItems:'center',width:96,justifyContent:'flex-end'}}>
              <span style={{fontSize:11,color:'var(--accent-gold-text)',fontWeight:700,width:44,textAlign:'right',letterSpacing:0.5}}>DYN</span>
              <span style={{fontSize:11,color:'var(--info)',fontWeight:700,width:44,textAlign:'right',letterSpacing:0.5}}>RDFT</span>
            </div>
          )}
        </div>

        {/* Positions */}
        {POS_ORDER.filter(p=>byPos[p].length>0).map(pos=>{
          const group=byPos[pos];
          const posTotalD=group.reduce((s,{fc})=>s+(fc?.value||0),0);
          const posTotalR=group.reduce((s,{fc})=>s+(fc?.redraftValue||0),0);
          const col=POS_COLORS[pos]||'var(--text-muted)';
          return (
            <div key={pos} style={{marginBottom:16}}>
              <div style={{display:'flex',alignItems:'center',gap:8,padding:'5px 10px',background:'var(--bg-divider)',borderLeft:'3px solid '+col,borderRadius:'5px 5px 0 0',marginBottom:3}}>
                <span style={{fontWeight:900,color:col,fontSize:13,letterSpacing:1}}>{pos}</span>
                <span style={{fontSize:12,color:col}}>({group.length})</span>
                {hasFc&&posTotalD>0&&(()=>{
                  const pr=posRanks[team.roster_id]?.[pos];
                  const n=ranked.length;
                  if(!pr?.dRank) return null;
                  return (
                    <span style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
                      <span style={{fontSize:13,color:'var(--accent-gold-text)',fontWeight:900,letterSpacing:0.5}}>DYN #{pr.dRank}/{n}</span>
                      <span style={{fontSize:13,color:'var(--info)',fontWeight:900,letterSpacing:0.5}}>RDFT #{pr.rRank}/{n}</span>
                    </span>
                  );
                })()}
              </div>
              <div style={{display:'flex',flexDirection:'column',gap:2}}>
                {group.map(({pid,fc})=>{
                  const dv=fc?.value||0;
                  const rv=fc?.redraftValue||0;
                  const dyn = fc?.positionRank||null;
                  const rdft = fc?.redraftPositionRank||null;
                  // Per-position tier config:
                  //   WR/RB: Elite both top 5, Stud 6-15, Starter 16-24, Flex Play 25-36
                  //   QB:    Elite both top 5, Stud 6-12, Starter 13-20, QB3 21-28
                  //   TE:    Elite both top 5, Stud 6-10, Starter 11-15, no green
                  const tierCfg =
                    pos==='QB' ? {eliteMax:5, purple:12, blue:20, green:28, greenBadge:{icon:'📋',label:'QB3',color:'var(--success)'}} :
                    pos==='TE' ? {eliteMax:5, purple:10, blue:15, green:0,  greenBadge:null} :
                                 {eliteMax:5, purple:15, blue:24, green:36, greenBadge:{icon:'🔄',label:'FLEX PLAY',color:'var(--success)'}};
                  const elite = dyn && dyn<=tierCfg.eliteMax && rdft && rdft<=tierCfg.eliteMax;
                  let borderCol = 'var(--border-hairline)';
                  let bgCol = 'var(--bg-elevated)';
                  let tierBadge = null;
                  if (dyn===1) { borderCol = '#ff6a00'; bgCol = '#2a1400'; tierBadge = {icon:'🐐',label:'GOAT',color:'#ff6a00'}; }
                  else if (elite) { borderCol = 'var(--accent-gold)'; bgCol = 'var(--accent-gold-bg)'; tierBadge = {icon:'⭐',label:'ELITE',color:'var(--accent-gold-text)'}; }
                  else if (dyn && dyn<=tierCfg.purple) { borderCol = 'var(--accent-violet)'; tierBadge = {icon:'💎',label:'STUD',color:'var(--accent-violet)'}; }
                  else if (dyn && dyn<=tierCfg.blue)   { borderCol = 'var(--info)'; tierBadge = {icon:'💪',label:'STARTER',color:'var(--info)'}; }
                  else if (dyn && dyn<=tierCfg.green)  { borderCol = 'var(--success)'; tierBadge = tierCfg.greenBadge; }
                  const ageNum = fc?.player?.maybeAge ?? fc?.player?.age ?? null;
                  const age = ageNum!=null ? Number(ageNum).toFixed(1) : null;
                  const ageCliff = ageNum!=null && AGE_RISK_AGE[pos]!=null && ageNum >= AGE_RISK_AGE[pos];
                  // Prime age tops (peak end) per position — at/below = prime or younger
                  const PRIME_MAX = {RB:28, WR:26, TE:26, QB:30};
                  const agePrime = !ageCliff && ageNum!=null && PRIME_MAX[pos]!=null && ageNum <= PRIME_MAX[pos];
                  const nflTeam = fc?.player?.team || null;
                  return (
                    <div key={pid} style={{display:'flex',alignItems:'center',gap:10,padding:'6px 4px',minWidth:0}}>
                      <div style={{display:'inline-flex',alignItems:'center',gap:8,padding:'5px 10px',background:bgCol,border:`1px solid ${borderCol}`,borderRadius:7,minWidth:0,flex:'0 1 auto'}}>
                        {tierBadge && <span style={{fontSize:12,fontWeight:900,color:tierBadge.color,letterSpacing:1,flexShrink:0}}>{tierBadge.icon} {tierBadge.label}</span>}
                        <span style={{fontSize:13,fontWeight:700,color:fc?'var(--text-primary)':'var(--border-strong)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',minWidth:0}}>{fc?.player?.name||pid}</span>
                        {nflTeam && <span style={{fontSize:12,color:'var(--text-secondary)',flexShrink:0,fontWeight:700}}>{nflTeam}</span>}
                      </div>
                      {age && <span style={{fontSize:12,color:ageCliff?'var(--danger)':agePrime?'var(--success)':'var(--text-muted)',fontWeight:ageCliff||agePrime?900:600,flexShrink:0,whiteSpace:'nowrap'}}>{ageCliff?'🚩 ':''}Age: {age}</span>}
                      {hasFc&&(
                        <div style={{display:'flex',gap:8,flexShrink:0,alignItems:'center'}}>
                          <span style={{fontSize:12,color:'var(--accent-gold-text)',fontWeight:700}}>Dyn {pos}#{dyn||'NR'}</span>
                          <span style={{fontSize:12,color:'var(--info)',fontWeight:700}}>Rdft {pos}#{rdft||'NR'}</span>
                        </div>
                      )}
                      {hasFc&&(
                        <div style={{marginLeft:'auto',display:'flex',flexShrink:0,alignItems:'center',width:96,justifyContent:'flex-end'}}>
                          <span style={{fontSize:13,fontWeight:700,color:'var(--accent-gold-text)',width:44,textAlign:'right'}}>{dv>0?(dv/1000).toFixed(1)+'k':'—'}</span>
                          <span style={{fontSize:13,fontWeight:700,color:'var(--info)',width:44,textAlign:'right'}}>{rv>0?(rv/1000).toFixed(1)+'k':'—'}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Draft Capital */}
        {picks.length>0&&(
          <div style={{marginTop:4}}>
            <div style={{display:'flex',alignItems:'center',gap:8,padding:'5px 10px',background:'var(--bg-divider)',borderLeft:'3px solid var(--accent-gold)',borderRadius:'5px 5px 0 0',marginBottom:3}}>
              <span style={{fontWeight:900,color:'var(--accent-gold-text)',fontSize:13,letterSpacing:1}}>DRAFT CAPITAL</span>
              <span style={{fontSize:12,color:'var(--accent-gold-text)'}}>({picks.length} picks)</span>
            </div>
            {Object.entries(
              picks.reduce((acc,p)=>{ if(!acc[p.year])acc[p.year]=[]; acc[p.year].push(p); return acc; },{})
            ).map(([year,picks])=>(
              <div key={year}>
                <div style={{fontSize:12,color:'var(--text-secondary)',fontWeight:700,letterSpacing:1,padding:'8px 12px 4px',background:'var(--bg-elevated)',borderTop:'1px solid var(--border-hairline)'}}>{year} ROOKIE PICKS</div>
                {picks.map((p,i)=>{
                  const fromTeam = !p.isOwn ? rosterNameMap[p.origRid] : null;
                  const ord = ORDINALS[p.round-1]||`${p.round}th`;
                  const labelColor = p.round===1?'var(--accent-gold)':p.round===2?'var(--text-bbb)':p.round===3?'#8B6914':'var(--border-outline)';
                  const {slotValues={},futureValues={}}=fcPickMap;
                  let fcVal=0;
                  if(p.isCurrent){
                    fcVal = (p.slotNum && slotValues[p.round]?.[p.slotNum]) || 0;
                  } else {
                    fcVal = futureValues[`${p.year}_${p.round}`] || 0;
                  }
                  const label = p.slotStr ? `${p.round}.${p.slotStr}` : ord;
                  return (
                    <div key={i} style={{display:'flex',alignItems:'center',gap:8,padding:'8px 12px',background:'var(--bg-elevated)',border:'1px solid var(--border-hairline)',borderTop:'none',borderRadius:i===picks.length-1?'0 0 6px 6px':'0'}}>
                      <span style={{fontSize:14,fontWeight:900,color:labelColor,minWidth:36,flexShrink:0}}>{label}</span>
                      <span style={{flex:1,fontSize:13,color:'var(--text-secondary)'}}>{fromTeam?`via ${fromTeam}`:'Own pick'}</span>
                      <span style={{fontSize:13,fontWeight:700,color:labelColor,flexShrink:0}}>{fcVal>0?(fcVal/1000).toFixed(1)+'k':'—'}</span>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{display:'flex',flexDirection:'column',gap:20}}>

      {/* Connect Sleeper */}
      <div style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:12,padding:20}}>
        <div style={{fontSize:13,fontWeight:900,color:'var(--accent-gold-text)',marginBottom:14,textTransform:'uppercase',letterSpacing:1}}>🔗 Sleeper Account</div>
        <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
          <input value={username} onChange={e=>setUsername(e.target.value)} onKeyDown={e=>e.key==='Enter'&&connectUser()}
            placeholder="Sleeper username" style={inp2({flex:1,minWidth:160})}/>
          <button onClick={connectUser} disabled={!!loading}
            style={{padding:'7px 18px',background:'var(--accent-gold)',border:'none',borderRadius:7,color:'#000',fontWeight:900,cursor:'pointer',fontSize:13}}>
            {loading==='user'||loading==='leagues'?'...':'Load Teams'}
          </button>
          {sleeperUser&&<span style={{fontSize:14,color:'var(--success)',fontWeight:700}}>✓ {sleeperUser.display_name}</span>}
        </div>
        {error&&<div style={{marginTop:10,padding:'8px 12px',background:'var(--danger-bg)',border:'1px solid var(--danger)',borderRadius:7,fontSize:14,color:'var(--danger)'}}>{error}</div>}
      </div>

      {/* League Picker */}
      {sleeperUser&&leagues.length>0&&(
        <div style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:12,padding:20}}>
          <div style={{fontSize:13,fontWeight:900,color:'var(--accent-gold-text)',marginBottom:14,textTransform:'uppercase',letterSpacing:1}}>🏈 Your Leagues · 2025</div>
          <div style={{display:'flex',flexDirection:'column',gap:6}}>
            {leagues.map(lg=>{
              const active=league?.league_id===lg.league_id;
              const arc=leagueArcs[lg.league_id];
              return (
                <button key={lg.league_id} onClick={()=>selectLeague(lg)}
                  style={{display:'flex',alignItems:'center',gap:8,padding:'10px 14px',background:active?'var(--bg-divider)':'var(--bg-page)',border:'1px solid '+(active?'var(--accent-gold)':'var(--bg-pill)'),borderRadius:9,cursor:'pointer',textAlign:'left',width:'100%',flexWrap:'wrap'}}>
                  {arc&&<span style={{padding:'2px 8px',borderRadius:4,fontSize:12,fontWeight:800,background:'var(--bg-divider)',color:arc.color,border:'1px solid '+arc.color,flexShrink:0}}>{arc.emoji} {arc.label}</span>}
                  <span style={{fontWeight:700,fontSize:13,color:active?'var(--accent-gold)':'var(--text-primary)',flex:1,minWidth:120}}>{lg.name}</span>
                  <span style={{fontSize:13,color:'var(--text-secondary)'}}>{lg.total_rosters} teams</span>
                  {active&&<span style={{fontSize:13,color:'var(--accent-gold-text)',fontWeight:700}}>●</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {sleeperUser&&!loading&&leagues.length===0&&(
        <div style={{padding:'20px',textAlign:'center',color:'var(--text-secondary)',fontSize:13}}>No 2025 leagues found for this account.</div>
      )}

      {/* Loading */}
      {(loading==='data'||loading==='fc')&&(
        <div style={{padding:'24px',textAlign:'center',color:'var(--accent-gold-text)',fontSize:13,fontWeight:700,letterSpacing:1}}>
          {loading==='data'?'Loading roster data…':'Fetching player values…'}
        </div>
      )}

      {/* Pending-trade indicator */}
      {league && ranked.length>0 && pendingTrades>0 && (
        <a href={`https://sleeper.com/leagues/${league.league_id}/trade`} target="_blank" rel="noopener" style={{alignSelf:'flex-start',display:'inline-flex',alignItems:'center',gap:8,padding:'8px 14px',background:'var(--accent-gold-bg)',border:'1px solid var(--accent-gold)',borderRadius:8,fontSize:14,fontWeight:800,color:'var(--accent-gold-text)',textDecoration:'none',letterSpacing:0.5,animation:'pfk-pulse 1.8s ease-in-out infinite'}}>
          🔔 {pendingTrades} pending trade{pendingTrades>1?'s':''}
        </a>
      )}

      {/* League Settings bar */}
      {league && (()=>{
        const rp  = league.roster_positions||[];
        const ss  = league.scoring_settings||{};
        const isSF= rp.includes('SUPER_FLEX');
        const starters = rp.filter(s=>s!=='BN' && s!=='IR' && s!=='TAXI').length;
        const ppr  = ss.rec ?? 0;
        const tep  = ss.bonus_rec_te ?? 0;
        const pTD  = ss.pass_td ?? 4;
        const recFD= ss.bonus_rec_fd ?? 0;
        const rushFD = ss.bonus_rush_fd ?? 0;
        const ppc  = ss.rush_att ?? 0;
        const pprLabel = ppr>=1?'Full PPR':ppr>=0.5?'Half PPR':ppr>0?`${ppr} PPR`:'Standard';
        const pills = [
          {l:'Format', v:isSF?'Superflex':'1-QB'},
          {l:'Starters', v:String(starters)},
          {l:'Pass TD', v:`${pTD} pts`},
          {l:'PPR', v:pprLabel},
        ];
        if(tep>0)           pills.push({l:'TE Premium', v:`+${tep}`});
        if(ppc>0)           pills.push({l:'PPC',        v:`+${ppc}`});
        if(recFD>0||rushFD>0){
          const parts=[];
          if(recFD>0)  parts.push(`+${recFD} rec`);
          if(rushFD>0) parts.push(`+${rushFD} rush`);
          pills.push({l:'PPFD', v:parts.join(' / ')});
        }
        return (
          <div style={{background:'var(--bg-elevated)',border:'1px solid var(--border)',borderRadius:10,padding:'10px 14px'}}>
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8,flexWrap:'wrap'}}>
              <span style={{fontSize:12,fontWeight:800,color:'var(--text-secondary)',letterSpacing:2}}>LEAGUE SETTINGS</span>
              <span style={{fontSize:13,color:'var(--text-secondary)'}}>· {league.name}</span>
            </div>
            <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
              {pills.map(p=>(
                <div key={p.l} style={{background:'var(--bg-divider)',border:'1px solid var(--border)',borderRadius:7,padding:'6px 10px',display:'flex',flexDirection:'column',minWidth:92}}>
                  <span style={{fontSize:11,color:'var(--text-secondary)',fontWeight:700,letterSpacing:0.5}}>{p.l}</span>
                  <span style={{fontSize:14,fontWeight:800,color:'var(--text-primary)',marginTop:1}}>{p.v}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Selected Team Card — analyze any team in the league */}
      {(()=>{
        const selTeam = ranked.find(t=>t.roster_id===analyzerRid) || myTeam;
        if(!selTeam) return null;
        const aSel = analyzeTeam(selTeam);
        const avgAge = aSel?.avgAge;
        const ageTierInfo = aSel?.ageTierInfo;
        const isMine = selTeam.owner_id===sleeperUser?.user_id;
        return (
          <div style={{background:'var(--bg-card)',border:`2px solid ${selTeam.arc.color}`,borderRadius:14,padding:24}}>
            {/* Header */}
            <div style={{display:'flex',alignItems:'flex-start',gap:16,flexWrap:'wrap',marginBottom:18}}>
              <div style={{flex:1,minWidth:200}}>
                <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
                  <div style={{fontSize:13,color:'var(--text-secondary)',fontWeight:700,letterSpacing:2}}>{isMine?'YOUR TEAM':'ANALYZING'}</div>
                  <button onClick={refreshLeague} disabled={!!loading} style={{marginLeft:'auto',padding:'3px 10px',background:'var(--bg-divider)',border:'1px solid var(--border-strong)',borderRadius:6,color:loading?'var(--border-strong)':'var(--text-secondary)',cursor:loading?'default':'pointer',fontSize:12,fontWeight:700,letterSpacing:0.5}}>{loading?'LOADING…':'↻ REFRESH'}</button>
                </div>
                <select value={selTeam.roster_id} onChange={e=>{setAnalyzerRid(Number(e.target.value));setSuggestionMode(null);}} style={{background:'var(--bg-divider)',border:`1px solid ${selTeam.arc.color}`,borderRadius:7,color:'var(--text-primary)',padding:'8px 12px',fontSize:17,fontWeight:900,minWidth:220,maxWidth:'100%'}}>
                  {[...ranked].sort((a,b)=>a.dRank-b.dRank).map(t=>(
                    <option key={t.roster_id} value={t.roster_id}>
                      #{t.dRank} · {t.teamName}{t.owner_id===sleeperUser?.user_id?' ★':''}
                    </option>
                  ))}
                </select>
                <div style={{display:'flex',alignItems:'center',gap:6,marginTop:6,flexWrap:'wrap'}}>
                  <div style={{fontSize:13,color:'var(--text-secondary)'}}>{league?.name}</div>
                  {lastFetched&&<div style={{fontSize:12,color:'var(--text-secondary)'}}>· Updated {lastFetched.toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'})}</div>}
                </div>
              </div>
              <div style={{textAlign:'center',padding:'12px 18px',background:'var(--bg-divider)',border:`2px solid ${selTeam.arc.color}`,borderRadius:10,flexShrink:0}}>
                <div style={{fontSize:26}}>{selTeam.arc.emoji}</div>
                <div style={{fontSize:15,fontWeight:900,color:selTeam.arc.color,marginTop:2,letterSpacing:1}}>{selTeam.arc.label.toUpperCase()}</div>
                <div style={{fontSize:12,color:'var(--text-secondary)',marginTop:3,maxWidth:160}}>{selTeam.arc.desc}</div>
              </div>
              {avgAge!=null && ageTierInfo && (
                <div style={{textAlign:'center',padding:'12px 18px',background:'var(--bg-divider)',border:`2px solid ${ageTierInfo.color}`,borderRadius:10,flexShrink:0}}>
                  <div style={{fontSize:22,fontWeight:900,color:ageTierInfo.color}}>{avgAge}</div>
                  <div style={{fontSize:14,fontWeight:900,color:ageTierInfo.color,marginTop:3,letterSpacing:1}}>{ageTierInfo.label.toUpperCase()}</div>
                  <div style={{fontSize:12,color:'var(--text-secondary)',marginTop:3}}>team avg age</div>
                </div>
              )}
            </div>
            {/* Stat pills */}
            {fcValues.length>0&&(
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))',gap:8,marginBottom:20}}>
                {[
                  {l:'Dynasty Rank', v:`#${selTeam.dRank} / ${ranked.length}`, c:selTeam.arc.color},
                  {l:'Redraft Rank',  v:`#${selTeam.rRank} / ${ranked.length}`, c:selTeam.rRank<=ranked.length/3?'var(--success)':selTeam.rRank<=ranked.length*2/3?'var(--accent-gold)':'var(--danger)'},
                  {l:'Dynasty Value', v:(selTeam.dVal/1000).toFixed(1)+'k',     c:'var(--accent-gold)'},
                  {l:'Redraft Value', v:(selTeam.rVal/1000).toFixed(1)+'k',     c:'var(--info)'},
                ].map(({l,v,c})=>(
                  <div key={l} style={{background:'var(--bg-elevated)',borderRadius:8,padding:'10px 12px',border:'1px solid var(--border)',textAlign:'center'}}>
                    <div style={{fontSize:18,fontWeight:900,color:c}}>{v}</div>
                    <div style={{fontSize:12,fontWeight:600,color:'var(--text-secondary)',marginTop:2}}>{l}</div>
                  </div>
                ))}
              </div>
            )}
            {fcError&&<div style={{padding:'8px 12px',background:'var(--accent-gold-bg)',border:'1px solid var(--warning)',borderRadius:7,fontSize:13,color:'var(--warning)',marginBottom:14}}>⚠️ Player values unavailable — showing Sleeper roster only.</div>}
            <div className="pfk-wide-scroll"><RosterSection team={selTeam}/></div>
            {aSel && (()=>{
              const {flags, picks, futureFirsts, windowText, sugsContending, sugsTanking} = aSel;
              const contenderArcs=['Dynasty','Contender','Win-Now','Aging Contender','Future Dynasty'];
              const defaultMode = contenderArcs.includes(selTeam.arc.label) ? 'contending' : 'tanking';
              const mode = suggestionMode || defaultMode;
              const activeSugs = mode==='tanking' ? sugsTanking : sugsContending;
              const n = rosters.length || 12;
              return (
                <div style={{display:'flex',flexDirection:'column',gap:16,marginTop:20}}>
                  <div style={{background:'var(--bg-elevated)',border:'1px solid var(--border)',borderRadius:12,padding:20}}>
                    <div style={{fontSize:14,fontWeight:900,color:'var(--accent-gold-text)',letterSpacing:1,marginBottom:14,textTransform:'uppercase'}}>Age Cliff Red Flags</div>
                    {flags.length===0 && <div style={{fontSize:13,color:'var(--success)'}}>✓ No startable players past their cliff threshold.</div>}
                    {flags.length>0 && (
                      <div style={{display:'flex',flexDirection:'column',gap:5}}>
                        {flags.map(f=>(
                          <div key={f.pid} style={{display:'flex',alignItems:'center',gap:12,padding:'9px 14px',background:'var(--bg-card)',border:'1px solid #2a1a00',borderRadius:7}}>
                            <span style={{fontSize:16,lineHeight:1}}>🚩</span>
                            <span style={{fontSize:14,fontWeight:700,color:'var(--text-primary)',flex:1}}>{f.name}</span>
                            <span style={{fontSize:14,fontWeight:900,color:'var(--warning)',letterSpacing:0.5}}>{f.pos} · {f.age.toFixed(1)}y</span>
                            <span style={{fontSize:13,color:'var(--text-secondary)'}}>Dyn {f.pos}#{f.dynPosRank||'NR'}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{background:'var(--bg-elevated)',border:'1px solid var(--border)',borderRadius:12,padding:20}}>
                    <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:14,flexWrap:'wrap'}}>
                      <span style={{fontSize:14,fontWeight:900,color:'var(--accent-gold-text)',letterSpacing:1,textTransform:'uppercase'}}>Team-Building Suggestions</span>
                      <div style={{display:'flex',gap:4,marginLeft:'auto',background:'var(--bg-card)',border:'1px solid var(--bg-pill)',borderRadius:8,padding:3}}>
                        {[['contending','🥇 Contending'],['tanking','🏗️ Tanking']].map(([k,l])=>(
                          <button key={k} onClick={()=>setSuggestionMode(k)} style={{padding:'6px 12px',background:mode===k?'var(--accent-gold)':'transparent',color:mode===k?'#000':'var(--text-muted)',border:'none',borderRadius:5,cursor:'pointer',fontSize:14,fontWeight:800,letterSpacing:0.5}}>{l}</button>
                        ))}
                      </div>
                    </div>
                    <div style={{display:'flex',flexDirection:'column',gap:7}}>
                      {activeSugs.map((s,i)=>{
                        const col = s.type==='critical' ? 'var(--danger)' : s.type==='thin' ? 'var(--warning)' : s.type==='age' ? 'var(--warning-strong)' : s.type==='window' ? 'var(--info)' : s.type==='tank' ? 'var(--accent-violet)' : s.type==='build' ? 'var(--success)' : s.type==='rule' ? 'var(--text-muted)' : 'var(--accent-gold)';
                        return (
                          <div key={i} style={{display:'flex',gap:12,padding:'11px 14px',background:'var(--bg-card)',borderLeft:`3px solid ${col}`,borderRadius:'0 7px 7px 0'}}>
                            <span style={{fontSize:14,color:'var(--text-primary)',lineHeight:1.45}}>{s.text}</span>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{fontSize:13,color:'var(--text-secondary)',marginTop:12}}>Broad guidance — no specific trade offers. Never suggests trading future picks away.</div>
                  </div>
                </div>
              );
            })()}
          </div>
        );
      })()}

      {/* League Standings + Championship History — side-by-side on desktop */}
      <div className="pfk-pr-grid">
      {ranked.length>0&&(
        <div style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:12,padding:20}}>
          <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:16,flexWrap:'wrap'}}>
            <span style={{fontSize:13,fontWeight:900,color:'var(--accent-gold-text)',textTransform:'uppercase',letterSpacing:1}}>📊 League Standings</span>
            <div style={{display:'flex',gap:6,marginLeft:'auto',alignItems:'center',flexWrap:'wrap'}}>
              <div style={{display:'flex',gap:2,background:'var(--bg-elevated)',border:'1px solid var(--bg-pill)',borderRadius:6,padding:2}} title="Starter = only usable lineup slots count · Total = all roster players count">
                {['starter','total'].map(m=>(
                  <button key={m} onClick={()=>setValueMode(m)} style={{padding:'4px 8px',background:valueMode===m?'#22c55e':'transparent',color:valueMode===m?'#000':'var(--text-muted)',border:'none',borderRadius:4,cursor:'pointer',fontSize:12,fontWeight:800,letterSpacing:1,textTransform:'uppercase'}}>{m}</button>
                ))}
              </div>
              <div style={{display:'flex',gap:2,background:'var(--bg-elevated)',border:'1px solid var(--bg-pill)',borderRadius:6,padding:2}}>
                {['dynasty','redraft'].map(m=>(
                  <button key={m} onClick={()=>setViewMode(m)} style={{padding:'4px 10px',background:viewMode===m?(m==='dynasty'?'var(--accent-gold)':'var(--info)'):'transparent',color:viewMode===m?'#000':'var(--text-muted)',border:'none',borderRadius:4,cursor:'pointer',fontSize:12,fontWeight:800,letterSpacing:1,textTransform:'uppercase'}}>{m}</button>
                ))}
              </div>
            </div>
          </div>
          {fcError&&<div style={{padding:'8px 12px',background:'var(--accent-gold-bg)',border:'1px solid var(--warning)',borderRadius:7,fontSize:13,color:'var(--warning)',marginBottom:12}}>⚠️ Player values unavailable — showing Sleeper rosters only.</div>}
          <div style={{display:'flex',flexDirection:'column',gap:5}}>
            {[...ranked].sort((a,b)=>viewMode==='redraft'?a.rRank-b.rRank:a.dRank-b.dRank).map((t,i)=>{
              const isMe=t.owner_id===sleeperUser?.user_id;
              const rings=champCounts[t.owner_id]||0;
              const isSelected=selectedOtherRid===t.roster_id;
              const clickable=!isMe;
              return (
                <div key={t.roster_id} className="pfk-standings-row" onClick={clickable?()=>setSelectedOtherRid(isSelected?null:t.roster_id):undefined} style={{display:'flex',flexDirection:'column',gap:6,padding:'10px 14px',background:isSelected?'#1a1a0a':isMe?'var(--bg-divider)':'var(--bg-elevated)',border:'1px solid '+(isSelected?'var(--accent-gold)':isMe?t.arc.color:'var(--border)'),borderRadius:9,cursor:clickable?'pointer':'default'}}>
                  <div style={{display:'flex',alignItems:'center',gap:8}}>
                    <span style={{fontSize:13,fontWeight:900,color:i<3?'var(--accent-gold)':'var(--border-outline)',width:28,flexShrink:0,textAlign:'center'}}>#{i+1}</span>
                    <span style={{flex:1,fontSize:14,fontWeight:isMe?900:700,color:isMe?'var(--accent-gold)':'var(--text-primary)',minWidth:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{t.username}{isMe?' ★':''}</span>
                  </div>
                  <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                    <span style={{padding:'2px 8px',fontSize:12,fontWeight:800,borderRadius:4,background:'var(--bg-divider)',color:t.arc.color,border:'1px solid '+t.arc.color,flexShrink:0,whiteSpace:'nowrap'}}>{t.arc.emoji} {t.arc.label}</span>
                    {rings>0&&<span style={{fontSize:14,flexShrink:0,letterSpacing:1}} title={`${rings} league title${rings>1?'s':''}`}>{'🏆'.repeat(Math.min(rings,5))}</span>}
                    {fcValues.length>0&&(
                      <>
                        <span style={{fontSize:13,color:'var(--accent-gold-text)',fontWeight:700}}>D {(t.dVal/1000).toFixed(1)}k</span>
                        <span style={{fontSize:13,color:'var(--info)',fontWeight:700}}>R {(t.rVal/1000).toFixed(1)}k</span>
                      </>
                    )}
                    <span style={{marginLeft:'auto',fontSize:12,color:'var(--text-secondary)',fontWeight:700,flexShrink:0}}>#{i+1} of {ranked.length}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Championship History */}
      {championships.length>0&&(
        <div style={{background:'var(--bg-card)',border:'2px solid var(--accent-gold)',borderRadius:14,padding:20}}>
          <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:18,flexWrap:'wrap'}}>
            <span style={{fontSize:13,fontWeight:900,color:'var(--accent-gold-text)',textTransform:'uppercase',letterSpacing:1}}>🏆 Championship History</span>
            <span style={{fontSize:13,color:'var(--text-secondary)'}}>{championships.length} season{championships.length!==1?'s':''} of data</span>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:6}}>
            {championships.map((c,i)=>{
              const isMe=c.ownerId===sleeperUser?.user_id;
              const rings=champCounts[c.ownerId]||1;
              const isDefending=i===0;
              return(
                <div key={c.year+c.ownerId} style={{display:'flex',alignItems:'center',gap:12,padding:'12px 16px',background:isMe?'var(--bg-divider)':'var(--bg-elevated)',border:'1px solid '+(isMe?'var(--accent-gold)':'var(--border)'),borderRadius:10}}>
                  <span style={{fontSize:13,fontWeight:900,color:'var(--accent-gold-text)',width:40,flexShrink:0}}>{c.year}</span>
                  <span style={{fontSize:24,flexShrink:0}}>🏆</span>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:14,fontWeight:800,color:isMe?'var(--accent-gold)':'var(--text-primary)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                      {c.teamName}{isMe?' ★':''}
                    </div>
                    {c.ownerName&&c.ownerName!==c.teamName&&(
                      <div style={{fontSize:13,color:'var(--text-secondary)',marginTop:1}}>{c.ownerName}</div>
                    )}
                  </div>
                  <div style={{display:'flex',gap:6,flexShrink:0,alignItems:'center'}}>
                    {rings>1&&<span style={{padding:'2px 8px',background:'var(--bg-divider)',border:'1px solid var(--accent-gold)',borderRadius:10,fontSize:12,fontWeight:800,color:'var(--accent-gold-text)'}}>{rings}× Champ</span>}
                    {isDefending&&<span style={{padding:'2px 9px',background:'var(--accent-gold)',borderRadius:10,fontSize:12,fontWeight:900,color:'#000'}}>DEFENDING</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      </div>{/* end pfk-pr-grid */}

      {/* Other team roster (click-to-view — toggles off on second click) */}
      {(()=>{
        const other = ranked.find(t=>t.roster_id===selectedOtherRid);
        if(!other || other.owner_id===sleeperUser?.user_id) return null;
        return (
          <div style={{background:'var(--bg-card)',border:`2px solid ${other.arc.color}`,borderRadius:14,padding:24}}>
            <div style={{display:'flex',alignItems:'flex-start',gap:16,flexWrap:'wrap',marginBottom:18}}>
              <div style={{flex:1,minWidth:180}}>
                <div style={{fontSize:13,color:'var(--text-secondary)',fontWeight:700,letterSpacing:2,marginBottom:4}}>VIEWING</div>
                <div style={{fontSize:20,fontWeight:900,color:'var(--text-primary)'}}>{other.teamName}</div>
                <div style={{display:'flex',gap:10,marginTop:4,flexWrap:'wrap'}}>
                  <span style={{fontSize:13,color:'var(--accent-gold-text)',fontWeight:700}}>Dyn #{other.dRank} · {(other.dVal/1000).toFixed(1)}k</span>
                  <span style={{fontSize:13,color:'var(--info)',fontWeight:700}}>Rdft #{other.rRank} · {(other.rVal/1000).toFixed(1)}k</span>
                </div>
              </div>
              <div style={{textAlign:'center',padding:'12px 18px',background:'var(--bg-divider)',border:`2px solid ${other.arc.color}`,borderRadius:10,flexShrink:0}}>
                <div style={{fontSize:26}}>{other.arc.emoji}</div>
                <div style={{fontSize:15,fontWeight:900,color:other.arc.color,marginTop:2,letterSpacing:1}}>{other.arc.label.toUpperCase()}</div>
                <div style={{fontSize:12,color:'var(--text-secondary)',marginTop:3,maxWidth:160}}>{other.arc.desc}</div>
              </div>
              <button onClick={()=>setSelectedOtherRid(null)} style={{padding:'6px 12px',background:'transparent',border:'1px solid var(--border-strong)',borderRadius:6,color:'var(--text-secondary)',cursor:'pointer',fontSize:13,fontWeight:700}}>✕ Close</button>
            </div>
            <div className="pfk-wide-scroll"><RosterSection team={other}/></div>
          </div>
        );
      })()}

      {sleeperUser&&!league&&!loading&&leagues.length>0&&(
        <div style={{padding:'30px',textAlign:'center',color:'var(--text-secondary)',fontSize:13}}>← Select a league above to see your team analysis</div>
      )}


    </div>
  );
}

function RenderList({src,allowEdit,autoTier,lockPlayers,lockReorder,onReorder,onMove,onEdit,onRemove,onRenameStart,onRenameCancel,onRenameSave,onDeleteTier,renamingTier,tierNameDraft,setTierNameDraft,editingPlayer,playerDraft,setPlayerDraft,onSavePlayer,onCancelEdit,posFilter,prospects,modelByName,pfkSettings,setRankingScoreOverride,showRankingScore,masterListForRS}){
  // autoTier defaults to !allowEdit. Pass autoTier={true} explicitly with allowEdit={true}
  // to get edit-capable rows that still group by PFK tiers (admin rankings preview).
  if(autoTier===undefined) autoTier = !allowEdit;
  // Drag-drop reorder is only meaningful when not auto-tiering.
  const allowDrag = allowEdit && !autoTier;
  // lockPlayers: hide ALL player-level edit affordances (✏️, ✕, ▲▼, drag handle).
  // Used in admin rankings (Option A): player roster comes from model tab.
  // Tier-level controls stay live.
  const allowPlayerEdit = allowEdit && !lockPlayers;
  // lockReorder: hide drag handles + ▲▼ but keep edit/remove. Used by the custom-tab
  // sort dropdown so users can still add/remove players while a sort is active.
  const allowPlayerDrag = allowDrag && !lockPlayers && !lockReorder;
  // Hide PFK Score on prod (main) — visible only on dev URL. Keeps the score private during
  // editing/preview but doesn't expose it to public prod visitors.
  const hidePfk = !isDevHost();
  const rowRefs = useRef({});
  const [draggingId,setDraggingId] = useState(null);
  const [insertBefore,setInsertBefore] = useState(null);
  const [ghostPos,setGhostPos] = useState({x:0,y:0});
  const [ghostOff,setGhostOff] = useState({x:0,y:0});
  const normName = s=>(s||'').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g,'').replace(/[^a-z0-9]/g,'');
  const hoverCapable = useMemo(()=>typeof window!=='undefined'&&window.matchMedia&&window.matchMedia('(hover:hover)').matches,[]);
  const [popover,setPopover] = useState(null);
  // Compute the PFK model breakdown for a player (or null if no model entry).
  // Applies the per-combo multiplier (FORMAT × TEP × PTD) to the baseline PFK so the
  // public site's score reshuffles when settings chips change.
  // Per-player per-combo override: if m.comboOverrides[settingsSig] is set, use it directly.
  const modelBreakdown = (item) => {
    const m = modelByName && modelByName[normName(item.name)];
    if(!m) return null;
    const pos = m.pos || item.pos;
    const dc = m.dcOverride!=null ? +m.dcOverride : dcScoreFromPick(pos, m.pick);
    const film = filmAvgPct(m.filmScores);
    const base = pfkScore(m.stats, dc, film);
    const baseline = Math.round(base * landingMultiplier(m.landing) * 10) / 10;
    const sig = settingsSig(pfkSettings);
    const override = m.comboOverrides?.[sig];
    const pfk = override!=null && !isNaN(+override)
      ? Math.round(+override * 10) / 10
      : Math.round(baseline * comboMultiplier(pos, pfkSettings) * 10) / 10;
    return { name:item.name, stats:m.stats, dc, film, landing:m.landing, pfk, baseline, isOverride: override!=null };
  };
  // Auto-tier views: dynamic per-class auto-tiering by PFK gaps + position targets.
  // Tier names come from src in order; players bucket by autoTierBoundaries; empty tiers hidden.
  // Manual views (autoTier=false) keep the user's manual ordering & tiers.
  const sortedSrc = useMemo(()=>{
    if(!autoTier) return src;
    const tierItems = src.filter(x=>x.type==='tier').slice(0, PFK_TIER_TARGETS.length + 1);
    if(!tierItems.length) return src;
    const players = src.filter(x=>x.type!=='tier').slice();
    players.sort((a,b)=>{
      const ma=modelBreakdown(a), mb=modelBreakdown(b);
      const pa = ma && ma.pfk!=null ? ma.pfk : -1;
      const pb = mb && mb.pfk!=null ? mb.pfk : -1;
      return pb - pa;
    });
    const scores = players.map(p=>{ const m=modelBreakdown(p); return m && m.pfk!=null ? m.pfk : 0; });
    const boundaries = autoTierBoundaries(scores, tierItems.length);
    const buckets = tierItems.map(()=>[]);
    let cur = 0;
    for(let i = 0; i < tierItems.length; i++){
      const end = (i < boundaries.length) ? boundaries[i] : players.length;
      for(let j = cur; j < end && j < players.length; j++){
        buckets[i].push(players[j]);
      }
      cur = end;
    }
    const out = [];
    for(let i = 0; i < tierItems.length; i++){
      if(buckets[i].length){
        out.push(tierItems[i]);
        out.push(...buckets[i]);
      }
    }
    return out;
  },[src,autoTier,modelByName]);
  // Position filter, then drop tier headers that have no players following (per filter).
  // EXCEPT in edit mode — there we keep empty tiers visible so users can drop players into
  // them (otherwise clicking "+ Tier" looks broken because the new empty tier disappears).
  const flRaw = posFilter.size>=4 ? sortedSrc : sortedSrc.filter(x=>x.type==="tier"||posFilter.has(x.pos));
  const fl = (()=>{
    const out=[];
    for(let i=0; i<flRaw.length; i++){
      const x = flRaw[i];
      if(x.type==='tier'){
        const next = flRaw[i+1];
        if(allowEdit || (next && next.type!=='tier')) out.push(x);
      } else {
        out.push(x);
      }
    }
    return out;
  })();
  const showProspect = (e,item,prospect)=>{
    const r=e.currentTarget.getBoundingClientRect();
    const vw=window.innerWidth, vh=window.innerHeight;
    const W=240;
    const x=Math.max(8,Math.min(r.left, vw-W-8));
    // Estimated popover height: 180 photo + 240 text/sections + 24 padding ≈ 440.
    // Old 300 estimate was too low — flip-above wasn't triggering and the box got cut off.
    const H = 440;
    let y=r.bottom+6;
    if(y+H>vh) y=Math.max(8,r.top-H-6);
    setPopover({id:item.id, item, prospect, x, y});
  };
  const hideProspect = ()=>setPopover(null);

  useEffect(()=>{
    if(!popover) return;
    const close=()=>setPopover(null);
    window.addEventListener('scroll',close,true);
    let onDoc;
    if(!hoverCapable){
      onDoc=()=>setPopover(null);
      setTimeout(()=>document.addEventListener('click',onDoc),0);
    }
    return ()=>{
      window.removeEventListener('scroll',close,true);
      if(onDoc) document.removeEventListener('click',onDoc);
    };
  },[popover,hoverCapable]);

  useEffect(()=>{
    if(draggingId) document.body.classList.add('pfk-dragging');
    else document.body.classList.remove('pfk-dragging');
    return ()=>document.body.classList.remove('pfk-dragging');
  },[draggingId]);

  const calcInsert = useCallback((clientY,excludeId)=>{
    const rows=Object.entries(rowRefs.current)
      .filter(([id,el])=>el&&id!==excludeId)
      .map(([id,el])=>({id,mid:el.getBoundingClientRect().top+el.getBoundingClientRect().height/2}))
      .sort((a,b)=>a.mid-b.mid);
    for(const {id,mid} of rows){ if(clientY<mid)return id; }
    return null;
  },[]);

  // dragRef tracks pending/active drag without stale closure issues
  const dragRef = useRef({pending:null, dragging:null, insertBefore:null});

  const onPD=(e,id)=>{
    if(e.pointerType==="mouse"&&e.button!==0) return;
    const r=e.currentTarget.getBoundingClientRect();
    dragRef.current.pending={id, pointerId:e.pointerId, el:e.currentTarget,
      startX:e.clientX, startY:e.clientY,
      offX:e.clientX-r.left, offY:e.clientY-r.top};
  };

  const onPM=useCallback(e=>{
    const dr=dragRef.current;
    if(dr.dragging){
      // Already committed — update position
      const ib=calcInsert(e.clientY,dr.dragging);
      dr.insertBefore=ib;
      setGhostPos({x:e.clientX,y:e.clientY});
      setInsertBefore(ib);
      return;
    }
    if(!dr.pending) return;
    const dist=Math.hypot(e.clientX-dr.pending.startX, e.clientY-dr.pending.startY);
    if(dist<10) return; // not enough movement — let scroll happen
    // Commit to drag
    e.preventDefault();
    try{ dr.pending.el.setPointerCapture(dr.pending.pointerId); }catch{}
    setGhostOff({x:dr.pending.offX, y:dr.pending.offY});
    setGhostPos({x:e.clientX, y:e.clientY});
    dr.dragging=dr.pending.id;
    const ib=calcInsert(e.clientY,dr.pending.id);
    dr.insertBefore=ib;
    dr.pending=null;
    setDraggingId(dr.dragging);
    setInsertBefore(ib);
  },[calcInsert]);

  const onPU=useCallback(()=>{
    const dr=dragRef.current;
    if(dr.dragging) onReorder(dr.dragging,dr.insertBefore);
    dr.pending=null; dr.dragging=null; dr.insertBefore=null;
    setDraggingId(null); setInsertBefore(null);
  },[onReorder]);

  const draggingItem=draggingId?src.find(x=>x.id===draggingId):null;
  const DropLine=()=><div style={{height:3,background:"var(--accent-gold)",borderRadius:2,margin:"1px 2px",flexShrink:0}}/>;

  const renderGhost=()=>{
    if(!draggingItem)return null;
    const gs={position:"fixed",left:ghostPos.x-ghostOff.x,top:ghostPos.y-ghostOff.y,pointerEvents:"none",zIndex:9999,opacity:0.93,transform:"rotate(1.2deg) scale(1.03)",width:"min(620px,calc(100vw - 32px))",boxShadow:"0 4px 12px rgba(0,0,0,0.18)"};
    if(draggingItem.type==="tier"){
      const col=getTierColor(draggingItem.name,src);
      return <div style={{...gs,borderLeft:"5px solid "+col,padding:"6px 14px",background:"var(--bg-card)",borderRadius:6,border:"1px solid "+col}}><span style={{fontSize:18,fontWeight:900,color:col,textTransform:"uppercase",letterSpacing:2}}>{draggingItem.name}</span></div>;
    }
    const slot=slotLabel(getPlayerIndex(draggingItem.id,src)),col=getTierColor(getPlayerTier(draggingItem.id,src),src);
    return <div style={{...gs,background:"var(--bg-card)",border:"2px solid "+col,borderRadius:10,padding:"10px 14px"}}>
      <div style={{display:"flex",alignItems:"center",gap:10}}>
        <span style={{width:44,textAlign:"center",fontSize:13,fontWeight:800,color:slot==="FAAB"?"#e0a800":col}}>{slot}</span>
        <span style={{padding:"2px 7px",borderRadius:5,fontSize:12,fontWeight:800,background:`color-mix(in srgb, ${POS_COLORS[draggingItem.pos]} 13%, transparent)`,color:POS_COLORS[draggingItem.pos],border:`1px solid color-mix(in srgb, ${POS_COLORS[draggingItem.pos]} 27%, transparent)`}}>{draggingItem.pos}</span>
        <span style={{fontWeight:700,fontSize:14,flex:1}}>{draggingItem.name}</span>
        <span style={{fontSize:13,color:"var(--border-outline)"}}>{draggingItem.college}</span>
      </div>
    </div>;
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:4,userSelect:"none"}}>
      {renderGhost()}
      {fl.map((item,vi)=>{
        const isDrag=draggingId===item.id;
        const showLine=draggingId&&draggingId!==item.id&&insertBefore===item.id;
        if(item.type==="tier"){
          const col=getTierColor(item.name,src), isRen=renamingTier===item.id;
          return(
            <React.Fragment key={item.id}>
              {showLine&&<DropLine/>}
              <div ref={el=>rowRefs.current[item.id]=el}
                onPointerDown={allowEdit?e=>onPD(e,item.id):undefined}
                onPointerMove={allowEdit?onPM:undefined} onPointerUp={allowEdit?onPU:undefined} onPointerCancel={allowEdit?onPU:undefined}
                style={{display:"flex",alignItems:"center",gap:10,borderLeft:"5px solid "+col,marginTop:vi===0?0:14,marginBottom:4,cursor:allowEdit?"grab":"default",opacity:isDrag?0.25:1,background:"transparent",borderRadius:4,padding:"4px 4px 4px 12px"}}>
                {allowEdit&&<span style={{color:"var(--border-outline)",fontSize:16,touchAction:"none",flexShrink:0}}>⠿</span>}
                {isRen?(
                  <><input value={tierNameDraft} onChange={e=>setTierNameDraft(e.target.value)} onKeyDown={e=>e.key==="Enter"&&onRenameSave()} autoFocus
                    style={{fontSize:16,fontWeight:900,background:"var(--bg-card)",border:"1px solid "+col,borderRadius:6,color:col,padding:"4px 10px",width:180}}/>
                  <button onPointerDown={e=>e.stopPropagation()} onClick={onRenameSave} style={{padding:"4px 12px",background:"var(--accent-gold)",border:"none",borderRadius:6,color:"#000",fontWeight:900,cursor:"pointer",fontSize:14}}>Save</button>
                  <button onPointerDown={e=>e.stopPropagation()} onClick={onRenameCancel} style={{padding:"4px 8px",background:"transparent",border:"1px solid var(--border-strong)",borderRadius:6,color:"var(--text-faint)",cursor:"pointer",fontSize:14}}>✕</button></>
                ):(
                  <><span style={{fontSize:20,fontWeight:900,color:col,textTransform:"uppercase",letterSpacing:2,flex:1}}>{item.name}</span>
                  {allowEdit&&<>
                    <button onPointerDown={e=>e.stopPropagation()} onClick={()=>onRenameStart(item.id,item.name)} style={{background:"none",border:"1px solid var(--border-hairline)",borderRadius:5,color:"var(--border-outline)",cursor:"pointer",fontSize:13,padding:"2px 8px"}}>✏️</button>
                    <button onPointerDown={e=>e.stopPropagation()} onClick={()=>onDeleteTier(item.id)} style={{background:"none",border:"1px solid var(--border-hairline)",borderRadius:5,color:"var(--border-strong)",cursor:"pointer",fontSize:13,padding:"2px 8px"}}>🗑️</button>
                    {allowEdit&&<div style={{display:"flex",flexDirection:"column",gap:1}} title="Move tier up/down — controls which auto-tier bucket this name labels">
                      <button onPointerDown={e=>e.stopPropagation()} onClick={()=>onMove(item.id,-1)} style={{background:"none",border:"1px solid var(--border-hairline)",borderRadius:3,color:"var(--border-outline)",cursor:"pointer",fontSize:12,padding:"1px 5px"}}>▲</button>
                      <button onPointerDown={e=>e.stopPropagation()} onClick={()=>onMove(item.id,1)} style={{background:"none",border:"1px solid var(--border-hairline)",borderRadius:3,color:"var(--border-outline)",cursor:"pointer",fontSize:12,padding:"1px 5px"}}>▼</button>
                    </div>}
                  </>}</>
                )}
              </div>
            </React.Fragment>
          );
        }
        const pidx=getPlayerIndex(item.id,sortedSrc),slot=slotLabel(pidx),tname=getPlayerTier(item.id,sortedSrc),col=getTierColor(tname,src),isEd=editingPlayer===item.id;
        const draftInfo = DRAFT_2026[normDraftName(item.name)] || null;
        if(isEd)return(
          <React.Fragment key={item.id}>
            {showLine&&<DropLine/>}
            <div style={{background:"var(--bg-card)",border:"1px solid var(--accent-gold)",borderRadius:10,padding:"14px 16px"}}>
              <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"flex-end",marginBottom:10}}>
                <div style={{display:"flex",flexDirection:"column",gap:3,flex:2,minWidth:120}}>
                  <label style={{fontSize:12,color:"var(--text-muted)"}}>NAME</label>
                  <input value={playerDraft.name||""} onChange={e=>setPlayerDraft({...playerDraft,name:e.target.value})} style={{padding:"7px 10px",background:"var(--bg-card)",border:"1px solid var(--border-strong)",borderRadius:7,color:"var(--text-bright)",fontSize:14,width:"100%"}}/>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:3}}>
                  <label style={{fontSize:12,color:"var(--text-muted)"}}>POS</label>
                  <select value={playerDraft.pos||"WR"} onChange={e=>setPlayerDraft({...playerDraft,pos:e.target.value})} style={{padding:"7px 10px",background:"var(--bg-card)",border:"1px solid var(--border-strong)",borderRadius:7,color:"var(--text-bright)",fontSize:14}}>
                    {["WR","RB","TE","QB"].map(o=><option key={o}>{o}</option>)}
                  </select>
                </div>
                {[["college","COLLEGE"]].map(([k,l])=>(
                  <div key={k} style={{display:"flex",flexDirection:"column",gap:3}}>
                    <label style={{fontSize:12,color:"var(--text-muted)"}}>{l}</label>
                    <input value={playerDraft[k]||""} onChange={e=>setPlayerDraft({...playerDraft,[k]:e.target.value})} style={{padding:"7px 8px",background:"var(--bg-card)",border:"1px solid var(--border-strong)",borderRadius:7,color:"var(--text-bright)",fontSize:14,width:k==="college"?90:58}}/>
                  </div>
                ))}
              </div>
              <div style={{display:"flex",gap:8}}>
                <button onClick={onSavePlayer} style={{padding:"7px 18px",background:"var(--accent-gold)",border:"none",borderRadius:7,color:"#000",fontWeight:900,cursor:"pointer",fontSize:14}}>Save</button>
                <button onClick={onCancelEdit} style={{padding:"7px 12px",background:"transparent",border:"1px solid var(--border-strong)",borderRadius:7,color:"var(--text-muted)",cursor:"pointer",fontSize:14}}>Cancel</button>
              </div>
            </div>
          </React.Fragment>
        );
        return(
          <React.Fragment key={item.id}>
            {showLine&&<DropLine/>}
            <div ref={el=>rowRefs.current[item.id]=el} className="pfk-rookie-row"
              onPointerDown={allowPlayerDrag?e=>onPD(e,item.id):undefined}
              onPointerMove={allowPlayerDrag?onPM:undefined} onPointerUp={allowPlayerDrag?onPU:undefined} onPointerCancel={allowPlayerDrag?onPU:undefined}
              style={{background:"var(--bg-card)",border:"2px solid var(--border)",borderRadius:10,padding:"10px 14px",cursor:allowPlayerDrag?"grab":"default",opacity:isDrag?0.25:1,transition:"none"}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                {allowPlayerDrag&&<span style={{color:"var(--border-outline)",fontSize:18,flexShrink:0,touchAction:"none"}}>⠿</span>}
                <span className="pfk-rook-slot" style={{width:44,textAlign:"center",fontSize:13,fontWeight:800,flexShrink:0,color:slot==="FAAB"?"#e0a800":col,letterSpacing:0.5}}>{slot}</span>
                {(()=>{
                  const p=prospects&&prospects[normName(item.name)];
                  return <PlayerHeadshot src={p?.headshot} name={item.name} pos={item.pos} size={28}/>;
                })()}
                <span style={{padding:"2px 7px",borderRadius:5,fontSize:12,fontWeight:800,flexShrink:0,background:"var(--bg-divider)",color:POS_COLORS[item.pos],border:"1px solid "+POS_COLORS[item.pos]}}>{item.pos}</span>
                {(()=>{
                  const p=prospects&&prospects[normName(item.name)];
                  // Popover always triggerable now — every player has a position, which is enough for FF hit rate.
                  const und={textDecorationLine:'underline',textDecorationStyle:'dotted',textUnderlineOffset:3,textDecorationColor:'rgba(var(--accent-gold-rgb),0.53)',cursor:'help'};
                  const handlers=hoverCapable?{
                    onMouseEnter:e=>showProspect(e,item,p),
                    onMouseLeave:hideProspect
                  }:{
                    onClick:e=>{e.stopPropagation();if(popover&&popover.id===item.id)setPopover(null);else showProspect(e,item,p);},
                    onPointerDown:e=>e.stopPropagation()
                  };
                  return <span {...handlers} style={{fontWeight:700,fontSize:14,flexShrink:0,...und}}>{item.name}</span>;
                })()}
                <span className="pfk-rook-college" style={{fontSize:13,color:"var(--text-muted)",flexShrink:0,fontStyle:"italic"}}>{item.college}</span>
                {draftInfo&&(
                  <span style={{display:'inline-flex',alignItems:'center',gap:5,padding:'2px 7px',background:'var(--bg-elevated)',border:'1px solid rgba(var(--accent-gold-rgb),0.33)',borderRadius:5,flexShrink:0}}>
                    <img src={`https://a.espncdn.com/i/teamlogos/nfl/500/${draftInfo.team.toLowerCase()}.png`} alt={draftInfo.team} style={{width:16,height:16,objectFit:'contain'}} onError={e=>{e.currentTarget.style.display='none';}}/>
                    <span style={{fontSize:12,fontWeight:800,color:'var(--accent-gold-text)',letterSpacing:0.5}}>{draftInfo.team} · {draftInfo.pick}</span>
                  </span>
                )}
                <span style={{flex:1}}/>
                {showRankingScore && (()=>{
                  // RS reflects the player's place in the MASTER list, not the derived view.
                  // This keeps WR/RB scores stable when TEP/PTD reshuffle TE/QB positions.
                  const rsRef = masterListForRS || src;
                  const auto = getAutoRankingScore(item, rsRef);
                  const eff = getEffectiveRankingScore(item, rsRef);
                  const isOverride = item.rankingScoreOverride != null && !isNaN(+item.rankingScoreOverride);
                  const onClick = () => {
                    const cur = isOverride ? item.rankingScoreOverride : (auto ?? '');
                    const v = prompt(`Ranking Score for ${item.name}\n(Auto: ${auto ?? '—'}, blank to clear override)`, cur);
                    if(v === null) return;
                    setRankingScoreOverride && setRankingScoreOverride(item.id, v.trim());
                  };
                  const labelColor = isOverride ? 'var(--accent-violet)' : 'var(--text-muted)';
                  const valColor   = isOverride ? 'var(--accent-violet)' : (eff != null ? 'var(--text-secondary)' : 'var(--border-strong)');
                  return (
                    <span onClick={onClick} title={`Ranking Score · click to edit · ${isOverride ? 'MANUALLY OVERRIDDEN — auto would be '+(auto ?? '—') : 'auto-derived from tier+rank'}`} style={{display:'inline-flex',alignItems:'baseline',gap:5,padding:'3px 9px',borderRadius:6,background:'var(--bg-elevated)',border:'1px solid '+(isOverride ? 'var(--accent-violet)' : 'var(--bg-pill)'),flexShrink:0,marginRight:6,cursor:'pointer'}}>
                      <span style={{fontSize:10,fontWeight:800,color:labelColor,letterSpacing:1}}>RS</span>
                      <span style={{color:valColor,fontWeight:900,fontSize:14,letterSpacing:0.3,minWidth:30,textAlign:'right'}}>{eff != null ? eff : '—'}</span>
                      {isOverride && <button onClick={e=>{e.stopPropagation(); setRankingScoreOverride && setRankingScoreOverride(item.id,'');}} style={{marginLeft:2,background:'transparent',border:'none',color:'var(--text-secondary)',cursor:'pointer',fontSize:11,padding:0}} title="Clear override">×</button>}
                    </span>
                  );
                })()}
                {!hidePfk && (()=>{ const m=modelBreakdown(item); return (
                  <span title={m?`PFK Score: ${m.pfk}`:'No PFK Score available'} style={{display:'inline-flex',alignItems:'baseline',gap:5,padding:'3px 9px',borderRadius:6,background:'var(--bg-elevated)',border:'1px solid '+(m?'rgba(var(--accent-gold-rgb),0.4)':'var(--bg-pill)'),flexShrink:0,marginRight:6}}>
                    <span style={{fontSize:10,fontWeight:800,color:'var(--text-secondary)',letterSpacing:1}}>PFK SCORE</span>
                    <span style={{color:m?'var(--accent-gold)':'var(--border-strong)',fontWeight:900,fontSize:14,letterSpacing:0.3,minWidth:30,textAlign:'right'}}>{m?m.pfk:'—'}</span>
                  </span>
                ); })()}
                {allowPlayerDrag&&<div style={{display:"flex",flexDirection:"column",gap:2,flexShrink:0}}>
                  <button onPointerDown={e=>e.stopPropagation()} onClick={()=>onMove(item.id,-1)} style={{background:"none",border:"1px solid var(--border-hairline)",borderRadius:4,color:"var(--border-outline)",cursor:"pointer",fontSize:12,padding:"1px 6px"}}>▲</button>
                  <button onPointerDown={e=>e.stopPropagation()} onClick={()=>onMove(item.id,1)} style={{background:"none",border:"1px solid var(--border-hairline)",borderRadius:4,color:"var(--border-outline)",cursor:"pointer",fontSize:12,padding:"1px 6px"}}>▼</button>
                </div>}
                {allowPlayerEdit&&<>
                  <button onPointerDown={e=>e.stopPropagation()} onClick={()=>onEdit(item)} style={{background:"none",border:"1px solid var(--border-hairline)",borderRadius:6,color:"var(--border-outline)",cursor:"pointer",fontSize:14,padding:"4px 8px"}}>✏️</button>
                  <button onPointerDown={e=>e.stopPropagation()} onClick={()=>onRemove(item.id)} style={{background:"none",border:"1px solid var(--border-hairline)",borderRadius:6,color:"var(--border-strong)",cursor:"pointer",fontSize:14,padding:"4px 8px"}}>✕</button>
                </>}
              </div>
            </div>
          </React.Fragment>
        );
      })}
      {draggingId&&insertBefore===null&&<DropLine/>}
      {popover&&(()=>{
        const it = popover.item;
        const draft = it ? DRAFT_2026[normName(it.name)] : null;
        const hr = it ? ffHitRate(it.pos, draft?.pick) : null;
        const draftLabel = draft?.pick ? (draft.pick==='UDFA' ? 'UDFA' : `${draft.team||'—'} · ${draft.pick}`) : 'Undrafted';
        const hrLabel = it?.pos && FF_HIT_RATE[it.pos] ? FF_HIT_RATE[it.pos].metric : 'Fantasy starter (first 3 yrs)';
        const hrColor = hr==null ? 'var(--border-strong)' : (hr>=40 ? 'var(--success)' : hr>=20 ? 'var(--accent-gold)' : hr>=10 ? 'var(--warning)' : 'var(--danger)');
        return (
          <div style={{position:'fixed',left:popover.x,top:popover.y,zIndex:9998,background:'var(--bg-card)',border:'1px solid var(--accent-gold)',borderRadius:10,padding:12,width:240,boxShadow:'0 4px 14px rgba(0,0,0,0.18)',pointerEvents:hoverCapable?'none':'auto'}}
            onClick={e=>e.stopPropagation()}>
            {popover.prospect&&(<>
              <img src={popover.prospect.headshot} alt="" style={{width:'100%',height:180,objectFit:'cover',borderRadius:8,background:'var(--bg-pill)'}} onError={e=>{e.currentTarget.style.display='none';}}/>
            </>)}
            <div style={{fontWeight:900,fontSize:14,marginTop:popover.prospect?8:0,color:'var(--accent-gold-text)'}}>{it?.name||popover.prospect?.name||''}</div>
            <div style={{fontSize:13,color:'var(--text-secondary)',marginTop:2}}>{(it?.pos||popover.prospect?.position||'')} · {(it?.college||popover.prospect?.college||'')}</div>
            {popover.prospect&&(
              <div style={{display:'flex',gap:14,marginTop:8,fontSize:14,flexWrap:'wrap'}}>
                <div><span style={{color:'var(--text-secondary)'}}>HT </span><span style={{fontWeight:700}}>{popover.prospect.height||'—'}</span></div>
                <div><span style={{color:'var(--text-secondary)'}}>WT </span><span style={{fontWeight:700}}>{popover.prospect.weight?popover.prospect.weight+' lbs':'—'}</span></div>
                <div><span style={{color:'var(--text-secondary)'}}>AGE </span><span style={{fontWeight:700}}>{popover.prospect.age!=null?popover.prospect.age:'—'}</span></div>
              </div>
            )}
            <div style={{marginTop:10,paddingTop:10,borderTop:'1px solid var(--bg-pill)'}}>
              <div style={{fontSize:11,fontWeight:800,color:'var(--text-secondary)',letterSpacing:1,marginBottom:4}}>NFL DRAFT CAPITAL</div>
              <div style={{fontSize:13,fontWeight:700,color:'var(--text-ddd)'}}>{draftLabel}</div>
            </div>
            <div style={{marginTop:10,paddingTop:10,borderTop:'1px solid var(--bg-pill)'}}>
              <div style={{display:'flex',alignItems:'baseline',justifyContent:'space-between'}}>
                <span style={{fontSize:11,fontWeight:800,color:'var(--text-secondary)',letterSpacing:1}}>FF HIT RATE</span>
                <span style={{fontSize:22,fontWeight:900,color:hrColor,letterSpacing:0.5}}>{hr!=null?hr+'%':'—'}</span>
              </div>
              <div style={{fontSize:11,color:'var(--text-secondary)',marginTop:4,lineHeight:1.4}}>{hrLabel}</div>
              <div style={{fontSize:10,color:'var(--text-secondary)',marginTop:6,fontStyle:'italic'}}>Sources: Fantasy Footballers, fantasyclassroom.org, ESPN/Yahoo/PFF top-pick analyses</div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

function TradePollsTab({session,onRequestSignIn}){
  const [polls,setPolls]=useState([]);
  const [myVotes,setMyVotes]=useState({});
  const [loading,setLoading]=useState(true);
  const [showCreate,setShowCreate]=useState(false);
  const [newOpts,setNewOpts]=useState(['','']);
  const [newSettings,setNewSettings]=useState({teams:'12',format:'Superflex',ppr:'1.0',tep:'0.5',passTd:'6',ppc:'0',status:''});
  const [err,setErr]=useState('');

  const load=useCallback(async()=>{
    if(!sb){ setLoading(false); return; }
    setLoading(true);
    const { data:polls } = await sb.from('polls').select('id,title,options,settings,created_at,poll_votes(option_index,user_id)').order('created_at',{ascending:false}).limit(100);
    // 48-hour expiration: polls older than 48hrs are filtered from the public feed.
    // Computed client-side from created_at — no schema migration needed.
    const POLL_TTL_MS = 48 * 60 * 60 * 1000;
    const cutoff = Date.now() - POLL_TTL_MS;
    const fresh = (polls||[]).filter(p => new Date(p.created_at).getTime() > cutoff);
    const list=fresh.map(p=>{
      const counts=Array(p.options.length).fill(0);
      (p.poll_votes||[]).forEach(v=>{ if(counts[v.option_index]!==undefined) counts[v.option_index]++; });
      return {...p, counts, total:(p.poll_votes||[]).length};
    });
    setPolls(list);
    if(session){
      const mine={};
      (polls||[]).forEach(p=>{
        const v=(p.poll_votes||[]).find(x=>x.user_id===session.user.id);
        if(v) mine[p.id]=v.option_index;
      });
      setMyVotes(mine);
    }else{ setMyVotes({}); }
    setLoading(false);
  },[session]);

  useEffect(()=>{ load(); },[load]);

  const castVote=async(pollId,idx)=>{
    if(!session){ onRequestSignIn(); return; }
    setMyVotes(m=>({...m,[pollId]:idx}));
    setPolls(prev=>prev.map(p=>{
      if(p.id!==pollId) return p;
      const counts=[...p.counts];
      const prior=myVotes[pollId];
      if(prior!==undefined&&prior!==idx) counts[prior]=Math.max(0,counts[prior]-1);
      if(prior!==idx) counts[idx]++;
      return {...p,counts,total:prior===undefined?p.total+1:p.total};
    }));
    await sb.from('poll_votes').upsert({poll_id:pollId,user_id:session.user.id,option_index:idx,updated_at:new Date().toISOString()});
  };

  const createPoll=async()=>{
    setErr('');
    const opts=newOpts.map(o=>o.trim()).filter(Boolean);
    if(opts.length<2){ setErr('Need at least 2 options.'); return; }
    const settings={ teams:Number(newSettings.teams)||null, format:newSettings.format, ppr:Number(newSettings.ppr), tep:Number(newSettings.tep), passTd:Number(newSettings.passTd)||null, ppc:Number(newSettings.ppc)||0, status:newSettings.status||null };
    const { data, error } = await sb.from('polls').insert({created_by:session.user.id,title:'',options:opts,settings}).select().single();
    if(error){ setErr(error.message); return; }
    setNewOpts(['','']); setShowCreate(false);
    setPolls(prev=>[{...data,counts:Array(opts.length).fill(0),total:0,poll_votes:[]},...prev]);
  };

  const addOpt=()=>setNewOpts(o=>o.length>=10?o:[...o,'']);
  const setOpt=(i,v)=>setNewOpts(o=>o.map((x,j)=>j===i?v:x));
  const rmOpt=(i)=>setNewOpts(o=>o.length<=2?o:o.filter((_,j)=>j!==i));

  return (
    <div>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16,gap:10,flexWrap:'wrap'}}>
        <div>
          <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
            <div style={{fontSize:20,fontWeight:900,color:'var(--accent-gold-text)',letterSpacing:1}}>TRADE POLLS</div>
            <HowItWorksButton title="Trade Polls" compact>
              <p><strong>What it does:</strong> Get the community's vote on whether a dynasty trade is fair.</p>
              <ol style={{paddingLeft:20,marginTop:10,display:'flex',flexDirection:'column',gap:8}}>
                <li><strong>Create a poll</strong> — describe each side of the trade, set your league's settings (teams, format, PPR, TEP, pass-TD), pick "Contender" or "Rebuilding" for context.</li>
                <li><strong>Share the link</strong> — voters tap which side they'd take. Add up to 10 options if it's a multi-way poll.</li>
                <li><strong>See the breakdown</strong> — vote counts, percentage split, and the rolling tally as votes come in.</li>
              </ol>
              <p style={{marginTop:12}}>Polls expire after 48 hours so the feed stays fresh. You can change your vote anytime within that window.</p>
              <p style={{marginTop:10,fontSize:12,color:'var(--text-muted)',fontStyle:'italic'}}>Sign-in needed to POST polls (so we can dedupe + let you edit later). Voting is open to anyone.</p>
            </HowItWorksButton>
          </div>
          <div style={{fontSize:13,color:'var(--text-secondary)',marginTop:2}}>Post a trade, vote anonymously. You can change your vote anytime.</div>
        </div>
        {session?(
          <button onClick={()=>setShowCreate(s=>!s)} style={{padding:'10px 18px',background:'var(--accent-gold)',color:'#000',border:'none',borderRadius:8,fontWeight:900,cursor:'pointer',fontSize:14,letterSpacing:1}}>{showCreate?'✕ CANCEL':'+ NEW POLL'}</button>
        ):(
          <button onClick={onRequestSignIn} style={{padding:'10px 18px',background:'transparent',color:'var(--accent-gold-text)',border:'1px solid var(--accent-gold)',borderRadius:8,fontWeight:900,cursor:'pointer',fontSize:14,letterSpacing:1}}>SIGN IN TO POST</button>
        )}
      </div>

      {showCreate&&(
        <div style={{background:'var(--bg-card)',border:'1px solid var(--accent-gold)',borderRadius:10,padding:16,marginBottom:18}}>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))',gap:8,marginBottom:12}}>
            <div style={{display:'flex',flexDirection:'column',gap:3}}>
              <label style={{fontSize:12,color:'var(--text-secondary)',letterSpacing:1}}>TEAMS</label>
              <input value={newSettings.teams} onChange={e=>setNewSettings({...newSettings,teams:e.target.value})} placeholder="12" style={{padding:9,background:'var(--bg-input)',border:'1px solid var(--border-strong)',borderRadius:6,color:'var(--text-bright)',fontSize:13}}/>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:3}}>
              <label style={{fontSize:12,color:'var(--text-secondary)',letterSpacing:1}}>FORMAT</label>
              <select value={newSettings.format} onChange={e=>setNewSettings({...newSettings,format:e.target.value})} style={{padding:9,background:'var(--bg-input)',border:'1px solid var(--border-strong)',borderRadius:6,color:'var(--text-bright)',fontSize:13}}>
                <option>Superflex</option><option>1QB</option>
              </select>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:3}}>
              <label style={{fontSize:12,color:'var(--text-secondary)',letterSpacing:1}}>PPR</label>
              <input value={newSettings.ppr} onChange={e=>setNewSettings({...newSettings,ppr:e.target.value})} placeholder="1.0" style={{padding:9,background:'var(--bg-input)',border:'1px solid var(--border-strong)',borderRadius:6,color:'var(--text-bright)',fontSize:13}}/>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:3}}>
              <label style={{fontSize:12,color:'var(--text-secondary)',letterSpacing:1}}>TE PREMIUM</label>
              <input value={newSettings.tep} onChange={e=>setNewSettings({...newSettings,tep:e.target.value})} placeholder="1.0" style={{padding:9,background:'var(--bg-input)',border:'1px solid var(--border-strong)',borderRadius:6,color:'var(--text-bright)',fontSize:13}}/>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:3}}>
              <label style={{fontSize:12,color:'var(--text-secondary)',letterSpacing:1}}>PASS TD</label>
              <input value={newSettings.passTd} onChange={e=>setNewSettings({...newSettings,passTd:e.target.value})} placeholder="6" style={{padding:9,background:'var(--bg-input)',border:'1px solid var(--border-strong)',borderRadius:6,color:'var(--text-bright)',fontSize:13}}/>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:3}}>
              <label style={{fontSize:12,color:'var(--text-secondary)',letterSpacing:1}}>PPC</label>
              <input value={newSettings.ppc} onChange={e=>setNewSettings({...newSettings,ppc:e.target.value})} placeholder="0" style={{padding:9,background:'var(--bg-input)',border:'1px solid var(--border-strong)',borderRadius:6,color:'var(--text-bright)',fontSize:13}}/>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:3}}>
              <label style={{fontSize:12,color:'var(--text-secondary)',letterSpacing:1}}>TEAM STATUS</label>
              <select value={newSettings.status} onChange={e=>setNewSettings({...newSettings,status:e.target.value})} style={{padding:9,background:'var(--bg-input)',border:'1px solid var(--border-strong)',borderRadius:6,color:'var(--text-bright)',fontSize:13}}>
                <option value="">—</option>
                <option>Contender</option>
                <option>Rebuilding</option>
              </select>
            </div>
          </div>
          {newOpts.map((o,i)=>(
            <div key={i} style={{display:'flex',gap:6,marginBottom:6}}>
              <input value={o} onChange={e=>setOpt(i,e.target.value)} placeholder={`Option ${i+1} (e.g., 'Accept', 'Decline', 'Counter')`} style={{flex:1,padding:9,background:'var(--bg-input)',border:'1px solid var(--border-strong)',borderRadius:6,color:'var(--text-bright)',fontSize:13}}/>
              {newOpts.length>2&&<button onClick={()=>rmOpt(i)} style={{padding:'0 10px',background:'transparent',border:'1px solid var(--border-strong)',borderRadius:6,color:'var(--text-secondary)',cursor:'pointer'}}>✕</button>}
            </div>
          ))}
          <div style={{display:'flex',gap:8,marginTop:10,flexWrap:'wrap'}}>
            {newOpts.length<10&&<button onClick={addOpt} style={{padding:'7px 12px',background:'transparent',border:'1px solid var(--border-strong)',borderRadius:6,color:'var(--text-secondary)',cursor:'pointer',fontSize:14}}>+ Option</button>}
            <button onClick={createPoll} style={{padding:'7px 18px',background:'var(--accent-gold)',color:'#000',border:'none',borderRadius:6,fontWeight:900,cursor:'pointer',fontSize:14,letterSpacing:1,marginLeft:'auto'}}>POST POLL</button>
          </div>
          {err&&<div style={{color:'var(--danger)',fontSize:14,marginTop:8}}>{err}</div>}
        </div>
      )}

      {loading?(
        <div style={{padding:40,color:'var(--text-secondary)',textAlign:'center',fontSize:13}}>Loading polls...</div>
      ):polls.length===0?(
        <div style={{padding:40,color:'var(--text-secondary)',textAlign:'center',fontSize:13,background:'var(--bg-elevated)',border:'1px solid var(--bg-pill)',borderRadius:10}}>No polls yet. Be the first to post one!</div>
      ):(
        <div style={{display:'flex',flexDirection:'column',gap:12}}>
          {polls.map(p=>{
            const myIdx=myVotes[p.id];
            const voted=myIdx!==undefined;
            return (
              <div key={p.id} style={{background:'var(--bg-card)',border:'1px solid var(--bg-pill)',borderRadius:10,padding:16}}>
                {p.title&&<div style={{fontWeight:700,fontSize:14,marginBottom:8,color:'var(--text-eee)'}}>{p.title}</div>}
                {p.settings&&(
                  <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:12}}>
                    {p.settings.teams&&<span style={{padding:'4px 10px',background:'var(--bg-pill)',border:'1px solid var(--accent-gold)',borderRadius:12,fontSize:13,color:'var(--accent-gold-text)',fontWeight:700}}>{p.settings.teams}-team</span>}
                    {p.settings.format&&<span style={{padding:'4px 10px',background:'var(--bg-pill)',border:'1px solid var(--accent-gold)',borderRadius:12,fontSize:13,color:'var(--accent-gold-text)',fontWeight:700}}>{p.settings.format}</span>}
                    {(p.settings.ppr||p.settings.ppr===0)&&<span style={{padding:'4px 10px',background:'var(--bg-pill)',border:'1px solid var(--accent-gold)',borderRadius:12,fontSize:13,color:'var(--accent-gold-text)',fontWeight:700}}>{p.settings.ppr} PPR</span>}
                    {(p.settings.tep||p.settings.tep===0)&&<span style={{padding:'4px 10px',background:'var(--bg-pill)',border:'1px solid var(--accent-gold)',borderRadius:12,fontSize:13,color:'var(--accent-gold-text)',fontWeight:700}}>{p.settings.tep} TEP</span>}
                    {p.settings.passTd&&<span style={{padding:'4px 10px',background:'var(--bg-pill)',border:'1px solid var(--accent-gold)',borderRadius:12,fontSize:13,color:'var(--accent-gold-text)',fontWeight:700}}>{p.settings.passTd} pt pass TD</span>}
                    {p.settings.ppc>0&&<span style={{padding:'4px 10px',background:'var(--bg-pill)',border:'1px solid var(--accent-gold)',borderRadius:12,fontSize:13,color:'var(--accent-gold-text)',fontWeight:700}}>{p.settings.ppc} PPC</span>}
                    {p.settings.status&&<span style={{padding:'4px 10px',background:'var(--accent-gold)',border:'1px solid var(--accent-gold)',borderRadius:12,fontSize:13,color:'#000',fontWeight:800}}>{p.settings.status}</span>}
                  </div>
                )}
                <div style={{display:'flex',flexDirection:'column',gap:6}}>
                  {p.options.map((opt,i)=>{
                    const pct=p.total>0?Math.round((p.counts[i]/p.total)*100):0;
                    const isMine=myIdx===i;
                    return (
                      <button key={i} onClick={()=>castVote(p.id,i)} style={{position:'relative',textAlign:'left',padding:'10px 14px',background:'var(--bg-elevated)',border:isMine?'2px solid var(--accent-gold)':'1px solid var(--border-hairline)',borderRadius:8,color:'var(--text-eee)',cursor:'pointer',overflow:'hidden',fontSize:13,fontWeight:isMine?700:500}}>
                        <div style={{position:'absolute',left:0,top:0,bottom:0,width:pct+'%',background:isMine?'rgba(221,179,77,0.18)':'rgba(255,255,255,0.06)',transition:'width 0.25s'}}/>
                        <div style={{position:'relative',display:'flex',justifyContent:'space-between',alignItems:'center',gap:10}}>
                          <span>{isMine&&'✓ '}{opt}</span>
                          <span style={{fontSize:13,color:'var(--text-muted)',fontWeight:700,flexShrink:0}}>{p.counts[i]} · {pct}%</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
                <div style={{marginTop:10,display:'flex',justifyContent:'space-between',alignItems:'center',fontSize:13,color:'var(--text-secondary)'}}>
                  <span>{p.total} vote{p.total===1?'':'s'} · anonymous</span>
                  <span>{new Date(p.created_at).toLocaleDateString()}</span>
                </div>
                {!session&&<div style={{marginTop:6,fontSize:12,color:'var(--text-secondary)'}}>Sign in to vote</div>}
                {voted&&<div style={{marginTop:6,fontSize:12,color:'var(--text-secondary)'}}>You can change your vote by tapping another option.</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function OutlookTab() {
  const ranked      = loadStorage('pfk_ranked', []);
  const leagueName  = loadStorage('pfk_league_name', '');
  const sleeperUser = loadStorage('pfk_sleeper_obj', null);
  const myTeam      = ranked.find(r => r.owner_id === sleeperUser?.user_id);

  if (!sleeperUser || !ranked.length || !myTeam) {
    return (
      <div style={{textAlign:'center',padding:'60px 20px',display:'flex',flexDirection:'column',alignItems:'center',gap:14}}>
        <div style={{fontSize:52}}>🔮</div>
        <div style={{fontSize:16,fontWeight:700,color:'var(--text-bright)'}}>No team data yet</div>
        <div style={{fontSize:13,color:'var(--text-secondary)',maxWidth:360,lineHeight:1.7}}>Connect your Sleeper account on the Power Rankings tab and select a league to see your team outlook and league comparison.</div>
      </div>
    );
  }

  const {arc,dRank,rRank,dVal,rVal,teamName} = myTeam;
  const n = ranked.length;
  const maxVal = ranked[0]?.dVal || 1;

  return (
    <div style={{display:'flex',flexDirection:'column',gap:18}}>

      {/* Archetype hero */}
      <div style={{background:'var(--bg-elevated)',border:`2px solid ${arc.color}`,borderRadius:16,padding:'32px 24px',textAlign:'center'}}>
        <div style={{fontSize:52,marginBottom:6}}>{arc.emoji}</div>
        <div style={{fontSize:32,fontWeight:900,color:arc.color,letterSpacing:3,lineHeight:1}}>{arc.label.toUpperCase()}</div>
        <div style={{fontSize:15,fontWeight:700,color:'var(--text-primary)',marginTop:10}}>{teamName}</div>
        <div style={{fontSize:13,color:'var(--text-secondary)',marginTop:3,letterSpacing:1}}>{leagueName}</div>
        <div style={{fontSize:13,color:arc.color,marginTop:14,fontStyle:'italic'}}>{arc.desc}</div>
      </div>

      {/* Dynasty vs Redraft rank cards */}
      {dVal > 0 && (
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
          {[
            {label:'Dynasty Rank', rank:dRank, color:'var(--accent-gold-text)', barColor:'var(--accent-gold)'},
            {label:'Redraft Rank', rank:rRank, color:'var(--info)', barColor:'var(--info)'},
          ].map(({label,rank,color,barColor})=>(
            <div key={label} style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:12,padding:20,textAlign:'center'}}>
              <div style={{fontSize:12,color:'var(--text-secondary)',fontWeight:700,letterSpacing:2,marginBottom:8}}>{label.toUpperCase()}</div>
              <div style={{fontSize:44,fontWeight:900,color,lineHeight:1}}>#{rank}</div>
              <div style={{fontSize:13,color:'var(--text-secondary)',marginTop:4}}>of {n} teams</div>
              <div style={{marginTop:12,height:6,background:'var(--bg-pill)',borderRadius:3,overflow:'hidden'}}>
                <div style={{height:'100%',width:`${Math.max(4,Math.round((1-(rank-1)/(n-1||1))*100))}%`,background:barColor,borderRadius:3}}/>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* League bar chart */}
      {dVal > 0 && (
        <div style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:12,padding:20}}>
          <div style={{fontSize:14,fontWeight:900,color:'var(--accent-gold-text)',marginBottom:18,textTransform:'uppercase',letterSpacing:1}}>📊 Dynasty Value · Full League</div>
          <div style={{display:'flex',flexDirection:'column',gap:10}}>
            {ranked.map(t => {
              const isMe = t.owner_id === sleeperUser?.user_id;
              const barW  = Math.max(4, Math.round((t.dVal/maxVal)*100));
              return (
                <div key={t.roster_id}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
                    <span style={{fontSize:13,fontWeight:isMe?800:500,color:isMe?t.arc.color:'var(--text-secondary)',maxWidth:'60%',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                      {isMe?'★ ':''}{t.teamName}
                    </span>
                    <span style={{fontSize:12,color:isMe?t.arc.color:'var(--text-secondary)',flexShrink:0}}>{t.arc.emoji} {t.arc.label}</span>
                  </div>
                  <div style={{height:isMe?10:6,background:'var(--bg-pill)',borderRadius:3,overflow:'hidden'}}>
                    <div style={{height:'100%',width:`${barW}%`,background:isMe?t.arc.color:'var(--border-hairline)',borderRadius:3}}/>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Archetype glossary */}
      <div style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:12,padding:20}}>
        <div style={{fontSize:14,fontWeight:900,color:'var(--accent-gold-text)',marginBottom:14,textTransform:'uppercase',letterSpacing:1}}>📖 Archetype Guide</div>
        {[classifyTeam(80,80),classifyTeam(20,80),classifyTeam(80,20),classifyTeam(65,65),classifyTeam(55,35),classifyTeam(45,30),classifyTeam(20,20)]
          .filter((v,i,a)=>a.findIndex(x=>x.label===v.label)===i)
          .map(a=>(
            <div key={a.label} style={{display:'flex',alignItems:'flex-start',gap:12,padding:'10px 0',borderBottom:'1px solid var(--border)'}}>
              <span style={{fontSize:22,width:30,flexShrink:0,textAlign:'center'}}>{a.emoji}</span>
              <div>
                <div style={{fontSize:14,fontWeight:800,color:a.color}}>{a.label}</div>
                <div style={{fontSize:13,color:'var(--text-secondary)',marginTop:2}}>{a.desc}</div>
              </div>
            </div>
          ))
        }
      </div>

    </div>
  );
}

// Dev URL access gate: shown when isDevHost() and the visitor isn't Evan.
function DevAuthGate({mode, doAuth, authEmail, setAuthEmail, authPassword, setAuthPassword, authMsg, doLogout, signedInAs}){
  return (
    <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',padding:20,background:'var(--bg-page)',color:'var(--text-primary)',fontFamily:"'Inter','Segoe UI',sans-serif"}}>
      <div style={{width:'100%',maxWidth:380,background:'var(--bg-divider)',border:'1px solid var(--accent-gold)',borderRadius:12,padding:28}}>
        <div style={{textAlign:'center',marginBottom:20}}>
          <div style={{fontWeight:900,fontSize:22,letterSpacing:3,color:'var(--accent-gold-text)'}}>PFK DEV</div>
          <div style={{fontSize:13,color:'var(--text-secondary)',marginTop:6}}>This is a private preview. Only the site owner can access it.</div>
        </div>
        {mode==='signin' && (
          <>
            <input placeholder="Email" value={authEmail} onChange={e=>setAuthEmail(e.target.value)} style={{width:'100%',padding:10,marginBottom:10,background:'var(--bg-input)',border:'1px solid var(--border-strong)',borderRadius:6,color:'var(--text-bright)'}}/>
            <input type="password" placeholder="Password" value={authPassword} onChange={e=>setAuthPassword(e.target.value)} onKeyDown={e=>e.key==='Enter'&&doAuth()} style={{width:'100%',padding:10,marginBottom:14,background:'var(--bg-input)',border:'1px solid var(--border-strong)',borderRadius:6,color:'var(--text-bright)'}}/>
            <button onClick={doAuth} style={{width:'100%',padding:12,background:'var(--accent-gold)',color:'#000',border:'none',borderRadius:6,fontWeight:900,cursor:'pointer',letterSpacing:1}}>SIGN IN</button>
            {authMsg&&<div style={{color:'var(--danger)',fontSize:14,marginTop:10,textAlign:'center'}}>{authMsg}</div>}
          </>
        )}
        {mode==='denied' && (
          <>
            <div style={{padding:14,background:'var(--danger-bg)',border:'1px solid var(--danger)',borderRadius:8,color:'var(--danger)',fontSize:14,textAlign:'center',marginBottom:14}}>
              Access denied. Signed in as <strong>{signedInAs}</strong>.
            </div>
            <button onClick={doLogout} style={{width:'100%',padding:12,background:'var(--accent-gold)',color:'#000',border:'none',borderRadius:6,fontWeight:900,cursor:'pointer',letterSpacing:1}}>SIGN OUT</button>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// useSleeperUser — shared hook for the linked Sleeper account.
// Backed by localStorage (key: pfk_sleeper_user). Any component can call
// useSleeperUser() and it stays in sync with every other instance: when one
// component links/unlinks, all the others re-render via the 'pfk:sleeper-changed'
// custom event (same-tab updates) and the native 'storage' event (cross-tab).
// This is what makes the Sleeper link in MasterToolbar instantly reflect in
// Trade Finder (and future Lookup/Dispersal integrations) on the same page.
// ============================================================================
const SLEEPER_STORAGE_KEY = 'pfk_sleeper_user';
const SLEEPER_CHANGE_EVENT = 'pfk:sleeper-changed';
function useSleeperUser(){
  const read = () => {
    try{
      const raw = localStorage.getItem(SLEEPER_STORAGE_KEY);
      if(!raw) return null;
      const parsed = JSON.parse(raw);
      // Validate shape — must be an object with user_id. If a previous bug
      // (or another component) wrote a bare string or other shape under this
      // key, treat it as "not linked" AND scrub the bad value so we stop
      // trying to parse it on every page load.
      if(parsed && typeof parsed === 'object' && parsed.user_id) return parsed;
      localStorage.removeItem(SLEEPER_STORAGE_KEY);
      return null;
    }catch(e){
      // Parse failed (e.g. legacy bare-string write) — clean up and start fresh.
      try{ localStorage.removeItem(SLEEPER_STORAGE_KEY); }catch(_e){}
      return null;
    }
  };
  const [user, setUserState] = useState(read);
  useEffect(() => {
    const refresh = () => setUserState(read());
    window.addEventListener(SLEEPER_CHANGE_EVENT, refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(SLEEPER_CHANGE_EVENT, refresh);
      window.removeEventListener('storage', refresh);
    };
  },[]);
  // DB-backed restore + opportunistic-upgrade. Two flows triggered by the
  // same useEffect, run on first mount AND on every Supabase auth state
  // change (so sign-in mid-session also fires it):
  //
  //   1. RESTORE: signed in + no local Sleeper link → fetch sleeper_username
  //      from users-row, look up the live Sleeper user, populate locally.
  //   2. OPPORTUNISTIC UPGRADE: signed in + LOCAL Sleeper link is set →
  //      push it to users-row (upsert). Catches the case where someone
  //      linked Sleeper before they signed up, OR before the DB-sync code
  //      existed — without this they'd be silently un-linked once their
  //      localStorage was eventually wiped (iOS Safari ITP).
  //
  // Guarded so the same flow doesn't duplicate-fire across multiple
  // useSleeperUser callers within one auth state.
  useEffect(() => {
    if(typeof sb === 'undefined' || !sb) return;
    let cancelled = false;
    const lookupRecover = async (uid, email) => {
      // (1) RESTORE — only when local is empty
      if(!localStorage.getItem(SLEEPER_STORAGE_KEY)){
        try{
          const { data: row } = await sb.from('users').select('sleeper_username').eq('id', uid).maybeSingle();
          if(cancelled) return;
          if(row?.sleeper_username){
            try{
              const u = await lookupResolveUser(row.sleeper_username);
              if(cancelled) return;
              const next = { user_id: u.user_id, username: u.username, display_name: u.display_name || u.username, avatar: u.avatar || null };
              localStorage.setItem(SLEEPER_STORAGE_KEY, JSON.stringify(next));
              setUserState(next);
              window.dispatchEvent(new Event(SLEEPER_CHANGE_EVENT));
              return; // restored — done
            }catch(e){/* sleeper lookup failed */}
          }
        }catch(e){/* db read failed */}
      }
      // (2) OPPORTUNISTIC UPGRADE — local link exists, push it to DB so it
      // outlives a future localStorage wipe.
      const localRaw = localStorage.getItem(SLEEPER_STORAGE_KEY);
      if(!localRaw) return;
      let local;
      try{ local = JSON.parse(localRaw); }catch(e){ return; }
      if(!local?.username) return;
      sb.from('users').upsert({ id: uid, email: email || null, sleeper_username: local.username }).then(()=>{}, ()=>{});
    };
    const run = async () => {
      const { data } = await sb.auth.getSession();
      const uid = data?.session?.user?.id;
      const email = data?.session?.user?.email;
      if(!uid || cancelled) return;
      await lookupRecover(uid, email);
    };
    run();
    // Re-run on auth state changes — e.g., user opens the page signed-out
    // and signs in mid-session. Without this hook, the restore would only
    // fire on first mount and miss the sign-in event.
    const sub = sb.auth.onAuthStateChange((event, session) => {
      if(cancelled) return;
      if(event === 'SIGNED_IN' && session?.user?.id){
        lookupRecover(session.user.id, session.user.email);
      }
    });
    return () => {
      cancelled = true;
      try{ sub?.data?.subscription?.unsubscribe?.(); }catch(e){}
    };
  },[]);
  const setUser = (next) => {
    if(next) localStorage.setItem(SLEEPER_STORAGE_KEY, JSON.stringify(next));
    else { localStorage.removeItem(SLEEPER_STORAGE_KEY); localStorage.removeItem('pfk_sleeper_league'); }
    setUserState(next);
    window.dispatchEvent(new Event(SLEEPER_CHANGE_EVENT));
    // Persist the username on the user's Supabase row when signed in so the
    // link survives a localStorage wipe (iOS Safari ITP clears site data
    // after ~7 days of inactivity) and follows them across devices. We
    // UPSERT (not update) so that if the user signed up before they linked
    // Sleeper and their row exists with a NULL username — or doesn't yet
    // exist — we still write the value. Email is included so the row
    // satisfies any NOT-NULL email constraint on first insert.
    // Fire-and-forget; failure is fine — local link still works.
    if(typeof sb !== 'undefined' && sb){
      sb.auth.getSession().then(({data}) => {
        const uid = data?.session?.user?.id;
        const email = data?.session?.user?.email;
        if(!uid) return;
        sb.from('users').upsert({ id: uid, email: email || null, sleeper_username: next?.username || null }).then(()=>{}, ()=>{});
      }).catch(()=>{});
    }
  };
  return [user, setUser];
}

// ============================================================================
// SleeperLink — the "Link Sleeper" button + linked chip used inside MasterToolbar.
// Pulled out so the toolbar's own state stays focused on session/tabs.
// ============================================================================
function SleeperLink(){
  const [user, setUser] = useSleeperUser();
  const [linking, setLinking] = useState(false);
  const [val, setVal] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setErr('');
    const q = (val||'').trim();
    if(!q){ setErr('Enter your Sleeper username'); return; }
    setBusy(true);
    try{
      const u = await lookupResolveUser(q);
      setUser({ user_id: u.user_id, username: u.username, display_name: u.display_name || u.username, avatar: u.avatar || null });
      setLinking(false);
      setVal('');
    }catch(e){
      setErr(e.message || 'Could not find that Sleeper user');
    }finally{
      setBusy(false);
    }
  };
  if(user){
    const avatar = user.avatar ? `https://sleepercdn.com/avatars/thumbs/${user.avatar}` : null;
    return (
      <div data-tooltip="Your linked Sleeper account — click × to unlink"
           style={{display:"inline-flex",alignItems:"center",gap:6,padding:"3px 6px 3px 3px",background:"var(--bg-elevated)",border:"1px solid rgba(var(--accent-gold-rgb),0.4)",borderRadius:18}}>
        {avatar
          ? <img src={avatar} alt="" style={{width:22,height:22,borderRadius:'50%',objectFit:'cover',background:'var(--bg-pill)'}} onError={e=>e.currentTarget.style.display='none'}/>
          : <div style={{width:22,height:22,borderRadius:'50%',background:'var(--bg-pill)',display:'flex',alignItems:'center',justifyContent:'center',color:'var(--accent-gold-text)',fontWeight:900,fontSize:11}}>{(user.username||'?')[0].toUpperCase()}</div>
        }
        <span style={{color:'var(--accent-gold-text)',fontWeight:800,fontSize:11,letterSpacing:0.3,maxWidth:120,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>@{user.username}</span>
        <button onClick={()=>{ if(confirm('Unlink your Sleeper account?')) setUser(null); }}
                title="Unlink Sleeper"
                style={{background:'transparent',border:'none',color:'var(--text-secondary)',fontSize:12,fontWeight:900,cursor:'pointer',padding:'0 4px',lineHeight:1}}>×</button>
      </div>
    );
  }
  if(linking){
    return (
      <span style={{display:"inline-flex",alignItems:"center",gap:4}}>
        <input autoFocus value={val} onChange={e=>{setVal(e.target.value); setErr('');}}
               onKeyDown={e=>e.key==='Enter'&&submit()}
               placeholder="Sleeper username"
               disabled={busy}
               style={{padding:'4px 8px',background:'var(--bg-elevated)',border:'1px solid rgba(var(--accent-gold-rgb),0.4)',borderRadius:6,color:'var(--text-bright)',fontSize:11,fontFamily:'inherit',width:130}}/>
        <button onClick={submit} disabled={busy}
                style={{padding:'4px 8px',background:'var(--accent-gold)',border:'none',borderRadius:6,color:'#000',fontWeight:900,fontSize:10,cursor:busy?'wait':'pointer',letterSpacing:0.5}}>{busy?'…':'GO'}</button>
        <button onClick={()=>{setLinking(false); setVal(''); setErr('');}}
                style={{background:'transparent',border:'none',color:'var(--text-secondary)',fontSize:14,cursor:'pointer',padding:'0 4px'}}>×</button>
        {err && <span style={{color:'var(--danger)',fontSize:10,marginLeft:4}}>{err}</span>}
      </span>
    );
  }
  return (
    <button onClick={()=>{setLinking(true); setErr('');}}
            data-tooltip="Link your Sleeper account once — Trade Finder, Lookup, and other tools will use it automatically"
            style={{display:"inline-flex",alignItems:"center",gap:5,padding:"4px 10px",background:"var(--bg-elevated)",border:"1px solid rgba(var(--accent-gold-rgb),0.33)",borderRadius:16,color:"var(--accent-gold-text)",fontWeight:700,fontSize:11,letterSpacing:0.3,cursor:'pointer'}}>
      🔗 Link Sleeper
    </button>
  );
}

// ============================================================================
// PFKFooter — site-wide footer with mini brand, contact + social, copyright.
// Renders at the bottom of every top-level page (App, LookupApp, TradeFinderApp,
// AdminApp, DispersalApp setup screen). Skipped on the live dispersal draft
// view since the bottom-sheet covers the bottom of the screen there.
// ============================================================================
function PFKFooter(){
  const year = new Date().getFullYear();
  return (
    <footer style={{borderTop:"1px solid var(--border)",background:"var(--bg-elevated)",padding:"22px 16px 26px",marginTop:32}}>
      <div style={{maxWidth:1140,margin:"0 auto",display:"flex",flexDirection:"column",alignItems:"center",gap:12}}>
        {/* Mini brand strip — small logo + wordmark */}
        <a href="/" style={{display:"flex",alignItems:"center",gap:10,textDecoration:"none"}}>
          <img src="/img/pfk-logo.png" alt="" style={{width:36,height:36,objectFit:"contain"}} onError={e=>e.target.style.display="none"}/>
          <div>
            <div style={{fontSize:14,fontWeight:900,color:"var(--wordmark-color)",letterSpacing:2}}>PLAY FOR KEEPS</div>
            <div style={{fontSize:9,color:"var(--wordmark-subtitle)",letterSpacing:2,fontWeight:700,textTransform:"uppercase",marginTop:1}}>Dynasty Fantasy Football</div>
          </div>
        </a>
        {/* Contact + social row */}
        <div style={{display:"flex",gap:10,flexWrap:"wrap",justifyContent:"center"}}>
          <a href="https://x.com/PlayForKeepsFF" target="_blank" rel="noopener noreferrer"
             aria-label="Follow @PlayForKeepsFF on X" data-tooltip="Follow @PlayforkeepsFF on X"
             style={{display:"inline-flex",alignItems:"center",gap:6,padding:"6px 12px",background:"transparent",border:"1px solid rgba(var(--accent-gold-rgb),0.33)",borderRadius:18,color:"var(--accent-gold-text)",textDecoration:"none",fontWeight:800,fontSize:12,letterSpacing:0.3}}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="var(--accent-gold-text)" aria-hidden="true"><path d="M18.244 2H21.5l-7.5 8.57L23 22h-6.844l-5.36-6.72L4.5 22H1.244l8.04-9.187L1 2h7.016l4.844 6.12L18.244 2zm-1.2 18h1.9L7.048 4H5.05l12 16z"/></svg>
            <span>@PlayforkeepsFF</span>
          </a>
          <a href="mailto:playforkeepsdynasty@gmail.com?subject=PFK%20Support"
             aria-label="Email PFK support with feedback or bug reports"
             data-tooltip="Email PFK support with feedback, bug reports, or partnership inquiries"
             style={{display:"inline-flex",alignItems:"center",gap:6,padding:"6px 12px",background:"transparent",border:"1px solid rgba(var(--accent-gold-rgb),0.33)",borderRadius:18,color:"var(--accent-gold-text)",textDecoration:"none",fontWeight:800,fontSize:12,letterSpacing:0.3}}>
            📧 Email Support
          </a>
          {/* Suggestion Box pill — footer-only (was also in the toolbar
              briefly; removed there per Evan's 2026-05-20 cleanup).
              Same destination address as support, distinct subject
              ("PFK Suggestion") so feature ideas land in their own
              triage lane. Body pre-fills with the current page URL so
              Evan knows which tool the user was on when they had the
              idea. */}
          <a href={`mailto:playforkeepsdynasty@gmail.com?subject=${encodeURIComponent('PFK Suggestion')}&body=${encodeURIComponent(`Page: ${typeof window !== 'undefined' ? window.location.href : ''}\n\nYour suggestion:\n`)}`}
             aria-label="Send PFK a suggestion or feature idea"
             data-tooltip="Got a feature idea or suggestion? Email it straight to Evan."
             style={{display:"inline-flex",alignItems:"center",gap:6,padding:"6px 12px",background:"transparent",border:"1px solid rgba(var(--accent-gold-rgb),0.33)",borderRadius:18,color:"var(--accent-gold-text)",textDecoration:"none",fontWeight:800,fontSize:12,letterSpacing:0.3}}>
            💡 Suggestion Box
          </a>
        </div>
        {/* Copyright */}
        <div style={{fontSize:11,color:"var(--border-outline)",letterSpacing:0.4,textAlign:"center"}}>© {year} Play For Keeps. All rights reserved.</div>
      </div>
    </footer>
  );
}

// ============================================================================
// HowItWorksButton — small chip-style "ⓘ HOW IT WORKS" button that opens a
// modal with a plain-English explanation of a tool. Each tool passes its own
// title + content. Backdrop click or Esc key closes. Mobile-friendly: scrollable
// modal, max width caps so it doesn't run edge-to-edge on tablets.
// ============================================================================
function HowItWorksButton({ title, children, label, compact }){
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if(!open) return;
    const onKey = e => { if(e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    // Lock body scroll while modal is open so swipes don't bleed through
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);
  return (
    <>
      <button type="button" onClick={()=>setOpen(true)}
              aria-label={`How ${title || 'this tool'} works`}
              data-tooltip="Plain-English explainer of how this tool works"
              style={{display:'inline-flex',alignItems:'center',gap:5,padding: compact ? '3px 8px' : '5px 11px',background:'transparent',border:'1px solid rgba(var(--accent-gold-rgb),0.4)',borderRadius:14,color:'var(--accent-gold-text)',cursor:'pointer',fontSize: compact ? 10 : 11,fontWeight:800,letterSpacing:0.4,fontFamily:'inherit',whiteSpace:'nowrap'}}>
        ⓘ {label || 'HOW IT WORKS'}
      </button>
      {open && (
        <div onClick={()=>setOpen(false)}
             role="dialog" aria-modal="true"
             style={{position:'fixed',inset:0,background:'var(--backdrop)',zIndex:1000,display:'flex',alignItems:'flex-start',justifyContent:'center',padding:'4vh 14px',overflowY:'auto'}}>
          <div onClick={e=>e.stopPropagation()}
               style={{background:'var(--bg-card)',border:'1.5px solid var(--accent-gold)',borderRadius:14,padding:'22px 22px 24px',maxWidth:560,width:'100%',position:'relative',boxShadow:'var(--shadow-card)',color:'var(--text-primary)'}}>
            <button type="button" onClick={()=>setOpen(false)} aria-label="Close"
                    style={{position:'absolute',top:8,right:10,background:'transparent',border:'none',color:'var(--text-secondary)',fontSize:24,cursor:'pointer',fontFamily:'inherit',padding:6,lineHeight:1}}>×</button>
            <div style={{fontSize:11,fontWeight:800,color:'var(--text-muted)',letterSpacing:1.5,textTransform:'uppercase',marginBottom:4}}>How it works</div>
            <h2 style={{fontSize:19,fontWeight:900,color:'var(--accent-gold-text)',letterSpacing:0.6,marginBottom:14,paddingRight:24,lineHeight:1.25}}>{title}</h2>
            <div style={{fontSize:13,lineHeight:1.7,color:'var(--text-primary)'}}>{children}</div>
          </div>
        </div>
      )}
    </>
  );
}

// ============================================================================
// DevScreenshotButton — dev-only floating pill that captures a PNG of the
// ENTIRE scrollable page (not just the viewport). Useful for previewing
// what a tweet attachment would look like before sharing.
//
// Uses `html-to-image` (lazy-loaded on first click) instead of the
// site-wide html2canvas because the PFK app paints with color-mix() in
// hundreds of inline styles — Chrome resolves those to color(srgb …),
// which html2canvas can't parse ("unsupported color function 'color'").
// html-to-image works by serializing the DOM into an SVG <foreignObject>
// and letting the browser render it — modern CSS (color-mix, oklch, etc.)
// is handled natively by the browser, so this just works.
//
// Behavior:
//   - Renders nothing on prod (isDevHost gate). Visible on dev + preview.
//   - First click loads modern-screenshot from jsDelivr CDN.
//   - Snapshot of <html> sized to scrollWidth × scrollHeight so all
//     below-the-fold content is included.
//   - Mobile (browsers with file share) → native share sheet.
//   - Desktop / unsupported → PNG download.
//   - The pill + toast carry data-pfk-dev-screenshot-skip="1" so the
//     capture's `filter` strips them out of the PNG.
// ============================================================================
const _loadHtmlToImage = () => {
  if(typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if(window.htmlToImage) return Promise.resolve(window.htmlToImage);
  if(window.__pfkHtmlToImagePromise) return window.__pfkHtmlToImagePromise;
  window.__pfkHtmlToImagePromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://cdn.jsdelivr.net/npm/html-to-image@1.11.13/dist/html-to-image.min.js';
    s.async = true;
    s.onload = () => {
      if(window.htmlToImage) resolve(window.htmlToImage);
      else reject(new Error('htmlToImage global missing after load'));
    };
    s.onerror = () => reject(new Error('failed to load html-to-image'));
    document.head.appendChild(s);
  });
  return window.__pfkHtmlToImagePromise;
};

function DevScreenshotButton(){
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  if(!isDevHost()) return null;
  const SKIP_ATTR = 'data-pfk-dev-screenshot-skip';
  const flash = (text, ms = 2200) => {
    setMsg(text);
    setTimeout(() => setMsg(prev => prev === text ? '' : prev), ms);
  };
  const capture = async () => {
    if(busy) return;
    setBusy(true);
    setMsg('Loading…');
    let hti;
    try{
      hti = await _loadHtmlToImage();
    }catch(e){
      flash('Screenshot library failed to load', 3500);
      setBusy(false);
      return;
    }
    setMsg('Capturing…');
    try{
      // Let the "Capturing…" toast paint before the (synchronous-feeling)
      // DOM walk begins, otherwise the UI feels frozen on tall pages.
      await new Promise(r => requestAnimationFrame(r));
      const html = document.documentElement;
      const body = document.body;
      const width = Math.max(html.scrollWidth, body.scrollWidth, html.clientWidth);
      const height = Math.max(html.scrollHeight, body.scrollHeight, html.clientHeight);
      const bg = getComputedStyle(body).backgroundColor || '#0a0a0a';
      const blob = await hti.toBlob(html, {
        width, height,
        backgroundColor: bg,
        // html-to-image's `filter` returns true to KEEP an element, false
        // to skip it. We strip the floating pill + its toast.
        filter: el => !(el && el.nodeType === 1 && el.hasAttribute && el.hasAttribute(SKIP_ATTR)),
        cacheBust: true,
      });
      if(!blob) throw new Error('toBlob returned null');
      const stamp = new Date().toISOString().slice(0,19).replace(/[:T]/g,'-');
      const fname = `pfk-${stamp}.png`;
      // Prefer the native share sheet when the browser supports sharing
      // files (mostly mobile Safari + Android Chrome). That gives the user
      // a direct path to Twitter, Photos, Messages, etc.
      try{
        if(navigator.canShare){
          const file = new File([blob], fname, { type: 'image/png' });
          if(navigator.canShare({ files: [file] })){
            await navigator.share({ files: [file] });
            flash('Shared');
            return;
          }
        }
      }catch(shareErr){
        // User cancelled or share failed — fall through to download.
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      flash('Downloaded');
    }catch(e){
      flash('Failed: ' + (e?.message || 'unknown'), 3500);
    }finally{
      setBusy(false);
    }
  };
  return (
    <div {...{[SKIP_ATTR]: '1'}}
         style={{position:'fixed',bottom:96,right:14,zIndex:900,display:'flex',flexDirection:'column',alignItems:'flex-end',gap:6,pointerEvents:'none'}}>
      {msg && (
        <div {...{[SKIP_ATTR]: '1'}}
             style={{pointerEvents:'auto',background:'var(--bg-card)',border:'1px solid var(--accent-gold)',color:'var(--text-bright)',padding:'5px 10px',borderRadius:8,fontSize:11,fontWeight:700,whiteSpace:'nowrap',boxShadow:'0 2px 6px rgba(0,0,0,0.3)',fontFamily:'inherit'}}>
          {msg}
        </div>
      )}
      <button type="button" onClick={capture} disabled={busy}
              aria-label="Dev: capture full-page screenshot"
              data-tooltip="Dev only: full-page screenshot → share/download"
              {...{[SKIP_ATTR]: '1'}}
              style={{pointerEvents:'auto',width:48,height:48,borderRadius:'50%',background:'var(--bg-card)',border:'1.5px solid var(--accent-gold)',color:'var(--accent-gold-text)',cursor:busy?'wait':'pointer',display:'inline-flex',alignItems:'center',justifyContent:'center',fontSize:20,boxShadow:'0 4px 12px rgba(0,0,0,0.35)',fontFamily:'inherit',padding:0,lineHeight:1}}>
        {busy ? '⏳' : '📸'}
      </button>
    </div>
  );
}

// ============================================================================
// MasterToolbar — shared header across every PFK page (App, DispersalApp, LookupApp).
// Above the yellow line: brand + all 5 nav tabs (left) + X + Email Support + Sign in/out (right).
// Used so users can jump between tools from any page without going home first.
//
// Props:
//   currentTab      — only set on the main App; one of 'pfk'|'team'|'polls' to highlight tab
//   onSetTab        — only set on the main App; intercepts in-page tab clicks (no navigation)
//   onSignInClick   — only set on the main App; opens its full auth modal
//                     when not provided, "Sign In" navigates to /?signin=1 instead
// ============================================================================
// PfkBetaTag — site-wide "iteration in progress" indicator. Appended to
// the MasterToolbar wordmark subtitle in muted color so it reads as
// supplementary rather than alarming. Inherits uppercase + letter-spacing
// from the subtitle parent. Phase 3a polish item.
const PfkBetaTag = () => (
  <span data-pfk-beta="1" style={{marginLeft:8, color:'var(--text-muted)', fontWeight:600}}>· Beta</span>
);

function MasterToolbar({ currentTab, onSetTab, onSignInClick, userSleeperName, compact, subtitle, forceNewTabLinks }){
  const [session, setSession] = useState(null);
  // Read the linked Sleeper user so the Sleeper Snapshot tab can deep-link to
  // YOUR OWN profile instead of the empty search page when you're linked.
  const [sleeperLinked] = useSleeperUser();
  // Theme is initialized from the data-theme attr the no-flash boot script
  // already set on <html>. We just track + toggle from there. Persisted in
  // localStorage under "pfk-theme". CSS variables under :root[data-theme=...]
  // do all the visual work; this hook only flips the attribute.
  const [theme, setTheme] = useState(() => (typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme')) || 'dark');
  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    try {
      document.documentElement.setAttribute('data-theme', next);
      localStorage.setItem('pfk-theme', next);
    } catch(e){}
  };
  useEffect(()=>{
    if(!sb) return;
    sb.auth.getSession().then(({data}) => setSession(data?.session || null));
    const { data: sub } = sb.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  },[]);
  // Soft email-verification banner state. Shows when the logged-in user
  // has email_confirmed_at === null (i.e. signed up via the Supabase
  // mailer_autoconfirm path but hasn't clicked the verification link
  // yet). Dismiss is per-user-id, sessionStorage scoped, so closing the
  // banner doesn't leak across users on a shared device. Resend uses
  // sb.auth.resend({type:'signup'}) which redelivers the same
  // verification email Supabase originally sent at signup.
  const [verifyDismissedFor, setVerifyDismissedFor] = useState(null);
  const [verifyResending, setVerifyResending] = useState(false);
  const [verifyMsg, setVerifyMsg] = useState('');
  useEffect(() => {
    if(!session?.user?.id){ setVerifyDismissedFor(null); return; }
    try {
      const v = window.sessionStorage?.getItem('pfk-verify-dismissed-' + session.user.id);
      setVerifyDismissedFor(v === '1' ? session.user.id : null);
    } catch(_) { setVerifyDismissedFor(null); }
  }, [session?.user?.id]);
  const onResendVerify = async () => {
    if(!sb || !session?.user?.email || verifyResending) return;
    setVerifyResending(true);
    setVerifyMsg('');
    try {
      const { error } = await sb.auth.resend({ type:'signup', email: session.user.email });
      setVerifyMsg(error ? ('Could not send: ' + (error.message||'unknown error')) : 'Verification email sent. Check your inbox.');
    } catch(e){
      setVerifyMsg('Could not send: ' + (e.message || 'unknown error'));
    } finally {
      setVerifyResending(false);
      setTimeout(() => setVerifyMsg(''), 5000);
    }
  };
  const onDismissVerify = () => {
    if(!session?.user?.id) return;
    try { window.sessionStorage?.setItem('pfk-verify-dismissed-' + session.user.id, '1'); } catch(_){}
    setVerifyDismissedFor(session.user.id);
  };
  // Render decision: only when we have a logged-in user, their email is
  // unverified, and they haven't dismissed THIS user's banner in this
  // session. Verified users (email_confirmed_at populated) get nothing.
  const showVerifyBanner = !!(session?.user && !session.user.email_confirmed_at && verifyDismissedFor !== session.user.id);
  const verifyBanner = showVerifyBanner ? (
    <div data-pfk-verify-banner="1"
         style={{background:'color-mix(in srgb, var(--accent-gold) 6%, var(--bg-elevated))',borderBottom:'1px solid color-mix(in srgb, var(--accent-gold) 30%, transparent)',padding:'8px 12px'}}>
      <div style={{maxWidth:1140,margin:'0 auto',display:'flex',alignItems:'center',gap:10,flexWrap:'wrap',rowGap:6}}>
        <span style={{display:'inline-flex',alignItems:'center',gap:6,fontSize:12,color:'var(--text-secondary)',minWidth:0}}>
          <span aria-hidden="true" style={{color:'var(--accent-gold)',fontSize:14,lineHeight:1}}>✉️</span>
          Verify your email to secure your account.
        </span>
        <span style={{flex:1,minWidth:0}}/>
        <button onClick={onResendVerify} disabled={verifyResending}
                data-pfk-verify-resend="1"
                style={{padding:'5px 11px',background:'transparent',border:'1px solid color-mix(in srgb, var(--accent-gold) 45%, transparent)',borderRadius:6,color:'var(--accent-gold-text)',fontWeight:700,cursor:verifyResending?'default':'pointer',fontSize:11,letterSpacing:0.4,opacity:verifyResending?0.55:1,whiteSpace:'nowrap',fontFamily:'inherit'}}>
          {verifyResending ? 'Sending…' : 'Resend email'}
        </button>
        <button onClick={onDismissVerify}
                aria-label="Dismiss verification reminder for this session"
                data-pfk-verify-dismiss="1"
                style={{padding:'5px 10px',background:'transparent',border:'1px solid var(--border-hairline)',borderRadius:6,color:'var(--text-muted)',cursor:'pointer',fontSize:11,fontWeight:700,whiteSpace:'nowrap',fontFamily:'inherit'}}>
          Dismiss
        </button>
        {verifyMsg && (
          <div data-pfk-verify-msg="1" style={{flexBasis:'100%',fontSize:11,color:'var(--text-secondary)'}}>{verifyMsg}</div>
        )}
      </div>
    </div>
  ) : null;
  const doLogout = async () => { if(sb) await sb.auth.signOut(); };
  const doSignIn = (e) => {
    if(onSignInClick){ e.preventDefault(); onSignInClick(); }
    // else let the link navigate to /?signin=1
  };
  // Sleeper Snapshot is now the home page (Evan 2026-05-02). The tab points
  // at "/" — the root route auto-detects the linked Sleeper username from
  // localStorage and loads that user's profile directly, otherwise shows
  // the search page. Keeping `/lookup/{username}` working as the explicit
  // shareable URL for individual snapshots.
  // Tab definitions — Power Rankings has been moved out of the public nav
  // and into the admin panel; we'll work on it there until it's ready to ship.
  // Each tab: [id, label, descriptionForTooltip, href, isInPageOnMainPage]
  // Order locked by Evan 2026-05-02: Sleeper Snapshot (home) → Dispersal → Trade Finder → Rookie Ranks → Trade Polls.
  const showPickProjector = true;
  const tabs = [
    ["lookup","🔍 Sleeper Snapshot",sleeperLinked?.username?"Open your own Sleeper Snapshot — search any other username from inside":"Type any Sleeper username and see their account age, dynasty leagues, trade activity, orphan history, and roster strength — vet new leaguemates before letting them in","/",false],
    ...(showPickProjector ? [["pick","🔮 2027 Pick Projector","Project your team's 2027 1st-round pick slot in every dynasty league based on current roster best-ball value","/pick-projector",false]] : []),
    ["pfk2027","🏆 2027 Rookie Ranks","PFK's official 2027 dynasty rookie rankings — save your own order to power your Pick Projector cards","/?tab=pfk2027",true],
    ["dispersal","🎲 Dispersal Draft","Pool teams from a Sleeper league, share a link, and draft live with mobile-friendly real-time picks","/dispersal",false],
    ["trade","⚖️ Trade Finder","Type a rookie pick or player and see every equivalent-value asset grouped by position — find trade ideas in one click","/trade-finder",false],
    ["pfk","👑 2026 Rookie Ranks","PFK's official 2026 dynasty rookie rankings — view the staff tier list or build your own","/?tab=pfk",true],
    ["polls","🗳️ Trade Polls","Create a dynasty trade poll, share the link, and get votes from the community","/?tab=polls",true],
  ];
  const handleTabClick = (e, id, isInPage) => {
    // forceNewTabLinks (set by DispersalApp when the user has an active claim)
    // means each nav tab gets target="_blank" — let the browser open the new
    // tab instead of preventing the default to do an in-page swap.
    if(forceNewTabLinks) return;
    if(isInPage && onSetTab){
      e.preventDefault();
      onSetTab(id);
    }
    // else: let it navigate
  };
  const isActiveTab = (id) => {
    if(id === 'pfk') return currentTab === 'pfk' || currentTab === 'custom';
    return currentTab === id;
  };
  // Reusable bits used by both layouts so we don't duplicate URLs / aria.
  const twitterChip = (
    <a href="https://x.com/PlayForKeepsFF" target="_blank" rel="noopener noreferrer"
       aria-label="Follow @PlayForKeepsFF on X" data-tooltip="Follow @PlayforkeepsFF on X"
       style={{display:"inline-flex",alignItems:"center",gap:5,padding:"4px 9px",background:"var(--bg-elevated)",border:"1px solid rgba(var(--accent-gold-rgb),0.33)",borderRadius:16,color:"var(--accent-gold-text)",textDecoration:"none",fontWeight:800,fontSize:11,letterSpacing:0.3,transition:"all .15s"}}
       onMouseEnter={e=>{e.currentTarget.style.background='var(--accent-gold)';e.currentTarget.style.color='#000';e.currentTarget.querySelector('svg').setAttribute('fill','#000');}}
       onMouseLeave={e=>{e.currentTarget.style.background='var(--bg-elevated)';e.currentTarget.style.color='var(--accent-gold)';e.currentTarget.querySelector('svg').setAttribute('fill','var(--accent-gold)');}}>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="var(--accent-gold-text)" aria-hidden="true"><path d="M18.244 2H21.5l-7.5 8.57L23 22h-6.844l-5.36-6.72L4.5 22H1.244l8.04-9.187L1 2h7.016l4.844 6.12L18.244 2zm-1.2 18h1.9L7.048 4H5.05l12 16z"/></svg>
      <span>@PlayforkeepsFF</span>
    </a>
  );
  const emailChip = (
    <a href="mailto:playforkeepsdynasty@gmail.com?subject=PFK%20Support"
       aria-label="Email PFK support with feedback or bug reports"
       data-tooltip="Email PFK support with feedback, bug reports, or partnership inquiries"
       style={{display:"inline-flex",alignItems:"center",gap:5,padding:"4px 10px",background:"var(--bg-elevated)",border:"1px solid rgba(var(--accent-gold-rgb),0.33)",borderRadius:16,color:"var(--accent-gold-text)",textDecoration:"none",fontWeight:700,fontSize:11,letterSpacing:0.3}}>
      📧 Email Support
    </a>
  );
  // Theme toggle. Icon shows the CURRENT theme (moon in dark, sun in light);
  // tooltip + aria-label describe the action ("Switch to light mode").
  // Uses CSS variables so the chip itself recolors correctly in both themes,
  // unlike most of the inline-styled UI which is migrated in a follow-up.
  const themeToggleChip = (
    <button onClick={toggleTheme}
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            data-tooltip={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:30,height:26,padding:0,background:"var(--bg-elevated)",border:"1px solid rgba(var(--accent-gold-rgb),0.33)",borderRadius:14,color:"var(--accent-gold-text)",cursor:"pointer",flexShrink:0}}>
      {theme === 'dark' ? (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
        </svg>
      ) : (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="4"/>
          <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/>
        </svg>
      )}
    </button>
  );
  // Compact layout — used on dispersal pages. Brand is centered with the
  // page-specific subtitle (e.g. "Dispersal Draft") below, and the social
  // chips sit on a centered row underneath. Padding is tightened so the
  // page content can start much higher up the viewport. The full-fat layout
  // (other pages) keeps its left-aligned brand + nav tabs.
  if (compact) {
    // Compact toolbar Contact Support chip — generic subject, but body
    // includes window.location.href which contains /dispersal/{draftId}
    // so support emails still pinpoint the exact draft.
    const compactSupportChip = (
      <a href={`mailto:playforkeepsdynasty@gmail.com?subject=${encodeURIComponent('Dispersal Draft Support')}&body=${encodeURIComponent(`Draft link: ${typeof window !== 'undefined' ? window.location.href : ''}\n\nDescribe the issue:\n`)}`}
         aria-label="Email PFK support about this dispersal draft"
         title="Email PFK support — pre-fills this draft's link so we can help faster"
         style={{display:"inline-flex",alignItems:"center",gap:5,padding:"4px 10px",background:"var(--bg-elevated)",border:"1px solid rgba(var(--accent-gold-rgb),0.33)",borderRadius:16,color:"var(--accent-gold-text)",textDecoration:"none",fontWeight:700,fontSize:11,letterSpacing:0.3}}>
        📧 Contact Support
      </a>
    );
    return (
      // padding-top respects iOS Safari safe-area-inset-top so the
      // brand row doesn't tuck under the dynamic URL bar / notch on iPhone.
      <>
      <div className="pfk-sticky-header" style={{background:"var(--bg-elevated)",borderBottom:"2px solid var(--accent-gold)",padding:"max(6px, env(safe-area-inset-top)) 12px 6px",position:"sticky",top:0,zIndex:100}}>
        <div style={{maxWidth:1140,margin:"0 auto"}}>
          {/* Top row: small logo + brand title side-by-side, centered as a group.
              When forceNewTabLinks is on (active claimed dispersal draft),
              the brand link opens in a new tab so the user doesn't lose
              their draft connection / queue / auto-pick state. */}
          <a href="/" {...(forceNewTabLinks ? { target:"_blank", rel:"noopener noreferrer" } : {})} style={{display:"flex",alignItems:"center",justifyContent:"center",gap:10,textDecoration:"none"}}>
            <img className="pfk-logo-img" src="/img/pfk-logo.png" alt="PFK" style={{width:48,height:48,objectFit:"contain",flexShrink:0}} onError={e=>e.target.style.display="none"}/>
            <div style={{textAlign:"center"}}>
              <div className="pfk-header-title" style={{fontSize:20,fontWeight:900,color:"var(--wordmark-color)",letterSpacing:2.5,lineHeight:1.05}}>PLAY FOR KEEPS</div>
              {subtitle && (
                <div style={{fontSize:10,color:"var(--wordmark-subtitle)",letterSpacing:2,textTransform:"uppercase",fontWeight:700,marginTop:2}}>{subtitle}<PfkBetaTag/></div>
              )}
            </div>
          </a>
          {/* Nav tabs row — added to compact mode so users actively in a
              dispersal draft can jump to other PFK tools. When
              forceNewTabLinks is set, each tab opens in a new tab so the
              dispersal session stays alive. Reuses the same .pfk-tabs-wrap /
              .pfk-top-tabs CSS as the full layout for mobile horizontal-
              scroll behavior. */}
          <div className="pfk-tabs-wrap" style={{position:"relative",width:"100%",marginTop:8}}>
            <div className="pfk-top-tabs" style={{display:"flex",gap:4,flexWrap:"wrap",justifyContent:"center",width:"100%"}}>
              {tabs.map(([id,label,desc,href,isInPage])=>{
                const active = isActiveTab(id);
                const linkProps = forceNewTabLinks ? { target:"_blank", rel:"noopener noreferrer" } : {};
                return (
                  <a key={id} href={href} {...linkProps} onClick={e=>handleTabClick(e,id,isInPage)} aria-label={desc} data-tooltip={desc}
                     style={{padding:"5px 10px",borderRadius:6,border:active?"1.5px solid var(--accent-gold)":"1.5px solid var(--border)",background:active?"var(--accent-gold)":"transparent",color:active?"#000":"var(--text-secondary)",fontWeight:700,fontSize:11,cursor:"pointer",letterSpacing:0.5,textDecoration:"none",display:"inline-flex",alignItems:"center",lineHeight:1.4,transition:"all .15s",whiteSpace:"nowrap"}}>{label}</a>
                );
              })}
            </div>
          </div>
          {/* Bottom row: utility chips hugging the yellow border-bottom.
              flex space-between puts X chip far-left and Contact Support
              far-right, with the theme toggle nestled next to support. */}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:8,gap:8,flexWrap:"wrap"}}>
            {twitterChip}
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              {themeToggleChip}
              {compactSupportChip}
            </div>
          </div>
        </div>
      </div>
      {verifyBanner}
      </>
    );
  }
  // Default layout (non-compact pages): centered brand block on top with the
  // logo + "PLAY FOR KEEPS" + "Dynasty Fantasy Football" stacked. Account
  // chips and nav tabs sit on centered rows below. Dispersal pages use the
  // separate compact branch above (kept untouched per Evan's request).
  return (
    <>
    <div className="pfk-sticky-header" style={{background:"var(--bg-elevated)",borderBottom:"2px solid var(--accent-gold)",padding:"8px 12px",position:"sticky",top:0,zIndex:100}}>
      {/* Twitter chip — absolute top-LEFT corner. Was inline below the
          brand; moving it into the corner reclaims vertical space so
          more of the page is visible above the fold. */}
      <div className="pfk-toolbar-tl" style={{position:"absolute",top:8,left:12,zIndex:101}}>
        {twitterChip}
      </div>
      {/* SIGN IN / OUT (top) + Sleeper account (directly underneath) — both
          live in the top-right corner so account state has a permanent home
          and the brand block in the center has full width to breathe. The
          .pfk-signin-anchor + .pfk-toolbar-tr classes let mobile CSS shrink
          everything in this corner to free up horizontal room. */}
      <div className="pfk-toolbar-tr" style={{position:"absolute",top:8,right:12,zIndex:101,display:"flex",flexDirection:"column",alignItems:"flex-end",gap:6}}>
        <div className="pfk-signin-anchor" style={{display:"flex",alignItems:"center",gap:6}}>
          {themeToggleChip}
          {session ? (
            <button onClick={doLogout} style={{padding:"6px 10px",background:"transparent",border:"1px solid var(--border-outline)",borderRadius:6,color:"var(--text-muted)",cursor:"pointer",fontSize:11,fontWeight:700,letterSpacing:0.3}}>Sign Out</button>
          ) : (
            <a href={(() => {
              // Carry the current page+search forward so post-auth lands the
              // user back where they were (instead of the home page). Skip
              // when we're already on '/' — the return param would be redundant.
              const here = (typeof window !== 'undefined') ? (window.location.pathname + window.location.search) : '/';
              return here && here !== '/' ? `/?signin=1&return=${encodeURIComponent(here)}` : '/?signin=1';
            })()} onClick={doSignIn} style={{padding:"7px 14px",background:"var(--accent-gold)",border:"none",borderRadius:6,color:"#000",fontWeight:900,cursor:"pointer",fontSize:12,letterSpacing:1,textDecoration:"none",display:"inline-block"}}>SIGN IN</a>
          )}
        </div>
        <SleeperLink/>
      </div>
      <div style={{maxWidth:1140,margin:"0 auto",display:"flex",flexDirection:"column",gap:6,alignItems:"center"}}>
        <a href="/" className="pfk-brand-link" style={{display:"flex",flexDirection:"column",alignItems:"center",gap:6,textDecoration:"none",justifyContent:"center"}}>
          {/* Mark-only logo — the wordmark is rendered as text below so we
              don't end up with "Play For Keeps" twice (image + heading). */}
          <img className="pfk-logo-img" src="/img/pfk-logo-mark.png" alt="PFK" style={{width:80,height:80,objectFit:"contain",flexShrink:0}} onError={e=>e.target.style.display="none"}/>
          <div style={{textAlign:"center"}}>
            <div className="pfk-header-title" style={{fontSize:26,fontWeight:900,color:"var(--wordmark-color)",letterSpacing:3,lineHeight:1.05}}>PLAY FOR KEEPS</div>
            <div style={{fontSize:12,color:"var(--wordmark-subtitle)",letterSpacing:2.5,textTransform:"uppercase",fontWeight:700,marginTop:4,textAlign:"center"}}>Dynasty Fantasy Football<PfkBetaTag/></div>
          </div>
        </a>
        {/* Nav tabs — centered on desktop, anchored to flex-start on mobile
            via CSS. The wrapper exists purely to host the right-edge gradient
            overlay (.pfk-tabs-wrap::after) that hints at horizontal scroll on
            mobile when not all tabs fit on screen. */}
        <div className="pfk-tabs-wrap" style={{position:"relative",width:"100%"}}>
          <div className="pfk-top-tabs" style={{display:"flex",gap:4,flexWrap:"wrap",justifyContent:"center",width:"100%"}}>
            {tabs.map(([id,label,desc,href,isInPage])=>{
              const active = isActiveTab(id);
              return (
                <a key={id} href={href} onClick={e=>handleTabClick(e,id,isInPage)} aria-label={desc} data-tooltip={desc}
                   style={{padding:"5px 10px",borderRadius:6,border:active?"1.5px solid var(--accent-gold)":"1.5px solid var(--border)",background:active?"var(--accent-gold)":"transparent",color:active?"#000":"var(--text-secondary)",fontWeight:700,fontSize:12,cursor:"pointer",letterSpacing:0.6,textDecoration:"none",display:"inline-flex",alignItems:"center",lineHeight:1.4,transition:"all .15s"}}>{label}</a>
              );
            })}
          </div>
        </div>
      </div>
    </div>
    {verifyBanner}
    <DevScreenshotButton/>
    </>
  );
}

// ============================================================================
// RookieRanks2027 — public-facing 2027 rookie rankings page.
// ============================================================================
// Chunks 2-3 of the 2027 Rookie Ranks feature.
//   Logged-out viewers see the PFK published list, read-only, with a
//   signup/login CTA banner explaining the value-add (save your own
//   order, surfaces on your Pick Projector cards).
//   Logged-in viewers can drag-reorder + ↑↓ chevron-reorder the list,
//   then click Save to upsert it into user_rookie_ranks_2027 (RLS-gated
//   by auth.uid()). Revert hard-deletes the row.
//
// In-progress edits survive page navigations within the same browser
// session via sessionStorage (pfk_2027_pending_order, scoped by user_id
// in the stored payload so different users on the same browser can't
// leak each other's pending state). The "Welcome back!" restore banner
// only appears when a pending order was rehydrated on mount.
//
// New rookies PFK adds after a user has saved are appended to the
// bottom of the user's order automatically — silent filter for orphans
// matches the approved Chunk 5 spec; the "X NEW" pill + the
// "PFK removed N rookies" toast are Chunk 5 polish, not yet wired.
const RR2027_SESSION_KEY = 'pfk_2027_pending_order';
function RookieRanks2027({ session, onRequestSignIn, onRequestSignUp }){
  const [rookies, setRookies] = useState(null); // null = loading, [] = no rookies, [...] = list
  // Logged-in editor state. userOrder is the array of rookie ids in
  // the order the user wants. Null until both the PFK list and the
  // saved row (if any) have loaded.
  const [userOrder, setUserOrder] = useState(null);
  const [savedRow, setSavedRow] = useState(null); // server row, null if none / not loaded yet
  const [savedRowLoaded, setSavedRowLoaded] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [actionMsg, setActionMsg] = useState(''); // "Saved ✓" / "Reverted" / error text
  const [restoredFromSession, setRestoredFromSession] = useState(false);
  // Drag-and-drop state. The 2026 Rookie Ranks page uses pointer events
  // (not native HTML5 draggable=true) because pointer events unify
  // touch + mouse + pen, work reliably on mobile, and let us
  // setPointerCapture so the drag stays attached even if the cursor
  // leaves the source row. The earlier HTML5 implementation here
  // highlighted rows on dragstart but the drop event silently failed
  // on Safari and the iOS Sleeper in-app browser — the same problem the
  // 2026 page solved by switching away from native drag months ago.
  const [draggingId, setDraggingId] = useState(null);
  const [insertBeforeId, setInsertBeforeId] = useState(null); // id to insert before, or null = end
  const [ghostPos, setGhostPos] = useState({x:0, y:0});
  const [ghostOff, setGhostOff] = useState({x:0, y:0});
  const rowRefs = useRef({});
  // dragRef holds pending+committed drag state without stale-closure
  // issues. Pattern lifted from the 2026 RenderList.
  const dragRef = useRef({ pending:null, dragging:null, insertBeforeId:null });

  // Fetch PFK published rookies once on mount.
  useEffect(() => {
    let alive = true;
    if(!sb){ setRookies([]); return; }
    sb.from('rookie_board_2027')
      .select('rookies')
      .eq('state', 'published')
      .limit(1)
      .then(({ data, error }) => {
        if(!alive) return;
        if(error || !data || !data[0]){ setRookies([]); return; }
        setRookies(Array.isArray(data[0].rookies) ? data[0].rookies : []);
      });
    return () => { alive = false; };
  }, []);

  // Fetch the user's saved row when they're logged in. Marks
  // savedRowLoaded true regardless of whether a row exists so the
  // init-userOrder effect below knows the lookup completed.
  useEffect(() => {
    const uid = session?.user?.id;
    if(!uid || !sb){ setSavedRow(null); setSavedRowLoaded(!uid); return; }
    let alive = true;
    sb.from('user_rookie_ranks_2027')
      .select('user_id, ranks, last_known_official_ids, updated_at')
      .eq('user_id', uid)
      .maybeSingle()
      .then(({ data, error }) => {
        if(!alive) return;
        if(error || !data){ setSavedRow(null); }
        else { setSavedRow(data); }
        setSavedRowLoaded(true);
      });
    return () => { alive = false; };
  }, [session?.user?.id]);

  // Once both PFK list AND the saved-row fetch have settled, initialize
  // userOrder. Order of preference:
  //   1. sessionStorage pending order (if it belongs to THIS user)
  //   2. Saved-row ranks (filtered for orphans + appended for new
  //      PFK additions)
  //   3. PFK official order
  // Only runs once per mount — subsequent edits flow through
  // setUserOrder + setIsDirty directly.
  useEffect(() => {
    if(!rookies || !savedRowLoaded) return;
    if(userOrder !== null) return; // already initialized
    const publishedIds = rookies.map(r => r.id);
    let baseOrder;
    if(savedRow && Array.isArray(savedRow.ranks)){
      const savedFiltered = savedRow.ranks.filter(id => publishedIds.includes(id));
      const newAdditions = publishedIds.filter(id => !savedRow.ranks.includes(id));
      baseOrder = [...savedFiltered, ...newAdditions];
    } else {
      baseOrder = publishedIds;
    }
    // sessionStorage restore — only consult when the user is logged in
    // (logged-out users can't edit, so a pending order would be stale).
    const uid = session?.user?.id;
    if(uid){
      try {
        const raw = window.sessionStorage?.getItem(RR2027_SESSION_KEY);
        if(raw){
          const stored = JSON.parse(raw);
          if(stored && stored.user_id === uid && Array.isArray(stored.order)){
            const restored = stored.order.filter(id => publishedIds.includes(id));
            const restoredMissing = publishedIds.filter(id => !stored.order.includes(id));
            const candidate = [...restored, ...restoredMissing];
            // Only consider it "pending" if it differs from what we'd
            // have shown without sessionStorage — otherwise the banner
            // would lie about restoring an in-progress edit.
            if(JSON.stringify(candidate) !== JSON.stringify(baseOrder)){
              setUserOrder(candidate);
              setIsDirty(true);
              setRestoredFromSession(true);
              return;
            }
          }
        }
      } catch(_){ /* parse / storage failure — fall through */ }
    }
    setUserOrder(baseOrder);
    setIsDirty(false);
  }, [rookies, savedRowLoaded, savedRow, session?.user?.id, userOrder]);

  // Persist any dirty in-progress edit to sessionStorage so a remount
  // (auth modal, tab nav, refresh) doesn't lose it.
  useEffect(() => {
    const uid = session?.user?.id;
    if(!uid || !isDirty || !Array.isArray(userOrder)) return;
    try {
      window.sessionStorage?.setItem(RR2027_SESSION_KEY, JSON.stringify({
        user_id: uid,
        order: userOrder,
        ts: Date.now(),
      }));
    } catch(_){ /* private mode — ignore */ }
  }, [userOrder, isDirty, session?.user?.id]);

  const clearPending = () => {
    try { window.sessionStorage?.removeItem(RR2027_SESSION_KEY); } catch(_){}
    setRestoredFromSession(false);
  };

  const moveUp = (idx) => {
    if(!userOrder || idx <= 0) return;
    const next = userOrder.slice();
    [next[idx-1], next[idx]] = [next[idx], next[idx-1]];
    setUserOrder(next);
    setIsDirty(true);
    setActionMsg('');
  };
  const moveDown = (idx) => {
    if(!userOrder || idx >= userOrder.length - 1) return;
    const next = userOrder.slice();
    [next[idx], next[idx+1]] = [next[idx+1], next[idx]];
    setUserOrder(next);
    setIsDirty(true);
    setActionMsg('');
  };
  // Pointer-event drag handlers, mirroring 2026 RenderList.
  // Pattern: pointerdown stores a "pending" intent; pointermove commits
  // to drag only after the cursor has moved >10px (so taps and quick
  // clicks don't accidentally start drags); pointerup applies the
  // reorder if a drag was committed.
  // calcInsert: given the cursor's clientY and the id we're dragging,
  // find which OTHER row's vertical midpoint the cursor is above —
  // that's the row we'll insert BEFORE. Returns null when the cursor
  // is below every row (= insert at end).
  const calcInsert = useCallback((clientY, excludeId) => {
    const rows = Object.entries(rowRefs.current)
      .filter(([id, el]) => el && id !== excludeId)
      .map(([id, el]) => {
        const r = el.getBoundingClientRect();
        return { id, mid: r.top + r.height / 2 };
      })
      .sort((a, b) => a.mid - b.mid);
    for(const { id, mid } of rows){ if(clientY < mid) return id; }
    return null;
  }, []);

  // Single cleanup primitive — used by every exit path (pointerup,
  // pointercancel, document-level safety listener, defensive reset
  // inside pointerdown). Releases any lingering pointer capture so a
  // stale handle DOM from a prior drag can't redirect subsequent
  // page-level pointer events to itself.
  const cleanupDrag = useCallback(() => {
    const dr = dragRef.current;
    const el = dr.captureEl;
    const pid = dr.capturePointerId;
    if(el && pid != null){
      try {
        if(typeof el.hasPointerCapture === 'function' && el.hasPointerCapture(pid)){
          el.releasePointerCapture(pid);
        }
      } catch(_){}
    }
    dr.pending = null;
    dr.dragging = null;
    dr.insertBeforeId = null;
    dr.captureEl = null;
    dr.capturePointerId = null;
    setDraggingId(null);
    setInsertBeforeId(null);
  }, []);

  const onRowPointerDown = (e, id) => {
    // Mouse: only left button starts a drag. Touch/pen: always start.
    if(e.pointerType === 'mouse' && e.button !== 0) return;
    if(!canEdit) return;
    // Defensive: any lingering state from a previous incomplete drag
    // (e.g. iOS lost the pointerup because the user dragged off-screen)
    // gets wiped here so the new gesture starts clean.
    if(dragRef.current.pending || dragRef.current.dragging){
      cleanupDrag();
    }
    const r = e.currentTarget.getBoundingClientRect();
    dragRef.current.pending = {
      id, pointerId: e.pointerId, el: e.currentTarget,
      startX: e.clientX, startY: e.clientY,
      offX: e.clientX - r.left, offY: e.clientY - r.top,
    };
  };
  const onRowPointerMove = useCallback((e) => {
    const dr = dragRef.current;
    if(dr.dragging){
      // Already in a drag → keep updating ghost + insert indicator
      const ib = calcInsert(e.clientY, dr.dragging);
      dr.insertBeforeId = ib;
      setGhostPos({ x:e.clientX, y:e.clientY });
      setInsertBeforeId(ib);
      return;
    }
    if(!dr.pending) return;
    const dist = Math.hypot(e.clientX - dr.pending.startX, e.clientY - dr.pending.startY);
    if(dist < 10) return; // not enough movement yet — let scroll happen
    // Commit to drag
    e.preventDefault();
    try {
      dr.pending.el.setPointerCapture(dr.pending.pointerId);
      // Track exactly which element + pointer we captured so cleanup
      // can release them deterministically.
      dr.captureEl = dr.pending.el;
      dr.capturePointerId = dr.pending.pointerId;
    } catch(_){}
    setGhostOff({ x:dr.pending.offX, y:dr.pending.offY });
    setGhostPos({ x:e.clientX, y:e.clientY });
    dr.dragging = dr.pending.id;
    const ib = calcInsert(e.clientY, dr.pending.id);
    dr.insertBeforeId = ib;
    dr.pending = null;
    setDraggingId(dr.dragging);
    setInsertBeforeId(ib);
  }, [calcInsert]);
  const onRowPointerUp = useCallback(() => {
    const dr = dragRef.current;
    if(dr.dragging && Array.isArray(userOrder)){
      const fromId = dr.dragging;
      const beforeId = dr.insertBeforeId;
      const fromIdx = userOrder.indexOf(fromId);
      if(fromIdx >= 0 && fromId !== beforeId){
        const next = userOrder.slice();
        next.splice(fromIdx, 1);
        if(beforeId == null){
          next.push(fromId);
        } else {
          const toIdx = next.indexOf(beforeId);
          if(toIdx >= 0) next.splice(toIdx, 0, fromId);
          else next.push(fromId);
        }
        if(JSON.stringify(next) !== JSON.stringify(userOrder)){
          setUserOrder(next);
          setIsDirty(true);
          setActionMsg('');
        }
      }
    }
    cleanupDrag();
  }, [userOrder, cleanupDrag]);

  // Document-level failsafe — if the handle's local pointerup somehow
  // doesn't fire (Safari lift-off-screen quirks, the captured element
  // getting unmounted mid-drag, etc.), the global listener still
  // catches the gesture's end and runs cleanup. Only attached while a
  // drag is actually committed, so idle-state pointer events on the
  // document are untouched.
  useEffect(() => {
    if(!draggingId) return;
    const handler = () => onRowPointerUp();
    document.addEventListener('pointerup', handler);
    document.addEventListener('pointercancel', handler);
    return () => {
      document.removeEventListener('pointerup', handler);
      document.removeEventListener('pointercancel', handler);
    };
  }, [draggingId, onRowPointerUp]);

  // Compare the user's snapshot of the PFK published pool at last save
  // (savedRow.last_known_official_ids) against the live published pool,
  // and surface a one-time toast when PFK has REMOVED rookies since
  // last save. The "NEW pill" sibling derivation (newIdsSinceSave) was
  // removed 2026-05-21 — Evan decided the appended-at-bottom signal was
  // adequate without an extra pill cluttering rows. Logged-out viewers
  // and PFK users without a saved row see an empty array (no snapshot
  // to diff against).
  const publishedIds = rookies ? rookies.map(r => r.id) : [];
  const savedSnapshot = (savedRow && Array.isArray(savedRow.last_known_official_ids)) ? savedRow.last_known_official_ids : null;
  const removedIdsSinceSave = savedSnapshot ? savedSnapshot.filter(id => !publishedIds.includes(id)) : [];
  const showRemovedToast = removedIdsSinceSave.length > 0 && !!session?.user?.id;
  const onDismissRemovedToast = async () => {
    if(!session?.user?.id || !sb || !savedRow) return;
    // Optimistic local update so the toast disappears immediately on
    // click — even if the server write fails, the user gets the right
    // visual response and the worst case is the toast reappears on
    // next mount (acceptable failure mode).
    const newSnapshot = publishedIds.slice();
    setSavedRow(prev => prev ? { ...prev, last_known_official_ids: newSnapshot } : prev);
    try {
      await sb.from('user_rookie_ranks_2027')
        .update({ last_known_official_ids: newSnapshot })
        .eq('user_id', session.user.id);
    } catch(_){ /* swallowed — see comment above */ }
  };

  const onSave = async () => {
    if(!session?.user?.id || saving || !Array.isArray(userOrder) || !rookies) return;
    setSaving(true);
    setActionMsg('');
    const publishedIds = rookies.map(r => r.id);
    const { error } = await sb.from('user_rookie_ranks_2027').upsert({
      user_id: session.user.id,
      ranks: userOrder,
      last_known_official_ids: publishedIds,
    }, { onConflict: 'user_id' });
    setSaving(false);
    if(error){
      setActionMsg('Save failed: ' + (error.message || 'unknown error'));
      setTimeout(() => setActionMsg(m => m.startsWith('Save failed') ? '' : m), 6000);
      return;
    }
    setSavedRow({ user_id: session.user.id, ranks: userOrder, last_known_official_ids: publishedIds, updated_at: new Date().toISOString() });
    setIsDirty(false);
    clearPending();
    setActionMsg('Saved ✓');
    setTimeout(() => setActionMsg(m => m === 'Saved ✓' ? '' : m), 2500);
  };

  const onRevert = async () => {
    if(!session?.user?.id || reverting) return;
    const ok = window.confirm("Use PFK's official 2027 rookie ranks? Your custom order will be deleted.");
    if(!ok) return;
    setReverting(true);
    setActionMsg('');
    const { error } = await sb.from('user_rookie_ranks_2027')
      .delete()
      .eq('user_id', session.user.id);
    setReverting(false);
    if(error){
      setActionMsg('Revert failed: ' + (error.message || 'unknown error'));
      setTimeout(() => setActionMsg(m => m.startsWith('Revert failed') ? '' : m), 6000);
      return;
    }
    const publishedIds = rookies ? rookies.map(r => r.id) : [];
    setUserOrder(publishedIds);
    setSavedRow(null);
    setIsDirty(false);
    clearPending();
    setActionMsg('Switched to PFK ranks');
    setTimeout(() => setActionMsg(m => m === 'Switched to PFK ranks' ? '' : m), 2500);
  };

  // Position color tokens borrowed from the existing rookie-board palette.
  const posColor = (pos) => ({
    QB: 'var(--accent-violet)',
    RB: 'var(--success)',
    WR: 'var(--accent-gold)',
    TE: 'var(--warning)',
  })[pos] || 'var(--text-muted)';

  // Compute the rendered order. Logged-out users always see PFK official
  // (the rookies array as-fetched). Logged-in users see their in-progress
  // userOrder once it's initialized.
  const rookieById = rookies ? Object.fromEntries(rookies.map(r => [r.id, r])) : {};
  const displayRookies = (session && Array.isArray(userOrder))
    ? userOrder.map(id => rookieById[id]).filter(Boolean)
    : (rookies || []);
  const canEdit = !!session && Array.isArray(userOrder);

  return (
    <div data-pp2027-root="1" style={{maxWidth:740,margin:'0 auto',padding:'20px 12px 60px',color:'var(--text-primary)',fontFamily:"'Inter','Segoe UI',sans-serif"}}>
      {/* Title block */}
      <div style={{marginBottom:14}}>
        <h1 style={{fontSize:24,fontWeight:900,color:'var(--accent-gold-text)',letterSpacing:1.5,margin:0}}>🏆 2027 Rookie Ranks</h1>
        <p style={{fontSize:13,color:'var(--text-secondary)',marginTop:6,lineHeight:1.55,maxWidth:680}}>
          PFK's official 2027 dynasty rookie rankings. Logged-in PFK users can save their own order to power their Pick Projector cards across every league.
        </p>
      </div>

      {/* Auth-state-aware top banner */}
      {!session ? (
        <div data-pp2027-cta="logged-out"
             style={{background:'color-mix(in srgb, var(--accent-gold) 8%, var(--bg-elevated))',border:'1px solid color-mix(in srgb, var(--accent-gold) 35%, transparent)',borderRadius:10,padding:'14px 16px',marginBottom:18}}>
          <div style={{fontSize:13,color:'var(--text-primary)',lineHeight:1.55,marginBottom:10}}>
            Want to customize your own 2027 rookie ranking? Sign up for a free PFK account to save your order. Your saved ranking will appear on every Pick Projector card across all your leagues.
          </div>
          <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
            <button type="button" onClick={onRequestSignUp}
                    data-pp2027-cta-signup="1"
                    style={{padding:'8px 16px',background:'var(--accent-gold)',border:'none',borderRadius:7,color:'#000',fontWeight:900,cursor:'pointer',fontSize:13,letterSpacing:0.5,fontFamily:'inherit'}}>
              Sign up
            </button>
            <button type="button" onClick={onRequestSignIn}
                    data-pp2027-cta-signin="1"
                    style={{padding:'8px 16px',background:'transparent',border:'1px solid var(--border-strong)',borderRadius:7,color:'var(--text-primary)',fontWeight:800,cursor:'pointer',fontSize:13,letterSpacing:0.5,fontFamily:'inherit'}}>
              Log in
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* sessionStorage restore notice — only when an in-progress
              edit was rehydrated on this mount. Quietly disappears
              after first Save / Revert. */}
          {restoredFromSession && isDirty && (
            <div data-pp2027-restore-banner="1"
                 style={{background:'color-mix(in srgb, var(--accent-gold) 6%, var(--bg-elevated))',border:'1px dashed color-mix(in srgb, var(--accent-gold) 40%, transparent)',borderRadius:8,padding:'10px 14px',marginBottom:10,fontSize:12,color:'var(--text-secondary)',lineHeight:1.5}}>
              👋 Welcome back! Your in-progress order has been restored — click <strong style={{color:'var(--text-primary)'}}>Save</strong> to keep it.
            </div>
          )}
          {/* Action bar — Save + Revert + transient status text. */}
          <div data-pp2027-actions="1"
               style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap',marginBottom:14,padding:'10px 14px',background:'var(--bg-elevated)',border:'1px solid var(--border)',borderRadius:10}}>
            <span style={{fontSize:11,color:'var(--text-muted)',fontWeight:800,letterSpacing:0.8,textTransform:'uppercase'}}>Your 2027 ranks</span>
            <span style={{flex:1,minWidth:0,fontSize:11,color:'var(--text-secondary)'}}>
              {actionMsg
                ? <span data-pp2027-action-msg="1" style={{color: (actionMsg.startsWith('Saved') || actionMsg.startsWith('Switched')) ? 'var(--success)' : (actionMsg.includes('failed') ? 'var(--danger)' : 'var(--text-secondary)'),fontWeight:700}}>{actionMsg}</span>
                : (isDirty
                    ? <span style={{color:'var(--accent-gold-text)',fontWeight:700}}>● Unsaved changes</span>
                    : (savedRow ? <span>Custom order is saved.</span> : <span>Drag rows or use ↑/↓ to reorder, then Save.</span>))}
            </span>
            <button type="button" onClick={onSave} disabled={!isDirty || saving || !Array.isArray(userOrder)}
                    data-pp2027-save="1"
                    style={{padding:'7px 16px',background:(isDirty && !saving)?'var(--accent-gold)':'var(--bg-pill)',border:'none',borderRadius:7,color:(isDirty && !saving)?'#000':'var(--text-muted)',fontWeight:900,cursor:(isDirty && !saving)?'pointer':'default',fontSize:13,letterSpacing:0.5,fontFamily:'inherit',opacity:saving?0.6:1}}>
              {saving ? 'Saving…' : '💾 Save'}
            </button>
            <button type="button" onClick={onRevert} disabled={reverting || !savedRow}
                    data-pp2027-revert="1"
                    style={{padding:'7px 14px',background:'transparent',border:'1px solid var(--border-outline)',borderRadius:7,color:savedRow?'var(--text-secondary)':'var(--text-muted)',fontWeight:800,cursor:(savedRow && !reverting)?'pointer':'default',fontSize:12,letterSpacing:0.4,fontFamily:'inherit',opacity:reverting?0.6:1}}>
              {reverting ? 'Switching…' : '↺ Use PFK Ranks'}
            </button>
          </div>
        </>
      )}

      {/* Removed-rookie toast — fires once when PFK has pruned rookies
          from the pool since the user's last save. Dismissing it
          syncs last_known_official_ids to the current published set
          so it won't re-fire on next mount. */}
      {showRemovedToast && (
        <div data-pp2027-removed-toast="1"
             style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',marginBottom:12,background:'color-mix(in srgb, var(--warning) 8%, var(--bg-elevated))',border:'1px solid color-mix(in srgb, var(--warning) 35%, transparent)',borderRadius:8,fontSize:12,color:'var(--text-secondary)',lineHeight:1.5}}>
          <span aria-hidden="true" style={{color:'var(--warning)',fontSize:14,lineHeight:1}}>ℹ️</span>
          <span style={{flex:1,minWidth:0}}>
            PFK removed <strong style={{color:'var(--text-primary)'}}>{removedIdsSinceSave.length}</strong> rookie{removedIdsSinceSave.length===1?'':'s'} from the 2027 pool since your last save.
          </span>
          <button type="button" onClick={onDismissRemovedToast}
                  aria-label="Dismiss"
                  data-pp2027-removed-toast-dismiss="1"
                  style={{padding:'4px 8px',background:'transparent',border:'1px solid var(--border-hairline)',borderRadius:5,color:'var(--text-muted)',cursor:'pointer',fontSize:12,fontWeight:700,lineHeight:1,fontFamily:'inherit',flexShrink:0}}>
            ✕
          </button>
        </div>
      )}

      {/* List */}
      {rookies === null && (
        <div data-pp2027-loading="1" style={{padding:30,textAlign:'center',color:'var(--text-secondary)',fontSize:13}}>Loading 2027 rookie ranks…</div>
      )}
      {rookies && rookies.length === 0 && (
        <div data-pp2027-empty="1" style={{padding:24,fontSize:13,color:'var(--text-secondary)',background:'var(--bg-elevated)',borderRadius:10,textAlign:'center'}}>
          The PFK 2027 rookie board isn't published yet. Check back soon.
        </div>
      )}
      {displayRookies.length > 0 && (
        <div data-pp2027-list="1" style={{display:'flex',flexDirection:'column',gap:8,userSelect: draggingId ? 'none' : 'auto'}}>
          {/* Ghost element — follows the cursor while a drag is in
              progress. Position:fixed; pointer-events:none so it
              doesn't intercept drop targets. */}
          {draggingId && (() => {
            const ghostR = rookieById[draggingId];
            if(!ghostR) return null;
            const pc = posColor(ghostR.position);
            return (
              <div data-pp2027-ghost="1"
                   style={{position:'fixed',left:ghostPos.x-ghostOff.x,top:ghostPos.y-ghostOff.y,pointerEvents:'none',zIndex:9999,opacity:0.93,transform:'rotate(0.8deg) scale(1.02)',width:'min(640px, calc(100vw - 32px))',background:'var(--bg-card)',border:'2px solid var(--accent-gold)',borderRadius:10,padding:'10px 12px',boxShadow:'0 6px 18px rgba(0,0,0,0.25)',display:'flex',alignItems:'center',gap:10}}>
                <span style={{color:'var(--accent-gold)',fontSize:13,fontWeight:900}}>⋮⋮</span>
                <span style={{minWidth:26,textAlign:'center',fontSize:18,fontWeight:900,color:'var(--accent-gold-text)',fontVariantNumeric:'tabular-nums'}}>{userOrder ? userOrder.indexOf(draggingId)+1 : ''}</span>
                {ghostR.photo_url && <img src={ghostR.photo_url} alt="" style={{width:40,height:48,objectFit:'contain'}}/>}
                <span style={{flex:1,fontSize:14,fontWeight:800,color:'var(--text-primary)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{ghostR.name}</span>
                <span style={{padding:'1px 6px',borderRadius:4,background:`color-mix(in srgb, ${pc} 16%, var(--bg-elevated))`,color:pc,border:`1px solid color-mix(in srgb, ${pc} 36%, transparent)`,fontWeight:800,fontSize:10}}>{ghostR.position || '—'}</span>
              </div>
            );
          })()}
          {displayRookies.map((r, i) => {
            const rank = i + 1;
            const pc = posColor(r.position);
            const isFirst = i === 0;
            const isLast = i === displayRookies.length - 1;
            const isBeingDragged = draggingId === r.id;
            const showDropLine = canEdit && draggingId && draggingId !== r.id && insertBeforeId === r.id;
            return (
              <React.Fragment key={r.id || `rank-${rank}`}>
                {showDropLine && (
                  <div data-pp2027-drop-line="1" aria-hidden="true"
                       style={{height:3,background:'var(--accent-gold)',borderRadius:2,margin:'-2px 4px',flexShrink:0}}/>
                )}
              <div ref={el => { if(r.id) rowRefs.current[r.id] = el; }}
                   data-pp2027-row={rank}
                   data-pp2027-rookie-id={r.id || ''}
                   data-pp2027-drag-active={isBeingDragged ? '1' : '0'}
                   style={{display:'flex',alignItems:'center',gap:10,background:'var(--bg-card)',border:'1px solid '+(isBeingDragged?'var(--accent-gold)':'var(--border)'),borderRadius:10,padding:'10px 12px',opacity:isBeingDragged?0.25:1}}>
                {canEdit && (
                  /* Pointer event handlers live on the drag HANDLE,
                     not the whole row, so a user can touch and scroll
                     anywhere else in the row without accidentally
                     initiating a drag. setPointerCapture on the handle
                     keeps pointermove + pointerup firing on the handle
                     after a drag commits, even when the cursor has
                     drifted far away from it. touchAction:none on the
                     handle only suppresses the browser's default
                     touch-scroll for taps that started ON the handle. */
                  <div data-pp2027-drag="1"
                       title="Drag to reorder"
                       aria-label="Drag handle"
                       onPointerDown={(e) => onRowPointerDown(e, r.id)}
                       onPointerMove={onRowPointerMove}
                       onPointerUp={onRowPointerUp}
                       onPointerCancel={onRowPointerUp}
                       style={{padding:'8px 10px',margin:'-6px 0 -6px -6px',color:'var(--text-muted)',fontWeight:900,userSelect:'none',fontSize:14,lineHeight:1,flexShrink:0,touchAction:'none',cursor:draggingId===r.id?'grabbing':'grab'}}>
                    ⋮⋮
                  </div>
                )}
                <div data-pp2027-rank="1"
                     style={{minWidth:26,textAlign:'center',fontSize:18,fontWeight:900,color:rank<=12?'var(--accent-gold-text)':'var(--text-muted)',fontVariantNumeric:'tabular-nums'}}>
                  {rank}
                </div>
                <div data-pp2027-photo="1" style={{width:48,height:56,flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center'}}>
                  {r.photo_url ? (
                    <img src={r.photo_url} alt="" loading="lazy"
                         onError={e => {
                           const img = e.target;
                           const fb = document.createElement('div');
                           fb.textContent = (r.name||'?')[0]?.toUpperCase() || '?';
                           fb.style.cssText = 'width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:color-mix(in srgb, var(--accent-gold) 12%, var(--bg-elevated));color:var(--accent-gold-text);font-size:20px;font-weight:900;border-radius:8px;';
                           img.replaceWith(fb);
                         }}
                         style={{width:'100%',height:'100%',objectFit:'contain',display:'block'}}/>
                  ) : (
                    <div style={{width:'100%',height:'100%',display:'flex',alignItems:'center',justifyContent:'center',background:'color-mix(in srgb, var(--accent-gold) 12%, var(--bg-elevated))',color:'var(--accent-gold-text)',fontSize:20,fontWeight:900,borderRadius:8}}>
                      {(r.name||'?')[0]?.toUpperCase() || '?'}
                    </div>
                  )}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div data-pp2027-name="1" style={{fontSize:15,fontWeight:800,color:'var(--text-primary)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',lineHeight:1.2}}>
                    {r.name || 'Unnamed rookie'}
                  </div>
                  <div style={{marginTop:3,fontSize:11,color:'var(--text-muted)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',lineHeight:1.25}}>
                    <span data-pp2027-pos="1"
                          style={{display:'inline-block',padding:'1px 6px',borderRadius:4,background:`color-mix(in srgb, ${pc} 16%, var(--bg-elevated))`,color:pc,border:`1px solid color-mix(in srgb, ${pc} 36%, transparent)`,fontWeight:800,fontSize:10,letterSpacing:0.4,marginRight:6}}>
                      {r.position || '—'}
                    </span>
                    {r.school && <span data-pp2027-school="1">{r.school}</span>}
                  </div>
                </div>
                {canEdit && (
                  /* The ↑↓ buttons live inside the same row that owns
                     the drag pointer handlers. We stopPropagation on
                     pointerdown so tapping an arrow doesn't first
                     register as a drag-start attempt on the row. */
                  <div data-pp2027-arrows="1" style={{display:'flex',flexDirection:'column',gap:2,flexShrink:0}}>
                    <button type="button"
                            onPointerDown={e => e.stopPropagation()}
                            onClick={() => moveUp(i)}
                            disabled={isFirst}
                            aria-label="Move up"
                            data-pp2027-up="1"
                            style={{width:44,height:22,padding:0,background:'transparent',border:'1px solid var(--border-outline)',borderRadius:5,color:isFirst?'var(--text-muted)':'var(--text-secondary)',cursor:isFirst?'default':'pointer',fontSize:12,fontWeight:900,lineHeight:1,fontFamily:'inherit',opacity:isFirst?0.4:1}}>
                      ↑
                    </button>
                    <button type="button"
                            onPointerDown={e => e.stopPropagation()}
                            onClick={() => moveDown(i)}
                            disabled={isLast}
                            aria-label="Move down"
                            data-pp2027-down="1"
                            style={{width:44,height:22,padding:0,background:'transparent',border:'1px solid var(--border-outline)',borderRadius:5,color:isLast?'var(--text-muted)':'var(--text-secondary)',cursor:isLast?'default':'pointer',fontSize:12,fontWeight:900,lineHeight:1,fontFamily:'inherit',opacity:isLast?0.4:1}}>
                      ↓
                    </button>
                  </div>
                )}
              </div>
              </React.Fragment>
            );
          })}
          {/* End-of-list drop indicator — shown when dragging and the
              cursor is past every row's midpoint (insertBeforeId null). */}
          {canEdit && draggingId && insertBeforeId === null && (
            <div data-pp2027-drop-line="1" data-pp2027-drop-end="1" aria-hidden="true"
                 style={{height:3,background:'var(--accent-gold)',borderRadius:2,margin:'-2px 4px',flexShrink:0}}/>
          )}
        </div>
      )}

      {/* Quiet "request a rookie" footer note. Always renders below the
          list (both logged-in and logged-out viewers). The clickable
          @PlayforkeepsFF Twitter pill lives in PFKFooter directly
          beneath this — no inline link here to avoid duplicating it. */}
      <div data-pp2027-request-footer="1"
           style={{marginTop:18,padding:'10px 4px',fontSize:11,color:'var(--text-muted)',lineHeight:1.5,textAlign:'center'}}>
        Want to add a rookie? DM us via the <strong style={{color:'var(--text-secondary)',fontWeight:700}}>@PlayforkeepsFF</strong> pill below.
      </div>
    </div>
  );
}

function App(){
  // Allowlist defined outside the initializer so the post-mount defensive
  // useEffect below can reuse the exact same set without drift.
  const TAB_ALLOWLIST = ['pfk','custom','team','polls','pfk2027'];
  const [tab,setTab]=useState(()=>{
    // Read ?tab=xxx from URL on mount so cross-page nav lands on the right tab
    if(typeof window === 'undefined') return 'pfk';
    const p = new URLSearchParams(window.location.search).get('tab');
    return (p && TAB_ALLOWLIST.includes(p)) ? p : 'pfk';
  });
  // Belt-and-suspenders: the useState initializer above SHOULD pick up
  // the URL param on first render, but a user-reported bug had
  // /?tab=pfk2027 landing on the default tab even though the param was
  // present. Couldn't reproduce from code analysis (routing + allowlist
  // both correct in the deployed bundle) but adding a defensive
  // mount-time useEffect costs nothing and guarantees the right tab on
  // direct-URL loads even if something I haven't spotted is racing the
  // initializer.
  useEffect(() => {
    if(typeof window === 'undefined') return;
    const p = new URLSearchParams(window.location.search).get('tab');
    if(p && TAB_ALLOWLIST.includes(p) && p !== tab) setTab(p);
    // intentionally only runs once on mount — runtime tab changes flow
    // through setTab from the toolbar click handler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Dev-only: count of completed dispersal drafts (live query on mount).
  // Hidden on production (gated by the existing global isDevHost() helper).
  // Cheap query — moves slowly.
  const [completedDispersalCount,setCompletedDispersalCount] = useState(null);
  useEffect(()=>{
    if(!isDevHost() || !sb) return;
    let cancelled = false;
    (async () => {
      try{
        const { count } = await sb.from('dispersal_drafts').select('*', { count: 'exact', head: true }).eq('status', 'complete');
        if(!cancelled && typeof count === 'number') setCompletedDispersalCount(count);
      }catch(e){}
    })();
    return ()=>{ cancelled = true; };
  },[]);
  const [savedLists,setSavedLists]=useState(()=>{
    const saved=loadStorage('pfk_saved_lists',null);
    if(saved&&saved.length) return saved;
    const old=loadStorage('pfk_list',null);
    return [{id:'list_1',name:'My Rankings',items:old||buildInitialList()}];
  });
  const [activeListId,setActiveListId]=useState(()=>{
    const saved=loadStorage('pfk_saved_lists',null);
    if(saved&&saved.length) return loadStorage('pfk_active_list_id',saved[0].id);
    return 'list_1';
  });
  const [history,setHistory]=useState([]);
  const [editingPlayer,setEditingPlayer]=useState(null);
  const [playerDraft,setPlayerDraft]=useState({});
  const [showAdd,setShowAdd]=useState(false);
  const [newPlayer,setNewPlayer]=useState({name:"",pos:"WR",college:""});
  const [renamingTier,setRenamingTier]=useState(null);
  const [tierNameDraft,setTierNameDraft]=useState("");
  const [renamingListId,setRenamingListId]=useState(null);
  const [listNameDraft,setListNameDraft]=useState("");
  const [showAddTier,setShowAddTier]=useState(false);
  const [newTierName,setNewTierName]=useState("");
  const [posFilter,setPosFilter]=useState(()=>new Set(["WR","RB","TE","QB"]));
  const togglePos=(p)=>setPosFilter(prev=>{const n=new Set(prev);n.has(p)?n.delete(p):n.add(p);return n.size?n:new Set(["WR","RB","TE","QB"]);});
  const [teamRoster,setTeamRoster]=useState(()=>loadStorage("pfk_roster",[]));
  const [picks,setPicks]=useState(()=>loadStorage("pfk_picks",[]));
  const [newTeamPlayer,setNewTeamPlayer]=useState({name:"",pos:"WR",age:""});
  const [saved,setSaved]=useState(false);
  const [officialUpdated,setOfficialUpdated]=useState(null);
  const [officialList,setOfficialList]=useState(null);
  const [pfkSettings,setPfkSettings]=useState(DEFAULT_SETTINGS);
  const [pfkMissing,setPfkMissing]=useState(false);
  const [prospects,setProspects]=useState({});
  const [modelByName,setModelByName]=useState({});
  const [masterList,setMasterList]=useState(null);

  useEffect(()=>{
    fetch('js/prospects-2026.json').then(r=>r.json()).then(arr=>{
      const norm=s=>(s||'').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g,'').replace(/[^a-z0-9]/g,'');
      const m={}; arr.forEach(p=>{m[norm(p.name)]=p;}); setProspects(m);
    }).catch(()=>{});
  },[]);

  // Load PFK rookie model data once and index by normalized name for fast lookup.
  useEffect(()=>{
    fetchModelData().then(row=>{
      if(!row?.data || !Array.isArray(row.data)) return;
      const m={};
      row.data.forEach(it=>{ if(it?.name) m[normDraftName(it.name)] = it; });
      setModelByName(m);
    }).catch(()=>{});
  },[]);
  // Keep masterList in sync. On default combo it mirrors officialList (no extra fetch).
  // On non-default combo, fetch the master separately so RS lookups have a stable reference.
  useEffect(()=>{
    if(sameSettings(pfkSettings, DEFAULT_SETTINGS)){
      setMasterList(null); // unused; effectiveMaster falls back to officialList
      return;
    }
    fetchMasterListForDerivation().then(m => setMasterList(m));
  },[pfkSettings]);
  const [session,setSession]=useState(null);
  const [userRow,setUserRow]=useState(null);
  const [authOpen,setAuthOpen]=useState(false);
  const [authMode,setAuthMode]=useState('signin');
  const [authEmail,setAuthEmail]=useState('');
  const [authPassword,setAuthPassword]=useState('');
  const [authSleeper,setAuthSleeper]=useState('');
  const [authMsg,setAuthMsg]=useState('');
  // Dev-only edit mode for the PFK Rookies tab. Becomes available when Evan signs in on dev URL.
  const [editMode,setEditMode]=useState(false);
  const [draftMsg,setDraftMsg]=useState('');
  const isEvanOnDev = isDevHost() && session?.user?.email === EVAN_EMAIL;

  useEffect(()=>{
    if(!sb) return;
    sb.auth.getSession().then(({data})=>setSession(data.session));
    const { data:sub } = sb.auth.onAuthStateChange((_e,s)=>setSession(s));
    return ()=>sub?.subscription?.unsubscribe?.();
  },[]);

  // Open sign-in modal automatically when arriving from /?signin=1 (sent by
  // MasterToolbar Sign In links on Dispersal/Lookup pages).
  useEffect(()=>{
    if(typeof window === 'undefined') return;
    const p = new URLSearchParams(window.location.search);
    if(p.get('signin') === '1'){
      setAuthMode('signin');
      setAuthOpen(true);
    }
  },[]);

  useEffect(()=>{
    if(!session||!sb){ setUserRow(null); return; }
    (async()=>{
      const { data } = await sb.from('users').select('*').eq('id',session.user.id).maybeSingle();
      if(data){ setUserRow(data); return; }
      const { data:inserted } = await sb.from('users').insert({id:session.user.id,email:session.user.email}).select().maybeSingle();
      if(inserted) setUserRow(inserted);
    })();
  },[session]);

  const doAuth=async()=>{
    setAuthMsg('');
    if(!sb){ setAuthMsg('Backend not loaded'); return; }
    if(authMode==='signup'){
      const { data, error } = await sb.auth.signUp({email:authEmail,password:authPassword});
      if(error){ setAuthMsg(error.message); return; }
      const uid = data.user?.id;
      if(uid){
        await sb.from('users').upsert({ id:uid, email:authEmail, sleeper_username:authSleeper||null });
      }
      // Soft-verification model (added 2026-05-20): if Supabase's
      // mailer_autoconfirm is enabled, the signUp response carries a
      // fresh session and the user is logged in immediately. The site-
      // wide verification banner (in MasterToolbar) takes over from
      // here, nudging them to click the verification link when they
      // have a moment. If mailer_autoconfirm is OFF (legacy path), we
      // fall back to the old "check email then sign in" flow so this
      // code keeps working regardless of dashboard configuration.
      if(data.session){
        setAuthMsg("Account created! You're logged in and ready to go. Check your inbox to verify your email when you have a moment.");
        setTimeout(() => {
          setAuthOpen(false);
          setAuthEmail(''); setAuthPassword(''); setAuthSleeper('');
          setAuthMsg('');
        }, 2500);
      } else {
        setAuthMsg('Account created! Check your inbox to verify your email, then log in.');
        setAuthMode('signin');
      }
    }else{
      const { error } = await sb.auth.signInWithPassword({email:authEmail,password:authPassword});
      if(error){ setAuthMsg(error.message); return; }
      // Optional Sleeper link from the sign-in form. Best-effort: if the
      // username resolves, persist it to users-row so the useSleeperUser hook's
      // post-sign-in auto-restore picks it up and populates the global link.
      // If the lookup fails (typo, deleted Sleeper account, etc.) we DON'T
      // block sign-in — user is still authenticated; they can link/relink
      // from the toolbar later.
      const handle = (authSleeper||'').trim();
      if(handle){
        try{
          const u = await lookupResolveUser(handle);
          const { data:sessData } = await sb.auth.getSession();
          const uid = sessData?.session?.user?.id;
          if(uid){
            await sb.from('users').upsert({ id:uid, email:authEmail, sleeper_username: u.username });
          }
        }catch(e){/* lookup failed — user can link later */}
      }
      setAuthOpen(false);
      setAuthEmail(''); setAuthPassword(''); setAuthSleeper('');
      // Sign In is reachable from anywhere via /?signin=1 — that URL drops
      // users onto the legacy App (Rookie Ranks). After successful auth we
      // bounce them BACK to where they came from (?return=…), or to the
      // home page (Sleeper Snapshot) as a fallback if no return path was
      // passed. Full reload also ensures the new auth state triggers the
      // Sleeper-link auto-restore. ?return is validated to start with a
      // single leading "/" so an attacker can't craft an open-redirect URL.
      try{
        const ret = new URLSearchParams(window.location.search).get('return');
        const safeReturn = (ret && ret.startsWith('/') && !ret.startsWith('//')) ? ret : '/';
        window.location.href = safeReturn;
      }catch(e){
        try{ window.location.href = '/'; }catch(e2){}
      }
    }
  };

  const doLogout=async()=>{ if(sb) await sb.auth.signOut(); };

  useEffect(()=>{
    // Fetch order:
    //   DEV URL:  dev draft for combo → published for combo → DERIVED FROM MASTER (if non-default)
    //   PROD URL: published for combo →                       DERIVED FROM MASTER (if non-default)
    // Default combo never derives (it IS the master).
    (async () => {
      const isDefault = sameSettings(pfkSettings, DEFAULT_SETTINGS);
      let row = null;
      if(isDevHost()) row = await fetchDevDraftRankings(pfkSettings);
      if(!row) row = await fetchOfficialRankings(pfkSettings);
      if(!row && !isDefault){
        const master = await fetchMasterListForDerivation();
        if(master){
          const derived = deriveListForCombo(master, pfkSettings);
          if(derived) row = { data: derived, settings: pfkSettings, updated_at: null, _isDerived: true };
        }
      }
      if(!row?.data||!Array.isArray(row.data)){
        setPfkMissing(true); setOfficialList(null); setOfficialUpdated(null); return;
      }
      setPfkMissing(!sameSettings(row.settings,pfkSettings) && !row._isDerived);
      setOfficialUpdated(row.updated_at);
      setOfficialList(row.data);
      const hadSaved=loadStorage('pfk_saved_lists',null);
      if(!hadSaved){
        // Seed three pre-made custom lists on first ever visit. Each starts as a copy
        // of PFK Official rankings — user can drag freely to customize each one.
        setSavedLists([
          {id:'list_1',name:'Custom1',items:row.data},
          {id:'list_2',name:'Custom2',items:row.data},
          {id:'list_3',name:'Custom3',items:row.data},
        ]);
      }
    })();
  },[pfkSettings,modelByName]);

  const list=useMemo(()=>savedLists.find(l=>l.id===activeListId)?.items||buildInitialList(),[savedLists,activeListId]);
  // Dirty tracking for the custom rankings tab. Any setList call from a user action
  // marks dirty; applying a sort or clicking Save clears it.
  const [isDirty,setIsDirty]=useState(false);
  // Last-applied "starting point" sort. Default is PFK Official, matching what the
  // initial list state actually reflects (lists seed from row.data = published rankings).
  const [sortBy,setSortBy]=useState('pfk');
  const setList=useCallback(updater=>{
    setSavedLists(prev=>prev.map(l=>l.id===activeListId?{...l,items:typeof updater==='function'?updater(l.items):updater}:l));
    setIsDirty(true);
  },[activeListId]);
  // Compute a sorted version of the user's current list. Used as a one-time
  // "starting point" — applied by applySort, then the user is free to drag freely.
  const computeSortedList=useCallback((sortKey)=>{
    if(!list) return list;
    const players=list.filter(it=>it?.type==='player');
    if(!players.length) return list;
    const officialIdx=new Map();
    if(officialList){
      let idx=0;
      for(const it of officialList){ if(it?.type==='player'){ officialIdx.set(normDraftName(it.name),idx++); } }
    }
    const officialRank=(it)=>officialIdx.get(normDraftName(it.name)) ?? Number.POSITIVE_INFINITY;
    if(sortKey==='pfk'){
      if(!officialList) return list;
      const userByKey=new Map();
      for(const p of players){ userByKey.set(normDraftName(p.name), p); }
      const out=[]; const seenKeys=new Set();
      for(const it of officialList){
        if(it?.type==='tier'){ out.push(it); continue; }
        if(it?.type==='player'){
          const k=normDraftName(it.name);
          if(userByKey.has(k)){ out.push(userByKey.get(k)); seenKeys.add(k); }
        }
      }
      const cleaned=[];
      for(let i=0;i<out.length;i++){
        const it=out[i];
        if(it?.type==='tier'){
          let hasPlayer=false;
          for(let j=i+1;j<out.length;j++){
            if(out[j]?.type==='tier') break;
            if(out[j]?.type==='player'){ hasPlayer=true; break; }
          }
          if(hasPlayer) cleaned.push(it);
        } else cleaned.push(it);
      }
      const extras=players.filter(p=>!seenKeys.has(normDraftName(p.name)));
      if(extras.length){
        cleaned.push({type:'tier',id:'tier_custom_extras_'+Date.now(),name:'Other'});
        for(const p of extras) cleaned.push(p);
      }
      return cleaned;
    }
    const draftAbs=(it)=>{
      const d=DRAFT_2026[normDraftName(it.name)];
      if(!d || !d.pick || d.pick==='UDFA') return Number.POSITIVE_INFINITY;
      const abs=pickToAbs(d.pick);
      return abs==null ? Number.POSITIVE_INFINITY : abs;
    };
    const modelPfk=(it)=>{
      const m=modelByName?.[normDraftName(it.name)];
      if(!m) return Number.NEGATIVE_INFINITY;
      const pos=m.pos || it.pos;
      const dc=m.dcOverride!=null ? +m.dcOverride : dcScoreFromPick(pos, m.pick);
      const film=filmAvgPct(m.filmScores);
      const base=pfkScore(m.stats, dc, film);
      const baseline=base * landingMultiplier(m.landing);
      return baseline * comboMultiplier(pos, pfkSettings);
    };
    return players.slice().sort((a,b)=>{
      if(sortKey==='draftcapital'){
        const da=draftAbs(a), db=draftAbs(b);
        if(da!==db) return da-db;
        return officialRank(a)-officialRank(b);
      }
      const ma=modelPfk(a), mb=modelPfk(b);
      if(mb!==ma) return mb-ma;
      return officialRank(a)-officialRank(b);
    });
  },[list,officialList,modelByName,pfkSettings]);
  // Apply a sort once as a starting point, then user drags freely from there.
  const applySort=(sortKey)=>{
    if(!sortKey) return;
    if(isDirty){
      if(!confirm('You have unsaved changes. Applying this sort will overwrite your current order. Continue?')) return;
    }
    const sorted=computeSortedList(sortKey);
    if(!sorted) return;
    setSavedLists(prev=>prev.map(l=>l.id===activeListId?{...l,items:sorted}:l));
    setSortBy(sortKey);
    setIsDirty(false);
    flash();
  };
  // Save = clear dirty + flash success. The list is already auto-persisted to
  // localStorage and Supabase on every change; this is a UX checkpoint.
  const saveList=()=>{ setIsDirty(false); flash(); };

  useEffect(()=>{ localStorage.setItem("pfk_saved_lists",JSON.stringify(savedLists)); },[savedLists]);

  // One-time migration: replace any pre-existing local OR cloud lists with three
  // fresh Custom1/2/3. Gated by a localStorage flag so it only runs once per browser.
  // Triggers when officialList is loaded so the seed reflects current PFK rankings.
  // Each list gets a DEEP-CLONED copy of officialList so they're fully independent.
  useEffect(()=>{
    if(!officialList) return;
    if(localStorage.getItem('pfk_lists_seeded_v6')) return;
    const seedFresh=()=>{
      const clone=()=>JSON.parse(JSON.stringify(officialList));
      setSavedLists([
        {id:'list_1',name:'Custom1',items:clone()},
        {id:'list_2',name:'Custom2',items:clone()},
        {id:'list_3',name:'Custom3',items:clone()},
      ]);
      setActiveListId('list_1');
      setIsDirty(false);
      localStorage.removeItem('pfk_lists_seeded_v3'); // clear any prior flag
      localStorage.setItem('pfk_lists_seeded_v6','1');
    };
    if(session && sb){
      sb.from('user_rankings').delete().eq('user_id',session.user.id).then(seedFresh);
    } else {
      seedFresh();
    }
  },[officialList,session]);

  useEffect(()=>{
    if(!session||!sb) return;
    if(!localStorage.getItem('pfk_lists_seeded_v6')) return; // wait for migration
    // Order by id so the lists appear in the order they were inserted (Custom1, Custom2,
    // Custom3...) instead of arbitrary Supabase row order. The schema has no created_at
    // column — id is auto-increment so it preserves insertion order.
    sb.from('user_rankings').select('*').eq('user_id',session.user.id).order('id',{ascending:true}).then(({data})=>{
      if(data&&data.length){
        setSavedLists(data.map(r=>({id:'cloud_'+r.id,cloudId:r.id,name:r.name,items:r.items})));
        setActiveListId('cloud_'+data[0].id);
        setIsDirty(false);
      }
    });
  },[session]);

  useEffect(()=>{
    if(!session||!sb) return;
    const t=setTimeout(()=>{
      savedLists.forEach(l=>{
        if(l.cloudId){
          sb.from('user_rankings').update({name:l.name,items:l.items,updated_at:new Date().toISOString()}).eq('id',l.cloudId);
        }else{
          sb.from('user_rankings').insert({user_id:session.user.id,name:l.name,items:l.items}).select().single().then(({data})=>{
            if(data){
              setSavedLists(prev=>prev.map(x=>x.id===l.id?{...x,id:'cloud_'+data.id,cloudId:data.id}:x));
              // Keep activeListId in sync — otherwise the list lookup goes stale and renders an empty fallback.
              setActiveListId(prev=>prev===l.id?'cloud_'+data.id:prev);
            }
          });
        }
      });
    },1000);
    return ()=>clearTimeout(t);
  },[savedLists,session]);
  useEffect(()=>{ localStorage.setItem("pfk_active_list_id",activeListId); },[activeListId]);
  // Safety net: if activeListId points at a list that doesn't exist (e.g. after migration
  // replaces savedLists or after cloud-save changes a list's id), fall back to the first list.
  // Without this, list useMemo returns the empty buildInitialList() fallback and the page
  // renders blank until the user clicks a tab.
  useEffect(()=>{
    if(!savedLists.length) return;
    if(!savedLists.find(l=>l.id===activeListId)){
      setActiveListId(savedLists[0].id);
    }
  },[savedLists,activeListId]);
  useEffect(()=>{ localStorage.setItem("pfk_roster",JSON.stringify(teamRoster)); },[teamRoster]);
  useEffect(()=>{ localStorage.setItem("pfk_picks",JSON.stringify(picks)); },[picks]);

  const flash=()=>{ setSaved(true); setTimeout(()=>setSaved(false),1500); };

  const switchList=id=>{setActiveListId(id);setHistory([]);setIsDirty(false);};
  const createList=()=>{
    const id='list_'+Date.now();
    const n=savedLists.length+1;
    setSavedLists(prev=>[...prev,{id,name:`Custom${n}`,items:officialList||buildInitialList()}]);
    setActiveListId(id);
    setHistory([]);
  };
  const deleteList=id=>{
    if(savedLists.length<=1) return;
    setSavedLists(prev=>{
      const next=prev.filter(l=>l.id!==id);
      if(id===activeListId) setActiveListId(next[0].id);
      return next;
    });
    setHistory([]);
  };
  const renameList=(id,name)=>{
    if(!name.trim()) return;
    setSavedLists(prev=>prev.map(l=>l.id===id?{...l,name:name.trim()}:l));
  };

  const push=l=>setHistory(h=>[...h.slice(-39),l]);
  const reorder=(fromId,beforeId)=>{push(list);setList(prev=>{const l=[...prev],fi=l.findIndex(x=>x.id===fromId);const[item]=l.splice(fi,1);if(beforeId===null){l.push(item);}else{const ti=l.findIndex(x=>x.id===beforeId);l.splice(ti!==-1?ti:l.length,0,item);}return l;});};
  const moveItem=(id,dir)=>{push(list);setList(prev=>{const l=[...prev],i=l.findIndex(x=>x.id===id),sw=i+dir;if(sw<0||sw>=l.length)return l;[l[i],l[sw]]=[l[sw],l[i]];return l;});};
  const undo=()=>{if(!history.length)return;setList(history[history.length-1]);setHistory(h=>h.slice(0,-1));};

  // ----- Edit Mode handlers for the OFFICIAL rankings (dev-only Edit Mode on PFK Rookies tab) -----
  // These mutate officialList directly so dev-page edits write to the dev draft Supabase row on Save.
  const officialOps = {
    onReorder: (fromId,beforeId) => setOfficialList(prev => {
      const l=[...(prev||[])]; const fi=l.findIndex(x=>x.id===fromId);
      if(fi<0) return prev; const [item]=l.splice(fi,1);
      if(beforeId===null) l.push(item);
      else { const ti=l.findIndex(x=>x.id===beforeId); l.splice(ti!==-1?ti:l.length,0,item); }
      return l;
    }),
    onMove: (id,dir) => setOfficialList(prev => {
      const l=[...(prev||[])]; const i=l.findIndex(x=>x.id===id); const sw=i+dir;
      if(sw<0||sw>=l.length) return l; [l[i],l[sw]]=[l[sw],l[i]]; return l;
    }),
    onEdit: (p) => { setEditingPlayer(p.id); setPlayerDraft({...p}); },
    onRemove: (id) => setOfficialList(prev => (prev||[]).filter(x=>x.id!==id)),
    onSavePlayer: () => {
      setOfficialList(prev => (prev||[]).map(x => x.id===editingPlayer ? {...x,...playerDraft} : x));
      setEditingPlayer(null);
    },
    setRankingScoreOverride: (id, val) => setOfficialList(prev => (prev||[]).map(x => {
      if(x.id !== id) return x;
      if(val === '' || val == null) { const {rankingScoreOverride, ...rest} = x; return rest; }
      return { ...x, rankingScoreOverride: Math.round(+val * 100) / 100 };
    })),
    onCancelEdit: () => setEditingPlayer(null),
    onRenameStart: (id,name) => { setRenamingTier(id); setTierNameDraft(name); },
    onRenameCancel: () => setRenamingTier(null),
    onRenameSave: () => {
      setOfficialList(prev => (prev||[]).map(x => x.id===renamingTier ? {...x, name:tierNameDraft} : x));
      setRenamingTier(null);
    },
    onDeleteTier: (id) => {
      if(!confirm('Delete this tier?')) return;
      setOfficialList(prev => (prev||[]).filter(x=>x.id!==id));
    },
  };
  const officialAddTier = () => {
    const name = prompt('Tier name:'); if(!name) return;
    setOfficialList(prev => [...(prev||[]), {id:'tier_'+Date.now(), type:'tier', name}]);
  };
  const saveDraft = async () => {
    if(!officialList?.length){ setDraftMsg('Nothing to save.'); setTimeout(()=>setDraftMsg(''),2000); return; }
    setDraftMsg('Saving...');
    const { error } = await saveDevDraftRankings(officialList, pfkSettings);
    if(error){ setDraftMsg('Save error: '+(error.message||error)); }
    else { setDraftMsg('Draft saved ✓ (dev URL only)'); setTimeout(()=>setDraftMsg(''),3000); }
  };
  // 🌱 Seed from Model — manually pulls the model's auto-tier output into officialList.
  // Only fires when Evan clicks it. Replaces whatever's currently shown. He can edit and SAVE DRAFT after.
  const seedOfficialFromModel = () => {
    if(!Object.keys(modelByName).length){ setDraftMsg('Model not loaded yet.'); setTimeout(()=>setDraftMsg(''),2500); return; }
    const seeded = buildSeedFromModel(modelByName, pfkSettings, officialList);
    if(!seeded?.length){ setDraftMsg('Could not build from model.'); setTimeout(()=>setDraftMsg(''),2500); return; }
    if(!confirm(`Replace current dev rankings with the model's auto-tier output (${seeded.filter(x=>x.type==='player').length} players)? Click 💾 SAVE DRAFT after to persist.`)) return;
    setOfficialList(seeded);
    setDraftMsg('Seeded from model — edit and SAVE DRAFT to persist.');
    setTimeout(()=>setDraftMsg(''),4000);
  };
  // 🔄 Derive from Master — only meaningful on non-default combos. Pulls the master combo's
  // current state, applies position multipliers for THIS combo, re-buckets into tiers. Replaces
  // the current officialList. Use it after editing master, or to reset a combo override back to
  // the derived view. Click 💾 SAVE DRAFT to persist as combo-specific override; otherwise just
  // click EXIT and the next reload will re-derive.
  const deriveFromMaster = async () => {
    const master = await fetchMasterListForDerivation();
    if(!master?.length){ setDraftMsg('Master list not found.'); setTimeout(()=>setDraftMsg(''),3000); return; }
    const derived = deriveListForCombo(master, pfkSettings);
    if(!derived?.length){ setDraftMsg('Could not derive.'); setTimeout(()=>setDraftMsg(''),3000); return; }
    if(!confirm(`Rebuild this combo's rankings by deriving from your master list (${derived.filter(x=>x.type==='player').length} players, position multipliers applied for ${pfkSettings.format}/${pfkSettings.tep}TEP/${pfkSettings.passTd}PTD)? Existing edits to this combo will be replaced.`)) return;
    setOfficialList(derived);
    setDraftMsg('Derived from master — edit and 💾 SAVE DRAFT to persist as combo override, or just exit and the page will re-derive next load.');
    setTimeout(()=>setDraftMsg(''),5500);
  };
  const publishToProd = async () => {
    if(!officialList?.length){ setDraftMsg('Nothing to publish.'); setTimeout(()=>setDraftMsg(''),2000); return; }
    if(!confirm('Publish current rankings to PRODUCTION? This pushes the live public site (playforkeepsdynasty.com) to match what you see on dev.')) return;
    setDraftMsg('Publishing to prod...');
    const { error } = await publishToProdRankings(officialList, pfkSettings);
    if(error){ setDraftMsg('Publish error: '+(error.message||error)); }
    else { setDraftMsg('🚀 Published to prod ✓'); setTimeout(()=>setDraftMsg(''),3500); }
  };
  const deleteTier=id=>{push(list);setList(prev=>prev.filter(x=>x.id!==id));};
  const saveRename=()=>{if(!renamingTier)return;const name=tierNameDraft.trim()||renamingTier;push(list);setList(prev=>prev.map(x=>x.type==="tier"&&x.id===renamingTier?{...x,name}:x));setRenamingTier(null);};
  const addTier=()=>{if(!newTierName.trim())return;const id="t_"+newTierName+"_"+Date.now();push(list);setList(prev=>[{type:"tier",id,name:newTierName.trim()},...prev]);setNewTierName("");setShowAddTier(false);};
  const savePlayer=()=>{push(list);setList(prev=>prev.map(x=>x.type==="player"&&x.id===editingPlayer?{...x,...playerDraft,id:x.id,type:"player"}:x));setEditingPlayer(null);flash();};
  const removePlayer=id=>{push(list);setList(prev=>prev.filter(x=>x.id!==id));};
  const addNew=()=>{if(!newPlayer.name)return;push(list);const id="p_"+Date.now();setList(prev=>[...prev,{type:"player",id,...newPlayer}]);setNewPlayer({name:"",pos:"WR",college:""});setShowAdd(false);};

  const inp=ex=>({padding:"7px 10px",background:"var(--bg-card)",border:"1px solid var(--border-strong)",borderRadius:7,color:"var(--text-bright)",fontSize:14,...ex});
  const avgAge=teamRoster.length?(teamRoster.reduce((s,p)=>s+Number(p.age||25),0)/teamRoster.length).toFixed(1):"—";
  const grade=(()=>{let s=Math.min(picks.length*5,20)+(Number(avgAge)<=24?15:Number(avgAge)<=26?8:0);if(s>=30)return{g:"A",l:"Championship Window",c:"var(--accent-gold)"};if(s>=20)return{g:"B+",l:"Contender",c:"var(--accent-gold)"};return{g:"B",l:"Building",c:"#e0a800"};})();

  const FilterBar=()=>(
    <div className="pfk-filter-bar" style={{display:"flex",gap:6,marginBottom:16,flexWrap:"wrap",alignItems:"center"}}>
      <span style={{color:"var(--border-outline)",fontSize:14,fontWeight:700}}>POS:</span>
      {["WR","RB","TE","QB"].map(p=>{
        const on=posFilter.has(p);
        return <button key={p} onClick={()=>togglePos(p)} style={{padding:"5px 11px",borderRadius:20,fontSize:13,fontWeight:700,cursor:"pointer",border:on?"2px solid "+POS_COLORS[p]:"2px solid var(--border-hairline)",background:on?`color-mix(in srgb, ${POS_COLORS[p]} 13%, transparent)`:"transparent",color:on?POS_COLORS[p]:"var(--border-outline)",transition:"all .15s"}}>{p}</button>;
      })}
      {posFilter.size<4&&<button onClick={()=>setPosFilter(new Set(["WR","RB","TE","QB"]))} style={{padding:"5px 10px",borderRadius:20,fontSize:12,fontWeight:700,cursor:"pointer",border:"2px solid var(--border-strong)",background:"transparent",color:"var(--border-outline)"}}>ALL</button>}
    </div>
  );

  // For RS calculation: when on default combo, master === officialList. When on derived combo,
  // use the separately-fetched masterList so RS stays constant per player regardless of combo.
  const effectiveMasterList = sameSettings(pfkSettings, DEFAULT_SETTINGS) ? officialList : masterList;
  const commonProps={onReorder:reorder,onMove:moveItem,onEdit:r=>{setEditingPlayer(r.id);setPlayerDraft({...r});},onRemove:removePlayer,onRenameStart:(id,name)=>{setRenamingTier(id);setTierNameDraft(name);},onRenameCancel:()=>setRenamingTier(null),onRenameSave:saveRename,onDeleteTier:deleteTier,renamingTier,tierNameDraft,setTierNameDraft,editingPlayer,playerDraft,setPlayerDraft,onSavePlayer:savePlayer,onCancelEdit:()=>setEditingPlayer(null),posFilter,modelByName,pfkSettings,masterListForRS:effectiveMasterList};

  // Dev URL gate: only Evan can access dev preview. Anyone else sees a login or access denied screen.
  if(isDevHost()){
    if(!session) return <DevAuthGate doAuth={doAuth} authEmail={authEmail} setAuthEmail={setAuthEmail} authPassword={authPassword} setAuthPassword={setAuthPassword} authMsg={authMsg} mode="signin"/>;
    if(session?.user?.email !== EVAN_EMAIL) return <DevAuthGate doLogout={doLogout} signedInAs={session.user.email} mode="denied"/>;
  }

  return(
    <div style={{background:"var(--bg-page)",minHeight:"100vh",color:"var(--text-primary)",fontFamily:"'Inter','Segoe UI',sans-serif"}}>
      {authOpen&&(
        <div onClick={()=>setAuthOpen(false)} style={{position:"fixed",inset:0,background:"var(--backdrop)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div onClick={e=>e.stopPropagation()} style={{width:"100%",maxWidth:380,background:"var(--bg-divider)",border:"1px solid var(--accent-gold)",borderRadius:12,padding:24}}>
            <div style={{display:"flex",gap:4,marginBottom:18,background:"var(--bg-elevated)",borderRadius:8,padding:4}}>
              <button onClick={()=>setAuthMode('signin')} style={{flex:1,padding:"8px",background:authMode==='signin'?"var(--accent-gold)":"transparent",color:authMode==='signin'?"#000":"var(--text-muted)",border:"none",borderRadius:6,fontWeight:900,cursor:"pointer",fontSize:14,letterSpacing:1}}>SIGN IN</button>
              <button onClick={()=>setAuthMode('signup')} style={{flex:1,padding:"8px",background:authMode==='signup'?"var(--accent-gold)":"transparent",color:authMode==='signup'?"#000":"var(--text-muted)",border:"none",borderRadius:6,fontWeight:900,cursor:"pointer",fontSize:14,letterSpacing:1}}>SIGN UP</button>
            </div>
            <input placeholder="Email" value={authEmail} onChange={e=>setAuthEmail(e.target.value)} style={{width:"100%",padding:10,marginBottom:10,background:"var(--bg-input)",border:"1px solid var(--border-strong)",borderRadius:6,color:"var(--text-bright)",fontSize:13}}/>
            <input type="password" placeholder="Password" value={authPassword} onChange={e=>setAuthPassword(e.target.value)} onKeyDown={e=>e.key==='Enter'&&doAuth()} style={{width:"100%",padding:10,marginBottom:10,background:"var(--bg-input)",border:"1px solid var(--border-strong)",borderRadius:6,color:"var(--text-bright)",fontSize:13}}/>
            <input placeholder={authMode==='signup'?"Sleeper username (optional)":"Sleeper username (optional)"} value={authSleeper} onChange={e=>setAuthSleeper(e.target.value)} onKeyDown={e=>e.key==='Enter'&&doAuth()} style={{width:"100%",padding:10,marginBottom:10,background:"var(--bg-input)",border:"1px solid var(--border-strong)",borderRadius:6,color:"var(--text-bright)",fontSize:13}}/>
            <button onClick={doAuth} style={{width:"100%",padding:12,background:"var(--accent-gold)",color:"#000",border:"none",borderRadius:6,fontWeight:900,cursor:"pointer",letterSpacing:1,fontSize:14}}>{authMode==='signup'?'CREATE ACCOUNT':'SIGN IN'}</button>
            {authMsg&&<div style={{color:authMsg.includes('created')?"var(--success)":"var(--danger)",fontSize:13,marginTop:10,textAlign:"center"}}>{authMsg}</div>}
            {/* OR divider — visually separates the account path from the
                guest path so neither feels like a hidden fallback. */}
            <div style={{display:"flex",alignItems:"center",gap:10,margin:"16px 0 12px"}}>
              <div style={{flex:1,height:1,background:"var(--border-strong)"}}/>
              <div style={{fontSize:11,fontWeight:800,color:"var(--text-muted)",letterSpacing:1.2}}>OR</div>
              <div style={{flex:1,height:1,background:"var(--border-strong)"}}/>
            </div>
            <button onClick={()=>setAuthOpen(false)} style={{width:"100%",padding:11,background:"transparent",color:"var(--text-primary)",border:"1px solid var(--border-outline)",borderRadius:6,fontWeight:800,cursor:"pointer",letterSpacing:1,fontSize:13}}>CONTINUE AS GUEST</button>
          </div>
        </div>
      )}
      <MasterToolbar currentTab={tab} onSetTab={setTab} onSignInClick={()=>{setAuthMode('signin');setAuthOpen(true);}} userSleeperName={userRow?.sleeper_username}/>
      {saved && <div style={{position:'fixed',top:14,right:14,zIndex:200,padding:"6px 14px",background:"var(--success-bg)",border:"1px solid var(--success)",borderRadius:20,fontSize:13,color:"var(--success)",fontWeight:700,boxShadow:"0 4px 12px rgba(0,0,0,0.5)"}}>✓ Saved</div>}
      <div className="pfk-content" style={{maxWidth:1140,margin:"0 auto",padding:"20px 14px"}}>
        {/* Dispersal hero CTA — only on Rookie Ranks (the landing tab).
            Other tabs reach Dispersal via the master toolbar nav above the yellow line. */}
        {(tab==="pfk"||tab==="custom") && (
          <a href="/dispersal" style={{display:"block",textDecoration:"none",marginBottom:18,background:"linear-gradient(135deg,var(--accent-gold-bg) 0%,var(--bg-card) 100%)",border:"2px solid var(--accent-gold)",borderRadius:14,padding:"18px 22px",transition:"all .15s"}}
             onMouseEnter={e=>{e.currentTarget.style.background="var(--bg-card-hover)";}}
             onMouseLeave={e=>{e.currentTarget.style.background="linear-gradient(135deg,var(--accent-gold-bg) 0%,var(--bg-card) 100%)";}}>
            <div style={{display:"flex",alignItems:"center",gap:18,flexWrap:"wrap"}}>
              <div style={{fontSize:38,flexShrink:0}}>🎲</div>
              <div style={{flex:1,minWidth:240}}>
                <div style={{fontSize:18,fontWeight:900,color:"var(--accent-gold-text)",letterSpacing:1.5,marginBottom:3}}>PFK DISPERSAL DRAFT</div>
                <div style={{fontSize:13,color:"var(--accent-gold-text)",fontWeight:700,fontStyle:"italic",marginBottom:4}}>Trying to fill multiple spots in your league?</div>
                <div style={{fontSize:13,color:"var(--text-bbb)",lineHeight:1.5}}>Real-time picks, mobile-friendly, auto-pulls rosters from your Sleeper league.</div>
              </div>
              <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:6}}>
                <div style={{padding:"10px 18px",background:"var(--accent-gold)",borderRadius:8,color:"#000",fontWeight:900,fontSize:13,letterSpacing:1.5,whiteSpace:"nowrap"}}>OPEN TOOL →</div>
                {/* Dev-only: completed-drafts counter — live query, hidden on prod */}
                {isDevHost() && completedDispersalCount !== null && (
                  <div style={{fontSize:11,color:"var(--accent-gold-text)",fontWeight:800,letterSpacing:0.5,whiteSpace:"nowrap"}}>{completedDispersalCount.toLocaleString()} completed drafts</div>
                )}
              </div>
            </div>
          </a>
        )}

        {(tab==="pfk"||tab==="custom") && (
          <div style={{display:"inline-flex",gap:4,padding:4,background:"var(--bg-elevated)",border:"1px solid var(--border)",borderRadius:8,marginBottom:14}}>
            <button onClick={()=>setTab("pfk")} style={{padding:"6px 14px",borderRadius:5,border:"none",background:tab==="pfk"?"var(--accent-gold)":"transparent",color:tab==="pfk"?"#000":"var(--text-muted)",fontWeight:800,fontSize:12,cursor:"pointer",letterSpacing:0.5}}>👑 View Official</button>
            <button onClick={()=>setTab("custom")} style={{padding:"6px 14px",borderRadius:5,border:"none",background:tab==="custom"?"var(--accent-gold)":"transparent",color:tab==="custom"?"#000":"var(--text-muted)",fontWeight:800,fontSize:12,cursor:"pointer",letterSpacing:0.5}}>✏️ Customize My Own</button>
          </div>
        )}
        {tab==="pfk"&&(
          <div>
            <div style={{background:"var(--bg-card)",border:"1px solid var(--accent-gold)",borderRadius:12,padding:"14px 20px",marginBottom:18,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
              <div style={{flex:1,minWidth:240}}>
                <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                  <div style={{fontSize:14,fontWeight:900,color:"var(--wordmark-color)",letterSpacing:1}}>PLAY FOR KEEPS OFFICIAL ROOKIE RANKINGS</div>
                  <HowItWorksButton title="Rookie Ranks" compact>
                    <p><strong>What it does:</strong> PFK's official 2026 dynasty rookie rankings, plus a tool to build and share your own list.</p>
                    <ul style={{paddingLeft:20,marginTop:10,display:'flex',flexDirection:'column',gap:6}}>
                      <li><strong>PFK Official rankings</strong> — staff tier list updated regularly, with PFK scores, draft capital, and metric splits per prospect.</li>
                      <li><strong>Custom list builder</strong> — drag-and-drop to reorder, save the list to your account, share it with a link.</li>
                      <li><strong>Adjusts for your scoring</strong> — Superflex, TEP, pass-TD, PPC, PPFD all change the rankings via the same multiplier engine Trade Finder uses.</li>
                      <li><strong>Tier markers</strong> — drop tier headers in to organize a list visually (Round 1 / Round 2, or Tier 1A / 1B / etc.).</li>
                    </ul>
                    <p style={{marginTop:12}}>Use it for mock draft prep, trade reference, or to settle "is this rookie worth a 1.07?" debates.</p>
                  </HowItWorksButton>
                </div>
                <div style={{fontSize:13,color:"var(--border-outline)",marginTop:4}}>2026 Dynasty Rookie Class{officialUpdated?" · Last updated by PFK Staff · "+new Date(officialUpdated).toLocaleString():" · PFK Staff Rankings"}</div>
              </div>
              {isEvanOnDev && (
                <div style={{display:'flex',gap:6,flexWrap:'wrap',alignItems:'center'}}>
                  {!editMode && <button onClick={()=>setEditMode(true)} style={{padding:'8px 14px',background:'transparent',border:'2px solid var(--success)',borderRadius:7,color:'var(--success)',fontWeight:900,cursor:'pointer',fontSize:13,letterSpacing:1}}>✏️ EDIT MODE</button>}
                  {editMode && <>
                    <button onClick={officialAddTier} style={{padding:'7px 12px',background:'transparent',border:'1px solid var(--accent-gold)',borderRadius:6,color:'var(--accent-gold-text)',cursor:'pointer',fontSize:13,fontWeight:700}}>+ Tier</button>
                    {sameSettings(pfkSettings, DEFAULT_SETTINGS) ? (
                      <button onClick={seedOfficialFromModel} style={{padding:'7px 12px',background:'transparent',border:'1px solid var(--accent-violet)',borderRadius:6,color:'var(--accent-violet)',cursor:'pointer',fontSize:13,fontWeight:700}} title="Replace current list with the model's auto-tier output">🌱 Seed from Model</button>
                    ) : (
                      <button onClick={deriveFromMaster} style={{padding:'7px 12px',background:'transparent',border:'1px solid #06b6d4',borderRadius:6,color:'#06b6d4',cursor:'pointer',fontSize:13,fontWeight:700}} title="Rebuild this combo from the master list with position multipliers applied">🔄 Derive from Master</button>
                    )}
                    <button onClick={saveDraft} style={{padding:'7px 14px',background:'var(--success)',border:'none',borderRadius:6,color:'#000',fontWeight:900,cursor:'pointer',fontSize:13,letterSpacing:1}}>💾 SAVE DRAFT</button>
                    <button onClick={publishToProd} style={{padding:'7px 14px',background:'var(--accent-gold)',border:'none',borderRadius:6,color:'#000',fontWeight:900,cursor:'pointer',fontSize:13,letterSpacing:1}}>🚀 PUBLISH TO PROD</button>
                    <button onClick={()=>setEditMode(false)} style={{padding:'7px 12px',background:'transparent',border:'1px solid var(--border-outline)',borderRadius:6,color:'var(--text-secondary)',cursor:'pointer',fontSize:13}}>Exit</button>
                  </>}
                </div>
              )}
            </div>
            {isEvanOnDev && draftMsg && <div style={{marginBottom:12,padding:'8px 12px',background:draftMsg.startsWith('Save error')||draftMsg.startsWith('Publish error')?'var(--danger-bg)':'var(--success-bg)',border:'1px solid '+(draftMsg.startsWith('Save error')||draftMsg.startsWith('Publish error')?'var(--danger)':'var(--success)'),borderRadius:8,color:draftMsg.startsWith('Save error')||draftMsg.startsWith('Publish error')?'var(--danger)':'var(--success)',fontSize:13,fontWeight:700}}>{draftMsg}</div>}
            <div style={{marginBottom:14,padding:'10px 12px',background:'var(--bg-elevated)',border:'1px solid var(--bg-pill)',borderRadius:10}}>
              <SettingsToggleBar value={pfkSettings} onChange={setPfkSettings}/>
              {pfkMissing&&<div style={{fontSize:12,color:'var(--warning-strong)',marginTop:8}}>No ranking published yet for this combo — showing most recent.</div>}
            </div>
            <FilterBar/>
            <div className="pfk-rookie-list">
              <RenderList src={officialList||PFK_LIST} allowEdit={editMode} autoTier={false} prospects={prospects} {...commonProps} {...(editMode ? officialOps : {})} showRankingScore={editMode && isEvanOnDev}/>
            </div>
          </div>
        )}
        {tab==="custom"&&(
          <div>
            {/* Saved lists selector */}
            <div style={{background:"var(--bg-elevated)",border:"1px solid var(--border)",borderRadius:12,padding:"12px 14px",marginBottom:10}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                <span style={{fontSize:13,fontWeight:700,color:"var(--border-outline)",letterSpacing:1,flexShrink:0}}>MY LISTS</span>
                <button onClick={createList} disabled={savedLists.length>=10} style={{marginLeft:"auto",padding:"4px 12px",background:savedLists.length>=10?"var(--bg-divider)":"var(--accent-gold)",border:"none",borderRadius:6,color:savedLists.length>=10?"var(--border-strong)":"#000",fontWeight:900,cursor:savedLists.length>=10?"default":"pointer",fontSize:13,flexShrink:0}}>+ New List</button>
              </div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {savedLists.map(sl=>{
                  const active=sl.id===activeListId;
                  const isRen=renamingListId===sl.id;
                  return(
                    <div key={sl.id} style={{display:"flex",alignItems:"center",gap:2,flexShrink:0}}>
                      {isRen?(
                        <input autoFocus value={listNameDraft} onChange={e=>setListNameDraft(e.target.value)}
                          onKeyDown={e=>{if(e.key==="Enter"){renameList(sl.id,listNameDraft);setRenamingListId(null);}if(e.key==="Escape")setRenamingListId(null);}}
                          onBlur={()=>{renameList(sl.id,listNameDraft);setRenamingListId(null);}}
                          style={{padding:"5px 10px",background:"var(--bg-card)",border:"1px solid var(--accent-gold)",borderRadius:7,color:"var(--accent-gold-text)",fontSize:14,fontWeight:700,width:130}}/>
                      ):(
                        <button onClick={()=>switchList(sl.id)}
                          style={{padding:"5px 12px",borderRadius:20,border:active?"2px solid var(--accent-gold)":"2px solid var(--border-hairline)",background:active?"var(--accent-gold-bg)":"transparent",color:active?"var(--accent-gold)":"var(--border-outline)",fontWeight:700,fontSize:14,cursor:"pointer",maxWidth:160,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                          {sl.name}
                        </button>
                      )}
                      {!isRen&&active&&(
                        <button onClick={()=>{setRenamingListId(sl.id);setListNameDraft(sl.name);}} title="Rename"
                          style={{background:"none",border:"1px solid var(--border-hairline)",borderRadius:5,color:"var(--border-outline)",cursor:"pointer",fontSize:12,padding:"2px 6px",flexShrink:0}}>✏️</button>
                      )}
                      {!isRen&&savedLists.length>1&&(
                        <button onClick={()=>deleteList(sl.id)} title="Delete list"
                          style={{background:"none",border:"1px solid var(--border-hairline)",borderRadius:5,color:"var(--border-strong)",cursor:"pointer",fontSize:12,padding:"2px 6px",flexShrink:0}}>✕</button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Toolbar */}
            <div style={{background:"var(--bg-divider)",border:"1px solid var(--accent-gold)",borderRadius:12,padding:"12px 18px",marginBottom:14,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
              <span style={{fontSize:13,fontWeight:800,color:"var(--accent-gold-text)"}}>✏️ {savedLists.find(l=>l.id===activeListId)?.name||"My Rankings"}</span>
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                <span style={{fontSize:11,fontWeight:800,color:"var(--text-muted)",letterSpacing:1}}>STARTING POINT</span>
                <select value={sortBy} onChange={e=>applySort(e.target.value)} title="Pick a starting order — you can drag freely from there. Re-pick any time to start over."
                  style={{padding:"5px 8px",background:"var(--bg-card)",border:"1px solid var(--border-strong)",borderRadius:6,color:"var(--accent-gold-text)",fontSize:13,fontWeight:700,cursor:"pointer"}}>
                  <option value="pfk">PFK Official Rankings</option>
                  <option value="draftcapital">NFL Draft Capital</option>
                  <option value="pfkmodel">PFK Rookie Model</option>
                </select>
              </div>
              {isDirty
                ? <span style={{fontSize:12,color:"var(--danger)",fontWeight:800,letterSpacing:0.5}}>● Unsaved changes</span>
                : saved
                  ? <span style={{fontSize:12,color:"var(--success)",fontWeight:800,letterSpacing:0.5}}>✓ Saved</span>
                  : <span style={{fontSize:12,color:"var(--border-outline)"}}>All changes saved</span>}
              <span style={{flex:1}}/>
              <button onClick={()=>setShowAddTier(v=>!v)} style={{padding:"6px 12px",background:"var(--bg-card)",border:"1px solid var(--accent-gold)",borderRadius:7,color:"var(--accent-gold-text)",fontWeight:700,cursor:"pointer",fontSize:14}}>+ Tier</button>
              <button onClick={()=>setShowAdd(v=>!v)} style={{padding:"6px 12px",background:"var(--bg-pill)",border:"1px solid var(--border-strong)",borderRadius:7,color:"#ccc",fontWeight:700,cursor:"pointer",fontSize:14}}>+ Player</button>
              <button onClick={undo} disabled={!history.length} style={{padding:"6px 12px",background:"transparent",border:"1px solid "+(history.length?"var(--accent-gold)":"var(--border-strong)"),borderRadius:7,color:history.length?"var(--accent-gold)":"var(--border-strong)",fontWeight:700,cursor:history.length?"pointer":"default",fontSize:14}}>↩ Undo</button>
              <button onClick={()=>{if(!confirm('Reset to the latest published PFK rankings? Your edits to this list will be lost.')) return; setHistory([]);setList(officialList||buildInitialList()); setIsDirty(false);}} style={{padding:"6px 12px",background:"transparent",border:"1px solid var(--border-outline)",borderRadius:7,color:"var(--text-muted)",fontWeight:700,cursor:"pointer",fontSize:14}}>↺ Reset</button>
              <button onClick={saveList} disabled={!isDirty} style={{padding:"6px 14px",background:isDirty?"var(--accent-gold)":"var(--bg-pill)",border:"none",borderRadius:7,color:isDirty?"#000":"var(--border-strong)",fontWeight:900,cursor:isDirty?"pointer":"default",fontSize:14,letterSpacing:0.5}}>💾 Save</button>
            </div>
            {showAddTier&&(<div style={{background:"var(--bg-divider)",border:"1px solid var(--accent-gold)",borderRadius:10,padding:14,marginBottom:12,display:"flex",gap:8,alignItems:"center"}}>
              <input value={newTierName} onChange={e=>setNewTierName(e.target.value)} placeholder="Tier name" onKeyDown={e=>e.key==="Enter"&&addTier()} style={{flex:1,padding:"7px 10px",background:"var(--bg-card)",border:"1px solid var(--border-strong)",borderRadius:7,color:"var(--text-bright)",fontSize:13}}/>
              <button onClick={addTier} style={{padding:"7px 16px",background:"var(--accent-gold)",border:"none",borderRadius:7,color:"#000",fontWeight:900,cursor:"pointer",fontSize:14}}>Add</button>
              <button onClick={()=>setShowAddTier(false)} style={{padding:"7px 10px",background:"transparent",border:"1px solid var(--border-strong)",borderRadius:7,color:"var(--text-muted)",cursor:"pointer",fontSize:14}}>✕</button>
            </div>)}
            {showAdd&&(<div style={{background:"var(--bg-divider)",border:"1px solid var(--accent-gold)",borderRadius:10,padding:14,marginBottom:12,display:"flex",gap:8,flexWrap:"wrap",alignItems:"flex-end"}}>
              <div style={{display:"flex",flexDirection:"column",gap:3,flex:2,minWidth:110}}><label style={{fontSize:12,color:"var(--border-outline)"}}>NAME</label><input value={newPlayer.name} onChange={e=>setNewPlayer({...newPlayer,name:e.target.value})} placeholder="Player name" style={inp({width:"100%"})}/></div>
              <div style={{display:"flex",flexDirection:"column",gap:3}}><label style={{fontSize:12,color:"var(--border-outline)"}}>POS</label><select value={newPlayer.pos} onChange={e=>setNewPlayer({...newPlayer,pos:e.target.value})} style={inp({})}>{["WR","RB","TE","QB"].map(o=><option key={o}>{o}</option>)}</select></div>
              {[["college","SCHOOL","School"]].map(([k,l,ph])=>(<div key={k} style={{display:"flex",flexDirection:"column",gap:3}}><label style={{fontSize:12,color:"var(--border-outline)"}}>{l}</label><input value={newPlayer[k]||""} onChange={e=>setNewPlayer({...newPlayer,[k]:e.target.value})} placeholder={ph} style={inp({width:120})}/></div>))}
              <button onClick={addNew} style={{padding:"7px 16px",background:"var(--accent-gold)",border:"none",borderRadius:7,color:"#000",fontWeight:900,cursor:"pointer",fontSize:14}}>Add</button>
              <button onClick={()=>setShowAdd(false)} style={{padding:"7px 10px",background:"transparent",border:"1px solid var(--border-strong)",borderRadius:7,color:"var(--text-muted)",cursor:"pointer",fontSize:14}}>✕</button>
            </div>)}
            <FilterBar/>
            <div className="pfk-rookie-list">
              <RenderList src={list} allowEdit={true} {...commonProps}/>
            </div>
          </div>
        )}
        <div style={{display:tab==="team"?"block":"none"}}><TeamTab/></div>
        {tab==="pfk2027"&&<RookieRanks2027 session={session}
          onRequestSignIn={()=>{setAuthMode('signin');setAuthOpen(true);}}
          onRequestSignUp={()=>{setAuthMode('signup');setAuthOpen(true);}}/>}
        {tab==="polls"&&<TradePollsTab session={session} onRequestSignIn={()=>{setAuthMode('signin');setAuthOpen(true);}}/>}
      </div>
      <PFKFooter/>
    </div>
  );
}

// PFK Rookie Model — admin-only tab. One sortable table across all positions.
// Each row: name, pos, stats% (editable), draft capital (auto from pick / overridable), film% (editable), PFK score (computed).
// Admin "PFK vs Zolty" tab — side-by-side comparison of Evan's PFK rankings against
// Zoltan's static rankings + ADP. Shows only players present in BOTH lists.
function PFKvsZoltyTab({officialList:livOfficialList}){
  // If AdminApp passes its live list (the in-memory rankings being edited), use that —
  // updates instantly as you drag/edit. Falls back to its own fetch if no prop given.
  const [fetchedList,setFetchedList]=useState(null);
  const [loading,setLoading]=useState(livOfficialList ? false : true);
  useEffect(()=>{
    if(livOfficialList) return;
    fetchOfficialRankings(DEFAULT_SETTINGS).then(row=>{
      setFetchedList(row?.data || []);
      setLoading(false);
    });
  },[livOfficialList]);
  const officialList = livOfficialList || fetchedList;
  // Strip trailing suffixes (Jr, Sr, II, III, IV) before matching, so "Omar Cooper Jr."
  // matches Zolty's "Omar Cooper" and "Chris Brazzell II" matches "Chris Brazzell".
  const compareKey = (s)=>normDraftName(s).replace(/(jr|sr|ii|iii|iv|v)$/, '');
  const rows = useMemo(()=>{
    if(!officialList) return [];
    const pfkRank = new Map();
    let r = 0;
    for(const it of officialList){
      if(it?.type === 'player'){
        r++;
        pfkRank.set(compareKey(it.name), { rank:r, name:it.name, pos:it.pos });
      }
    }
    const zByKey = new Map();
    for(const z of ZOLTY_2026){ zByKey.set(compareKey(z.name), z); }
    const out = [];
    for(const [key, p] of pfkRank.entries()){
      const z = zByKey.get(key);
      if(!z) continue;
      out.push({
        name: p.name, pos: p.pos,
        pfk: p.rank, zolty: z.rank, adp: z.adp,
        vsZolty: z.rank - p.rank,
        vsAdp:   z.adp  - p.rank,
      });
    }
    out.sort((a,b)=>a.pfk - b.pfk);
    return out;
  },[officialList]);

  const onlyPfk = useMemo(()=>{
    if(!officialList) return [];
    const zKeys = new Set(ZOLTY_2026.map(z=>compareKey(z.name)));
    const out = []; let r = 0;
    for(const it of officialList){
      if(it?.type === 'player'){
        r++;
        if(!zKeys.has(compareKey(it.name))) out.push({name:it.name,pos:it.pos,pfk:r});
      }
    }
    return out;
  },[officialList]);

  const onlyZolty = useMemo(()=>{
    if(!officialList) return [];
    const pfkKeys = new Set();
    for(const it of officialList){ if(it?.type==='player') pfkKeys.add(compareKey(it.name)); }
    return ZOLTY_2026.filter(z=>!pfkKeys.has(compareKey(z.name)));
  },[officialList]);

  if(loading) return <div style={{padding:20,color:'var(--text-secondary)'}}>Loading rankings…</div>;

  const POS_COLORS = {QB:'#a855f7', RB:'#22d3ee', WR:'#fbbf24', TE:'#f87171'};
  const Diff = ({n}) => {
    if(n===0) return <span style={{color:'var(--text-secondary)',fontWeight:800}}>±0</span>;
    const color = n>0 ? 'var(--success)' : 'var(--danger)';
    const sign = n>0 ? '+' : '';
    return <span style={{color,fontWeight:900}}>{sign}{n}</span>;
  };
  const th = {padding:'8px 10px',textAlign:'left',fontSize:11,letterSpacing:1,color:'var(--text-secondary)',fontWeight:800,borderBottom:'1px solid var(--bg-pill)',background:'var(--bg-elevated)',position:'sticky',top:0};
  const td = {padding:'8px 10px',fontSize:13,borderBottom:'1px solid var(--border)'};

  return (
    <div style={{padding:'16px 20px',color:'var(--text-eee)'}}>
      <div style={{marginBottom:14,display:'flex',alignItems:'baseline',gap:14,flexWrap:'wrap'}}>
        <div style={{fontSize:18,fontWeight:900,color:'var(--accent-gold-text)',letterSpacing:1}}>PFK vs ZOLTY</div>
        <div style={{fontSize:12,color:'var(--text-secondary)'}}>{rows.length} players in both lists · sorted by PFK rank · green = PFK higher · red = PFK lower</div>
      </div>
      <div className="pfk-wide-scroll" style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:10}}>
        <table style={{width:'100%',borderCollapse:'collapse',minWidth:560}}>
          <thead>
            <tr>
              <th style={{...th,width:60,textAlign:'right'}}>PFK</th>
              <th style={th}>PLAYER</th>
              <th style={{...th,width:50}}>POS</th>
              <th style={{...th,width:80,textAlign:'right'}}>ZOLTY</th>
              <th style={{...th,width:80,textAlign:'right'}}>ADP</th>
              <th style={{...th,width:110,textAlign:'right'}}>VS ZOLTY</th>
              <th style={{...th,width:110,textAlign:'right'}}>VS ADP</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r,i)=>(
              <tr key={r.name} style={{background:i%2?'var(--bg-card)':'var(--bg-card)'}}>
                <td style={{...td,textAlign:'right',fontWeight:900,color:'var(--accent-gold-text)'}}>{r.pfk}</td>
                <td style={{...td,fontWeight:700}}>{r.name}</td>
                <td style={td}><span style={{padding:'2px 7px',borderRadius:4,fontSize:11,fontWeight:800,background:'var(--bg-divider)',color:POS_COLORS[r.pos]||'var(--text-muted)',border:'1px solid '+(POS_COLORS[r.pos]||'var(--border-strong)')}}>{r.pos}</span></td>
                <td style={{...td,textAlign:'right',color:'var(--text-secondary)'}}>{r.zolty}</td>
                <td style={{...td,textAlign:'right',color:'var(--text-secondary)'}}>{r.adp}</td>
                <td style={{...td,textAlign:'right'}}><Diff n={r.vsZolty}/></td>
                <td style={{...td,textAlign:'right'}}><Diff n={r.vsAdp}/></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(onlyPfk.length>0 || onlyZolty.length>0) && (
        <div className="pfk-pr-grid" style={{marginTop:18,display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
          <div style={{background:'var(--bg-elevated)',border:'1px solid var(--border)',borderRadius:10,padding:'12px 14px'}}>
            <div style={{fontSize:11,fontWeight:800,letterSpacing:1,color:'var(--text-secondary)',marginBottom:6}}>ONLY ON PFK ({onlyPfk.length})</div>
            <div style={{fontSize:12,color:'var(--text-secondary)',lineHeight:1.7}}>
              {onlyPfk.map(p=><div key={p.name}><span style={{color:'var(--accent-gold-text)',fontWeight:700,display:'inline-block',width:30}}>{p.pfk}</span> {p.name} <span style={{color:'var(--text-secondary)'}}>({p.pos})</span></div>)}
            </div>
          </div>
          <div style={{background:'var(--bg-elevated)',border:'1px solid var(--border)',borderRadius:10,padding:'12px 14px'}}>
            <div style={{fontSize:11,fontWeight:800,letterSpacing:1,color:'var(--text-secondary)',marginBottom:6}}>ONLY ON ZOLTY ({onlyZolty.length})</div>
            <div style={{fontSize:12,color:'var(--text-secondary)',lineHeight:1.7}}>
              {onlyZolty.map(p=><div key={p.name}><span style={{color:'var(--text-secondary)',fontWeight:700,display:'inline-block',width:30}}>{p.rank}</span> {p.name} <span style={{color:'var(--text-secondary)'}}>({p.pos})</span></div>)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RookieModelTab(){
  const [items,setItems]=useState(null); // null = loading
  const [savedJson,setSavedJson]=useState('');
  const [posFilter,setPosFilter]=useState('ALL');
  const [showAdd,setShowAdd]=useState(false);
  const [newP,setNewP]=useState({name:'',pos:'WR',pick:''});
  const [msg,setMsg]=useState('');
  const [editing,setEditing]=useState(null); // {id, field}
  const [editVal,setEditVal]=useState('');
  // Combo selector for the model tab — drives the per-combo PFK column and override editing.
  const [modelSettings,setModelSettings]=useState(DEFAULT_SETTINGS);

  // Build initial model from current default-combo roster + DRAFT_2026 + baked maps.
  const buildFromRoster = useCallback(async()=>{
    const row = await fetchOfficialRankings(DEFAULT_SETTINGS);
    const roster = (row?.data||[]).filter(it=>it.type==='player');
    return roster.filter(p=>['QB','RB','WR','TE'].includes(p.pos)).map(p=>{
      const k = normDraftName(p.name);
      const draft = DRAFT_2026[k] || null;
      const filmScores = {};
      if(BAKED_FILM_ZOLTAN[k]   != null) filmScores.zoltan   = BAKED_FILM_ZOLTAN[k];
      if(BAKED_FILM_ZIERLEIN[k] != null) filmScores.zierlein = BAKED_FILM_ZIERLEIN[k];
      return {
        id: p.id || ('m_'+Date.now()+Math.random().toString(36).slice(2,6)),
        name: p.name, pos: p.pos,
        pick: draft?.pick || null,
        team: draft?.team || null,
        stats: BAKED_STATS_PCT[k] ?? null,
        filmScores,
        dcOverride: null,
      };
    });
  },[]);

  // Migrate legacy items + always-on backfill of any missing baked film source values.
  // Runs every load so newly-added baked values reach existing players without manual backfill.
  const migrate = (arr) => (arr||[]).map(it=>{
    const fs = (it.filmScores && typeof it.filmScores==='object') ? {...it.filmScores} : {};
    if(typeof it.film === 'number' && fs.zoltan==null) fs.zoltan = it.film;
    const k = normDraftName(it.name);
    if(fs.zoltan==null   && BAKED_FILM_ZOLTAN[k]   != null) fs.zoltan   = BAKED_FILM_ZOLTAN[k];
    if(fs.zierlein==null && BAKED_FILM_ZIERLEIN[k] != null) fs.zierlein = BAKED_FILM_ZIERLEIN[k];
    const {film, ...rest} = it;
    return {...rest, filmScores: fs};
  });

  useEffect(()=>{
    (async()=>{
      // Admin always reads draft (with fallback to published if draft missing).
      const row = await fetchModelDraft();
      if(row && Array.isArray(row.data) && row.data.length){
        const migrated = migrate(row.data);
        setItems(migrated);
        setSavedJson(JSON.stringify(migrated));
        if(JSON.stringify(migrated) !== JSON.stringify(row.data)){
          await saveModelData(migrated);
          setSavedJson(JSON.stringify(migrated));
        }
      }else{
        const seeded = await buildFromRoster();
        setItems(seeded);
        const { error } = await saveModelData(seeded);
        if(!error) setSavedJson(JSON.stringify(seeded));
      }
    })();
  },[buildFromRoster]);

  // Debounced auto-save
  useEffect(()=>{
    if(items===null) return;
    const cur = JSON.stringify(items);
    if(cur===savedJson) return;
    const t = setTimeout(async()=>{
      setMsg('Saving...');
      const { error } = await saveModelData(items);
      if(error){ setMsg('Save error: '+(error.message||error)); }
      else{ setSavedJson(cur); setMsg('Saved ✓'); setTimeout(()=>setMsg(''),1200); }
    },800);
    return ()=>clearTimeout(t);
  },[items,savedJson]);

  const computeDc = (it) => it.dcOverride!=null ? +it.dcOverride : dcScoreFromPick(it.pos, it.pick);
  const computeFilm = (it) => filmAvgPct(it.filmScores);
  // Baseline PFK = before per-combo multiplier and per-combo override.
  const computeBaselinePfk = (it) => {
    const base = pfkScore(it.stats, computeDc(it), computeFilm(it));
    return Math.round(base * landingMultiplier(it.landing) * 10) / 10;
  };
  // Combo-adjusted PFK = baseline × FORMAT × TEP × PTD multiplier; per-player override wins if present.
  const computePfk = (it) => {
    const sig = settingsSig(modelSettings);
    const ov = it.comboOverrides?.[sig];
    if(ov!=null && !isNaN(+ov)) return Math.round(+ov*10)/10;
    return Math.round(computeBaselinePfk(it) * comboMultiplier(it.pos, modelSettings) * 10) / 10;
  };

  const filtered = (items||[]).filter(it=>posFilter==='ALL' || it.pos===posFilter);
  const sorted = [...filtered].sort((a,b)=>computePfk(b)-computePfk(a));

  const updateField = (id,field,val) => setItems(prev=>prev.map(it=>{
    if(it.id!==id) return it;
    // Film source field: 'film:<source>' e.g. 'film:zoltan', 'film:evan', 'film:zierlein'
    if(field.startsWith('film:')){
      const source = field.slice(5);
      const src = FILM_SOURCES[source];
      const fs = {...(it.filmScores||{})};
      if(val===''||val===null||val===undefined){ delete fs[source]; }
      else if(src && src.kind==='categorical'){ fs[source] = String(val); }
      else { fs[source] = +val; }
      return {...it, filmScores: fs};
    }
    // Per-combo override: 'override' = current modelSettings combo's override value.
    if(field==='override'){
      const sig = settingsSig(modelSettings);
      const co = {...(it.comboOverrides||{})};
      if(val===''||val===null||val===undefined) delete co[sig];
      else co[sig] = Math.max(0, +val);
      return {...it, comboOverrides: co};
    }
    if(field==='dc') return {...it, dcOverride: val===''||val===null ? null : Math.max(0,Math.min(100,+val))};
    if(field==='stats') return {...it, stats: val===''||val===null ? null : Math.max(0,Math.min(100,+val))};
    if(field==='landing') return {...it, landing: val===''||val===null||val===undefined ? null : +val};
    if(field==='pick') return {...it, pick: val||null};
    return {...it, [field]: val};
  }));

  // Cycle a categorical film source: unrated → option[0] → option[1] → ... → unrated.
  const cycleCategorical = (id, sourceKey) => {
    const src = FILM_SOURCES[sourceKey]; if(!src || src.kind!=='categorical') return;
    setItems(prev=>prev.map(it=>{
      if(it.id!==id) return it;
      const cur = it.filmScores?.[sourceKey];
      const opts = src.options;
      const idx = cur==null ? -1 : opts.findIndex(o=>o.key===cur);
      const nextIdx = idx + 1; // -1 → 0, last → opts.length (which becomes "unrated")
      const fs = {...(it.filmScores||{})};
      if(nextIdx >= opts.length){ delete fs[sourceKey]; }
      else { fs[sourceKey] = opts[nextIdx].key; }
      return {...it, filmScores: fs};
    }));
  };

  const removeRow = (id) => { if(!confirm('Remove this player from the model?')) return; setItems(prev=>prev.filter(it=>it.id!==id)); };

  // PUBLISH MODEL — copies current (auto-saved) draft into the published row that prod reads.
  const [publishingModel,setPublishingModel] = useState(false);
  const [publishedSig,setPublishedSig] = useState(null);
  // Load the currently-published row signature so the button can show diff/no-diff state.
  useEffect(()=>{
    (async()=>{
      if(!sb) return;
      const { data } = await sb.from('pfk_rankings').select('id,data,settings').order('updated_at',{ascending:false});
      const pub = (data||[]).find(r=>r.settings && r.settings.kind==='rookie_model');
      if(pub) setPublishedSig(JSON.stringify(pub.data));
    })();
  },[]);
  const draftDiffersFromPub = items!==null && publishedSig !== JSON.stringify(items);
  const onPublishModel = async () => {
    if(!items?.length) { setMsg('Nothing to publish.'); setTimeout(()=>setMsg(''),2000); return; }
    if(!confirm('Publish current model state to PRODUCTION? This pushes every player\'s scores, overrides, and edits live to the public site.')) return;
    setPublishingModel(true); setMsg('Publishing model...');
    const { error } = await publishModel(items);
    setPublishingModel(false);
    if(error){ setMsg('Publish error: '+(error.message||error)); }
    else{ setPublishedSig(JSON.stringify(items)); setMsg('✓ Model published to production'); setTimeout(()=>setMsg(''),3500); }
  };

  const addRow = () => {
    if(!newP.name.trim()) return;
    const k = normDraftName(newP.name);
    const draft = newP.pick ? null : (DRAFT_2026[k]||null);
    const filmScores = {};
    if(BAKED_FILM_ZOLTAN[k]   != null) filmScores.zoltan   = BAKED_FILM_ZOLTAN[k];
    if(BAKED_FILM_ZIERLEIN[k] != null) filmScores.zierlein = BAKED_FILM_ZIERLEIN[k];
    const it = {
      id:'m_'+Date.now(),
      name:newP.name.trim(), pos:newP.pos,
      pick: newP.pick || draft?.pick || null,
      team: draft?.team || null,
      stats: BAKED_STATS_PCT[k] ?? null,
      filmScores,
      dcOverride: null,
    };
    setItems(prev=>[...(prev||[]), it]);
    setNewP({name:'',pos:'WR',pick:''});
    setShowAdd(false);
  };

  const reseed = async() => {
    if(!confirm('Rebuild the model from the current rookie roster? Edits to existing players will be kept; new players will be added with baked stats/film if available.')) return;
    const seeded = await buildFromRoster();
    setItems(prev=>{
      const byKey = new Map((prev||[]).map(it=>[normDraftName(it.name), it]));
      return seeded.map(s=>{
        const existing = byKey.get(normDraftName(s.name));
        return existing ? {...s, stats:existing.stats, filmScores:existing.filmScores, dcOverride:existing.dcOverride, id:existing.id} : s;
      });
    });
  };

  if(items===null) return <div style={{padding:40,color:'var(--accent-gold-text)',textAlign:'center'}}>Loading model...</div>;

  const cellStyle = {padding:'8px 10px', borderBottom:'1px solid var(--bg-pill)', fontSize:13, color:'var(--text-ddd)'};
  const headStyle = {padding:'10px 10px', background:'var(--bg-elevated)', borderBottom:'2px solid var(--accent-gold)', fontSize:11, color:'var(--accent-gold-text)', fontWeight:800, letterSpacing:1, textAlign:'left', position:'sticky', top:0};
  const numCellStyle = {...cellStyle, textAlign:'right', cursor:'pointer'};
  const editInputStyle = {width:60, padding:'4px 6px', background:'var(--bg-input)', border:'1px solid var(--accent-gold)', borderRadius:4, color:'var(--text-bright)', fontSize:13, textAlign:'right'};
  const POS_COLOR = {QB:'var(--danger)', RB:'var(--success)', WR:'var(--info)', TE:'var(--warning)'};

  // Returns true if a field's current value differs from its baked/default state.
  // For fields with no baked default (Evan in/out, Landing, per-combo Override): true if any value set.
  // Used to color manually-edited cells purple so Evan can see what he's overridden at a glance.
  const isManuallyEdited = (it, field) => {
    if(!it) return false;
    const k = normDraftName(it.name||'');
    switch(field){
      case 'stats':         { const b = BAKED_STATS_PCT[k]; return it.stats != null && (b == null || +it.stats !== +b); }
      case 'dc':            return it.dcOverride != null;
      case 'film:zoltan':   { const b = BAKED_FILM_ZOLTAN[k];   const v = it.filmScores?.zoltan;   return v != null && (b == null || +v !== +b); }
      case 'film:zierlein': { const b = BAKED_FILM_ZIERLEIN[k]; const v = it.filmScores?.zierlein; return v != null && (b == null || +v !== +b); }
      case 'film:evan':     return it.filmScores?.evan != null;
      case 'landing':       return it.landing != null;
      case 'override':      return it.comboOverrides?.[settingsSig(modelSettings)] != null;
      default: return false;
    }
  };
  const EDITED_COLOR = 'var(--accent-violet)';

  const startEdit = (id,field,cur) => { setEditing({id,field}); setEditVal(cur==null?'':String(cur)); };
  const commitEdit = () => {
    if(!editing) return;
    updateField(editing.id, editing.field, editVal);
    setEditing(null); setEditVal('');
  };
  const cancelEdit = () => { setEditing(null); setEditVal(''); };

  const renderNum = (it,field,val,suffix='') => {
    const isEdit = editing && editing.id===it.id && editing.field===field;
    if(isEdit) return (
      <input autoFocus value={editVal} onChange={e=>setEditVal(e.target.value)}
        onKeyDown={e=>{if(e.key==='Enter')commitEdit(); else if(e.key==='Escape')cancelEdit();}}
        onBlur={commitEdit} style={editInputStyle}/>
    );
    return <span onClick={()=>startEdit(it.id,field,val)} style={{display:'inline-block',minWidth:36,padding:'2px 4px',borderRadius:3}} title="Click to edit">{val==null?'—':val+suffix}</span>;
  };

  return (
    <div style={{padding:'12px 16px'}}>
      <div style={{padding:'10px 12px',background:'var(--bg-elevated)',border:'1px solid var(--bg-pill)',borderRadius:10,marginBottom:12,display:'flex',alignItems:'flex-start',gap:14,flexWrap:'wrap'}}>
        <div style={{flex:1,minWidth:280}}>
          <div style={{fontSize:11,color:'var(--accent-gold-text)',fontWeight:800,letterSpacing:2,marginBottom:8}}>VIEWING COMBO — settings drive PFK column & per-combo override edits</div>
          <SettingsToggleBar value={modelSettings} onChange={setModelSettings}/>
          <div style={{fontSize:11,color:'var(--text-secondary)',marginTop:6}}>Stats / DC / Film / Landing are baseline inputs and stay the same across combos. PFK column and Override apply only to the selected combo.</div>
        </div>
        <div style={{display:'flex',flexDirection:'column',gap:6,minWidth:180}}>
          <div style={{fontSize:11,color:'var(--accent-gold-text)',fontWeight:800,letterSpacing:2}}>PUBLISH MODEL</div>
          <button onClick={onPublishModel} disabled={publishingModel || !draftDiffersFromPub} style={{
            padding:'10px 14px',
            background: (publishingModel || !draftDiffersFromPub) ? 'var(--bg-pill)' : 'var(--accent-gold)',
            color: (publishingModel || !draftDiffersFromPub) ? 'var(--border-outline)' : '#000',
            border:'none', borderRadius:8, fontWeight:900, letterSpacing:1, fontSize:13,
            cursor: (publishingModel || !draftDiffersFromPub) ? 'not-allowed' : 'pointer'
          }}>{publishingModel ? 'PUBLISHING...' : draftDiffersFromPub ? '🚀 PUBLISH TO PROD' : '✓ IN SYNC'}</button>
          <div style={{fontSize:10,color:'var(--text-secondary)',lineHeight:1.4}}>{draftDiffersFromPub ? 'Draft differs from published. Click to push current scores live to playforkeeps-web.pages.dev.' : 'Draft and published are identical.'}</div>
        </div>
      </div>
      <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:12,flexWrap:'wrap'}}>
        <span style={{fontSize:12,color:'var(--text-secondary)',marginRight:6}}>FILTER</span>
        {['ALL','QB','RB','WR','TE'].map(p=>(
          <button key={p} onClick={()=>setPosFilter(p)} style={{
            padding:'5px 12px', borderRadius:14, fontSize:12, fontWeight:800, cursor:'pointer',
            background: posFilter===p ? 'var(--accent-gold)' : 'transparent',
            color: posFilter===p ? '#000' : 'var(--text-muted)',
            border: '1px solid '+(posFilter===p ? 'var(--accent-gold)' : 'var(--border-strong)'),
          }}>{p}</button>
        ))}
        <span style={{flex:1}}/>
        <button onClick={()=>setShowAdd(s=>!s)} style={{padding:'6px 12px',background:'transparent',border:'1px solid var(--accent-gold)',borderRadius:6,color:'var(--accent-gold-text)',cursor:'pointer',fontSize:13,fontWeight:700}}>+ Player</button>
        <button onClick={reseed} style={{padding:'6px 12px',background:'transparent',border:'1px solid var(--border-outline)',borderRadius:6,color:'var(--text-secondary)',cursor:'pointer',fontSize:13,fontWeight:700}} title="Pull any new rookies from the roster into the model">↻ Sync roster</button>
        {msg && <span style={{fontSize:12,color: msg.startsWith('Save error')?'var(--danger)':'var(--success)',marginLeft:8}}>{msg}</span>}
      </div>
      {showAdd && (
        <div style={{padding:'10px 12px',background:'var(--bg-card)',border:'1px solid var(--bg-pill)',borderRadius:8,marginBottom:12,display:'flex',gap:8,flexWrap:'wrap',alignItems:'flex-end'}}>
          <div style={{display:'flex',flexDirection:'column',gap:3,minWidth:160}}>
            <label style={{fontSize:11,color:'var(--text-secondary)'}}>NAME</label>
            <input autoFocus value={newP.name} onChange={e=>setNewP({...newP,name:e.target.value})} onKeyDown={e=>e.key==='Enter'&&addRow()} placeholder="Player name" style={{padding:'7px 9px',background:'var(--bg-input)',border:'1px solid var(--border-strong)',borderRadius:5,color:'var(--text-bright)',fontSize:13}}/>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:3}}>
            <label style={{fontSize:11,color:'var(--text-secondary)'}}>POS</label>
            <select value={newP.pos} onChange={e=>setNewP({...newP,pos:e.target.value})} style={{padding:'7px 9px',background:'var(--bg-input)',border:'1px solid var(--border-strong)',borderRadius:5,color:'var(--text-bright)',fontSize:13}}>{['QB','RB','WR','TE'].map(o=><option key={o}>{o}</option>)}</select>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:3}}>
            <label style={{fontSize:11,color:'var(--text-secondary)'}}>PICK (optional, e.g. 3.05)</label>
            <input value={newP.pick} onChange={e=>setNewP({...newP,pick:e.target.value})} onKeyDown={e=>e.key==='Enter'&&addRow()} placeholder="leave blank for UDFA" style={{padding:'7px 9px',background:'var(--bg-input)',border:'1px solid var(--border-strong)',borderRadius:5,color:'var(--text-bright)',fontSize:13,width:160}}/>
          </div>
          <button onClick={addRow} style={{padding:'8px 14px',background:'var(--accent-gold)',color:'#000',border:'none',borderRadius:5,fontWeight:900,cursor:'pointer',fontSize:13}}>Add</button>
          <button onClick={()=>setShowAdd(false)} style={{padding:'8px 10px',background:'transparent',border:'1px solid var(--border-strong)',borderRadius:5,color:'var(--text-secondary)',cursor:'pointer',fontSize:13}}>Cancel</button>
        </div>
      )}
      <div style={{overflowX:'auto',border:'1px solid var(--bg-pill)',borderRadius:8}}>
        <table style={{width:'100%',borderCollapse:'collapse',background:'var(--bg-elevated)'}}>
          <thead>
            <tr>
              <th style={{...headStyle,width:50,textAlign:'center'}}>#</th>
              <th style={headStyle}>Player</th>
              <th style={{...headStyle,width:50,textAlign:'center'}}>Pos</th>
              <th style={{...headStyle,width:80}}>Pick</th>
              <th style={{...headStyle,width:75,textAlign:'right'}}>Stats%</th>
              <th style={{...headStyle,width:75,textAlign:'right'}}>DC</th>
              {Object.entries(FILM_SOURCES).map(([k,src])=>(
                <th key={k} style={{...headStyle,width:75,textAlign: src.kind==='categorical'?'center':'right'}} title={`${src.label} (weight ${src.weight}%, raw scale ${src.scaleHint})`}>{src.label}<span style={{color:'var(--text-secondary)',fontWeight:600,marginLeft:4}}>{src.weight}%</span></th>
              ))}
              <th style={{...headStyle,width:75,textAlign:'right',color:'var(--text-secondary)'}} title="Average of all populated film sources, normalized to 0-100">Film%</th>
              <th style={{...headStyle,width:75,textAlign:'center'}} title="Landing spot multiplier — 5=best (×1.08), 4 (×1.04), 3=neutral (×1.00), 2 (×0.96), 1=worst (×0.92). Default — = ×1.00 (no impact).">Landing</th>
              <th style={{...headStyle,width:75,textAlign:'right'}} title="Per-combo manual override of PFK score. Empty = use formula. Only applies to the currently-selected combo.">Override</th>
              <th style={{...headStyle,width:75,textAlign:'right',color:'var(--accent-gold-text)'}} title="PFK = baseline × FORMAT × TEP × PTD multiplier (or override if set)">PFK</th>
              <th style={{...headStyle,width:40}}></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((it,i)=>{
              const dc = computeDc(it);
              const filmPct = computeFilm(it);
              const pfk = computePfk(it);
              const dcOverridden = it.dcOverride!=null;
              return (
                <tr key={it.id} style={{background:i%2?'var(--bg-card)':'var(--bg-elevated)'}}>
                  <td style={{...cellStyle,textAlign:'center',color:'var(--text-secondary)',fontWeight:700}}>{i+1}</td>
                  <td style={cellStyle}>{it.name}{it.team?<span style={{color:'var(--text-secondary)',fontWeight:600,marginLeft:6,fontSize:11}}>{it.team}</span>:null}</td>
                  <td style={{...cellStyle,textAlign:'center'}}><span style={{display:'inline-block',padding:'2px 6px',borderRadius:4,background:POS_COLOR[it.pos]+'33',color:POS_COLOR[it.pos],fontWeight:800,fontSize:11}}>{it.pos}</span></td>
                  <td style={cellStyle}>{it.pick||'UDFA'}</td>
                  <td style={{...numCellStyle, color: isManuallyEdited(it,'stats') ? EDITED_COLOR : 'var(--text-ddd)'}} title={isManuallyEdited(it,'stats') ? 'Manually edited' : 'Click to edit'}>{renderNum(it,'stats',it.stats)}</td>
                  <td style={{...numCellStyle,color: dcOverridden?'var(--accent-violet)':'var(--text-ddd)'}} title={dcOverridden?'Manual override (click to edit / clear)':'Auto from pick (click to override)'}>
                    {renderNum(it,'dc', dcOverridden?it.dcOverride:dc)}
                    {dcOverridden && <button onClick={(e)=>{e.stopPropagation(); updateField(it.id,'dc','');}} style={{marginLeft:4,background:'transparent',border:'none',color:'var(--text-secondary)',cursor:'pointer',fontSize:11}} title="Clear override">×</button>}
                  </td>
                  {Object.keys(FILM_SOURCES).map(srcKey=>{
                    const src = FILM_SOURCES[srcKey];
                    const cur = it.filmScores?.[srcKey];
                    const fieldKey = 'film:'+srcKey;
                    const edited = isManuallyEdited(it, fieldKey);
                    const tooltip = `${src.label} · weight ${src.weight}% · scale ${src.scaleHint}${edited ? ' · MANUALLY EDITED' : ''}`;
                    if(src.kind==='categorical'){
                      const opt = cur==null ? null : src.options.find(o=>o.key===cur);
                      return (
                        <td key={srcKey} style={{...cellStyle, textAlign:'center', boxShadow: edited ? `inset 0 0 0 2px ${EDITED_COLOR}` : 'none'}} title={tooltip}>
                          <select
                            value={cur||''}
                            onChange={e=>updateField(it.id,'film:'+srcKey, e.target.value||null)}
                            style={{
                              minWidth:60, padding:'3px 6px', borderRadius:4, cursor:'pointer',
                              fontSize:11, fontWeight:900, letterSpacing:0.5, textAlign:'center',
                              background: opt ? opt.color+'33' : 'var(--bg-elevated)',
                              color: opt ? opt.color : 'var(--border-outline)',
                              border: '1px solid '+(opt ? opt.color : 'var(--bg-pill)'),
                              appearance:'none', WebkitAppearance:'none', MozAppearance:'none',
                            }}
                          >
                            <option value="">—</option>
                            {src.options.map(o=><option key={o.key} value={o.key}>{o.label}</option>)}
                          </select>
                        </td>
                      );
                    }
                    return (
                      <td key={srcKey} style={{...numCellStyle, color: edited ? EDITED_COLOR : 'var(--text-ddd)'}} title={tooltip}>
                        {renderNum(it,'film:'+srcKey, cur)}
                      </td>
                    );
                  })}
                  <td style={{...cellStyle,textAlign:'right',color:filmPct==null?'var(--border-strong)':'var(--text-secondary)',fontWeight:700}} title="Average of populated sources">{filmPct==null?'—':filmPct}</td>
                  {(() => {
                    const lopt = it.landing==null ? null : LANDING_OPTIONS.find(o=>o.key===+it.landing);
                    const lEdited = isManuallyEdited(it, 'landing');
                    return (
                      <td style={{...cellStyle, textAlign:'center', boxShadow: lEdited ? `inset 0 0 0 2px ${EDITED_COLOR}` : 'none'}} title={`Landing multiplier ×${landingMultiplier(it.landing).toFixed(2)}${lEdited ? ' · MANUALLY EDITED' : ''}`}>
                        <select
                          value={it.landing==null?'':String(it.landing)}
                          onChange={e=>updateField(it.id,'landing',e.target.value||null)}
                          style={{
                            minWidth:50, padding:'3px 6px', borderRadius:4, cursor:'pointer',
                            fontSize:11, fontWeight:900, letterSpacing:0.5, textAlign:'center',
                            background: lopt ? lopt.color+'33' : 'var(--bg-elevated)',
                            color: lopt ? lopt.color : 'var(--border-outline)',
                            border: '1px solid '+(lopt ? lopt.color : 'var(--bg-pill)'),
                            appearance:'none', WebkitAppearance:'none', MozAppearance:'none',
                          }}
                        >
                          <option value="">—</option>
                          {LANDING_OPTIONS.map(o=><option key={o.key} value={o.key}>{o.label}</option>)}
                        </select>
                      </td>
                    );
                  })()}
                  {(() => {
                    const sig = settingsSig(modelSettings);
                    const ov = it.comboOverrides?.[sig];
                    const isOv = ov!=null && !isNaN(+ov);
                    return (
                      <td style={{...numCellStyle,color: isOv?'var(--accent-violet)':'var(--border-outline)'}} title={isOv?`Override for this combo (×${comboMultiplier(it.pos,modelSettings).toFixed(2)} bypassed). Click to edit / clear.`:`No override — using formula × ${comboMultiplier(it.pos,modelSettings).toFixed(2)} multiplier`}>
                        {renderNum(it,'override', isOv?ov:null)}
                        {isOv && <button onClick={(e)=>{e.stopPropagation(); updateField(it.id,'override','');}} style={{marginLeft:4,background:'transparent',border:'none',color:'var(--text-secondary)',cursor:'pointer',fontSize:11}} title="Clear override">×</button>}
                      </td>
                    );
                  })()}
                  {(() => {
                    const sig = settingsSig(modelSettings);
                    const isOv = it.comboOverrides?.[sig]!=null;
                    return <td style={{...cellStyle,textAlign:'right',fontWeight:900,color: isOv?'var(--accent-violet)':'var(--accent-gold)',fontSize:14}} title={isOv?'Override active for this combo':`Auto: baseline × ${comboMultiplier(it.pos,modelSettings).toFixed(2)} combo multiplier`}>{pfk}</td>;
                  })()}
                  <td style={{...cellStyle,textAlign:'center'}}><button onClick={()=>removeRow(it.id)} style={{background:'transparent',border:'none',color:'var(--text-secondary)',cursor:'pointer',fontSize:16}} title="Remove from model">×</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{marginTop:10,fontSize:11,color:'var(--text-secondary)',lineHeight:1.6}}>
        <div>PFK = (35% Stats + 50% Draft Capital + 15% Film) × Landing multiplier × per-combo multiplier (FORMAT × TEP × PTD).</div>
        <div>Film% = weighted average of populated sources (Zoltan 50% · You 30% · Zierlein 20%); missing sources drop and weights re-normalize.</div>
        <div>Landing: 5=best (×1.08) ... 1=worst (×0.92), — = ×1.00 (no impact). Override (per-combo) bypasses the formula entirely for the selected combo only — other combos unaffected.</div>
        <div>Click any number to edit. DC and Override cells turn purple when manually set; × clears.</div>
      </div>
    </div>
  );
}

function AdminApp(){
  const [session,setSession]=useState(null);
  const [email,setEmail]=useState('');
  const [password,setPassword]=useState('');
  const [loginErr,setLoginErr]=useState('');
  const [loading,setLoading]=useState(true);
  const [sets,setSets]=useState({});
  const [editingPlayer,setEditingPlayer]=useState(null);
  const [playerDraft,setPlayerDraft]=useState({});
  const [renamingTier,setRenamingTier]=useState(null);
  const [tierNameDraft,setTierNameDraft]=useState("");
  const [posFilter,setPosFilter]=useState(()=>new Set(["WR","RB","TE","QB"]));
  const [publishMsg,setPublishMsg]=useState('');
  const [adminSettings,setAdminSettings]=useState(DEFAULT_SETTINGS);
  const [showAddPlayer,setShowAddPlayer]=useState(false);
  const [newP,setNewP]=useState({name:'',pos:'WR',college:''});
  const [adminTab,setAdminTab]=useState('rankings'); // 'rankings' | 'model'
  // Mirror the public site's modelByName so admin rankings auto-tier from the same data.
  // Pulls the DRAFT row (so admin previews match dev URL).
  const [modelByName,setModelByName]=useState({});
  useEffect(()=>{
    fetchModelDraft().then(row=>{
      if(!row?.data || !Array.isArray(row.data)) return;
      const m={}; row.data.forEach(it=>{ if(it?.name) m[normDraftName(it.name)] = it; });
      setModelByName(m);
    }).catch(()=>{});
  },[]);

  const sigOf=s=>`${s.format||'Superflex'}|${s.tep}|${s.ppr}|${s.passTd}|${s.ppc}`;
  const currentSig=sigOf(adminSettings);
  const bucket=sets[currentSig];
  const list=bucket?.items||[];
  const history=bucket?.history||[];
  const lastUpdated=bucket?.updatedAt||null;
  const adminMissing=bucket?.missing||false;
  const isDirty=s=>JSON.stringify(s.items)!==(s.saved||'');
  const dirtyCount=Object.values(sets).filter(isDirty).length;

  useEffect(()=>{
    if(!sb){ setLoading(false); return; }
    sb.auth.getSession().then(({data})=>{ setSession(data.session); setLoading(false); });
    const { data:sub } = sb.auth.onAuthStateChange((_e,s)=>setSession(s));
    return ()=>sub?.subscription?.unsubscribe?.();
  },[]);

  useEffect(()=>{
    if(!session) return;
    if(sets[currentSig]) return;
    // Wait for the model so first-time combos can auto-seed from PFK order
    // (with the agreed-upon dynamic per-class tiers and the per-combo position multiplier).
    if(!Object.keys(modelByName).length) return;
    const snapshotSettings={...adminSettings};
    fetchOfficialRankings(snapshotSettings).then(row=>{
      const hasMatch=row&&row.data&&Array.isArray(row.data)&&sameSettings(row.settings,snapshotSettings);
      let items;
      if(hasMatch){
        items = row.data;
      } else {
        // No published row for this combo → seed from current model in this combo's order
        items = buildSeedFromModel(modelByName, snapshotSettings, row?.data || null) || buildInitialList();
      }
      setSets(prev=>prev[currentSig]?prev:{...prev,[currentSig]:{
        items, saved: hasMatch?JSON.stringify(items):null,
        updatedAt: hasMatch?row.updated_at:null,
        missing: !hasMatch, history:[], settings:snapshotSettings
      }});
    });
  },[session,currentSig,modelByName]);

  const login=async()=>{
    setLoginErr('');
    if(!sb){ setLoginErr('Supabase not loaded'); return; }
    const { error } = await sb.auth.signInWithPassword({email,password});
    if(error) setLoginErr(error.message);
  };
  const logout=async()=>{ await sb.auth.signOut(); };

  const publish=async()=>{
    const dirty=Object.entries(sets).filter(([,s])=>isDirty(s));
    if(!dirty.length){ setPublishMsg('Nothing to publish.'); setTimeout(()=>setPublishMsg(''),2500); return; }
    setPublishMsg(`Publishing ${dirty.length} combo${dirty.length>1?'s':''}...`);
    const errors=[];
    const nowIso=new Date().toISOString();
    for(const [sig,s] of dirty){
      const { error } = await publishOfficialRankings(s.items, s.settings);
      if(error) errors.push(error.message||String(error));
      else {
        const snap=JSON.stringify(s.items);
        setSets(prev=>prev[sig]?{...prev,[sig]:{...prev[sig],saved:snap,updatedAt:nowIso,missing:false}}:prev);
      }
    }
    if(errors.length) setPublishMsg('Error: '+errors.join('; '));
    else setPublishMsg(`Published ${dirty.length} combo${dirty.length>1?'s':''}! Live on the site.`);
    setTimeout(()=>setPublishMsg(''),3500);
  };

  const mutate=(fn)=>setSets(prev=>{
    const cur=prev[currentSig]; if(!cur) return prev;
    const next=fn(cur.items);
    return {...prev,[currentSig]:{...cur,items:next,history:[...cur.history.slice(-20),cur.items]}};
  });
  const undo=()=>setSets(prev=>{
    const cur=prev[currentSig]; if(!cur||!cur.history.length) return prev;
    return {...prev,[currentSig]:{...cur,items:cur.history[cur.history.length-1],history:cur.history.slice(0,-1)}};
  });

  const onReorder=(fromId,beforeId)=>mutate(prev=>{ const l=[...prev], fi=l.findIndex(x=>x.id===fromId); if(fi<0) return prev; const [item]=l.splice(fi,1); if(beforeId===null){ l.push(item); }else{ const ti=l.findIndex(x=>x.id===beforeId); l.splice(ti!==-1?ti:l.length,0,item); } return l; });
  const onMove=(id,dir)=>mutate(prev=>{ const l=[...prev], i=l.findIndex(x=>x.id===id), sw=i+dir; if(sw<0||sw>=l.length) return l; [l[i],l[sw]]=[l[sw],l[i]]; return l; });
  const onEdit=(p)=>{ setEditingPlayer(p.id); setPlayerDraft({...p}); };
  const onRemove=(id)=>mutate(prev=>prev.filter(x=>x.id!==id));
  const onSavePlayer=()=>{ mutate(prev=>prev.map(x=>x.id===editingPlayer?{...x,...playerDraft}:x)); setEditingPlayer(null); };
  const onCancelEdit=()=>setEditingPlayer(null);
  const onRenameStart=(id,name)=>{ setRenamingTier(id); setTierNameDraft(name); };
  const onRenameCancel=()=>setRenamingTier(null);
  const onRenameSave=()=>{ mutate(prev=>prev.map(x=>x.id===renamingTier?{...x,name:tierNameDraft}:x)); setRenamingTier(null); };
  const onDeleteTier=(id)=>{ if(!confirm('Delete this tier? Players inside will fall into the tier above.')) return; mutate(prev=>prev.filter(x=>x.id!==id)); };
  const addTier=()=>{ const name=prompt('Tier name:'); if(!name) return; mutate(prev=>[...prev,{id:'tier_'+Date.now(),type:'tier',name}]); };
  // 🌱 Seed from Model — replaces the current combo's list with the model's auto-tier output
  // for the currently-selected adminSettings. Wraps the shared buildSeedFromModel helper.
  const seedFromModel=()=>{
    if(!Object.keys(modelByName).length){ alert('Model data not loaded yet — try again in a moment.'); return; }
    const seeded = buildSeedFromModel(modelByName, adminSettings, list);
    if(!seeded){ alert('Could not build from model.'); return; }
    if(!confirm(`Replace the current combo's rankings with the model's auto-tier output (${seeded.filter(x=>x.type==='player').length} players)? Your manual edits to this combo will be lost. Other combos are unaffected.`)) return;
    mutate(prev=>seeded);
    setPublishMsg(`Seeded from model — ${seeded.filter(x=>x.type==='player').length} players in ${seeded.filter(x=>x.type==='tier').length} tiers. Hit PUBLISH to push live.`);
    setTimeout(()=>setPublishMsg(''),5000);
  };
  const copyToAllCombos=()=>{
    if(!list.length){ alert('Current list is empty — nothing to copy.'); return; }
    const total=FORMAT_CHOICES.length*TEP_CHOICES.length*PPR_CHOICES.length*PTD_CHOICES.length*PPC_CHOICES.length;
    if(!confirm(`Overwrite this list into all ${total} settings combos? You can still tweak individual combos after. Hit PUBLISH to commit.`)) return;
    const itemsJson=JSON.stringify(list);
    const items=JSON.parse(itemsJson);
    setSets(prev=>{
      const next={...prev};
      for(const format of FORMAT_CHOICES)
      for(const tep of TEP_CHOICES)
      for(const ppr of PPR_CHOICES)
      for(const passTd of PTD_CHOICES)
      for(const ppc of PPC_CHOICES){
        const settings={format,tep,ppr,passTd,ppc};
        const sig=sigOf(settings);
        const cur=next[sig];
        next[sig]={
          items: JSON.parse(itemsJson),
          saved: cur?.saved ?? null,
          updatedAt: cur?.updatedAt ?? null,
          missing: cur?.missing ?? true,
          history: cur?(cur.history||[]).concat([cur.items]).slice(-20):[],
          settings
        };
      }
      return next;
    });
    setPublishMsg(`Staged list into ${total} combos — hit PUBLISH to commit.`);
    setTimeout(()=>setPublishMsg(''),4000);
  };
  const addPlayer=async()=>{
    if(!newP.name.trim()) return;
    const player={type:'player',id:'p_'+Date.now(),name:newP.name.trim(),pos:newP.pos,college:newP.college.trim()};
    setNewP({name:'',pos:'WR',college:''}); setShowAddPlayer(false);
    const allCombos=[];
    for(const format of FORMAT_CHOICES)
    for(const tep of TEP_CHOICES)
    for(const ppr of PPR_CHOICES)
    for(const passTd of PTD_CHOICES)
    for(const ppc of PPC_CHOICES) allCombos.push({format,tep,ppr,passTd,ppc});
    setPublishMsg(`Adding ${player.name} to all ${allCombos.length} combos...`);
    const missing=allCombos.filter(s=>!sets[sigOf(s)]);
    const fetched=await Promise.all(missing.map(async s=>{
      const row=await fetchOfficialRankings(s);
      const hasMatch=row&&row.data&&Array.isArray(row.data)&&sameSettings(row.settings,s);
      const items=(row?.data&&Array.isArray(row.data))?row.data:buildInitialList();
      return {s,items,saved:hasMatch?JSON.stringify(items):null,updatedAt:hasMatch?row.updated_at:null,missing:!hasMatch};
    }));
    setSets(prev=>{
      const next={...prev};
      for(const f of fetched){
        const sig=sigOf(f.s);
        if(!next[sig]) next[sig]={items:f.items,saved:f.saved,updatedAt:f.updatedAt,missing:f.missing,history:[],settings:f.s};
      }
      for(const s of allCombos){
        const sig=sigOf(s);
        const cur=next[sig];
        if(cur) next[sig]={...cur,items:[...cur.items,player],history:[...(cur.history||[]).slice(-20),cur.items]};
      }
      return next;
    });
    setPublishMsg(`Added ${player.name} to all ${allCombos.length} combos — hit PUBLISH to commit.`);
    setTimeout(()=>setPublishMsg(''),4000);
  };

  if(loading) return <div style={{padding:40,color:'var(--accent-gold-text)',textAlign:'center'}}>Loading...</div>;

  if(!session){
    return (
      <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
        <div style={{width:'100%',maxWidth:380,background:'var(--bg-divider)',border:'1px solid var(--border-strong)',borderRadius:12,padding:28}}>
          <div style={{textAlign:'center',marginBottom:20}}>
            <div style={{fontWeight:900,fontSize:22,letterSpacing:3,color:'var(--accent-gold-text)'}}>PFK ADMIN</div>
            <div style={{fontSize:13,color:'var(--text-secondary)',marginTop:4}}>Staff login</div>
          </div>
          <input placeholder="Email" value={email} onChange={e=>setEmail(e.target.value)} style={{width:'100%',padding:10,marginBottom:10,background:'var(--bg-input)',border:'1px solid var(--border-strong)',borderRadius:6,color:'var(--text-bright)'}}/>
          <input type="password" placeholder="Password" value={password} onChange={e=>setPassword(e.target.value)} onKeyDown={e=>e.key==='Enter'&&login()} style={{width:'100%',padding:10,marginBottom:14,background:'var(--bg-input)',border:'1px solid var(--border-strong)',borderRadius:6,color:'var(--text-bright)'}}/>
          <button onClick={login} style={{width:'100%',padding:12,background:'var(--accent-gold)',color:'#000',border:'none',borderRadius:6,fontWeight:900,cursor:'pointer',letterSpacing:1}}>SIGN IN</button>
          {loginErr&&<div style={{color:'var(--danger)',fontSize:14,marginTop:10,textAlign:'center'}}>{loginErr}</div>}
          <div style={{textAlign:'center',marginTop:16}}><a href="/" style={{fontSize:13,color:'var(--text-secondary)',textDecoration:'none'}}>← Back to site</a></div>
        </div>
      </div>
    );
  }

  const commonProps={ onReorder,onMove,onEdit,onRemove,onRenameStart,onRenameCancel,onRenameSave,onDeleteTier,renamingTier,tierNameDraft,setTierNameDraft,editingPlayer,playerDraft,setPlayerDraft,onSavePlayer,onCancelEdit,posFilter, modelByName, pfkSettings: adminSettings };

  return (
    <div style={{minHeight:'100vh',paddingBottom:40}}>
      <div className="pfk-admin-topbar" style={{position:'sticky',top:0,zIndex:100,background:'var(--bg-page)',borderBottom:'2px solid var(--accent-gold)',padding:'12px 16px',display:'flex',alignItems:'center',justifyContent:'space-between',gap:10,flexWrap:'wrap'}}>
        <div>
          <div style={{fontWeight:900,fontSize:16,letterSpacing:2,color:'var(--accent-gold-text)'}}>PFK ADMIN</div>
          <div style={{fontSize:12,color:'var(--text-secondary)'}}>{session.user.email}{lastUpdated&&' · last published '+new Date(lastUpdated).toLocaleString()}</div>
        </div>
        <div className="pfk-admin-actions" style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
          {adminTab==='rankings' && <a href={(window.location.hostname.includes('dev.')?'':'https://dev.playforkeeps-web.pages.dev')+'/'} target={window.location.hostname.includes('dev.')?'_self':'_blank'} rel="noopener" style={{padding:'8px 14px',background:'transparent',border:'2px solid var(--success)',borderRadius:6,color:'var(--success)',cursor:'pointer',fontSize:13,fontWeight:900,textDecoration:'none',letterSpacing:1}}>✏️ EDIT ON DEV</a>}
          <button onClick={logout} style={{padding:'8px 12px',background:'transparent',border:'1px solid var(--border-outline)',borderRadius:6,color:'var(--text-secondary)',cursor:'pointer',fontSize:14}}>Sign out</button>
        </div>
      </div>
      {publishMsg&&<div style={{padding:'8px 16px',background:publishMsg.startsWith('Error')?'var(--danger-bg)':'#103a10',color:publishMsg.startsWith('Error')?'var(--danger)':'var(--success)',fontSize:14,fontWeight:700}}>{publishMsg}</div>}
      <div style={{display:'flex',gap:0,background:'var(--bg-page)',borderBottom:'1px solid var(--bg-pill)',padding:'0 16px'}}>
        {[['rankings','RANKINGS'],['model','ROOKIE MODEL'],['compare','PFK vs ZOLTY'],['power','POWER RANKINGS']].map(([k,label])=>(
          <button key={k} onClick={()=>setAdminTab(k)} style={{
            padding:'10px 18px', background:'transparent', border:'none',
            borderBottom: adminTab===k ? '2px solid var(--accent-gold)' : '2px solid transparent',
            color: adminTab===k ? 'var(--accent-gold)' : 'var(--border-outline)', fontWeight:800, fontSize:13, letterSpacing:1.5, cursor:'pointer',
          }}>{label}</button>
        ))}
      </div>
      {adminTab==='model' ? <RookieModelTab/> : adminTab==='compare' ? <PFKvsZoltyTab officialList={list}/> : adminTab==='power' ? <div style={{padding:'16px'}}><TeamTab/></div> : (<>
      <div style={{padding:'12px 16px',background:'var(--bg-elevated)',borderBottom:'1px solid var(--bg-pill)'}}>
        <div style={{fontSize:12,color:'var(--accent-gold-text)',fontWeight:800,letterSpacing:2,marginBottom:8}}>RANKING SET — PICK SETTINGS, EDIT, PUBLISH</div>
        <SettingsToggleBar value={adminSettings} onChange={setAdminSettings}/>
        {adminMissing&&<div style={{fontSize:13,color:'var(--warning-strong)',marginTop:8}}>⚠ No ranking published for this combo yet. Editing will start from the most recent ranking — hit PUBLISH to create a new set for these settings.</div>}
      </div>
      {showAddPlayer&&(
        <div style={{padding:'12px 16px',background:'var(--bg-card)',borderBottom:'1px solid var(--bg-pill)',display:'flex',gap:8,flexWrap:'wrap',alignItems:'flex-end'}}>
          <div style={{display:'flex',flexDirection:'column',gap:3,flex:'1 1 200px',minWidth:140}}>
            <label style={{fontSize:12,color:'var(--text-secondary)'}}>NAME</label>
            <input autoFocus value={newP.name} onChange={e=>setNewP({...newP,name:e.target.value})} onKeyDown={e=>e.key==='Enter'&&addPlayer()} placeholder="Player name" style={{padding:'8px 10px',background:'var(--bg-input)',border:'1px solid var(--border-strong)',borderRadius:6,color:'var(--text-bright)',fontSize:13}}/>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:3}}>
            <label style={{fontSize:12,color:'var(--text-secondary)'}}>POS</label>
            <select value={newP.pos} onChange={e=>setNewP({...newP,pos:e.target.value})} style={{padding:'8px 10px',background:'var(--bg-input)',border:'1px solid var(--border-strong)',borderRadius:6,color:'var(--text-bright)',fontSize:13}}>{['WR','RB','TE','QB'].map(o=><option key={o}>{o}</option>)}</select>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:3,flex:'1 1 160px',minWidth:120}}>
            <label style={{fontSize:12,color:'var(--text-secondary)'}}>SCHOOL</label>
            <input value={newP.college} onChange={e=>setNewP({...newP,college:e.target.value})} onKeyDown={e=>e.key==='Enter'&&addPlayer()} placeholder="School" style={{padding:'8px 10px',background:'var(--bg-input)',border:'1px solid var(--border-strong)',borderRadius:6,color:'var(--text-bright)',fontSize:13}}/>
          </div>
          <button onClick={addPlayer} style={{padding:'9px 16px',background:'var(--accent-gold)',color:'#000',border:'none',borderRadius:6,fontWeight:900,cursor:'pointer',fontSize:14}}>Add</button>
          <button onClick={()=>setShowAddPlayer(false)} style={{padding:'9px 12px',background:'transparent',border:'1px solid var(--border-strong)',borderRadius:6,color:'var(--text-secondary)',cursor:'pointer',fontSize:14}}>Cancel</button>
        </div>
      )}
      <div className="pfk-admin-list pfk-rookie-list" style={{padding:'16px'}}>
        <div style={{padding:'10px 12px',background:'var(--accent-gold-bg)',border:'1px solid rgba(var(--accent-gold-rgb),0.2)',borderRadius:8,marginBottom:12,fontSize:12,color:'var(--text-secondary)',lineHeight:1.6}}>
          <span style={{color:'var(--accent-gold-text)',fontWeight:800,letterSpacing:1,marginRight:6}}>READ-ONLY PREVIEW</span>
          This shows the model's auto-tier output for the selected combo — what the dev URL displays before any of your edits.
          <div style={{marginTop:6}}>
            <span style={{color:'var(--text-secondary)',fontWeight:700}}>To edit & publish:</span> click <span style={{color:'var(--success)',fontWeight:800}}>✏️ EDIT ON DEV</span> above. You'll go to the dev URL where you can drag/move/edit and then push to prod.
          </div>
          <div style={{marginTop:4}}>
            <span style={{color:'var(--text-secondary)',fontWeight:700}}>To change a player's PFK score:</span> use the <span style={{color:'var(--accent-gold-text)',fontWeight:800}}>ROOKIE MODEL</span> tab.
          </div>
        </div>
        <RenderList src={list} allowEdit={false} autoTier={true} {...commonProps}/>
      </div>
      </>)}
    </div>
  );
}

// ============================================================
// DISPERSAL DRAFT — multi-user snake draft for orphaned dynasty teams
// Route: /dispersal (commish setup) | /dispersal/[id] (live draft)
// Storage: dispersal_drafts table in Supabase. Polling-based refresh
// (every 3s while live). Manager identity persisted to localStorage
// per draft (no account needed; passcode-based slot claim).
// ============================================================

const DISP_POOL_TYPES = { PLAYER:'player', PICK:'pick' };
const dispGenPasscode = () => String(Math.floor(1000 + Math.random()*9000));
const dispGenItemId = () => 'i_' + Date.now().toString(36) + Math.random().toString(36).slice(2,7);

// Pick-clock options shared between the pre-draft setup form and the live
// draft's mid-draft timer-change dropdown. Keep these in sync — the pre-draft
// Sleeper-mode <select> currently hardcodes the same six values; if either
// list changes, change both. Each option's `seconds` is what gets persisted
// to dispersal_drafts.timer_seconds (null for "off").
const DISP_TIMER_OPTIONS = [
  { value:'off', label:'None (no clock)', seconds:null },
  { value:'30m', label:'30 minutes',      seconds:30*60 },
  { value:'1h',  label:'1 hour',          seconds:3600 },
  { value:'2h',  label:'2 hours',         seconds:2*3600 },
  { value:'4h',  label:'4 hours',         seconds:4*3600 },
  { value:'8h',  label:'8 hours',         seconds:8*3600 },
];
const dispTimerKeyForSeconds = (s) => {
  if(s == null) return 'off';
  const found = DISP_TIMER_OPTIONS.find(o => o.seconds === s);
  return found ? found.value : 'off';
};

// ===== Overnight auto-pause helpers (Piece 3) ============================
// The commish stores their pause window as wall-clock HH:MM strings + an
// IANA timezone (auto-detected from their browser at save time). Every
// connected client computes "is now inside the window?" using that tz, so
// a draft configured "10pm-8am Eastern" looks identical in every viewer's
// browser regardless of where they live. Display formatting uses the
// VIEWER's local tz so a user in CT sees "Resumes at 7:00 AM CST".

// Convert "wall-clock HH:MM on date Y/M/D in tz Z" to a UTC instant in ms.
// Uses the formatToParts round-trip trick: assume UTC, see what tz wall it
// shows, shift by the diff. Two iterations to handle DST edge cases.
const _utcForLocalInTz = (year, month, day, hour, minute, tz) => {
  let guess = Date.UTC(year, month - 1, day, hour, minute, 0);
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
  for(let i=0; i<2; i++){
    const parts = fmt.formatToParts(new Date(guess));
    const get = (t) => parseInt(parts.find(p => p.type === t)?.value || '0', 10);
    let gh = get('hour'); if(gh === 24) gh = 0;
    const targetMs = Date.UTC(year, month - 1, day, hour, minute, 0);
    const tzWallMs = Date.UTC(get('year'), get('month') - 1, get('day'), gh, get('minute'), 0);
    const diff = targetMs - tzWallMs;
    if(diff === 0) return guess;
    guess += diff;
  }
  return guess;
};

// Returns true if `nowMs` falls inside [startLocal, endLocal) as wall-clock
// times in `tz`. Handles cross-midnight windows (end < start = next day).
const dispIsInAutoPauseWindow = (nowMs, startLocal, endLocal, tz) => {
  if(!startLocal || !endLocal || !tz) return false;
  if(startLocal === endLocal) return false; // zero-length window
  try {
    const fmt = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz, hour12: false, hour: '2-digit', minute: '2-digit',
    });
    const parts = fmt.formatToParts(new Date(nowMs));
    let h = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10);
    if(h === 24) h = 0;
    const m = parseInt(parts.find(p => p.type === 'minute')?.value || '0', 10);
    const cur = h*60 + m;
    const [sH, sM] = startLocal.split(':').map(n => parseInt(n, 10));
    const [eH, eM] = endLocal.split(':').map(n => parseInt(n, 10));
    const startMin = sH*60 + sM;
    const endMin = eH*60 + eM;
    if(startMin < endMin) return cur >= startMin && cur < endMin;
    return cur >= startMin || cur < endMin; // crosses midnight
  } catch(e){ return false; }
};

// Returns the UTC ms of the next occurrence of `endLocal` in `tz` after `nowMs`.
// Used to display "Resumes at 8:00 AM ET" in the viewer's own tz.
const dispNextAutoPauseEndUtc = (nowMs, endLocal, tz) => {
  if(!endLocal || !tz) return null;
  try {
    const [eH, eM] = endLocal.split(':').map(n => parseInt(n, 10));
    const dateFmt = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
    });
    const partsToday = dateFmt.formatToParts(new Date(nowMs));
    const ty = parseInt(partsToday.find(p => p.type === 'year').value, 10);
    const tm = parseInt(partsToday.find(p => p.type === 'month').value, 10);
    const td = parseInt(partsToday.find(p => p.type === 'day').value, 10);
    let candidate = _utcForLocalInTz(ty, tm, td, eH, eM, tz);
    if(candidate <= nowMs){
      const tomorrow = new Date(nowMs + 24*60*60*1000);
      const partsTom = dateFmt.formatToParts(tomorrow);
      const ty2 = parseInt(partsTom.find(p => p.type === 'year').value, 10);
      const tm2 = parseInt(partsTom.find(p => p.type === 'month').value, 10);
      const td2 = parseInt(partsTom.find(p => p.type === 'day').value, 10);
      candidate = _utcForLocalInTz(ty2, tm2, td2, eH, eM, tz);
    }
    return candidate;
  } catch(e){ return null; }
};

// Format a UTC ms instant as "8:00 AM EST" in the viewer's local tz.
const dispFormatLocalTime = (utcMs) => {
  if(utcMs == null) return '';
  try {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric', minute: '2-digit', hour12: true, timeZoneName: 'short',
    }).format(new Date(utcMs));
  } catch(e){ return ''; }
};

// Format a "HH:MM" wall-clock string as "8:00 AM" for display.
const dispFormatHHMM = (hhmm) => {
  if(!hhmm || !/^\d{1,2}:\d{2}$/.test(hhmm)) return hhmm || '';
  const [h, m] = hhmm.split(':').map(n => parseInt(n, 10));
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2,'0')} ${period}`;
};

// Snake-aware: given pickIdx + numTeams + pick_order, return the team slot whose turn it is.
const dispSlotForPick = (pickIdx, numTeams, snake, pickOrder) => {
  if(!numTeams || numTeams<1) return null;
  const round = Math.floor(pickIdx / numTeams);
  const posInRound = pickIdx % numTeams;
  const idx = (snake && round % 2 === 1) ? (numTeams - 1 - posInRound) : posInRound;
  return pickOrder[idx];
};

const dispFetch = async (id) => {
  if(!sb) return null;
  const { data, error } = await sb.from('dispersal_drafts').select('*').eq('id', id).maybeSingle();
  if(error){ console.error(error); return null; }
  return data;
};
// Optional fields we attempt to persist when newer schemas have the columns
// (roster_positions, sleeper_league_id). If the table doesn't have them yet,
// PostgREST rejects the entire insert/update. dispCreate / dispUpdate retry
// once with those fields stripped so older schemas keep working — the live
// TEAM tab falls back to its default lineup when the field is missing.
// `paused` / `paused_reason` / `paused_remaining_ms` were added 2026-05-04 for
// Piece 2 of the mid-draft timer feature (real Pause/Resume that preserves
// remaining time). The `auto_pause_*` columns were added 2026-05-05 for
// Piece 3 (daily overnight pause window). All optional so writes degrade
// gracefully if the ALTER TABLE hasn't been run yet — the draft just behaves
// as if those features are unsupported.
const _DISP_OPTIONAL_FIELDS = [
  'roster_positions','sleeper_league_id','user_state',
  'paused','paused_reason','paused_remaining_ms',
  'auto_pause_enabled','auto_pause_start_local','auto_pause_end_local','auto_pause_tz',
];
const _isMissingColumnErr = (err) => {
  if(!err) return false;
  const m = (err.message || '').toLowerCase();
  if(err.code === 'PGRST204') return true;
  if(m.includes('could not find') && m.includes('column')) return true;
  if(m.includes('column') && m.includes('does not exist')) return true;
  return _DISP_OPTIONAL_FIELDS.some(f => m.includes(f));
};
const _stripOptional = (obj) => {
  const out = {...obj};
  _DISP_OPTIONAL_FIELDS.forEach(f => { delete out[f]; });
  return out;
};
const dispCreate = async (payload) => {
  if(!sb) return { error:'Supabase not loaded' };
  let res = await sb.from('dispersal_drafts').insert(payload).select().single();
  if(res.error && _isMissingColumnErr(res.error) && _DISP_OPTIONAL_FIELDS.some(f => f in payload)) {
    res = await sb.from('dispersal_drafts').insert(_stripOptional(payload)).select().single();
  }
  return { data: res.data, error: res.error };
};
const dispUpdate = async (id, patch) => {
  if(!sb) return { error:'Supabase not loaded' };
  const stamped = {...patch, updated_at:new Date().toISOString()};
  let res = await sb.from('dispersal_drafts').update(stamped).eq('id', id);
  if(res.error && _isMissingColumnErr(res.error) && _DISP_OPTIONAL_FIELDS.some(f => f in stamped)) {
    res = await sb.from('dispersal_drafts').update(_stripOptional(stamped)).eq('id', id);
  }
  return { error: res.error };
};

// Sleeper API helpers — public API, no auth needed.
// players/nfl is ~10MB; cached in module scope so repeated imports during one
// session don't re-fetch.
let _sleeperPlayersCache = null;
const sleeperLeague = async (id) => {
  const [league, users, rosters] = await Promise.all([
    fetch(`https://api.sleeper.app/v1/league/${id}`).then(r=>{ if(!r.ok) throw new Error('Sleeper league not found'); return r.json(); }),
    fetch(`https://api.sleeper.app/v1/league/${id}/users`).then(r=>r.json()),
    fetch(`https://api.sleeper.app/v1/league/${id}/rosters`).then(r=>r.json()),
  ]);
  return { league, users, rosters };
};
const sleeperPlayers = async () => {
  if(_sleeperPlayersCache) return _sleeperPlayersCache;
  const data = await fetch('https://api.sleeper.app/v1/players/nfl').then(r=>r.json());
  _sleeperPlayersCache = data;
  return data;
};

// FantasyCalc dynasty values — used to silently sort the dispersal pool by trade value.
// Cached per-URL so different league formats coexist (e.g., a user opens an
// 8-team draft then a 12-team draft on the same page load — both get the
// right ranking without refetching the same format twice).
// Returns a map: { bySleeperId: {sleeperId -> value}, byName: {normName -> value} }
// opts: { numQbs, numTeams, ppr, tepLevel } — defaults preserve old behavior
// (12-team Superflex 1.0 PPR no-TEP) when no opts passed.
const _fcValuesMapCache = new Map();
const fetchFcValues = async (opts = {}) => {
  const numQbs = opts.numQbs ?? 2;
  const numTeams = opts.numTeams ?? 12;
  const ppr = opts.ppr ?? 1;
  const tepLevel = opts.tepLevel ?? 0;
  const url = `${FC}/values/current?isDynasty=true&numQbs=${numQbs}&numTeams=${numTeams}&ppr=${ppr}&tepLevel=${tepLevel}`;
  if(_fcValuesMapCache.has(url)) return _fcValuesMapCache.get(url);
  let result;
  try{
    const arr = await fetch(url).then(r=>r.json());
    const bySleeperId = {}, byName = {};
    (arr||[]).forEach(row => {
      const v = row?.value ?? 0;
      const sid = row?.player?.sleeperId;
      const nm = row?.player?.name;
      if(sid != null) bySleeperId[String(sid)] = v;
      if(nm) byName[normDraftName(nm)] = v;
    });
    result = { bySleeperId, byName };
  }catch(e){
    result = { bySleeperId:{}, byName:{} }; // fail silently — pool just falls back to insertion order
  }
  _fcValuesMapCache.set(url, result);
  return result;
};
// Derive FantasyCalc format params from a Sleeper league object. Returns
// undefined when lg is missing — caller should pass undefined to fetchFcValues
// to get the default 12-team Superflex 1.0 PPR ranking.
const fcFormatFromSleeperLeague = (lg) => {
  if(!lg) return undefined;
  const rp = Array.isArray(lg.roster_positions) ? lg.roster_positions : [];
  return {
    numQbs: rp.includes('SUPER_FLEX') ? 2 : 1,
    numTeams: lg.total_rosters || 12,
    ppr: lg.scoring_settings?.rec ?? 1,
    // FC ignores tepLevel; always pass 0. Dispersal Draft does NOT currently
    // apply PFK's TEP_MULT here — that's out of scope for this PR. Previous
    // code sent raw bonus_rec_te (potentially 0.75) which FC accepted but
    // ignored, so this change is functionally equivalent.
    tepLevel: 0,
  };
};

// ---------- Setup (commish creates a new draft) ----------
function DispersalSetup(){
  const [name,setName] = useState('');
  const [poolText,setPoolText] = useState('');
  const [picksText,setPicksText] = useState('');
  const [teamsText,setTeamsText] = useState('');
  const [snake,setSnake] = useState(true);
  const [timer,setTimer] = useState('off'); // 'off' | '1h' | '2h' | '4h' | '8h'
  const [autoPick,setAutoPick] = useState(false);
  // Overnight auto-pause window — Piece 3. Defaults sit there in case the
  // commish enables it; tz auto-detected from the browser at create time.
  const [autoPauseEnabled,setAutoPauseEnabled] = useState(false);
  const [autoPauseStart,setAutoPauseStart] = useState('22:00');
  const [autoPauseEnd,setAutoPauseEnd] = useState('08:00');
  const [creating,setCreating] = useState(false);
  const [createdDraft,setCreatedDraft] = useState(null);
  const [err,setErr] = useState('');
  // Setup mode: 'sleeper' = guided flow that pulls pool + managers from a Sleeper league;
  //              'custom'  = manual paste of pool + managers
  const [setupMode,setSetupMode] = useState('sleeper');
  // Sleeper-flow state
  const [sleeperOpen,setSleeperOpen] = useState(false); // legacy modal (custom-mode shortcut)
  const [sleeperId,setSleeperId] = useState('');
  const [sleeperLoading,setSleeperLoading] = useState(false);
  const [sleeperData,setSleeperData] = useState(null); // {users, rosters}
  const [sleeperSelected,setSleeperSelected] = useState(()=>new Set()); // roster_id values
  const [sleeperUsernames,setSleeperUsernames] = useState({}); // roster_id -> username override
  const [sleeperErr,setSleeperErr] = useState('');
  const [sleeperPlayersLoading,setSleeperPlayersLoading] = useState(false);
  const [sleeperBuilding,setSleeperBuilding] = useState(false);
  const [showTeamsHelp,setShowTeamsHelp] = useState(false);
  // Linked Sleeper user (from the toolbar's global link). When present we can
  // show a league dropdown instead of forcing the user to find + paste an ID.
  const [sleeperLinkedUser, setSleeperLinkedUser] = useSleeperUser();
  const [linkedLeagues,setLinkedLeagues] = useState(null); // null=not-fetched, []=empty, [...]=ready
  // Inline Sleeper link form state — lets users link RIGHT in the YOUR LEAGUES
  // section instead of being told to scroll up to the toolbar. Same end result
  // as the toolbar link (writes to the same global useSleeperUser hook).
  const [inlineLinkInput,setInlineLinkInput] = useState('');
  const [inlineLinkErr,setInlineLinkErr] = useState('');
  const [inlineLinkBusy,setInlineLinkBusy] = useState(false);
  const inlineLinkSubmit = async () => {
    setInlineLinkErr('');
    const q = (inlineLinkInput||'').trim();
    if(!q){ setInlineLinkErr('Enter your Sleeper username'); return; }
    setInlineLinkBusy(true);
    try{
      const u = await lookupResolveUser(q);
      setSleeperLinkedUser({ user_id: u.user_id, username: u.username, display_name: u.display_name || u.username, avatar: u.avatar || null });
      setInlineLinkInput('');
      // After link succeeds the inline form unmounts and the new YOUR LEAGUES
      // dropdown takes its place — content above the cursor shrinks, so iOS
      // ends up showing a stale scroll position that's BELOW the dropdown.
      // Scroll back to the top of the form so the dropdown is in view.
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }catch(e){
      setInlineLinkErr(e.message || 'Could not find that Sleeper user');
    }finally{
      setInlineLinkBusy(false);
    }
  };
  useEffect(() => {
    if(!sleeperLinkedUser?.user_id){
      setLinkedLeagues(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try{
        const yr = String(new Date().getFullYear());
        const lgs = await fetch(`https://api.sleeper.app/v1/user/${sleeperLinkedUser.user_id}/leagues/nfl/${yr}`, {cache:'no-store'}).then(r=>r.ok?r.json():[]).catch(()=>[]);
        if(cancelled) return;
        pfkRecordUserLeagues(sleeperLinkedUser, lgs); // Sharp Tracker pool ingestion (Phase 0a)
        // Show ALL league types (dispersals can be needed for redraft, keeper,
        // dynasty — let the user choose). Sort dynasty first since they're the
        // most common dispersal use case, then alpha within each group.
        const sorted = (lgs||[]).slice().sort((a,b) => {
          const at = (a?.settings?.type||0) === 2 ? 0 : 1;
          const bt = (b?.settings?.type||0) === 2 ? 0 : 1;
          if(at !== bt) return at - bt;
          return (a.name||'').localeCompare(b.name||'');
        });
        setLinkedLeagues(sorted);
      }catch(e){ setLinkedLeagues([]); }
    })();
    return () => { cancelled = true; };
  },[sleeperLinkedUser?.user_id]);
  const [showModeHelp,setShowModeHelp] = useState(false);
  // Top-of-page explainer for managers who've never run / been in a dispersal
  // draft. Closed by default — only the curious tap it.
  const [showWhatIsDispersal,setShowWhatIsDispersal] = useState(false);
  const [showPicksHelp,setShowPicksHelp] = useState(false);
  // Customize / add more picks expandable toggle. Closed by default so the
  // primary "Pull from Sleeper" action stays the headline.
  const [showCustomizePicks,setShowCustomizePicks] = useState(false);
  // Sleeper traded_picks cache for the "Pull actual ownership" button. Lazy
  // loaded on first click since most commishes will use the assumed-clean-slate
  // bulk-add and we don't want to fetch unnecessary data.
  const [sleeperTradedPicks,setSleeperTradedPicks] = useState(null);
  const [pullingSleeperPicks,setPullingSleeperPicks] = useState(false);
  const [pullPicksMsg,setPullPicksMsg] = useState('');
  // Share-pool state — for the 📸 SHARE POOL button (commish previews + shares
  // a PFK-branded image of the pool BEFORE creating the draft, to advertise it).
  const poolCardRef = useRef(null);
  const [poolShareBusy,setPoolShareBusy] = useState(false);
  const [previewData,setPreviewData] = useState(null);
  // Desktop share modal — appears after image generation on desktop (mobile uses native share sheet).
  // Holds {blob, filename}; null = modal hidden. Three actions: download / copy to clipboard / share to X.
  const [desktopShare,setDesktopShare] = useState(null);
  const [shareToast,setShareToast] = useState(''); // transient feedback in modal ("Copied!" etc.)
  // League-meta state — surfaced on the share card. Auto-detected from Sleeper in
  // sleeper mode; manually entered in custom mode. Buy-in is always manual.
  const [buyIn,setBuyIn] = useState('');
  const [leagueType,setLeagueType] = useState('Dynasty');         // Dynasty | Redraft
  const [leagueQB,setLeagueQB] = useState('Superflex');           // Superflex | 1QB
  const [leaguePPR,setLeaguePPR] = useState('1.0');               // 0 | 0.5 | 1.0
  const [leagueTEP,setLeagueTEP] = useState('0.5');               // 0 | 0.5 | 0.75 | 1.0
  const [leagueStarters,setLeagueStarters] = useState('');        // free text — "10", "11", etc.
  const [leaguePPC,setLeaguePPC] = useState('');                  // point-per-carry (rare); blank → hide
  const [leaguePPFD,setLeaguePPFD] = useState('');                // point-per-first-down; blank → hide

  const fetchSleeperLeague = async (overrideId) => {
    // Optional override lets the league dropdown trigger a fetch immediately
    // after setSleeperId without waiting for state to flush on the next render.
    setSleeperErr(''); setSleeperData(null);
    const id = (typeof overrideId === 'string' && overrideId.trim()) || sleeperId.trim();
    if(!id){ setSleeperErr('Enter a league ID'); return; }
    setSleeperLoading(true);
    try{
      const data = await sleeperLeague(id);
      setSleeperData(data);
      // Auto-select rosters with no owner (orphaned)
      const auto = new Set();
      (data.rosters||[]).forEach(r => { if(!r.owner_id) auto.add(r.roster_id); });
      setSleeperSelected(auto);
      // Pre-fill default usernames per roster (display_name or "Team N")
      const usersById = {}; (data.users||[]).forEach(u=>{ usersById[u.user_id]=u; });
      const defaults = {};
      (data.rosters||[]).forEach(r => {
        const u = r.owner_id ? usersById[r.owner_id] : null;
        defaults[r.roster_id] = u?.display_name || u?.username || `Team ${r.roster_id}`;
      });
      setSleeperUsernames(defaults);
      // Auto-populate the draft name from the Sleeper league name (only if commish hasn't typed one yet)
      if(data.league?.name && !name.trim()) setName(`${data.league.name} Dispersal`);
      // Auto-detect league settings for the share card (commish can override via UI).
      // IMPORTANT: dropdown <option> values use "1.0" / "0.5" string format. Sleeper
      // returns scoring values as JS numbers — JSON.parse turns 1.0 into 1 (integer)
      // since they're numerically equal. So String(1) === "1" wouldn't match the
      // "1.0" option → dropdown silently falls back to first option ("0 PPR").
      // normalizeNumStr forces integer values to render as "X.0" so they match.
      const normalizeNumStr = (n) => {
        const num = parseFloat(n);
        if(isNaN(num)) return '0';
        if(num === 0) return '0';
        if(Number.isInteger(num)) return num + '.0';
        return String(num);
      };
      const lg = data.league || {};
      const ss = lg.scoring_settings || {};
      const rp = lg.roster_positions || [];
      const settings = lg.settings || {};
      // type: 0=redraft, 1=keeper, 2=dynasty
      setLeagueType(settings.type === 2 ? 'Dynasty' : settings.type === 1 ? 'Keeper' : 'Redraft');
      setLeagueQB(rp.includes('SUPER_FLEX') ? 'Superflex' : '1QB');
      setLeaguePPR(normalizeNumStr(ss.rec ?? 0));
      setLeagueTEP(normalizeNumStr(ss.bonus_rec_te ?? 0));
      // Starters = roster slots not in BN/IR/TAXI
      const skip = new Set(['BN','IR','TAXI']);
      const starters = rp.filter(p => !skip.has(p)).length;
      if(starters > 0) setLeagueStarters(String(starters));
      // Point per carry: rush_att in scoring_settings (rare scoring rule)
      const ppc = ss.rush_att ?? 0;
      setLeaguePPC(ppc ? String(ppc) : '');
      // Point per first down: rec_fd + rush_fd in scoring_settings. Most PPFD
      // leagues set both to the same value; if they differ we surface the higher.
      const ppfd = Math.max(parseFloat(ss.rec_fd ?? 0) || 0, parseFloat(ss.rush_fd ?? 0) || 0);
      setLeaguePPFD(ppfd > 0 ? String(ppfd) : '');
    }catch(e){ setSleeperErr(e.message || 'Fetch failed'); }
    finally{ setSleeperLoading(false); }
  };

  // Sleeper-mode draft creation: builds pool + teams in one shot from selected rosters.
  const createFromSleeper = async () => {
    setErr('');
    if(!name.trim()){ setErr('Draft name required'); return; }
    if(!sleeperData){ setErr('Fetch a Sleeper league first'); return; }
    if(sleeperSelected.size < 2){ setErr('Select at least 2 teams'); return; }
    setSleeperBuilding(true);
    try{
      const players = await sleeperPlayers();
      const selectedRosters = (sleeperData.rosters||[]).filter(r=>sleeperSelected.has(r.roster_id));
      // Build pool from selected rosters' player IDs
      const poolItems = [];
      const seenPlayerIds = new Set();
      selectedRosters.forEach(r => {
        (r.players||[]).forEach(pid => {
          if(seenPlayerIds.has(pid)) return; seenPlayerIds.add(pid);
          const p = players[pid];
          if(!p) return;
          const fullName = p.full_name || `${p.first_name||''} ${p.last_name||''}`.trim() || pid;
          const pos = p.position || '';
          poolItems.push({ id:dispGenItemId(), type:DISP_POOL_TYPES.PLAYER, name: pos ? `${fullName} (${pos})` : fullName, sleeperId:String(pid), pos: (pos||'').toUpperCase() });
        });
      });
      // Append manual future picks
      const futurePicks = picksText.split('\n').map(s=>s.trim()).filter(Boolean);
      futurePicks.forEach(n => poolItems.push({ id:dispGenItemId(), type:DISP_POOL_TYPES.PICK, name:n }));
      if(!poolItems.length){ setErr('No players found in selected rosters'); setSleeperBuilding(false); return; }
      // Build managers list — preserve roster order from selectedRosters
      const teams = selectedRosters.map((r,i)=>({
        slot:i,
        username:(sleeperUsernames[r.roster_id] || `Team ${r.roster_id}`).trim(),
        passcode:'',
        joined:false,
      }));
      const order = teams.map((_,i)=>i);
      const timerSeconds = timer==='30m' ? 30*60 : timer==='1h' ? 3600 : timer==='2h' ? 2*3600 : timer==='4h' ? 4*3600 : timer==='8h' ? 8*3600 : null;
      setCreating(true);
      // Persist the linked Sleeper league's lineup config so the live draft's
      // TEAM tab can render an accurate starting-lineup view (QB/RB/WR/TE/
      // FLEX/SF/etc) that matches what the league actually uses. If the
      // dispersal_drafts table doesn't have this column the field is just
      // dropped on insert; the TEAM tab falls back to a sensible default.
      const rosterPositions = (sleeperData?.league?.roster_positions || []).filter(s => s !== 'BN' && s !== 'TAXI' && s !== 'IR');
      // Auto-fill blank draft name from the linked Sleeper league. Falls back
      // to a generic label if the league name isn't available for any reason.
      const finalName = name.trim() || (sleeperData?.league?.name ? `${sleeperData.league.name} Dispersal` : 'Dispersal Draft');
      const browserTz = (Intl.DateTimeFormat().resolvedOptions().timeZone) || 'UTC';
      const { data, error } = await dispCreate({
        name:finalName, snake, timer_seconds:timerSeconds, auto_pick:autoPick,
        pool:poolItems, teams, pick_order:order, picks:[],
        current_pick_idx:0, status:'ready',
        roster_positions: rosterPositions.length ? rosterPositions : null,
        sleeper_league_id: sleeperData?.league?.league_id || null,
        auto_pause_enabled: !!(timerSeconds && autoPauseEnabled),
        auto_pause_start_local: (timerSeconds && autoPauseEnabled) ? autoPauseStart : null,
        auto_pause_end_local: (timerSeconds && autoPauseEnabled) ? autoPauseEnd : null,
        auto_pause_tz: (timerSeconds && autoPauseEnabled) ? browserTz : null,
      });
      setCreating(false);
      if(error){ setErr(error.message || 'Create failed'); return; }
      try{ localStorage.setItem('pfk_disp_creator_'+data.id, '1'); }catch(e){}
      window.location.href = '/dispersal/' + data.id;
    }catch(e){ setErr(e.message || 'Build failed'); }
    finally{ setSleeperBuilding(false); }
  };

  const importSelected = async () => {
    if(!sleeperData || sleeperSelected.size===0){ setSleeperErr('Pick at least one team'); return; }
    setSleeperPlayersLoading(true);
    try{
      const players = await sleeperPlayers();
      // Pull player IDs from selected rosters
      const playerIds = new Set();
      (sleeperData.rosters||[]).forEach(r => {
        if(!sleeperSelected.has(r.roster_id)) return;
        (r.players||[]).forEach(pid => playerIds.add(pid));
      });
      // Resolve to "First Last (POS)" — sortable, readable
      const lines = [];
      playerIds.forEach(pid => {
        const p = players[pid];
        if(!p) return;
        const fullName = p.full_name || `${p.first_name||''} ${p.last_name||''}`.trim() || pid;
        const pos = p.position || '';
        lines.push(pos ? `${fullName} (${pos})` : fullName);
      });
      lines.sort();
      // Append (de-duped) to existing pool textarea
      const existing = new Set(poolText.split('\n').map(s=>s.trim()).filter(Boolean));
      const merged = [...existing, ...lines.filter(l=>!existing.has(l))];
      setPoolText(merged.join('\n'));
      setSleeperOpen(false);
    }catch(e){ setSleeperErr(e.message || 'Player lookup failed'); }
    finally{ setSleeperPlayersLoading(false); }
  };

  const create = async () => {
    setErr('');
    if(!name.trim()){ setErr('Name required'); return; }
    const players = poolText.split('\n').map(s=>s.trim()).filter(Boolean);
    const futurePicks = picksText.split('\n').map(s=>s.trim()).filter(Boolean);
    const usernames = teamsText.split('\n').map(s=>s.trim()).filter(Boolean);
    if(players.length === 0 && futurePicks.length === 0){ setErr('Pool is empty — paste players and/or future picks'); return; }
    if(usernames.length < 2){ setErr('Need at least 2 teams'); return; }
    const pool = [
      ...players.map(n => {
        // Parse "(POS)" suffix off the player name (e.g. "Justin Jefferson (WR)")
        // so position-tab filters in the live draft work.
        const m = (n||'').match(/\(([A-Za-z]+)\)\s*$/);
        const pos = m ? m[1].toUpperCase() : '';
        return { id:dispGenItemId(), type:DISP_POOL_TYPES.PLAYER, name:n, pos };
      }),
      ...futurePicks.map(n => ({ id:dispGenItemId(), type:DISP_POOL_TYPES.PICK, name:n })),
    ];
    // Pick order = order in the textarea (commish controls this — see Randomize button).
    const order = usernames.map((_,i)=>i);
    const teams = usernames.map((u,i)=>({ slot:i, username:u, passcode:'', joined:false }));
    const timerSeconds = timer==='30m' ? 30*60 : timer==='1h' ? 3600 : timer==='2h' ? 2*3600 : timer==='4h' ? 4*3600 : timer==='8h' ? 8*3600 : null;
    setCreating(true);
    // Custom mode has no linked Sleeper league, so the blank-name fallback
    // is just a generic label — keeps creation from failing on an empty name.
    const finalName = name.trim() || 'Dispersal Draft';
    const { data, error } = await dispCreate({
      name:finalName, snake, timer_seconds:timerSeconds,
      pool, teams, pick_order:order, picks:[],
      current_pick_idx:0, status:'ready',
    });
    setCreating(false);
    if(error){ setErr(error.message || 'Create failed'); return; }
    // Mark creator as commish in localStorage and drop them into the draft room
    try{ localStorage.setItem('pfk_disp_creator_'+data.id, '1'); }catch(e){}
    window.location.href = '/dispersal/' + data.id;
  };

  // (success screen removed — commish is redirected straight to /dispersal/[id] after create)

  // Build the pool data needed for the share-card from current setup state.
  // Returns { players, picks, teamCount, draftName, snake } or null if there's nothing to render.
  // Used by the 📸 SHARE POOL preview/share flow on this setup page.
  const buildPreviewPool = async () => {
    const draftName = name.trim() || 'Dispersal Draft';
    const pickLines = picksText.split('\n').map(s=>s.trim()).filter(Boolean);
    let players = [];
    // managersNeeded = how many of the selected rosters need a new manager.
    // Reflects the commish's selection regardless of whether each roster has
    // an owner_id on Sleeper — once they're picked for the dispersal pool,
    // they need a new manager by definition (e.g. league re-form, mass disperse).
    let managersNeeded = 0;
    if(setupMode === 'sleeper'){
      if(!sleeperData || sleeperSelected.size === 0) return null;
      const playersDb = await sleeperPlayers();
      const selectedRosters = (sleeperData.rosters||[]).filter(r=>sleeperSelected.has(r.roster_id));
      const seen = new Set();
      selectedRosters.forEach(r => {
        (r.players||[]).forEach(pid => {
          if(seen.has(pid)) return; seen.add(pid);
          const p = playersDb[pid];
          if(!p) return;
          const fullName = p.full_name || `${p.first_name||''} ${p.last_name||''}`.trim() || pid;
          const pos = (p.position || '').toUpperCase();
          players.push({ id:'p_'+pid, name: pos ? `${fullName} (${pos})` : fullName, sleeperId:String(pid), pos });
        });
      });
      managersNeeded = selectedRosters.length;
    } else {
      const lines = poolText.split('\n').map(s=>s.trim()).filter(Boolean);
      players = lines.map((n,i) => {
        const m = n.match(/\(([^)]+)\)\s*$/);
        const pos = m ? m[1].toUpperCase() : '';
        return { id:'p_c_'+i, name:n, pos };
      });
      managersNeeded = teamsText.split('\n').map(s=>s.trim()).filter(Boolean).length;
    }
    if(players.length === 0 && pickLines.length === 0) return null;
    // Sort players by FantasyCalc value within position. When a Sleeper league
    // is linked, ask FC for values calibrated to that league's actual format
    // (numQbs, numTeams, ppr, tepLevel) instead of the 12-team Superflex
    // 1.0 PPR default — otherwise 8/14/16-team or 1-QB leagues get rankings
    // skewed toward the wrong league shape.
    const fc = await fetchFcValues(fcFormatFromSleeperLeague(sleeperData?.league));
    const valOf = (it) => {
      if(it.sleeperId && fc.bySleeperId[it.sleeperId] != null) return fc.bySleeperId[it.sleeperId];
      const stripped = (it.name || '').replace(/\s*\([^)]*\)\s*$/, '');
      const k = normDraftName(stripped);
      return fc.byName[k] != null ? fc.byName[k] : 0;
    };
    const POS_ORDER = ['QB','RB','WR','TE','K','DEF'];
    const playersByPos = {};
    POS_ORDER.forEach(p => { playersByPos[p] = []; });
    playersByPos.OTHER = [];
    players.forEach(it => {
      const bucket = POS_ORDER.includes(it.pos) ? it.pos : 'OTHER';
      playersByPos[bucket].push(it);
    });
    Object.keys(playersByPos).forEach(p => playersByPos[p].sort((a,b)=>valOf(b)-valOf(a)));
    // Sort picks by year, then round
    const picks = pickLines.map((n,i) => ({ id:'pk_'+i, name:n }));
    const parsePick = (n) => {
      const yM = (n||'').match(/(\d{4})/);
      // Accept both formats: round-only "2026 1st" and slot-baked "2026 1.02".
      // Without the slot branch, slot-form picks all collapse to round 9 and
      // sort incorrectly inside their year on the share card.
      const slotM = (n||'').match(/\b(\d+)\.(\d+)\b/);
      const rM = (n||'').match(/(\d)(st|nd|rd|th)/i);
      const round = slotM ? +slotM[1] : (rM ? +rM[1] : 9);
      const slot = slotM ? +slotM[2] : 99;
      return { year: yM?+yM[1]:9999, round, slot };
    };
    picks.sort((a,b)=>{
      const pa = parsePick(a.name), pb = parsePick(b.name);
      return pa.year - pb.year || pa.round - pb.round || pa.slot - pb.slot;
    });
    // Build the settings string for the share card. Format example:
    //   "DYNASTY · SUPERFLEX · 1.0 PPR · 0.5 TEP · START 10"
    // PPC is appended only when present (rare scoring rule).
    const settingsParts = [
      (leagueType||'').toUpperCase(),
      (leagueQB||'').toUpperCase(),
      `${leaguePPR||0} PPR`,
      `${leagueTEP||0} TEP`,
    ];
    if(leagueStarters && leagueStarters.trim()) settingsParts.push(`START ${leagueStarters.trim()}`);
    if(leaguePPC && parseFloat(leaguePPC) > 0) settingsParts.push(`${leaguePPC} PPC`);
    if(leaguePPFD && parseFloat(leaguePPFD) > 0) settingsParts.push(`${leaguePPFD} PPFD`);
    const settingsLine = settingsParts.filter(Boolean).join(' · ');
    return {
      draftName, players, playersByPos, picks, managersNeeded, posOrder: POS_ORDER,
      buyIn: (buyIn||'').trim(), settingsLine,
    };
  };

  const stripPosFromName = (n) => (n || '').replace(/\s*\([^)]*\)\s*$/, '').trim();

  // 📸 SHARE POOL — builds preview data, renders the off-screen card, then captures
  // and saves/shares via Web Share API on mobile / download fallback on desktop.
  const sharePoolImage = async () => {
    if(!window.html2canvas){ alert('Screenshot library not loaded yet — refresh and try again.'); return; }
    setPoolShareBusy(true);
    try{
      const data = await buildPreviewPool();
      if(!data){
        alert(setupMode==='sleeper'
          ? 'Fetch a Sleeper league and select at least one team first.'
          : 'Add players and/or future picks to the pool first.');
        return;
      }
      setPreviewData(data);
      // Wait two animation frames so React commits the offscreen card before we capture.
      await new Promise(r => requestAnimationFrame(()=>requestAnimationFrame(r)));
      const el = poolCardRef.current;
      if(!el){ alert('Preview card not ready — try again.'); return; }
      const canvas = await window.html2canvas(el, { backgroundColor:'#080808', scale:2, useCORS:true });
      const filename = `${data.draftName.replace(/[^a-z0-9]/gi,'_')}_pool.png`;
      const blob = await new Promise(res => canvas.toBlob(res, 'image/png'));
      // Mobile: native share sheet (Photos, Messages, X, Discord, etc.) — works perfectly.
      // Desktop: pop a small modal with three options (download / copy / share to X).
      const isMobile = /iPad|iPhone|iPod|Android/i.test(navigator.userAgent);
      if(isMobile && blob && navigator.canShare){
        const file = new File([blob], filename, { type:'image/png' });
        if(navigator.canShare({ files:[file] })){
          try{
            await navigator.share({ files:[file], title:`${data.draftName} — pool` });
            return;
          }catch(e){ if(e && e.name==='AbortError') return; }
        }
      }
      // Mobile fallback (no Web Share API): download via anchor.
      if(isMobile){
        const url = blob ? URL.createObjectURL(blob) : canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.download = filename;
        link.href = url;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        if(blob) setTimeout(()=>URL.revokeObjectURL(url), 1000);
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        if(isIOS && !navigator.canShare){
          window.open(canvas.toDataURL('image/png'), '_blank');
        }
        return;
      }
      // Desktop: stash blob + filename and show the share modal so user picks an action.
      if(blob){
        setShareToast('');
        setDesktopShare({ blob, filename, draftName: data.draftName });
      }
    }catch(e){ alert('Share pool failed: ' + (e.message||e)); }
    finally{ setPoolShareBusy(false); }
  };

  const inputStyle = {width:'100%',padding:'10px 12px',background:'var(--bg-elevated)',border:'1px solid var(--border-strong)',borderRadius:6,color:'var(--text-bright)',fontSize:14,fontFamily:'inherit'};
  const labelStyle = {fontSize:11,color:'var(--text-secondary)',fontWeight:800,letterSpacing:1.5,marginBottom:6,display:'block'};
  const selectStyle = {flex:'1 1 110px',padding:'8px 10px',background:'var(--bg-elevated)',border:'1px solid var(--border-strong)',borderRadius:6,color:'var(--accent-gold-text)',fontSize:13,fontWeight:700,fontFamily:'inherit',cursor:'pointer'};

  // Shared "league info on share image" block — shown in both Sleeper and Custom modes.
  // Defined as a JSX value (NOT a child component) so React reconciles it instead of
  // remounting on every parent render — that remount was unmounting the buy-in input
  // mid-keystroke and stealing focus after every character.
  const leagueInfoInputs = (
    <div style={{background:'var(--bg-elevated)',border:'1px solid var(--border)',borderRadius:10,padding:'14px 16px',display:'flex',flexDirection:'column',gap:12}}>
      <div style={{fontSize:11,color:'var(--accent-gold-text)',fontWeight:800,letterSpacing:1.5}}>📸 SHOWN ON THE DISPERSAL ASSET SCREENSHOT</div>
      <div>
        <label style={{...labelStyle,marginBottom:5}}>LEAGUE BUY-IN</label>
        <input value={buyIn} onChange={e=>setBuyIn(e.target.value)} placeholder="enter any $amount or free text" style={inputStyle}/>
      </div>
      <div>
        <label style={{...labelStyle,marginBottom:5}}>LEAGUE SETTINGS{setupMode==='sleeper' && sleeperData && <span style={{color:'var(--success)',fontWeight:700,marginLeft:8,letterSpacing:0.5,textTransform:'none'}}>· auto-filled, edit if needed</span>}</label>
        <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
          <select value={leagueType} onChange={e=>setLeagueType(e.target.value)} style={selectStyle}>
            <option value="Dynasty">Dynasty</option>
            <option value="Keeper">Keeper</option>
            <option value="Redraft">Redraft</option>
          </select>
          <select value={leagueQB} onChange={e=>setLeagueQB(e.target.value)} style={selectStyle}>
            <option value="Superflex">Superflex</option>
            <option value="1QB">1QB</option>
          </select>
          <select value={leaguePPR} onChange={e=>setLeaguePPR(e.target.value)} style={selectStyle}>
            <option value="0">0 PPR</option>
            <option value="0.5">0.5 PPR</option>
            <option value="1.0">1.0 PPR</option>
          </select>
          <select value={leagueTEP} onChange={e=>setLeagueTEP(e.target.value)} style={selectStyle}>
            <option value="0">0 TEP</option>
            <option value="0.5">0.5 TEP</option>
            <option value="0.75">0.75 TEP</option>
            <option value="1.0">1.0 TEP</option>
            <option value="1.5">1.5 TEP</option>
          </select>
        </div>
        <div style={{display:'flex',flexWrap:'wrap',gap:8,marginTop:8}}>
          <div style={{flex:'1 1 130px'}}>
            <div style={{fontSize:10,color:'var(--text-secondary)',fontWeight:800,letterSpacing:1.5,marginBottom:4}}>STARTING LINEUP SIZE</div>
            <input value={leagueStarters} onChange={e=>setLeagueStarters(e.target.value)} placeholder="e.g. 10" style={inputStyle}/>
          </div>
          <div style={{flex:'1 1 130px'}}>
            <div style={{fontSize:10,color:'var(--text-secondary)',fontWeight:800,letterSpacing:1.5,marginBottom:4}}>POINT PER CARRY</div>
            <input value={leaguePPC} onChange={e=>setLeaguePPC(e.target.value)} placeholder="leave blank if none" style={inputStyle}/>
          </div>
          <div style={{flex:'1 1 130px'}}>
            <div style={{fontSize:10,color:'var(--text-secondary)',fontWeight:800,letterSpacing:1.5,marginBottom:4}}>POINT PER FIRST DOWN</div>
            <input value={leaguePPFD} onChange={e=>setLeaguePPFD(e.target.value)} placeholder="leave blank if none" style={inputStyle}/>
          </div>
        </div>
      </div>
    </div>
  );

  // Year-pick dropdowns above the future picks textarea. In Sleeper mode the
  // options include "via {username}" for every Sleeper user; in Custom mode the
  // option is "via" (no username) and commish types it after.
  const PICK_YEARS = [2026, 2027, 2028, 2029];
  const PICK_ROUNDS = [['1st','1st'],['2nd','2nd'],['3rd','3rd'],['4th','4th']];
  const usernamesForDropdowns = (() => {
    if(setupMode==='sleeper'){
      return (sleeperData?.users||[]).map(u => (u.display_name || u.username || '').trim()).filter(Boolean);
    }
    return teamsText.split('\n').map(s=>s.trim()).filter(Boolean);
  })();
  const addPickFromDropdown = (line) => {
    setPicksText(prev => (prev ? prev.replace(/\n+$/,'') + '\n' : '') + line);
  };
  // Pull ACTUAL pick ownership from Sleeper for the selected dispersal rosters.
  // For each selected roster, walks traded_picks to figure out:
  //   - which of their original picks they still own (not traded away)
  //   - which picks they've ACQUIRED from other rosters
  // Each line goes into picksText with a "via {original-owner-username}" tag
  // so the commish can see whose pick is which once they're in the pool.
  // For years where Sleeper already has a draft order set (e.g. 2026 startup),
  // we render the actual slot — "2026 1.02" — by fetching slot_to_roster_id
  // from /draft/{id}. For years with no draft order set yet, we fall back to
  // the round-only "2026 1st via Manager" form so users can still see whose
  // pick it is.
  const applyPullSleeperPicks = async (yearsOverride) => {
    // yearsOverride lets the per-year pills (+ 2027 picks, + 2028 picks, etc.)
    // pull a single year without affecting the all-years default used by the
    // primary button.
    const yearsSource = yearsOverride || new Set(PICK_YEARS);
    if(setupMode !== 'sleeper' || !sleeperData || sleeperSelected.size === 0 || yearsSource.size === 0) return;
    setPullingSleeperPicks(true);
    setPullPicksMsg('');
    try{
      let tp = sleeperTradedPicks;
      if(!tp){
        tp = await fetch(`https://api.sleeper.app/v1/league/${sleeperId.trim()}/traded_picks`, {cache:'no-store'}).then(r=>r.ok?r.json():[]).catch(()=>[]);
        setSleeperTradedPicks(tp || []);
      }
      const usersById = {};
      (sleeperData.users||[]).forEach(u => { usersById[u.user_id] = u; });
      const usernameForRoster = (rid) => {
        if(sleeperUsernames[rid]) return sleeperUsernames[rid];
        const r = (sleeperData.rosters||[]).find(x => x.roster_id === rid);
        const u = r?.owner_id ? usersById[r.owner_id] : null;
        return u?.display_name || u?.username || `Team ${rid}`;
      };
      const roundLabel = (n) => n === 1 ? '1st' : n === 2 ? '2nd' : n === 3 ? '3rd' : `${n}th`;
      const years = Array.from(yearsSource).sort();
      const rounds = [1, 2, 3, 4];
      // Fetch upcoming-draft slot maps once per pull. Years with no draft order
      // set in Sleeper return null and silently fall back to "via Manager".
      const slotMaps = await fetchSleeperDraftSlots(sleeperId.trim(), years);
      let slottedCount = 0;
      const lines = [];
      const buildLine = (originalRid, y, round) => {
        const slotInfo = slotMaps.get(String(y))?.get(originalRid);
        if(slotInfo){
          slottedCount++;
          return formatPickSlot(y, round, slotInfo);
        }
        return `${y} ${roundLabel(round)} via ${usernameForRoster(originalRid)}`;
      };
      sleeperSelected.forEach(rid => {
        years.forEach(y => {
          rounds.forEach(round => {
            // Original pick: include unless this roster traded it away
            const tradedAway = (tp||[]).find(t =>
              String(t.season) === String(y) && t.round === round &&
              t.roster_id === rid && t.owner_id !== rid
            );
            if(!tradedAway){
              lines.push(buildLine(rid, y, round));
            }
            // Acquired picks: ones this roster currently owns that originally
            // belonged to a different roster
            const acquired = (tp||[]).filter(t =>
              String(t.season) === String(y) && t.round === round &&
              t.owner_id === rid && t.roster_id !== rid
            );
            acquired.forEach(t => {
              lines.push(buildLine(t.roster_id, y, round));
            });
          });
        });
      });
      if(lines.length === 0){
        setPullPicksMsg('No picks found for the selected managers in those years.');
      } else {
        setPicksText(prev => (prev ? prev.replace(/\n+$/,'') + '\n' : '') + lines.join('\n'));
        const slotNote = slottedCount > 0
          ? ` (${slottedCount} with Sleeper draft slot)`
          : '';
        setPullPicksMsg(`Added ${lines.length} pick${lines.length===1?'':'s'} reflecting actual Sleeper ownership${slotNote}.`);
      }
    }catch(e){
      setPullPicksMsg('Could not pull picks — try again.');
    }finally{
      setPullingSleeperPicks(false);
    }
  };
  // PickYearDropdowns — must be defined BEFORE bulkPicksControls because
  // bulkPicksControls now references <PickYearDropdowns/> inside its JSX.
  // JSX evaluates immediately at const initialization, so any reference
  // to a not-yet-defined const throws a temporal-dead-zone ReferenceError
  // (which manifested as a blank screen when the customize section toggled).
  const PickYearDropdowns = () => (
    <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:8}}>
      {PICK_YEARS.map(y => (
        <select key={y} value="" onChange={e=>{ if(e.target.value){ addPickFromDropdown(e.target.value); e.target.value=''; } }}
          style={{flex:'1 1 130px',padding:'7px 9px',background:'var(--bg-elevated)',border:'1px solid var(--border-strong)',borderRadius:6,color:'var(--accent-gold-text)',fontSize:12,fontWeight:700,cursor:'pointer'}}>
          <option value="">+ Add {y} pick</option>
          {setupMode==='sleeper'
            ? PICK_ROUNDS.flatMap(([rk,rl]) => [
                <option key={`${rk}-generic`} value={`${y} ${rl}`}>{y} {rl}</option>,
                ...usernamesForDropdowns.map(u => (
                  <option key={`${rk}-${u}`} value={`${y} ${rl} via ${u}`}>{rl} via {u}</option>
                ))
              ])
            : PICK_ROUNDS.flatMap(([rk,rl]) => [
                <option key={`${rk}-generic`} value={`${y} ${rl}`}>{y} {rl}</option>,
                <option key={rk} value={`${y} ${rl} via `}>{rl} via</option>
              ])
          }
        </select>
      ))}
    </div>
  );
  // Bulk-picks controls UI — defined as a JSX value (not a Component) so React
  // reconciles in place. (Same lesson as the leagueInfoInputs focus-loss fix.)
  //
  // Layout: shared "years to apply" selector at the top, then three options
  // stacked vertically:
  //   1. ✨ Pull selected teams' picks (Sleeper-only, primary recommendation)
  //   2. 🪄 All teams have all future picks (clean-slate fallback)
  //   3. + Add picks one by one (dropdowns below the textarea — separate JSX)
  // All three flow into the same picksText textarea, so commish can mix-and-match
  // and edit freely.
  const isSleeperMode = setupMode === 'sleeper';
  // Current rookie-draft year — drives the primary button's default. When the
  // calendar rolls into 2027 the button auto-says "2027 ROOKIE PICKS" with no
  // code change. The future-year chip row is also derived from this, showing
  // the next 3 seasons.
  const currentDraftYear = new Date().getFullYear();
  const futureYearsForChips = [currentDraftYear + 1, currentDraftYear + 2, currentDraftYear + 3];
  const currentYearOnlySet = new Set([currentDraftYear]);
  const bulkPicksControls = (
    <div style={{display:'flex',flexDirection:'column',gap:8,marginBottom:8}}>
      {/* PRIMARY — Pull selected teams' CURRENT-YEAR rookie picks only.
          Reflects the most common dispersal-draft norm (newcomer keeps their
          own future picks; only this year's rookie picks come over with the
          orphan team). Future years are additive via the chip row below. */}
      {isSleeperMode && (
        <div>
          <button type="button" onClick={() => applyPullSleeperPicks(currentYearOnlySet)}
            disabled={!sleeperData || sleeperSelected.size===0 || pullingSleeperPicks}
            style={{width:'100%',padding:'10px 14px',background:(!sleeperData||sleeperSelected.size===0||pullingSleeperPicks)?'var(--bg-pill)':'var(--accent-gold)',border:'none',borderRadius:6,color:(!sleeperData||sleeperSelected.size===0||pullingSleeperPicks)?'var(--border-outline)':'#000',fontWeight:900,cursor:(!sleeperData||sleeperSelected.size===0||pullingSleeperPicks)?'default':'pointer',fontSize:13,letterSpacing:1}}>
            {pullingSleeperPicks
              ? 'PULLING FROM SLEEPER…'
              : !sleeperData
              ? `✨ PULL SELECTED TEAMS' ${currentDraftYear} ROOKIE PICKS · fetch a league first`
              : sleeperSelected.size===0
              ? `✨ PULL SELECTED TEAMS' ${currentDraftYear} ROOKIE PICKS · select managers first`
              : `✨ PULL SELECTED TEAMS' ${currentDraftYear} ROOKIE PICKS`}
          </button>
          <div style={{fontSize:11,color:'var(--text-secondary)',marginTop:5,lineHeight:1.5}}>Click the button above to add any {currentDraftYear} picks currently on selected teams rosters; to show the correct placement of the {currentDraftYear} picks (e.g. {currentDraftYear} 1.03) make sure the draft order is set correctly in your league.</div>
          {/* Secondary additive chips — for leagues that disperse future-year
              picks too. Each chip APPENDS to whatever's already in the textarea
              so commish can stack them as needed. */}
          {sleeperData && sleeperSelected.size > 0 && (
            <div style={{marginTop:10,padding:'8px 10px',background:'var(--bg-elevated)',border:'1px solid var(--border)',borderRadius:6}}>
              <div style={{fontSize:11,color:'var(--text-secondary)',fontWeight:700,marginBottom:6,letterSpacing:0.5}}>Need future picks too? <span style={{color:'var(--text-secondary)',fontWeight:500}}>(some leagues disperse all future picks; most don't)</span></div>
              <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                {futureYearsForChips.map(y => (
                  <button key={y} type="button" onClick={() => applyPullSleeperPicks(new Set([y]))} disabled={pullingSleeperPicks}
                    style={{padding:'6px 12px',background:'transparent',border:'1px solid rgba(var(--accent-gold-rgb),0.4)',borderRadius:14,color:'var(--accent-gold-text)',fontWeight:800,fontSize:12,cursor:pullingSleeperPicks?'wait':'pointer',letterSpacing:0.3}}>
                    + {y} picks
                  </button>
                ))}
              </div>
            </div>
          )}
          {pullPicksMsg && <div style={{fontSize:11,color:pullPicksMsg.startsWith('Added')?'var(--success)':'var(--warning)',marginTop:5,fontWeight:700}}>{pullPicksMsg}</div>}
        </div>
      )}

      {/* SECONDARY — Customize / add more picks. Closed by default. Holds the
          year-by-year dropdowns AND the clean-slate "all teams have own picks"
          bulk button so the primary action stays uncluttered. */}
      <button type="button" onClick={()=>setShowCustomizePicks(v=>!v)}
        style={{padding:'8px 12px',background:'transparent',border:'1px dashed rgba(var(--accent-gold-rgb),0.4)',borderRadius:6,color:'var(--accent-gold-text)',fontWeight:700,cursor:'pointer',fontSize:12,letterSpacing:0.5,textAlign:'left',display:'flex',alignItems:'center',justifyContent:'space-between',gap:8}}>
        <span>⚙️ Customize / add more picks</span>
        <span style={{fontSize:14}}>{showCustomizePicks ? '▲' : '▼'}</span>
      </button>
      {showCustomizePicks && (
        <div style={{background:'var(--bg-elevated)',border:'1px solid var(--border)',borderRadius:8,padding:'12px 14px',display:'flex',flexDirection:'column',gap:14}}>
          {/* Year dropdowns — add picks one at a time, optionally tagged "via @user". */}
          <div>
            <div style={{fontSize:10,color:'var(--text-secondary)',fontWeight:800,letterSpacing:1.5,marginBottom:6}}>+ ADD ONE AT A TIME</div>
            <PickYearDropdowns/>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div style={{maxWidth:760,margin:'24px auto',padding:'20px',color:'var(--text-eee)'}}>
      <div style={{display:'flex',alignItems:'center',gap:14,marginBottom:18,flexWrap:'wrap'}}>
        <div style={{fontSize:24,fontWeight:900,color:'var(--accent-gold-text)',letterSpacing:2}}>🎲 DISPERSAL DRAFT</div>
        <button type="button" onClick={()=>setShowWhatIsDispersal(v=>!v)}
          style={{padding:'4px 10px',background:'transparent',border:'1px solid rgba(var(--accent-gold-rgb),0.4)',borderRadius:14,color:'var(--accent-gold-text)',cursor:'pointer',fontSize:11,fontWeight:800,letterSpacing:0.3}}>
          ⓘ {showWhatIsDispersal ? 'hide' : 'what is a dispersal draft?'}
        </button>
        <HowItWorksButton title="Dispersal Draft">
          <p><strong>What it does:</strong> Pool players (and optionally picks) from a Sleeper league or a custom list, share a single link with your league, and run a live snake draft on phones.</p>
          <ol style={{paddingLeft:20,marginTop:10,display:'flex',flexDirection:'column',gap:8}}>
            <li><strong>Commish creates the draft</strong> — auto-pulls rosters from a Sleeper league (paste the league ID or pick from your linked leagues), or pastes a custom pool. Sets timer + auto-pick options.</li>
            <li><strong>Share the link</strong> — every new manager opens it and claims their team slot.</li>
            <li><strong>Live picks happen on phones</strong> — snake order, real-time updates, queue your favorites, auto-pick if you're AFK, mid-draft pause.</li>
            <li><strong>Overnight pause window</strong> — optional setting that auto-pauses every night and resumes in the morning so the clock doesn't burn while everyone's asleep.</li>
            <li><strong>Share-pool image</strong> — generate a PFK-branded card of the final pool to advertise the draft before it starts.</li>
          </ol>
          <p style={{marginTop:12}}>Built for dispersal redrafts (when leagues redistribute orphan teams), but works for any startup snake too.</p>
        </HowItWorksButton>
      </div>
      {showWhatIsDispersal && (
        <div style={{background:'var(--accent-gold-bg)',border:'1px solid rgba(var(--accent-gold-rgb),0.33)',borderRadius:8,padding:'14px 16px',marginBottom:14,fontSize:13,color:'var(--text-ddd)',lineHeight:1.65}}>
          <div style={{fontWeight:900,color:'var(--accent-gold-text)',letterSpacing:1,marginBottom:8}}>WHAT IS A DISPERSAL DRAFT?</div>
          <div style={{marginBottom:8}}>A dispersal draft is how a fantasy league re-stocks one or more teams that have been abandoned by their previous managers. The orphaned team's players (and often their draft picks) go into a shared pool, and the new managers draft from it to build their team.</div>
          <div style={{marginBottom:8}}>What goes in the pool is up to each league. Most commissioners disperse the orphan teams' <strong style={{color:'var(--text-bright)'}}>current-year rookie picks</strong> only — the new manager keeps their own future picks since they're in for the long haul. Some leagues include <strong style={{color:'var(--text-bright)'}}>all future picks</strong> too. PFK's setup form lets you do either with a single tap.</div>
          <div style={{color:'var(--text-secondary)',fontSize:12}}>Once the pool is built, you share a link, each new manager claims their team, and the snake draft runs from your phone or browser.</div>
        </div>
      )}
      <div style={{fontSize:13,color:'var(--text-secondary)',marginBottom:18,lineHeight:1.6}}>Set up a snake dispersal draft. Pool the rosters and picks of the teams that are re-drafting, share the link with the new managers, and they each draft on their own device.</div>

      {/* Mode selector */}
      <div style={{marginBottom:18}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8,gap:8,flexWrap:'wrap'}}>
          <div style={{fontSize:11,color:'var(--text-secondary)',fontWeight:800,letterSpacing:1.5}}>HOW DO YOU WANT TO BUILD THE POOL?</div>
          <button type="button" onClick={()=>setShowModeHelp(v=>!v)} style={{padding:'2px 8px',background:'transparent',border:'1px solid rgba(var(--accent-gold-rgb),0.33)',borderRadius:12,color:'var(--accent-gold-text)',cursor:'pointer',fontSize:11,fontWeight:800}}>ⓘ {showModeHelp?'hide':'which mode should I pick?'}</button>
        </div>
        {showModeHelp && (
          <div style={{background:'var(--accent-gold-bg)',border:'1px solid rgba(var(--accent-gold-rgb),0.33)',borderRadius:8,padding:'12px 14px',marginBottom:10,fontSize:12,color:'var(--text-ddd)',lineHeight:1.6}}>
            <div style={{marginBottom:6}}><strong style={{color:'var(--accent-gold-text)'}}>📥 SLEEPER LEAGUE ID</strong> — Fastest. Paste your Sleeper league ID and we'll auto-pull every team's roster + manager name. Pick the new managers to disperse and you're done. Best for active Sleeper leagues.</div>
            <div><strong style={{color:'var(--accent-gold-text)'}}>✏️ CUSTOM</strong> — For non-Sleeper leagues (ESPN, Yahoo, MFL, etc.) or custom scenarios. Paste your player pool and manager usernames yourself. More work but works for any league.</div>
          </div>
        )}
        <div style={{display:'flex',gap:8,padding:6,background:'var(--bg-elevated)',border:'1px solid var(--border)',borderRadius:10}}>
          <button onClick={()=>setSetupMode('sleeper')} style={{flex:1,padding:'10px 14px',background:setupMode==='sleeper'?'var(--accent-gold)':'transparent',border:'none',borderRadius:7,color:setupMode==='sleeper'?'#000':'var(--text-muted)',fontWeight:900,cursor:'pointer',fontSize:13,letterSpacing:1}}>📥 SLEEPER LEAGUE ID</button>
          <button onClick={()=>setSetupMode('custom')} style={{flex:1,padding:'10px 14px',background:setupMode==='custom'?'var(--accent-gold)':'transparent',border:'none',borderRadius:7,color:setupMode==='custom'?'#000':'var(--text-muted)',fontWeight:900,cursor:'pointer',fontSize:13,letterSpacing:1}}>✏️ CUSTOM</button>
        </div>
      </div>

      {setupMode==='sleeper' && (
        <div style={{display:'flex',flexDirection:'column',gap:18,background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:12,padding:'22px 24px'}}>
          {/* League dropdown — always visible so users see the incentive to
              link Sleeper. Disabled tease state when not linked, fully
              functional dropdown when linked. Manual ID input stays available
              below for users who don't want to link or are setting up a draft
              for a league they're not in. Lives ABOVE the league-info snapshot
              card per Evan — fetching a league should be the first action. */}
          <div>
            <label style={labelStyle}>YOUR LEAGUES <span style={{color:'var(--success)',fontWeight:700,fontSize:10,letterSpacing:1.5,marginLeft:6}}>· FASTEST</span></label>
            {!sleeperLinkedUser && (
              <>
                <div style={{padding:'10px 12px',background:'var(--accent-gold-bg)',border:'1px dashed rgba(var(--accent-gold-rgb),0.4)',borderRadius:8}}>
                  <div style={{fontSize:12,color:'rgba(var(--accent-gold-rgb),0.8)',marginBottom:8,lineHeight:1.5}}>
                    💡 Link your Sleeper username for easy league select.
                  </div>
                  <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                    <input value={inlineLinkInput} onChange={e=>{setInlineLinkInput(e.target.value); setInlineLinkErr('');}}
                           onKeyDown={e=>e.key==='Enter'&&inlineLinkSubmit()}
                           placeholder="Sleeper username"
                           disabled={inlineLinkBusy}
                           name="sleeper-handle"
                           autoComplete="off"
                           autoCorrect="off"
                           autoCapitalize="none"
                           spellCheck={false}
                           data-1p-ignore="true"
                           data-lpignore="true"
                           /* iOS Safari saw "username" in the placeholder + saved Safari
                              credentials for the site and was auto-triggering its password
                              autofill prompt — that prompt focuses the input and scrolls the
                              page past the top of the dispersal form. The full autofill-off
                              combo (autoComplete off + neutral name + 1p/lp ignore hints)
                              tells iOS, 1Password, and LastPass to leave this field alone. */
                           style={{flex:'1 1 180px',minWidth:0,padding:'10px 12px',background:'var(--bg-elevated)',border:'1.5px solid rgba(var(--accent-gold-rgb),0.4)',borderRadius:6,color:'var(--text-bright)',fontSize:14,fontFamily:'inherit'}}/>
                    <button onClick={inlineLinkSubmit} disabled={inlineLinkBusy}
                            style={{padding:'10px 18px',background:inlineLinkBusy?'var(--border-strong)':'var(--accent-gold)',border:'none',borderRadius:6,color:'#000',fontWeight:900,cursor:inlineLinkBusy?'wait':'pointer',fontSize:13,letterSpacing:1,whiteSpace:'nowrap'}}>
                      {inlineLinkBusy ? '…' : '🔗 LINK'}
                    </button>
                  </div>
                  {inlineLinkErr && <div style={{color:'var(--danger)',fontSize:12,marginTop:6}}>{inlineLinkErr}</div>}
                  <div style={{fontSize:10,color:'var(--text-secondary)',marginTop:6}}>Public Sleeper lookup — no password, no permission needed. Just your username.</div>
                </div>
              </>
            )}
            {sleeperLinkedUser && linkedLeagues === null && (
              <div style={{fontSize:12,color:'var(--text-secondary)',padding:'10px 12px',background:'var(--bg-elevated)',border:'1px solid var(--border)',borderRadius:6}}>Loading your Sleeper leagues…</div>
            )}
            {sleeperLinkedUser && linkedLeagues && linkedLeagues.length === 0 && (
              <div style={{fontSize:12,color:'var(--text-secondary)',padding:'10px 12px',background:'var(--bg-elevated)',border:'1px solid var(--border)',borderRadius:6}}>No leagues found for {new Date().getFullYear()} on @{sleeperLinkedUser.username}.</div>
            )}
            {sleeperLinkedUser && linkedLeagues && linkedLeagues.length > 0 && (
              <select value={sleeperId} onChange={e => {
                const v = e.target.value;
                setSleeperId(v);
                if(v){
                  // Defer the fetch so the input value commits first; fetchSleeperLeague
                  // accepts an override id so we don't depend on the next render either.
                  setTimeout(() => fetchSleeperLeague(v), 0);
                }
              }} style={{...inputStyle,cursor:'pointer'}}>
                <option value="">— Pick a league —</option>
                {linkedLeagues.map(l => {
                  const t = l?.settings?.type;
                  const tag = t === 2 ? 'Dynasty' : t === 1 ? 'Keeper' : 'Redraft';
                  return <option key={l.league_id} value={l.league_id}>{l.name} · {tag} · {l.total_rosters||'?'}-team</option>;
                })}
              </select>
            )}
            {sleeperLinkedUser && (
              <div style={{fontSize:11,color:'var(--text-secondary)',marginTop:5}}>Or paste a league ID manually below — works for any league you're not in.</div>
            )}
          </div>
          <div>
            <label style={labelStyle}>SLEEPER LEAGUE ID</label>
            <div style={{display:'flex',gap:8}}>
              <input value={sleeperId} onChange={e=>setSleeperId(e.target.value)} onKeyDown={e=>e.key==='Enter'&&fetchSleeperLeague()} placeholder="e.g. 1042739852394871234" style={{...inputStyle,fontFamily:'monospace'}}/>
              <button type="button" onClick={fetchSleeperLeague} disabled={sleeperLoading} style={{padding:'10px 18px',background:sleeperLoading?'var(--border-strong)':'var(--accent-gold)',border:'none',borderRadius:6,color:'#000',fontWeight:900,cursor:sleeperLoading?'default':'pointer',fontSize:13,letterSpacing:1,whiteSpace:'nowrap'}}>{sleeperLoading?'…':'FETCH'}</button>
            </div>
            <div style={{fontSize:11,color:'var(--text-secondary)',marginTop:5}}>Long number from <code style={{color:'var(--accent-gold-text)'}}>sleeper.com/leagues/&lt;id&gt;</code>{!sleeperLinkedUser && <> · or <strong style={{color:'var(--accent-gold-text)'}}>link Sleeper in the toolbar above</strong> to pick from a dropdown</>}</div>
          </div>
          {leagueInfoInputs}
          <div>
            <label style={labelStyle}>DRAFT NAME</label>
            <input value={name} onChange={e=>setName(e.target.value)} placeholder="leave blank to pull from Sleeper" style={inputStyle}/>
          </div>
          {sleeperErr && <div style={{padding:'10px 14px',background:'var(--danger-bg)',border:'1px solid var(--danger)',borderRadius:6,color:'var(--danger)',fontSize:13}}>{sleeperErr}</div>}
          {sleeperData && (()=>{
            const usersById = {}; (sleeperData.users||[]).forEach(u=>{ usersById[u.user_id]=u; });
            return (
              <div>
                <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6,flexWrap:'wrap'}}>
                  <label style={{...labelStyle,marginBottom:0}}>CHOOSE TEAMS & ROSTERS TO INCLUDE · {sleeperSelected.size} of {(sleeperData.rosters||[]).length} selected</label>
                  <button type="button" onClick={()=>setShowTeamsHelp(v=>!v)} title="What does this do?" style={{padding:'2px 8px',background:'transparent',border:'1px solid rgba(var(--accent-gold-rgb),0.33)',borderRadius:12,color:'var(--accent-gold-text)',cursor:'pointer',fontSize:11,fontWeight:800}}>ⓘ {showTeamsHelp?'hide':'what is this?'}</button>
                </div>
                {showTeamsHelp && (
                  <div style={{background:'var(--accent-gold-bg)',border:'1px solid rgba(var(--accent-gold-rgb),0.33)',borderRadius:8,padding:'12px 14px',marginBottom:8,fontSize:12,color:'var(--text-ddd)',lineHeight:1.6}}>
                    Every team you check below will be part of the dispersal. Their entire current roster goes into the draft pool. After you create the draft, share the link with the league and each manager clicks CLAIM next to their team.
                  </div>
                )}
                <div style={{display:'flex',flexDirection:'column',gap:6,maxHeight:340,overflowY:'auto',border:'1px solid var(--bg-pill)',borderRadius:8,padding:8}}>
                  {(sleeperData.rosters||[]).map(r=>{
                    const owner = r.owner_id ? usersById[r.owner_id] : null;
                    const sel = sleeperSelected.has(r.roster_id);
                    const orphan = !r.owner_id;
                    const playerCount = (r.players||[]).length;
                    return (
                      <div key={r.roster_id} style={{padding:'8px 10px',background:sel?'var(--success-bg)':'var(--bg-elevated)',border:'1px solid '+(sel?'var(--success)':'var(--bg-pill)'),borderRadius:6,display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                        <span onClick={()=>{
                          const next = new Set(sleeperSelected);
                          if(sel) next.delete(r.roster_id); else next.add(r.roster_id);
                          setSleeperSelected(next);
                        }} style={{color:sel?'var(--success)':'var(--border-outline)',fontSize:18,cursor:'pointer',userSelect:'none'}}>{sel?'☑':'☐'}</span>
                        <div style={{flex:'1 1 130px',minWidth:120}}>
                          <input value={sleeperUsernames[r.roster_id]||''} onChange={e=>setSleeperUsernames({...sleeperUsernames,[r.roster_id]:e.target.value})} disabled={!sel} placeholder="manager username" style={{width:'100%',padding:'5px 8px',background:'var(--bg-input)',border:'1px solid '+(sel?'var(--accent-gold)':'var(--bg-pill)'),borderRadius:5,color:sel?'var(--accent-gold)':'var(--border-outline)',fontSize:13,fontWeight:700}}/>
                          <div style={{fontSize:10,color:'var(--text-secondary)',marginTop:3}}>{playerCount} players · roster #{r.roster_id} {orphan && <span style={{color:'var(--warning)',fontWeight:800,marginLeft:4}}>· ORPHAN</span>} {owner && <span>· originally {owner.display_name||owner.username}</span>}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{fontSize:11,color:'var(--text-secondary)',marginTop:6}}>Tip: edit the username next to any team before creating — that's what shows up in the draft.</div>
              </div>
            );
          })()}
          <div>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:6,gap:8,flexWrap:'wrap'}}>
              <label style={{...labelStyle,marginBottom:0}}>FUTURE PICKS (optional)</label>
              <button type="button" onClick={()=>setShowPicksHelp(v=>!v)} style={{padding:'2px 8px',background:'transparent',border:'1px solid rgba(var(--accent-gold-rgb),0.33)',borderRadius:12,color:'var(--accent-gold-text)',cursor:'pointer',fontSize:11,fontWeight:800}}>ⓘ {showPicksHelp?'hide':'what goes here?'}</button>
            </div>
            {showPicksHelp && (
              <div style={{background:'var(--accent-gold-bg)',border:'1px solid rgba(var(--accent-gold-rgb),0.33)',borderRadius:8,padding:'12px 14px',marginBottom:8,fontSize:12,color:'var(--text-ddd)',lineHeight:1.6}}>
                Sleeper doesn't track future picks reliably, so add them here manually. These get pooled alongside the players and can be drafted just like any other asset.
                <div style={{marginTop:6,color:'var(--text-secondary)'}}>Use the year dropdowns to add draft picks fast.</div>
              </div>
            )}
            {bulkPicksControls}
            <textarea value={picksText} onChange={e=>setPicksText(e.target.value)} placeholder={`Click a year dropdown above to add a pick`} rows={4} style={{...inputStyle,fontFamily:'monospace',resize:'vertical'}}/>
            <div style={{fontSize:11,color:'var(--text-secondary)',marginTop:5,lineHeight:1.5}}>Picks can be edited or deleted by typing in the text box. Add any free text if needed.</div>
          </div>
          <div style={{display:'flex',gap:18,flexWrap:'wrap'}}>
            <div>
              <label style={labelStyle}>FORMAT</label>
              <div style={{display:'flex',gap:6}}>
                <button onClick={()=>setSnake(true)} style={{padding:'8px 14px',background:snake?'var(--accent-gold)':'var(--bg-elevated)',color:snake?'#000':'var(--text-muted)',border:'1px solid '+(snake?'var(--accent-gold)':'var(--border-strong)'),borderRadius:6,fontWeight:800,cursor:'pointer',fontSize:13}}>🐍 Snake</button>
                <button onClick={()=>setSnake(false)} style={{padding:'8px 14px',background:!snake?'var(--accent-gold)':'var(--bg-elevated)',color:!snake?'#000':'var(--text-muted)',border:'1px solid '+(!snake?'var(--accent-gold)':'var(--border-strong)'),borderRadius:6,fontWeight:800,cursor:'pointer',fontSize:13}}>↓ Linear</button>
              </div>
            </div>
            <div>
              <label style={labelStyle}>TIMER PER PICK</label>
              <select value={timer} onChange={e=>setTimer(e.target.value)} style={{padding:'8px 12px',background:'var(--bg-elevated)',border:'1px solid var(--border-strong)',borderRadius:6,color:'var(--accent-gold-text)',fontSize:13,fontWeight:800,cursor:'pointer',minWidth:160}}>
                <option value="off">None (no clock)</option>
                <option value="30m">30 minutes</option>
                <option value="1h">1 hour</option>
                <option value="2h">2 hours</option>
                <option value="4h">4 hours</option>
                <option value="8h">8 hours</option>
              </select>
              {timer !== 'off' && (
                <label style={{display:'flex',alignItems:'center',gap:8,marginTop:8,fontSize:12,color:'var(--text-secondary)',cursor:'pointer'}}>
                  <input type="checkbox" checked={autoPick} onChange={e=>setAutoPick(e.target.checked)} style={{width:16,height:16,accentColor:'var(--success)'}}/>
                  Auto-pick best available if a manager misses their pick
                </label>
              )}
              {timer !== 'off' && !autoPick && <div style={{fontSize:11,color:'var(--text-secondary)',marginTop:5}}>When a pick clock expires, the draft pauses for the commish to advance.</div>}
              {timer !== 'off' && (
                <label style={{display:'flex',alignItems:'center',gap:8,marginTop:10,fontSize:12,color:'var(--text-secondary)',cursor:'pointer'}}>
                  <input type="checkbox" checked={autoPauseEnabled} onChange={e=>setAutoPauseEnabled(e.target.checked)} style={{width:16,height:16,accentColor:'var(--accent-gold)'}}/>
                  Overnight pause (clock freezes during a daily window)
                </label>
              )}
              {timer !== 'off' && autoPauseEnabled && (
                <div style={{marginTop:6,display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
                  <input type="time" value={autoPauseStart} onChange={e=>setAutoPauseStart(e.target.value)} aria-label="Pause start" style={{padding:'7px 8px',background:'var(--bg-elevated)',border:'1px solid var(--border-strong)',borderRadius:6,color:'var(--accent-gold-text)',fontSize:13,fontWeight:800}}/>
                  <span style={{fontSize:12,color:'var(--text-secondary)',fontWeight:800}}>→</span>
                  <input type="time" value={autoPauseEnd} onChange={e=>setAutoPauseEnd(e.target.value)} aria-label="Pause end" style={{padding:'7px 8px',background:'var(--bg-elevated)',border:'1px solid var(--border-strong)',borderRadius:6,color:'var(--accent-gold-text)',fontSize:13,fontWeight:800}}/>
                  <span style={{fontSize:11,color:'var(--text-secondary)'}}>your local time</span>
                </div>
              )}
              {timer !== 'off' && autoPauseEnabled && (
                <div style={{fontSize:11,color:'var(--text-secondary)',marginTop:5,lineHeight:1.4}}>Only managers who turn on their <em>personal</em> auto-pick will draft during the pause window. Everyone else just waits until the window ends.</div>
              )}
            </div>
            {/* SHARE DISPERSAL — moved here per Evan so it sits next to the
                draft timer at the bottom of the form. Same action as before. */}
            <div>
              <label style={labelStyle}>SHARE</label>
              <button onClick={sharePoolImage} disabled={poolShareBusy||!sleeperData||sleeperSelected.size===0} title="Save a PFK-branded image of the pool to advertise the league on Twitter/Discord BEFORE creating the draft"
                style={{padding:'8px 14px',background:(poolShareBusy||!sleeperData||sleeperSelected.size===0)?'var(--bg-pill)':'var(--accent-gold)',border:'none',borderRadius:6,color:(poolShareBusy||!sleeperData||sleeperSelected.size===0)?'var(--border-outline)':'#000',fontWeight:800,cursor:(poolShareBusy||!sleeperData||sleeperSelected.size===0)?'default':'pointer',fontSize:13}}>{poolShareBusy?'BUILDING…':'📸 DISPERSAL ASSET SCREENSHOT'}</button>
            </div>
          </div>
          {err && <div style={{padding:'10px 14px',background:'var(--danger-bg)',border:'1px solid var(--danger)',borderRadius:6,color:'var(--danger)',fontSize:13}}>{err}</div>}
          <div>
            <button onClick={createFromSleeper} disabled={creating||sleeperBuilding||!sleeperData||sleeperSelected.size<2} style={{width:'100%',padding:'14px 24px',background:(creating||sleeperBuilding||!sleeperData||sleeperSelected.size<2)?'var(--border-strong)':'var(--success)',border:'none',borderRadius:8,color:'#000',fontWeight:900,cursor:(creating||sleeperBuilding||!sleeperData||sleeperSelected.size<2)?'default':'pointer',fontSize:14,letterSpacing:1.5}}>{sleeperBuilding?'BUILDING POOL FROM SLEEPER…':creating?'CREATING…':'CREATE DRAFT'}</button>
            <div style={{fontSize:11,color:'var(--text-secondary)',marginTop:6,textAlign:'center',lineHeight:1.5}}>(sharable draft link inside draft)</div>
          </div>
        </div>
      )}

      {setupMode==='custom' && (
      <div style={{display:'flex',flexDirection:'column',gap:18,background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:12,padding:'22px 24px'}}>
        {leagueInfoInputs}
        <div>
          <label style={labelStyle}>DRAFT NAME</label>
          <input value={name} onChange={e=>setName(e.target.value)} placeholder="leave blank to pull from Sleeper" style={inputStyle}/>
        </div>
        <div>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:6,flexWrap:'wrap',gap:8}}>
            <label style={{...labelStyle,marginBottom:0}}>PLAYER POOL (one player per line)</label>
            <button onClick={()=>setSleeperOpen(true)} type="button" style={{padding:'5px 11px',background:'var(--bg-card)',border:'1px solid var(--accent-gold)',borderRadius:6,color:'var(--accent-gold-text)',fontSize:11,fontWeight:800,cursor:'pointer',letterSpacing:0.5}}>📥 IMPORT FROM SLEEPER</button>
          </div>
          <textarea value={poolText} onChange={e=>setPoolText(e.target.value)} placeholder={`Justin Jefferson\nBijan Robinson\n...`} rows={8} style={{...inputStyle,fontFamily:'monospace',resize:'vertical'}}/>
        </div>
        <div>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:6,gap:8,flexWrap:'wrap'}}>
            <label style={{...labelStyle,marginBottom:0}}>FUTURE PICKS (optional)</label>
            <button type="button" onClick={()=>setShowPicksHelp(v=>!v)} style={{padding:'2px 8px',background:'transparent',border:'1px solid rgba(var(--accent-gold-rgb),0.33)',borderRadius:12,color:'var(--accent-gold-text)',cursor:'pointer',fontSize:11,fontWeight:800}}>ⓘ {showPicksHelp?'hide':'what goes here?'}</button>
          </div>
          {showPicksHelp && (
            <div style={{background:'var(--accent-gold-bg)',border:'1px solid rgba(var(--accent-gold-rgb),0.33)',borderRadius:8,padding:'12px 14px',marginBottom:8,fontSize:12,color:'var(--text-ddd)',lineHeight:1.6}}>
              Sleeper doesn't track future picks reliably, so add them here manually. These get pooled alongside the players and can be drafted just like any other asset.
              <div style={{marginTop:6,color:'var(--text-secondary)'}}>Use the year dropdowns to add draft picks fast.</div>
            </div>
          )}
          {bulkPicksControls}
          <textarea value={picksText} onChange={e=>setPicksText(e.target.value)} placeholder={`Click a year dropdown above to add a pick, then type the original owner's username after "via"`} rows={4} style={{...inputStyle,fontFamily:'monospace',resize:'vertical'}}/>
          <div style={{fontSize:11,color:'var(--text-secondary)',marginTop:5,lineHeight:1.5}}>Picks can be edited or deleted by typing in the text box. Add any free text if needed.</div>
        </div>
        <div>
          <label style={labelStyle}>NEW MANAGER USERNAMES (one per line)</label>
          <textarea value={teamsText} onChange={e=>setTeamsText(e.target.value)} placeholder={`user1\nuser2\nuser3`} rows={5} style={{...inputStyle,fontFamily:'monospace',resize:'vertical'}}/>
          <div style={{fontSize:11,color:'var(--text-secondary)',marginTop:5}}>After creating the draft, share the link with your league. Each manager clicks CLAIM next to their team. Pick order can be randomized in the lobby.</div>
        </div>
        <div style={{display:'flex',gap:18,flexWrap:'wrap'}}>
          <div>
            <label style={labelStyle}>FORMAT</label>
            <div style={{display:'flex',gap:6}}>
              <button onClick={()=>setSnake(true)} style={{padding:'8px 14px',background:snake?'var(--accent-gold)':'var(--bg-elevated)',color:snake?'#000':'var(--text-muted)',border:'1px solid '+(snake?'var(--accent-gold)':'var(--border-strong)'),borderRadius:6,fontWeight:800,cursor:'pointer',fontSize:13}}>🐍 Snake</button>
              <button onClick={()=>setSnake(false)} style={{padding:'8px 14px',background:!snake?'var(--accent-gold)':'var(--bg-elevated)',color:!snake?'#000':'var(--text-muted)',border:'1px solid '+(!snake?'var(--accent-gold)':'var(--border-strong)'),borderRadius:6,fontWeight:800,cursor:'pointer',fontSize:13}}>↓ Linear</button>
            </div>
          </div>
          <div>
            <label style={labelStyle}>TIMER PER PICK</label>
            <div style={{display:'flex',gap:6}}>
              {[['off','None'],['4h','4 hrs'],['8h','8 hrs']].map(([k,l])=>(
                <button key={k} onClick={()=>setTimer(k)} style={{padding:'8px 14px',background:timer===k?'var(--accent-gold)':'var(--bg-elevated)',color:timer===k?'#000':'var(--text-muted)',border:'1px solid '+(timer===k?'var(--accent-gold)':'var(--border-strong)'),borderRadius:6,fontWeight:800,cursor:'pointer',fontSize:13}}>{l}</button>
              ))}
            </div>
            <div style={{fontSize:11,color:'var(--text-secondary)',marginTop:5}}>When a pick clock expires, the draft pauses for the commish to advance.</div>
          </div>
          {/* SHARE DISPERSAL — moved here per Evan so it sits next to the
              draft timer at the bottom of the form. Same action as before. */}
          <div>
            <label style={labelStyle}>SHARE</label>
            <button onClick={sharePoolImage} disabled={poolShareBusy||(!poolText.trim()&&!picksText.trim())} title="Save a PFK-branded image of the pool to advertise the league on Twitter/Discord BEFORE creating the draft"
              style={{padding:'8px 14px',background:(poolShareBusy||(!poolText.trim()&&!picksText.trim()))?'var(--bg-pill)':'var(--accent-gold)',border:'none',borderRadius:6,color:(poolShareBusy||(!poolText.trim()&&!picksText.trim()))?'var(--border-outline)':'#000',fontWeight:800,cursor:(poolShareBusy||(!poolText.trim()&&!picksText.trim()))?'default':'pointer',fontSize:13}}>{poolShareBusy?'BUILDING…':'📸 DISPERSAL ASSET SCREENSHOT'}</button>
          </div>
        </div>
        {err && <div style={{padding:'10px 14px',background:'var(--danger-bg)',border:'1px solid var(--danger)',borderRadius:6,color:'var(--danger)',fontSize:13}}>{err}</div>}
        <div>
          <button onClick={create} disabled={creating} style={{width:'100%',padding:'14px 24px',background:creating?'var(--border-strong)':'var(--success)',border:'none',borderRadius:8,color:'#000',fontWeight:900,cursor:creating?'default':'pointer',fontSize:14,letterSpacing:1.5}}>{creating?'CREATING…':'CREATE DRAFT'}</button>
          <div style={{fontSize:11,color:'var(--text-secondary)',marginTop:6,textAlign:'center',lineHeight:1.5}}>(sharable draft link inside draft)</div>
        </div>
      </div>
      )}

      {/* Sleeper import modal (custom-mode shortcut button) */}
      {sleeperOpen && (()=>{
        const usersById = {}; (sleeperData?.users||[]).forEach(u=>{ usersById[u.user_id]=u; });
        return (
          <div onClick={()=>setSleeperOpen(false)} style={{position:'fixed',inset:0,background:'var(--backdrop)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
            <div onClick={e=>e.stopPropagation()} style={{width:'100%',maxWidth:540,maxHeight:'90vh',overflowY:'auto',background:'var(--bg-card)',border:'1px solid var(--accent-gold)',borderRadius:12,padding:'18px 22px'}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
                <div style={{fontWeight:900,fontSize:16,letterSpacing:1.5,color:'var(--accent-gold-text)'}}>📥 IMPORT FROM SLEEPER</div>
                <button onClick={()=>setSleeperOpen(false)} style={{background:'none',border:'none',color:'var(--text-secondary)',fontSize:20,cursor:'pointer'}}>✕</button>
              </div>
              <div style={{fontSize:12,color:'var(--text-secondary)',marginBottom:10,lineHeight:1.5}}>Paste your Sleeper league ID (the long number from <code style={{color:'var(--accent-gold-text)'}}>sleeper.com/leagues/&lt;id&gt;</code>). We'll pull the team list. Pick the orphaned teams to import their rosters into the pool.</div>
              <div style={{display:'flex',gap:8,marginBottom:14}}>
                <input value={sleeperId} onChange={e=>setSleeperId(e.target.value)} onKeyDown={e=>e.key==='Enter'&&fetchSleeperLeague()} placeholder="Sleeper league ID" style={{flex:1,padding:'10px 12px',background:'var(--bg-input)',border:'1px solid var(--border-strong)',borderRadius:6,color:'var(--text-bright)',fontSize:14,fontFamily:'monospace'}}/>
                <button onClick={fetchSleeperLeague} disabled={sleeperLoading} style={{padding:'10px 16px',background:sleeperLoading?'var(--border-strong)':'var(--accent-gold)',border:'none',borderRadius:6,color:'#000',fontWeight:900,cursor:sleeperLoading?'default':'pointer',fontSize:13,letterSpacing:1}}>{sleeperLoading?'…':'FETCH'}</button>
              </div>
              {sleeperErr && <div style={{padding:'8px 12px',background:'var(--danger-bg)',border:'1px solid var(--danger)',borderRadius:6,color:'var(--danger)',fontSize:12,marginBottom:12}}>{sleeperErr}</div>}
              {sleeperData && (
                <>
                  <div style={{fontSize:11,color:'var(--text-secondary)',fontWeight:800,letterSpacing:1.5,marginBottom:8}}>SELECT ORPHANED TEAMS · {sleeperSelected.size} of {(sleeperData.rosters||[]).length} selected</div>
                  <div style={{display:'flex',flexDirection:'column',gap:4,marginBottom:14,maxHeight:280,overflowY:'auto'}}>
                    {(sleeperData.rosters||[]).map(r=>{
                      const owner = r.owner_id ? usersById[r.owner_id] : null;
                      const sel = sleeperSelected.has(r.roster_id);
                      const orphan = !r.owner_id;
                      const playerCount = (r.players||[]).length;
                      return (
                        <div key={r.roster_id} onClick={()=>{
                          const next = new Set(sleeperSelected);
                          if(sel) next.delete(r.roster_id); else next.add(r.roster_id);
                          setSleeperSelected(next);
                        }} style={{padding:'8px 12px',background:sel?'var(--success-bg)':'var(--bg-elevated)',border:'1px solid '+(sel?'var(--success)':'var(--bg-pill)'),borderRadius:6,cursor:'pointer',display:'flex',alignItems:'center',gap:10}}>
                          <span style={{color:sel?'var(--success)':'var(--border-outline)',fontSize:18}}>{sel?'☑':'☐'}</span>
                          <div style={{flex:1}}>
                            <div style={{fontWeight:700,fontSize:14}}>{owner?.display_name || owner?.username || `Team #${r.roster_id}`}</div>
                            <div style={{fontSize:11,color:'var(--text-secondary)'}}>{playerCount} players · roster #{r.roster_id} {orphan && <span style={{color:'var(--warning)',fontWeight:800,marginLeft:6}}>· ORPHAN</span>}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <button onClick={importSelected} disabled={sleeperPlayersLoading || sleeperSelected.size===0} style={{width:'100%',padding:'12px',background:sleeperPlayersLoading||sleeperSelected.size===0?'var(--border-strong)':'var(--success)',border:'none',borderRadius:7,color:'#000',fontWeight:900,cursor:sleeperPlayersLoading||sleeperSelected.size===0?'default':'pointer',fontSize:14,letterSpacing:1}}>
                    {sleeperPlayersLoading ? 'LOADING PLAYER NAMES (~10MB, one-time)…' : 'ADD PLAYERS TO POOL →'}
                  </button>
                </>
              )}
            </div>
          </div>
        );
      })()}

      {/* Desktop share modal — shown after image is generated on PC.
          Three actions: download / copy to clipboard / share to X (with image already copied). */}
      {desktopShare && (() => {
        const closeModal = () => { setDesktopShare(null); setShareToast(''); setPreviewData(null); };
        const doDownload = () => {
          const url = URL.createObjectURL(desktopShare.blob);
          const link = document.createElement('a');
          link.download = desktopShare.filename;
          link.href = url;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setTimeout(()=>URL.revokeObjectURL(url), 1000);
          setShareToast('Downloaded ✓');
          setTimeout(closeModal, 900);
        };
        const doCopy = async () => {
          try{
            // ClipboardItem is supported in modern Chrome/Edge/Firefox 127+/Safari 13.1+.
            // Writes the PNG to the system clipboard so the user can paste in iMessage,
            // Slack, X compose box, Photos, etc.
            if(!window.ClipboardItem || !navigator.clipboard || !navigator.clipboard.write){
              setShareToast('Copy not supported in this browser — use Download instead');
              return;
            }
            const item = new window.ClipboardItem({ 'image/png': desktopShare.blob });
            await navigator.clipboard.write([item]);
            setShareToast('Copied to clipboard ✓ — paste anywhere');
            setTimeout(closeModal, 1400);
          }catch(e){ setShareToast('Copy failed: ' + (e.message||e)); }
        };
        const doShareToX = async () => {
          // Twitter web intents don't accept image attachments via URL — best we can do is
          // copy the image to the clipboard, then open the X compose window with prefilled
          // text. The user pastes the image into the tweet (Cmd/Ctrl+V).
          let copied = false;
          try{
            if(window.ClipboardItem && navigator.clipboard && navigator.clipboard.write){
              const item = new window.ClipboardItem({ 'image/png': desktopShare.blob });
              await navigator.clipboard.write([item]);
              copied = true;
            }
          }catch(e){}
          const tweetText = `Dispersal draft alert ⚠️ — ${desktopShare.draftName}. Looking for new managers. DM me to join. (built with @PlayforkeepsFF)`;
          const intentUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;
          window.open(intentUrl, '_blank');
          setShareToast(copied ? 'Image copied — paste it into the tweet (Cmd/Ctrl+V)' : 'Tweet opened — image not auto-copied (use Copy first)');
          setTimeout(closeModal, 2400);
        };
        return (
          <div onClick={closeModal} style={{position:'fixed',inset:0,background:'var(--backdrop)',zIndex:99999,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
            <div onClick={e=>e.stopPropagation()} style={{width:'100%',maxWidth:440,background:'var(--bg-card)',border:'2px solid var(--accent-gold)',borderRadius:12,padding:'22px 24px'}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
                <div style={{fontWeight:900,fontSize:15,letterSpacing:1.5,color:'var(--accent-gold-text)'}}>📸 DISPERSAL ASSET SCREENSHOT</div>
                <button onClick={closeModal} style={{background:'none',border:'none',color:'var(--text-secondary)',fontSize:20,cursor:'pointer'}}>✕</button>
              </div>
              <div style={{fontSize:13,color:'var(--text-secondary)',marginBottom:14,lineHeight:1.5}}>Image is ready. Pick what to do with it:</div>
              <div style={{display:'flex',flexDirection:'column',gap:8}}>
                <button onClick={doDownload} style={{padding:'12px 16px',background:'var(--success-bg)',border:'1.5px solid var(--success)',borderRadius:7,color:'var(--success)',fontWeight:900,cursor:'pointer',fontSize:14,letterSpacing:1,textAlign:'left'}}>📥 &nbsp;DOWNLOAD &nbsp;<span style={{fontWeight:600,color:'var(--text-secondary)',fontSize:12,letterSpacing:0.3}}>· save .png to your computer</span></button>
                <button onClick={doCopy} style={{padding:'12px 16px',background:'transparent',border:'1.5px solid var(--accent-gold)',borderRadius:7,color:'var(--accent-gold-text)',fontWeight:900,cursor:'pointer',fontSize:14,letterSpacing:1,textAlign:'left'}}>📋 &nbsp;COPY TO CLIPBOARD &nbsp;<span style={{fontWeight:600,color:'var(--text-secondary)',fontSize:12,letterSpacing:0.3}}>· paste into iMessage, Slack, etc.</span></button>
                <button onClick={doShareToX} style={{padding:'12px 16px',background:'#000',border:'1.5px solid #fff',borderRadius:7,color:'#fff',fontWeight:900,cursor:'pointer',fontSize:14,letterSpacing:1,textAlign:'left',display:'flex',alignItems:'center',gap:8}}>
                  <svg viewBox="0 0 24 24" style={{width:16,height:16}}>
                    <path fill="var(--text-bright)" d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                  &nbsp;SHARE TO X &nbsp;<span style={{fontWeight:600,color:'var(--text-secondary)',fontSize:12,letterSpacing:0.3}}>· copies image + opens tweet</span>
                </button>
              </div>
              {shareToast && <div style={{marginTop:14,padding:'10px 12px',background:'var(--success-bg)',border:'1px solid var(--success)',borderRadius:7,color:'var(--success)',fontSize:12,fontWeight:700,textAlign:'center'}}>{shareToast}</div>}
            </div>
          </div>
        );
      })()}

      {/* Off-screen share-card — populated by sharePoolImage() then captured via html2canvas.
          Painted at -99999px so the card renders correctly without ever being visible. */}
      {previewData && (
        <div style={{position:'fixed',left:'-99999px',top:0,pointerEvents:'none'}} aria-hidden="true">
          <div ref={poolCardRef} data-theme="dark" style={{width:900,background:'var(--bg-page)',padding:'32px 36px',color:'var(--text-primary)',fontFamily:"'Inter','Segoe UI',sans-serif",border:'3px solid var(--accent-gold)',borderRadius:14,boxSizing:'border-box'}}>
            <div style={{display:'flex',alignItems:'center',gap:18,marginBottom:18,paddingBottom:18,borderBottom:'2px solid rgba(var(--accent-gold-rgb),0.27)'}}>
              {/* Local logo (1500x500 actual) — display at fixed height respecting aspect ratio.
                  Hosted on the same origin so html2canvas captures it cleanly (no CORS). */}
              <img src="/img/pfk-logo.jpg" alt="PFK" style={{height:60,width:'auto',objectFit:'contain',flexShrink:0}}/>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:800,color:'var(--accent-gold-text)',letterSpacing:2.5,marginBottom:4}}>🎲 DISPERSAL DRAFT POOL</div>
                <div style={{fontSize:26,fontWeight:900,color:'var(--text-bright)',letterSpacing:0.5,lineHeight:1.15,wordBreak:'break-word'}}>{previewData.draftName}</div>
              </div>
            </div>
            {/* Stats row — 3 cards: TOTAL ASSETS (players+picks), NEW MANAGERS, BUY-IN. */}
            <div style={{display:'flex',gap:10,marginBottom:14,flexWrap:'wrap'}}>
              <div style={{flex:'1 1 0',padding:'10px 14px',background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:8,textAlign:'center'}}>
                <div style={{fontSize:24,fontWeight:900,color:'var(--accent-gold-text)',lineHeight:1}}>{previewData.players.length + previewData.picks.length}</div>
                <div style={{fontSize:10,color:'var(--text-secondary)',fontWeight:800,letterSpacing:1.5,marginTop:4}}>TOTAL ASSETS</div>
              </div>
              <div style={{flex:'1 1 0',padding:'10px 14px',background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:8,textAlign:'center'}}>
                <div style={{fontSize:24,fontWeight:900,color:'var(--accent-gold-text)',lineHeight:1}}>{previewData.managersNeeded||0}</div>
                <div style={{fontSize:10,color:'var(--text-secondary)',fontWeight:800,letterSpacing:1.5,marginTop:4}}>NEW MANAGERS NEEDED</div>
              </div>
              <div style={{flex:'1 1 0',padding:'10px 14px',background:'var(--success-bg)',border:'1px solid var(--success)',borderRadius:8,textAlign:'center'}}>
                <div style={{fontSize:22,fontWeight:900,color:'var(--success)',lineHeight:1}}>{(() => {
                  const raw = (previewData.buyIn||'').trim();
                  return raw || 'TBD';
                })()}</div>
                <div style={{fontSize:10,color:'var(--text-secondary)',fontWeight:800,letterSpacing:1.5,marginTop:4}}>BUY-IN</div>
              </div>
            </div>
            {/* Settings strip — auto-detected from Sleeper or commish-entered for Custom mode. */}
            {previewData.settingsLine && (
              <div style={{padding:'10px 14px',background:'var(--accent-gold-bg)',border:'1px solid rgba(var(--accent-gold-rgb),0.2)',borderRadius:8,marginBottom:18,textAlign:'center',fontSize:13,fontWeight:800,color:'var(--accent-gold-text)',letterSpacing:1.5}}>
                {previewData.settingsLine}
              </div>
            )}
            <div style={{display:'grid',gridTemplateColumns: previewData.picks.length ? '1.6fr 1fr' : '1fr',gap:20}}>
              <div>
                <div style={{fontSize:12,fontWeight:900,color:'var(--accent-gold-text)',letterSpacing:2,marginBottom:10,paddingBottom:6,borderBottom:'1px solid rgba(var(--accent-gold-rgb),0.2)'}}>📋 PLAYER POOL</div>
                {[...previewData.posOrder, 'OTHER'].filter(p => previewData.playersByPos[p]?.length).map(pos => (
                  <div key={pos} style={{marginBottom:14}}>
                    <div style={{fontSize:11,fontWeight:900,color:'var(--accent-gold-text)',letterSpacing:1.5,marginBottom:6}}>{pos === 'OTHER' ? 'OTHER' : pos} <span style={{color:'var(--text-secondary)',fontWeight:700}}>· {previewData.playersByPos[pos].length}</span></div>
                    <div style={{display:'grid',gridTemplateColumns: previewData.playersByPos[pos].length > 8 ? '1fr 1fr' : '1fr',gap:'2px 14px'}}>
                      {previewData.playersByPos[pos].map(it => (
                        <div key={it.id} style={{fontSize:13,color:'var(--text-ddd)',lineHeight:1.5,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                          {stripPosFromName(it.name)}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              {previewData.picks.length > 0 && (
                <div>
                  <div style={{fontSize:12,fontWeight:900,color:'var(--accent-gold-text)',letterSpacing:2,marginBottom:10,paddingBottom:6,borderBottom:'1px solid rgba(var(--accent-gold-rgb),0.2)'}}>🎟 ROOKIE PICKS</div>
                  {(() => {
                    const byYear = {};
                    previewData.picks.forEach(p => {
                      const yM = (p.name||'').match(/(\d{4})/);
                      const y = yM ? yM[1] : '????';
                      if(!byYear[y]) byYear[y] = [];
                      byYear[y].push(p);
                    });
                    return Object.keys(byYear).sort().map(y => (
                      <div key={y} style={{marginBottom:14}}>
                        <div style={{fontSize:11,fontWeight:900,color:'var(--accent-gold-text)',letterSpacing:1.5,marginBottom:6}}>{y} <span style={{color:'var(--text-secondary)',fontWeight:700}}>· {byYear[y].length}</span></div>
                        <div style={{display:'flex',flexDirection:'column',gap:2}}>
                          {byYear[y].map(p => (
                            <div key={p.id} style={{fontSize:12,color:'var(--text-ddd)',lineHeight:1.5}}>
                              {p.name.replace(/^\d{4}\s*/, '')}
                            </div>
                          ))}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              )}
            </div>
            <div style={{marginTop:22,paddingTop:16,borderTop:'2px solid rgba(var(--accent-gold-rgb),0.27)',display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:11,color:'var(--text-secondary)',fontWeight:800,letterSpacing:1.5,marginBottom:3}}>CREATE YOUR OWN DISPERSAL DRAFT AT</div>
                <div style={{fontSize:18,fontWeight:900,color:'var(--accent-gold-text)',letterSpacing:0.5}}>playforkeepsdynasty.com</div>
              </div>
              {/* X (Twitter) icon + handle. Inline SVG so html2canvas captures it cleanly. */}
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <svg viewBox="0 0 24 24" style={{width:22,height:22,flexShrink:0}}>
                  <path fill="var(--accent-gold-text)" d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
                <div style={{fontSize:18,fontWeight:900,color:'var(--accent-gold-text)',letterSpacing:0.5}}>@PlayforkeepsFF</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Active draft view (lobby / live / complete) ----------
function DispersalDraft({draftId}){
  const [draft,setDraft] = useState(null);
  const [loading,setLoading] = useState(true);
  // Bottom-sheet height (px). Draggable anywhere between collapsed (~56px,
  // just the tab bar) and "view" (within 80px of the top of the viewport).
  // First-visit default is ~25% of the viewport so users see the board but
  // can pull the sheet up. Last-set height persists across visits.
  // Migration: old localStorage key 'pfk_disp_rosters_collapsed' is honored
  // — '1' (collapsed) → 56px, '0' (expanded) → 25% default.
  // Collapsed height fits both the drag handle (~38px) and the full tab bar
  // (~50px) so PLAYERS / TEAM / ▼ / ▲ stay visible and tappable when the
  // sheet is at its floor — Evan needs the snap buttons reachable from the
  // collapsed state so he can re-expand with one tap.
  const SHEET_COLLAPSED_PX = 110;
  // Max height = viewport minus the actual toolbar height (queried live so it
  // adapts to desktop's bigger logo and mobile's compact one). Without this
  // the sheet can expand BEHIND the sticky toolbar — on desktop the ▼ button
  // ends up obscured by the toolbar's z-index, leaving users stuck at the top.
  const sheetMaxPx = () => {
    if(typeof window === 'undefined') return 600;
    const tb = typeof document !== 'undefined' ? document.querySelector('.pfk-sticky-header') : null;
    const tbH = tb ? tb.offsetHeight : 80;
    return Math.max(SHEET_COLLAPSED_PX+1, window.innerHeight - tbH - 12);
  };
  const sheetDefaultPx = () => Math.round((typeof window!=='undefined' ? window.innerHeight : 800) * 0.25);
  const [sheetHeight,setSheetHeight] = useState(() => {
    if(typeof window === 'undefined') return 200;
    const savedNum = localStorage.getItem('pfk_disp_sheet_h');
    if(savedNum != null) {
      const n = parseInt(savedNum, 10);
      if(!isNaN(n)) return Math.max(SHEET_COLLAPSED_PX, Math.min(sheetMaxPx(), n));
    }
    // Honor legacy boolean key on first encounter
    const legacy = localStorage.getItem('pfk_disp_rosters_collapsed');
    if(legacy === '1') return SHEET_COLLAPSED_PX;
    return sheetDefaultPx();
  });
  // Persist whenever the user drags or snaps. Debounced via passive setter —
  // every change writes once. localStorage is fast enough for this.
  useEffect(() => {
    try{ localStorage.setItem('pfk_disp_sheet_h', String(sheetHeight)); }catch(e){}
  },[sheetHeight]);
  // Re-clamp on viewport resize so a sheet saved at 600px on a tablet doesn't
  // exceed the viewport on a phone the user opens it on next.
  useEffect(() => {
    const onResize = () => setSheetHeight(h => Math.max(SHEET_COLLAPSED_PX, Math.min(sheetMaxPx(), h)));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  },[]);
  // Drag handle uses React synthetic touch + an inline mouse drag pattern.
  // CSS `touch-action: none` on the handle (set inline below) is what stops
  // the browser from hijacking the gesture as a page scroll — preventDefault
  // is a belt-and-suspenders no-op on passive listeners. Active drag is
  // tracked via ref so we don't re-render between touchmove ticks.
  const sheetDrag = useRef({ active:false, startY:0, startH:0 });
  const onSheetDragStart = (e) => {
    const y = e.touches ? e.touches[0].clientY : e.clientY;
    sheetDrag.current = { active:true, startY:y, startH:sheetHeight };
    if(e.cancelable) e.preventDefault();
  };
  const onSheetDragMove = (e) => {
    if(!sheetDrag.current.active) return;
    const y = e.touches ? e.touches[0].clientY : e.clientY;
    const delta = sheetDrag.current.startY - y; // upward drag = positive
    // Use the same sheetMaxPx() ceiling that the ▲ snap button uses, so the
    // drag can't sneak the sheet behind the sticky toolbar (which would hide
    // the ▼ button and trap the user). Was using a hardcoded -80 buffer.
    const next = Math.max(SHEET_COLLAPSED_PX, Math.min(sheetMaxPx(), sheetDrag.current.startH + delta));
    setSheetHeight(next);
    if(e.cancelable) e.preventDefault();
  };
  const onSheetDragEnd = () => { sheetDrag.current.active = false; };
  const isSheetCollapsed = sheetHeight <= SHEET_COLLAPSED_PX + 4;
  // Convenience: jump to a useful expanded height when programmatic UI wants
  // to make sure content is visible (e.g. tapping a TEAM avatar).
  const ensureSheetOpen = () => {
    if(isSheetCollapsed) setSheetHeight(Math.max(sheetDefaultPx(), 280));
  };
  // Spectator mode: ?spectate=1 in URL hides claim/pick UI; the user can watch
  // but never interact. Useful for league mates not in the disperse pool.
  const isSpectator = typeof window!=='undefined' && new URLSearchParams(window.location.search).get('spectate')==='1';
  // Commissioner = the browser that created the draft (flagged in localStorage on create) OR
  // anyone who clicked "I AM THE COMMISSIONER" in the lobby. Stored in localStorage so the
  // flag survives reloads. Held in state so toggling re-renders immediately.
  const [commishFlag,setCommishFlag] = useState(()=>{
    try{ return localStorage.getItem('pfk_disp_creator_'+draftId)==='1'; }catch(e){ return false; }
  });
  // Admin override: if the signed-in user is Evan (the site admin), grant
  // full commish powers on every dispersal draft so he can help users who
  // get stuck. Tied to the Supabase session email — works on any device,
  // survives localStorage wipes. Tracked separately from commishFlag so the
  // UI can show an "ADMIN OVERRIDE" badge when he's NOT the actual creator.
  const [userEmail,setUserEmail] = useState(null);
  useEffect(()=>{
    let cancelled = false;
    sb.auth.getSession().then(({data})=>{ if(!cancelled) setUserEmail(data?.session?.user?.email || null); }).catch(()=>{});
    const sub = sb.auth.onAuthStateChange((_e, s)=>{ if(!cancelled) setUserEmail(s?.user?.email || null); });
    return ()=>{ cancelled = true; try{ sub?.data?.subscription?.unsubscribe?.(); }catch(e){} };
  },[]);
  const isAdminOverride = !isSpectator && userEmail === EVAN_EMAIL;
  const isCommish = !isSpectator && (commishFlag || isAdminOverride);
  const declareCommish = () => {
    try{ localStorage.setItem('pfk_disp_creator_'+draftId, '1'); }catch(e){}
    setCommishFlag(true);
  };
  const [me,setMe] = useState(()=>{
    if(isSpectator) return null;
    try{ return JSON.parse(localStorage.getItem('pfk_disp_'+draftId) || 'null'); }catch(e){ return null; }
  });
  // Notify DispersalApp (which renders MasterToolbar) whenever the claim
  // changes — so the nav-tab "open in new tab" behavior reactively turns on
  // when the user claims a team and back off if they unclaim. Same-tab
  // counterpart to the 'storage' event (which only fires cross-tab).
  useEffect(() => {
    try { window.dispatchEvent(new Event('pfk-disp-claim-change')); } catch(e) {}
  }, [me?.slot]);
  // Tier S: Sleeper-tied persistent claims. We tag teams with sleeper_user_id
  // when a linked user claims them, so if their localStorage ever evaporates
  // (clear cache, device switch, iOS eviction) we can silently restore their
  // identity by matching on their Sleeper account. RECOVER button stays as the
  // last-resort fallback for unlinked users.
  const [sleeperLinkedUser, setSleeperLinkedUser] = useSleeperUser();
  // Nudge state: banner dismiss is sticky-per-browser, claim-link prompt
  // intercepts CLAIM clicks for unlinked users with a "link first?" modal.
  const [unlinkBannerDismissed, setUnlinkBannerDismissed] = useState(() => {
    try{ return localStorage.getItem('pfk_disp_link_nudge_dismissed') === '1'; }catch(e){ return false; }
  });
  const dismissUnlinkBanner = () => {
    try{ localStorage.setItem('pfk_disp_link_nudge_dismissed', '1'); }catch(e){}
    setUnlinkBannerDismissed(true);
  };
  // Per-draft chat-recommendation banner. Keyed by draftId so a fresh draft
  // shows the banner again — dismissal isn't global.
  const chatBannerKey = 'pfk_disp_chat_banner_dismissed_'+draftId;
  const [chatBannerDismissed, setChatBannerDismissed] = useState(() => {
    try{ return localStorage.getItem(chatBannerKey) === '1'; }catch(e){ return false; }
  });
  const dismissChatBanner = () => {
    try{ localStorage.setItem(chatBannerKey, '1'); }catch(e){}
    setChatBannerDismissed(true);
  };
  // Modal state: holds the pending slot the user is trying to claim. When
  // non-null we show the "link first?" modal. Cleared when user picks a path.
  const [pendingClaimSlot, setPendingClaimSlot] = useState(null);
  const [claimLinkInput, setClaimLinkInput] = useState('');
  const [claimLinkErr, setClaimLinkErr] = useState('');
  const [claimLinkBusy, setClaimLinkBusy] = useState(false);
  // Wrapper around claimSlot — for unlinked users, route through the "link
  // first?" modal so they hit the nudge at the highest-impact moment. Linked
  // users skip the modal entirely.
  const handleClaimClick = (slot) => {
    if(sleeperLinkedUser?.user_id){
      claimSlot(slot);
    } else {
      setClaimLinkInput('');
      setClaimLinkErr('');
      setPendingClaimSlot(slot);
    }
  };
  const claimLinkSubmit = async () => {
    setClaimLinkErr('');
    const q = (claimLinkInput||'').trim();
    if(!q){ setClaimLinkErr('Enter your Sleeper username'); return; }
    setClaimLinkBusy(true);
    try{
      const u = await lookupResolveUser(q);
      setSleeperLinkedUser({ user_id: u.user_id, username: u.username, display_name: u.display_name || u.username, avatar: u.avatar || null });
      const slotToClaim = pendingClaimSlot;
      setPendingClaimSlot(null);
      setClaimLinkInput('');
      // Defer claim so the new sleeperLinkedUser state has flushed — claimSlot
      // reads sleeperLinkedUser via closure to tag the team with sleeper_user_id.
      // Without the defer the closure would still see null and skip the tag.
      setTimeout(() => claimSlot(slotToClaim), 50);
    }catch(e){
      setClaimLinkErr(e.message || 'Could not find that Sleeper user');
    }finally{
      setClaimLinkBusy(false);
    }
  };
  const claimLinkSkip = () => {
    const slotToClaim = pendingClaimSlot;
    setPendingClaimSlot(null);
    claimSlot(slotToClaim);
  };
  const [claimUser,setClaimUser] = useState('');
  const [claimPass,setClaimPass] = useState('');
  const [claimErr,setClaimErr] = useState('');
  const [pickErr,setPickErr] = useState('');
  const [poolFilter,setPoolFilter] = useState('');
  // Pool tab: 'all' shows every undrafted asset; QB/RB/WR/TE filter players by
  // position; 'picks' shows draft picks only. Default 'all' so users see the
  // whole pool out of the box without having to hunt through tabs.
  const [poolTab,setPoolTab] = useState('all');
  // Bottom-sheet tab (Sleeper-style): 'players' shows the available pool, 'team'
  // shows a manager's roster. Default to 'players' so users immediately see who
  // they can draft. Tapping a team's avatar in the strip jumps to 'team' with
  // that manager pre-selected.
  const [sheetTab,setSheetTab] = useState('players');
  const [teamTabSlot,setTeamTabSlot] = useState(null); // which manager's roster to show in TEAM tab
  const [pickConfirm,setPickConfirm] = useState(null); // pool item awaiting Yes/No
  const [linkCopied,setLinkCopied] = useState(false);
  const [fcValues,setFcValues] = useState(null); // {bySleeperId, byName} from FantasyCalc — silent sort
  // Fetch FC values calibrated to the linked Sleeper league's actual format
  // (numQbs/numTeams/ppr/tepLevel) so 8/14/16-team or 1-QB leagues sort the
  // pool correctly. Re-runs when sleeper_league_id changes (typical: once,
  // on first draft load). Custom-mode drafts (no sleeper_league_id) fall
  // back to the default 12-team Superflex 1.0 PPR ranking.
  useEffect(()=>{
    let cancelled = false;
    (async () => {
      let fmt;
      const lid = draft?.sleeper_league_id;
      if(lid){
        try{
          const lg = await fetch(`${SLEEPER}/league/${lid}`, {cache:'force-cache'}).then(r=>r.ok?r.json():null);
          if(lg) fmt = fcFormatFromSleeperLeague(lg);
        }catch(e){}
      }
      if(cancelled) return;
      const result = await fetchFcValues(fmt);
      if(!cancelled) setFcValues(result);
    })();
    return ()=>{ cancelled = true; };
  },[draft?.sleeper_league_id]);
  // Sleeper avatar lookup keyed by user_id. Populated by a debounced effect
  // that runs whenever a new linked team appears in the draft, so claimants
  // who linked Sleeper render with their actual Sleeper profile picture in the
  // team-strip circle. Falls back to first-letter initial when missing/unset.
  // Cached aggressively (force-cache) — avatar IDs rarely change.
  const [teamAvatars,setTeamAvatars] = useState({}); // { [user_id]: avatar_hash | null }
  // Track in-flight auto-pick attempts so we don't double-fire on the same pick index.
  const autoPickAttempted = useRef(new Set());
  // Per-user auto-pick (Phase 2): tracks which current_pick_idx values this
  // client has already attempted to fire from the queue, so toggling auto-pick
  // off/on or a draft state refresh doesn't re-fire on the same idx. Separate
  // from autoPickAttempted (which gates the draft-level deadline auto-pick).
  const myAutoPickAttempted = useRef(new Set());
  // Cross-tab fallback (any tab fires per-user auto-pick at ~3s when on-clock
  // user's tab is closed). Separate ref so the on-clock user's own 1.5s
  // attempt doesn't lock out the cross-tab fallback or vice versa.
  const crossTabAutoPickAttempted = useRef(new Set());
  const [pendingAutoPick,setPendingAutoPick] = useState(null);
  // Phase 3: Realtime presence — { [slotKey]: [{slot, auto_pick, presence_ref, ...}, ...] }
  // Drives the ⚡ AUTO badge under each connected user's avatar. A slot with no
  // entries means that user's tab is closed; the badge disappears for everyone.
  const [presenceMap,setPresenceMap] = useState({});
  const presenceChannelRef = useRef(null);
  // Latest auto_pick value, kept in a ref so the subscribe callback (which
  // captures the initial value at channel-creation time) can read the most
  // recent toggle state when it fires its initial track().
  const autoPickRef = useRef(false);
  // 1-second tick for the live timer countdown (only ticks when live + deadline set)
  const [now,setNow] = useState(()=>Date.now());
  useEffect(()=>{
    const t = setInterval(()=>setNow(Date.now()), 1000);
    return ()=>clearInterval(t);
  },[]);
  // Refs must be at top of component (before any conditional returns) per React Rules of Hooks.
  const rosterRefs = useRef({});

  // Initial load + Supabase realtime subscription. Falls back to polling every
  // 15s as a safety net in case the websocket drops.
  useEffect(()=>{
    let cancelled = false;
    const load = async () => {
      const d = await dispFetch(draftId);
      if(!cancelled){ setDraft(d); setLoading(false); }
    };
    load();
    let channel = null;
    if(sb && sb.channel){
      channel = sb.channel('dispersal_'+draftId)
        .on('postgres_changes', {event:'UPDATE', schema:'public', table:'dispersal_drafts', filter:`id=eq.${draftId}`}, (payload)=>{
          if(cancelled) return;
          // Use payload.new only if it actually contains parsed JSONB arrays — Supabase
          // realtime sometimes truncates large rows or returns JSONB columns as strings.
          // In any partial / suspect case, fall back to a fresh fetch so we never render
          // picks that can't find their pool item ("(missing)" for ~10s until polling).
          const n = payload?.new;
          const ok = n && Array.isArray(n.pool) && Array.isArray(n.picks) && Array.isArray(n.teams);
          if(ok) setDraft(n);
          else load();
        })
        .subscribe();
    }
    const t = setInterval(load, 15000);
    return ()=>{ cancelled = true; clearInterval(t); if(channel && sb?.removeChannel) sb.removeChannel(channel); };
  },[draftId]);

  // Tier S — silent auto-recover: when the draft loads, if the user has no
  // localStorage claim BUT they have a linked Sleeper account AND a team in
  // this draft is tagged with their sleeper_user_id, restore them silently.
  // This is the "lockout never happens for linked users" payoff.
  // Tight defensive conditions:
  //   - me must be null (don't override an active claim)
  //   - sleeperLinkedUser must exist (user has linked Sleeper)
  //   - draft.teams must have a team with matching sleeper_user_id
  // Spectator mode bypasses this (spectators shouldn't auto-claim).
  useEffect(() => {
    if(isSpectator) return;
    if(me) return;
    if(!sleeperLinkedUser?.user_id) return;
    if(!draft?.teams) return;
    const myTeam = draft.teams.find(t => t.sleeper_user_id === sleeperLinkedUser.user_id && t.joined);
    if(!myTeam) return;
    const meNew = { slot: myTeam.slot, username: myTeam.username };
    try{ localStorage.setItem('pfk_disp_'+draftId, JSON.stringify(meNew)); }catch(e){}
    setMe(meNew);
  },[me, sleeperLinkedUser?.user_id, draft?.teams, isSpectator, draftId]);

  // Tier S — silent upgrade: when the user has a valid localStorage claim AND
  // a linked Sleeper account AND their team isn't yet tagged with their
  // sleeper_user_id, opportunistically tag it in the background. Protects them
  // from future lockouts on the next load. Fires once per page load.
  useEffect(() => {
    if(!me || !sleeperLinkedUser?.user_id || !draft?.teams) return;
    const myTeam = draft.teams.find(t => t.slot === me.slot);
    if(!myTeam || myTeam.sleeper_user_id === sleeperLinkedUser.user_id) return; // nothing to upgrade
    const newTeams = draft.teams.map(t => t.slot===me.slot ? {...t, sleeper_user_id: sleeperLinkedUser.user_id} : t);
    setDraft(d => d ? {...d, teams:newTeams} : d);
    dispUpdate(draftId, { teams:newTeams }).catch(()=>{}); // fire-and-forget
  },[me?.slot, sleeperLinkedUser?.user_id, draft?.teams, draftId]);

  // Pull Sleeper avatar hashes for any team linked via sleeper_user_id. Runs
  // whenever the set of linked users in the draft changes. Each fetch is
  // independently cached + recoverable; one network error doesn't blow away
  // the whole map. Map value of `null` means "we tried, no avatar set" — keeps
  // us from refetching the same user repeatedly.
  useEffect(() => {
    if(!Array.isArray(draft?.teams)) return;
    const linkedIds = draft.teams.map(t => t.sleeper_user_id).filter(Boolean);
    const missing = linkedIds.filter(id => !(id in teamAvatars));
    if(missing.length === 0) return;
    let cancelled = false;
    Promise.all(missing.map(id =>
      fetch(`https://api.sleeper.app/v1/user/${id}`, {cache:'force-cache'})
        .then(r => r.ok ? r.json() : null)
        .then(u => [id, u?.avatar || null])
        .catch(() => [id, null])
    )).then(pairs => {
      if(cancelled) return;
      setTeamAvatars(prev => {
        const next = {...prev};
        pairs.forEach(([id, av]) => { next[id] = av; });
        return next;
      });
    });
    return () => { cancelled = true; };
  },[draft?.teams?.map(t => t.sleeper_user_id||'').join('|')]);

  const claimSlot = async (slot) => {
    setClaimErr('');
    const team = draft.teams.find(t => t.slot===slot);
    if(!team){ setClaimErr('Team not found'); return; }
    if(team.joined){ setClaimErr('Already claimed — pick another team'); return; }
    // Tag the team with the user's Sleeper user_id when linked so we can
    // silently auto-recover their claim if their localStorage ever evaporates.
    // Field is optional — unlinked users still claim normally.
    const tagged = sleeperLinkedUser?.user_id ? { joined:true, sleeper_user_id: sleeperLinkedUser.user_id } : { joined:true };
    const newTeams = draft.teams.map(t => t.slot===slot ? {...t, ...tagged} : t);
    const meNew = { slot:team.slot, username:team.username };
    setDraft(d => d ? {...d, teams:newTeams} : d); // optimistic
    try{ localStorage.setItem('pfk_disp_'+draftId, JSON.stringify(meNew)); }catch(e){}
    setMe(meNew);
    const { error } = await dispUpdate(draftId, { teams:newTeams });
    if(error){ setClaimErr(error.message || 'Claim failed'); return; }
  };
  const releaseSlot = async (slot) => {
    if(!confirm('Release this team so someone else can claim it?')) return;
    const newTeams = draft.teams.map(t => t.slot===slot ? {...t, joined:false} : t);
    setDraft(d => d ? {...d, teams:newTeams} : d); // optimistic
    if(me && me.slot === slot){
      try{ localStorage.removeItem('pfk_disp_'+draftId); }catch(e){}
      setMe(null);
    }
    await dispUpdate(draftId, { teams:newTeams });
  };

  // Recover a claim — for users who got locked out (cleared cache, switched
  // device, etc.) and the team's still marked joined in the DB but their
  // local "me" identity is gone. Sets me to that slot so they can draft again.
  // Trust-based: anyone on the page can technically click this. In practice
  // dispersal participants are in a Discord/text thread, so abuse is socially
  // visible. Confirmation copy makes the intent crystal-clear.
  const recoverSlot = async (slot) => {
    const team = draft.teams.find(t => t.slot===slot);
    if(!team) return;
    const ok = confirm(`Take over team "${team.username}"?\n\nUse this ONLY if you're the original manager who claimed this team but lost your session (cleared cache, switched browser, switched device, etc.). This sets you as the active claimer for this team going forward.`);
    if(!ok) return;
    // If this user already had a different slot claimed, release it so they don't
    // hold two simultaneously.
    let newTeams = draft.teams;
    let needsSync = false;
    if(me && me.slot !== slot){
      newTeams = newTeams.map(t => t.slot===me.slot ? {...t, joined:false} : t);
      needsSync = true;
    }
    // Re-tag with sleeper_user_id on recover too, so future evictions auto-recover.
    if(sleeperLinkedUser?.user_id){
      newTeams = newTeams.map(t => t.slot===slot ? {...t, sleeper_user_id: sleeperLinkedUser.user_id} : t);
      needsSync = true;
    }
    setDraft(d => d ? {...d, teams:newTeams} : d);
    const meNew = { slot:team.slot, username:team.username };
    try{ localStorage.setItem('pfk_disp_'+draftId, JSON.stringify(meNew)); }catch(e){}
    setMe(meNew);
    if(needsSync) await dispUpdate(draftId, { teams:newTeams });
  };

  // Two-step start: lobby → ready (commish + everyone now in the draft view) → live (picks begin)
  const goToDraft = async () => {
    if(draft.teams.some(t=>!t.joined)){
      if(!confirm('Some managers haven\'t joined yet. Move to draft anyway?')) return;
    }
    await dispUpdate(draftId, { status:'ready' });
  };
  const startDraft = async () => {
    const deadline = draft.timer_seconds ? new Date(Date.now() + draft.timer_seconds*1000).toISOString() : null;
    setDraft(d => d ? {...d, status:'live', pick_deadline:deadline} : d); // optimistic
    await dispUpdate(draftId, { status:'live', pick_deadline:deadline });
  };

  const makePick = async (poolItemId, slotOverride) => {
    setPickErr('');
    // slotOverride lets the commish make a pick on behalf of the on-clock team
    // (used by the "DRAFT FOR @username" button when a manager is stuck).
    // For normal picks, slotOverride is undefined and we use me.slot.
    const pickerSlot = (typeof slotOverride === 'number') ? slotOverride : me?.slot;
    if(pickerSlot == null){ setPickErr('You must claim a team first'); return; }
    const onClockSlot = dispSlotForPick(draft.current_pick_idx, teams.length, draft.snake, draft.pick_order);
    if(onClockSlot !== pickerSlot){ setPickErr('Not the on-clock team'); return; }
    const item = draft.pool.find(p => p.id === poolItemId);
    if(!item || item.drafted){ setPickErr('Already drafted'); return; }
    const newPool = draft.pool.map(p => p.id===poolItemId ? {...p, drafted:true} : p);
    const newPicks = [...draft.picks, { pickIdx:draft.current_pick_idx, slot:pickerSlot, poolItemId, ts:new Date().toISOString() }];
    const newIdx = draft.current_pick_idx + 1;
    const totalPicks = draft.pool.length;
    const newStatus = newIdx >= totalPicks ? 'complete' : 'live';
    // If the draft is paused, the next team's clock should also be paused —
    // their captured remaining_ms is set to a full timer so when the draft
    // unpauses they get the full clock. If we're not paused, deadline ticks
    // normally and paused_remaining_ms stays null.
    const isPaused = !!draft.paused;
    const newDeadline = (newStatus==='live' && draft.timer_seconds && !isPaused) ? new Date(Date.now() + draft.timer_seconds*1000).toISOString() : null;
    const newPausedRemaining = (newStatus==='live' && draft.timer_seconds && isPaused) ? draft.timer_seconds*1000 : null;
    setDraft(d => d ? {...d, pool:newPool, picks:newPicks, current_pick_idx:newIdx, status:newStatus, pick_deadline:newDeadline, paused_remaining_ms:newPausedRemaining} : d); // optimistic
    const { error } = await dispUpdate(draftId, {
      pool:newPool, picks:newPicks, current_pick_idx:newIdx,
      status:newStatus, pick_deadline:newDeadline, paused_remaining_ms:newPausedRemaining,
    });
    if(error){ setPickErr(error.message || 'Pick failed'); return; }
  };

  // "switch" button — releases the user's current team so they can claim a
  // different one. Three things have to happen, in order, or you get stuck:
  //   1. Mark the team as not joined in the DB
  //   2. Clear the sleeper_user_id tag so Tier S auto-recover doesn't snap you
  //      back to the same team on the next render
  //   3. Clear localStorage + me state
  // Confirms first since releasing a team is a meaningful action.
  const signOut = async () => {
    if(me && draft?.teams){
      if(!confirm('Switch teams? Your current team will be released so you can claim a different one.')) return;
      const newTeams = draft.teams.map(t => t.slot===me.slot ? {...t, joined:false, sleeper_user_id:undefined} : t);
      setDraft(d => d ? {...d, teams:newTeams} : d); // optimistic
      try{ localStorage.removeItem('pfk_disp_'+draftId); }catch(e){}
      setMe(null);
      await dispUpdate(draftId, { teams:newTeams });
    } else {
      // No active claim — just nuke the local state (rare path, defensive).
      try{ localStorage.removeItem('pfk_disp_'+draftId); }catch(e){}
      setMe(null);
    }
  };

  // Draft-level auto-pick — fires when the commish's draft.auto_pick is on
  // AND the pick clock has expired. This is the deadline-only fallback for
  // teams that didn't personally opt into auto-pick (claimed without
  // personal toggle, or unclaimed). Honors the on-clock team's queue first
  // (if claimed and has one), falling back to top FC available. Unclaimed
  // teams have no user_state, so they go straight to top FC.
  //
  // Pause behavior: ANY pause (manual OR auto/overnight) blocks this
  // effect. Overnight auto-pause is meant for sleeping humans — only
  // teams that explicitly opted in via personal auto-pick should fire
  // during the window (handled by the per-user effects below). The
  // draft-level fallback only applies to clock-expiry, never to overnight
  // pause. Race-safe via .eq('current_pick_idx', idx).
  useEffect(()=>{
    if(!draft || draft.status !== 'live' || !draft.auto_pick) return;
    if(draft.paused) return;
    if(!draft.pick_deadline) return;
    const deadlineMs = new Date(draft.pick_deadline).getTime();
    if(now < deadlineMs) return;
    const idx = draft.current_pick_idx || 0;
    if(autoPickAttempted.current.has(idx)) return;
    const delay = 200 + Math.floor(Math.random()*800);
    const t = setTimeout(async () => {
      // Mark attempted INSIDE the timeout (not at scheduling) so a state
      // change that cancels via cleanup doesn't permanently lock idx — a
      // re-run on the new state can re-schedule. The .eq('current_pick_idx',
      // idx) filter on the update is the actual race safeguard.
      autoPickAttempted.current.add(idx);
      const onClock = dispSlotForPick(idx, (draft.teams||[]).length, draft.snake, draft.pick_order||[]);
      if(onClock == null) return;
      // Queue-first: if the on-clock slot is claimed and has an undrafted
      // queued player, take that. Otherwise top FC available.
      const slotState = (draft.user_state || {})[String(onClock)] || null;
      let target = null;
      const queueIds = slotState?.queue || [];
      for(const id of queueIds){
        const it = (draft.pool || []).find(p => p.id === id);
        if(it && !it.drafted){ target = it; break; }
      }
      if(!target){
        const available = (draft.pool||[]).filter(p => !p.drafted && p.type !== 'pick');
        if(!available.length) return;
        const valueOf = (it) => {
          if(!fcValues) return 0;
          if(it.sleeperId && fcValues.bySleeperId[it.sleeperId] != null) return fcValues.bySleeperId[it.sleeperId];
          const stripped = (it.name || '').replace(/\s*\([^)]*\)\s*$/, '');
          const k = normDraftName(stripped);
          return fcValues.byName[k] != null ? fcValues.byName[k] : -1;
        };
        target = available.slice().sort((a,b)=>valueOf(b)-valueOf(a))[0];
      }
      if(!target) return;
      const newPool = (draft.pool||[]).map(p => p.id===target.id ? {...p, drafted:true} : p);
      const newPicks = [...(draft.picks||[]), { pickIdx:idx, slot:onClock, poolItemId:target.id, ts:new Date().toISOString(), auto:true, autoMode:'deadline' }];
      const newIdx = idx + 1;
      const totalSlots = (draft.pool||[]).length;
      const newStatus = newIdx >= totalSlots ? 'complete' : 'live';
      const newDeadline = (newStatus==='live' && draft.timer_seconds) ? new Date(Date.now() + draft.timer_seconds*1000).toISOString() : null;
      await sb.from('dispersal_drafts').update({
        pool:newPool, picks:newPicks, current_pick_idx:newIdx,
        status:newStatus, pick_deadline:newDeadline,
        updated_at:new Date().toISOString(),
      }).eq('id', draftId).eq('current_pick_idx', idx);
    }, delay);
    return ()=>clearTimeout(t);
  },[draft, now, fcValues, draftId]);

  // Cross-tab per-user auto-pick — fires ~3s after going on-clock from ANY
  // open tab when the on-clock slot has user_state[slot].auto_pick = true.
  // Picks the top of that user's queue, falls back to highest-FC available
  // non-pick. See TODO.md for server-side migration plan.
  //
  // Why this exists: the on-clock user's own tab fires its own per-user
  // effect at 1.5s (line below). If that user's tab is CLOSED, no fire
  // happened — and waiting for the deadline (could be 8 hours) is useless.
  // This effect runs from every connected client and fires at 3s + jitter,
  // giving the on-clock user's own tab a head start. Race-safety via
  // .eq('current_pick_idx', idx): the first update wins, the rest no-op.
  // attempted is marked INSIDE the timeout (not at scheduling) so re-runs
  // from realtime updates don't permanently lock out a slot.
  useEffect(()=>{
    if(!draft || draft.status !== 'live') return;
    // Manual pause blocks everything; auto-pause does NOT (we still want to
    // advance sleeping humans through the queue overnight).
    if(draft.paused && draft.paused_reason === 'manual') return;
    const idx = draft.current_pick_idx || 0;
    const onClock = dispSlotForPick(idx, (draft.teams||[]).length, draft.snake, draft.pick_order||[]);
    if(onClock == null) return;
    const slotState = (draft.user_state || {})[String(onClock)] || null;
    if(!slotState?.auto_pick) return;
    if(crossTabAutoPickAttempted.current.has(idx)) return;
    // 3.0–3.5s: deliberately later than the on-clock user's own 1.5s effect
    // so their tab wins when present. Adds 0–500ms jitter so multiple
    // fallback tabs don't all hit Postgres in the same ms.
    const delay = 3000 + Math.floor(Math.random()*500);
    const t = setTimeout(async () => {
      crossTabAutoPickAttempted.current.add(idx);
      // Recompute on-clock + slot state from latest draft (defensive)
      const slotStateNow = (draft.user_state || {})[String(onClock)] || null;
      if(!slotStateNow?.auto_pick) return;
      let target = null;
      const queueIds = slotStateNow.queue || [];
      for(const id of queueIds){
        const it = (draft.pool || []).find(p => p.id === id);
        if(it && !it.drafted){ target = it; break; }
      }
      if(!target){
        const available = (draft.pool || []).filter(p => !p.drafted && p.type !== 'pick');
        if(!available.length) return;
        const valueOf = (it) => {
          if(!fcValues) return 0;
          if(it.sleeperId && fcValues.bySleeperId[it.sleeperId] != null) return fcValues.bySleeperId[it.sleeperId];
          const stripped = (it.name || '').replace(/\s*\([^)]*\)\s*$/, '');
          const k = normDraftName(stripped);
          return fcValues.byName[k] != null ? fcValues.byName[k] : -1;
        };
        target = available.slice().sort((a,b)=>valueOf(b)-valueOf(a))[0];
      }
      if(!target) return;
      const newPool = (draft.pool || []).map(p => p.id===target.id ? {...p, drafted:true} : p);
      const newPicks = [...(draft.picks || []), { pickIdx:idx, slot:onClock, poolItemId:target.id, ts:new Date().toISOString(), auto:true, autoMode:'cross-tab' }];
      const newIdx = idx + 1;
      const totalSlots = (draft.pool || []).length;
      const newStatus = newIdx >= totalSlots ? 'complete' : 'live';
      // Preserve auto-pause across the pick — next team also starts in
      // auto-pause with a fresh full timer captured.
      const stillAutoPaused = !!draft.paused && draft.paused_reason === 'auto' && newStatus === 'live';
      const newDeadline = (newStatus==='live' && draft.timer_seconds && !stillAutoPaused) ? new Date(Date.now() + draft.timer_seconds*1000).toISOString() : null;
      const newPausedRemaining = stillAutoPaused && draft.timer_seconds ? draft.timer_seconds*1000 : null;
      await sb.from('dispersal_drafts').update({
        pool:newPool, picks:newPicks, current_pick_idx:newIdx,
        status:newStatus, pick_deadline:newDeadline, paused_remaining_ms:newPausedRemaining,
        updated_at:new Date().toISOString(),
      }).eq('id', draftId).eq('current_pick_idx', idx);
    }, delay);
    return () => clearTimeout(t);
  },[draft, draftId, fcValues]);

  // Per-user auto-pick (Phase 2). Fires ~1.5s after THIS client's user goes
  // on-clock when their personal auto_pick toggle is on. Picks the top
  // undrafted item from their queue; if the queue is empty / all drafted,
  // falls back to highest FC-valued non-pick available player. Same race-
  // safety pattern as the draft-level auto-pick: direct Supabase update with
  // .eq('current_pick_idx', idx) so two tabs both firing only let one win.
  useEffect(()=>{
    if(!draft || draft.status !== 'live') return;
    // Manual pause blocks; auto-pause does NOT (overnight, advance through
    // sleeping humans automatically when their queue says what they want).
    if(draft.paused && draft.paused_reason === 'manual') return;
    if(me?.slot == null) return;
    const slotKey = String(me.slot);
    const myState = (draft.user_state || {})[slotKey];
    if(!myState?.auto_pick) return;
    const idx = draft.current_pick_idx || 0;
    const onClock = dispSlotForPick(idx, (draft.teams||[]).length, draft.snake, draft.pick_order||[]);
    if(onClock !== me.slot) return;
    if(myAutoPickAttempted.current.has(idx)) return;

    // Determine target — top undrafted queue item, else highest-FC fallback.
    const queueIds = myState.queue || [];
    let target = null;
    for(const id of queueIds){
      const it = (draft.pool || []).find(p => p.id === id);
      if(it && !it.drafted){ target = it; break; }
    }
    if(!target){
      const available = (draft.pool || []).filter(p => !p.drafted && p.type !== 'pick');
      if(!available.length) return;
      const valueOf = (it) => {
        if(!fcValues) return 0;
        if(it.sleeperId && fcValues.bySleeperId[it.sleeperId] != null) return fcValues.bySleeperId[it.sleeperId];
        const stripped = (it.name || '').replace(/\s*\([^)]*\)\s*$/, '');
        const k = normDraftName(stripped);
        return fcValues.byName[k] != null ? fcValues.byName[k] : -1;
      };
      target = available.slice().sort((a,b)=>valueOf(b)-valueOf(a))[0];
    }
    if(!target) return;

    setPendingAutoPick({ idx, item: target });

    const t = setTimeout(async () => {
      // Mark idx as attempted ONLY when the timeout actually fires. If we
      // marked it at scheduling time, an interim re-render (e.g., the
      // realtime event that confirms the toggle commit) would clear the
      // timeout via the cleanup function AND leave the attempted flag set,
      // making the second effect run silently bail and the pick never fire.
      myAutoPickAttempted.current.add(idx);
      const newPool = (draft.pool || []).map(p => p.id===target.id ? {...p, drafted:true} : p);
      const newPicks = [...(draft.picks || []), { pickIdx:idx, slot:me.slot, poolItemId:target.id, ts:new Date().toISOString(), auto:true, autoMode:'user' }];
      const newIdx = idx + 1;
      const totalSlots = (draft.pool || []).length;
      const newStatus = newIdx >= totalSlots ? 'complete' : 'live';
      const stillAutoPaused = !!draft.paused && draft.paused_reason === 'auto' && newStatus === 'live';
      const newDeadline = (newStatus==='live' && draft.timer_seconds && !stillAutoPaused) ? new Date(Date.now() + draft.timer_seconds*1000).toISOString() : null;
      const newPausedRemaining = stillAutoPaused && draft.timer_seconds ? draft.timer_seconds*1000 : null;
      await sb.from('dispersal_drafts').update({
        pool:newPool, picks:newPicks, current_pick_idx:newIdx,
        status:newStatus, pick_deadline:newDeadline, paused_remaining_ms:newPausedRemaining,
        updated_at:new Date().toISOString(),
      }).eq('id', draftId).eq('current_pick_idx', idx);
      setPendingAutoPick(null);
    }, 1500);

    return () => {
      clearTimeout(t);
      setPendingAutoPick(null);
    };
  },[draft, me, fcValues, draftId]);

  // Auto-pause boundary detection (Piece 3). Any client viewing the draft
  // checks the wall clock against the configured window every render tick
  // (~1s) and flips paused state when crossings happen. Race-safe via
  // .eq('paused', current_value) so only the first tab to land wins —
  // others no-op. Manual (commish) pauses are NEVER touched by this effect:
  //  - When entering window: only flips if NOT already paused.
  //  - When leaving window: only flips if paused_reason==='auto' (so we
  //    don't accidentally resume a manual pause).
  useEffect(() => {
    if(!draft || draft.status !== 'live') return;
    if(!draft.auto_pause_enabled) return;
    if(!draft.auto_pause_start_local || !draft.auto_pause_end_local || !draft.auto_pause_tz) return;
    const inWindow = dispIsInAutoPauseWindow(now, draft.auto_pause_start_local, draft.auto_pause_end_local, draft.auto_pause_tz);
    const isPaused = !!draft.paused;
    const reason = draft.paused_reason || null;
    if(inWindow && !isPaused){
      // Engage auto-pause. Capture remaining_ms so resume picks up where left off.
      const deadlineMs = draft.pick_deadline ? new Date(draft.pick_deadline).getTime() : null;
      const remaining = (deadlineMs && draft.timer_seconds) ? Math.max(0, deadlineMs - Date.now()) : (draft.timer_seconds ? draft.timer_seconds*1000 : null);
      // Optimistic local update so the firing tab's UI flips to PAUSED
      // immediately, instead of waiting for the realtime broadcast to
      // round-trip back. Matches the pattern used by pauseDraft/resumeDraft/
      // toggleAutoPickMidDraft etc. The .eq('paused', false) filter on the
      // SQL still handles cross-tab race-safety; if our write is a no-op
      // because another tab beat us, the realtime ping will still confirm
      // the same paused state, so local stays consistent.
      setDraft(d => d ? {...d, paused:true, paused_reason:'auto', paused_remaining_ms:remaining, pick_deadline:null} : d);
      sb.from('dispersal_drafts').update({
        paused:true, paused_reason:'auto', paused_remaining_ms:remaining, pick_deadline:null,
        updated_at:new Date().toISOString(),
      }).eq('id', draftId).eq('paused', false).then(()=>{}, ()=>{});
    } else if(!inWindow && isPaused && reason === 'auto'){
      // Release auto-pause. Rebuild deadline = now + remaining (or full timer).
      const remaining = (typeof draft.paused_remaining_ms === 'number') ? draft.paused_remaining_ms : (draft.timer_seconds ? draft.timer_seconds*1000 : null);
      const newDeadline = (remaining != null && draft.timer_seconds) ? new Date(Date.now() + remaining).toISOString() : null;
      // Optimistic local update — same reasoning as the engage branch.
      setDraft(d => d ? {...d, paused:false, paused_reason:null, paused_remaining_ms:null, pick_deadline:newDeadline} : d);
      sb.from('dispersal_drafts').update({
        paused:false, paused_reason:null, paused_remaining_ms:null, pick_deadline:newDeadline,
        updated_at:new Date().toISOString(),
      }).eq('id', draftId).eq('paused_reason', 'auto').then(()=>{}, ()=>{});
    }
  },[draft, now, draftId]);

  // myUserState is hoisted here (from its semantic home further down in
  // this function) because the useEffects below reference it in their
  // deps arrays. Under the original text/babel transform, late `const`
  // declarations got compiled to `var` and the hoisted-to-undefined
  // behavior masked this ordering issue. Under the Vite/esbuild build,
  // `const` keeps its TDZ semantics and accessing the variable before
  // declaration throws ReferenceError. Declaration must precede every
  // closure that captures it. Build-step-migration cleanup, 2026-05-21.
  const myUserState = (me?.slot != null && draft?.user_state) ? (draft.user_state[String(me.slot)] || null) : null;

  // Phase 3: Supabase Realtime presence — broadcasts { slot, auto_pick } so
  // every connected client can render a ⚡ AUTO badge under each manager whose
  // tab is currently connected AND has personal auto-pick on. Disconnect (tab
  // close, network drop) auto-untracks; the badge disappears for everyone in
  // ~1-2s without us doing anything explicit. Spectators / unclaimed users
  // don't subscribe — nothing meaningful to broadcast.
  useEffect(() => {
    autoPickRef.current = !!myUserState?.auto_pick;
  }, [myUserState?.auto_pick]);
  useEffect(() => {
    if(me?.slot == null || !sb || !sb.channel) {
      setPresenceMap({});
      return;
    }
    const slot = me.slot;
    const channel = sb.channel('disp_pres_'+draftId, {
      config: { presence: { key: String(slot) } }
    });
    presenceChannelRef.current = channel;
    const refreshState = () => {
      try { setPresenceMap(channel.presenceState() || {}); } catch(e) {}
    };
    channel
      .on('presence', { event: 'sync' }, refreshState)
      .on('presence', { event: 'join' }, refreshState)
      .on('presence', { event: 'leave' }, refreshState)
      .subscribe(async (status) => {
        if(status === 'SUBSCRIBED'){
          try { await channel.track({ slot, auto_pick: autoPickRef.current }); } catch(e) {}
          refreshState();
        }
      });
    return () => {
      presenceChannelRef.current = null;
      try { sb.removeChannel(channel); } catch(e) {}
    };
  }, [draftId, me?.slot]);
  // Re-track on auto_pick toggle without re-subscribing the channel. The
  // direct channel.track() call inside updateMyUserState is the primary
  // broadcast path; this effect is a fallback for any state changes that
  // don't go through that handler (e.g., a future programmatic setDraft).
  useEffect(() => {
    const channel = presenceChannelRef.current;
    if(!channel || me?.slot == null) return;
    try { channel.track({ slot: me.slot, auto_pick: !!myUserState?.auto_pick }); } catch(e) {}
  }, [myUserState, me?.slot]);

  // Commish controls — anyone with the (non-spectator) link can use these.
  // Trade off some security for "any league mate can rescue a stuck draft."
  const undoLastPick = async () => {
    if(!draft.picks?.length){ alert('No picks to undo.'); return; }
    if(!confirm('Undo the last pick?')) return;
    const last = draft.picks[draft.picks.length-1];
    const newPicks = draft.picks.slice(0,-1);
    const newPool = (draft.pool||[]).map(p => p.id===last.poolItemId ? {...p, drafted:false} : p);
    const newIdx = Math.max(0, (draft.current_pick_idx||0) - 1);
    // Preserve pause state on undo — the team that was on the clock returns
    // with a fresh full clock (consistent with how mid-draft timer changes
    // work), and if the draft was paused, the clock stays paused.
    const isPaused = !!draft.paused;
    const newDeadline = (isPaused || !draft.timer_seconds) ? null : new Date(Date.now() + draft.timer_seconds*1000).toISOString();
    const newPausedRemaining = (isPaused && draft.timer_seconds) ? draft.timer_seconds*1000 : null;
    await dispUpdate(draftId, { pool:newPool, picks:newPicks, current_pick_idx:newIdx, status:'live', pick_deadline:newDeadline, paused_remaining_ms:newPausedRemaining });
  };
  const forceSkip = async () => {
    if((draft.status||'lobby')!=='live'){ alert('Draft not live.'); return; }
    if(!confirm('Skip the on-clock manager? Their pick is forfeited.')) return;
    const newIdx = (draft.current_pick_idx||0) + 1;
    const totalSlots = (draft.pool||[]).length;
    const newStatus = newIdx >= totalSlots ? 'complete' : 'live';
    const isPaused = !!draft.paused;
    const newDeadline = (newStatus==='live' && draft.timer_seconds && !isPaused) ? new Date(Date.now() + draft.timer_seconds*1000).toISOString() : null;
    const newPausedRemaining = (newStatus==='live' && draft.timer_seconds && isPaused) ? draft.timer_seconds*1000 : null;
    await dispUpdate(draftId, { current_pick_idx:newIdx, status:newStatus, pick_deadline:newDeadline, paused_remaining_ms:newPausedRemaining });
  };
  const endDraft = async () => {
    if(!confirm('Force the draft to complete now? Remaining pool items stay undrafted.')) return;
    await dispUpdate(draftId, { status:'complete', pick_deadline:null });
  };
  const reopenDraft = async () => {
    if(!confirm('Reopen the draft? Anyone whose turn it is can resume picking.')) return;
    // Reopen always brings the draft back live and unpaused with a fresh clock.
    const newDeadline = draft.timer_seconds ? new Date(Date.now() + draft.timer_seconds*1000).toISOString() : null;
    await dispUpdate(draftId, { status:'live', pick_deadline:newDeadline, paused:false, paused_reason:null, paused_remaining_ms:null });
  };
  // Mid-draft pick-clock change. Commish-only (UI gates the call). Writes the
  // new timer_seconds to the dispersal_drafts row. When status is 'live', also
  // resets pick_deadline so the team currently on the clock gets a FRESH timer
  // starting now at the new duration — remaining seconds from the previous
  // clock are NOT carried over (per the spec). When status isn't 'live' (e.g.
  // 'ready' lobby or paused-via-ready), pick_deadline stays null and the new
  // timer kicks in when picks resume.
  const changeTimerMidDraft = async (newKey) => {
    const opt = DISP_TIMER_OPTIONS.find(o => o.value === newKey);
    if(!opt) return;
    const newSeconds = opt.seconds;
    const live = (draft?.status||'') === 'live';
    const isPaused = !!draft?.paused;
    // When live + not paused, the on-clock team gets a fresh ticking deadline.
    // When live + paused, no deadline; the captured remaining_ms resets to a
    // full timer at the new duration so resume gives them the new full clock.
    // When not live (lobby), neither field updates beyond timer_seconds.
    const newDeadline = (live && newSeconds && !isPaused) ? new Date(Date.now() + newSeconds*1000).toISOString() : null;
    const newPausedRemaining = (live && newSeconds && isPaused) ? newSeconds*1000 : (isPaused ? null : (draft?.paused_remaining_ms ?? null));
    setDraft(d => d ? {...d, timer_seconds:newSeconds, pick_deadline:newDeadline, paused_remaining_ms:newPausedRemaining} : d); // optimistic
    await dispUpdate(draftId, { timer_seconds:newSeconds, pick_deadline:newDeadline, paused_remaining_ms:newPausedRemaining });
  };
  // Toggle the draft-level auto-pick flag mid-draft. Mirrors the setup-form
  // checkbox so the commish can flip it on/off without recreating the draft.
  // Used together with the on-clock team's queue (queue first, top FC fallback)
  // when a deadline expires or an auto-pause window engages.
  const toggleAutoPickMidDraft = async () => {
    const next = !draft?.auto_pick;
    setDraft(d => d ? {...d, auto_pick:next} : d);
    await dispUpdate(draftId, { auto_pick:next });
  };
  // Update the auto-pause (overnight) window mid-draft. Pass partial fields;
  // unspecified fields keep their current value. Auto-detects the commish's
  // browser tz when enabling for the first time.
  //
  // Optimistic update reads the LATEST local state via the functional setDraft
  // updater (not the closure-captured `draft`), so two rapid changes — e.g.,
  // editing start and end inputs in quick succession — don't read stale `cur`
  // and clobber each other. SQL write only sends `patch` fields so parallel
  // writes targeting different columns can't overwrite each other; columns
  // not in patch stay as-is in the DB.
  const updateAutoPauseMidDraft = async (patch) => {
    setDraft(d => {
      if (!d) return d;
      const cur = {
        auto_pause_enabled: !!d.auto_pause_enabled,
        auto_pause_start_local: d.auto_pause_start_local || '22:00',
        auto_pause_end_local: d.auto_pause_end_local || '08:00',
        auto_pause_tz: d.auto_pause_tz || (Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'),
      };
      return { ...d, ...cur, ...patch };
    });
    await dispUpdate(draftId, patch);
  };
  // Pause freezes the active pick clock without leaving 'live' phase. We
  // capture the remaining_ms on the on-clock team's deadline so resume can
  // rebuild the deadline from where it left off — no fresh full clock.
  // Picks are still allowed while paused; the auto-pick effects bail when
  // draft.paused_reason==='manual' so commish pause stops everything. (The
  // 'auto' overnight pause does NOT block auto-pick — see Piece 3.)
  const pauseDraft = async () => {
    if((draft.status||'lobby')!=='live'){ alert('Draft not live.'); return; }
    if(draft.paused){ alert('Draft is already paused.'); return; }
    if(!confirm('Pause the draft? The clock stops and the on-clock manager waits until you resume.')) return;
    const deadlineMs = draft.pick_deadline ? new Date(draft.pick_deadline).getTime() : null;
    const remaining = (deadlineMs && draft.timer_seconds) ? Math.max(0, deadlineMs - Date.now()) : (draft.timer_seconds ? draft.timer_seconds*1000 : null);
    setDraft(d => d ? {...d, paused:true, paused_reason:'manual', paused_remaining_ms:remaining, pick_deadline:null} : d);
    await dispUpdate(draftId, { paused:true, paused_reason:'manual', paused_remaining_ms:remaining, pick_deadline:null });
  };
  // Resume rebuilds pick_deadline = now + paused_remaining_ms so the on-clock
  // team gets exactly the time they had left when the commish hit pause.
  const resumeDraft = async () => {
    if(!draft.paused){ alert('Draft is not paused.'); return; }
    const remaining = (typeof draft.paused_remaining_ms === 'number') ? draft.paused_remaining_ms : (draft.timer_seconds ? draft.timer_seconds*1000 : null);
    const newDeadline = (remaining != null && draft.timer_seconds) ? new Date(Date.now() + remaining).toISOString() : null;
    setDraft(d => d ? {...d, paused:false, paused_reason:null, paused_remaining_ms:null, pick_deadline:newDeadline, status:'live'} : d);
    await dispUpdate(draftId, { paused:false, paused_reason:null, paused_remaining_ms:null, pick_deadline:newDeadline, status:'live' });
  };
  const randomizeOrder = async () => {
    if(!confirm('Randomize the pick order?')) return;
    const order = (draft.pick_order||[]).slice();
    for(let i=order.length-1;i>0;i--){ const j=Math.floor(Math.random()*(i+1)); [order[i],order[j]]=[order[j],order[i]]; }
    setDraft(d => d ? {...d, pick_order:order} : d); // optimistic
    await dispUpdate(draftId, { pick_order:order });
  };

  // ===== Per-user player queue (Phase 1; auto_pick toggle persisted but
  // unused until Phase 2). Lives on draft.user_state (jsonb), keyed by team
  // slot. Drafted players are filtered at READ time only — we don't
  // proactively scrub anyone's queue, which avoids cross-user write races
  // every time anyone drafts. updateMyUserState writes the WHOLE user_state
  // jsonb back; that's fine because writes are scoped to one user's actions.
  //
  // myUserState itself moved up — see the hoist comment + declaration
  // earlier in this function. Build-step-migration cleanup, 2026-05-21.
  const myQueueIds = myUserState?.queue || [];
  const myQueueItems = myQueueIds
    .map(id => (draft?.pool || []).find(p => p.id === id))
    .filter(it => it && !it.drafted);
  const updateMyUserState = async (mutator) => {
    if(me?.slot == null) return;
    const slotKey = String(me.slot);
    const current = (draft?.user_state || {})[slotKey] || { queue: [], auto_pick: false };
    const next = mutator(current);
    if(next === current) return;
    const newUserState = { ...(draft?.user_state || {}), [slotKey]: next };
    setDraft(d => d ? {...d, user_state: newUserState} : d); // optimistic
    // Eagerly broadcast presence with the NEW auto_pick value so other clients
    // see the change without waiting on React's effect dep machinery. Belt-and-
    // suspenders: the useEffect re-track also fires, but this direct call is
    // the source of truth for presence freshness.
    try {
      const channel = presenceChannelRef.current;
      if(channel) channel.track({ slot: me.slot, auto_pick: !!next.auto_pick });
    } catch(e) {}
    await dispUpdate(draftId, { user_state: newUserState });
  };
  const isInMyQueue = (poolItemId) => myQueueIds.includes(poolItemId);
  const toggleQueue = (poolItemId) => updateMyUserState(s => {
    const q = (s.queue || []).slice();
    const i = q.indexOf(poolItemId);
    if(i >= 0) q.splice(i, 1); else q.push(poolItemId);
    return { ...s, queue: q };
  });
  const removeFromQueue = (poolItemId) => updateMyUserState(s => ({
    ...s, queue: (s.queue||[]).filter(id => id !== poolItemId)
  }));
  const moveQueueItem = (poolItemId, direction) => updateMyUserState(s => {
    const q = (s.queue || []).slice();
    const i = q.indexOf(poolItemId);
    if(i < 0) return s;
    const j = direction === 'up' ? i - 1 : i + 1;
    if(j < 0 || j >= q.length) return s;
    [q[i], q[j]] = [q[j], q[i]];
    return { ...s, queue: q };
  });

  if(loading) return <div style={{padding:40,textAlign:'center',color:'var(--text-secondary)'}}>Loading draft… <span style={{fontSize:11,color:'var(--text-secondary)'}}>(id: {draftId})</span></div>;
  if(!draft) return <div style={{padding:40,textAlign:'center',color:'var(--danger)'}}>Draft not found. <div style={{fontSize:11,color:'var(--text-secondary)',marginTop:6}}>id: {draftId}</div></div>;

  // Defensive defaults — if Supabase returns nulls or missing fields we still render.
  const teams = Array.isArray(draft.teams) ? draft.teams : [];
  const picks = Array.isArray(draft.picks) ? draft.picks : [];
  const pool  = Array.isArray(draft.pool)  ? draft.pool  : [];
  const pickOrder = Array.isArray(draft.pick_order) ? draft.pick_order : teams.map((_,i)=>i);
  const status = draft.status || 'lobby';

  // Compute roster per team. If "me" is claimed, push their team to the front so they
  // always see their own roster first.
  const rostersUnsorted = teams.map(t => ({
    ...t,
    picks: picks.filter(p => p.slot===t.slot).map(p => ({...p, item:pool.find(it=>it.id===p.poolItemId)}))
  }));
  const rosters = me
    ? [...rostersUnsorted.filter(r=>r.slot===me.slot), ...rostersUnsorted.filter(r=>r.slot!==me.slot)]
    : rostersUnsorted;

  const onClockSlot = status==='live' ? dispSlotForPick(draft.current_pick_idx||0, teams.length, draft.snake, pickOrder) : null;
  const onClockTeam = onClockSlot != null ? teams.find(t => t.slot === onClockSlot) : null;
  const myTurn = me && onClockSlot === me.slot;
  const round = Math.floor(draft.current_pick_idx / Math.max(1,teams.length)) + 1;
  const posInRound = (draft.current_pick_idx % Math.max(1,teams.length)) + 1;

  const screenshotRoster = async (slot, username) => {
    const el = rosterRefs.current[slot];
    if(!el || !window.html2canvas){ alert('Screenshot library not loaded yet — refresh and try again.'); return; }
    // Force dark theme on the captured subtree even when the user is in
    // light mode — share images should always be branded dark PFK. The
    // attribute scopes [data-theme="dark"] CSS variables locally; the
    // finally block restores the user's previous attribute regardless of
    // capture success or failure.
    const wasTheme = el.getAttribute('data-theme');
    el.setAttribute('data-theme', 'dark');
    try{
      const canvas = await window.html2canvas(el, { backgroundColor:'#080808', scale:2 });
      const filename = `${draft.name.replace(/[^a-z0-9]/gi,'_')}_${username}.png`;
      const blob = await new Promise(res => canvas.toBlob(res, 'image/png'));
      // Mobile: try Web Share API (opens native share sheet → Save to Photos / Files / Messages)
      if(blob && navigator.canShare){
        const file = new File([blob], filename, { type:'image/png' });
        if(navigator.canShare({ files:[file] })){
          try{
            await navigator.share({ files:[file], title:`${username} — ${draft.name}` });
            return;
          }catch(e){ if(e && e.name==='AbortError') return; /* fall through to download */ }
        }
      }
      // Desktop: download via anchor
      const url = blob ? URL.createObjectURL(blob) : canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = filename;
      link.href = url;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      if(blob) setTimeout(()=>URL.revokeObjectURL(url), 1000);
      // iOS Safari fallback: if download attr was ignored, open in new tab so user can long-press → Save Image
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      if(isIOS && !navigator.canShare){
        window.open(canvas.toDataURL('image/png'), '_blank');
      }
    }catch(e){ alert('Screenshot failed: ' + (e.message||e)); }
    finally {
      if(wasTheme === null) el.removeAttribute('data-theme'); else el.setAttribute('data-theme', wasTheme);
    }
  };

  // status==='lobby' has no bottom sheet so no extra bottom padding needed.
  // When the sheet is up, mirror its current height (+ a small buffer) so
  // content above isn't hidden under the sheet as the user drags it.
  const stickyRostersActive = status==='ready' || status==='live' || status==='complete';
  const sheetPadding = stickyRostersActive ? (sheetHeight + 14) : 14;
  return (
    <div style={{padding:'8px 14px',color:'var(--text-eee)',maxWidth:1240,margin:'0 auto',paddingBottom:sheetPadding}}>
      {/* Header */}
      <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:8,flexWrap:'wrap'}}>
        <div style={{fontSize:18,fontWeight:900,color:'var(--accent-gold-text)',letterSpacing:1.2}}>🎲 {draft.name}</div>
        <div style={{padding:'4px 10px',background:status==='live'?'var(--success-bg)':status==='complete'?'#1a1a3a':'#2a1a0a',border:'1px solid '+(status==='live'?'var(--success)':status==='complete'?'var(--pos-qb)':'var(--accent-gold)'),borderRadius:20,fontSize:11,fontWeight:800,letterSpacing:1.5,color:status==='live'?'var(--success)':status==='complete'?'var(--pos-qb)':'var(--accent-gold)'}}>{status.toUpperCase()}</div>
        <button onClick={()=>{
          try{ navigator.clipboard.writeText(window.location.origin + '/dispersal/' + draftId); }catch(e){}
          setLinkCopied(true);
          setTimeout(()=>setLinkCopied(false), 2000);
        }} style={{padding:'5px 11px',background:linkCopied?'var(--success)':'var(--accent-gold)',border:'none',borderRadius:6,color:'#000',fontWeight:900,cursor:'pointer',fontSize:12,letterSpacing:1}}>{linkCopied?'✓ COPIED!':'🔗 COPY LINK'}</button>
        <div style={{flex:1}}/>
        {isSpectator && <div style={{padding:'4px 10px',background:'var(--bg-pill)',border:'1px solid var(--accent-gold)',borderRadius:20,fontSize:11,fontWeight:800,letterSpacing:1.5,color:'var(--accent-gold-text)'}}>👀 SPECTATING</div>}
        {!isSpectator && me && <div style={{fontSize:13,color:'var(--text-secondary)'}}>You: <span style={{color:'var(--accent-gold-text)',fontWeight:800}}>{me.username}</span> <button onClick={signOut} style={{marginLeft:8,padding:'3px 9px',background:'transparent',border:'1px solid var(--border-strong)',borderRadius:5,color:'var(--text-secondary)',cursor:'pointer',fontSize:11}}>switch</button></div>}
      </div>

      {/* Auto-pick countdown banner — fixed-position so it's visible regardless
          of which sheet tab is open or how expanded the sheet is. Floats just
          above the bottom-sheet's top edge, riding sheetHeight as the sheet
          drags. z-index 51 sits above the sheet (50) but below modals (9999+). */}
      {pendingAutoPick && (
        <div style={{position:'fixed',left:12,right:12,bottom:sheetHeight+8,zIndex:51,padding:'8px 12px',background:'var(--accent-gold-bg)',border:'1px solid var(--accent-gold)',borderRadius:6,display:'flex',alignItems:'center',gap:10,fontSize:13,boxShadow:'0 2px 8px rgba(0,0,0,0.15)',maxWidth:560,margin:'0 auto'}}>
          <span style={{fontSize:16}}>⚡</span>
          <span style={{flex:1,fontWeight:700,color:'var(--accent-gold-text)',minWidth:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
            Auto-picking <span style={{color:'var(--text-bright)'}}>{(pendingAutoPick.item?.name || '').replace(/\s*\([A-Za-z]+\)\s*$/, '')}</span>…
          </span>
          <span style={{fontSize:11,color:'var(--text-secondary)',fontWeight:700,flexShrink:0}}>1.5s</span>
        </div>
      )}

      {/* Chat-recommendation banner — sits at the top of the draft room across
          all active phases (lobby/ready/live). Hidden once the draft is
          complete, and dismissible per-draft via localStorage. */}
      {status !== 'complete' && !chatBannerDismissed && (
        <div style={{background:'var(--bg-card)',border:'1px dashed rgba(96,165,250,0.4)',borderRadius:8,padding:'10px 14px',marginBottom:12,fontSize:12,color:'var(--text-ddd)',lineHeight:1.55,display:'flex',alignItems:'flex-start',gap:10}}>
          <div style={{flex:1,minWidth:0}}>
            💬 <strong style={{color:'var(--accent-gold-text)'}}>Recommended:</strong> start a Sleeper group chat with all participants so you can ping each other when on the clock.
          </div>
          <button onClick={dismissChatBanner} title="Dismiss"
            style={{flexShrink:0,background:'transparent',border:'1px solid var(--border-strong)',borderRadius:5,color:'var(--text-secondary)',padding:'2px 8px',fontSize:13,fontWeight:700,cursor:'pointer'}}>×</button>
        </div>
      )}

      {/* Quick how-it-works strip — only during lobby phase. Tells the commish
          what to do: copy the link to leaguemates, have them claim teams. */}
      {status === 'lobby' && (
        <div style={{background:'var(--accent-gold-bg)',border:'1px dashed rgba(var(--accent-gold-rgb),0.4)',borderRadius:8,padding:'10px 14px',marginBottom:12,fontSize:12,color:'var(--text-ddd)',lineHeight:1.55}}>
          💡 <strong style={{color:'var(--accent-gold-text)'}}>How this works:</strong> Tap <span style={{color:'#000',background:'var(--accent-gold)',padding:'1px 6px',borderRadius:3,fontWeight:800,fontSize:11}}>🔗 COPY LINK</span> above and send it to your league. Each manager opens the link and claims their team below — once everyone's joined, the commish hits <span style={{color:'var(--success)',fontWeight:800}}>▶ GO TO DRAFT</span>. Claimed the wrong team by mistake? Tap <span style={{color:'var(--text-secondary)',fontWeight:800}}>✕ UNCLAIM</span> on your card to release it and pick a different one.
        </div>
      )}

      {/* Sleeper-link nudge banner — shows in lobby for unlinked users.
          Dismissible (sticks per browser via localStorage). Benefit-framed copy
          rather than warning copy — converts better. */}
      {status === 'lobby' && !isSpectator && !sleeperLinkedUser && !unlinkBannerDismissed && (
        <div style={{background:'#0a1a14',border:'1px dashed rgba(var(--success-rgb),0.4)',borderRadius:8,padding:'10px 14px',marginBottom:12,fontSize:12,color:'var(--text-ddd)',lineHeight:1.55,display:'flex',alignItems:'flex-start',gap:10}}>
          <div style={{flex:1}}>
            🔗 <strong style={{color:'var(--success)'}}>Pro tip:</strong> Tap <strong style={{color:'var(--accent-gold-text)'}}>🔗 Link Sleeper</strong> in the toolbar above before you claim. Linking your Sleeper account means your team claim follows you across browsers and devices — no risk of getting locked out if your phone clears its cache mid-draft. One-time setup, no password, takes 5 seconds.
          </div>
          <button onClick={dismissUnlinkBanner} title="Dismiss"
            style={{flexShrink:0,background:'transparent',border:'1px solid var(--border-strong)',borderRadius:5,color:'var(--text-secondary)',padding:'2px 8px',fontSize:13,fontWeight:700,cursor:'pointer'}}>×</button>
        </div>
      )}

      {/* Lobby phase — minimal control banner. Per-team CLAIM / RECOVER /
          UNCLAIM buttons live in the team-avatar strip below (rendered for
          every phase), so this banner only carries the global controls:
          waiting count, randomize order, and "GO TO DRAFT". */}
      {status === 'lobby' && (
        <div className="pfk-disp-lobby-controls" style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:10,padding:'14px 16px',marginBottom:14,display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
          <div style={{flex:'1 1 200px',minWidth:0}}>
            <div style={{fontSize:11,fontWeight:800,color:'var(--text-secondary)',letterSpacing:1.5,marginBottom:2}}>WAITING FOR MANAGERS</div>
            <div style={{fontSize:14,fontWeight:900,color:'var(--accent-gold-text)'}}>{teams.filter(t=>t.joined).length} <span style={{color:'var(--text-secondary)',fontWeight:700}}>of</span> {teams.length} <span style={{color:'var(--text-secondary)',fontWeight:700,fontSize:12}}>joined</span></div>
          </div>
          <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
            {isCommish && <button onClick={randomizeOrder} style={{padding:'10px 14px',background:'transparent',border:'1px solid var(--accent-gold)',borderRadius:7,color:'var(--accent-gold-text)',fontWeight:800,cursor:'pointer',fontSize:12,letterSpacing:0.8,whiteSpace:'nowrap'}}>🎲 RANDOMIZE DRAFT ORDER</button>}
            <button onClick={goToDraft} style={{padding:'10px 18px',background:'var(--success)',border:'none',borderRadius:7,color:'#000',fontWeight:900,cursor:'pointer',fontSize:13,letterSpacing:1.2,whiteSpace:'nowrap'}}>▶ GO TO DRAFT</button>
          </div>
          {claimErr && <div style={{flex:'1 1 100%',color:'var(--danger)',fontSize:12,marginTop:4}}>{claimErr}</div>}
        </div>
      )}
      {status === 'lobby' && !me && !isSpectator && !isCommish && (
        <div style={{background:'var(--accent-gold-bg)',border:'2px solid var(--accent-gold)',borderRadius:10,padding:'14px 18px',marginBottom:12,display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
          <div style={{flex:1,minWidth:200}}>
            <div style={{fontSize:13,fontWeight:900,color:'var(--accent-gold-text)',letterSpacing:1,marginBottom:2}}>👑 RUNNING THIS DRAFT?</div>
            <div style={{fontSize:12,color:'var(--text-secondary)'}}>Click in as commissioner — start/randomize/undo controls. No passcode needed.</div>
          </div>
          <button onClick={declareCommish} style={{padding:'10px 16px',background:'var(--accent-gold)',border:'none',borderRadius:7,color:'#000',fontWeight:900,cursor:'pointer',fontSize:13,letterSpacing:1}}>I AM THE COMMISSIONER</button>
        </div>
      )}
      {status === 'lobby' && !me && isCommish && (
        <div style={{background:'var(--accent-gold-bg)',border:'1px solid var(--accent-gold)',borderRadius:10,padding:'10px 14px',marginBottom:12,display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
          <span style={{fontSize:13,fontWeight:900,color:'var(--accent-gold-text)',letterSpacing:1}}>👑 COMMISSIONER MODE</span>
          <span style={{fontSize:12,color:'var(--text-secondary)'}}>You're not drafting — you run the lobby + start the draft. Claim a team below if you do want to draft.</span>
        </div>
      )}

      {/* Ready / Live / Complete all share the draft view (pool + rosters). 'ready' adds a
          pre-start banner with START DRAFT + RANDOMIZE; 'live' has the on-clock card + timer;
          'complete' has the trophy banner. */}
      {(status === 'ready' || status === 'live' || status === 'complete') && (
        <>
          {status === 'ready' && (
            <div style={{marginBottom:14}}>
              <div style={{background:'var(--accent-gold-bg)',border:'2px solid var(--accent-gold)',borderRadius:'10px 10px 0 0',padding:'14px 16px',display:'flex',alignItems:'center',gap:12,flexWrap:'wrap',borderBottom:'none'}}>
                <div style={{flex:'1 1 220px',minWidth:0}}>
                  <div style={{fontSize:11,fontWeight:800,color:'var(--accent-gold-text)',letterSpacing:1.5}}>READY TO DRAFT</div>
                  <div style={{fontSize:13,color:'var(--text-secondary)',marginTop:2}}>Review the draft order. Hit START DRAFT when everyone's ready.</div>
                </div>
                <div style={{display:'flex',gap:8,flexWrap:'wrap',flex:'0 1 auto'}}>
                  <button onClick={randomizeOrder} style={{padding:'10px 14px',background:'transparent',border:'1px solid var(--accent-gold)',borderRadius:7,color:'var(--accent-gold-text)',fontWeight:800,cursor:'pointer',fontSize:12,letterSpacing:0.8,whiteSpace:'nowrap',flex:'1 1 auto',minWidth:160}}>🎲 RANDOMIZE DRAFT ORDER</button>
                  <button onClick={startDraft} style={{padding:'10px 18px',background:'var(--success)',border:'none',borderRadius:7,color:'#000',fontWeight:900,cursor:'pointer',fontSize:13,letterSpacing:1,whiteSpace:'nowrap',flex:'1 1 auto',minWidth:140}}>▶ START DRAFT</button>
                </div>
              </div>
              {/* Pick order strip — visible right under the READY banner so RANDOMIZE shows the reorder live */}
              <div style={{background:'var(--bg-elevated)',border:'2px solid var(--accent-gold)',borderTop:'none',borderRadius:'0 0 10px 10px',padding:'8px 12px',display:'flex',gap:6,overflowX:'auto'}}>
                {pickOrder.map((slotIdx,i)=>{
                  const team = teams.find(t=>t.slot===slotIdx);
                  const isMine = me && slotIdx === me.slot;
                  return (
                    <div key={i} style={{flexShrink:0,padding:'4px 10px',borderRadius:14,fontSize:11,fontWeight:800,letterSpacing:0.5,background:isMine?'var(--success-bg)':'var(--bg-divider)',color:isMine?'var(--success)':'var(--text-muted)',border:'1px solid '+(isMine?'var(--success)':'var(--bg-pill)')}}>
                      <span style={{opacity:0.65,marginRight:5}}>{i+1}.</span>{team?.username||'?'}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {status === 'live' && onClockTeam && (()=>{
            // When paused, the displayed countdown is FROZEN at paused_remaining_ms
            // — no tick. When live (not paused), it's deadline - now ticking down.
            const isPaused = !!draft.paused;
            const isAutoPause = isPaused && draft.paused_reason === 'auto';
            const deadlineMs = draft.pick_deadline ? new Date(draft.pick_deadline).getTime() : null;
            const remaining = isPaused
              ? (typeof draft.paused_remaining_ms === 'number' ? draft.paused_remaining_ms : (draft.timer_seconds ? draft.timer_seconds*1000 : null))
              : (deadlineMs ? Math.max(0, deadlineMs - now) : null);
            const mins = remaining!=null ? Math.floor(remaining/60000) : null;
            const secs = remaining!=null ? Math.floor((remaining%60000)/1000) : null;
            const expired = !isPaused && remaining===0;
            const lowTime = !isPaused && remaining!=null && remaining < 60000 && !expired;
            const timerColor = isPaused ? 'var(--text-secondary)' : expired ? 'var(--danger)' : lowTime ? 'var(--warning)' : myTurn ? 'var(--success)' : 'var(--accent-gold)';
            const borderColor = isPaused ? 'var(--accent-gold)' : expired ? 'var(--danger)' : myTurn ? 'var(--success)' : 'var(--accent-gold)';
            const headerLabel = isPaused ? '⏸ DRAFT PAUSED' : expired ? 'TIME EXPIRED' : myTurn ? '🚨 YOU ARE OTC!' : 'ON THE CLOCK';
            const headerColor = isPaused ? 'var(--accent-gold-text)' : expired ? 'var(--danger)' : myTurn ? 'var(--success)' : 'var(--accent-gold)';
            // For auto-pause: compute resume time in the viewer's local tz.
            const resumeUtc = (isAutoPause && draft.auto_pause_end_local && draft.auto_pause_tz)
              ? dispNextAutoPauseEndUtc(now, draft.auto_pause_end_local, draft.auto_pause_tz)
              : null;
            const resumeLocal = resumeUtc ? dispFormatLocalTime(resumeUtc) : '';
            return (
              <div style={{marginBottom:14}}>
                <div style={{background:isPaused?'var(--accent-gold-bg)':myTurn?'var(--success-bg)':'var(--bg-elevated)',border:'2px solid '+borderColor,borderRadius:'10px 10px 0 0',padding:'14px 18px',display:'flex',alignItems:'center',gap:14,flexWrap:'wrap',borderBottom:'none'}}>
                  <div style={{display:'flex',flexDirection:'column',gap:2}}>
                    <div style={{fontSize:isPaused?20:11,fontWeight:900,color:headerColor,letterSpacing:isPaused?2.5:1.5,lineHeight:1.1}}>{headerLabel}</div>
                    {isAutoPause && resumeLocal && (
                      <div style={{fontSize:13,fontWeight:800,color:'var(--accent-gold-text)',letterSpacing:0.5,marginTop:2}}>Resumes at {resumeLocal}</div>
                    )}
                  </div>
                  <div style={{fontSize:18,fontWeight:900}}>{onClockTeam.username}</div>
                  <div style={{fontSize:13,color:'var(--text-secondary)'}}>Round {round} · Pick {posInRound} (overall #{draft.current_pick_idx+1})</div>
                  {remaining!=null && (
                    <div style={{marginLeft:'auto',fontSize:22,fontWeight:900,color:timerColor,fontFamily:'monospace',letterSpacing:2,opacity:isPaused?0.85:1}} title={isAutoPause?('Clock paused until '+resumeLocal):isPaused?'Clock is paused — resume from the COMMISH row':undefined}>
                      {String(mins).padStart(2,'0')}:{String(secs).padStart(2,'0')}
                    </div>
                  )}
                </div>
                {/* Pick order strip — all teams in order, current highlighted */}
                <div style={{background:'var(--bg-elevated)',border:'2px solid '+borderColor,borderTop:'none',borderRadius:'0 0 10px 10px',padding:'8px 12px',display:'flex',gap:6,overflowX:'auto'}}>
                  {pickOrder.map((slotIdx,i)=>{
                    const team = teams.find(t=>t.slot===slotIdx);
                    const isCurrent = slotIdx === onClockSlot;
                    const isMe = me && slotIdx === me.slot;
                    return (
                      <div key={i} style={{flexShrink:0,padding:'4px 10px',borderRadius:14,fontSize:11,fontWeight:800,letterSpacing:0.5,background:isCurrent?'var(--accent-gold)':isMe?'var(--success-bg)':'var(--bg-divider)',color:isCurrent?'#000':isMe?'var(--success)':'var(--text-muted)',border:'1px solid '+(isCurrent?'var(--accent-gold)':isMe?'var(--success)':'var(--bg-pill)')}}>
                        <span style={{opacity:0.65,marginRight:5}}>{i+1}.</span>{team?.username||'?'}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
          {/* Pick-clock control — visible to ALL participants on a live or
              ready (lobby/paused) draft so everyone can see the configured
              timer; only the commish can change it. Mid-draft changes start
              a fresh clock for the on-clock team (no remaining-time carryover).
              Includes auto-pick toggle and overnight-pause window. Hidden
              once the draft is complete. */}
          {(status === 'live' || status === 'ready') && (() => {
            const apEnabled = !!draft.auto_pause_enabled;
            const apStart = draft.auto_pause_start_local || '22:00';
            const apEnd = draft.auto_pause_end_local || '08:00';
            const apTz = draft.auto_pause_tz || (Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
            const labelStyle = {fontSize:11,fontWeight:800,color:'var(--text-secondary)',letterSpacing:1.5,minWidth:104};
            const helperStyle = {fontSize:11,color:'var(--text-secondary)',lineHeight:1.4,flex:'1 1 220px'};
            const faintStyle = {fontSize:11,color:'var(--text-faint)',lineHeight:1.4,flex:'1 1 220px'};
            const inputStyle = {padding:'9px 12px',minHeight:40,background:'var(--bg-input)',border:'1px solid var(--border-strong)',borderRadius:6,color:isCommish?'var(--accent-gold-text)':'var(--text-secondary)',fontSize:13,fontWeight:800,cursor:isCommish?'pointer':'default',opacity:isCommish?1:0.85};
            return (
              <div style={{display:'flex',flexDirection:'column',gap:10,marginBottom:14,padding:'10px 14px',background:'var(--bg-elevated)',border:'1px solid var(--bg-pill)',borderRadius:8}}>
                {/* PICK CLOCK row */}
                <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                  <div style={labelStyle}>PICK CLOCK</div>
                  <select
                    value={dispTimerKeyForSeconds(draft.timer_seconds)}
                    onChange={e=>changeTimerMidDraft(e.target.value)}
                    disabled={!isCommish}
                    aria-label="Pick clock duration"
                    title={isCommish ? 'Change the pick clock for the rest of the draft. The team on the clock gets a fresh timer.' : 'Only the commish can change the pick clock.'}
                    style={inputStyle}>
                    {DISP_TIMER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  {isCommish ? (
                    <span style={helperStyle}>Changing the clock starts a fresh timer for the team on the clock.</span>
                  ) : (
                    <span style={faintStyle}>Set by the commish.</span>
                  )}
                </div>
                {/* AUTO-PICK row — only meaningful when timer is set */}
                {!!draft.timer_seconds && (
                  <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                    <div style={labelStyle}>AUTO-PICK</div>
                    <button
                      onClick={isCommish ? toggleAutoPickMidDraft : undefined}
                      disabled={!isCommish}
                      aria-pressed={!!draft.auto_pick}
                      data-testid="autopick-toggle"
                      title={isCommish ? 'When the pick clock expires, auto-fire from the on-clock team\'s queue (or top available if empty). Unclaimed teams take top available.' : 'Only the commish can toggle this.'}
                      style={{padding:'9px 18px',minHeight:40,minWidth:64,background:draft.auto_pick?'var(--success)':'var(--bg-input)',border:'1px solid '+(draft.auto_pick?'var(--success)':'var(--border-strong)'),borderRadius:6,color:draft.auto_pick?'#000':isCommish?'var(--text-muted)':'var(--text-secondary)',fontSize:13,fontWeight:900,letterSpacing:1.2,cursor:isCommish?'pointer':'default',opacity:isCommish?1:0.85}}>
                      {draft.auto_pick ? 'ON' : 'OFF'}
                    </button>
                    {isCommish ? (
                      <span style={helperStyle}>{draft.auto_pick ? 'Expirations auto-fire from queue. If no queue, then top available.' : 'When a pick clock expires, the draft pauses for the commish to advance.'}</span>
                    ) : (
                      <span style={faintStyle}>Set by the commish.</span>
                    )}
                  </div>
                )}
                {/* AUTO-PAUSE (overnight) row */}
                {!!draft.timer_seconds && (
                  <div style={{display:'flex',alignItems:'flex-start',gap:10,flexWrap:'wrap'}}>
                    <div style={{...labelStyle,marginTop:10}}>OVERNIGHT</div>
                    <div style={{display:'flex',flexDirection:'column',gap:6,flex:'1 1 auto'}}>
                      <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                        <button
                          onClick={isCommish ? () => updateAutoPauseMidDraft({ auto_pause_enabled: !apEnabled, auto_pause_start_local: apStart, auto_pause_end_local: apEnd, auto_pause_tz: apTz }) : undefined}
                          disabled={!isCommish}
                          aria-pressed={apEnabled}
                          data-testid="overnight-toggle"
                          title={isCommish ? 'Pause the clock during a daily window (e.g. overnight). Only managers with personal auto-pick on will fire during the window — clock expirations are paused for everyone else.' : 'Only the commish can toggle this.'}
                          style={{padding:'9px 18px',minHeight:40,minWidth:64,background:apEnabled?'var(--accent-gold)':'var(--bg-input)',border:'1px solid '+(apEnabled?'var(--accent-gold)':'var(--border-strong)'),borderRadius:6,color:apEnabled?'#000':isCommish?'var(--text-muted)':'var(--text-secondary)',fontSize:13,fontWeight:900,letterSpacing:1.2,cursor:isCommish?'pointer':'default',opacity:isCommish?1:0.85}}>
                          {apEnabled ? 'ON' : 'OFF'}
                        </button>
                        {apEnabled && (
                          <>
                            <input type="time" value={apStart} disabled={!isCommish} onChange={e => updateAutoPauseMidDraft({ auto_pause_start_local: e.target.value, auto_pause_tz: apTz })} aria-label="Pause start" style={{...inputStyle,padding:'9px 8px'}}/>
                            <span style={{fontSize:12,color:'var(--text-secondary)',fontWeight:800}}>→</span>
                            <input type="time" value={apEnd} disabled={!isCommish} onChange={e => updateAutoPauseMidDraft({ auto_pause_end_local: e.target.value, auto_pause_tz: apTz })} aria-label="Pause end" style={{...inputStyle,padding:'9px 8px'}}/>
                          </>
                        )}
                      </div>
                      {apEnabled ? (
                        <span style={{fontSize:11,color:'var(--text-secondary)',lineHeight:1.4}}>Times in <strong>{apTz}</strong> ({dispFormatHHMM(apStart)} – {dispFormatHHMM(apEnd)}). Only managers with their <em>personal</em> auto-pick on will fire during the window.</span>
                      ) : isCommish ? (
                        <span style={{fontSize:11,color:'var(--text-secondary)',lineHeight:1.4}}>Pause the clock during a daily window (e.g. 10pm-8am) so nobody loses sleep over a deadline.</span>
                      ) : (
                        <span style={{fontSize:11,color:'var(--text-faint)',lineHeight:1.4}}>Off.</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
          {status === 'complete' && (
            <div style={{background:'linear-gradient(135deg,#1a1a3a 0%, var(--accent-gold-bg) 100%)',border:'2px solid var(--accent-gold)',borderRadius:10,padding:'18px 22px',marginBottom:14,textAlign:'center'}}>
              <div style={{fontSize:16,fontWeight:900,color:'var(--accent-gold-text)',letterSpacing:2,marginBottom:6}}>🏆 DRAFT COMPLETE</div>
              <div style={{fontSize:13,color:'var(--accent-gold-text)',marginBottom:14,letterSpacing:1}}>{picks.length} picks made · 📸 download each roster from the bar below</div>
              <div style={{fontSize:14,color:'var(--text-eee)',marginBottom:6,fontWeight:700}}>Thanks for drafting with Play For Keeps!</div>
              <div style={{fontSize:12,color:'var(--text-secondary)',marginBottom:14,maxWidth:460,marginLeft:'auto',marginRight:'auto',lineHeight:1.5}}>If this saved you time please follow @PlayForKeepsFF on twitter. Check out playforkeepsdynasty.com for more free dynasty tools and content.</div>
              <div style={{display:'flex',gap:10,justifyContent:'center',flexWrap:'wrap'}}>
                <a href="https://x.com/PlayForKeepsFF" target="_blank" rel="noopener noreferrer" style={{display:'inline-flex',alignItems:'center',gap:8,padding:'12px 22px',background:'var(--accent-gold)',color:'#000',textDecoration:'none',borderRadius:8,fontWeight:900,fontSize:14,letterSpacing:1}}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="#000" aria-hidden="true"><path d="M18.244 2H21.5l-7.5 8.57L23 22h-6.844l-5.36-6.72L4.5 22H1.244l8.04-9.187L1 2h7.016l4.844 6.12L18.244 2zm-1.2 18h1.9L7.048 4H5.05l12 16z"/></svg>
                  FOLLOW @PlayForKeepsFF
                </a>
                <a href="https://playforkeepsdynasty.com/" target="_blank" rel="noopener noreferrer" style={{display:'inline-flex',alignItems:'center',gap:8,padding:'12px 22px',background:'transparent',color:'var(--accent-gold-text)',textDecoration:'none',borderRadius:8,fontWeight:900,fontSize:14,letterSpacing:1,border:'2px solid var(--accent-gold)'}}>
                  <span style={{fontSize:16}}>👑</span>
                  PLAYFORKEEPSDYNASTY.COM
                </a>
              </div>
            </div>
          )}
          {pickErr && <div style={{padding:'10px 14px',background:'var(--danger-bg)',border:'1px solid var(--danger)',borderRadius:6,color:'var(--danger)',fontSize:13,marginBottom:14}}>{pickErr}</div>}

          {isCommish && (
            <div style={{background:'var(--bg-elevated)',border:'1px solid '+(isAdminOverride?'var(--accent-gold)':'var(--bg-pill)'),borderRadius:8,padding:'10px 14px',marginBottom:14,display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
              <div style={{fontSize:11,fontWeight:800,color:'var(--text-secondary)',letterSpacing:1.5,marginRight:6}}>COMMISH:</div>
              {isAdminOverride && (
                <div title={commishFlag ? "You're signed in as PFK admin (in addition to being the draft creator)" : "You're using PFK admin powers, not draft-creator powers"} style={{display:'inline-flex',alignItems:'center',gap:6,padding:'4px 10px',background:'var(--bg-pill)',border:'1px solid var(--accent-gold)',borderRadius:14,color:'var(--accent-gold-text)',fontSize:10,fontWeight:900,letterSpacing:1.2}}>
                  <span aria-hidden="true">🛡</span> ADMIN OVERRIDE
                </div>
              )}
              <button onClick={undoLastPick} disabled={!picks.length} style={{padding:'6px 12px',background:'transparent',border:'1px solid '+(picks.length?'var(--accent-gold)':'var(--border-strong)'),borderRadius:6,color:picks.length?'var(--accent-gold)':'var(--border-strong)',fontSize:12,fontWeight:800,cursor:picks.length?'pointer':'default'}}>↩ Undo last pick</button>
              {status==='live' && !draft.paused && <button onClick={pauseDraft} style={{padding:'6px 12px',background:'transparent',border:'1px solid var(--accent-gold)',borderRadius:6,color:'var(--accent-gold-text)',fontSize:12,fontWeight:800,cursor:'pointer'}}>⏸ Pause draft</button>}
              {status==='live' && draft.paused && <button onClick={resumeDraft} style={{padding:'6px 12px',background:'var(--accent-gold)',border:'1px solid var(--accent-gold)',borderRadius:6,color:'#000',fontSize:12,fontWeight:800,cursor:'pointer'}}>▶ Resume draft</button>}
              {status==='live' && <button onClick={forceSkip} style={{padding:'6px 12px',background:'transparent',border:'1px solid var(--warning)',borderRadius:6,color:'var(--warning)',fontSize:12,fontWeight:800,cursor:'pointer'}}>⏭ Force skip</button>}
              {status==='live' && <button onClick={endDraft} style={{padding:'6px 12px',background:'transparent',border:'1px solid var(--danger)',borderRadius:6,color:'var(--danger)',fontSize:12,fontWeight:800,cursor:'pointer'}}>⏹ End draft</button>}
              {status==='complete' && <button onClick={reopenDraft} style={{padding:'6px 12px',background:'transparent',border:'1px solid var(--success)',borderRadius:6,color:'var(--success)',fontSize:12,fontWeight:800,cursor:'pointer'}}>▶ Reopen draft</button>}
            </div>
          )}
        </>
      )}

      {/* SLEEPER-STYLE DRAFT BOARD — team avatar strip on top, color-coded
          pick grid below. Horizontal scroll keeps the 8-12 columns readable
          on mobile. Tap a manager's avatar to jump the bottom sheet to their
          TEAM tab. Renders in EVERY phase (lobby through complete) so the
          team strip is the single, consistent place to claim/recover/unclaim
          a team. The pool list + per-manager rosters live in the bottom
          sheet, which is gated to ready/live/complete only. */}
          {(() => {
            const N = pickOrder.length || 1;
            const numRounds = Math.max(1, Math.ceil(pool.length / N));
            const pickByIdx = {};
            picks.forEach(p => { pickByIdx[p.pickIdx] = p; });
            const itemById = {};
            pool.forEach(p => { itemById[p.id] = p; });
            const teamBySlot = {};
            teams.forEach(t => { teamBySlot[t.slot] = t; });
            const posOf = (it) => {
              if(!it) return '';
              if(it.pos) return it.pos.toUpperCase();
              const m = (it.name||'').match(/\(([A-Za-z]+)\)\s*$/);
              return m ? m[1].toUpperCase() : '';
            };
            const cleanName = (it) => (it?.name || '').replace(/\s*\([A-Za-z]+\)\s*$/, '');
            const POS_COLORS = { QB:'var(--pos-qb)', RB:'var(--pos-rb)', WR:'var(--pos-wr)', TE:'var(--pos-te)', K:'var(--pos-other)' };
            const cellColorOf = (it) => {
              if(!it) return null;
              if(it.type === 'pick') return 'var(--accent-gold)';
              return POS_COLORS[posOf(it)] || 'var(--pos-other)';
            };
            // Snake mapping: even rounds flow left→right, odd rounds reverse.
            // Each column always represents the same team across rounds.
            const overallForCell = (r, c) => {
              if(draft.snake && r % 2 === 1) return r * N + (N - 1 - c);
              return r * N + c;
            };
            const SLOT_COLORS = ['var(--accent-gold)','var(--success)','var(--pos-qb)','var(--pos-wr)','var(--pos-te)','var(--danger)','var(--pos-rb)','#ec4899','#06b6d4','var(--warning)','#8b5cf6','#84cc16'];
            const initialOf = (s) => (s||'?').trim().charAt(0).toUpperCase();
            const currentIdx = draft.current_pick_idx || 0;
            // Min cell width keeps player names legible while letting 12-team
            // boards scroll horizontally on a 375-wide phone.
            const MIN_CELL = 96;
            return (
              <div className="pfk-disp-board-wrap" style={{marginBottom:14,border:'1px solid var(--border)',borderRadius:10,overflow:'hidden',background:'var(--bg-elevated)'}}>
                <div className="pfk-disp-board-scroll" style={{overflowX:'auto',WebkitOverflowScrolling:'touch'}}>
                  {/* Team avatar strip — sleeper avatars when linked, action chips
                      (CLAIM / ↻ RECOVER / ✕ UNCLAIM) under each name so all team
                      management lives here in every phase (replaces the old
                      lobby claim grid). Tapping the avatar/name jumps the bottom
                      sheet to that manager's TEAM tab; chip taps stop bubbling
                      so the two interactions don't fight each other. */}
                  <div style={{
                    display:'grid',gridTemplateColumns:`repeat(${N}, minmax(${MIN_CELL}px, 1fr))`,
                    gap:0,minWidth:N*MIN_CELL,
                    background:'var(--bg-card)',borderBottom:'1px solid var(--border)'
                  }}>
                    {pickOrder.map((slotIdx, idx) => {
                      const t = teamBySlot[slotIdx];
                      if(!t) return <div key={slotIdx} style={{padding:6}}/>;
                      const isOnClock = onClockSlot === slotIdx && status === 'live';
                      const isMe = me && me.slot === slotIdx;
                      const slotColor = SLOT_COLORS[slotIdx % SLOT_COLORS.length];
                      const avatarHash = t.sleeper_user_id ? teamAvatars[t.sleeper_user_id] : null;
                      const canClaim = !t.joined && !me && !isSpectator;
                      const canRecover = t.joined && !isMe && !isSpectator;
                      const canUnclaim = isMe;
                      return (
                        <div key={slotIdx} style={{
                          position:'relative',
                          display:'flex',flexDirection:'column',alignItems:'center',gap:4,
                          padding:'8px 4px 8px',
                          borderBottom:'2px solid '+(isOnClock?'var(--success)':'transparent'),
                          opacity:t.joined?1:0.78,
                          minHeight:138
                        }}>
                          <button
                            onClick={() => { setSheetTab('team'); setTeamTabSlot(slotIdx); ensureSheetOpen(); }}
                            title={`View @${t.username}'s roster`}
                            style={{
                              background:'transparent',border:'none',cursor:'pointer',
                              padding:0,display:'flex',flexDirection:'column',alignItems:'center',gap:3,
                              color:'inherit',font:'inherit',width:'100%'
                            }}>
                            <div style={{
                              width:40,height:40,borderRadius:'50%',
                              background:`color-mix(in srgb, ${slotColor} 20%, transparent)`,
                              border:'2px solid '+(isOnClock?'var(--success)':isMe?'var(--accent-gold)':t.joined?`color-mix(in srgb, ${slotColor} 67%, transparent)`:'var(--border-strong)'),
                              color:slotColor,
                              overflow:'hidden',
                              display:'flex',alignItems:'center',justifyContent:'center',
                              fontSize:15,fontWeight:900,
                              boxShadow:isOnClock?'0 0 0 3px rgba(16,185,129,0.25)':'none'
                            }}>
                              {avatarHash
                                ? <img src={`https://sleepercdn.com/avatars/thumbs/${avatarHash}`} alt="" style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}} onError={(e)=>{ e.target.style.display='none'; }}/>
                                : initialOf(t.username)
                              }
                            </div>
                            <div style={{
                              fontSize:10,fontWeight:800,letterSpacing:0.3,
                              color:isOnClock?'var(--success)':isMe?'var(--accent-gold)':'var(--text-ddd)',
                              maxWidth:MIN_CELL-8,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'
                            }}>{t.username}{isMe && <span style={{color:'var(--success)',marginLeft:3}}>•you</span>}</div>
                            <div style={{fontSize:9,color:'var(--text-secondary)',fontWeight:700,letterSpacing:0.3}}>Pick #{idx+1}{!t.joined && <span style={{color:'var(--text-secondary)',marginLeft:4}}>· open</span>}</div>
                          </button>
                          {(() => {
                            // ⚡ AUTO badge — shows whenever this slot has
                            // auto-pick on, EITHER via live presence (their
                            // tab is connected and broadcasting) OR via the
                            // persisted user_state.auto_pick on the row.
                            // The persisted gate matters because the cross-tab
                            // fallback will fire for them even when their tab
                            // is closed — so other managers need to see the
                            // indicator without their tab being present. Sits
                            // OUTSIDE the avatar <button> so it stacks cleanly
                            // above the action chip (CLAIM/RECOVER/UNCLAIM).
                            const presList = presenceMap[String(slotIdx)] || [];
                            const isOnAutoLive = presList.some(p => p?.auto_pick === true);
                            const slotPersisted = (draft?.user_state || {})[String(slotIdx)] || null;
                            const isOnAutoPersisted = !!slotPersisted?.auto_pick;
                            const showSelfFallback = isMe && !isOnAutoLive && !!myUserState?.auto_pick;
                            if(!isOnAutoLive && !isOnAutoPersisted && !showSelfFallback) return null;
                            const titleText = isMe
                              ? 'Your auto-pick is on'
                              : isOnAutoLive
                                ? `@${t.username} has auto-pick on`
                                : `@${t.username} has auto-pick on (tab closed — any open tab will fire it)`;
                            return (
                              <div title={titleText} style={{padding:'1px 6px',background:'var(--success-bg)',border:'1px solid #10b98155',borderRadius:8,fontSize:9,fontWeight:900,letterSpacing:0.6,color:'var(--success)',whiteSpace:'nowrap'}}>
                                ⚡ AUTO
                              </div>
                            );
                          })()}
                          {/* Action chip row — only one at a time renders */}
                          {canClaim && (
                            <button onClick={(e)=>{e.stopPropagation(); handleClaimClick(slotIdx);}}
                              style={{padding:'5px 10px',background:'var(--accent-gold)',border:'none',borderRadius:4,color:'#000',fontWeight:900,fontSize:10,letterSpacing:0.8,cursor:'pointer',width:'calc(100% - 6px)'}}>CLAIM</button>
                          )}
                          {!canClaim && canRecover && (
                            <button onClick={(e)=>{e.stopPropagation(); recoverSlot(slotIdx);}}
                              title="Take over this team — only if you're the original claimer and lost your session"
                              style={{padding:'5px 8px',background:'transparent',border:'1px solid #DDB34D77',borderRadius:4,color:'var(--accent-gold-text)',fontWeight:800,fontSize:9,letterSpacing:0.5,cursor:'pointer',width:'calc(100% - 6px)',whiteSpace:'nowrap'}}>↻ RECOVER</button>
                          )}
                          {canUnclaim && (
                            <button onClick={(e)=>{e.stopPropagation(); releaseSlot(slotIdx);}}
                              title="Unclaim — frees this team so you can claim another"
                              style={{padding:'5px 8px',background:'transparent',border:'1px solid #ef444477',borderRadius:4,color:'var(--danger)',fontWeight:800,fontSize:9,letterSpacing:0.5,cursor:'pointer',width:'calc(100% - 6px)',whiteSpace:'nowrap'}}>✕ UNCLAIM</button>
                          )}
                          {/* Commish quick-release — separate, smaller, only when commish + foreign claim */}
                          {t.joined && isCommish && !isMe && !isSpectator && (
                            <button onClick={(e)=>{e.stopPropagation(); releaseSlot(slotIdx);}}
                              title="Commish: release this claim"
                              style={{position:'absolute',top:4,right:4,padding:'1px 5px',background:'var(--bg-pill)',border:'1px solid var(--border-strong)',borderRadius:3,color:'var(--text-secondary)',fontSize:9,fontWeight:700,cursor:'pointer'}}>✕</button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {/* Pick grid — each row is a round, each cell is a position-colored card. */}
                  <div style={{
                    display:'grid',gridTemplateColumns:`repeat(${N}, minmax(${MIN_CELL}px, 1fr))`,
                    gap:1,padding:1,minWidth:N*MIN_CELL,background:'var(--bg-pill)'
                  }}>
                    {Array.from({length:numRounds}).flatMap((_, r) =>
                      pickOrder.map((slotIdx, c) => {
                        const overallIdx = overallForCell(r, c);
                        const pick = pickByIdx[overallIdx];
                        const item = pick ? itemById[pick.poolItemId] : null;
                        const cellColor = cellColorOf(item) || 'var(--border-strong)';
                        const isOnClock = status === 'live' && overallIdx === currentIdx;
                        const isPick = item?.type === 'pick';
                        const pos = isPick ? 'PICK' : posOf(item);
                        const pickInRound = (overallIdx % N) + 1;
                        const pickLabel = `${r+1}.${pickInRound}`;
                        const ownerTeam = teamBySlot[slotIdx];
                        return (
                          <div key={`${r}-${c}`} className={"pfk-disp-pick-card"+(isOnClock?' on-clock':'')} style={{
                            background:item?`color-mix(in srgb, ${cellColor} 13%, transparent)`:'var(--bg-elevated)',
                            border:'2px solid '+(item?cellColor:'var(--border)'),
                            padding:'6px 8px',minHeight:62,
                            display:'flex',flexDirection:'column',justifyContent:'space-between',gap:4,
                            position:'relative',
                            outline:isOnClock?'2px solid var(--success)':'none',
                            outlineOffset:isOnClock?'-2px':0,
                            animation:isOnClock?'pfk-pulse 1.6s infinite':'none'
                          }}>
                            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:4}}>
                              <span style={{
                                fontSize:9,fontWeight:900,letterSpacing:0.5,
                                color:item?cellColor:'var(--border-strong)',
                                whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'
                              }}>{item?pos:'\u00a0'}</span>
                              <span style={{fontSize:9,fontWeight:700,color:'var(--text-secondary)',flexShrink:0}}>{pickLabel}</span>
                            </div>
                            <div style={{display:'flex',alignItems:'center',gap:6,minHeight:24}}>
                              {item && !isPick && (
                                <PlayerHeadshot sleeperId={item.sleeperId} name={cleanName(item)} pos={pos} size={24}/>
                              )}
                              <div style={{
                                flex:1,minWidth:0,
                                fontSize:11,fontWeight:800,color:item?'var(--text-bright)':'var(--border-strong)',
                                lineHeight:1.2,wordBreak:'break-word',
                                overflow:'hidden',display:'-webkit-box',
                                WebkitLineClamp:2,WebkitBoxOrient:'vertical'
                              }}>
                                {item ? cleanName(item) : (isOnClock?(<span style={{color:'var(--success)',fontSize:10,letterSpacing:0.5}}>ON CLOCK</span>):'—')}
                              </div>
                            </div>
                            {pick?.auto && (
                              <div style={{position:'absolute',bottom:2,right:4,fontSize:8,color:'var(--warning)',fontStyle:'italic',fontWeight:700}}>auto</div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Claim-time link prompt — intercepts CLAIM clicks for unlinked
              users with a "link first?" modal. Two clear paths: link Sleeper
              inline (recommended, recovery superpower), or skip and claim
              anyway (preserves existing behavior). Not blocking — same
              end-state available either way. */}
          {pendingClaimSlot != null && (
            <div onClick={()=>setPendingClaimSlot(null)} style={{position:'fixed',inset:0,background:'var(--backdrop)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
              <div onClick={e=>e.stopPropagation()} style={{width:'100%',maxWidth:400,background:'var(--bg-card)',border:'2px solid var(--success)',borderRadius:12,padding:'22px 24px'}}>
                <div style={{fontSize:11,fontWeight:800,color:'var(--success)',letterSpacing:1.5,marginBottom:8,textAlign:'center'}}>BEFORE YOU CLAIM</div>
                <div style={{fontSize:16,fontWeight:900,color:'var(--text-bright)',textAlign:'center',marginBottom:8}}>Link Sleeper for safer drafting?</div>
                <div style={{fontSize:13,color:'var(--text-secondary)',textAlign:'center',lineHeight:1.55,marginBottom:14}}>
                  Linking your Sleeper account means your team claim follows you across browsers and devices — no risk of getting locked out if your phone clears its cache mid-draft. One-time setup, no password, takes 5 seconds.
                </div>
                <div style={{display:'flex',gap:6,marginBottom:6,flexWrap:'wrap'}}>
                  <input value={claimLinkInput} onChange={e=>{setClaimLinkInput(e.target.value); setClaimLinkErr('');}}
                    onKeyDown={e=>e.key==='Enter'&&claimLinkSubmit()}
                    placeholder="Sleeper username"
                    disabled={claimLinkBusy}
                    name="sleeper-handle"
                    autoComplete="off" autoCorrect="off" autoCapitalize="none" spellCheck={false}
                    data-1p-ignore="true" data-lpignore="true"
                    style={{flex:'1 1 180px',minWidth:0,padding:'10px 12px',background:'var(--bg-elevated)',border:'1.5px solid rgba(var(--accent-gold-rgb),0.4)',borderRadius:6,color:'var(--text-bright)',fontSize:14,fontFamily:'inherit'}}/>
                  <button onClick={claimLinkSubmit} disabled={claimLinkBusy}
                    style={{padding:'10px 16px',background:claimLinkBusy?'var(--border-strong)':'var(--accent-gold)',border:'none',borderRadius:6,color:'#000',fontWeight:900,cursor:claimLinkBusy?'wait':'pointer',fontSize:13,letterSpacing:0.5,whiteSpace:'nowrap'}}>
                    {claimLinkBusy ? '…' : '🔗 LINK & CLAIM'}
                  </button>
                </div>
                {claimLinkErr && <div style={{color:'var(--danger)',fontSize:12,marginBottom:6}}>{claimLinkErr}</div>}
                <div style={{display:'flex',gap:8,marginTop:14}}>
                  <button onClick={()=>setPendingClaimSlot(null)} style={{flex:1,padding:'10px',background:'transparent',border:'1px solid var(--border-strong)',borderRadius:6,color:'var(--text-secondary)',fontWeight:700,cursor:'pointer',fontSize:12,letterSpacing:0.5}}>CANCEL</button>
                  <button onClick={claimLinkSkip} style={{flex:2,padding:'10px',background:'transparent',border:'1px solid var(--border-outline)',borderRadius:6,color:'var(--text-secondary)',fontWeight:700,cursor:'pointer',fontSize:12,letterSpacing:0.5}}>Skip — claim without linking</button>
                </div>
              </div>
            </div>
          )}

          {/* Pick confirmation modal — surfaces commish-override state so the
              commish knows they're picking on someone else's behalf. */}
          {pickConfirm && (
            <div onClick={()=>setPickConfirm(null)} style={{position:'fixed',inset:0,background:'var(--backdrop)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
              <div onClick={e=>e.stopPropagation()} style={{width:'100%',maxWidth:380,background:'var(--bg-card)',border:'2px solid '+(pickConfirm._commishOverride ? 'var(--accent-gold)' : 'var(--success)'),borderRadius:12,padding:'22px 24px',textAlign:'center'}}>
                <div style={{fontSize:11,fontWeight:800,color:'var(--text-secondary)',letterSpacing:1.5,marginBottom:8}}>{pickConfirm._commishOverride ? 'COMMISH OVERRIDE PICK' : 'CONFIRM PICK'}</div>
                <div style={{fontSize:14,color:'var(--text-secondary)',marginBottom:6}}>Draft</div>
                <div style={{fontSize:22,fontWeight:900,color:'var(--accent-gold-text)',marginBottom:6,wordBreak:'break-word'}}>{pickConfirm.name}</div>
                {pickConfirm._commishOverride && pickConfirm._onClockUsername && (
                  <div style={{fontSize:13,fontWeight:700,color:'var(--accent-gold-text)',marginBottom:8}}>on behalf of @{pickConfirm._onClockUsername}</div>
                )}
                <div style={{fontSize:12,color:'var(--text-secondary)',marginBottom:18}}>This pick is final once confirmed.</div>
                <div style={{display:'flex',gap:8}}>
                  <button onClick={()=>setPickConfirm(null)} style={{flex:1,padding:'12px',background:'transparent',border:'1px solid var(--border-outline)',borderRadius:7,color:'var(--text-secondary)',fontWeight:900,cursor:'pointer',fontSize:14,letterSpacing:1}}>NO, BACK</button>
                  <button onClick={()=>{
                    const id = pickConfirm.id;
                    const slot = pickConfirm._commishOverride ? pickConfirm._onClockSlot : undefined;
                    setPickConfirm(null);
                    makePick(id, slot);
                  }} style={{flex:1,padding:'12px',background: pickConfirm._commishOverride ? 'var(--accent-gold)' : 'var(--success)',border:'none',borderRadius:7,color:'#000',fontWeight:900,cursor:'pointer',fontSize:14,letterSpacing:1}}>YES, DRAFT</button>
                </div>
              </div>
            </div>
          )}

      {/* SLEEPER-STYLE BOTTOM SHEET — PLAYERS / TEAM tabs.
          PLAYERS: position-filtered pool (used to be the standalone pool block).
          TEAM: a manager's roster grouped by position (used to be the rosters bar).
          Only ready/live/complete render this — there's no point in a player
          pool during the lobby. Collapses to ~56px; expands to ~52vh (60vh
          on mobile via CSS). */}
      {(status === 'ready' || status === 'live' || status === 'complete') && (
        <>
          {(() => {
            const posOf = (it) => {
              if(!it) return '';
              if(it.pos) return it.pos.toUpperCase();
              const m = (it.name||'').match(/\(([A-Za-z]+)\)\s*$/);
              return m ? m[1].toUpperCase() : '';
            };
            const POS_COLORS = { QB:'var(--pos-qb)', RB:'var(--pos-rb)', WR:'var(--pos-wr)', TE:'var(--pos-te)', K:'var(--pos-other)' };
            const undrafted = pool.filter(p => !p.drafted);
            const counts = {
              all: undrafted.length,
              QB: undrafted.filter(p => p.type !== 'pick' && posOf(p) === 'QB').length,
              RB: undrafted.filter(p => p.type !== 'pick' && posOf(p) === 'RB').length,
              WR: undrafted.filter(p => p.type !== 'pick' && posOf(p) === 'WR').length,
              TE: undrafted.filter(p => p.type !== 'pick' && posOf(p) === 'TE').length,
              picks: undrafted.filter(p => p.type === 'pick').length,
            };
            // TEAM tab — which manager's roster is showing. Default to the
            // claimed user, else the on-clock team, else first manager.
            const activeTeamSlot = teamTabSlot != null ? teamTabSlot
              : me?.slot != null ? me.slot
              : onClockSlot != null ? onClockSlot
              : pickOrder[0];
            const activeTeam = teams.find(t => t.slot === activeTeamSlot);
            const activeTeamPicks = picks.filter(p => p.slot === activeTeamSlot)
              .map(p => ({...p, item: pool.find(it => it.id === p.poolItemId)}))
              .sort((a,b) => a.pickIdx - b.pickIdx);
            const SLOT_COLORS = ['var(--accent-gold)','var(--success)','var(--pos-qb)','var(--pos-wr)','var(--pos-te)','var(--danger)','var(--pos-rb)','#ec4899','#06b6d4','var(--warning)','#8b5cf6','#84cc16'];
            const initialOf = (s) => (s||'?').trim().charAt(0).toUpperCase();
            return (
              <div className={"pfk-disp-bottom-sheet"+(isSheetCollapsed?' collapsed':'')} style={{
                position:'fixed',left:0,right:0,bottom:0,
                background:'var(--bg-page)',borderTop:'2px solid var(--accent-gold)',
                paddingBottom:'env(safe-area-inset-bottom)',
                // Drag-controlled height (px). Mobile CSS no longer overrides
                // this — the user is in charge. SHEET_COLLAPSED_PX is the
                // floor (just the tab bar); upper limit is computed at runtime.
                height: sheetHeight,
                display:'flex',flexDirection:'column',
                zIndex:50,boxShadow:'0 -4px 12px rgba(0,0,0,0.15)',
                // No height transition during active drag (would feel laggy);
                // only animate snap-to clicks.
                transition: sheetDrag.current.active ? 'none' : 'height 0.18s ease-out'
              }}>
                <div style={{maxWidth:1240,margin:'0 auto',width:'100%',display:'flex',flexDirection:'column',minHeight:0,flex:1}}>
                  {/* Drag handle bar — full-width touch target. CSS `touch-
                      action: none` keeps the browser from interpreting the
                      gesture as a page scroll. ~38px tall touch zone with a
                      bright gold pill in the middle so it's findable. */}
                  <div
                    className="pfk-disp-sheet-handle"
                    onTouchStart={onSheetDragStart} onTouchMove={onSheetDragMove} onTouchEnd={onSheetDragEnd} onTouchCancel={onSheetDragEnd}
                    onMouseDown={(e)=>{ onSheetDragStart(e); const move=(ev)=>onSheetDragMove(ev); const up=()=>{ onSheetDragEnd(); window.removeEventListener('mousemove',move); window.removeEventListener('mouseup',up); }; window.addEventListener('mousemove',move); window.addEventListener('mouseup',up); }}
                    style={{padding:'14px 0 14px',display:'flex',justifyContent:'center',cursor:'ns-resize',touchAction:'none',userSelect:'none',WebkitUserSelect:'none'}}>
                    <div style={{width:80,height:8,borderRadius:4,background:'var(--accent-gold)'}}/>
                  </div>
                  {/* Tab strip — PLAYERS · TEAM · COLLAPSE · VIEW (snap buttons) */}
                  <div style={{display:'flex',alignItems:'stretch',borderBottom:'1px solid var(--border)'}}>
                    {[
                      {id:'players', label:'PLAYERS', count: counts.all},
                      {id:'queue',   label:'QUEUE',   count: me?.slot != null ? myQueueItems.length : null},
                      {id:'team',    label:'TEAM',    count: null},
                    ].map(tab => {
                      const sel = sheetTab === tab.id;
                      return (
                        <button key={tab.id}
                          className="pfk-disp-sheet-tab"
                          onClick={() => { setSheetTab(tab.id); ensureSheetOpen(); }}
                          style={{
                            flex:1,padding:'14px 6px',background:sel?'var(--bg-card)':'transparent',border:'none',cursor:'pointer',
                            color:sel?'var(--accent-gold)':'var(--text-muted)',fontWeight:900,fontSize:12,letterSpacing:1.2,
                            borderBottom:'2px solid '+(sel?'var(--accent-gold)':'transparent')
                          }}>
                          {tab.label}{tab.count!=null && <span style={{opacity:0.7,marginLeft:5,fontWeight:700}}>({tab.count})</span>}
                        </button>
                      );
                    })}
                    {/* COLLAPSE — snap to floor */}
                    <button onClick={()=>setSheetHeight(SHEET_COLLAPSED_PX)} title="Collapse"
                      className="pfk-disp-sheet-snap"
                      style={{padding:'14px 14px',background:'transparent',border:'none',cursor:'pointer',color:'var(--text-secondary)',fontSize:14,fontWeight:900,letterSpacing:0.5,minWidth:54,minHeight:48}}>▼</button>
                    {/* VIEW — snap to ceiling (full open) */}
                    <button onClick={()=>setSheetHeight(sheetMaxPx())} title="Expand to full view"
                      className="pfk-disp-sheet-snap"
                      style={{padding:'14px 14px',background:'transparent',border:'none',cursor:'pointer',color:'var(--accent-gold-text)',fontSize:14,fontWeight:900,letterSpacing:0.5,minWidth:54,minHeight:48}}>▲</button>
                  </div>

                  {/* Tab content — only renders when sheet is expanded enough
                      to show meaningful content. Below the threshold the user
                      is essentially "collapsed" so we save the render. */}
                  {!isSheetCollapsed && (
                    <div style={{flex:1,overflowY:'auto',WebkitOverflowScrolling:'touch'}}>
                      {/* ===== PLAYERS TAB ===== */}
                      {sheetTab === 'players' && (
                        <div style={{padding:'10px 12px'}}>
                          {/* Position filter chips with counts */}
                          <div style={{display:'flex',gap:6,marginBottom:10,padding:4,background:'var(--bg-elevated)',border:'1px solid var(--border)',borderRadius:8,overflowX:'auto',WebkitOverflowScrolling:'touch'}}>
                            {[
                              { id:'all',   label:'ALL',     active:'var(--accent-gold)' },
                              { id:'QB',    label:'QB',      active:'var(--pos-qb)' },
                              { id:'RB',    label:'RB',      active:'var(--pos-rb)' },
                              { id:'WR',    label:'WR',      active:'var(--pos-wr)' },
                              { id:'TE',    label:'TE',      active:'var(--pos-te)' },
                              { id:'picks', label:'PICKS',   active:'var(--accent-gold)' },
                            ].map(t => {
                              const sel = poolTab === t.id;
                              return (
                                <button key={t.id} onClick={()=>setPoolTab(t.id)}
                                  style={{flexShrink:0,padding:'8px 14px',background:sel?t.active:'transparent',border:'none',borderRadius:6,color:sel?'#000':'var(--text-muted)',fontWeight:900,cursor:'pointer',fontSize:12,letterSpacing:0.8,whiteSpace:'nowrap'}}>
                                  {t.label} <span style={{opacity:0.75,fontWeight:700,marginLeft:3}}>{counts[t.id]}</span>
                                </button>
                              );
                            })}
                          </div>
                          {/* Search */}
                          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10}}>
                            <input value={poolFilter} onChange={e=>setPoolFilter(e.target.value)} placeholder={poolTab==='picks'?'Search picks…':poolTab==='all'?'Search assets…':`Search ${poolTab}s…`} style={{flex:1,minWidth:0,padding:'9px 12px',background:'var(--bg-elevated)',border:'1px solid var(--border-strong)',borderRadius:6,color:'var(--text-bright)',fontSize:13}}/>
                            {poolFilter && <button onClick={()=>setPoolFilter('')} style={{padding:'7px 10px',background:'transparent',border:'1px solid var(--border-strong)',borderRadius:5,color:'var(--text-secondary)',cursor:'pointer',fontSize:11}}>✕</button>}
                          </div>
                          {/* Player rows */}
                          <div style={{display:'flex',flexDirection:'column',gap:5}}>
                            {(() => {
                              const matchesTab = (p) => {
                                if(poolTab === 'all') return true;
                                if(poolTab === 'picks') return p.type === 'pick';
                                return p.type !== 'pick' && posOf(p) === poolTab;
                              };
                              let items = pool.filter(p => !p.drafted)
                                .filter(matchesTab)
                                .filter(p => !poolFilter || p.name.toLowerCase().includes(poolFilter.toLowerCase()));
                              if(poolTab === 'picks'){
                                // Picks tab — sort by ascending draft order:
                                // year ASC, then round ASC, then slot ASC.
                                // Picks without a known slot ("via Manager"
                                // form) cluster at the end of their round.
                                const parsePick = (n) => {
                                  const yM = (n||'').match(/(\d{4})/);
                                  const slotM = (n||'').match(/\b(\d+)\.(\d+)\b/);
                                  const rM = (n||'').match(/(\d+)(st|nd|rd|th)/i);
                                  return {
                                    year: yM ? +yM[1] : 9999,
                                    round: slotM ? +slotM[1] : (rM ? +rM[1] : 9),
                                    slot: slotM ? +slotM[2] : 99,
                                  };
                                };
                                items = items.slice().sort((a,b) => {
                                  const pa = parsePick(a.name), pb = parsePick(b.name);
                                  return pa.year - pb.year || pa.round - pb.round || pa.slot - pb.slot;
                                });
                              } else if(fcValues){
                                const valueOf = (it) => {
                                  if(it.sleeperId && fcValues.bySleeperId[it.sleeperId] != null) return fcValues.bySleeperId[it.sleeperId];
                                  const stripped = (it.name || '').replace(/\s*\([^)]*\)\s*$/, '');
                                  const k = normDraftName(stripped);
                                  return fcValues.byName[k] != null ? fcValues.byName[k] : -1;
                                };
                                items = items.slice().sort((a,b) => valueOf(b) - valueOf(a));
                              }
                              return items;
                            })().map(item => {
                              const commishOverride = isCommish && status==='live' && onClockSlot != null && (!me || me.slot !== onClockSlot);
                              const clickable = (myTurn || commishOverride) && status==='live';
                              const inferredPos = item.pos || (item.name||'').match(/\(([A-Za-z]+)\)\s*$/)?.[1]?.toUpperCase() || '';
                              const badgeLabel = item.type === 'pick' ? 'PICK' : (inferredPos || 'PLAYER');
                              const badgeColor = item.type === 'pick' ? 'var(--accent-gold)' : (POS_COLORS[badgeLabel] || 'var(--pos-other)');
                              const cleanName = (item.name || '').replace(/\s*\([A-Za-z]+\)\s*$/, '');
                              return (
                                <div key={item.id} className="pfk-disp-pool-item"
                                  style={{padding:'10px 12px',background:'var(--bg-elevated)',border:'1px solid '+(clickable?'rgba(var(--success-rgb),0.4)':'var(--border)'),borderLeft:'3px solid '+badgeColor,borderRadius:6,display:'flex',alignItems:'center',gap:10}}>
                                  {item.type !== 'pick' && (
                                    <PlayerHeadshot sleeperId={item.sleeperId} name={cleanName} pos={inferredPos} size={32}/>
                                  )}
                                  <span className="pfk-disp-pool-badge" style={{padding:'3px 8px',borderRadius:4,fontSize:10,fontWeight:900,letterSpacing:0.5,background:`color-mix(in srgb, ${badgeColor} 13%, transparent)`,color:badgeColor,border:`1px solid color-mix(in srgb, ${badgeColor} 33%, transparent)`,minWidth:40,textAlign:'center'}}>{badgeLabel}</span>
                                  <span className="pfk-disp-pool-name" style={{flex:1,fontSize:14,fontWeight:700,color:'var(--text-eee)'}}>{cleanName}</span>
                                  {me?.slot != null && (
                                    <button onClick={(e)=>{ e.stopPropagation(); toggleQueue(item.id); }}
                                      title={isInMyQueue(item.id) ? 'Remove from your queue' : 'Add to your queue'}
                                      style={{fontSize:11,color:isInMyQueue(item.id)?'#000':'var(--accent-gold)',background:isInMyQueue(item.id)?'var(--accent-gold)':'transparent',fontWeight:800,letterSpacing:0.4,border:'1px solid rgba(var(--accent-gold-rgb),0.53)',borderRadius:5,padding:'6px 9px',cursor:'pointer',whiteSpace:'nowrap'}}>
                                      {isInMyQueue(item.id) ? '✓ queued' : '+ queue'}
                                    </button>
                                  )}
                                  {clickable && <button onClick={()=>setPickConfirm({...item, _commishOverride: !myTurn && commishOverride, _onClockSlot: onClockSlot, _onClockUsername: onClockTeam?.username})} className="pfk-disp-pool-action" style={{fontSize:11,color:'#000',background: !myTurn && commishOverride ? 'var(--accent-gold)' : 'var(--success)',fontWeight:900,letterSpacing:1,border:'none',borderRadius:5,padding:'7px 14px',cursor:'pointer',whiteSpace:'nowrap'}}>{!myTurn && commishOverride ? `DRAFT FOR @${onClockTeam?.username||'team'}` : 'DRAFT'}</button>}
                                </div>
                              );
                            })}
                            {(() => {
                              const matchesTab = (p) => {
                                if(poolTab === 'all') return true;
                                if(poolTab === 'picks') return p.type === 'pick';
                                return p.type !== 'pick' && posOf(p) === poolTab;
                              };
                              const visibleCount = pool.filter(p => !p.drafted).filter(matchesTab).filter(p => !poolFilter || p.name.toLowerCase().includes(poolFilter.toLowerCase())).length;
                              if(visibleCount > 0) return null;
                              const emptyMsg = poolTab === 'all' ? 'No assets left.'
                                : poolTab === 'picks' ? 'No draft picks left.'
                                : `No ${poolTab}s left.`;
                              return <div style={{color:'var(--text-secondary)',fontSize:13,padding:14,textAlign:'center'}}>{poolFilter ? `No matches for "${poolFilter}".` : emptyMsg}</div>;
                            })()}
                          </div>
                        </div>
                      )}

                      {/* ===== TEAM TAB ===== */}
                      {sheetTab === 'team' && (
                        <div style={{padding:'10px 12px'}}>
                          {/* Manager picker chips — horizontal scroll */}
                          <div style={{display:'flex',gap:6,marginBottom:10,overflowX:'auto',WebkitOverflowScrolling:'touch',padding:'2px 0 6px'}}>
                            {pickOrder.map((slotIdx) => {
                              const t = teams.find(x => x.slot === slotIdx);
                              if(!t) return null;
                              const sel = activeTeamSlot === slotIdx;
                              const slotColor = SLOT_COLORS[slotIdx % SLOT_COLORS.length];
                              const isMine = me && me.slot === slotIdx;
                              return (
                                <button key={slotIdx} onClick={()=>setTeamTabSlot(slotIdx)}
                                  style={{
                                    flexShrink:0,display:'flex',alignItems:'center',gap:8,
                                    padding:'6px 12px 6px 6px',borderRadius:24,
                                    background:sel?'var(--bg-card)':'transparent',
                                    border:'1px solid '+(sel?'var(--accent-gold)':'var(--bg-pill)'),
                                    cursor:'pointer',color:sel?'var(--accent-gold)':'var(--text-secondary)',
                                    fontSize:12,fontWeight:800,letterSpacing:0.3
                                  }}>
                                  <span style={{
                                    width:24,height:24,borderRadius:'50%',
                                    background:`color-mix(in srgb, ${slotColor} 20%, transparent)`,
                                    border:`1px solid color-mix(in srgb, ${slotColor} 40%, transparent)`,
                                    color:slotColor,
                                    display:'inline-flex',alignItems:'center',justifyContent:'center',
                                    fontSize:11,fontWeight:900
                                  }}>{initialOf(t.username)}</span>
                                  {t.username}{isMine && <span style={{color:'var(--success)',fontSize:10,marginLeft:2}}>(you)</span>}
                                </button>
                              );
                            })}
                          </div>

                          {/* Active manager header — name + pick count + screenshot button */}
                          {activeTeam && (
                            <div ref={el=>{ if(el) rosterRefs.current[activeTeamSlot]=el; }} style={{background:'var(--bg-elevated)',border:'1px solid var(--border)',borderRadius:8,padding:'12px 14px'}}>
                              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10,flexWrap:'wrap'}}>
                                <span style={{fontSize:11,color:'var(--text-secondary)',fontWeight:800}}>#{pickOrder.indexOf(activeTeamSlot)+1}</span>
                                <span style={{fontSize:16,fontWeight:900,color:'var(--text-eee)',flex:'1 1 auto',minWidth:0,wordBreak:'break-word'}}>{activeTeam.username}</span>
                                <span style={{fontSize:11,color:'var(--text-secondary)'}}>{activeTeamPicks.length} pick{activeTeamPicks.length===1?'':'s'}</span>
                                {status==='complete' && (
                                  <button onClick={()=>screenshotRoster(activeTeamSlot, activeTeam.username)} title={`Download ${activeTeam.username}'s roster as PNG`}
                                    style={{padding:'6px 12px',background:'var(--accent-gold)',border:'none',borderRadius:5,color:'#000',cursor:'pointer',fontSize:11,fontWeight:800,letterSpacing:0.5}}>📸 SCREENSHOT</button>
                                )}
                              </div>
                              {/* Sleeper-style starting lineup. If the draft was
                                  created from a Sleeper league we use the
                                  league's actual roster_positions (persisted
                                  on draft create); otherwise we fall back to
                                  a sensible dynasty default (1QB/2RB/3WR/1TE/
                                  1FLEX/1SF/1K). Players auto-fill into their
                                  best matching slot, sorted by FantasyCalc
                                  value desc. Future picks + leftover players
                                  go on the bench. */}
                              {(() => {
                                if(activeTeamPicks.length === 0) {
                                  return <div style={{color:'var(--text-secondary)',fontSize:13,padding:14,textAlign:'center'}}>No picks yet.</div>;
                                }
                                // Slot eligibility — Sleeper roster_position
                                // names map to the position groups they accept.
                                const SLOT_ELIG = {
                                  QB: ['QB'], RB: ['RB'], WR: ['WR'], TE: ['TE'], K: ['K'], DEF: ['DEF'],
                                  FLEX: ['RB','WR','TE'],
                                  WRT: ['RB','WR','TE'],
                                  REC_FLEX: ['WR','TE'],
                                  SUPER_FLEX: ['QB','RB','WR','TE'],
                                  IDP_FLEX: ['DL','LB','DB'],
                                };
                                const SLOT_LABELS = {
                                  QB:'QB', RB:'RB', WR:'WR', TE:'TE', K:'K', DEF:'DEF',
                                  FLEX:'WRT', WRT:'WRT', REC_FLEX:'WR/TE',
                                  SUPER_FLEX:'SF', IDP_FLEX:'IDP',
                                };
                                const DEFAULT_SLOTS = ['QB','RB','RB','WR','WR','WR','TE','FLEX','SUPER_FLEX','K'];
                                const rawSlots = (Array.isArray(draft.roster_positions) && draft.roster_positions.length)
                                  ? draft.roster_positions : DEFAULT_SLOTS;
                                const starterSlots = rawSlots.filter(s => s !== 'BN' && s !== 'TAXI' && s !== 'IR');
                                // Value lookup — same FC values used elsewhere.
                                const valueOf = (it) => {
                                  if(!it || it.type === 'pick') return -1;
                                  if(!fcValues) return 0;
                                  if(it.sleeperId && fcValues.bySleeperId[it.sleeperId] != null) return fcValues.bySleeperId[it.sleeperId];
                                  const stripped = (it.name || '').replace(/\s*\([^)]*\)\s*$/, '');
                                  const k = normDraftName(stripped);
                                  return fcValues.byName[k] != null ? fcValues.byName[k] : 0;
                                };
                                // Split future picks off — they always go to bench.
                                const playerPicks = activeTeamPicks.filter(p => p.item && p.item.type !== 'pick');
                                const futurePicks = activeTeamPicks.filter(p => !p.item || p.item.type === 'pick');
                                // Auto-fill: walk slots in order, take best
                                // available eligible player for each. Greedy
                                // — not optimal but matches the Sleeper feel.
                                const sorted = playerPicks.slice().sort((a,b) => valueOf(b.item) - valueOf(a.item));
                                const used = new Set(); // pickIdx of placed picks
                                const lineup = starterSlots.map((slotKey) => {
                                  const accepts = SLOT_ELIG[slotKey] || [slotKey];
                                  const found = sorted.find(p => !used.has(p.pickIdx) && accepts.includes(posOf(p.item)));
                                  if(found) used.add(found.pickIdx);
                                  return { slotKey, label: SLOT_LABELS[slotKey] || slotKey, pick: found || null };
                                });
                                const bench = sorted.filter(p => !used.has(p.pickIdx));
                                const benchPlusPicks = [...bench, ...futurePicks];
                                // Slot color — use the position's color when
                                // filled, otherwise grey.
                                const lineupSlotColor = (slotKey, pick) => {
                                  if(pick && pick.item) {
                                    if(pick.item.type === 'pick') return 'var(--accent-gold)';
                                    return POS_COLORS[posOf(pick.item)] || 'var(--pos-other)';
                                  }
                                  // Empty — slot accent based on slot type
                                  if(['FLEX','WRT','REC_FLEX'].includes(slotKey)) return '#22d3ee';
                                  if(slotKey === 'SUPER_FLEX') return '#ec4899';
                                  return POS_COLORS[slotKey] || 'var(--pos-other)';
                                };
                                const renderRow = (pick, slotLabel, slotColor, isBench) => {
                                  const it = pick?.item;
                                  const isFuturePick = it?.type === 'pick';
                                  const cleanNm = it ? (it.name || '').replace(/\s*\([A-Za-z]+\)\s*$/, '') : '';
                                  const r = pick ? (Math.floor(pick.pickIdx / Math.max(1,teams.length)) + 1) : null;
                                  const pInRound = pick ? ((pick.pickIdx % Math.max(1,teams.length)) + 1) : null;
                                  return (
                                    <div style={{
                                      display:'flex',alignItems:'center',gap:10,
                                      padding:'8px 10px',background:isBench?'var(--bg-elevated)':'var(--bg-card)',
                                      borderLeft:'3px solid '+slotColor,borderRadius:5
                                    }}>
                                      <span style={{padding:'4px 7px',borderRadius:4,fontSize:10,fontWeight:900,letterSpacing:0.4,background:`color-mix(in srgb, ${slotColor} 13%, transparent)`,color:slotColor,border:`1px solid color-mix(in srgb, ${slotColor} 33%, transparent)`,minWidth:46,textAlign:'center'}}>{slotLabel}</span>
                                      {pick && !isFuturePick && (
                                        <PlayerHeadshot sleeperId={it?.sleeperId} name={cleanNm} pos={posOf(it)} size={32}/>
                                      )}
                                      {pick ? (
                                        <>
                                          <span style={{flex:1,fontSize:14,fontWeight:700,color:'var(--text-eee)',wordBreak:'break-word',minWidth:0}}>
                                            {cleanNm || '(missing)'}
                                            {!isFuturePick && it && <span style={{display:'block',fontSize:10,color:'var(--text-secondary)',fontWeight:600,marginTop:1}}>{posOf(it)||'—'}</span>}
                                            {isFuturePick && <span style={{display:'block',fontSize:10,color:'var(--accent-gold-text)',fontWeight:600,marginTop:1}}>future pick</span>}
                                            {pick.auto && <span style={{color:'var(--warning)',fontStyle:'italic',marginLeft:6,fontSize:10,fontWeight:600}}>(auto)</span>}
                                          </span>
                                          <span style={{fontSize:11,color:'var(--text-secondary)',fontWeight:700,whiteSpace:'nowrap',flexShrink:0}}>{r}.{pInRound}</span>
                                        </>
                                      ) : (
                                        <span style={{flex:1,fontSize:13,color:'var(--text-secondary)',fontStyle:'italic'}}>empty</span>
                                      )}
                                    </div>
                                  );
                                };
                                return (
                                  <>
                                    {/* STARTERS */}
                                    <div style={{fontSize:10,fontWeight:900,letterSpacing:1.5,color:'var(--text-secondary)',marginBottom:6}}>STARTERS</div>
                                    <div style={{display:'flex',flexDirection:'column',gap:4,marginBottom:12}}>
                                      {lineup.map((row, i) => (
                                        <div key={`s${i}`}>
                                          {renderRow(row.pick, row.label, lineupSlotColor(row.slotKey, row.pick), false)}
                                        </div>
                                      ))}
                                    </div>
                                    {/* BENCH */}
                                    {benchPlusPicks.length > 0 && (
                                      <>
                                        <div style={{fontSize:10,fontWeight:900,letterSpacing:1.5,color:'var(--text-secondary)',marginBottom:6}}>BENCH ({benchPlusPicks.length})</div>
                                        <div style={{display:'flex',flexDirection:'column',gap:4}}>
                                          {benchPlusPicks.map(p => {
                                            const it = p.item;
                                            const isFuturePick = it?.type === 'pick';
                                            const pos = isFuturePick ? 'PICK' : posOf(it);
                                            const color = isFuturePick ? 'var(--accent-gold)' : (POS_COLORS[pos] || 'var(--pos-other)');
                                            return <div key={p.pickIdx}>{renderRow(p, pos || 'BN', color, true)}</div>;
                                          })}
                                        </div>
                                      </>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                      )}

                      {/* ===== QUEUE TAB =====
                          Per-user player queue. Drafted players are filtered
                          at render time, not pruned from the persisted list.
                          Auto-pick toggle ships in Phase 2 — for now this is
                          pure queue management. */}
                      {sheetTab === 'queue' && (
                        <div style={{padding:'10px 12px'}}>
                          {me?.slot == null ? (
                            <div style={{textAlign:'center',padding:'24px 12px',color:'var(--text-secondary)',fontSize:13,lineHeight:1.55}}>
                              Claim a team first — your queue is per-team.
                            </div>
                          ) : (
                            <>
                              {/* Auto-pick toggle — persisted on draft.user_state[slot].auto_pick */}
                              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:10,padding:'10px 12px',background:'var(--bg-elevated)',border:'1px solid '+(myUserState?.auto_pick?'var(--accent-gold)':'var(--border)'),borderRadius:6,marginBottom:10}}>
                                <div style={{minWidth:0}}>
                                  <div style={{fontSize:12,fontWeight:900,color:'var(--accent-gold-text)',letterSpacing:0.8}}>⚡ AUTO-PICK</div>
                                  <div style={{fontSize:11,color:'var(--text-secondary)',marginTop:2,lineHeight:1.4}}>When on, picks the top of your queue ~1.5s after you're on the clock. Empty queue → highest-value available player.</div>
                                  <div style={{fontSize:10,color:'var(--text-faint)',marginTop:3,lineHeight:1.4,fontStyle:'italic'}}>Requires at least 1 open tab on this draft to fire — yours or anyone else's (commish, spectator, leaguemate).</div>
                                </div>
                                <button onClick={() => updateMyUserState(s => ({ ...s, auto_pick: !s.auto_pick }))}
                                  aria-pressed={!!myUserState?.auto_pick}
                                  style={{flexShrink:0,padding:'8px 14px',borderRadius:14,fontWeight:900,fontSize:11,letterSpacing:1.2,background:myUserState?.auto_pick?'var(--accent-gold)':'transparent',color:myUserState?.auto_pick?'#000':'var(--text-muted)',border:'1px solid '+(myUserState?.auto_pick?'var(--accent-gold)':'var(--border-strong)'),cursor:'pointer',minWidth:60}}>
                                  {myUserState?.auto_pick ? 'ON' : 'OFF'}
                                </button>
                              </div>
                              {myQueueItems.length === 0 ? (
                                <div style={{textAlign:'center',padding:'18px 12px',color:'var(--text-secondary)',fontSize:13,lineHeight:1.55}}>
                                  Add players from the Players tab to build your queue.
                                </div>
                              ) : (
                                <div style={{display:'flex',flexDirection:'column',gap:5}}>
                              {myQueueItems.map((item, idx) => {
                                const inferredPos = item.pos || (item.name||'').match(/\(([A-Za-z]+)\)\s*$/)?.[1]?.toUpperCase() || '';
                                const badgeLabel = item.type === 'pick' ? 'PICK' : (inferredPos || 'PLAYER');
                                const badgeColor = item.type === 'pick' ? 'var(--accent-gold)' : (POS_COLORS[badgeLabel] || 'var(--pos-other)');
                                const cleanName = (item.name || '').replace(/\s*\([A-Za-z]+\)\s*$/, '');
                                const isFirst = idx === 0;
                                const isLast = idx === myQueueItems.length - 1;
                                const canDraftFromQueue = myTurn && status === 'live';
                                return (
                                  <div key={item.id} style={{padding:'8px 10px',background:'var(--bg-elevated)',border:'1px solid '+(canDraftFromQueue?'rgba(var(--success-rgb),0.4)':'var(--border)'),borderLeft:'3px solid '+badgeColor,borderRadius:6,display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                                    <span style={{fontSize:11,fontWeight:900,color:'var(--text-secondary)',minWidth:20,textAlign:'right'}}>{idx + 1}.</span>
                                    {item.type !== 'pick' && (
                                      <PlayerHeadshot sleeperId={item.sleeperId} name={cleanName} pos={inferredPos} size={28}/>
                                    )}
                                    <span style={{padding:'3px 7px',borderRadius:4,fontSize:10,fontWeight:900,letterSpacing:0.5,background:`color-mix(in srgb, ${badgeColor} 13%, transparent)`,color:badgeColor,border:`1px solid color-mix(in srgb, ${badgeColor} 33%, transparent)`,minWidth:38,textAlign:'center'}}>{badgeLabel}</span>
                                    <span style={{flex:1,fontSize:13,fontWeight:700,color:'var(--text-eee)',minWidth:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{cleanName}</span>
                                    {canDraftFromQueue && (
                                      <button onClick={()=>setPickConfirm({...item, _commishOverride:false, _onClockSlot:onClockSlot, _onClockUsername:onClockTeam?.username})}
                                        title="Draft this player now" style={{fontSize:11,color:'#000',background:'var(--success)',fontWeight:900,letterSpacing:0.8,border:'none',borderRadius:5,padding:'6px 11px',cursor:'pointer',whiteSpace:'nowrap',flexShrink:0}}>DRAFT</button>
                                    )}
                                    <button onClick={()=>moveQueueItem(item.id,'up')} disabled={isFirst} title="Move up" style={{padding:'4px 8px',background:'transparent',border:'1px solid '+(isFirst?'var(--bg-pill)':'var(--border-strong)'),borderRadius:4,color:isFirst?'var(--border-strong)':'var(--text-muted)',fontSize:11,fontWeight:900,cursor:isFirst?'default':'pointer',minWidth:30}}>▲</button>
                                    <button onClick={()=>moveQueueItem(item.id,'down')} disabled={isLast} title="Move down" style={{padding:'4px 8px',background:'transparent',border:'1px solid '+(isLast?'var(--bg-pill)':'var(--border-strong)'),borderRadius:4,color:isLast?'var(--border-strong)':'var(--text-muted)',fontSize:11,fontWeight:900,cursor:isLast?'default':'pointer',minWidth:30}}>▼</button>
                                    <button onClick={()=>removeFromQueue(item.id)} title="Remove from queue" style={{padding:'4px 8px',background:'transparent',border:'1px solid #553030',borderRadius:4,color:'var(--danger)',fontSize:11,fontWeight:900,cursor:'pointer',minWidth:30}}>✕</button>
                                  </div>
                                );
                              })}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
}

// ---------- Top-level Dispersal page (route: /dispersal[/id]) ----------
function DispersalApp({draftId}){
  // Dev-only completed-drafts counter — moved here from the main page so it
  // shows across the whole dispersal flow (setup + live draft). Tucks under
  // the master toolbar (top:80) so it doesn't overlap the toolbar's right
  // controls on mobile. Also renders placeholder text while loading or on
  // error so Evan can SEE the badge exists even if the query is misbehaving.
  const [completedDispersalCount,setCompletedDispersalCount] = useState(null);
  const [counterErr,setCounterErr] = useState(null);
  useEffect(()=>{
    if(!isDevHost() || !sb) return;
    let cancelled = false;
    (async () => {
      try{
        const res = await sb.from('dispersal_drafts').select('*', { count:'exact', head:true }).eq('status', 'complete');
        if(cancelled) return;
        if(res?.error){ setCounterErr(res.error.message || 'query error'); return; }
        if(typeof res?.count === 'number') setCompletedDispersalCount(res.count);
        else setCounterErr('no count returned');
      }catch(e){
        if(!cancelled) setCounterErr(e?.message || 'fetch failed');
      }
    })();
    return ()=>{ cancelled = true; };
  },[]);
  // hasActiveClaim — true when the user has claimed a team in THIS draft.
  // Drives the MasterToolbar's forceNewTabLinks behavior so nav clicks open
  // in a new tab and the dispersal session (websocket, queue, auto-pick)
  // stays alive. Read from the same localStorage key DispersalDraft uses;
  // refreshed on storage events (other tabs) and the custom
  // 'pfk-disp-claim-change' event DispersalDraft dispatches when me changes.
  const readClaim = () => {
    if(!draftId) return false;
    try {
      const raw = localStorage.getItem('pfk_disp_'+draftId);
      if(!raw) return false;
      const parsed = JSON.parse(raw);
      return parsed?.slot != null;
    } catch(e) { return false; }
  };
  const [hasActiveClaim,setHasActiveClaim] = useState(readClaim);
  useEffect(() => {
    if(!draftId) { setHasActiveClaim(false); return; }
    const refresh = () => setHasActiveClaim(readClaim());
    refresh();
    window.addEventListener('storage', refresh);
    window.addEventListener('pfk-disp-claim-change', refresh);
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener('pfk-disp-claim-change', refresh);
    };
  }, [draftId]);
  return (
    <div style={{background:'var(--bg-page)',minHeight:'100vh',color:'var(--text-primary)',fontFamily:"'Inter','Segoe UI',sans-serif"}}>
      <MasterToolbar currentTab="dispersal" compact={!!draftId} subtitle={draftId ? 'Dispersal Draft' : 'New Dispersal Draft'} forceNewTabLinks={hasActiveClaim}/>
      {/* Dev-only completed-drafts counter. Live draft view hides this — Evan
          wants every pixel for the draft board there. Setup view keeps it so
          the counter is still visible while creating new drafts. */}
      {isDevHost() && !draftId && (
        <div style={{display:'flex',justifyContent:'flex-end',padding:'8px 14px 0',maxWidth:1240,margin:'0 auto'}}>
          <div style={{display:'inline-block',padding:'6px 12px',background:'var(--bg-elevated)',border:'1px solid rgba(var(--accent-gold-rgb),0.4)',borderRadius:20,fontSize:11,color:'var(--accent-gold-text)',fontWeight:800,letterSpacing:0.5,whiteSpace:'nowrap'}}>
            {completedDispersalCount !== null
              ? <>🎲 {completedDispersalCount.toLocaleString()} completed drafts</>
              : counterErr
              ? <span style={{color:'var(--danger)'}}>🎲 counter: {counterErr}</span>
              : <span style={{color:'var(--text-secondary)'}}>🎲 counter: loading…</span>}
          </div>
        </div>
      )}
      {draftId ? <DispersalDraft draftId={draftId}/> : <DispersalSetup/>}
      {/* Skip footer on the live draft view — the fixed bottom-sheet covers
          the bottom of the screen there and a footer below it would never
          be visible. Setup screen still renders the footer. */}
      {!draftId && <PFKFooter/>}
    </div>
  );
}

// ============================================================================
// 🔍 LOOKUP — Phase 0 of the PFK Trust Layer
// "Type a Sleeper username, see public-data facts that would've taken you an
// hour to gather manually." Origin doc + spec live in ~/pfk_research/.
// Facts only — never judgments. Carfax model, not "snitch site."
// ============================================================================

// Seasons to scan are computed at page load — the current NFL season is the
// current calendar year (dynasty leagues roll over to the next year in offseason).
// Same approach the Power Rankings tool uses, which has been proven correct.
const LOOKUP_CURRENT_YEAR = new Date().getFullYear();
const SEASONS_TO_SCAN = [
  String(LOOKUP_CURRENT_YEAR),
  String(LOOKUP_CURRENT_YEAR-1),
  String(LOOKUP_CURRENT_YEAR-2),
  String(LOOKUP_CURRENT_YEAR-3),
  String(LOOKUP_CURRENT_YEAR-4),
];

// Resolve a Sleeper user (accepts username OR numeric user_id)
const lookupResolveUser = async (input) => {
  const q = (input||'').trim();
  if(!q) throw new Error('Enter a Sleeper username or user ID');
  const r = await fetch(`https://api.sleeper.app/v1/user/${encodeURIComponent(q)}`);
  if(!r.ok) throw new Error('No Sleeper user found with that name');
  const u = await r.json();
  if(!u || !u.user_id) throw new Error('No Sleeper user found with that name');
  return u;
};

// Pull all leagues for a user across the seasons we scan. Used for activity +
// orphan history. Each league row carries ._season so we can walk back.
// cache:'no-store' bypasses HTTP caching — same approach Power Rankings uses,
// since stale league lists were one source of the undercount bug.
const lookupAllLeagues = async (userId) => {
  const all = [];
  await Promise.all(SEASONS_TO_SCAN.map(async (s) => {
    try{
      const lgs = await fetch(`https://api.sleeper.app/v1/user/${userId}/leagues/nfl/${s}`, {cache:'no-store'}).then(r=>r.json());
      (lgs||[]).forEach(lg => all.push({...lg, _season:s}));
    }catch(e){}
  }));
  // Sharp Tracker pool ingestion (Phase 0a). We don't have a user object
  // here — just the user_id — so username/display_name go in as null and
  // will be backfilled by the crawler on its next meta-refresh pass.
  pfkRecordUserLeagues({ user_id: userId }, all);
  return all;
};

// Orphan check: walk DYNASTY leagues only and see if the user was in a league
// during a prior season but is NOT in it (or its descendant) the next season.
// Redraft leagues (settings.type !== 2) are never counted as orphans — they
// inherently end after one season, so missing in season N+1 is normal, not a
// red flag. Same logic for keeper leagues.
//
// Conversion-year leniency: if a dynasty league's immediate predecessor was
// REDRAFT (settings.type === 0), the user's first dynasty year was forced on
// them by a commish converting the league. Leaving after that conversion year
// isn't really "abandoning a dynasty team" — they signed up for redraft.
// (Reported by whiskeyyankee 2026-04-29: his "Lets Go Brandon" league was
// redraft in 2022, commish converted it to dynasty in 2023, he left after.)
const lookupOrphanHistory = async (userId, allLeagues) => {
  // Restrict to dynasty leagues only (settings.type === 2)
  const dynastyOnly = (allLeagues||[]).filter(lg => (lg.settings||{}).type === 2);
  // Map of ALL leagues (any type) by id — used to detect redraft→dynasty
  // conversions when a dynasty league's previous_league_id points to a
  // redraft league we have visibility into.
  const allById = {};
  (allLeagues||[]).forEach(lg => { allById[String(lg.league_id)] = lg; });
  const wasConvertedFromRedraft = (lg) => {
    const prevId = lg.previous_league_id;
    if(!prevId || String(prevId) === '0') return false;
    const prev = allById[String(prevId)];
    if(!prev) return false;
    return (prev.settings||{}).type === 0;
  };
  const heldBySeason = {};
  dynastyOnly.forEach(lg => {
    if(!heldBySeason[lg._season]) heldBySeason[lg._season] = new Set();
    heldBySeason[lg._season].add(String(lg.league_id));
  });
  const details = [];
  const sortedSeasons = SEASONS_TO_SCAN.slice().sort();
  for(let i=0;i<sortedSeasons.length-1;i++){
    const sCur = sortedSeasons[i], sNext = sortedSeasons[i+1];
    const cur = heldBySeason[sCur] || new Set();
    if(!heldBySeason[sNext]) continue;
    const next = heldBySeason[sNext];
    cur.forEach(lid => {
      const lg = dynastyOnly.find(x => String(x.league_id) === lid && x._season === sCur);
      if(!lg) return;
      // Conversion-year leniency: if THIS dynasty year was forced by a
      // redraft→dynasty conversion, don't flag the user for leaving after it.
      if(wasConvertedFromRedraft(lg)) return;
      const descendants = dynastyOnly.filter(x => x._season === sNext && String(x.previous_league_id||'') === lid);
      if(descendants.length === 0 && !next.has(lid)){
        details.push({ league_name: lg.name || `League #${lid}`, league_id: lid, season_left: sCur });
      }
    });
  }

  // ===== False-positive filter ===========================================
  // The candidate detection above ("user was in year N, no descendant league
  // in year N+1, not in year N+1 directly") collapses three very different
  // situations into one signal:
  //   (1) User actually left a real ongoing dynasty league   → real orphan
  //   (2) The whole league folded (commish didn't roll over) → not the
  //       user's fault; nobody is in the league anymore
  //   (3) The "league" was a sandbox the user spun up alone   → never had
  //       real rosters to abandon
  // For each candidate, fetch /league/{id}/users (one call). If only the
  // candidate is in there (<=1 user), it's a solo sandbox — skip. Otherwise
  // parallel-fetch every other user's year-N+1 leagues; if NOBODY rolled
  // forward into a successor with previous_league_id matching, the league
  // folded — skip. On any fetch error, retain the candidate (preserves
  // prior behavior so a Sleeper API hiccup doesn't silently undercount).
  const filteredDetails = (await Promise.all(details.map(async (d) => {
    try {
      const users = await fetch(`https://api.sleeper.app/v1/league/${d.league_id}/users`, {cache:'no-store'}).then(r => r.json());
      if (!Array.isArray(users) || users.length <= 1) return null; // sandbox / solo test league
      const others = users.filter(u => String(u.user_id) !== String(userId));
      const nextSeason = String(+d.season_left + 1);
      const otherLgsResults = await Promise.all(others.map(u =>
        fetch(`https://api.sleeper.app/v1/user/${u.user_id}/leagues/nfl/${nextSeason}`, {cache:'no-store'})
          .then(r => r.json())
          .catch(() => [])
      ));
      const someoneContinued = otherLgsResults.some(lgs =>
        Array.isArray(lgs) && lgs.some(lg => String(lg.previous_league_id || '') === String(d.league_id))
      );
      if (!someoneContinued) return null; // folded league — nobody rolled forward
      return d;
    } catch (e) {
      return d; // fetch error: retain so we don't silently undercount
    }
  }))).filter(Boolean);

  const nowYear = new Date().getFullYear();
  const last12 = filteredDetails.filter(d => +d.season_left >= nowYear - 1).length;
  const last24 = filteredDetails.filter(d => +d.season_left >= nowYear - 2).length;

  // Total UNIQUE dynasty leagues this user has joined (chain-root deduped).
  // Each dynasty league gets a new league_id per season chained via
  // previous_league_id. Walk the chain back to find the root for each league;
  // distinct roots = unique leagues for the orphan-rate denominator.
  const allIds = new Set(dynastyOnly.map(l => String(l.league_id)));
  const roots = new Set();
  dynastyOnly.forEach(lg => {
    let cur = lg, depth = 0;
    while(cur && depth < 10){
      const prev = cur.previous_league_id;
      // If prev isn't in our scanned data, treat current as the root we have visibility into.
      if(!prev || String(prev) === '0' || !allIds.has(String(prev))){
        roots.add(String(cur.league_id));
        break;
      }
      cur = dynastyOnly.find(d => String(d.league_id) === String(prev));
      depth++;
    }
  });
  const uniqueLeagues = roots.size;
  const orphanRatePct = uniqueLeagues > 0 ? (filteredDetails.length / uniqueLeagues) * 100 : 0;

  return {
    last12mo: last12,
    last24mo: last24,
    all: filteredDetails.length,
    uniqueLeagues,
    orphanRatePct,
    details: filteredDetails,
  };
};

// Compute trade stats for a single season's dynasty leagues. Helper used by
// lookupTradeActivity to compute current + prior seasons in parallel.
//
// IMPORTANT: dynasty trades happen YEAR-ROUND, but Sleeper stores them under
// whichever league_id is active for that season. In April 2026 (now), most
// active trading lives in 2026 league_ids. The 2025 league_id holds end-of-
// playoffs trades from Jan/Feb 2026 + the in-season weeks of 2025 itself.
const lookupTradesForSeason = async (userId, allLeagues, season) => {
  const dynastyTarget = (allLeagues||[]).filter(lg =>
    (lg.settings||{}).type === 2 && lg._season === season
  );
  if(dynastyTarget.length === 0){
    return { season, leaguesCount: 0, totalTrades: 0, avgPerLeague: 0, lastTradeDate: null };
  }
  let totalTrades = 0;
  let lastTradeDate = null;
  let leaguesWithUser = 0;
  await Promise.all(dynastyTarget.map(async (lg) => {
    try{
      const rosters = await fetch(`https://api.sleeper.app/v1/league/${lg.league_id}/rosters`, {cache:'no-store'}).then(r=>r.json());
      const userRoster = (rosters||[]).find(r => r.owner_id === userId || (r.co_owners||[]).includes(userId));
      if(!userRoster) return;
      leaguesWithUser++;
      const userRosterId = userRoster.roster_id;
      const weeks = Array.from({length: 17}, (_, i) => i + 1);
      const txArrays = await Promise.all(weeks.map(w =>
        fetch(`https://api.sleeper.app/v1/league/${lg.league_id}/transactions/${w}`, {cache:'no-store'})
          .then(r=>r.json()).catch(()=>[])
      ));
      txArrays.flat().forEach(tx => {
        if(tx.type !== 'trade') return;
        if(tx.status !== 'complete') return;
        if(!(tx.roster_ids||[]).includes(userRosterId)) return;
        totalTrades++;
        const ts = tx.status_updated || tx.created;
        if(ts){
          const d = new Date(ts);
          if(!lastTradeDate || d > lastTradeDate) lastTradeDate = d;
        }
      });
    }catch(e){}
  }));
  return {
    season,
    leaguesCount: leaguesWithUser,
    totalTrades,
    avgPerLeague: leaguesWithUser > 0 ? (totalTrades / leaguesWithUser) : 0,
    lastTradeDate,
  };
};

// Trade activity snapshot — fetches CURRENT + 2 PRIOR seasons in parallel.
// Showing 3 years gives commish a fuller picture early in a season. Heavy
// (~600 API calls for a 12-league user) — runs in background so page doesn't
// feel slow.
const lookupTradeActivity = async (userId, allLeagues) => {
  const seasons = [
    String(LOOKUP_CURRENT_YEAR),
    String(LOOKUP_CURRENT_YEAR - 1),
    String(LOOKUP_CURRENT_YEAR - 2),
  ];
  const [current, prior, prior2] = await Promise.all(
    seasons.map(s => lookupTradesForSeason(userId, allLeagues, s))
  );
  return { current, prior, prior2 };
};

// FantasyCalc value cache, keyed by format string. Each format = different value set
// (TEP, PPR, SF, league size all change player rankings). We cache per-page-load
// to avoid re-fetching the same format twice.
const _fcByFormat = {};
// Translate a Sleeper league into FantasyCalc API parameters + PFK's per-league
// TEP value. FC's `tepLevel` URL param is empirically a no-op (values identical
// at tepLevel=0/1/2 — verified 2026-05-13), so the fetchers always pass
// tepLevel=0 and we apply PFK's own TEP_MULT to TE values after the fetch.
// Raw `tep` (Sleeper's bonus_rec_te) is carried through to drive both the chip
// label and the post-fetch multiplier. Cache key uses raw tep so 0/0.25/0.5/0.75/1.0
// leagues don't collide.
const fcFormatForLeague = (lg) => {
  const ss = lg.scoring_settings || {};
  const rp = lg.roster_positions || [];
  const numQbs = rp.includes('SUPER_FLEX') ? 2 : 1;
  const recVal = parseFloat(ss.rec ?? 0);
  const ppr = recVal === 1 ? 1 : recVal === 0.5 ? 0.5 : 0;
  const tep = parseFloat(ss.bonus_rec_te ?? 0) || 0;
  const numTeams = lg.total_rosters || 12;
  return { numQbs, ppr, tep, numTeams, key: `${numQbs}q-${ppr}p-${tep}t-${numTeams}n` };
};
// Picks ARE in the main FC /values/current response — listed by name (e.g.
// "2026 1st", "2027 2nd", "2026 Pick 1.05"). Players are looked up by sleeperId,
// picks are looked up by name. Both maps are built from one fetch per format.
const _fcPicksByFormat = {};
const fetchFcForFormat = async (fmt) => {
  if(_fcByFormat[fmt.key]) return _fcByFormat[fmt.key];
  // tepLevel hardcoded 0 — FC ignores the param. PFK applies TEP_MULT to TE
  // values below so 0/0.25/0.5/0.75/1.0 leagues all get correctly-tiered TEs.
  const url = `https://api.fantasycalc.com/values/current?isDynasty=true&numQbs=${fmt.numQbs}&numTeams=${fmt.numTeams}&ppr=${fmt.ppr}&tepLevel=0`;
  const teMult = TEP_MULT[fmt.tep]?.TE ?? 1;
  try{
    const arr = await fetch(url).then(r=>r.json());
    const playerMap = {};
    const pickMap = {};
    (arr||[]).forEach(row => {
      const sid = row?.player?.sleeperId;
      const nm = row?.player?.name || '';
      const pos = (row?.player?.position || '').toUpperCase();
      const raw = row?.value ?? 0;
      const v = pos === 'TE' ? raw * teMult : raw;
      if(sid != null){
        playerMap[String(sid)] = v;
      }
      // Generic picks ("2026 1st") and specific picks ("2026 Pick 1.05") both keyed by name
      if(/^\d{4}\s+(1st|2nd|3rd|4th|5th)$/.test(nm) || /^\d{4}\s+Pick\s/.test(nm)){
        pickMap[nm] = v;
      }
    });
    _fcByFormat[fmt.key] = playerMap;
    _fcPicksByFormat[fmt.key] = pickMap;
    return playerMap;
  }catch(e){
    _fcByFormat[fmt.key] = {};
    _fcPicksByFormat[fmt.key] = {};
    return {};
  }
};
// Look up a GENERIC future pick value (e.g. fcPickValue("...", 2026, 1) → "2026 1st" → $3214).
// Used as the fallback when we can't determine the slot.
const fcPickValue = (formatKey, year, round) => {
  const map = _fcPicksByFormat[formatKey] || {};
  const suffix = round === 1 ? '1st' : round === 2 ? '2nd' : round === 3 ? '3rd' : `${round}th`;
  return map[`${year} ${suffix}`] || 0;
};
// Look up a SLOT-SPECIFIC pick value (e.g. fcPickSlotValue("...", 2026, 1, 5) → "2026 Pick 1.05" → $3811).
// Slot is the position WITHIN the round (snake-aware computation done by caller).
// Falls back to the generic value if FC doesn't have the specific slot.
const fcPickSlotValue = (formatKey, year, round, slotInRound) => {
  const map = _fcPicksByFormat[formatKey] || {};
  const padded = String(slotInRound).padStart(2, '0');
  const specific = map[`${year} Pick ${round}.${padded}`];
  if(specific != null) return specific;
  return fcPickValue(formatKey, year, round);
};
// Compute the slot a pick lands at WITHIN its round, given the original owner's
// R1 slot. Snake reverses every other round; linear stays the same.
const slotInRoundFor = (originalR1Slot, round, draftType, leagueSize) => {
  if(draftType === 'snake'){
    // R1 = slot, R2 = N-slot+1, R3 = slot, R4 = N-slot+1, ...
    return round % 2 === 1 ? originalR1Slot : (leagueSize - originalR1Slot + 1);
  }
  // 'linear' or anything else: slot stays the same every round
  return originalR1Slot;
};
// Build map: roster_id → R1 slot for this league's CURRENT-season rookie draft.
// Returns null if we can't determine slots (no draft, auction draft, etc.).
// Tries draft.slot_to_roster_id first (post-draft), falls back to draft.draft_order
// (which is keyed by user_id and needs roster mapping).
const lookupDraftSlots = async (lid, rosters) => {
  let drafts;
  try{ drafts = await fetch(`https://api.sleeper.app/v1/league/${lid}/drafts`, {cache:'no-store'}).then(r=>r.json()); }
  catch(e){ return null; }
  if(!Array.isArray(drafts) || drafts.length === 0) return null;
  // Pick the most recent rookie-style draft: typically rounds 4-6 for dynasty.
  // (Startup drafts have 15+ rounds; we don't want those for slot inference.)
  const rookieDrafts = drafts.filter(d => {
    const rounds = (d.settings || {}).rounds || 0;
    return rounds > 0 && rounds <= 6;
  });
  if(rookieDrafts.length === 0) return null;
  rookieDrafts.sort((a,b) => (b.start_time || 0) - (a.start_time || 0));
  const draft = rookieDrafts[0];
  const draftType = draft.type; // 'snake' | 'linear' | 'auction'
  if(draftType === 'auction') return null;
  const slotByRoster = {};
  // Path 1: slot_to_roster_id is populated (after draft is set up)
  const sToR = draft.slot_to_roster_id || {};
  if(Object.keys(sToR).length > 0){
    Object.entries(sToR).forEach(([slot, rid]) => {
      if(rid != null) slotByRoster[rid] = parseInt(slot);
    });
    return { slotByRoster, draftType, leagueSize: (rosters||[]).length };
  }
  // Path 2: fallback to draft_order (user_id → slot). Map via roster.owner_id.
  const draftOrder = draft.draft_order || {};
  if(Object.keys(draftOrder).length === 0) return null;
  const usedSlots = new Set();
  (rosters||[]).forEach(r => {
    if(r.owner_id && draftOrder[r.owner_id] != null){
      const s = parseInt(draftOrder[r.owner_id]);
      slotByRoster[r.roster_id] = s;
      usedSlots.add(s);
    }
  });
  // For orphaned rosters (no owner_id, so no draft_order entry), assign the
  // remaining slot(s). If exactly one orphan + one missing slot, easy match.
  const N = (rosters||[]).length;
  const unmapped = (rosters||[]).filter(r => slotByRoster[r.roster_id] == null);
  const missingSlots = [];
  for(let s=1; s<=N; s++) if(!usedSlots.has(s)) missingSlots.push(s);
  if(unmapped.length === missingSlots.length){
    unmapped.forEach((r, i) => { slotByRoster[r.roster_id] = missingSlots[i]; });
  }
  return { slotByRoster, draftType, leagueSize: N };
};

// Team strength snapshot: for each CURRENT-season dynasty league the user is in,
// fetch rosters, sum each team's FantasyCalc dynasty value (using the FC value set
// matched to THAT LEAGUE'S format — SF/1QB, PPR, TEP, league size), rank teams
// within the league, and find where the user lands. Then average those ranks
// across leagues.
//
// Per Evan: framed as a "matching signal" not a judgment — commissioners can use
// this both to find competitive players AND to find casual managers, depending on
// what they want for their league. Same data, two markets.
const lookupTeamStrength = async (userId, allLeagues) => {
  const targetSeason = String(LOOKUP_CURRENT_YEAR);
  let dynastyTarget = (allLeagues||[]).filter(lg =>
    (lg.settings||{}).type === 2 && lg._season === targetSeason
  );
  if(dynastyTarget.length === 0){
    const prior = String(LOOKUP_CURRENT_YEAR - 1);
    dynastyTarget = (allLeagues||[]).filter(lg =>
      (lg.settings||{}).type === 2 && lg._season === prior
    );
  }
  if(dynastyTarget.length === 0){
    return { leaguesCount: 0, avgRank: null, avgTeams: 0, ranks: [] };
  }
  const ranks = [];
  // Pick years/rounds we value (matches FantasyCalc's published generic-pick range)
  const PICK_YEARS = [LOOKUP_CURRENT_YEAR, LOOKUP_CURRENT_YEAR+1, LOOKUP_CURRENT_YEAR+2];
  const PICK_ROUNDS = [1, 2, 3, 4];
  await Promise.all(dynastyTarget.map(async (lg) => {
    try{
      const fmt = fcFormatForLeague(lg);
      const fcMap = await fetchFcForFormat(fmt);
      const valueOfPlayer = (pid) => fcMap[String(pid)] || 0;
      // Fetch rosters AND traded picks in parallel — picks add significant value
      // to dynasty rosters (a 2026 1st can be worth $3K+, a 2027 1st $3K+).
      // Without picks, contender teams that hold lots of picks get undervalued.
      const [rosters, tradedPicks] = await Promise.all([
        fetch(`https://api.sleeper.app/v1/league/${lg.league_id}/rosters`, {cache:'no-store'}).then(r=>r.json()),
        fetch(`https://api.sleeper.app/v1/league/${lg.league_id}/traded_picks`, {cache:'no-store'}).then(r=>r.json()).catch(()=>[]),
      ]);
      // Now that we have rosters, fetch draft slots for the current-year rookie
      // draft. This lets us use SLOT-SPECIFIC FC pick values for 2026 picks
      // ("2026 Pick 1.05" = $3,811 vs generic "2026 1st" = $3,214). Closes the
      // accuracy gap with FantasyCalc's website rankings for current-year picks.
      // For 2027/2028 we don't know slot order yet → use generic.
      const draftSlots = await lookupDraftSlots(lg.league_id, rosters);
      // Build pick ownership map: each roster starts owning their own picks for
      // PICK_YEARS × PICK_ROUNDS. Then apply traded_picks to redirect ownership.
      // Sleeper traded_picks entries: roster_id = original owner, owner_id = current owner.
      const rosterCount = (rosters||[]).length;
      const pickOwner = {}; // "year-round-origRid" → currentOwnerRid
      for(let r = 1; r <= rosterCount; r++){
        for(const y of PICK_YEARS){
          for(const round of PICK_ROUNDS){
            pickOwner[`${y}-${round}-${r}`] = r;
          }
        }
      }
      (tradedPicks||[]).forEach(tp => {
        const y = parseInt(tp.season);
        const round = tp.round;
        const orig = tp.roster_id;
        const cur = tp.owner_id;
        if(PICK_YEARS.includes(y) && PICK_ROUNDS.includes(round) && orig != null && cur != null){
          pickOwner[`${y}-${round}-${orig}`] = cur;
        }
      });
      // Aggregate pick value per current-owner roster
      const pickValByRoster = {};
      Object.keys(pickOwner).forEach(key => {
        const [yStr, roundStr, origStr] = key.split('-');
        const y = parseInt(yStr);
        const round = parseInt(roundStr);
        const origRosterId = parseInt(origStr);
        const owner = pickOwner[key];
        // Slot-specific value for current year, generic for future years
        let val;
        if(y === LOOKUP_CURRENT_YEAR && draftSlots && draftSlots.slotByRoster[origRosterId]){
          const r1Slot = draftSlots.slotByRoster[origRosterId];
          const slotInRound = slotInRoundFor(r1Slot, round, draftSlots.draftType, draftSlots.leagueSize);
          val = fcPickSlotValue(fmt.key, y, round, slotInRound);
        } else {
          val = fcPickValue(fmt.key, y, round);
        }
        pickValByRoster[owner] = (pickValByRoster[owner] || 0) + val;
      });
      const rosterValues = (rosters||[]).map(r => {
        const playerVal = (r.players||[]).reduce((sum, pid) => sum + valueOfPlayer(pid), 0);
        const pickVal = pickValByRoster[r.roster_id] || 0;
        return {
          roster_id: r.roster_id,
          owner_id: r.owner_id,
          co_owners: r.co_owners || [],
          totalValue: playerVal + pickVal,
        };
      });
      rosterValues.sort((a,b) => b.totalValue - a.totalValue);
      const userIndex = rosterValues.findIndex(r =>
        r.owner_id === userId || r.co_owners.includes(userId)
      );
      if(userIndex >= 0){
        ranks.push({
          league_name: lg.name,
          rank: userIndex + 1,
          total_teams: rosterValues.length,
        });
      }
    }catch(e){}
  }));
  if(ranks.length === 0){
    return { leaguesCount: 0, avgRank: null, avgTeams: 0, ranks: [] };
  }
  const avgRank = ranks.reduce((s, r) => s + r.rank, 0) / ranks.length;
  const avgTeams = ranks.reduce((s, r) => s + r.total_teams, 0) / ranks.length;
  return { leaguesCount: ranks.length, avgRank, avgTeams, ranks };
};

// Career stats — total W/L, championships, championship %, longest title streak.
// Walks each unique dynasty league chain back via previous_league_id (deeper
// than SEASONS_TO_SCAN, so multi-year dynasties get full credit). Only counts
// COMPLETED seasons — in-progress leagues distort the W-L and ring-rate.
//
// Streak definition: longest run of consecutive championship wins WITHIN a
// single league chain. Winning different leagues in different years doesn't
// stack.
const lookupCareerStats = async (userId, allLeagues) => {
  const dynasty = (allLeagues||[]).filter(lg => (lg.settings||{}).type === 2);
  if(!dynasty.length){
    return { totalWins:0, totalLosses:0, totalSeasons:0, championships:0, longestStreak:0, leagueChains:0 };
  }
  // Index leagues we already have by league_id for chain-walking lookup.
  const byId = {};
  dynasty.forEach(lg => { byId[String(lg.league_id)] = lg; });
  // Find the leaves (most-recent leagues in each chain) — leagues whose
  // league_id isn't anyone's previous_league_id within our scan.
  const referenced = new Set();
  dynasty.forEach(lg => { if(lg.previous_league_id) referenced.add(String(lg.previous_league_id)); });
  const leaves = dynasty.filter(lg => !referenced.has(String(lg.league_id)));
  // Dedupe leaves by league_id in case the same league_id appears multiple times.
  const seenLeafIds = new Set();
  const uniqueLeaves = leaves.filter(l => {
    const k = String(l.league_id);
    if(seenLeafIds.has(k)) return false;
    seenLeafIds.add(k);
    return true;
  });
  // Walk each leaf back via previous_league_id, fetching older seasons we
  // didn't already have in the SEASONS_TO_SCAN window.
  const chains = []; // each entry: array of leagues (oldest-first)
  await Promise.all(uniqueLeaves.map(async (leaf) => {
    const chain = [leaf];
    let cur = leaf;
    for(let i = 0; i < 12; i++){
      if(!cur.previous_league_id || String(cur.previous_league_id) === '0') break;
      const prevKey = String(cur.previous_league_id);
      let prev = byId[prevKey];
      if(!prev){
        try{
          prev = await fetch(`${SLEEPER}/league/${cur.previous_league_id}`).then(r=>r.json()).catch(()=>null);
        }catch(e){ prev = null; }
        if(!prev || !prev.league_id) break;
        byId[prevKey] = prev;
      }
      // Only walk back into dynasty parents — if we hit a redraft predecessor
      // (the redraft→dynasty conversion case), stop. Older redraft data
      // would distort the streak/W-L numbers.
      if(prev.settings && prev.settings.type !== 2 && prev.settings.type !== undefined) break;
      chain.push(prev);
      cur = prev;
    }
    chains.push(chain.slice().reverse()); // oldest first
  }));
  // For each season in each chain: fetch rosters + winners_bracket, score it.
  // Wins/losses come from roster.settings (Sleeper's standings cache).
  // Championship is the winners_bracket final-round winner.
  const results = []; // {chainIdx, season, won, played}
  await Promise.all(chains.flatMap((chain, chainIdx) =>
    chain.map(async (lg, seasonIdx) => {
      // Skip in-progress / pre-draft leagues — their W/L and bracket aren't final.
      if(lg.status && lg.status !== 'complete') return;
      try{
        const [rosters, bracket] = await Promise.all([
          fetch(`${SLEEPER}/league/${lg.league_id}/rosters`).then(r=>r.json()).catch(()=>[]),
          fetch(`${SLEEPER}/league/${lg.league_id}/winners_bracket`).then(r=>r.json()).catch(()=>[]),
        ]);
        const myRoster = (Array.isArray(rosters) ? rosters : []).find(r =>
          r.owner_id === userId || (Array.isArray(r.co_owners) && r.co_owners.includes(userId))
        );
        if(!myRoster) return;
        const wins = Number(myRoster.settings?.wins || 0);
        const losses = Number(myRoster.settings?.losses || 0);
        if(wins + losses === 0) return; // didn't play any regular-season games
        // Championship — final round winner of the playoff bracket.
        let won = false;
        if(Array.isArray(bracket) && bracket.length > 0){
          const maxR = Math.max(...bracket.map(g => g.r));
          const finalRound = bracket.filter(g => g.r === maxR);
          // p===null is the championship game (not a 3rd-place match)
          const champGame = finalRound.find(g => g.p == null) || finalRound[0];
          if(champGame?.w === myRoster.roster_id) won = true;
        }
        results.push({ chainIdx, season: Number(lg.season || seasonIdx), wins, losses, won });
      }catch(e){}
    })
  ));
  // Aggregate
  let totalWins = 0, totalLosses = 0, championships = 0;
  results.forEach(r => { totalWins += r.wins; totalLosses += r.losses; if(r.won) championships++; });
  const totalSeasons = results.length;
  // Longest streak: per chain, sort seasons chronologically and count consecutive wins.
  let longestStreak = 0;
  for(let ci = 0; ci < chains.length; ci++){
    const chainResults = results.filter(r => r.chainIdx === ci).sort((a, b) => a.season - b.season);
    let cur = 0;
    for(const r of chainResults){
      if(r.won){ cur++; if(cur > longestStreak) longestStreak = cur; }
      else cur = 0;
    }
  }
  // Peak ring year: the calendar season in which the user won the most
  // championships. If they're in 5 dynasty leagues and won 3 of them in
  // the same year, that year totals 3 rings — beat that and you have a
  // new peak. Ties resolve to the most recent year (we sort by year desc
  // before walking).
  const ringsByYear = {};
  results.forEach(r => { if(r.won) ringsByYear[r.season] = (ringsByYear[r.season] || 0) + 1; });
  const yearsDesc = Object.keys(ringsByYear).sort((a, b) => Number(b) - Number(a));
  let peakRingCount = 0, peakRingYear = null;
  for(const y of yearsDesc){
    if(ringsByYear[y] > peakRingCount){
      peakRingCount = ringsByYear[y];
      peakRingYear = y;
    }
  }
  return { totalWins, totalLosses, totalSeasons, championships, longestStreak, leagueChains: chains.length, peakRingCount, peakRingYear };
};

// Format a date as "X time ago" (e.g. "2 hours ago", "3 days ago", "5 yrs ago")
const lookupTimeAgo = (date) => {
  if(!date) return null;
  const d = (date instanceof Date) ? date : new Date(date);
  if(isNaN(d.getTime())) return null;
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if(sec < 60) return `${sec} sec ago`;
  if(sec < 3600) return `${Math.floor(sec/60)} min ago`;
  if(sec < 86400) return `${Math.floor(sec/3600)} hours ago`;
  if(sec < 86400*30) return `${Math.floor(sec/86400)} days ago`;
  if(sec < 86400*365) return `${Math.floor(sec/(86400*30))} months ago`;
  const yrs = Math.floor(sec/(86400*365));
  return `${yrs} ${yrs===1?'yr':'yrs'} ago`;
};

// ---------- Lookup search page ----------
function LookupSearch(){
  const [query,setQuery] = useState('');
  const [err,setErr] = useState('');
  const submit = (e) => {
    if(e) e.preventDefault();
    const q = query.trim();
    if(!q){ setErr('Enter a Sleeper username or user ID'); return; }
    window.location.href = '/lookup/' + encodeURIComponent(q);
  };
  return (
    <div style={{maxWidth:680,margin:'80px auto',padding:'0 20px',color:'var(--text-eee)',textAlign:'center'}}>
      <div style={{fontSize:32,fontWeight:900,color:'var(--accent-gold-text)',letterSpacing:3,marginBottom:10}}>🔍 SLEEPER SNAPSHOT</div>
      <div style={{fontSize:14,color:'var(--text-bbb)',marginBottom:14,letterSpacing:0.3,lineHeight:1.6,maxWidth:540,margin:'0 auto 14px'}}>Type any Sleeper username to instantly see their account age, total dynasty leagues, trade activity, orphan history, and roster average dynasty value. Vet new leaguemates in seconds.</div>
      <div style={{marginBottom:24}}>
        <HowItWorksButton title="Sleeper Snapshot">
          <p><strong>What it does:</strong> Type any Sleeper username and get a full scouting report on that manager — built entirely from public Sleeper data.</p>
          <ul style={{paddingLeft:20,marginTop:10,display:'flex',flexDirection:'column',gap:6}}>
            <li><strong>Account age + activity</strong> — when they joined Sleeper, how many leagues they're in, how active they are.</li>
            <li><strong>Dynasty league list</strong> with format details (Superflex, TEP, league size, scoring).</li>
            <li><strong>Recent trade activity</strong> across all their dynasty leagues — so you can see how they actually negotiate.</li>
            <li><strong>Championship history</strong> — total ships, championship rate, longest title streak, peak ring year.</li>
            <li><strong>Team strength rank</strong> — average finish across their dynasty leagues, computed from FantasyCalc roster values + traded picks.</li>
            <li><strong>Orphan history</strong> — leagues they joined mid-stream (signals "took over a bad team" vs "founding member").</li>
          </ul>
          <p style={{marginTop:12}}>Use it to vet new leaguemates before letting them in, scout potential trade partners, or just look up your own profile to see what other commissioners would see.</p>
          <p style={{marginTop:10,fontSize:12,color:'var(--text-muted)',fontStyle:'italic'}}>Facts only, never judgments. Carfax model — same data anyone could gather manually, just instant.</p>
        </HowItWorksButton>
      </div>
      <form onSubmit={submit} style={{display:'flex',gap:8,marginBottom:14}}>
        <input
          value={query} onChange={e=>{setQuery(e.target.value); setErr('');}}
          placeholder="Sleeper username (e.g. ej97)"
          style={{flex:1,padding:'14px 18px',background:'var(--bg-elevated)',border:'1.5px solid rgba(var(--accent-gold-rgb),0.4)',borderRadius:8,color:'var(--text-bright)',fontSize:16,fontFamily:'inherit'}}/>
        <button type="submit" style={{padding:'14px 24px',background:'var(--accent-gold)',border:'none',borderRadius:8,color:'#000',fontWeight:900,fontSize:14,letterSpacing:1.5,cursor:'pointer',whiteSpace:'nowrap'}}>LOOKUP →</button>
      </form>
      {err && <div style={{padding:'10px 14px',background:'var(--danger-bg)',border:'1px solid var(--danger)',borderRadius:6,color:'var(--danger)',fontSize:13,marginBottom:14}}>{err}</div>}
    </div>
  );
}

// ---------- Lookup profile page ----------
function LookupProfile({ identifier }){
  const [user,setUser] = useState(null);
  const [leagues,setLeagues] = useState(null);
  const [orphan,setOrphan] = useState(null);
  const [loading,setLoading] = useState(true);
  const [err,setErr] = useState('');
  const [linkCopied,setLinkCopied] = useState(false);
  // Trade activity loads in the background after the rest of the profile renders
  // (it's heavy — ~200 API calls for an active user). null = not yet loaded.
  const [tradeActivity,setTradeActivity] = useState(null);
  const [tradeLoading,setTradeLoading] = useState(false);
  // Recent trades modal (shared with Trade Finder). Open when the commish wants
  // to scout a user's actual trade history for collusion patterns. Stays simple
  // — passes the looked-up user, lets the modal scan + render.
  const [tradesModalOpen,setTradesModalOpen] = useState(false);
  const [fcAssetIndex,setFcAssetIndex] = useState(null); // for player name resolution
  useEffect(() => {
    // Lazy-load FC's rich asset list (with names) so the trades modal can render
    // "Bijan Robinson" instead of "Player #4866". One-time module-cached fetch.
    fetchTradeFinderAssets().then(setFcAssetIndex);
  },[]);
  const lookupResolvePlayerName = (pid) => {
    if(!fcAssetIndex || !pid) return `Player #${pid}`;
    const hit = fcAssetIndex.find(a => a.sleeperId && String(a.sleeperId) === String(pid));
    return hit ? hit.name : `Player #${pid}`;
  };
  // Team strength (avg power-rank across current dynasty leagues) — also background.
  const [teamStrength,setTeamStrength] = useState(null);
  const [strengthLoading,setStrengthLoading] = useState(false);
  // Career stats — total W/L, championships, championship %, longest title
  // streak. Heavy: walks every dynasty league chain back via previous_league_id
  // and fetches rosters + winners_bracket per season. Background-loaded.
  const [careerStats,setCareerStats] = useState(null);
  const [careerLoading,setCareerLoading] = useState(false);
  // Snapshot share state — same pattern as the dispersal share-pool feature.
  const profileCardRef = useRef(null);
  const [profileShareBusy,setProfileShareBusy] = useState(false);
  const [desktopShare,setDesktopShare] = useState(null);
  const [shareToast,setShareToast] = useState('');
  // Orphan history is always visible on the live snapshot page; this toggle
  // controls whether it gets baked into the SHARE image. Defaults to on so
  // existing behavior is preserved — opt-out only.
  const [shareIncludeOrphan,setShareIncludeOrphan] = useState(true);

  useEffect(()=>{
    let cancelled = false;
    (async () => {
      setLoading(true); setErr('');
      try{
        const u = await lookupResolveUser(identifier);
        if(cancelled) return;
        setUser(u);
        const lgs = await lookupAllLeagues(u.user_id);
        if(cancelled) return;
        setLeagues(lgs);
        const o = await lookupOrphanHistory(u.user_id, lgs);
        if(cancelled) return;
        setOrphan(o);
      }catch(e){ if(!cancelled) setErr(e.message || 'Lookup failed'); }
      finally{ if(!cancelled) setLoading(false); }
    })();
    return ()=>{ cancelled = true; };
  },[identifier]);

  // Background load: trade activity. Fires after main profile data lands.
  useEffect(()=>{
    if(!user || !leagues) return;
    let cancelled = false;
    (async () => {
      setTradeLoading(true);
      try{
        const ta = await lookupTradeActivity(user.user_id, leagues);
        if(!cancelled) setTradeActivity(ta);
      }catch(e){}
      finally{ if(!cancelled) setTradeLoading(false); }
    })();
    return ()=>{ cancelled = true; };
  },[user, leagues]);

  // Background load: team strength (avg power rank). Runs in parallel with trade activity.
  useEffect(()=>{
    if(!user || !leagues) return;
    let cancelled = false;
    (async () => {
      setStrengthLoading(true);
      try{
        const ts = await lookupTeamStrength(user.user_id, leagues);
        if(!cancelled) setTeamStrength(ts);
      }catch(e){}
      finally{ if(!cancelled) setStrengthLoading(false); }
    })();
    return ()=>{ cancelled = true; };
  },[user, leagues]);

  // Background load: career stats (W/L, championships, longest streak). Walks
  // each dynasty league chain backward, so it can take a few seconds on heavy
  // users — runs after main profile renders, doesn't block.
  useEffect(()=>{
    if(!user || !leagues) return;
    let cancelled = false;
    (async () => {
      setCareerLoading(true);
      try{
        const cs = await lookupCareerStats(user.user_id, leagues);
        if(!cancelled) setCareerStats(cs);
      }catch(e){}
      finally{ if(!cancelled) setCareerLoading(false); }
    })();
    return ()=>{ cancelled = true; };
  },[user, leagues]);

  const copyLink = () => {
    try{ navigator.clipboard.writeText(window.location.href); }catch(e){}
    setLinkCopied(true);
    setTimeout(()=>setLinkCopied(false), 2000);
  };

  // Mirror the latest "background data loaded" status into a ref so the
  // share function (which lives in a closure) can poll it without stale-
  // closure bugs. Each background section gates its own card with `&&`,
  // so if SHARE fires before trade activity / team strength / career
  // stats have landed, those sections are silently dropped from the
  // captured image. Refs let us hold the share until everything is ready.
  const shareDataReadyRef = useRef(false);
  shareDataReadyRef.current = !!(tradeActivity && teamStrength && careerStats);

  // 📸 SHARE PROFILE — captures the off-screen profile card and:
  //   mobile: native share sheet (Photos / iMessage / X / Discord)
  //   desktop: pops a modal with Download / Copy / Share to X
  // Same pattern as the dispersal share-pool feature.
  const shareProfileImage = async () => {
    if(!window.html2canvas){ alert('Screenshot library not loaded yet — refresh and try again.'); return; }
    if(!profileCardRef.current){ alert('Profile card not ready — try again in a sec.'); return; }
    setProfileShareBusy(true);
    try{
      // Auto-wait for all background sections to finish loading so the
      // captured image isn't missing data the user expects (especially
      // Trade Activity, which is the slowest — ~200 API calls). Cap at
      // 20 seconds so the share never hangs indefinitely; if a section
      // genuinely failed to load we still capture what's there.
      if(!shareDataReadyRef.current){
        const waitStart = Date.now();
        while(!shareDataReadyRef.current && Date.now() - waitStart < 20000){
          await new Promise(r => setTimeout(r, 200));
        }
      }
      const canvas = await window.html2canvas(profileCardRef.current, { backgroundColor:'#080808', scale:2, useCORS:true });
      const filename = `pfk_${(user?.username||user?.user_id||'profile').replace(/[^a-z0-9]/gi,'_')}.png`;
      const blob = await new Promise(res => canvas.toBlob(res, 'image/png'));
      const isMobile = /iPad|iPhone|iPod|Android/i.test(navigator.userAgent);
      if(isMobile && blob && navigator.canShare){
        const file = new File([blob], filename, { type:'image/png' });
        if(navigator.canShare({ files:[file] })){
          try{
            await navigator.share({ files:[file], title:`PFK profile — ${user?.display_name || user?.username || ''}` });
            return;
          }catch(e){ if(e && e.name==='AbortError') return; }
        }
      }
      if(isMobile){
        // Mobile fallback when Web Share isn't supported
        const url = blob ? URL.createObjectURL(blob) : canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.download = filename; link.href = url;
        document.body.appendChild(link); link.click(); document.body.removeChild(link);
        if(blob) setTimeout(()=>URL.revokeObjectURL(url), 1000);
        return;
      }
      // Desktop: pop the share modal so user picks an action
      if(blob){
        setShareToast('');
        setDesktopShare({ blob, filename });
      }
    }catch(e){ alert('Share failed: ' + (e.message||e)); }
    finally{ setProfileShareBusy(false); }
  };

  if(loading){
    return <div style={{maxWidth:680,margin:'80px auto',textAlign:'center',color:'var(--text-secondary)',fontSize:14}}>Looking up <span style={{color:'var(--accent-gold-text)',fontFamily:'monospace'}}>{identifier}</span>…</div>;
  }
  if(err){
    return (
      <div style={{maxWidth:680,margin:'80px auto',padding:'0 20px',textAlign:'center',color:'var(--text-eee)'}}>
        <div style={{padding:'20px 24px',background:'var(--danger-bg)',border:'1px solid var(--danger)',borderRadius:8,color:'var(--danger)',fontSize:14,marginBottom:18}}>{err}</div>
        <a href="/lookup" style={{color:'var(--accent-gold-text)',textDecoration:'none',fontWeight:800}}>← Try another lookup</a>
      </div>
    );
  }

  // Stats — dynasty-focused since that's PFK's audience.
  // Sleeper league.settings.type: 0=redraft, 1=keeper, 2=dynasty
  // Use dynamic current year (we're in 2026 now — not all leagues have 2025 listings).
  const lgs = leagues || [];
  const activeYear = String(LOOKUP_CURRENT_YEAR);
  let activeLeagues = lgs.filter(l => l._season === activeYear);
  let displayedActiveYear = activeYear;
  // Fallback: if current year has no leagues at all (very early offseason before
  // anyone's rolled over), show prior year so we still display something useful.
  if(activeLeagues.length === 0){
    const prior = String(LOOKUP_CURRENT_YEAR - 1);
    const priorLeagues = lgs.filter(l => l._season === prior);
    if(priorLeagues.length > 0){
      activeLeagues = priorLeagues;
      displayedActiveYear = prior;
    }
  }
  const dynastyActive = activeLeagues.filter(l => (l.settings||{}).type === 2);
  // Sleeper user object: created may be a unix ms timestamp
  const createdMs = user.metadata?.created || user.created;
  const createdDate = createdMs ? new Date(Number(createdMs)) : null;

  const sectionHdr = {fontSize:11,color:'var(--accent-gold-text)',fontWeight:800,letterSpacing:1.5,marginBottom:10,textAlign:'center'};
  const card = {background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:10,padding:'18px 22px',marginBottom:14};

  return (
    <div style={{maxWidth:680,margin:'40px auto',padding:'0 20px',color:'var(--text-eee)'}}>
      {/* Profile header — "SLEEPER SNAPSHOT" page title centered on its own
          row at the top of the card, then avatar + username + share button
          on the row below it. Evan 2026-05-02 wanted the title fully
          centered on the page rather than tucked next to the username. */}
      <div style={card}>
        <div style={{textAlign:'center',marginBottom:14,paddingBottom:12,borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'center',gap:10,flexWrap:'wrap'}}>
          <div style={{fontSize:13,fontWeight:900,color:'var(--accent-gold-text)',letterSpacing:3,textTransform:'uppercase'}}>Sleeper Snapshot</div>
          <HowItWorksButton title="Sleeper Snapshot" compact>
            <p><strong>What it does:</strong> Pulls public Sleeper data into a single scouting report on any manager — account age, dynasty leagues, trade activity, championship history, team strength rank, orphan history.</p>
            <p style={{marginTop:10}}>Use it to vet new leaguemates, scout trade partners, or check what your own profile looks like to outsiders.</p>
            <p style={{marginTop:10,fontSize:12,color:'var(--text-muted)',fontStyle:'italic'}}>Facts only, no judgments. Same data anyone could gather manually, just instant.</p>
          </HowItWorksButton>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:16,flexWrap:'wrap'}}>
          {user.avatar && <img src={`https://sleepercdn.com/avatars/thumbs/${user.avatar}`} alt="" style={{width:64,height:64,borderRadius:'50%',objectFit:'cover',background:'var(--bg-elevated)',border:'1px solid var(--border)'}} onError={e=>e.target.style.display='none'}/>}
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:24,fontWeight:900,color:'var(--accent-gold-text)',letterSpacing:0.5,wordBreak:'break-word'}}>{user.display_name || user.username}</div>
            {user.username && user.username !== user.display_name && <div style={{fontSize:12,color:'var(--text-secondary)',marginTop:2}}>@{user.username}</div>}
            {createdDate && <div style={{fontSize:13,color:'var(--text-secondary)',marginTop:6}}>Account created: <span style={{color:'var(--text-bright)'}}>{createdDate.toISOString().slice(0,10)}</span> ({lookupTimeAgo(createdDate)})</div>}
          </div>
          {(() => {
          const allDataReady = !!(tradeActivity && teamStrength && careerStats);
          // When sections are still loading we let the user click anyway —
          // the share function auto-waits up to 20s — but the button copy
          // signals what's happening so it doesn't feel frozen.
          const label = profileShareBusy
            ? (allDataReady ? 'BUILDING…' : 'WAITING ON DATA…')
            : (allDataReady ? '📸 SHARE' : '📸 SHARE (loading)');
          return (
            <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:6}}>
              <button onClick={shareProfileImage} disabled={profileShareBusy} title={allDataReady ? 'Share a PFK-branded snapshot of this profile' : 'Share — waits for trade activity + career stats to finish loading first'} style={{padding:'10px 14px',background:profileShareBusy?'var(--bg-pill)':'var(--accent-gold)',border:'none',borderRadius:7,color:profileShareBusy?'var(--text-muted)':'#000',fontWeight:900,cursor:profileShareBusy?'default':'pointer',fontSize:12,letterSpacing:1.2,whiteSpace:'nowrap'}}>{label}</button>
              <label title="Uncheck to hide the orphan history section from the shared image only — it stays visible on this page." style={{display:'inline-flex',alignItems:'center',gap:6,fontSize:11,color:'var(--text-secondary)',fontWeight:700,cursor:'pointer',userSelect:'none'}}>
                <input type="checkbox" checked={shareIncludeOrphan} onChange={e=>setShareIncludeOrphan(e.target.checked)} style={{width:14,height:14,accentColor:'var(--accent-gold)',cursor:'pointer'}}/>
                Include orphan history
              </label>
            </div>
          );
        })()}
        </div>
      </div>

      {/* Career stats — moved to the top per Evan 2026-05-02 (was below trade
          activity / standings). Walks every dynasty league chain back via
          previous_league_id so this counts every completed season we can find,
          not just the last 5. Skips in-progress leagues so the rates aren't
          distorted by mid-season data. */}
      <div style={card}>
        <div style={sectionHdr}>🏆 CAREER DYNASTY RECORD</div>
        {careerLoading && !careerStats && (
          <div style={{padding:'14px',color:'var(--text-secondary)',fontSize:13,textAlign:'center'}}>Walking dynasty league chains…</div>
        )}
        {careerStats && careerStats.totalSeasons === 0 && (
          <div style={{padding:'14px',color:'var(--text-secondary)',fontSize:13,textAlign:'center'}}>No completed dynasty seasons yet.</div>
        )}
        {careerStats && careerStats.totalSeasons > 0 && (() => {
          const totalGames = careerStats.totalWins + careerStats.totalLosses;
          const winPct = totalGames > 0 ? (careerStats.totalWins / totalGames * 100) : 0;
          const ringPct = careerStats.totalSeasons > 0 ? (careerStats.championships / careerStats.totalSeasons * 100) : 0;
          return (
            <>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:12}}>
                <div style={{padding:'12px 14px',background:'var(--bg-elevated)',border:'1px solid var(--border)',borderRadius:8,textAlign:'center'}}>
                  <div style={{fontSize:24,fontWeight:900,color:'var(--accent-gold-text)',lineHeight:1}}>{careerStats.totalWins}-{careerStats.totalLosses}</div>
                  <div style={{fontSize:10,color:'var(--text-secondary)',fontWeight:800,letterSpacing:1.5,marginTop:6}}>RECORD</div>
                  <div style={{fontSize:11,color:'var(--text-secondary)',fontWeight:700,marginTop:2}}>{winPct.toFixed(1)}% win rate</div>
                </div>
                <div style={{padding:'12px 14px',background:'var(--bg-elevated)',border:'1px solid var(--border)',borderRadius:8,textAlign:'center'}}>
                  <div style={{fontSize:24,fontWeight:900,color:'var(--accent-gold-text)',lineHeight:1}}>{careerStats.championships}</div>
                  <div style={{fontSize:10,color:'var(--text-secondary)',fontWeight:800,letterSpacing:1.5,marginTop:6}}>CHAMPIONSHIPS</div>
                  <div style={{fontSize:11,color:'var(--text-secondary)',fontWeight:700,marginTop:2}}>{ringPct.toFixed(1)}% of seasons</div>
                </div>
                <div style={{padding:'12px 14px',background:'var(--bg-elevated)',border:'1px solid var(--border)',borderRadius:8,textAlign:'center'}}>
                  <div style={{fontSize:24,fontWeight:900,color:'var(--accent-gold-text)',lineHeight:1}}>{careerStats.longestStreak}</div>
                  <div style={{fontSize:10,color:'var(--text-secondary)',fontWeight:800,letterSpacing:1.5,marginTop:6}}>LONGEST TITLE STREAK</div>
                  <div style={{fontSize:11,color:'var(--text-secondary)',fontWeight:700,marginTop:2}}>{careerStats.longestStreak === 0 ? '—' : `${careerStats.longestStreak === 1 ? 'one ring' : `${careerStats.longestStreak} in a row`}`}</div>
                </div>
                <div style={{padding:'12px 14px',background:'var(--bg-elevated)',border:'1px solid var(--border)',borderRadius:8,textAlign:'center'}}>
                  <div style={{fontSize:24,fontWeight:900,color:'var(--accent-gold-text)',lineHeight:1}}>{careerStats.peakRingCount || 0}</div>
                  {/* Long label intentionally wraps onto 2 lines so it fits in
                      the tile box at the same min-width (140px) as the others. */}
                  <div style={{fontSize:9,color:'var(--text-secondary)',fontWeight:800,letterSpacing:1,marginTop:6,lineHeight:1.25}}>MOST TITLES WON IN A SINGLE SEASON</div>
                  <div style={{fontSize:11,color:'var(--text-secondary)',fontWeight:700,marginTop:2}}>{careerStats.peakRingCount > 0 ? `${careerStats.peakRingCount === 1 ? 'in' : 'in'} ${careerStats.peakRingYear}` : '—'}</div>
                </div>
                <div style={{padding:'12px 14px',background:'var(--bg-elevated)',border:'1px solid var(--border)',borderRadius:8,textAlign:'center'}}>
                  <div style={{fontSize:24,fontWeight:900,color:'var(--accent-gold-text)',lineHeight:1}}>{careerStats.totalSeasons}</div>
                  <div style={{fontSize:10,color:'var(--text-secondary)',fontWeight:800,letterSpacing:1.5,marginTop:6}}>SEASONS PLAYED</div>
                </div>
                <div style={{padding:'12px 14px',background:'var(--bg-elevated)',border:'1px solid var(--border)',borderRadius:8,textAlign:'center'}}>
                  <div style={{fontSize:24,fontWeight:900,color:'var(--accent-gold-text)',lineHeight:1}}>{dynastyActive.length}</div>
                  <div style={{fontSize:10,color:'var(--text-secondary)',fontWeight:800,letterSpacing:1.5,marginTop:6}}>TOTAL LEAGUES</div>
                  <div style={{fontSize:11,color:'var(--text-secondary)',fontWeight:700,marginTop:2}}>{displayedActiveYear} active</div>
                </div>
              </div>
              <div style={{fontSize:11,color:'var(--text-secondary)',marginTop:10,fontStyle:'italic',lineHeight:1.6,textAlign:'center'}}>Title streak counts consecutive championships in the same league. In-progress seasons are not included.</div>
            </>
          );
        })()}
      </div>

      {/* Trade activity — current + prior season. Both load in background.
          Showing both gives commish more data points early in a new season. */}
      <div style={card}>
        <div style={sectionHdr}>🤝 TRADE ACTIVITY</div>
        {tradeLoading && !tradeActivity && (
          <div style={{padding:'14px',color:'var(--text-secondary)',fontSize:13,textAlign:'center'}}>Counting trades across leagues…</div>
        )}
        {tradeActivity && (() => {
          // Inline sub-section renderer. Current year shows TOTAL / AVG / LAST TRADE
          // (LAST TRADE is the live "is this person trading now" signal).
          // Prior years drop LAST TRADE since it's always "X months ago" by definition
          // (always end-of-playoffs-ish) — useless info per Evan.
          const renderSeason = (data, label, showLastTrade) => {
            if(!data || data.leaguesCount === 0){
              return (
                <div style={{marginBottom:12}}>
                  <div style={{fontSize:11,color:'var(--accent-gold-text)',fontWeight:800,letterSpacing:1.5,marginBottom:6}}>{label}</div>
                  <div style={{padding:'10px 14px',background:'var(--bg-elevated)',border:'1px solid var(--border)',borderRadius:8,fontSize:12,color:'var(--text-secondary)',textAlign:'center'}}>No dynasty leagues found for this season.</div>
                </div>
              );
            }
            return (
              <div style={{marginBottom:12}}>
                <div style={{fontSize:11,color:'var(--accent-gold-text)',fontWeight:800,letterSpacing:1.5,marginBottom:6}}>{label}</div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))',gap:8}}>
                  <div style={{padding:'10px 12px',background:'var(--bg-elevated)',border:'1px solid var(--border)',borderRadius:8,textAlign:'center'}}>
                    <div style={{fontSize:22,fontWeight:900,color:'var(--accent-gold-text)'}}>{data.totalTrades}</div>
                    <div style={{fontSize:10,color:'var(--text-secondary)',fontWeight:800,letterSpacing:1.5,marginTop:3}}>TOTAL TRADES</div>
                  </div>
                  <div style={{padding:'10px 12px',background:'var(--bg-elevated)',border:'1px solid var(--border)',borderRadius:8,textAlign:'center'}}>
                    <div style={{fontSize:22,fontWeight:900,color:'var(--accent-gold-text)'}}>{data.avgPerLeague.toFixed(1)}</div>
                    <div style={{fontSize:10,color:'var(--text-secondary)',fontWeight:800,letterSpacing:1.5,marginTop:3}}>AVG PER LEAGUE</div>
                  </div>
                  {showLastTrade && (
                    <div style={{padding:'10px 12px',background:'var(--bg-elevated)',border:'1px solid var(--border)',borderRadius:8,textAlign:'center'}}>
                      <div style={{fontSize:16,fontWeight:900,color:'var(--accent-gold-text)',lineHeight:1.2}}>{data.lastTradeDate ? lookupTimeAgo(data.lastTradeDate) : 'none'}</div>
                      <div style={{fontSize:10,color:'var(--text-secondary)',fontWeight:800,letterSpacing:1.5,marginTop:3}}>LAST TRADE</div>
                    </div>
                  )}
                </div>
                <div style={{fontSize:11,color:'var(--text-secondary)',marginTop:6,fontStyle:'italic'}}>Across {data.leaguesCount} dynasty {data.leaguesCount===1?'league':'leagues'}.</div>
              </div>
            );
          };
          return (
            <>
              {renderSeason(tradeActivity.current, `${tradeActivity.current?.season || LOOKUP_CURRENT_YEAR} (CURRENT)`, true)}
              {renderSeason(tradeActivity.prior, `${tradeActivity.prior?.season || LOOKUP_CURRENT_YEAR-1} SEASON`, false)}
              {renderSeason(tradeActivity.prior2, `${tradeActivity.prior2?.season || LOOKUP_CURRENT_YEAR-2} SEASON`, false)}
            </>
          );
        })()}
        {/* Recent trades CTA — opens the same modal Trade Finder uses, scoped
            to this user across all their dynasty leagues. Useful for commish
            collusion scouting (facts only — they draw their own conclusions). */}
        {user && (
          <button onClick={() => setTradesModalOpen(true)}
                  style={{marginTop:8,width:'100%',padding:'12px 14px',background:'var(--bg-elevated)',border:'1.5px solid rgba(var(--accent-gold-rgb),0.4)',borderRadius:8,color:'var(--accent-gold-text)',fontWeight:800,fontSize:13,letterSpacing:0.5,cursor:'pointer'}}>
            🔍 View Recent Trades →
          </button>
        )}
        <div style={{fontSize:11,color:'var(--text-secondary)',marginTop:6,lineHeight:1.5}}>See up to 500 of this user's most recent dynasty trades across every league they're in. Search within the modal to filter by any player or pick. Useful for spotting unusual patterns (commish collusion checks).</div>
      </div>

      {/* Team strength — avg power-rank by FantasyCalc value across current dynasty leagues */}
      <div style={card}>
        <div style={sectionHdr}>💪 AVG LEAGUE POWER RANKING</div>
        <div style={{fontSize:12,color:'var(--text-secondary)',marginTop:-4,marginBottom:12,lineHeight:1.5}}>Where you rank in dynasty value on avg across all your leagues. <span style={{color:'var(--text-secondary)'}}>Values via fantasycalc.com.</span></div>
        {strengthLoading && !teamStrength && (
          <div style={{padding:'14px',color:'var(--text-secondary)',fontSize:13,textAlign:'center'}}>Calculating roster ranks across leagues…</div>
        )}
        {teamStrength && teamStrength.leaguesCount === 0 && (
          <div style={{padding:'14px',color:'var(--text-secondary)',fontSize:13,textAlign:'center'}}>No current dynasty leagues to rank.</div>
        )}
        {teamStrength && teamStrength.leaguesCount > 0 && (
          <>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:12}}>
              <div style={{padding:'12px 14px',background:'var(--bg-elevated)',border:'1px solid var(--border)',borderRadius:8,textAlign:'center'}}>
                <div style={{fontSize:24,fontWeight:900,color:'var(--accent-gold-text)'}}>{teamStrength.avgRank.toFixed(1)}</div>
                <div style={{fontSize:10,color:'var(--text-secondary)',fontWeight:800,letterSpacing:1.5,marginTop:4}}>AVG RANK</div>
              </div>
              <div style={{padding:'12px 14px',background:'var(--bg-elevated)',border:'1px solid var(--border)',borderRadius:8,textAlign:'center'}}>
                <div style={{fontSize:24,fontWeight:900,color:'var(--accent-gold-text)'}}>{teamStrength.leaguesCount}</div>
                <div style={{fontSize:10,color:'var(--text-secondary)',fontWeight:800,letterSpacing:1.5,marginTop:4}}>LEAGUES</div>
              </div>
              <div style={{padding:'12px 14px',background:'var(--bg-elevated)',border:'1px solid var(--border)',borderRadius:8,textAlign:'center'}}>
                <div style={{fontSize:24,fontWeight:900,color:'var(--accent-gold-text)'}}>{teamStrength.avgTeams.toFixed(0)}</div>
                <div style={{fontSize:10,color:'var(--text-secondary)',fontWeight:800,letterSpacing:1.5,marginTop:4}}>TYPICAL LEAGUE SIZE</div>
              </div>
            </div>
            <div style={{fontSize:11,color:'var(--text-secondary)',marginTop:10,fontStyle:'italic',lineHeight:1.6}}>Co-managed leagues where this user isn't the primary manager aren't factored in.</div>
          </>
        )}
      </div>

      {/* Orphan history — dynasty leagues only */}
      <div style={card}>
        <div style={sectionHdr}>🪦 DYNASTY ORPHAN HISTORY</div>
        <div style={{fontSize:13,color:'var(--text-secondary)',lineHeight:1.7}}>Past <strong style={{color:'var(--text-bright)'}}>dynasty</strong> leagues this user appears to have left between seasons. Redraft / keeper leagues that ended naturally are not counted.</div>
        <div style={{display:'flex',gap:14,marginTop:10,flexWrap:'wrap'}}>
          <div style={{padding:'8px 14px',background:'var(--bg-elevated)',border:'1px solid var(--border)',borderRadius:6,fontSize:13}}>Last 12 months: <strong style={{color:'var(--accent-gold-text)',fontSize:16,marginLeft:6}}>{orphan?.last12mo ?? 0}</strong></div>
          <div style={{padding:'8px 14px',background:'var(--bg-elevated)',border:'1px solid var(--border)',borderRadius:6,fontSize:13}}>Last 24 months: <strong style={{color:'var(--accent-gold-text)',fontSize:16,marginLeft:6}}>{orphan?.last24mo ?? 0}</strong></div>
          {orphan && orphan.uniqueLeagues > 0 && (
            <div style={{padding:'8px 14px',background:'var(--bg-elevated)',border:'1px solid var(--border)',borderRadius:6,fontSize:13}}>Orphan rate: <strong style={{color:'var(--accent-gold-text)',fontSize:16,marginLeft:6}}>{orphan.orphanRatePct.toFixed(1)}%</strong> <span style={{color:'var(--text-secondary)',marginLeft:4,fontSize:11}}>({orphan.all} of {orphan.uniqueLeagues})</span></div>
          )}
        </div>
        {orphan?.details && orphan.details.length > 0 && (
          <details style={{marginTop:12}}>
            <summary style={{cursor:'pointer',color:'var(--text-secondary)',fontSize:12}}>Show details</summary>
            <div style={{marginTop:8,fontSize:12,color:'var(--text-bbb)',lineHeight:1.7}}>
              {orphan.details.map((d,i)=>(<div key={i}>• {d.league_name} ({d.season_left})</div>))}
            </div>
          </details>
        )}
      </div>

      {/* Share + opt-out */}
      <div style={card}>
        <div style={sectionHdr}>🔗 PROFILE LINK</div>
        <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
          <code style={{flex:1,minWidth:200,padding:'8px 12px',background:'var(--bg-elevated)',border:'1px solid var(--border)',borderRadius:6,color:'var(--accent-gold-text)',fontSize:12,wordBreak:'break-all'}}>{window.location.href}</code>
          <button onClick={copyLink} style={{padding:'8px 14px',background:linkCopied?'var(--success)':'var(--accent-gold)',border:'none',borderRadius:6,color:'#000',fontWeight:900,fontSize:12,letterSpacing:1,cursor:'pointer',whiteSpace:'nowrap'}}>{linkCopied?'✓ COPIED':'📋 COPY'}</button>
        </div>
      </div>

      <div style={{textAlign:'center',marginTop:24,marginBottom:40,fontSize:12,color:'var(--text-secondary)'}}>
        <div style={{marginBottom:8}}>Is this you and you'd rather not be searchable?</div>
        <a href={`mailto:playforkeepsdynasty@gmail.com?subject=PFK%20Lookup%20opt-out%20request&body=Please%20remove%20my%20PFK%20Lookup%20profile.%0A%0ASleeper%20username%3A%20${encodeURIComponent(user.username||'')}%0ASleeper%20user_id%3A%20${encodeURIComponent(user.user_id||'')}`}
           style={{color:'var(--accent-gold-text)',textDecoration:'underline'}}>Email us to opt out →</a>
        <div style={{marginTop:18}}>
          <a href="/lookup" style={{color:'var(--text-secondary)',textDecoration:'none',fontWeight:700}}>← Search another user</a>
        </div>
      </div>

      {/* Desktop share modal — same 3-action pattern as the dispersal share-pool flow */}
      {desktopShare && (() => {
        const closeModal = () => { setDesktopShare(null); setShareToast(''); };
        const doDownload = () => {
          const url = URL.createObjectURL(desktopShare.blob);
          const link = document.createElement('a');
          link.download = desktopShare.filename; link.href = url;
          document.body.appendChild(link); link.click(); document.body.removeChild(link);
          setTimeout(()=>URL.revokeObjectURL(url), 1000);
          setShareToast('Downloaded ✓'); setTimeout(closeModal, 900);
        };
        const doCopy = async () => {
          try{
            if(!window.ClipboardItem || !navigator.clipboard?.write){
              setShareToast('Copy not supported in this browser — use Download instead'); return;
            }
            await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': desktopShare.blob })]);
            setShareToast('Copied to clipboard ✓ — paste anywhere');
            setTimeout(closeModal, 1400);
          }catch(e){ setShareToast('Copy failed: ' + (e.message||e)); }
        };
        const doShareToX = async () => {
          let copied = false;
          try{
            if(window.ClipboardItem && navigator.clipboard?.write){
              await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': desktopShare.blob })]);
              copied = true;
            }
          }catch(e){}
          const tweetText = `Sleeper background check on @${user?.username || user?.display_name || 'this manager'} — built with @PlayforkeepsFF\n${window.location.href}`;
          window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`, '_blank');
          setShareToast(copied ? 'Image copied — paste it into the tweet (Cmd/Ctrl+V)' : 'Tweet opened — image not auto-copied (use Copy first)');
          setTimeout(closeModal, 2400);
        };
        return (
          <div onClick={closeModal} style={{position:'fixed',inset:0,background:'var(--backdrop)',zIndex:99999,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
            <div onClick={e=>e.stopPropagation()} style={{width:'100%',maxWidth:440,background:'var(--bg-card)',border:'2px solid var(--accent-gold)',borderRadius:12,padding:'22px 24px'}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
                <div style={{fontWeight:900,fontSize:15,letterSpacing:1.5,color:'var(--accent-gold-text)'}}>📸 SHARE PROFILE</div>
                <button onClick={closeModal} style={{background:'none',border:'none',color:'var(--text-secondary)',fontSize:20,cursor:'pointer'}}>✕</button>
              </div>
              <div style={{fontSize:13,color:'var(--text-secondary)',marginBottom:14,lineHeight:1.5}}>Image is ready. Pick what to do with it:</div>
              <div style={{display:'flex',flexDirection:'column',gap:8}}>
                <button onClick={doDownload} style={{padding:'12px 16px',background:'var(--success-bg)',border:'1.5px solid var(--success)',borderRadius:7,color:'var(--success)',fontWeight:900,cursor:'pointer',fontSize:14,letterSpacing:1,textAlign:'left'}}>📥 &nbsp;DOWNLOAD &nbsp;<span style={{fontWeight:600,color:'var(--text-secondary)',fontSize:12,letterSpacing:0.3}}>· save .png to your computer</span></button>
                <button onClick={doCopy} style={{padding:'12px 16px',background:'transparent',border:'1.5px solid var(--accent-gold)',borderRadius:7,color:'var(--accent-gold-text)',fontWeight:900,cursor:'pointer',fontSize:14,letterSpacing:1,textAlign:'left'}}>📋 &nbsp;COPY TO CLIPBOARD &nbsp;<span style={{fontWeight:600,color:'var(--text-secondary)',fontSize:12,letterSpacing:0.3}}>· paste into iMessage, Slack, etc.</span></button>
                <button onClick={doShareToX} style={{padding:'12px 16px',background:'#000',border:'1.5px solid #fff',borderRadius:7,color:'#fff',fontWeight:900,cursor:'pointer',fontSize:14,letterSpacing:1,textAlign:'left',display:'flex',alignItems:'center',gap:8}}>
                  <svg viewBox="0 0 24 24" style={{width:16,height:16}}>
                    <path fill="var(--text-bright)" d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                  &nbsp;SHARE TO X &nbsp;<span style={{fontWeight:600,color:'var(--text-secondary)',fontSize:12,letterSpacing:0.3}}>· copies image + opens tweet</span>
                </button>
              </div>
              {shareToast && <div style={{marginTop:14,padding:'10px 12px',background:'var(--success-bg)',border:'1px solid var(--success)',borderRadius:7,color:'var(--success)',fontSize:12,fontWeight:700,textAlign:'center'}}>{shareToast}</div>}
            </div>
          </div>
        );
      })()}

      {/* Off-screen share card — captured via html2canvas. Painted at -99999px so
          the layout is correct without ever being visible to the user. */}
      <div style={{position:'fixed',left:'-99999px',top:0,pointerEvents:'none'}} aria-hidden="true">
        <div ref={profileCardRef} data-theme="dark" style={{width:780,background:'var(--bg-page)',padding:'28px 34px',color:'var(--text-primary)',fontFamily:"'Inter','Segoe UI',sans-serif",border:'3px solid var(--accent-gold)',borderRadius:14,boxSizing:'border-box'}}>
          {/* Card header — transparent square logo above centered "PLAY FOR
              KEEPS" wordmark and "Dynasty Fantasy Football" subtitle. This
              card is one of PFK's biggest promo surfaces (users post these
              snapshots into their leagues), so the brand stack at the top
              is intentionally hero-sized. */}
          <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:6,marginBottom:18,paddingBottom:16,borderBottom:'2px solid rgba(var(--accent-gold-rgb),0.27)'}}>
            <img src="/img/pfk-logo.png" alt="PFK" style={{width:96,height:96,objectFit:'contain',flexShrink:0}}/>
            <div style={{fontSize:30,fontWeight:900,color:'var(--wordmark-color)',letterSpacing:3,lineHeight:1.05}}>PLAY FOR KEEPS</div>
            <div style={{fontSize:11,color:'var(--wordmark-subtitle)',letterSpacing:2.5,textTransform:'uppercase',fontWeight:700,marginTop:2}}>Dynasty Fantasy Football</div>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:14,marginBottom:18}}>
            {/* Real Sleeper avatar — proxied through /api/avatar/{id} so it's
                same-origin (sleepercdn.com doesn't send CORS headers; html2canvas
                couldn't read the pixels otherwise). Falls back to an initial-
                letter placeholder if the proxy fetch fails for any reason. */}
            {user.avatar
              ? <img src={`/api/avatar/${user.avatar}`} alt="" style={{width:54,height:54,borderRadius:'50%',objectFit:'cover',background:'var(--bg-elevated)',border:'1px solid var(--border)',flexShrink:0}} onError={e=>{e.target.style.display='none'; const fb = e.target.nextElementSibling; if(fb) fb.style.display='flex';}}/>
              : null}
            <div style={{width:54,height:54,borderRadius:'50%',background:'var(--accent-gold)',display: user.avatar ? 'none' : 'flex',alignItems:'center',justifyContent:'center',flexShrink:0,fontSize:24,fontWeight:900,color:'#000',letterSpacing:0,fontFamily:"'Inter','Segoe UI',sans-serif"}}>
              {((user.display_name || user.username || '?')[0] || '?').toUpperCase()}
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:24,fontWeight:900,color:'var(--text-bright)',letterSpacing:0.5,wordBreak:'break-word'}}>{user.display_name || user.username}</div>
              {createdDate && <div style={{fontSize:12,color:'var(--text-secondary)',marginTop:2}}>Account {lookupTimeAgo(createdDate)}</div>}
            </div>
          </div>

          {/* Career Dynasty Record — the headline content for the share image.
              Six tiles in two rows of three so each label is fully readable
              at the share-card scale (smaller than the live page). Falls
              through silently if the user has no completed dynasty seasons. */}
          {careerStats && careerStats.totalSeasons > 0 && (() => {
            const totalGames = careerStats.totalWins + careerStats.totalLosses;
            const winPct = totalGames > 0 ? (careerStats.totalWins / totalGames * 100) : 0;
            const ringPct = (careerStats.championships / careerStats.totalSeasons * 100);
            const tileStyle = {padding:'12px 10px',background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:8,textAlign:'center'};
            const big = {fontSize:22,fontWeight:900,color:'var(--accent-gold-text)',lineHeight:1};
            const label = {fontSize:9,color:'var(--text-secondary)',fontWeight:800,letterSpacing:1.2,marginTop:5,lineHeight:1.25};
            const sub = {fontSize:9,color:'var(--text-secondary)',fontWeight:700,marginTop:2};
            return (
              <div style={{marginBottom:16}}>
                <div style={{fontSize:11,fontWeight:900,color:'var(--accent-gold-text)',letterSpacing:2,marginBottom:8,textAlign:'center'}}>🏆 CAREER DYNASTY RECORD</div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8,marginBottom:8}}>
                  <div style={tileStyle}>
                    <div style={big}>{careerStats.totalWins}-{careerStats.totalLosses}</div>
                    <div style={label}>RECORD</div>
                    <div style={sub}>{winPct.toFixed(1)}% win rate</div>
                  </div>
                  <div style={tileStyle}>
                    <div style={big}>{careerStats.championships}</div>
                    <div style={label}>CHAMPIONSHIPS</div>
                    <div style={sub}>{ringPct.toFixed(1)}% of seasons</div>
                  </div>
                  <div style={tileStyle}>
                    <div style={big}>{careerStats.longestStreak}</div>
                    <div style={label}>LONGEST TITLE STREAK</div>
                    <div style={sub}>{careerStats.longestStreak === 0 ? '—' : (careerStats.longestStreak === 1 ? 'one ring' : `${careerStats.longestStreak} in a row`)}</div>
                  </div>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8}}>
                  <div style={tileStyle}>
                    <div style={big}>{careerStats.peakRingCount || 0}</div>
                    <div style={label}>MOST TITLES WON IN A SINGLE SEASON</div>
                    <div style={sub}>{careerStats.peakRingCount > 0 ? `in ${careerStats.peakRingYear}` : '—'}</div>
                  </div>
                  <div style={tileStyle}>
                    <div style={big}>{careerStats.totalSeasons}</div>
                    <div style={label}>SEASONS PLAYED</div>
                  </div>
                  <div style={tileStyle}>
                    <div style={big}>{dynastyActive.length}</div>
                    <div style={label}>TOTAL LEAGUES</div>
                    <div style={sub}>{displayedActiveYear} active</div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Avg dynasty value standings */}
          {teamStrength && teamStrength.leaguesCount > 0 && (
            <div style={{marginBottom:16}}>
              <div style={{fontSize:11,fontWeight:900,color:'var(--accent-gold-text)',letterSpacing:2,marginBottom:8,textAlign:'center'}}>💪 AVG LEAGUE POWER RANKING</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8}}>
                <div style={{padding:'12px 14px',background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:8,textAlign:'center'}}>
                  <div style={{fontSize:24,fontWeight:900,color:'var(--accent-gold-text)',lineHeight:1}}>{teamStrength.avgRank.toFixed(1)}</div>
                  <div style={{fontSize:9,color:'var(--text-secondary)',fontWeight:800,letterSpacing:1.5,marginTop:4}}>AVG RANK</div>
                </div>
                <div style={{padding:'12px 14px',background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:8,textAlign:'center'}}>
                  <div style={{fontSize:24,fontWeight:900,color:'var(--accent-gold-text)',lineHeight:1}}>{teamStrength.leaguesCount}</div>
                  <div style={{fontSize:9,color:'var(--text-secondary)',fontWeight:800,letterSpacing:1.5,marginTop:4}}>LEAGUES</div>
                </div>
                <div style={{padding:'12px 14px',background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:8,textAlign:'center'}}>
                  <div style={{fontSize:24,fontWeight:900,color:'var(--accent-gold-text)',lineHeight:1}}>{teamStrength.avgTeams.toFixed(0)}</div>
                  <div style={{fontSize:9,color:'var(--text-secondary)',fontWeight:800,letterSpacing:1.5,marginTop:4}}>TYPICAL LEAGUE SIZE</div>
                </div>
              </div>
            </div>
          )}

          {/* Trade activity — current shows LAST TRADE; prior years drop it (always stale) */}
          {tradeActivity && (
            <div style={{marginBottom:16}}>
              <div style={{fontSize:11,fontWeight:900,color:'var(--accent-gold-text)',letterSpacing:2,marginBottom:8,textAlign:'center'}}>🤝 TRADE ACTIVITY</div>
              {[
                { label: `${tradeActivity.current?.season || LOOKUP_CURRENT_YEAR} (CURRENT)`, data: tradeActivity.current, showLast: true },
                { label: `${tradeActivity.prior?.season || LOOKUP_CURRENT_YEAR-1} SEASON`, data: tradeActivity.prior, showLast: false },
                { label: `${tradeActivity.prior2?.season || LOOKUP_CURRENT_YEAR-2} SEASON`, data: tradeActivity.prior2, showLast: false },
              ].map((s, idx) => s.data && s.data.leaguesCount > 0 && (
                <div key={idx} style={{marginBottom:8}}>
                  <div style={{fontSize:10,color:'var(--accent-gold-text)',fontWeight:800,letterSpacing:1.5,marginBottom:5}}>{s.label}</div>
                  <div style={{display:'grid',gridTemplateColumns: s.showLast ? '1fr 1fr 1fr' : '1fr 1fr',gap:8}}>
                    <div style={{padding:'10px 12px',background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:8,textAlign:'center'}}>
                      <div style={{fontSize:20,fontWeight:900,color:'var(--accent-gold-text)'}}>{s.data.totalTrades}</div>
                      <div style={{fontSize:9,color:'var(--text-secondary)',fontWeight:800,letterSpacing:1.5,marginTop:3}}>TOTAL TRADES</div>
                    </div>
                    <div style={{padding:'10px 12px',background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:8,textAlign:'center'}}>
                      <div style={{fontSize:20,fontWeight:900,color:'var(--accent-gold-text)'}}>{s.data.avgPerLeague.toFixed(1)}</div>
                      <div style={{fontSize:9,color:'var(--text-secondary)',fontWeight:800,letterSpacing:1.5,marginTop:3}}>AVG / LEAGUE</div>
                    </div>
                    {s.showLast && (
                      <div style={{padding:'10px 12px',background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:8,textAlign:'center'}}>
                        <div style={{fontSize:14,fontWeight:900,color:'var(--accent-gold-text)',lineHeight:1.3}}>{s.data.lastTradeDate ? lookupTimeAgo(s.data.lastTradeDate) : 'none'}</div>
                        <div style={{fontSize:9,color:'var(--text-secondary)',fontWeight:800,letterSpacing:1.5,marginTop:3}}>LAST TRADE</div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Orphan history — last 12mo / 24mo + overall orphan rate %.
              Gated by the "Include orphan history" share-toggle so users can
              opt out of having it in the shared image. Stays visible on the
              live snapshot page either way. */}
          {shareIncludeOrphan && (
          <div style={{marginBottom:18}}>
            <div style={{fontSize:11,fontWeight:900,color:'var(--accent-gold-text)',letterSpacing:2,marginBottom:8,textAlign:'center'}}>🪦 DYNASTY ORPHAN HISTORY</div>
            <div style={{display:'flex',gap:10}}>
              <div style={{flex:1,padding:'12px 14px',background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:8,textAlign:'center'}}>
                <div style={{fontSize:24,fontWeight:900,color:'var(--accent-gold-text)',lineHeight:1}}>{orphan?.last12mo ?? 0}</div>
                <div style={{fontSize:9,color:'var(--text-secondary)',fontWeight:800,letterSpacing:1.5,marginTop:4}}>LAST 12 MONTHS</div>
              </div>
              <div style={{flex:1,padding:'12px 14px',background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:8,textAlign:'center'}}>
                <div style={{fontSize:24,fontWeight:900,color:'var(--accent-gold-text)',lineHeight:1}}>{orphan?.last24mo ?? 0}</div>
                <div style={{fontSize:9,color:'var(--text-secondary)',fontWeight:800,letterSpacing:1.5,marginTop:4}}>LAST 24 MONTHS</div>
              </div>
              {orphan && orphan.uniqueLeagues > 0 && (
                <div style={{flex:1,padding:'12px 14px',background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:8,textAlign:'center'}}>
                  <div style={{fontSize:24,fontWeight:900,color:'var(--accent-gold-text)',lineHeight:1}}>{orphan.orphanRatePct.toFixed(1)}%</div>
                  <div style={{fontSize:9,color:'var(--text-secondary)',fontWeight:800,letterSpacing:1.5,marginTop:4}}>ORPHAN RATE</div>
                  <div style={{fontSize:9,color:'var(--text-secondary)',fontWeight:700,marginTop:2}}>{orphan.all} of {orphan.uniqueLeagues}</div>
                </div>
              )}
            </div>
          </div>
          )}

          {/* Footer */}
          <div style={{paddingTop:16,borderTop:'2px solid rgba(var(--accent-gold-rgb),0.27)',display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:10,color:'var(--text-secondary)',fontWeight:800,letterSpacing:1.5,marginBottom:3}}>LOOK UP ANY SLEEPER USER AT</div>
              <div style={{fontSize:16,fontWeight:900,color:'var(--accent-gold-text)',letterSpacing:0.5}}>playforkeepsdynasty.com/lookup</div>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:8}}>
              <svg viewBox="0 0 24 24" style={{width:18,height:18,flexShrink:0}}>
                <path fill="var(--accent-gold-text)" d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
              <div style={{fontSize:14,fontWeight:900,color:'var(--accent-gold-text)',letterSpacing:0.5}}>@PlayforkeepsFF</div>
            </div>
          </div>
        </div>
      </div>
      {/* Recent trades modal — opens from the Trade Activity card's CTA. No
          relevantContext (we're not in a Trade Finder flow), so the ★ Relevant
          tab doesn't render — modal just shows All trades. */}
      {tradesModalOpen && user && (
        <RecentTradesModal
          userInfo={{
            user_id: user.user_id,
            username: user.username,
            display_name: user.display_name || user.username,
            avatar: user.avatar,
          }}
          onClose={() => setTradesModalOpen(false)}
          resolvePlayerName={lookupResolvePlayerName}
        />
      )}
    </div>
  );
}

// ---------- Top-level Lookup page (route: /lookup[/identifier]) ----------
// Quick search input for the header — only renders on profile pages so users
// can run a new lookup without navigating back to /lookup.
function LookupHeaderSearch(){
  const [q,setQ] = useState('');
  const submit = (e) => {
    if(e) e.preventDefault();
    const v = (q||'').trim();
    if(!v) return;
    window.location.href = '/lookup/' + encodeURIComponent(v);
  };
  return (
    <form onSubmit={submit} style={{display:'flex',gap:6,alignItems:'center'}}>
      <input value={q} onChange={e=>setQ(e.target.value)} placeholder="🔍 New lookup…"
        style={{padding:'6px 10px',background:'var(--bg-elevated)',border:'1px solid rgba(var(--accent-gold-rgb),0.33)',borderRadius:6,color:'var(--text-bright)',fontSize:13,fontFamily:'inherit',width:160}}/>
      <button type="submit" style={{padding:'6px 11px',background:'var(--accent-gold)',border:'none',borderRadius:6,color:'#000',fontWeight:900,fontSize:11,letterSpacing:1,cursor:'pointer'}}>GO</button>
    </form>
  );
}

function LookupApp({ identifier }){
  // Safety-net redirect: if someone hits /lookup directly (typed URL, bookmark,
  // old browser back) AND has a Sleeper account linked, send them straight to
  // their own snapshot. The toolbar's smart href handles the click case; this
  // catches everything else. Use replace() so back-button doesn't bounce them
  // back into a redirect loop.
  const [linkedUser] = useSleeperUser();
  useEffect(() => {
    if(!identifier && linkedUser?.username){
      window.location.replace('/lookup/' + encodeURIComponent(linkedUser.username));
    }
  },[identifier, linkedUser?.username]);
  return (
    <div style={{background:'var(--bg-page)',minHeight:'100vh',color:'var(--text-primary)',fontFamily:"'Inter','Segoe UI',sans-serif"}}>
      <MasterToolbar currentTab="lookup"/>
      {/* Quick lookup bar lives BELOW the yellow line on profile pages so it sits in
          the page-specific area, not in the master toolbar. Search page omits it
          since it already has the big input. */}
      {identifier && (
        <div style={{maxWidth:1240,margin:'0 auto',padding:'12px 16px 0'}}>
          <LookupHeaderSearch/>
        </div>
      )}
      {identifier ? <LookupProfile identifier={identifier}/> : <LookupSearch/>}
      <PFKFooter/>
    </div>
  );
}

// ============================================================================
// ⚖️ TRADE FINDER
// "Type 1.03 → see every veteran (and pick) within ±X% of its value, grouped
// by position." Fills a gap that KTC, FantasyCalc, and DynastyProcess all
// share: every existing calculator forces a two-sided trade entry, so users
// have to already know what to type. Trade Finder reverses the flow.
// Default format: Superflex 1.0 PPR (PFK's standard everywhere else).
// ============================================================================

// Cache rich asset arrays per FC format key so swapping leagues only re-fetches
// once per unique format combo (most users have 2-4 distinct formats max).
const _tfRichByFormat = {};
// Default format when no league is linked, per Evan: Superflex / 1.0 PPR / 0.5 TEP / 12 teams.
// Pass TD baseline (5pt) is implicit — ptdAdjust treats 5 as no-op, and the chip
// shows "5pt PT" via formatLabel's pass-TD fallback.
const TF_DEFAULT_FORMAT = { numQbs: 2, ppr: 1, tep: 0.5, numTeams: 12, key: '2q-1p-0.5t-12n' };
const fetchTradeFinderAssets = async (fmt = TF_DEFAULT_FORMAT) => {
  if(_tfRichByFormat[fmt.key]) return _tfRichByFormat[fmt.key];
  try{
    // tepLevel hardcoded 0 — FC ignores the param. PFK applies TEP_MULT to TE
    // values below so 0/0.25/0.5/0.75/1.0 leagues all get correctly-tiered TEs.
    const url = `https://api.fantasycalc.com/values/current?isDynasty=true&numQbs=${fmt.numQbs}&numTeams=${fmt.numTeams}&ppr=${fmt.ppr}&tepLevel=0`;
    const teMult = TEP_MULT[fmt.tep]?.TE ?? 1;
    const arr = await fetch(url).then(r=>r.json());
    const assets = (arr||[]).map(row => {
      const p = row?.player || {};
      const name = p.name || '';
      const pos = (p.position || '').toUpperCase();
      const rawValue = row?.value ?? 0;
      const value = pos === 'TE' ? rawValue * teMult : rawValue;
      const isPick = /^\d{4}\s+(1st|2nd|3rd|4th|5th)$/.test(name) || /^\d{4}\s+Pick\s/.test(name);
      const isSlotPick = /^\d{4}\s+Pick\s/.test(name);
      // Generic round picks ("2026 1st") clutter results next to slot picks ("2026 Pick 1.03")
      // and confuse users — slot picks are more accurate so we hide the generic ones.
      if(isPick && !isSlotPick) return null;
      return {
        name,
        normName: (name||'').toLowerCase().replace(/[^a-z0-9 ]/g,'').replace(/\s+/g,' ').trim(),
        pos: isPick ? 'PICK' : (pos || 'OTHER'),
        age: p.maturedYear ? null : (p.age ?? null),
        value,
        isPick,
        sleeperId: p.sleeperId || null,
      };
    }).filter(Boolean).filter(a => a.value > 0);
    _tfRichByFormat[fmt.key] = assets;
  }catch(e){
    _tfRichByFormat[fmt.key] = [];
  }
  return _tfRichByFormat[fmt.key];
};

// Per-position pass-TD multiplier. FC's API doesn't differentiate values by
// pass TD scoring, so we apply our own multiplier on top of FC values — same
// table the rookie rankings use (PTD_MULT). Baseline is 5pt = 1.00.
// 4pt PT: QB tax 5%. 6pt PT: QB bump 5%. RB/WR/TE never affected.
// Picks unaffected (no position).
const ptdAdjust = (value, pos, passTd) => {
  if(!passTd || passTd === 5) return value;
  const m = ({4:0.95, 6:1.05}[passTd]) ?? 1;
  return pos === 'QB' ? value * m : value;
};

// PPC (per-carry) and PPFD (per-first-down) multipliers — not in FC's API.
// Calibrated against 3-yr nflverse data (2022-2024) for top fantasy producers
// per position, scaled to PFK convention (~⅓ pass-through of raw point bumps
// to match existing TEP_MULT / PTD_MULT scale).
//
// RB multipliers are TIERED by FC value. Workhorse RBs (Bijan/Gibbs tier) get
// 2-3x the carries of fringe starters and 5-10x the carries of FAAB depth, so
// the per-carry / per-first-down value bump is much bigger at the top of the
// position. Brackets calibrated from analyst pick-value tier sheets (2026-04-29).
//
// QB/WR/TE stay flat — receptions don't fall off as steeply by FC value rank.
//
// PPC 0.25:  RBs nearly the only gainer (+23% raw → +10-15% mult tiered).
// PPFD 1.0:  RB > TE > WR > QB; RB rec_fd ratio confirmed at 34% (not 50%).
//            Full derivation in ~/pfk_research/PROJECT_STATUS.md.
//
// Calibrated for PPC=0.25 and PPFD=1; for other values we scale linearly.

// Tier brackets (descending min FC value → multiplier).
//
// RB tiering is the biggest impact — workhorses get ~5x the carries of FAAB
// depth. QB tiering matters too because elite mobile QBs (Hurts/Jackson/Allen/
// Daniels) get ~5x the carries and ~9x the rush 1D of pocket-leaning starters.
// Validated against 72 player-seasons of nflverse data, 2022-2024.
//
// Per Evan: "the further from the top of the list, the less the multiplier
// matters" — low-value QBs aren't long-term dynasty assets even if they run,
// so bottom-tier multiplier returns 1.00.
// Source: pfk_research/PROJECT_STATUS.md:308-315 + pfk_research/MULTIPLIER_AUDIT_2026-05-13.md.
//   Validated against 72 player-seasons of nflverse data (top-24 RB × 3 years,
//   2022-2024) + analyst pick-value tier sheets.
// Status: RESEARCH-BACKED.
// Last reviewed: 2026-05-13
const RB_PPC_TIERS = [
  { min: 6500, mult: 1.15 },  // Elite young workhorse (Bijan/Gibbs tier)
  { min: 4000, mult: 1.10 },  // Solid RB1/RB2 starter
  { min: 2000, mult: 1.06 },  // Low-end starter / committee back
  { min: 800,  mult: 1.03 },  // Depth / handcuff with role
  { min: 0,    mult: 1.00 },  // Pure FAAB
];
// Source: pfk_research/PROJECT_STATUS.md:267-271, 316-322 + MULTIPLIER_AUDIT_2026-05-13.md.
//   RB rec_fd ratio confirmed at 34% of receptions (corrected from 50% estimate
//   on 2026-04-29 after pulling actual nflverse data; stable across 2022/23/24).
//   Top-tier multiplier corrected from 1.10 → 1.09 as a result.
// Status: RESEARCH-BACKED.
// Last reviewed: 2026-05-13
const RB_PPFD_TIERS = [
  { min: 6500, mult: 1.13 },
  { min: 4000, mult: 1.09 },
  { min: 2000, mult: 1.05 },
  { min: 800,  mult: 1.02 },
  { min: 0,    mult: 1.00 },
];
// Source: pfk_research/PROJECT_STATUS.md:323-327, 337-344 + MULTIPLIER_AUDIT_2026-05-13.md.
//   Per-bucket validation (3-yr avg, n=72): elite mobile 129 carries, solid
//   mobile 67, mostly pocket 43, fringe/backup 26.
// Status: RESEARCH-BACKED.
// Last reviewed: 2026-05-13
const QB_PPC_TIERS = [
  { min: 7500, mult: 1.04 },  // Elite mobile (Hurts/Jackson/Allen/Daniels)
  { min: 4500, mult: 1.02 },  // Solid mobile starter
  { min: 2000, mult: 1.01 },  // Mostly pocket starter
  { min: 0,    mult: 1.00 },  // Fringe / backup — multiplier doesn't matter
];
// Source: pfk_research/PROJECT_STATUS.md:329-333, 341-344 + MULTIPLIER_AUDIT_2026-05-13.md.
//   Elite mobile QB rush 1D conversion ratio is ~0.41 vs ~0.30 for the field —
//   biggest single insight from the QB-tiering validation.
// Status: RESEARCH-BACKED.
// Last reviewed: 2026-05-13
const QB_PPFD_TIERS = [
  { min: 7500, mult: 1.06 },  // Elite mobile QB rush 1D conversion is ~0.41 (high)
  { min: 4500, mult: 1.03 },
  { min: 2000, mult: 1.02 },
  { min: 0,    mult: 1.00 },
];
const lookupTierMult = (tiers, baseValue) => {
  for(const t of tiers) if(baseValue >= t.min) return t.mult;
  return 1.0;
};

// Per-position multiplier resolvers. RB and QB consult tier brackets using the
// player's BASE FC value (pre-adjustment); WR and TE are flat (receptions
// don't drop as steeply by FC value rank as carries / rush first downs do).
//
// Source: pfk_research/PROJECT_STATUS.md:281, 282, 289-292 + MULTIPLIER_AUDIT_2026-05-13.md.
//   ppcMultiplier WR/TE flat at 1.00: WR PPC raw bump +0.3%, TE +0.1% — both
//     effectively zero, no adjustment warranted.
//   ppfdMultiplier WR/TE: WR raw +22.1% → 1.06 (⅓ pass-through ~1.07);
//     TE raw +24.4% → 1.07 (⅓ pass-through ~1.08). Within 1pp tolerance.
// Status: RESEARCH-BACKED (flat WR/TE entries).
// Last reviewed: 2026-05-13
const ppcMultiplier = (pos, baseValue) => {
  if(pos === 'RB') return lookupTierMult(RB_PPC_TIERS, baseValue);
  if(pos === 'QB') return lookupTierMult(QB_PPC_TIERS, baseValue);
  return ({WR:1.00, TE:1.00}[pos]) ?? 1.00;
};
const ppfdMultiplier = (pos, baseValue) => {
  if(pos === 'RB') return lookupTierMult(RB_PPFD_TIERS, baseValue);
  if(pos === 'QB') return lookupTierMult(QB_PPFD_TIERS, baseValue);
  return ({WR:1.06, TE:1.07}[pos]) ?? 1.00;
};

const ppcAdjust = (value, pos, ppcLevel, baseValue) => {
  if(!ppcLevel || ppcLevel === 0) return value;
  const m = ppcMultiplier(pos, baseValue ?? value);
  if(m === 1) return value;
  return value * (1 + (m - 1) * (ppcLevel / 0.25));
};
const ppfdAdjust = (value, pos, ppfdLevel, baseValue) => {
  if(!ppfdLevel || ppfdLevel === 0) return value;
  const m = ppfdMultiplier(pos, baseValue ?? value);
  if(m === 1) return value;
  return value * (1 + (m - 1) * ppfdLevel);
};

// Shared helper for the PPC + PPFD half of the per-player scoring chain.
// Both Trade Finder and Pick Projector call this so the tier-bracket lookups
// (which are calibrated for FantasyCalc's value scale, see RB_PPC_TIERS etc.)
// stay in sync. Trade Finder passes its FC value as both `value` and
// `baseValueForTiering`. Pick Projector passes a PP-scale `value` plus a
// scaled-up `baseValueForTiering` (= value × PP_TO_FC_SCALE) so the tier
// lookups fire correctly even though PP's values max around 1000 vs FC's
// ~9000 for elite players.
//
// PTD is intentionally NOT included here — TF and PP anchor PTD at different
// baselines (TF at 5PT to match FC, PP at 4PT to match Underdog ADP), so
// they apply it in their own pipelines. Math is equivalent either way; only
// the absolute anchor differs.
const applyPpcAndPpfd = (value, pos, league, baseValueForTiering) => {
  if(!value || !pos) return value || 0;
  const tierBase = (baseValueForTiering != null && isFinite(baseValueForTiering)) ? baseValueForTiering : value;
  let v = value;
  const ppc  = ppcLevelForLeague(league);
  const ppfd = ppfdLevelForLeague(league);
  if(ppc)  v = ppcAdjust(v, pos, ppc, tierBase);
  if(ppfd) v = ppfdAdjust(v, pos, ppfd, tierBase);
  return v;
};

// Extract PPC and PPFD levels from a Sleeper league.
// PPFD: most leagues set rush_fd === rec_fd; if they differ we take the max
// (small approximation — multipliers are calibrated for both = 1).
const ppcLevelForLeague = (lg) => parseFloat(lg?.scoring_settings?.rush_att) || 0;
const ppfdLevelForLeague = (lg) => {
  const rush = parseFloat(lg?.scoring_settings?.rush_fd) || 0;
  const rec  = parseFloat(lg?.scoring_settings?.rec_fd) || 0;
  return Math.max(rush, rec);
};

// Pretty-print a format for the chip ("SF · 1.0 PPR · 0.5 TEP · 5pt PT · 12-team").
// Optional `league` arg surfaces scoring fields FC doesn't take as params
// (pass TD, PPC, PPFD — all applied via PFK multipliers). When no league is
// provided we fall back to PFK's baseline pass TD (5pt) so the default chip
// is fully transparent about what's being shown.
const formatLabel = (fmt, league = null) => {
  const qb = fmt.numQbs === 2 ? 'SF' : '1QB';
  const ppr = fmt.ppr === 1 ? '1.0 PPR' : fmt.ppr === 0.5 ? '0.5 PPR' : '0 PPR';
  // Render the league's actual TEP value — no rounding. 0 hides the line.
  // Whole numbers show as "X.0" so the unit feels consistent ("1.0 TEP" not "1 TEP").
  const tepVal = fmt.tep ?? 0;
  const tep = tepVal > 0 ? ` • ${Number.isInteger(tepVal) ? tepVal + '.0' : tepVal} TEP` : '';
  const ptd = league?.scoring_settings?.pass_td ?? 5;
  const ppc = ppcLevelForLeague(league);
  const ppfd = ppfdLevelForLeague(league);
  const ppcStr  = ppc  > 0 ? ` • ${ppc} PPC` : '';
  const ppfdStr = ppfd > 0 ? ` • ${ppfd} PPFD` : '';
  return `${qb} • ${ppr}${tep} • ${ptd}pt PT${ppcStr}${ppfdStr} • ${fmt.numTeams}-team`;
};

// Normalize free-text pick input. Accepts "1.03", "2026 1.03", "1.3", "26 1.03"
// and returns a canonical "2026 Pick 1.03" string for matching against FC names.
const normalizePickQuery = (q) => {
  const trimmed = (q||'').trim();
  // Already canonical?
  if(/^\d{4}\s+Pick\s+\d+\.\d+$/i.test(trimmed)) return trimmed;
  // Pure "R.PP" form (e.g. "1.03")
  const bare = trimmed.match(/^(\d+)\.(\d+)$/);
  if(bare){
    const yr = new Date().getFullYear();
    const round = bare[1];
    const slot = bare[2].padStart(2,'0');
    return `${yr} Pick ${round}.${slot}`;
  }
  // "YYYY R.PP" or "YY R.PP"
  const withYr = trimmed.match(/^(\d{2}|\d{4})\s+(\d+)\.(\d+)$/);
  if(withYr){
    let yr = withYr[1];
    if(yr.length === 2) yr = '20' + yr;
    return `${yr} Pick ${withYr[2]}.${withYr[3].padStart(2,'0')}`;
  }
  return null;
};

// ============================================================================
// RecentTradesModal — shared "last N trades for this Sleeper user" modal.
// Used by Trade Finder (with Trade Finder context for ★ Relevant highlighting)
// and Sleeper Snapshot (no context — just the raw trade history).
//
// Props:
//   userInfo          { user_id, username, display_name, avatar }
//   onClose           () => void
//   resolvePlayerName (sleeperId) => string  — falls back to "Player #id" if omitted
//   relevantContext   optional { playerIds: Set, pickKeys: Set } for highlighting
//   tradeCap          optional number (default 25)
// ============================================================================
function RecentTradesModal({ userInfo, onClose, resolvePlayerName, relevantContext, tradeCap }){
  const cap = tradeCap || 500;
  const nameOf = resolvePlayerName || (pid => `Player #${pid}`);
  const [status, setStatus] = useState('loading');
  const [trades, setTrades] = useState([]);
  const [leagueCount, setLeagueCount] = useState(0);
  const [filterMode, setFilterMode] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const tradeIsRelevant = (tx) => {
    if(!relevantContext) return false;
    const allPlayerIds = new Set([
      ...Object.keys(tx.adds || {}),
      ...Object.keys(tx.drops || {}),
    ]);
    for(const pid of allPlayerIds){
      if(relevantContext.playerIds.has(String(pid))) return true;
    }
    for(const p of (tx.draft_picks || [])){
      if(relevantContext.pickKeys.has(`${p.season}-${p.round}`)) return true;
    }
    return false;
  };
  const isRelevantPlayer = (pid) => relevantContext?.playerIds?.has(String(pid));
  const isRelevantPick = (p) => relevantContext?.pickKeys?.has(`${p.season}-${p.round}`);

  // Resolve picks to their slot when we have league-specific draft order
  // ("2026 Pick 1.03" instead of generic "2026 1st"). Only for current draft year.
  const formatPick = (p, slotByRosterId, draftSeason) => {
    const round = p.round === 1 ? '1st' : p.round === 2 ? '2nd' : p.round === 3 ? '3rd' : `${p.round}th`;
    const generic = `${p.season} ${round}`;
    if(!slotByRosterId || String(p.season) !== draftSeason) return generic;
    const slot = slotByRosterId[p.roster_id];
    if(!slot) return generic;
    return `${p.season} Pick ${p.round}.${String(slot).padStart(2,'0')}`;
  };

  useEffect(() => {
    if(!userInfo?.user_id) return;
    let cancelled = false;
    setStatus('loading');
    setTrades([]);
    (async () => {
      try{
        const yrCur = String(new Date().getFullYear());
        const yrPrev = String(new Date().getFullYear() - 1);
        const [lgsCur, lgsPrev] = await Promise.all([
          fetch(`https://api.sleeper.app/v1/user/${userInfo.user_id}/leagues/nfl/${yrCur}`, {cache:'no-store'}).then(r=>r.ok?r.json():[]).catch(()=>[]),
          fetch(`https://api.sleeper.app/v1/user/${userInfo.user_id}/leagues/nfl/${yrPrev}`, {cache:'no-store'}).then(r=>r.ok?r.json():[]).catch(()=>[]),
        ]);
        pfkRecordUserLeagues(userInfo, lgsCur); // Sharp Tracker pool ingestion (Phase 0a)
        const dynasty = [...lgsCur, ...lgsPrev].filter(l => l?.settings?.type === 2);
        const tradesNested = await Promise.all(dynasty.map(async (lg) => {
          try{
            const rosters = await fetch(`https://api.sleeper.app/v1/league/${lg.league_id}/rosters`, {cache:'no-store'}).then(r=>r.json());
            const myRoster = (rosters||[]).find(r => r.owner_id === userInfo.user_id || (r.co_owners||[]).includes(userInfo.user_id));
            if(!myRoster) return [];
            const myRosterId = myRoster.roster_id;
            const weeks = Array.from({length: 18}, (_, i) => i + 1);
            const [draftSlots, ...txArrays] = await Promise.all([
              lookupDraftSlots(lg.league_id, rosters),
              ...weeks.map(w =>
                fetch(`https://api.sleeper.app/v1/league/${lg.league_id}/transactions/${w}`, {cache:'no-store'})
                  .then(r => r.ok ? r.json() : []).catch(() => [])
              ),
            ]);
            const slotByRosterId = {};
            if(draftSlots?.slotByRoster){
              Object.entries(draftSlots.slotByRoster).forEach(([rid, slot]) => {
                slotByRosterId[parseInt(rid)] = parseInt(slot);
              });
            }
            return txArrays.flat()
              .filter(tx => tx.type === 'trade' && tx.status === 'complete' && (tx.roster_ids||[]).includes(myRosterId))
              .map(tx => ({ ...tx, _leagueName: lg.name, _myRosterId: myRosterId, _slotByRosterId: slotByRosterId, _draftSeason: String(lg.season) }));
          }catch(e){ return []; }
        }));
        if(cancelled) return;
        const all = tradesNested.flat()
          .sort((a,b) => (b.status_updated || b.created || 0) - (a.status_updated || a.created || 0))
          .slice(0, cap);
        setTrades(all);
        setLeagueCount(dynasty.length);
        // Default to ★ Relevant tab if any matches (only when context provided)
        if(relevantContext && all.some(tradeIsRelevant)) setFilterMode('relevant');
        else setFilterMode('all');
        setStatus('ready');
      }catch(e){
        if(!cancelled) setStatus('error');
      }
    })();
    return () => { cancelled = true; };
  },[userInfo?.user_id]);

  const avatarUrl = userInfo?.avatar ? `https://sleepercdn.com/avatars/thumbs/${userInfo.avatar}` : null;

  return (
    <div onClick={onClose}
         style={{position:'fixed',inset:0,background:'var(--backdrop)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
      <div onClick={e=>e.stopPropagation()}
           style={{background:'var(--bg-elevated)',border:'2px solid var(--accent-gold)',borderRadius:14,padding:20,maxWidth:560,width:'100%',maxHeight:'80vh',overflowY:'auto'}}>
        <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:14,paddingBottom:12,borderBottom:'1px solid var(--border)'}}>
          {avatarUrl
            ? <img src={avatarUrl} alt="" style={{width:36,height:36,borderRadius:'50%',objectFit:'cover',background:'var(--bg-pill)'}} onError={e=>e.currentTarget.style.display='none'}/>
            : <div style={{width:36,height:36,borderRadius:'50%',background:'var(--bg-pill)',display:'flex',alignItems:'center',justifyContent:'center',color:'var(--accent-gold-text)',fontWeight:900}}>{(userInfo?.display_name||'?')[0].toUpperCase()}</div>}
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:15,fontWeight:900,color:'var(--text-bright)'}}>@{userInfo?.username || userInfo?.display_name}</div>
            <div style={{fontSize:11,color:'var(--text-secondary)'}}>{status === 'ready' ? `${trades.length} most recent trade${trades.length===1?'':'s'}` : 'Recent trades'}{leagueCount ? ` · scanned ${leagueCount} dynasty league${leagueCount===1?'':'s'}` : ''}</div>
          </div>
          <button onClick={onClose}
                  style={{background:'transparent',border:'1px solid var(--border-strong)',borderRadius:6,color:'var(--text-secondary)',padding:'4px 10px',cursor:'pointer',fontSize:14,fontWeight:700}}>×</button>
        </div>
        {status === 'loading' && <div style={{padding:20,textAlign:'center',color:'var(--text-secondary)',fontSize:13}}>Scanning league transactions…</div>}
        {status === 'error' && <div style={{padding:20,textAlign:'center',color:'var(--danger)',fontSize:13}}>Couldn't load trades — try again.</div>}
        {status === 'ready' && trades.length === 0 && (
          <div style={{padding:20,textAlign:'center',color:'var(--text-secondary)',fontSize:13}}>No completed trades found across this user's dynasty leagues.</div>
        )}
        {status === 'ready' && trades.length > 0 && (() => {
          const relevantTrades = relevantContext ? trades.filter(tradeIsRelevant) : [];
          // Asset search — case-insensitive substring match across every
          // player name (resolved via nameOf) and pick label (formatted via
          // formatPick) on both sides of each trade. A trade is kept if ANY
          // involved asset matches.
          const q = (searchQuery || '').trim().toLowerCase();
          const tradeMatchesSearch = (tx) => {
            if(!q) return true;
            const allPlayerIds = [
              ...Object.keys(tx.adds || {}),
              ...Object.keys(tx.drops || {}),
            ];
            for(const pid of allPlayerIds){
              if((nameOf(pid) || '').toLowerCase().includes(q)) return true;
            }
            for(const p of (tx.draft_picks || [])){
              if((formatPick(p, tx._slotByRosterId, tx._draftSeason) || '').toLowerCase().includes(q)) return true;
            }
            return false;
          };
          const baseTrades = filterMode === 'relevant' ? relevantTrades : trades;
          const visibleTrades = q ? baseTrades.filter(tradeMatchesSearch) : baseTrades;
          const hasRelevant = relevantTrades.length > 0;
          const tabBtn = (key, label, count, disabled) => (
            <button onClick={()=>!disabled && setFilterMode(key)} disabled={disabled}
                    style={{flex:1,padding:'8px 10px',borderRadius:6,
                            border: filterMode===key ? '1.5px solid var(--accent-gold)' : '1.5px solid var(--border)',
                            background: filterMode===key ? 'var(--accent-gold)' : 'transparent',
                            color: filterMode===key ? '#000' : (disabled?'var(--border-strong)':'var(--text-secondary)'),
                            fontWeight:800,fontSize:12,letterSpacing:0.4,cursor:disabled?'not-allowed':'pointer'}}>
              {label} <span style={{opacity:0.7,fontWeight:700}}>({count})</span>
            </button>
          );
          return (
            <>
              {relevantContext && (
                <div style={{display:'flex',gap:6,marginBottom:14}}>
                  {tabBtn('relevant', '★ Relevant', relevantTrades.length, !hasRelevant)}
                  {tabBtn('all', 'All trades', trades.length, false)}
                </div>
              )}
              <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:12}}>
                <input
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search by player or pick (e.g. 'jefferson' or '1.03')"
                  style={{flex:1,minWidth:0,padding:'9px 12px',background:'var(--bg-card)',border:'1px solid var(--border-strong)',borderRadius:6,color:'var(--text-bright)',fontSize:13}}
                />
                {searchQuery && (
                  <button onClick={()=>setSearchQuery('')}
                    title="Clear search"
                    style={{padding:'7px 10px',background:'transparent',border:'1px solid var(--border-strong)',borderRadius:5,color:'var(--text-secondary)',cursor:'pointer',fontSize:11,fontWeight:700}}>✕</button>
                )}
              </div>
              {q && (
                <div style={{fontSize:11,color:'var(--text-secondary)',marginBottom:10,marginTop:-4}}>
                  {visibleTrades.length} of {baseTrades.length} trade{baseTrades.length===1?'':'s'} match "{searchQuery}"
                </div>
              )}
              {filterMode === 'relevant' && relevantTrades.length === 0 && (
                <div style={{padding:20,textAlign:'center',color:'var(--text-secondary)',fontSize:13}}>No trades involve any player or pick from your current Trade Finder results.</div>
              )}
              {q && visibleTrades.length === 0 && (
                <div style={{padding:20,textAlign:'center',color:'var(--text-secondary)',fontSize:13}}>No trades match "{searchQuery}" in the {baseTrades.length} loaded.</div>
              )}
              {visibleTrades.map((tx,i) => {
                const myRosterId = tx._myRosterId;
                const received = Object.entries(tx.adds || {}).filter(([_,rid]) => rid === myRosterId).map(([pid]) => pid);
                const sent     = Object.entries(tx.drops || {}).filter(([_,rid]) => rid === myRosterId).map(([pid]) => pid);
                const picksReceived = (tx.draft_picks || []).filter(p => p.owner_id === myRosterId && p.previous_owner_id !== myRosterId);
                const picksSent     = (tx.draft_picks || []).filter(p => p.previous_owner_id === myRosterId && p.owner_id !== myRosterId);
                const date = tx.status_updated || tx.created;
                const dateStr = date ? new Date(date).toLocaleDateString(undefined, {month:'short', day:'numeric', year:'numeric'}) : '';
                const txRelevant = relevantContext && tradeIsRelevant(tx);
                // Startup detection: rookie drafts in dynasty are almost
                // always 4-5 rounds. A pick of round 6+ is effectively
                // always a startup-draft pick. Flag the whole trade if any
                // involved pick (either side) is round 6+.
                const isStartupTrade = (tx.draft_picks || []).some(p => (p.round || 0) >= 6);
                const renderItems = (playerIds, picks, kind) => {
                  const items = [
                    ...playerIds.map(pid => ({key:`${kind}-p-${pid}`, label:nameOf(pid), relevant:isRelevantPlayer(pid)})),
                    ...picks.map(p => ({key:`${kind}-d-${p.season}-${p.round}-${p.roster_id}`, label:formatPick(p, tx._slotByRosterId, tx._draftSeason), relevant:isRelevantPick(p)})),
                  ];
                  if(items.length === 0) return <span style={{color:'var(--text-secondary)'}}>nothing</span>;
                  return items.map((it, idx) => (
                    <React.Fragment key={it.key}>
                      {idx > 0 && <span style={{color:'var(--text-secondary)'}}> · </span>}
                      <span style={it.relevant ? {color:'var(--accent-gold-text)',fontWeight:800} : null}>
                        {it.relevant && '★ '}{it.label}
                      </span>
                    </React.Fragment>
                  ));
                };
                return (
                  <div key={tx.transaction_id || i}
                       style={{padding:'12px 14px',
                               background: txRelevant ? 'var(--accent-gold-bg)' : 'var(--bg-card)',
                               border: txRelevant ? '1px solid rgba(var(--accent-gold-rgb),0.4)' : '1px solid var(--border)',
                               borderRadius:8,marginBottom:10,fontSize:13}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8,gap:8,flexWrap:'wrap'}}>
                      <div style={{display:'flex',alignItems:'center',gap:6,maxWidth:'70%',minWidth:0}}>
                        <div style={{fontSize:11,color:'var(--accent-gold-text)',fontWeight:700,letterSpacing:0.3,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',minWidth:0}}>{tx._leagueName}</div>
                        {isStartupTrade && (
                          <span title="Trade involves a pick from round 6+ — almost always a startup-draft pick (rookie drafts are typically 4-5 rounds)" style={{flexShrink:0,padding:'2px 6px',background:'color-mix(in srgb, var(--pos-other,#7c8896) 20%, var(--bg-elevated))',color:'var(--text-bright)',border:'1px solid color-mix(in srgb, var(--pos-other,#7c8896) 50%, transparent)',borderRadius:4,fontSize:9,fontWeight:900,letterSpacing:0.6,whiteSpace:'nowrap'}}>STARTUP</span>
                        )}
                      </div>
                      <div style={{fontSize:11,color:'var(--text-secondary)',fontWeight:700,letterSpacing:0.5}}>{dateStr}</div>
                    </div>
                    <div style={{marginBottom:6}}>
                      <span style={{color:'var(--success)',fontWeight:800,fontSize:11,letterSpacing:0.5}}>RECEIVED:</span>
                      <div style={{color:'var(--text-eee)',marginTop:2,lineHeight:1.5}}>{renderItems(received, picksReceived, 'rec')}</div>
                    </div>
                    <div>
                      <span style={{color:'var(--danger)',fontWeight:800,fontSize:11,letterSpacing:0.5}}>SENT:</span>
                      <div style={{color:'var(--text-eee)',marginTop:2,lineHeight:1.5}}>{renderItems(sent, picksSent, 'snt')}</div>
                    </div>
                  </div>
                );
              })}
            </>
          );
        })()}
        <div style={{fontSize:10,color:'var(--text-secondary)',marginTop:8,textAlign:'center',lineHeight:1.5}}>{relevantContext ? '★ = involves a player/pick from your Trade Finder results · ' : ''}STARTUP = trade involves a pick from round 6+ (rookie drafts are typically 4-5 rounds) · player names via FantasyCalc index</div>
      </div>
    </div>
  );
}

function TradeFinderApp(){
  const [assets, setAssets] = useState(null); // null=loading, []=failed, [...]=loaded
  const [query, setQuery] = useState('');
  const [anchor, setAnchor] = useState(null);
  // Tolerance persists across visits — once a user finds their preferred range
  // they shouldn't have to re-set it every time. Clamp on read so stale or
  // tampered values can't break the slider.
  // Default 10% — tighter defaults like 3% returned zero matches for many anchors
  // and made the tool feel broken. The red delta color on lower-value results
  // already discourages overpaying.
  const [tolerance, setTolerance] = useState(() => {
    const saved = parseInt(localStorage.getItem('pfk_tf_tolerance') || '', 10);
    return (saved >= 3 && saved <= 25) ? saved : 10;
  });
  useEffect(() => { localStorage.setItem('pfk_tf_tolerance', String(tolerance)); }, [tolerance]);
  // 2026-05-20 rebuild — the All/Players/Picks filter pill row was retired.
  // The 2-col tile grid naturally separates picks (full-width PICKS tile) from
  // players (per-position tiles), so a manual filter became redundant. The
  // anchor-is-pick suppression below still hides picks when the anchor itself
  // is a pick, since that's a genuine "don't offer pick → pick swaps" rule.
  const [showSuggestions, setShowSuggestions] = useState(false);
  // Modal: "How ›" pill inside the anchor card opens this. Body-scroll lock
  // and Esc-close are handled in the modal effect just like HowItWorksButton.
  const [howOpen, setHowOpen] = useState(false);
  // WR tile capped at 6 visible. "+ N more ›" expands to show all WRs.
  // Resets whenever the anchor changes so a new search starts collapsed.
  const [wrExpanded, setWrExpanded] = useState(false);
  // Viewport-driven breakpoint for the results grid: at < 768px (phones)
  // tiles are too narrow to fit name + raw value + delta on one line
  // without ellipsing the name. We drop the raw value on mobile and keep
  // it on tablet/desktop where there's room. Name wins over raw value,
  // delta wins over name. Listener fires on resize + orientationchange so
  // landscape ↔ portrait flips re-evaluate.
  const [isWideTf, setIsWideTf] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 768);
  useEffect(() => {
    const onResize = () => setIsWideTf(window.innerWidth >= 768);
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);
  const inputRef = useRef(null);

  // ---- Sleeper account + league-aware values (Phase 1) ----
  // sleeperUser is now globally shared via useSleeperUser — linking happens in
  // the master toolbar (or anywhere else that uses the hook), and Trade Finder
  // re-renders automatically when the linked account changes.
  const [sleeperUser] = useSleeperUser();
  const [leagues, setLeagues] = useState(null);   // null=not-yet-fetched, []=fetched-empty, [...]=fetched
  const [leagueId, setLeagueId] = useState(() => localStorage.getItem('pfk_sleeper_league') || '');
  const selectedLeague = useMemo(() => (leagues||[]).find(l => l.league_id === leagueId) || null, [leagues, leagueId]);
  const activeFormat = useMemo(() => selectedLeague ? fcFormatForLeague(selectedLeague) : TF_DEFAULT_FORMAT, [selectedLeague]);

  // Re-fetch FC values whenever the active format changes (league switch or first load).
  // Anchor is intentionally preserved — the same player/pick is still a valid anchor,
  // we just refresh the value table around them.
  useEffect(() => {
    setAssets(null);
    fetchTradeFinderAssets(activeFormat).then(setAssets);
  },[activeFormat.key]);

  // Refetch leagues whenever the linked user changes (toolbar link/unlink).
  useEffect(() => {
    if(!sleeperUser?.user_id){
      setLeagues(null);
      setLeagueId('');
      return;
    }
    (async () => {
      try{
        const yr = String(new Date().getFullYear());
        const lgs = await fetch(`https://api.sleeper.app/v1/user/${sleeperUser.user_id}/leagues/nfl/${yr}`, {cache:'no-store'}).then(r=>r.json());
        pfkRecordUserLeagues(sleeperUser, lgs); // Sharp Tracker pool ingestion (Phase 0a)
        // Dynasty only — settings.type === 2 (redraft = different mindset, intentionally excluded)
        const dynasty = (lgs||[]).filter(l => l?.settings?.type === 2).sort((a,b) => (a.name||'').localeCompare(b.name||''));
        setLeagues(dynasty);
        // If the previously-saved leagueId isn't in the current set, clear it.
        if(leagueId && !dynasty.find(l => l.league_id === leagueId)) setLeagueId('');
      }catch(e){ setLeagues([]); }
    })();
  },[sleeperUser?.user_id]);

  const pickLeague = (id) => {
    setLeagueId(id);
    if(id) localStorage.setItem('pfk_sleeper_league', id);
    else localStorage.removeItem('pfk_sleeper_league');
  };

  // ---- Phase 2: leaguemate ownership + trade history ----
  // ownership = {
  //   byPlayerId:  {sleeperId -> rosterId}        — current rostered player owners
  //   byRosterId:  {rosterId  -> userInfo}        — roster → user profile
  //   byPickName:  {fcPickName -> rosterId}       — current owner of slot-specific picks
  //                                                  (CURRENT-year rookie draft only;
  //                                                   future picks have no slot yet)
  // }
  // Fetched eagerly when a league is selected so result rows show owner tags
  // the moment a user anchors on something. Trade history is fetched lazily
  // (on click) since most users won't open the modal.
  const [ownership, setOwnership] = useState(null);
  useEffect(() => {
    if(!selectedLeague?.league_id){
      setOwnership(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try{
        const lid = selectedLeague.league_id;
        // Fetch rosters first since lookupDraftSlots needs them; the rest in parallel.
        const rosters = await fetch(`https://api.sleeper.app/v1/league/${lid}/rosters`, {cache:'no-store'}).then(r=>r.json());
        if(cancelled) return;
        const [users, draftSlots, tradedPicks] = await Promise.all([
          fetch(`https://api.sleeper.app/v1/league/${lid}/users`, {cache:'no-store'}).then(r=>r.json()),
          // Reuse the same draft-slot resolver Power Rankings uses (slot_to_roster_id
          // first, draft_order fallback). Returns null for auction drafts or leagues
          // with no rookie draft yet.
          lookupDraftSlots(lid, rosters),
          fetch(`https://api.sleeper.app/v1/league/${lid}/traded_picks`, {cache:'no-store'}).then(r=>r.ok?r.json():[]).catch(()=>[]),
        ]);
        if(cancelled) return;
        const byUserId = {};
        (users||[]).forEach(u => { byUserId[u.user_id] = u; });
        const byRosterId = {};
        const byPlayerId = {};
        (rosters||[]).forEach(r => {
          const u = byUserId[r.owner_id];
          byRosterId[r.roster_id] = {
            roster_id: r.roster_id,
            user_id: r.owner_id,
            username: u?.username || null,
            display_name: u?.display_name || u?.username || `Roster ${r.roster_id}`,
            avatar: u?.avatar || null,
          };
          (r.players || []).forEach(pid => { byPlayerId[String(pid)] = r.roster_id; });
        });
        // Build slot-specific pick → current owner map. CURRENT-year picks only —
        // future-year slots aren't determined yet (no draft order set), and FC's
        // pick names are slot-specific so we'd be guessing for 2027+.
        const byPickName = {};
        if(draftSlots?.slotByRoster){
          // Reverse: slot → original-owner roster_id
          const rosterBySlot = {};
          Object.entries(draftSlots.slotByRoster).forEach(([rid, slot]) => {
            rosterBySlot[parseInt(slot)] = parseInt(rid);
          });
          const seasonStr = String(selectedLeague.season || new Date().getFullYear());
          const N = (rosters||[]).length;
          // Walk every rookie-draft slot × every plausible round (1-5).
          // FC names are formatted "YYYY Pick R.PP" with zero-padded slot.
          for(let round = 1; round <= 5; round++){
            for(let slot = 1; slot <= N; slot++){
              const originalRid = rosterBySlot[slot];
              if(!originalRid) continue;
              const tp = (tradedPicks||[]).find(t =>
                String(t.season) === seasonStr && t.round === round && t.roster_id === originalRid
              );
              const currentOwnerRid = tp ? tp.owner_id : originalRid;
              const slotPad = String(slot).padStart(2, '0');
              byPickName[`${seasonStr} Pick ${round}.${slotPad}`] = currentOwnerRid;
            }
          }
        }
        setOwnership({ byPlayerId, byRosterId, byPickName });
      }catch(e){
        if(!cancelled) setOwnership(null);
      }
    })();
    return () => { cancelled = true; };
  },[selectedLeague?.league_id]);

  // Trade history modal — entirely self-contained in <RecentTradesModal>.
  // We just track which user we're viewing; the modal handles scanning + render.
  const [tradeModalUser, setTradeModalUser] = useState(null);
  const openTradeHistory = (rosterInfo) => setTradeModalUser(rosterInfo);
  // Resolve a Sleeper player_id → readable name using the loaded FC asset list.
  // Used by RecentTradesModal so trade rows show "Bijan Robinson" not "Player #4866".
  const resolvePlayerName = (pid) => {
    if(!assets || !pid) return `Player #${pid}`;
    const hit = assets.find(a => a.sleeperId && String(a.sleeperId) === String(pid));
    return hit ? hit.name : `Player #${pid}`;
  };

  // displayAssets = FC values with PFK's per-position multipliers applied for
  // scoring fields FC doesn't take as params: pass TD, PPC, PPFD. All three
  // are independent and multiply through. When no league is linked (or none
  // of the fields are set / non-baseline), this is a pass-through.
  //
  // PPC + PPFD route through applyPpcAndPpfd — the SAME helper Pick Projector
  // uses — so tier-table tweaks land in both tools at once.
  const displayAssets = useMemo(() => {
    if(!assets) return null;
    const ptd  = selectedLeague?.scoring_settings?.pass_td;
    const ppc  = ppcLevelForLeague(selectedLeague);
    const ppfd = ppfdLevelForLeague(selectedLeague);
    const ptdActive = ptd && ptd !== 5;
    if(!ptdActive && !ppc && !ppfd) return assets;
    return assets.map(a => {
      const baseValue = a.value;
      let v = baseValue;
      if(ptdActive) v = ptdAdjust(v, a.pos, ptd);
      // PPC/PPFD: pass the BASE FC value as the tier-lookup baseline so RB
      // tier lookup is stable (doesn't shift after PTD multiplication).
      v = applyPpcAndPpfd(v, a.pos, selectedLeague, baseValue);
      return { ...a, value: v };
    });
  },[assets, selectedLeague]);

  // Anchor is stored as the asset object captured at selection time; if the league
  // changes after that, look up the current displayAssets entry by name so the
  // anchor's value stays in sync with whichever PTD multiplier is active now.
  const liveAnchor = useMemo(() => {
    if(!anchor || !displayAssets) return anchor;
    return displayAssets.find(a => a.name === anchor.name) || anchor;
  },[anchor, displayAssets]);

  // Suggestions: fuzzy on player names + canonical pick names. Boost exact pick matches.
  // Query gets the SAME normalization as asset names so apostrophes/punctuation
  // never break matching ("jamar" or "ja'marr" both find "Ja'Marr Chase").
  const suggestions = useMemo(() => {
    if(!displayAssets || !query.trim()) return [];
    const q = query.trim().toLowerCase().replace(/[^a-z0-9 ]/g,'').replace(/\s+/g,' ').trim();
    if(!q) return [];
    const pickCanonical = normalizePickQuery(query);
    const out = [];
    displayAssets.forEach(a => {
      let score = 0;
      if(pickCanonical && a.name === pickCanonical) score = 1000;
      else if(a.normName === q) score = 900;
      else if(a.normName.startsWith(q)) score = 500;
      else if(a.normName.includes(q)) score = 200;
      else if(a.isPick && a.name.toLowerCase().includes(q)) score = 100;
      if(score > 0) out.push({ ...a, _score: score });
    });
    return out.sort((x,y) => y._score - x._score || y.value - x.value).slice(0, 10);
  },[displayAssets, query]);

  // Set of sleeperIds + pick keys ("YYYY-R") for the anchor + every equivalent
  // currently visible. Used by the trade history modal to flag which trades
  // involve players/picks the user is actively considering — that's the most
  // useful context for "what does this leaguemate value these assets at?"
  const relevantContext = useMemo(() => {
    const playerIds = new Set();
    const pickKeys = new Set();
    const addAsset = (a) => {
      if(!a) return;
      if(a.isPick){
        // Match by season+round; FC's slot-specific pick name "2026 Pick 1.03"
        // and Sleeper's traded-pick "2026 1st" both reduce to "2026-1".
        const m = a.name.match(/^(\d{4})\s+(?:Pick\s+)?(\d+)/);
        if(m) pickKeys.add(`${m[1]}-${m[2]}`);
      } else if(a.sleeperId){
        playerIds.add(String(a.sleeperId));
      }
    };
    addAsset(liveAnchor);
    if(displayAssets && liveAnchor){
      const lo = liveAnchor.value * (1 - tolerance/100);
      const hi = liveAnchor.value * (1 + tolerance/100);
      displayAssets.forEach(a => {
        if(a.name === liveAnchor.name) return;
        if(a.value >= lo && a.value <= hi) addAsset(a);
      });
    }
    return { playerIds, pickKeys };
  },[liveAnchor, displayAssets, tolerance]);

  const tradeIsRelevant = (tx) => {
    if(!relevantContext) return false;
    // Players in the trade (adds + drops keys are sleeperIds)
    const allPlayerIds = new Set([
      ...Object.keys(tx.adds || {}),
      ...Object.keys(tx.drops || {}),
    ]);
    for(const pid of allPlayerIds){
      if(relevantContext.playerIds.has(String(pid))) return true;
    }
    // Picks in the trade (each has season + round)
    for(const p of (tx.draft_picks || [])){
      if(relevantContext.pickKeys.has(`${p.season}-${p.round}`)) return true;
    }
    return false;
  };
  const isRelevantPlayer = (pid) => relevantContext?.playerIds?.has(String(pid));
  const isRelevantPick = (p) => relevantContext?.pickKeys?.has(`${p.season}-${p.round}`);

  const equivalents = useMemo(() => {
    if(!liveAnchor || !displayAssets) return null;
    const lo = liveAnchor.value * (1 - tolerance/100);
    const hi = liveAnchor.value * (1 + tolerance/100);
    const matches = displayAssets.filter(a => a.name !== liveAnchor.name && a.value >= lo && a.value <= hi);
    // When anchoring on a pick, suppress other picks — users want vets, not
    // "1.09 → 1.08". The 2026-05-20 rebuild removed the manual filter row;
    // pick/player separation now happens visually via the PICKS tile.
    const filtered = liveAnchor.isPick ? matches.filter(a => !a.isPick) : matches;
    const groups = { QB:[], RB:[], WR:[], TE:[], PICK:[], OTHER:[] };
    filtered.forEach(a => {
      const k = groups[a.pos] ? a.pos : 'OTHER';
      groups[k].push(a);
    });
    Object.keys(groups).forEach(k => groups[k].sort((x,y) => y.value - x.value));
    return { groups, total: filtered.length };
  },[liveAnchor, displayAssets, tolerance]);

  // Reset the WR "+N more" expansion every time the anchor changes — a new
  // search starts collapsed so the grid reads at-a-glance again.
  useEffect(() => { setWrExpanded(false); }, [liveAnchor?.name]);

  // Body-scroll lock + Esc-to-close for the "How ›" modal opened from the
  // anchor card. Mirrors the pattern HowItWorksButton already uses so the
  // modal feels identical to every other PFK help surface.
  useEffect(() => {
    if(!howOpen) return;
    const onKey = e => { if(e.key === 'Escape') setHowOpen(false); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [howOpen]);

  const selectAsset = (a) => {
    setAnchor(a);
    setQuery(a.name);
    setShowSuggestions(false);
    if(inputRef.current) inputRef.current.blur();
    // On mobile, the keyboard dismiss + anchor-card render can leave the
    // viewport stuck mid-page with the anchor + tolerance slider above the
    // fold. Wait a frame for the keyboard to dismiss, then snap to top.
    requestAnimationFrame(() => window.scrollTo({top: 0, behavior: 'smooth'}));
  };

  const reset = () => {
    setAnchor(null);
    setQuery('');
    setShowSuggestions(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const posColor = (pos) => ({
    QB:'var(--pos-qb)', RB:'var(--pos-rb)', WR:'var(--pos-wr)', TE:'var(--pos-te)', PICK:'var(--accent-gold)', OTHER:'var(--pos-other)'
  }[pos] || 'var(--pos-other)');

  // ── Compact name + pick label formatters (2026-05-20 truncation fix) ───
  // Player tiles in the results grid are ~167px wide at 375px; full names
  // truncate to ellipsis on common dynasty names (Quinshon Judkins,
  // TreVeyon Henderson, etc.). Switch all position-tile names to
  // first-initial + last-name form ("Q. Judkins") so they actually fit.
  // Anchor card is intentionally NOT routed through this — it has room
  // for the full name.
  //
  // Edge cases handled:
  //   * Multi-initial first names ("A.J.", "J.K.") → kept as-is
  //   * Single-word names ("Pacheco" if it ever appears) → returned verbatim
  //   * Hyphenated last names ("Smith-Njigba") → preserved
  //   * Apostrophes ("Ja'Marr Chase") → preserved (only the first letter
  //     of the first token is used as the initial; rest is dropped)
  //   * Suffixes ("Marvin Harrison Jr.") → preserved
  const formatPlayerShortName = (name) => {
    if(!name) return '';
    const s = String(name).trim();
    if(!s) return '';
    const parts = s.split(/\s+/);
    if(parts.length === 1) return parts[0];
    const first = parts[0];
    const rest = parts.slice(1).join(' ');
    // Multi-initial first names already contain a dot — preserve verbatim.
    if(first.indexOf('.') !== -1) return `${first} ${rest}`;
    return `${first.charAt(0).toUpperCase()}. ${rest}`;
  };

  // Pick names from FantasyCalc come in the form "2026 Pick 1.03". For
  // the PICKS tile we display short labels: "'26 1st", "'27 3rd". Slot
  // info (".03") is intentionally dropped — the slot doesn't fit at our
  // tile widths and the ordinal round is what users care about.
  const formatPickShortLabel = (name) => {
    if(!name) return '?';
    const m = String(name).match(/^(\d{4})\s+Pick\s+(\d+)/);
    if(!m) return name;
    const yr = m[1].slice(2);                 // '2026' → '26'
    const rnd = parseInt(m[2], 10);
    const ord = rnd === 1 ? '1st'
              : rnd === 2 ? '2nd'
              : rnd === 3 ? '3rd'
              : `${rnd}th`;
    return `'${yr} ${ord}`;
  };

  return (
    <div style={{background:'var(--bg-page)',minHeight:'100vh',color:'var(--text-primary)',fontFamily:"'Inter','Segoe UI',sans-serif"}}>
      <MasterToolbar currentTab="trade"/>
      <div style={{maxWidth:1100,margin:'0 auto',padding:'24px 16px 60px'}}>

        {/* League dropdown — Sleeper link itself lives in the master toolbar now,
            so all we need here is the per-tool league picker (one league at a time
            in Trade Finder, even if the user's in many). */}
        <div style={{marginBottom:20,display:'flex',flexDirection:'column',alignItems:'flex-start',gap:8}}>
          {!sleeperUser && (
            <div style={{padding:'10px 14px',background:'var(--bg-elevated)',border:'1px dashed rgba(var(--accent-gold-rgb),0.27)',borderRadius:8,color:'var(--text-secondary)',fontSize:12,maxWidth:480}}>
              💡 Link your Sleeper account in the toolbar above — Trade Finder will then auto-adjust values to your specific league's scoring settings.
            </div>
          )}
          {sleeperUser && (
            <>
              {leagues === null && <div style={{fontSize:12,color:'var(--text-secondary)'}}>Loading your dynasty leagues…</div>}
              {leagues && leagues.length === 0 && <div style={{fontSize:12,color:'var(--text-secondary)'}}>No dynasty leagues found for {new Date().getFullYear()}.</div>}
              {leagues && leagues.length > 0 && (
                <select value={leagueId} onChange={e=>pickLeague(e.target.value)}
                        style={{padding:'8px 12px',background:'var(--bg-elevated)',border:'1.5px solid rgba(var(--accent-gold-rgb),0.4)',borderRadius:6,color:'var(--text-bright)',fontSize:13,fontFamily:'inherit',minWidth:240,maxWidth:'100%',cursor:'pointer'}}>
                  <option value="">— Pick a league —</option>
                  {leagues.map(l => <option key={l.league_id} value={l.league_id}>{l.name}</option>)}
                </select>
              )}
            </>
          )}
        </div>

        <div style={{textAlign:'center',marginBottom:18}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:10,flexWrap:'wrap',marginBottom:6}}>
            <div style={{fontSize:28,fontWeight:900,color:'var(--accent-gold-text)',letterSpacing:2.5}}>⚖️ TRADE FINDER</div>
            <HowItWorksButton title="Trade Finder">
              <p><strong>What it does:</strong> Type any dynasty player or rookie pick — see every equivalent-value asset on the market, calibrated to your specific league.</p>
              <ol style={{paddingLeft:20,marginTop:10,display:'flex',flexDirection:'column',gap:8}}>
                <li><strong>Anchor</strong> on a player or pick (e.g. <code style={{color:'var(--accent-gold-text)',background:'var(--accent-gold-bg)',padding:'1px 5px',borderRadius:3}}>Bijan Robinson</code> or <code style={{color:'var(--accent-gold-text)',background:'var(--accent-gold-bg)',padding:'1px 5px',borderRadius:3}}>1.05</code>). Live FantasyCalc dynasty values power the baseline.</li>
                <li><strong>Values get adjusted</strong> for your league's scoring: Superflex/1QB, TEP, pass-TD points, PPC, PPFD all factor in.</li>
                <li><strong>Results are grouped by position</strong> (QB, RB, WR, TE, Picks) and ranked by closeness to your anchor's value.</li>
                <li><strong>Tolerance slider</strong> widens or tightens the value range. Lower = stricter; higher = more options.</li>
                <li><strong>If you link a Sleeper league</strong>, each result also shows the current owner — so you know who to actually trade with.</li>
              </ol>
              <p style={{marginTop:12,fontSize:12,color:'var(--text-muted)',fontStyle:'italic'}}>Reverses the flow of every other trade calculator: instead of guessing two players and asking "is this fair?", you anchor on what you want to send and discover what's worth asking back.</p>
            </HowItWorksButton>
          </div>
          <div style={{fontSize:13,color:'var(--text-bbb)',maxWidth:600,margin:'0 auto',lineHeight:1.5}}>
            {!liveAnchor && <>Type a rookie pick (e.g. <code style={{color:'var(--accent-gold-text)',background:'var(--accent-gold-bg)',padding:'1px 6px',borderRadius:4}}>1.03</code>) or any player name to see every equivalent-value asset grouped by position. </>}
            <strong style={{color:'var(--text-eee)'}}>Link your Sleeper account in the toolbar</strong> and pick a league — values auto-adjust to that league's exact scoring settings.
          </div>
          {/* Format chip — tells the user exactly which value table is in use right now. */}
          <div style={{marginTop:10,display:'inline-flex',alignItems:'center',gap:8,padding:'5px 12px',background:'var(--bg-elevated)',border:'1px solid '+(selectedLeague?'rgba(var(--success-rgb),0.4)':'var(--border)'),borderRadius:16,fontSize:11,fontWeight:700,letterSpacing:0.5,color:selectedLeague?'var(--success)':'var(--text-muted)'}}>
            <span style={{fontSize:9}}>●</span>
            {selectedLeague
              ? <><span style={{color:'var(--text-bright)'}}>{selectedLeague.name}</span> — {formatLabel(activeFormat, selectedLeague)}</>
              : <>Default values — {formatLabel(TF_DEFAULT_FORMAT)}</>
            }
          </div>
        </div>

        <div style={{position:'relative',marginBottom:18}}>
          <div style={{display:'flex',gap:8}}>
            <input
              ref={inputRef}
              value={query}
              onChange={e=>{ setQuery(e.target.value); setShowSuggestions(true); if(anchor) setAnchor(null); }}
              onFocus={()=>setShowSuggestions(true)}
              onBlur={()=>setTimeout(()=>setShowSuggestions(false), 150)}
              onKeyDown={e=>{ if(e.key==='Enter' && suggestions.length>0){ e.preventDefault(); selectAsset(suggestions[0]); } }}
              placeholder={assets===null ? 'Loading values…' : 'Try "1.03" or "Ja\'Marr Chase"'}
              disabled={assets===null}
              style={{flex:1,padding:'14px 18px',background:'var(--bg-elevated)',border:'1.5px solid rgba(var(--accent-gold-rgb),0.4)',borderRadius:8,color:'var(--text-bright)',fontSize:16,fontFamily:'inherit'}}/>
            {anchor && <button onClick={reset} style={{padding:'14px 18px',background:'transparent',border:'1px solid var(--border-outline)',borderRadius:8,color:'var(--text-secondary)',fontWeight:700,fontSize:13,cursor:'pointer',letterSpacing:0.5}}>CLEAR</button>}
          </div>
          {showSuggestions && suggestions.length > 0 && (
            <div style={{position:'absolute',top:'100%',left:0,right:anchor?72:0,marginTop:4,background:'var(--bg-card)',border:'1px solid rgba(var(--accent-gold-rgb),0.27)',borderRadius:8,boxShadow:'0 4px 12px rgba(0,0,0,0.15)',zIndex:50,maxHeight:320,overflowY:'auto'}}>
              {suggestions.map(s => (
                <div key={s.name} onMouseDown={()=>selectAsset(s)}
                     style={{padding:'10px 14px',cursor:'pointer',borderBottom:'1px solid var(--bg-pill)',display:'flex',alignItems:'center',gap:10,fontSize:14}}
                     onMouseEnter={e=>e.currentTarget.style.background='var(--accent-gold-bg)'}
                     onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                  <span style={{padding:'2px 7px',background:`color-mix(in srgb, ${posColor(s.pos)} 13%, transparent)`,color:posColor(s.pos),borderRadius:4,fontSize:10,fontWeight:900,letterSpacing:0.5,minWidth:38,textAlign:'center'}}>{s.pos}</span>
                  <span style={{flex:1,color:'var(--text-bright)'}}>{s.name}</span>
                  <span style={{color:'var(--accent-gold-text)',fontWeight:800,fontSize:13}}>{Math.round(s.value).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {liveAnchor && (() => {
          // ============================================================
          // 2026-05-20 rebuild — 2-col tile grid. Replaces the auto-fit
          // sectioned layout. Goal: most positions at-a-glance on a
          // single mobile screen.
          //
          // Page order when an anchor is selected:
          //   ZONE 1 — tolerance slider (above the anchor card)
          //   ZONE 2 — compact anchor card with "How ›" pill
          //   GRID   — 2-col tiles (QB / RB / WR / TE) + full-width
          //            PICKS tile spanning both columns + OTHER fallback
          //
          // Behavior preserved verbatim:
          //   - Tolerance slider still drives `equivalents` via tolerance
          //     state (unchanged math).
          //   - Anchor-on-a-pick still suppresses the PICKS tile (the
          //     equivalents filter drops same-class assets).
          //   - Every player row + pick cell that has a resolved owner is
          //     a tap target → openTradeHistory(ownerInfo). Unchanged.
          //   - PlayerHeadshot is the same component as before.
          //   - relevantContext (the prop RecentTradesModal uses for its
          //     ★ Relevant tab) is also untouched — it's computed off
          //     liveAnchor + displayAssets + tolerance, independent of
          //     this render.
          // ============================================================
          const lo = Math.round(liveAnchor.value * (1 - tolerance/100));
          const hi = Math.round(liveAnchor.value * (1 + tolerance/100));
          // Count of distinct leaguemates across every visible result —
          // shown in the anchor card sub-line so the user gets a quick
          // sense of how many trade partners are in play before scanning
          // the tiles. Only computed when ownership is loaded.
          let leaguematesCount = 0;
          if(ownership && equivalents){
            const owners = new Set();
            ['QB','RB','WR','TE','PICK','OTHER'].forEach(k => {
              (equivalents.groups[k]||[]).forEach(a => {
                const rid = a.isPick
                  ? ownership.byPickName?.[a.name]
                  : (a.sleeperId ? ownership.byPlayerId[String(a.sleeperId)] : null);
                if(rid) owners.add(rid);
              });
            });
            leaguematesCount = owners.size;
          }
          // ── Mobile size bumps (2026-05-20 readability pass) ────────────
          // The original sizes packed maximum density into a 375px screen
          // and were genuinely hard to read on a phone. tablet+ keeps the
          // dense layout since it has the screen real estate; mobile gets
          // a real bump across every interactive element — names, deltas,
          // usernames, and both avatars. Anchor card / tolerance slider /
          // "How ›" pill are intentionally NOT bumped (separate concerns).
          const tfSizes = isWideTf ? {
            posHdr:    11,  name: 11,  delta: 10,  username: 9,
            pickLabel: 11,  headshot: 28,  ownerAv: 10,
          } : {
            posHdr:    15,  name: 14,  delta: 12,  username: 11,
            pickLabel: 14,  headshot: 34,  ownerAv: 13,
          };
          // Render a single result row inside a position tile. Whole row
          // is one <button> so the entire surface (photo + name + value +
          // delta + owner line) is the tap target → openTradeHistory.
          // Rows without a resolvable owner are non-interactive divs.
          const renderPlayerRow = (a, tileKey) => {
            const delta = ((a.value - liveAnchor.value) / liveAnchor.value) * 100;
            // Green = equivalent worth MORE (good return for the seller);
            // red = worth less; muted gray if within 0.1% (near-perfect).
            const deltaStr = (delta >= 0 ? '+' : '') + delta.toFixed(1) + '%';
            const deltaColor = Math.abs(delta) < 0.1 ? 'var(--text-muted)' : delta > 0 ? 'var(--success)' : 'var(--danger)';
            const ownerRosterId = ownership ? (
              a.isPick ? ownership.byPickName?.[a.name]
                       : (a.sleeperId ? ownership.byPlayerId[String(a.sleeperId)] : null)
            ) : null;
            const ownerInfo = ownerRosterId ? ownership.byRosterId[ownerRosterId] : null;
            const tappable = !!ownerInfo;
            const inner = (
              <>
                <PlayerHeadshot sleeperId={a.sleeperId} name={a.name} pos={a.pos || tileKey} size={tfSizes.headshot}/>
                <div style={{flex:1,minWidth:0,display:'flex',flexDirection:'column',gap:2}}>
                  <div style={{display:'flex',alignItems:'baseline',gap:6,minWidth:0}}>
                    {/* First-initial + last-name format (e.g. "Q. Judkins")
                        keeps every dynasty name on one line at our tile
                        widths. Anchor card is unchanged. */}
                    <span data-tf-player-name={a.name}
                          style={{flex:1,minWidth:0,fontSize:tfSizes.name,fontWeight:500,color:'var(--text-eee)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{formatPlayerShortName(a.name)}</span>
                    {/* Raw value lives on tablet/desktop only (>= 768px).
                        On mobile we drop it so the player name has the full
                        tile width to render without truncation. Delta is
                        never dropped — it's the most important number. */}
                    {isWideTf && <span style={{flexShrink:0,fontSize:10,color:'var(--accent-gold-text)',fontWeight:500,fontVariantNumeric:'tabular-nums'}}>{Math.round(a.value).toLocaleString()}</span>}
                    <span style={{flexShrink:0,fontSize:tfSizes.delta,color:deltaColor,fontWeight:700,fontVariantNumeric:'tabular-nums'}}>{deltaStr}</span>
                  </div>
                  {ownerInfo && (
                    <div style={{display:'flex',alignItems:'center',gap:4,minWidth:0}}>
                      {ownerInfo.avatar
                        ? <img src={`https://sleepercdn.com/avatars/thumbs/${ownerInfo.avatar}`} alt="" style={{width:tfSizes.ownerAv,height:tfSizes.ownerAv,borderRadius:'50%',objectFit:'cover',background:'var(--bg-pill)',flexShrink:0}} onError={e=>e.currentTarget.style.display='none'}/>
                        : <span aria-hidden="true" style={{fontSize:tfSizes.username,lineHeight:1}}>👤</span>}
                      <span className="pfk-tf-username"
                            style={{fontSize:tfSizes.username,fontWeight:600,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',minWidth:0}}>@{ownerInfo.username || ownerInfo.display_name}</span>
                    </div>
                  )}
                </div>
              </>
            );
            return tappable
              ? <button key={a.name} type="button"
                        onClick={()=>openTradeHistory(ownerInfo)}
                        data-tf-player-row={a.name}
                        data-tooltip={`Tap to see ${ownerInfo.display_name}'s recent trades`}
                        style={{display:'flex',alignItems:'center',gap:8,padding:0,background:'transparent',border:'none',color:'inherit',font:'inherit',textAlign:'left',cursor:'pointer',minWidth:0,width:'100%'}}>
                  {inner}
                </button>
              : <div key={a.name} data-tf-player-row={a.name} data-tf-player-tappable="0"
                     style={{display:'flex',alignItems:'center',gap:8,minWidth:0,width:'100%'}}>
                  {inner}
                </div>;
          };
          // Render a single pick cell inside the PICKS tile. Layout is:
          //   [avatar]  '26 1st            +9.2%
          //             @ownerUsername
          // Avatar (10px, matching the player-row owner avatar) sits to the
          // left of a two-line text column. Tappable when an owner can be
          // resolved (current-season slot-mapped picks); future-year picks
          // with no slot mapping render as non-tappable cells.
          // 2026-05-20 rebuild — old format truncated to "2026 Pic..." with
          // no usable info; this version surfaces ordinal round + delta +
          // owner @username in <167px tile widths.
          const renderPickCell = (a) => {
            const delta = ((a.value - liveAnchor.value) / liveAnchor.value) * 100;
            const deltaStr = (delta >= 0 ? '+' : '') + delta.toFixed(1) + '%';
            const deltaColor = Math.abs(delta) < 0.1 ? 'var(--text-muted)' : delta > 0 ? 'var(--success)' : 'var(--danger)';
            const ownerRosterId = ownership ? ownership.byPickName?.[a.name] : null;
            const ownerInfo = ownerRosterId ? ownership.byRosterId[ownerRosterId] : null;
            const tappable = !!ownerInfo;
            const shortLabel = formatPickShortLabel(a.name);
            const avatar = ownerInfo
              ? (ownerInfo.avatar
                  ? <img src={`https://sleepercdn.com/avatars/thumbs/${ownerInfo.avatar}`} alt="" style={{width:tfSizes.ownerAv,height:tfSizes.ownerAv,borderRadius:'50%',objectFit:'cover',background:'var(--bg-pill)',flexShrink:0,marginTop:3}} onError={e=>e.currentTarget.style.display='none'}/>
                  : <span aria-hidden="true" style={{fontSize:tfSizes.username,lineHeight:1,flexShrink:0,marginTop:3}}>👤</span>)
              : null;
            const inner = (
              <>
                {avatar}
                <div style={{flex:1,minWidth:0,display:'flex',flexDirection:'column',gap:2}}>
                  <div style={{display:'flex',alignItems:'baseline',gap:6,minWidth:0}}>
                    <span data-tf-pick-label={a.name}
                          style={{flex:1,minWidth:0,fontSize:tfSizes.pickLabel,fontWeight:600,color:'var(--text-eee)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',fontVariantNumeric:'tabular-nums'}}>{shortLabel}</span>
                    {isWideTf && <span style={{flexShrink:0,fontSize:10,color:'var(--accent-gold-text)',fontWeight:500,fontVariantNumeric:'tabular-nums'}}>{Math.round(a.value).toLocaleString()}</span>}
                    <span style={{flexShrink:0,fontSize:tfSizes.delta,color:deltaColor,fontWeight:700,fontVariantNumeric:'tabular-nums'}}>{deltaStr}</span>
                  </div>
                  {ownerInfo && (
                    <span className="pfk-tf-username"
                          style={{fontSize:tfSizes.username,fontWeight:600,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>@{ownerInfo.username || ownerInfo.display_name}</span>
                  )}
                </div>
              </>
            );
            const cellStyle = {display:'flex',alignItems:'flex-start',gap:6,padding:'6px 8px',background:'var(--bg-card)',border:'0.5px solid var(--border-hairline)',borderRadius:8,minWidth:0};
            return tappable
              ? <button key={a.name} type="button"
                        onClick={()=>openTradeHistory(ownerInfo)}
                        data-tf-pick-cell={a.name}
                        data-tooltip={`Tap to see ${ownerInfo.display_name}'s recent trades`}
                        style={{...cellStyle,width:'100%',color:'inherit',textAlign:'left',font:'inherit',cursor:'pointer'}}>
                  {inner}
                </button>
              : <div key={a.name} data-tf-pick-cell={a.name} data-tf-pick-tappable="0" style={cellStyle}>
                  {inner}
                </div>;
          };
          return (
            <>
              {/* ZONE 1 — VALUE TOLERANCE SLIDER (moved above the anchor
                  card 2026-05-20 rebuild). Compact single-band card,
                  ~70px tall. Range sub-line uses a regular hyphen, never
                  an en-dash. */}
              <div data-tf-tolerance="1"
                   style={{background:'var(--bg-elevated)',border:'0.5px solid var(--border)',borderRadius:10,padding:'10px 14px',marginBottom:8}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',marginBottom:6}}>
                  <span style={{fontSize:11,color:'var(--text-muted)',fontWeight:700,letterSpacing:0.6,textTransform:'uppercase'}}>Value tolerance</span>
                  <span style={{fontSize:12,color:'var(--accent-gold-text)',fontWeight:800,fontVariantNumeric:'tabular-nums'}}>±{tolerance}%</span>
                </div>
                <input type="range" min="3" max="25" step="1" value={tolerance}
                       onChange={e=>setTolerance(Number(e.target.value))}
                       aria-label="Value tolerance percent"
                       style={{width:'100%',accentColor:'var(--accent-gold)',display:'block',margin:0}}/>
                <div style={{fontSize:11,color:'var(--text-secondary)',marginTop:4,fontVariantNumeric:'tabular-nums'}}>
                  Range: {lo.toLocaleString()} - {hi.toLocaleString()}
                </div>
              </div>

              {/* ZONE 2 — ANCHOR CARD (compact, ~60px tall). Photo (32px)
                  on the left, identity + counts in the middle, "How ›"
                  pill on the right opens the help modal. The 3-bullet
                  always-visible "How it works" hint that used to live
                  above the grid now lives inside that modal. */}
              <div data-tf-anchor="1"
                   style={{background:'linear-gradient(135deg,var(--accent-gold-bg) 0%,var(--bg-card) 100%)',border:'1.5px solid var(--accent-gold)',borderRadius:10,padding:'10px 12px',marginBottom:12,display:'flex',alignItems:'center',gap:10}}>
                {!liveAnchor.isPick ? (
                  <PlayerHeadshot sleeperId={liveAnchor.sleeperId} name={liveAnchor.name} pos={liveAnchor.pos} size={32}/>
                ) : (
                  <div aria-hidden="true" style={{width:32,height:32,borderRadius:'50%',background:'var(--bg-pill)',color:'var(--accent-gold-text)',fontSize:10,fontWeight:900,letterSpacing:0.4,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,border:'1px solid var(--border-hairline)'}}>PCK</div>
                )}
                <div style={{flex:1,minWidth:0,display:'flex',flexDirection:'column',gap:2}}>
                  <div style={{fontSize:13,fontWeight:700,color:'var(--text-bright)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                    <span>{liveAnchor.name}</span>
                    <span style={{color:'var(--text-muted)',fontWeight:500}}> · {liveAnchor.pos} · </span>
                    <span style={{color:'var(--accent-gold-text)',fontWeight:800,fontVariantNumeric:'tabular-nums'}}>{Math.round(liveAnchor.value).toLocaleString()}</span>
                  </div>
                  <div style={{fontSize:10,color:'var(--text-secondary)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                    {equivalents
                      ? `${equivalents.total} ${equivalents.total === 1 ? 'asset' : 'assets'}` + (leaguematesCount ? ` across ${leaguematesCount} ${leaguematesCount === 1 ? 'leaguemate' : 'leaguemates'}` : '')
                      : 'Loading…'}
                  </div>
                </div>
                <button type="button" onClick={()=>setHowOpen(true)}
                        aria-label="How Trade Finder works"
                        data-tf-how-pill="1"
                        style={{flexShrink:0,padding:'5px 10px',background:'var(--bg-elevated)',border:'1px solid var(--border-strong)',borderRadius:12,color:'var(--accent-gold-text)',fontSize:10,fontWeight:800,letterSpacing:0.4,cursor:'pointer',fontFamily:'inherit',whiteSpace:'nowrap'}}>
                  How ›
                </button>
              </div>

              {/* EMPTY STATE — slider has been pulled too tight. */}
              {equivalents && equivalents.total === 0 && (
                <div style={{textAlign:'center',padding:40,color:'var(--text-secondary)',fontSize:14}}>
                  No equivalent assets in this range. Widen the tolerance with the slider above.
                </div>
              )}

              {/* Inline hint — appears on every results view (above the
                  grid, below anchor + slider). Drives the click-through
                  behavior that is Trade Finder's core value: a user
                  tapping any @username opens that owner's recent trades
                  with the current Trade Finder context (★ Relevant). */}
              {equivalents && equivalents.total > 0 && (
                <div data-tf-hint="1"
                     style={{textAlign:'center',padding:'10px 8px',fontSize:11,color:'var(--text-secondary)',lineHeight:1.4}}>
                  Tap any owner's <span style={{color:'var(--accent-gold-text)',fontWeight:700}}>@username</span> to see their recent trades involving these assets
                </div>
              )}
              {/* GRID — 2-col mobile-first. Position tiles auto-size to
                  their content height (WR may run taller than QB; that's
                  intentional). PICKS spans full width via grid-column 1 /
                  -1. OTHER (rare fallback) renders as a normal tile. */}
              {equivalents && equivalents.total > 0 && (
                <div data-tf-results-grid="1"
                     style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                  {['QB','RB','WR','TE'].map(k => {
                    const list = equivalents.groups[k] || [];
                    if(list.length === 0) return null;
                    const isWr = k === 'WR';
                    const visible = (isWr && !wrExpanded) ? list.slice(0,6) : list;
                    const hiddenCount = (isWr && !wrExpanded) ? Math.max(0, list.length - 6) : 0;
                    return (
                      <div key={k} data-tf-tile={k}
                           style={{background:'var(--bg-elevated)',border:'0.5px solid var(--border)',borderRadius:10,padding:'10px 12px',display:'flex',flexDirection:'column',gap:8,minWidth:0}}>
                        <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',paddingBottom:6,borderBottom:'0.5px solid var(--border)'}}>
                          <span style={{fontSize:tfSizes.posHdr,fontWeight:800,color:posColor(k),letterSpacing:0.8}}>{k}</span>
                          <span style={{fontSize:tfSizes.delta,color:'var(--text-muted)',fontWeight:600}}>{list.length}</span>
                        </div>
                        <div style={{display:'flex',flexDirection:'column',gap:8}}>
                          {visible.map(a => renderPlayerRow(a, k))}
                        </div>
                        {isWr && hiddenCount > 0 && (
                          <button type="button" onClick={()=>setWrExpanded(true)}
                                  data-tf-wr-expand="1"
                                  style={{marginTop:4,padding:'6px 0 0',background:'transparent',border:'none',borderTop:'1px dashed var(--border)',color:'var(--text-muted)',fontSize:10,fontWeight:600,cursor:'pointer',fontFamily:'inherit',letterSpacing:0.3,textAlign:'center',width:'100%'}}>
                            + {hiddenCount} more ›
                          </button>
                        )}
                      </div>
                    );
                  })}
                  {(equivalents.groups.PICK || []).length > 0 && (
                    <div data-tf-tile="PICK"
                         style={{gridColumn:'1 / -1',background:'var(--bg-elevated)',border:'0.5px solid var(--border)',borderRadius:10,padding:'10px 12px',display:'flex',flexDirection:'column',gap:8,minWidth:0}}>
                      <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',paddingBottom:6,borderBottom:'0.5px solid var(--border)'}}>
                        <span style={{fontSize:tfSizes.posHdr,fontWeight:800,color:'var(--accent-gold-text)',letterSpacing:0.8}}>PICKS</span>
                        <span style={{fontSize:tfSizes.delta,color:'var(--text-muted)',fontWeight:600}}>{equivalents.groups.PICK.length}</span>
                      </div>
                      {/* Responsive inner grid: at mobile widths each pick
                          stacks vertically (one per row) so the new label +
                          avatar + delta + @username layout has the full tile
                          width to render. At tablet+ widths the 3-column
                          arrangement returns. */}
                      <div style={{display:'grid',gridTemplateColumns: isWideTf ? '1fr 1fr 1fr' : '1fr',gap:8}}>
                        {equivalents.groups.PICK.map(a => renderPickCell(a))}
                      </div>
                    </div>
                  )}
                  {(equivalents.groups.OTHER || []).length > 0 && (
                    <div data-tf-tile="OTHER"
                         style={{gridColumn:'1 / -1',background:'var(--bg-elevated)',border:'0.5px solid var(--border)',borderRadius:10,padding:'10px 12px',display:'flex',flexDirection:'column',gap:8,minWidth:0}}>
                      <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',paddingBottom:6,borderBottom:'0.5px solid var(--border)'}}>
                        <span style={{fontSize:tfSizes.posHdr,fontWeight:800,color:posColor('OTHER'),letterSpacing:0.8}}>OTHER</span>
                        <span style={{fontSize:tfSizes.delta,color:'var(--text-muted)',fontWeight:600}}>{equivalents.groups.OTHER.length}</span>
                      </div>
                      <div style={{display:'flex',flexDirection:'column',gap:8}}>
                        {equivalents.groups.OTHER.map(a => renderPlayerRow(a, 'OTHER'))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          );
        })()}

        {!anchor && assets && assets.length > 0 && (
          <div style={{textAlign:'center',padding:'40px 20px 20px',color:'var(--text-secondary)',fontSize:14,lineHeight:1.7}}>
            <div style={{fontSize:34,marginBottom:8}}>💡</div>
            Pick anything to anchor on — we'll show every player and pick within ±10% of its value by default.<br/>
            <span style={{fontSize:12,color:'var(--text-secondary)'}}>Use the slider to tighten the range down to ±3% or widen up to ±25%.</span>
            <div style={{maxWidth:560,margin:'24px auto 0',padding:'14px 16px',background:'var(--bg-elevated)',border:'1px solid rgba(var(--accent-gold-rgb),0.2)',borderRadius:8,textAlign:'left',color:'var(--text-secondary)',fontSize:12,lineHeight:1.6}}>
              <div style={{color:'var(--accent-gold-text)',fontWeight:800,letterSpacing:0.5,fontSize:11,marginBottom:8}}>👥 WITH SLEEPER LINKED</div>
              <div>
                Values auto-adjust to your league's exact scoring settings, and every result tags the leaguemate who owns it. Tap any username to see their <strong style={{color:'var(--text-bright)'}}>last 500 dynasty trades</strong>.
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Trade history modal — handed off to the shared RecentTradesModal so
          Trade Finder + Sleeper Snapshot stay in sync. We pass the current
          Trade Finder context (anchor + equivalents) so the ★ Relevant tab
          can highlight trades involving assets the user is actively
          considering. */}
      {tradeModalUser && (
        <RecentTradesModal
          userInfo={tradeModalUser}
          onClose={() => setTradeModalUser(null)}
          resolvePlayerName={resolvePlayerName}
          relevantContext={relevantContext}
        />
      )}

      {/* "How ›" pill in the anchor card opens this modal. Houses the
          three-bullet usage tips that previously rendered always-visible
          above the results grid. Body-scroll lock + Esc-close handled in
          a useEffect higher up. */}
      {howOpen && (
        <div onClick={()=>setHowOpen(false)}
             role="dialog" aria-modal="true"
             data-tf-how-modal="1"
             style={{position:'fixed',inset:0,background:'var(--backdrop)',zIndex:1000,display:'flex',alignItems:'flex-start',justifyContent:'center',padding:'4vh 14px',overflowY:'auto'}}>
          <div onClick={e=>e.stopPropagation()}
               style={{background:'var(--bg-card)',border:'1.5px solid var(--accent-gold)',borderRadius:14,padding:'20px 22px 22px',maxWidth:520,width:'100%',position:'relative',boxShadow:'var(--shadow-card)',color:'var(--text-primary)'}}>
            <button type="button" onClick={()=>setHowOpen(false)} aria-label="Close"
                    style={{position:'absolute',top:8,right:10,background:'transparent',border:'none',color:'var(--text-secondary)',fontSize:24,cursor:'pointer',fontFamily:'inherit',padding:6,lineHeight:1}}>×</button>
            <div style={{fontSize:11,fontWeight:800,color:'var(--text-muted)',letterSpacing:1.5,textTransform:'uppercase',marginBottom:6}}>How it works</div>
            <h2 style={{fontSize:17,fontWeight:900,color:'var(--accent-gold-text)',letterSpacing:0.4,marginTop:0,marginBottom:12,paddingRight:24,lineHeight:1.25}}>Reading the results</h2>
            <ul style={{margin:0,paddingLeft:20,fontSize:13,lineHeight:1.6,color:'var(--text-secondary)',display:'flex',flexDirection:'column',gap:8}}>
              <li><span style={{color:'var(--success)',fontWeight:700}}>👤 @username</span> under a row shows the leaguemate currently rostering that asset in <strong style={{color:'var(--text-bright)'}}>{selectedLeague?.name || 'your league'}</strong>.</li>
              <li>Tap any player row or pick cell to see that owner's <strong style={{color:'var(--text-bright)'}}>last 500 dynasty trades</strong> — across every dynasty league they're in, not just this one.</li>
              <li>The <span style={{color:'var(--accent-gold-text)',fontWeight:800}}>★ Relevant</span> tab inside that modal filters those trades to the players and picks in your current Trade Finder results.</li>
            </ul>
          </div>
        </div>
      )}

      <PFKFooter/>
    </div>
  );
}

// ============================================================================
// PICK PROJECTOR — Projects each team's 2027 1st-round pick slot based on
// roster strength under that league's exact scoring. Standalone tool at
// /pick-projector. One admin page supports it:
//   /admin/rookie-board-2027  — 3 tabs: Rookie Board · Projections · Value Overrides
//
// Algorithm assumes reverse-standings draft order (lowest roster value =
// projected 1.01). The tool is a "talent floor" projection, NOT a literal
// standings forecast. Confidence is gap-based: clean gaps yield High,
// clusters yield Medium/Low ranges so users see honest uncertainty.
//
// Phase 5: legacy ADP engine path deleted. PP now reads player_projections
// directly and feeds applyLeagueScoring per player per league. The
// multiplier chain (SF QB curve, TE curve, format mult, TEP mult, PTD mult)
// is gone — league scoring drives values natively.
// ============================================================================

// ----- Pure helpers (testable; exposed on window for Playwright) -----

// Phase 3a — hardcoded check for the two scoring families dynasty users
// actually care about as unmodeled. Replaces an earlier severity-based
// classifier that surfaced too much developer-log noise (st_td, blk_kick,
// unrecognized 1-coefficient keys, etc.) — users only care about
// 100+ yard bonuses and PPFD. Everything else stays inside the engine's
// warnings array (still emitted for future audit) but is silently dropped
// from the user-facing UI.
const PP_THRESHOLD_YARDAGE_KEYS = [
  'bonus_pass_yd_300', 'bonus_pass_yd_400',
  'bonus_rush_yd_100', 'bonus_rush_yd_200',
  'bonus_rec_yd_100',  'bonus_rec_yd_200',
];
// Sleeper exposes per-first-down bonuses as `rush_fd` and `rec_fd` in
// scoring_settings (no `bonus_` prefix — confirmed by inspecting real
// PPFD leagues and the legacy PP code that detects PPFD format).
const PP_PPFD_KEYS = ['rush_fd', 'rec_fd'];
const ppLeagueHasYardageBonuses = (scoringSettings) =>
  PP_THRESHOLD_YARDAGE_KEYS.some(k => (parseFloat(scoringSettings?.[k]) || 0) > 0);
const ppLeagueHasPpfd = (scoringSettings) =>
  PP_PPFD_KEYS.some(k => (parseFloat(scoringSettings?.[k]) || 0) > 0);

// Phase 1 Session 4 transparency badge: classify a rostered player who has no
// projection row. team === '' (FA in Sleeper) → 'FA'; otherwise the player is
// on a depth chart but FFA's sources skipped them (deep depth or injury).
// Returns null when no badge should render (loading / no signal / in-projections).
// Pure — exported on window.PFK_PP_UNMATCHED_REASON for tests.
const ppUnmatchedReason = (inProjections, team) => {
  if(inProjections == null) return null;        // loading
  if(inProjections === true) return null;       // has projection → no badge
  const t = (team == null ? '' : String(team)).trim();
  return t === '' ? 'FA' : 'Deep Depth or Injury';
};

// Derive PFK comboMultiplier settings ({format, tep, passTd}) from a Sleeper
// league. Reuses the same field paths fcFormatForLeague / TF use.
const ppLeagueSettings = (lg) => {
  const rp = Array.isArray(lg?.roster_positions) ? lg.roster_positions : [];
  const ss = lg?.scoring_settings || {};
  return {
    format: rp.includes('SUPER_FLEX') ? 'Superflex' : '1QB',
    tep: parseFloat(ss.bonus_rec_te ?? 0) || 0,
    passTd: parseFloat(ss.pass_td ?? 5) || 5,
  };
};

// PIECE 4 — bye-week-based bench contribution math.
//
// Replaces the PIECE 2 top-2-cap percentage weights with a model that
// directly counts the games a bench player would actually play:
//
//   starter_contribution = (season_projection / 17) × starter_games
//   bench_contribution   = (season_projection / 17) × byes_covered
//
// where `starter_games` is the fantasy-regular-season weeks minus any bye
// the starter takes that falls within the regular season; and
// `byes_covered` is 1 if this bench player is assigned to cover a starter
// bye at their position, otherwise 0.
//
// Coverage assignment: per-position greedy on bench projection desc. Each
// bench player gets matched to the highest-projection uncovered starter
// at their position whose bye doesn't conflict with the bench player's
// own bye. A bench player can cover at most one starter; bench players
// without an assignment contribute 0. This penalizes:
//   - Hoarding (extra bench past starter count = no contribution)
//   - Bye-conflict draft mistakes (bench whose bye matches their starter's)
//   - Bench at positions the league doesn't field starters for (K/DEF
//     today — they have no starter slots, so no coverage targets)
//
// NFL bye weeks: hardcoded constant below. Public data, 32 teams × 1 bye
// week each, updated annually each summer once the NFL releases the
// schedule. JAX/JAC canonicalization is needed because Sleeper has
// historically returned both abbreviations (see
// supabase/scripts/dedupe_underdog_sleeper_map_jax_jac.sql for the prior
// fix on a different surface).
//
// Fantasy regular-season weeks: read per-league from
// league.settings.playoff_week_start (defaults to 15 → 14-week regular
// season). This makes the math league-aware: a 13-week league sees
// shorter starter contributions than a 15-week league. Within any one
// league, relative team rankings stay correct.
//
// 2026 NFL bye weeks, sourced from SI's 2026 schedule release on 2026-05-17.
// 32 teams accounted for. Byes span weeks 5-14; no byes scheduled in week 12.
// Update annually each May once the next year's schedule is announced.
const PFK_NFL_BYE_WEEKS_2026 = {
  ARI: 14, ATL: 11, BAL: 13, BUF: 7,  CAR: 5,  CHI: 10,
  CIN: 6,  CLE: 11, DAL: 14, DEN: 10, DET: 6,  GB:  11,
  HOU: 8,  IND: 13, JAX: 7,  KC:  5,  LAC: 7,  LAR: 11,
  LV:  13, MIA: 6,  MIN: 6,  NE:  11, NO:  8,  NYG: 8,
  NYJ: 13, PHI: 10, PIT: 9,  SEA: 11, SF:  8,  TB:  10,
  TEN: 9,  WAS: 7,
};

// Sleeper occasionally returns "JAC" instead of "JAX" for the Jaguars.
// Normalize so the bye lookup hits.
const PFK_NFL_TEAM_CANONICAL = { JAC: 'JAX' };
const ppCanonicalTeam = (team) => {
  if(team == null) return null;
  const t = String(team).toUpperCase();
  return PFK_NFL_TEAM_CANONICAL[t] || t;
};
const ppLookupBye = (team, byeWeeks) => {
  // Resolution order:
  //   1. Explicit byeWeeks param — direct-function-call test seam
  //   2. window.PFK_NFL_BYE_WEEKS_OVERRIDE — integration-test seam set
  //      via page.addInitScript before the page renders
  //   3. PFK_NFL_BYE_WEEKS_2026 — production default
  const table = byeWeeks
    || (typeof window !== 'undefined' && window.PFK_NFL_BYE_WEEKS_OVERRIDE)
    || PFK_NFL_BYE_WEEKS_2026;
  const canonical = ppCanonicalTeam(team);
  if(!canonical) return null;
  const v = table[canonical];
  return (typeof v === 'number' && v > 0) ? v : null;
};



// ppApplyLineupAndBench — runs flex-aware starter selection on a team's
// players, then applies PIECE 4 bye-week contribution math.
//
// Per starter:
//   adjValue = (baseValue / 17) × starterGames
//   where starterGames = (playoff_week_start - 1) - (bye in reg season ? 1 : 0)
//
// Per bench player at QB/RB/WR/TE — multi-bye coverage:
//   - bench sorted by baseValue desc within position
//   - each bench player claims ALL uncovered starter byes at their position
//     that don't conflict with their own bye
//   - byes claimed by a higher-projection bench are removed from the pool
//     before lower-projection bench iterates
//   - adjValue = (baseValue / 17) × (number of starters covered)
//   - uncovered (no claimable byes) → adjValue = 0
//
// K/DEF bench: no starter slots in PP_SLOT_ELIGIBILITY, so no coverage
// targets exist → all K/DEF bench contribute 0.
//
// FA / unknown-team players: no bye known → can't cover anyone →
// contribute 0 as bench. As starters, they still get the full
// starterGames credit (we just can't deduct a bye since we don't know it).
//
// Inputs:
//   playerLines       array of {pos, baseValue, sleeperId, team, ...}
//   rosterPositions   league.roster_positions array
//   league            league object — reads league.settings.playoff_week_start
//                     for the regular-season length (defaults to 15 → 14 weeks)
//   byeWeeks          OPTIONAL — override PFK_NFL_BYE_WEEKS_2026. Used by tests.
//
// Returns: { playerLines: stamped, lineup }
//   Stamped fields per player:
//     isStarter, lineupSlot, posDepthRank        (from lineup fill)
//     bye, byeInRegSeason                        (NFL bye lookup)
//     starterGames                               (starters only — N games played)
//     coversStarterId/Name/Bye                   (bench that covered, for display)
//     adjValue                                   (contribution to team total)
const PP_SEASON_GAMES = 17;
const PP_DEFAULT_PLAYOFF_WEEK_START = 15;  // → 14-week reg season
const ppApplyLineupAndBench = (playerLines, rosterPositions, league, byeWeeks) => {
  if(!Array.isArray(playerLines)) return { playerLines: [], lineup: [] };
  // 1. Lineup fill (unchanged).
  const { lineup } = ppFillStartingLineup(playerLines, rosterPositions);
  const idToSlot = new Map();
  lineup.forEach(row => {
    if(row && row.player && row.player.sleeperId){
      idToSlot.set(row.player.sleeperId, row.slotKey);
    }
  });
  // 2. Position depth rank within team (informational; drives "WR4" labels).
  const groups = { QB: [], RB: [], WR: [], TE: [] };
  playerLines.forEach((p, i) => {
    if(p && groups[p.pos]) groups[p.pos].push({ p, i });
  });
  const depthByIndex = {};
  Object.keys(groups).forEach(pos => {
    const sorted = groups[pos].slice().sort((a, b) => (b.p.baseValue || 0) - (a.p.baseValue || 0));
    sorted.forEach((m, idx) => { depthByIndex[m.i] = idx + 1; });
  });
  // 3. Read per-league regular-season length. Falls back to default if missing.
  const pws = parseInt(league?.settings?.playoff_week_start, 10);
  const regSeasonWeeks = (pws > 0 ? pws - 1 : PP_DEFAULT_PLAYOFF_WEEK_START - 1);
  // 4. Stamp every player with bye + isStarter + posDepthRank, plus pre-set
  //    adjValue=0 / starterGames=null. Subsequent passes overwrite for
  //    starters and for bench players that get assigned coverage.
  const stamped = playerLines.map((p, i) => {
    if(!p) return p;
    const slot = (p.sleeperId && idToSlot.get(p.sleeperId)) || null;
    const bye = ppLookupBye(p.team, byeWeeks);
    const byeInRegSeason = bye != null && bye <= regSeasonWeeks;
    return {
      ...p,
      isStarter:      !!slot,
      lineupSlot:     slot,
      posDepthRank:   depthByIndex[i] ?? null,
      bye,
      byeInRegSeason,
      starterGames:   null,
      // Array of starters this bench player covers — empty for non-bench
      // and for bench players who couldn't cover any. Each entry:
      //   { sleeperId, name, bye }
      coversStarters: [],
      adjValue:       0,
    };
  });
  // 5. Starter contributions. starterGames = regSeasonWeeks minus 1 if the
  //    starter has a bye in the regular season. baseValue × games / 17.
  stamped.forEach(p => {
    if(!p || !p.isStarter) return;
    const games = regSeasonWeeks - (p.byeInRegSeason ? 1 : 0);
    p.starterGames = games;
    p.adjValue = (p.baseValue || 0) * games / PP_SEASON_GAMES;
  });
  // 6. Bench bye coverage — per-position greedy on bench projection desc.
  //    A bench player covers the highest-projection uncovered starter at
  //    their position whose REGULAR-SEASON bye doesn't match the bench's
  //    own bye. Starters without a regular-season bye don't need coverage
  //    (their bye is in fantasy playoffs, not reg season).
  ['QB','RB','WR','TE'].forEach(pos => {
    const startersAtPos = stamped
      .filter(p => p && p.isStarter && p.pos === pos && p.byeInRegSeason)
      .sort((a, b) => (b.baseValue || 0) - (a.baseValue || 0));
    const benchAtPos = stamped
      .filter(p => p && !p.isStarter && p.pos === pos)
      .sort((a, b) => (b.baseValue || 0) - (a.baseValue || 0));
    // Pool of uncovered starter byes — depleted as bench players claim.
    const covered = new Set();
    benchAtPos.forEach(b => {
      if(b.bye == null) return;  // unknown team → can't cover
      // Claim every uncovered starter whose bye doesn't conflict with
      // this bench player's own bye. Higher-projection bench iterates
      // first, so it gets first pick of multi-bye coverage.
      const targets = startersAtPos.filter(s =>
        !covered.has(s.sleeperId) && b.bye !== s.bye
      );
      if(targets.length === 0) return;
      targets.forEach(t => covered.add(t.sleeperId));
      b.coversStarters = targets.map(t => ({
        sleeperId: t.sleeperId,
        name:      t.name || `Player #${t.sleeperId}`,
        bye:       t.bye,
      }));
      b.adjValue = (b.baseValue || 0) / PP_SEASON_GAMES * targets.length;
    });
  });
  // Re-bind lineup.player references to the stamped objects. ppFillStartingLineup
  // returned references to the ORIGINAL (unstamped) playerLines entries, so
  // downstream consumers (display) wouldn't see bye/adjValue/isStarter on
  // lineup.player otherwise.
  const stampedById = new Map();
  stamped.forEach(p => { if(p && p.sleeperId) stampedById.set(p.sleeperId, p); });
  const reboundLineup = lineup.map(row => {
    if(!row || !row.player || !row.player.sleeperId) return row;
    const swapped = stampedById.get(row.player.sleeperId);
    return swapped ? { ...row, player: swapped } : row;
  });
  return { playerLines: stamped, lineup: reboundLineup };
};


// Fuzzy-match an Underdog player to a Sleeper player_id.
// players = the Sleeper /v1/players/nfl object keyed by player_id.
// Returns { sleeper_player_id, confidence: 'auto'|'needs_review' }.
// 'auto' iff name+position match uniquely (team breaks ties when needed).
const ppFuzzyMatch = (udPlayer, players) => {
  const udNameNorm = normDraftName(udPlayer.name);
  const udPos = (udPlayer.position || '').toUpperCase();
  const udTeam = (udPlayer.team || '').toUpperCase();
  if(!udNameNorm || !players) return { sleeper_player_id: null, confidence: 'needs_review' };
  const posMatches = [];
  for(const pid in players){
    const p = players[pid];
    if(!p) continue;
    const sleeperName = p.full_name || `${p.first_name||''} ${p.last_name||''}`.trim();
    if(!sleeperName) continue;
    if(normDraftName(sleeperName) !== udNameNorm) continue;
    const sleeperPos = (p.position || '').toUpperCase();
    const sleeperTeam = (p.team || '').toUpperCase();
    if(sleeperPos === udPos) posMatches.push({ pid, sleeperTeam });
  }
  if(posMatches.length === 0) return { sleeper_player_id: null, confidence: 'needs_review' };
  if(posMatches.length === 1) return { sleeper_player_id: posMatches[0].pid, confidence: 'auto' };
  const teamHit = posMatches.find(m => m.sleeperTeam && m.sleeperTeam === udTeam);
  if(teamHit) return { sleeper_player_id: teamHit.pid, confidence: 'auto' };
  return { sleeper_player_id: null, confidence: 'needs_review' };
};

// Starter-lineup eligibility per Sleeper roster_positions slot string.
// Strict slots have a 1-element list. Slot strings not in this table are
// non-starters (BN/TAXI/IR) or positions we don't price (K/DEF/IDP*) — both
// skipped from the lineup view.
const PP_SLOT_ELIGIBILITY = {
  QB: ['QB'], RB: ['RB'], WR: ['WR'], TE: ['TE'],
  FLEX:       ['RB','WR','TE'],
  WRRB_FLEX:  ['RB','WR'],
  RB_WR_FLEX: ['RB','WR'],
  REC_FLEX:   ['WR','TE'],
  SUPER_FLEX: ['QB','RB','WR','TE'],
  SF:         ['QB','RB','WR','TE'],
};

// Human-readable label per slot. Keep keys aligned with PP_SLOT_ELIGIBILITY.
const PP_SLOT_LABEL = {
  QB: 'QB', RB: 'RB', WR: 'WR', TE: 'TE',
  FLEX:       'FLEX',
  WRRB_FLEX:  'WR/RB',
  RB_WR_FLEX: 'WR/RB',
  REC_FLEX:   'WR/TE',
  SUPER_FLEX: 'SF',
  SF:         'SF',
};

// Greedy starter fill. Given a team's playerLines (post-bench-weights, each
// carrying both raw and weighted values) and the league's roster_positions
// array, returns:
//   - lineup: one entry per recognized starter slot, in roster_positions order
//   - bench:  the players left over, in the original playerLines order
//
// Fill order is strict-slots-before-flex so an RB1 lands in the RB slot
// rather than getting absorbed by FLEX. Within each group (strict vs flex)
// we preserve original roster order. Players are picked desc by raw value
// (rawAdjValue, fallback to adjValue) so the most valuable eligible player
// claims each slot.
//
// Players with no recognized pos (kicker, defense, unmatched ADP) are not
// candidates for any slot — they stay in the bench list at zero value.
const ppFillStartingLineup = (playerLines, rosterPositions) => {
  const pls = Array.isArray(playerLines) ? playerLines : [];
  const rps = Array.isArray(rosterPositions) ? rosterPositions : [];
  // 1. Parse roster_positions → recognized slots, preserving order.
  const orderedSlots = [];
  rps.forEach((slotKey, originalIdx) => {
    const elig = PP_SLOT_ELIGIBILITY[slotKey];
    if(!elig) return; // BN/TAXI/IR/K/DEF/IDP* — silently skip
    orderedSlots.push({
      originalIdx, slotKey, elig,
      strict: elig.length === 1,
      displayLabel: PP_SLOT_LABEL[slotKey] || slotKey,
    });
  });
  // 2. Pool: indexed players with valid pos, sorted desc by raw value.
  const pool = pls
    .map((p, idx) => ({ p, idx }))
    .filter(x => x.p && x.p.pos && PP_SLOT_ELIGIBILITY[x.p.pos])
    .sort((a, b) => {
      const av = (a.p.rawAdjValue ?? a.p.adjValue) || 0;
      const bv = (b.p.rawAdjValue ?? b.p.adjValue) || 0;
      return bv - av;
    });
  // 3. Fill in strict-first priority. Ties broken by roster_positions order.
  const fillOrder = orderedSlots
    .map((slot, i) => ({ slot, prio: slot.strict ? 0 : 1, tiebreak: i }))
    .sort((a, b) => a.prio - b.prio || a.tiebreak - b.tiebreak);
  const usedIdxs = new Set();
  const slotPlayer = {};
  fillOrder.forEach(({ slot }) => {
    for(const cand of pool){
      if(usedIdxs.has(cand.idx)) continue;
      if(slot.elig.includes(cand.p.pos)){
        usedIdxs.add(cand.idx);
        slotPlayer[slot.originalIdx] = cand.p;
        return;
      }
    }
    slotPlayer[slot.originalIdx] = null;
  });
  // 4. Lineup output preserves roster_positions order.
  const lineup = orderedSlots.map(s => ({
    slotKey:      s.slotKey,
    displayLabel: s.displayLabel,
    player:       slotPlayer[s.originalIdx],
  }));
  // 5. Bench: everyone not assigned to a starter slot.
  const bench = pls.filter((_, i) => !usedIdxs.has(i));
  return { lineup, bench };
};

// Per-league relative thresholds, anchored to total Proj-Max-PF spread.
// MERGE: any consecutive gap ≤ 4% of spread folds the teams on either
//        side into a shared "1.0X-1.0Y" range card. Inside a merged
//        group the order is uncertain — that's why they're grouped.
// DAYLIGHT: any consecutive gap > 8% of spread reads as a clear tier
//           boundary. Gaps in the (4%, 8%] zone don't trigger a merge
//           but don't count as "clean" either — they're "close-zone".
//
// Confidence on each group (single-team or merged) looks at the two
// boundaries OUTSIDE the group. Edge groups (containing the bottom or
// top pick of the league) have no neighbor on one side; that missing
// side counts as "clean" alongside daylight, so a 1.01 with daylight
// to 1.02 is still High.
//
//   HIGH:   both boundaries are daylight or edge
//   MEDIUM: one boundary is daylight/edge, the other is close-zone
//   LOW:    both boundaries are close-zone, or the group spans the
//           entire league with size > 1 (fully-clustered / fully-tied
//           — no information to distinguish positions)
const PP_CONF_MERGE_PCT    = 0.04; // gap ≤ this → merge teams into one group
const PP_CONF_DAYLIGHT_PCT = 0.08; // gap >  this → clear daylight (clean boundary)

// slotDisplayRange (added 2026-05-18) is the team's pick range as the user
// SEES it on the card — extending outward through any gap that hasn't
// reached the daylight threshold (>8% of spread). This is wider than the
// merge group (≤4%): a 4–8% close-zone gap breaks the merge group (driving
// confidence labeling internals) but still keeps the slot inside the
// visually-displayed range, because there's no real tier boundary yet.
// Returns [lo, hi] (1-indexed) or null when lo === hi (single slot).
const ppComputeDisplayRange = (sortedTeams, idx, daylightAbs) => {
  let lo = idx;
  while(lo > 0 && sortedTeams[lo].totalValue - sortedTeams[lo-1].totalValue <= daylightAbs) lo--;
  let hi = idx;
  while(hi < sortedTeams.length - 1 && sortedTeams[hi+1].totalValue - sortedTeams[hi].totalValue <= daylightAbs) hi++;
  return (lo === hi) ? null : [lo + 1, hi + 1];
};

const ppComputeProjections = (teams, _legacyArg) => {
  if(!Array.isArray(teams) || teams.length === 0) return [];
  const sorted = teams.map(t => ({...t})).sort((a, b) => a.totalValue - b.totalValue);
  const n = sorted.length;
  // Single-team league — no neighbors, no ambiguity. Lock to High.
  if(n === 1){
    return [{
      ...sorted[0],
      rank: 1, slot: 1, slotRange: null, slotDisplayRange: null, confidence: 'High',
      gapAbove: null, gapBelow: null,
    }];
  }
  const minPf = sorted[0].totalValue;
  const maxPf = sorted[n - 1].totalValue;
  const spread = maxPf - minPf;
  // Fully-tied league — every team identical, every gap = 0. Render as
  // one giant LOW range so the UI honestly says "we can't differentiate."
  if(spread <= 0){
    return sorted.map((team, idx) => ({
      ...team,
      rank: idx + 1,
      slot: null,
      slotRange: [1, n],
      slotDisplayRange: [1, n],
      confidence: 'Low',
      gapAbove: idx > 0     ? 0 : null,
      gapBelow: idx < n - 1 ? 0 : null,
    }));
  }
  const mergeAbs    = spread * PP_CONF_MERGE_PCT;
  const daylightAbs = spread * PP_CONF_DAYLIGHT_PCT;
  // Walk consecutive teams, building groups. A new group starts whenever
  // the gap to the previous team exceeds the merge threshold.
  const groups = [[0]];
  for(let i = 1; i < n; i++){
    const gap = sorted[i].totalValue - sorted[i-1].totalValue;
    if(gap <= mergeAbs) groups[groups.length - 1].push(i);
    else groups.push([i]);
  }
  // Classify each group's two external boundaries.
  const classify = (gap) =>
    gap === null         ? 'edge'
    : gap >  daylightAbs ? 'daylight'
    : /* otherwise */      'close';
  const out = [];
  groups.forEach(group => {
    const firstIdx = group[0];
    const lastIdx  = group[group.length - 1];
    const lowGap   = firstIdx > 0     ? sorted[firstIdx].totalValue - sorted[firstIdx - 1].totalValue : null;
    const highGap  = lastIdx  < n - 1 ? sorted[lastIdx + 1].totalValue - sorted[lastIdx].totalValue   : null;
    const lowClass  = classify(lowGap);
    const highClass = classify(highGap);
    const lowClean  = lowClass  === 'daylight' || lowClass  === 'edge';
    const highClean = highClass === 'daylight' || highClass === 'edge';
    let confidence;
    if(lowClass === 'edge' && highClass === 'edge' && group.length > 1){
      // Group spans the entire league (no external neighbors on either
      // side) and contains multiple teams — fully clustered. LOW.
      confidence = 'Low';
    } else if(lowClean && highClean){
      confidence = 'High';
    } else if(lowClean || highClean){
      confidence = 'Medium';
    } else {
      confidence = 'Low';
    }
    const isSingleton = group.length === 1;
    const groupLo = group[0] + 1;
    const groupHi = group[group.length - 1] + 1;
    group.forEach(idx => {
      const team = sorted[idx];
      out.push({
        ...team,
        rank: idx + 1,
        slot: isSingleton ? (idx + 1) : null,
        slotRange: isSingleton ? null : [groupLo, groupHi],
        // slotDisplayRange walks gaps ≤ daylight threshold (8%) — wider
        // than the merge group's ≤ 4%. Computed per-team rather than
        // per-group because the walk's reach depends on the team's
        // position in the sorted array (close-zone gaps may extend
        // beyond the merge group's own boundaries).
        slotDisplayRange: ppComputeDisplayRange(sorted, idx, daylightAbs),
        confidence,
        gapAbove: idx > 0     ? sorted[idx].totalValue - sorted[idx - 1].totalValue : null,
        gapBelow: idx < n - 1 ? sorted[idx + 1].totalValue - sorted[idx].totalValue : null,
      });
    });
  });
  return out;
};

// ---- Slot/tier probability model ------------------------------------------
//
// Each pair of adjacent teams (by ascending Proj Max PF) has a "swap rate" —
// the chance the two teams finish the season in reversed order from the PF
// prediction. Tighter gaps → higher swap rate. The bands map gap-as-%-of-
// league-spread to a swap probability:
//
//   gap < 1% of spread     → 45%   (basically tied, easy flip)
//   gap 1-3%               → 30%
//   gap 3-6%               → 18%
//   gap 6-10%              → 10%
//   gap 10-15%             → 5%
//   gap 15%+               → 2%    (clear separation, hard flip)
//
// To turn these into full per-team slot distributions, we walk pairs left-
// to-right then right-to-left (two passes). Each pair-pass applies the
// transformation:
//
//   probs[i]   = (1 - P) × probs[i]   + P × probs[i+1]
//   probs[i+1] =      P × probs[i_old] + (1 - P) × probs[i+1]
//
// where probs is the team's distribution over slots and P is the pair's
// swap rate. The LR+RL combination lets a team drift more than one slot
// from base when neighbors are close — captures the multi-team shuffle
// the spec describes.
const PP_SWAP_BANDS = [
  { maxPct: 0.01, prob: 0.45 },
  { maxPct: 0.03, prob: 0.30 },
  { maxPct: 0.06, prob: 0.18 },
  { maxPct: 0.10, prob: 0.10 },
  { maxPct: 0.15, prob: 0.05 },
  { maxPct: Infinity, prob: 0.02 },
];

const ppGapToSwapRate = (gapPct) => {
  for(const band of PP_SWAP_BANDS){
    if(gapPct <= band.maxPct) return band.prob;
  }
  return 0.02;
};

// ---- Seeded PRNG + normal sampler for Monte Carlo slot probabilities -----
//
// mulberry32 is a tiny 32-bit-state PRNG with good statistical properties
// for the volume of sampling we do here (a few thousand draws per league
// per page load). FNV-1a hash → 32-bit unsigned seed.
const ppHashSeed = (str) => {
  let h = 2166136261 >>> 0;
  const s = String(str || '');
  for(let i = 0; i < s.length; i++){
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
};
const ppMulberry32 = (seed) => {
  let s = (seed >>> 0) || 1;
  return () => {
    s = (s + 0x6D2B79F5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t = t + Math.imul(t ^ (t >>> 7), t | 61) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};
// Box-Muller transform: two uniform draws → one standard-normal sample.
// Guards against log(0) by clamping u1 away from 0.
const ppNormalSample = (rng) => {
  const u1 = Math.max(rng(), 1e-12);
  const u2 = rng();
  return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
};
// UTC-day bucket string. Used as part of the deterministic seed so that
// every refresh within the same UTC day produces identical output, and
// the projector naturally re-randomizes once a day as new data lands.
const ppCurrentDailyBucket = () => {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,'0')}-${String(d.getUTCDate()).padStart(2,'0')}`;
};
// Per-league daily seed. Pass an explicit `bucket` for testing — otherwise
// the current UTC day is used.
const ppDailySeed = (leagueId, bucket) => ppHashSeed(`${leagueId || ''}|${bucket || ppCurrentDailyBucket()}`);

// PP_MC_DEFAULTS — knobs governing the Monte Carlo. PP_MC_SIGMA_FRAC is the
// per-team noise stdev as a fraction of league spread (12% by default —
// rebumped from 3.5% on 2026-05-19 because the tighter value was still
// pinning 7.4%-gap teams at 92% confidence at slot 1.01, which doesn't
// match realistic season-long PF noise. 12% maps a 7.4% gap to ~55-70%
// at the top slot, and still locks down teams with daylight separation).
// PP_MC_TRIALS = number of simulated season-reorder draws per league.
// At 12 teams × 2000 trials, each league runs in single-digit ms.
const PP_MC_SIGMA_FRAC_DEFAULT = 0.12;
const PP_MC_TRIALS_DEFAULT = 2000;

// Returns probs[teamIdx][slotIdx] = probability that team teamIdx (base
// slot teamIdx, sorted ascending by totalValue) ends the season at slotIdx.
//
// Algorithm (Monte Carlo, replaces the prior LR+RL chain 2026-05-18):
//   For each of `trials` simulated seasons, sample per-team noise
//   ε ~ Normal(0, sigma) where sigma = sigmaFrac × leagueSpread. Realized
//   PF = projection μ + ε. Sort teams ascending by realized PF: worst PF
//   gets slot 1 (1.01), best gets slot N. Tally each team's resulting
//   slot; divide by trials.
//
// Why this beats LR+RL: LR+RL treated each adjacent pair-swap as an
// independent event and decayed mass geometrically per hop. That
// undercounted realistic multi-slot moves through tight clusters where
// many teams are near-tied. Monte Carlo handles those naturally — if four
// teams have near-identical projected PF, all 24 orderings of those four
// occur with roughly equal frequency, just like real-world noise would
// produce.
//
// opts: { sigmaFrac, trials, seed } — all optional. Pass `seed` for
// deterministic output (used by tests). `rankedAsc` must already be sorted
// by ascending totalValue.
const ppComputeSlotProbabilities = (rankedAsc, opts) => {
  const n = rankedAsc.length;
  if(n === 0) return [];
  if(n === 1) return [[1]];
  const sigmaFrac = (opts && typeof opts.sigmaFrac === 'number') ? opts.sigmaFrac : PP_MC_SIGMA_FRAC_DEFAULT;
  const trials   = (opts && typeof opts.trials === 'number')    ? Math.max(1, Math.floor(opts.trials)) : PP_MC_TRIALS_DEFAULT;
  const seed     = (opts && typeof opts.seed === 'number')      ? opts.seed : ppDailySeed('default');
  const values = rankedAsc.map(t => t.totalValue || 0);
  const minPf = values[0];
  const maxPf = values[n-1];
  const spread = maxPf - minPf;
  // Fully-tied league — every team has identical projected PF. Without
  // noise, every team is uniformly likely to land at any slot.
  if(spread <= 0){
    const u = 1 / n;
    return rankedAsc.map(() => new Array(n).fill(u));
  }
  const sigma = spread * sigmaFrac;
  const rng = ppMulberry32(seed);
  // tallies[teamIdx][slotIdx] — incremented when teamIdx finishes at slotIdx.
  const tallies = rankedAsc.map(() => new Array(n).fill(0));
  // Per-trial scratch arrays — reused across trials to keep allocations low.
  const realized = new Array(n);
  const order = new Array(n);
  for(let t = 0; t < trials; t++){
    for(let i = 0; i < n; i++){
      realized[i] = values[i] + ppNormalSample(rng) * sigma;
      order[i] = i;
    }
    // Sort indices ascending by realized PF — worst → slot 0, best → slot n-1.
    order.sort((a, b) => realized[a] - realized[b]);
    for(let slotIdx = 0; slotIdx < n; slotIdx++){
      tallies[order[slotIdx]][slotIdx]++;
    }
  }
  // Divide tallies by trial count to get probabilities.
  return tallies.map(row => row.map(c => c / trials));
};

// Tier boundaries scale with league size. For 12 teams: EARLY=1-4, MID=5-8,
// LATE=9-12. For non-12-team leagues, splits into rough thirds via ceil(N/3)
// and ceil(2N/3) so the buckets stay balanced.
const ppTierBuckets = (n) => ({
  earlyEnd: Math.ceil(n / 3),       // last 0-indexed slot in EARLY = earlyEnd-1
  midEnd:   Math.ceil(2 * n / 3),   // last 0-indexed slot in MID   = midEnd-1
});

// Returns { early, mid, late } as whole-number percentages summing to 100.
// Rounding errors are carried into the largest tier so totals stay exact.
const ppTierProbabilities = (slotProbs) => {
  const n = slotProbs.length;
  if(n === 0) return { early: 0, mid: 0, late: 0 };
  const { earlyEnd, midEnd } = ppTierBuckets(n);
  let early = 0, mid = 0, late = 0;
  for(let i = 0; i < n; i++){
    if(i < earlyEnd) early += slotProbs[i];
    else if(i < midEnd) mid += slotProbs[i];
    else late += slotProbs[i];
  }
  let e = Math.round(early * 100);
  let m = Math.round(mid * 100);
  let l = Math.round(late * 100);
  const diff = 100 - (e + m + l);
  if(diff !== 0){
    if(e >= m && e >= l) e += diff;
    else if(m >= l) m += diff;
    else l += diff;
  }
  return { early: e, mid: m, late: l };
};

// Returns the slots to render on the "MOST LIKELY SLOTS" line of a pick
// card. Rules:
//   1. Take the team's displayed pick range — slotRange (when given,
//      typically the slotDisplayRange that walks close-zone gaps) or
//      [slot, slot] for a singleton.
//   2. Pad the range outward to a minimum of MIN_SLOTS slots,
//      preferring to extend UPWARD (toward worse picks) first, then
//      downward, clamping at the league edges. This guarantees a
//      meaningful row even when a team's MC distribution is sharply
//      concentrated on one slot — the user still sees adjacent
//      possibilities at their actual computed probability.
//   3. Return every slot in the padded range in ASCENDING slot order
//      (low → high), with each slot's whole-percent probability.
//   4. If neither slot nor slotRange is provided (legacy 1-arg calls),
//      return the team's full distribution in ascending order.
// The previous "if top ≥ 80% collapse to one slot" rule was removed
// 2026-05-18 per the spec — the user wants three+ slots always shown.
const PP_MIN_LIKELY_SLOTS = 3;
const ppTopLikelySlots = (slotProbs, slot, slotRange) => {
  if(!Array.isArray(slotProbs) || slotProbs.length === 0) return [];
  const all = slotProbs.map((p, i) => ({ slot: i + 1, pct: Math.round(p * 100) }));
  const n = all.length;
  // Decide where the range starts.
  let lo, hi;
  if(slotRange && Array.isArray(slotRange) && slotRange.length === 2){
    lo = slotRange[0];
    hi = slotRange[1];
  } else if(slot != null){
    lo = hi = slot;
  } else {
    // Legacy 1-arg behavior — return every slot in ascending order.
    return all;
  }
  // Pad outward until we have at least MIN_SLOTS slots (or until we hit
  // both league edges). Prefer expanding upward (toward higher slots /
  // worse picks) first, then downward — this keeps the row visually
  // anchored at the team's projected position when one direction is
  // available, only walking the other way as a fallback.
  while((hi - lo + 1) < PP_MIN_LIKELY_SLOTS){
    if(hi < n)      hi++;
    else if(lo > 1) lo--;
    else break;
  }
  const out = [];
  for(let s = lo; s <= hi; s++){
    const idx = s - 1;
    if(idx >= 0 && idx < n) out.push(all[idx]);
  }
  return out;
};

// Expose pure helpers on window for Playwright tests
if(typeof window !== 'undefined'){
  window.PFK_PP_COMPUTE_PROJECTIONS = ppComputeProjections;
  window.PFK_PP_COMPUTE_SLOT_PROBABILITIES = ppComputeSlotProbabilities;
  window.PFK_PP_TIER_PROBABILITIES = ppTierProbabilities;
  window.PFK_PP_TOP_LIKELY_SLOTS = ppTopLikelySlots;
  window.PFK_PP_GAP_TO_SWAP_RATE = ppGapToSwapRate;
  window.PFK_PP_DAILY_SEED = ppDailySeed;
  window.PFK_PP_HASH_SEED = ppHashSeed;
  window.PFK_PP_CURRENT_DAILY_BUCKET = ppCurrentDailyBucket;
  window.PFK_PP_FUZZY_MATCH = ppFuzzyMatch;
  window.PFK_PP_LEAGUE_SETTINGS = ppLeagueSettings;
  window.PFK_PP_APPLY_LINEUP_AND_BENCH = ppApplyLineupAndBench;
  window.PFK_PP_FILL_STARTING_LINEUP = ppFillStartingLineup;
  window.PFK_PP_SLOT_LABEL = PP_SLOT_LABEL;
  window.PFK_APPLY_PPC_AND_PPFD = applyPpcAndPpfd;
  window.PFK_PP_UNMATCHED_REASON = ppUnmatchedReason;
  window.PFK_PP_LEAGUE_HAS_YARDAGE_BONUSES = ppLeagueHasYardageBonuses;
  window.PFK_PP_LEAGUE_HAS_PPFD = ppLeagueHasPpfd;
  // PIECE 4 bye-week math
  window.PFK_NFL_BYE_WEEKS_2026 = PFK_NFL_BYE_WEEKS_2026;
  window.PFK_PP_LOOKUP_BYE = ppLookupBye;
  window.PFK_PP_CANONICAL_TEAM = ppCanonicalTeam;
  // PFK_PP_FETCH_PROJECTIONS lives in its own export block below — it's
  // defined further down the file and would be undefined here under
  // Babel-standalone's const-to-var transform.
}

// ----- Data layer (Supabase + Sleeper) -----

const ppFetchAdp = async () => {
  if(!sb) return [];
  const { data, error } = await sb.from('underdog_adp').select('*').order('adp', {ascending:true});
  if(error){ console.warn('underdog_adp fetch failed', error); return []; }
  return data || [];
};
const ppFetchMap = async () => {
  if(!sb) return [];
  const { data, error } = await sb.from('underdog_sleeper_map').select('*');
  if(error){ console.warn('underdog_sleeper_map fetch failed', error); return []; }
  return data || [];
};
// PP settings table defaults.
//   decay_k / gap_threshold_pct — legacy Phase 5 holdovers, currently
//     unread by the projections engine; kept so existing rows don't get
//     dropped on save.
//   pp_mc_sigma_frac — per-team noise stdev as a fraction of league spread
//     for the Monte Carlo slot probabilities. Higher = flatter tight
//     clusters. Default 0.035 (3.5%).
//   pp_mc_trials — number of Monte Carlo simulated season-reorders per
//     league. Higher = less MC noise, slower compute. Default 2000.
// Upsert via SQL on `pick_projector_settings` to tune; an admin UI page
// for these knobs is a separate (non-shipped-yet) followup.
const PP_DEFAULT_SETTINGS = {
  decay_k: 50,
  gap_threshold_pct: 40,
  pp_mc_sigma_frac: PP_MC_SIGMA_FRAC_DEFAULT,
  pp_mc_trials: PP_MC_TRIALS_DEFAULT,
};
// Keys we accept from the pick_projector_settings table. Anything else is ignored.
const PP_KNOWN_SETTING_KEYS = Object.keys(PP_DEFAULT_SETTINGS);
const ppFetchSettings = async () => {
  if(!sb) return { ...PP_DEFAULT_SETTINGS };
  const { data, error } = await sb.from('pick_projector_settings').select('*');
  if(error || !data) return { ...PP_DEFAULT_SETTINGS };
  const out = { ...PP_DEFAULT_SETTINGS };
  data.forEach(row => {
    const v = parseFloat(row.value);
    if(!isFinite(v)) return;
    if(PP_KNOWN_SETTING_KEYS.includes(row.key)) out[row.key] = v;
  });
  return out;
};
const ppSaveSetting = async (key, value) => {
  if(!sb) return { error: 'Supabase not loaded' };
  const { error } = await sb.from('pick_projector_settings').upsert({
    key, value, updated_at: new Date().toISOString(),
  }, { onConflict: 'key' });
  return { error };
};
// Fetches every traded pick for the given (league, year), across ALL
// rounds — not just 1st. The leagues-list breakdown needs per-round data
// to surface "3 × 1sts · 2 × 2nds · 1 × 3rd" correctly. Per-round filtering
// (e.g., for ownerByOriginalRoster's 1st-round chart highlight) happens at
// the use site instead.
const ppFetchTradedPicks = async (leagueId, year) => {
  try {
    const tps = await fetch(`https://api.sleeper.app/v1/league/${leagueId}/traded_picks`, {cache:'no-store'}).then(r=>r.ok?r.json():[]);
    return (tps||[]).filter(t => String(t.season) === String(year));
  } catch(e){ return []; }
};

// Fetch the FULL latest (season, period) player_projections group as an
// array of records. Two downstream consumers derive different views from
// this single fetch:
//   - Session 4 transparency badge: a Set<sleeper_player_id> for O(1)
//     membership testing
//   - Phase 3 PP integration: a {sleeper_player_id → record} map keyed
//     for per-player scoring lookup
// Originally added Session 4 as `ppFetchProjectedSleeperIds` returning a
// Set; Phase 3 Commit 2 widened it to the full record shape so both views
// derive from the same network round-trip. Fails silently to []; empty
// array collapses to "no badges, no projection-based scoring" — the same
// no-opinion UI as a still-loading state.
const ppFetchProjections = async () => {
  if(!sb) return [];
  try {
    const { data: latestArr, error: latestErr } = await sb
      .from('player_projections')
      .select('season, period, generated_at')
      .order('generated_at', { ascending: false })
      .limit(1);
    if(latestErr || !latestArr || !latestArr.length) return [];
    const latest = latestArr[0];
    const { data: rows, error: rowsErr } = await sb
      .from('player_projections')
      .select('sleeper_player_id, ffa_name, position, team, projected_stats')
      .eq('season', latest.season)
      .eq('period', latest.period);
    if(rowsErr || !Array.isArray(rows)) return [];
    return rows;
  } catch(e){ return []; }
};

// Export ppFetchProjections here (not in the main export block above)
// because Babel-standalone const-to-var hoisting captures it as undefined
// when assigned before its declaration.
if(typeof window !== 'undefined'){
  window.PFK_PP_FETCH_PROJECTIONS = ppFetchProjections;
}

// Sleeper avatar URL (full-size). Avatar field can be missing for users
// who never uploaded one — caller should fall back to a default badge.
const ppAvatarUrl = (avatar) => avatar ? `https://sleepercdn.com/avatars/${avatar}` : null;

// Confidence badge color — High=success (clean gap), Medium=warning, Low=muted.
const ppConfidenceColor = (c) => c === 'High' ? 'var(--success)' : c === 'Medium' ? 'var(--warning)' : 'var(--text-secondary)';

// Reusable AdminGate — wraps any admin tool with the EVAN_EMAIL-only sign-in
// gate. `Inner` is the actual page component; we pass a `signOut` prop so the
// page can render a logout chip.
function AdminGate({ children }){
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authMsg, setAuthMsg] = useState('');
  useEffect(() => {
    if(!sb){ setLoading(false); return; }
    sb.auth.getSession().then(({data}) => { setSession(data?.session || null); setLoading(false); });
    const { data: sub } = sb.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub?.subscription?.unsubscribe?.();
  }, []);
  const doAuth = async () => {
    setAuthMsg('');
    if(!sb){ setAuthMsg('Supabase not loaded'); return; }
    const { error } = await sb.auth.signInWithPassword({ email, password });
    if(error) setAuthMsg(error.message);
  };
  const doLogout = async () => { if(sb) await sb.auth.signOut(); };
  if(loading) return <div style={{padding:40,textAlign:'center',color:'var(--text-secondary)'}}>Loading…</div>;
  if(!session) return <DevAuthGate mode="signin" doAuth={doAuth} authEmail={email} setAuthEmail={setEmail} authPassword={password} setAuthPassword={setPassword} authMsg={authMsg} doLogout={doLogout}/>;
  if(session?.user?.email !== EVAN_EMAIL) return <DevAuthGate mode="denied" doLogout={doLogout} signedInAs={session?.user?.email}/>;
  return children;
}

// ============================================================================
// PickProjectorApp — the main user-facing tool at /pick-projector
// ============================================================================
function PickProjectorApp(){
  const [sleeperUser] = useSleeperUser();
  const [leagues, setLeagues] = useState(null);
  // '' = overview (all leagues at a glance); otherwise drilled-in to that league.
  const [leagueId, setLeagueId] = useState('');
  // Scroll position on the leagues list, captured the moment the user
  // drills into a league. Restored when they tap "All leagues" so a user
  // with 55+ leagues doesn't have to re-scroll past the cards above the
  // one they were just inspecting. Stays 0 until the first drill-in.
  const leaguesListScrollRef = useRef(0);
  // {leagueId: {league, users, rosters, tradedPicks, error?}} — populated as
  // each league's parallel fetches complete.
  const [allLeaguesData, setAllLeaguesData] = useState({});
  const [loadingProgress, setLoadingProgress] = useState({done: 0, total: 0});
  const [settings, setSettings] = useState(PP_DEFAULT_SETTINGS);
  const [expanded, setExpanded] = useState(null); // roster_id — legacy expand state (unused after tabs redesign, kept until next cleanup)
  // Per-team-card Graph/Team tab selection. Map keyed by roster_id, values
  // 'graph' | 'team' | null. NO default tab — every card opens with neither
  // tab selected and a "Select a tab to view" placeholder in the content
  // area. The map is wiped on pickLeague so each league reopens fresh.
  // setTeamTab is a TOGGLE: tapping the active tab deselects (back to
  // placeholder); tapping the inactive tab switches.
  const [teamTabs, setTeamTabs] = useState({});
  const getTeamTab = (rid) => teamTabs[rid] || null;
  const setTeamTab = (rid, tab) => setTeamTabs(prev => ({ ...prev, [rid]: prev[rid] === tab ? null : tab }));
  // Leagues-list search filter. Persists across page navigations within the
  // same browser session via sessionStorage so users don't lose their filter
  // when drilling into a card and backing out. Initialized lazily to avoid a
  // sessionStorage read on every re-render. Wrapped in try/catch because
  // Safari private mode throws on storage access.
  const [leagueSearch, setLeagueSearch] = useState(() => {
    try { return (typeof window !== 'undefined' && window.sessionStorage && window.sessionStorage.getItem('pp-league-search')) || ''; }
    catch(_) { return ''; }
  });
  useEffect(() => {
    try { if(typeof window !== 'undefined' && window.sessionStorage){ window.sessionStorage.setItem('pp-league-search', leagueSearch); } }
    catch(_) { /* private mode — silently no-op */ }
  }, [leagueSearch]);
  // Published 2027 rookie board (top 12 attach to picks 1.01–1.12). Empty
  // array on first paint and any fetch failure — empty == placeholders, never
  // a crash. Admin edits it at /admin/rookie-board-2027.
  const [publishedRookies, setPublishedRookies] = useState([]);
  // Separate "fetch resolved at least once" flag so we can hide the dashed
  // "?" placeholder during the initial load window. Without this, the first
  // paint renders empty publishedRookies → 12 ugly "?" tiles flash on screen
  // until the rookie-board fetch resolves a few hundred ms later. Once true,
  // either the real rookie OR the placeholder is appropriate to show.
  const [rookieBoardLoaded, setRookieBoardLoaded] = useState(false);
  // Supabase auth session — for the "Using your 2027 ranks" lookup. Pick
  // Projector identifies users by Sleeper linkage, but the saved 2027
  // ranks are keyed by PFK auth user_id, so we need both. Anonymous
  // visitors keep seeing PFK official ranks on cards (session === null →
  // userRanks2027 stays null → effectiveRookies falls through to
  // publishedRookies). 2027 Rookie Ranks page is where users sign up.
  const [session, setSession] = useState(null);
  // null = not yet loaded / no session / no saved row. Truthy {ranks: [...]} = user has saved.
  const [userRanks2027, setUserRanks2027] = useState(null);
  useEffect(() => {
    if(!sb) return;
    sb.auth.getSession().then(({data}) => setSession(data?.session || null));
    const { data: sub } = sb.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);
  useEffect(() => {
    const uid = session?.user?.id;
    if(!uid || !sb){ setUserRanks2027(null); return; }
    let alive = true;
    sb.from('user_rookie_ranks_2027')
      .select('ranks')
      .eq('user_id', uid)
      .maybeSingle()
      .then(({ data, error }) => {
        if(!alive) return;
        if(error || !data || !Array.isArray(data.ranks) || data.ranks.length === 0){
          setUserRanks2027(null);
        } else {
          setUserRanks2027({ ranks: data.ranks });
        }
      });
    return () => { alive = false; };
  }, [session?.user?.id]);
  // Effective rookie pool used by every consumer below. When the user has
  // a saved 2027 ranking, render in their order (silent-filter orphans
  // whose ids no longer exist in publishedRookies) and append any newly-
  // added PFK rookies at the bottom. When they don't, fall through to
  // PFK's published order verbatim. Memoized so the card renders only
  // recompute when the underlying inputs change.
  const effectiveRookies = useMemo(() => {
    if(!Array.isArray(publishedRookies) || publishedRookies.length === 0) return publishedRookies;
    if(!userRanks2027 || !Array.isArray(userRanks2027.ranks) || userRanks2027.ranks.length === 0) return publishedRookies;
    const byId = Object.fromEntries(publishedRookies.map(r => [r.id, r]));
    const userOrdered = userRanks2027.ranks.map(id => byId[id]).filter(Boolean);
    const newAdditions = publishedRookies.filter(r => !userRanks2027.ranks.includes(r.id));
    return [...userOrdered, ...newAdditions];
  }, [publishedRookies, userRanks2027]);
  const usingCustomRanks = !!userRanks2027;
  // Full latest player_projections records — fetched once on mount.
  // `null` while loading (badges hide; players render with adjValue=0 +
  // Sleeper-sourced name/pos until data lands).
  // Two views are derived below via useMemo:
  //   projectedSet     — Set<sleeper_player_id> for badge membership
  //   projectionsById  — {sleeper_player_id → record} for scoring lookup
  const [projections, setProjections] = useState(null);
  // Full Sleeper /v1/players/nfl response. Provides player.team (FA vs
  // Deep-Depth badge) + name/position fallback for unmatched players.
  // ~10MB on first fetch, cached by sleeperPlayers() for the session.
  const [sleeperPlayersData, setSleeperPlayersData] = useState({});

  // Pick year/round — locked to 2027 1st per spec
  const PICK_YEAR = 2027;
  const PICK_ROUND = 1;

  // 1) Fetch settings once on mount (don't depend on league).
  useEffect(() => {
    ppFetchSettings().then(setSettings);
  }, []);

  // 1b) Fetch the published 2027 rookie board once on mount. Independent of
  // league selection so the data is ready when the board renders. Flip the
  // loaded flag in both success + failure paths so the placeholder UX still
  // gets to show if the network call fails (rather than hanging on a blank).
  useEffect(() => {
    fetchPublishedRookies2027()
      .then(arr => { setPublishedRookies(Array.isArray(arr) ? arr : []); })
      .catch(() => { /* leave [] */ })
      .finally(() => { setRookieBoardLoaded(true); });
  }, []);

  // 1c) Fetch player_projections records + Sleeper player DB in parallel on
  // mount. Together they feed the Session 4 transparency badge and (after
  // Phase 3 Commit 3) the projections-engine scoring path. Neither blocks
  // the projector — until they land, no badges render and projections-engine
  // scoring (when enabled) sees an empty record map.
  useEffect(() => {
    ppFetchProjections().then(setProjections);
    sleeperPlayers().then(setSleeperPlayersData).catch(() => {});
  }, []);

  // 2) Fetch dynasty leagues for the linked Sleeper user.
  useEffect(() => {
    if(!sleeperUser?.user_id){ setLeagues(null); setAllLeaguesData({}); setLeagueId(''); return; }
    let cancelled = false;
    (async () => {
      try {
        const yr = String(new Date().getFullYear());
        const lgs = await fetch(`https://api.sleeper.app/v1/user/${sleeperUser.user_id}/leagues/nfl/${yr}`, {cache:'no-store'}).then(r=>r.json());
        if(cancelled) return;
        pfkRecordUserLeagues(sleeperUser, lgs); // Sharp Tracker pool ingestion (Phase 0a)
        const dynasty = (lgs||[]).filter(l => l?.settings?.type === 2).sort((a,b) => (a.name||'').localeCompare(b.name||''));
        setLeagues(dynasty);
      } catch(e){ setLeagues([]); }
    })();
    return () => { cancelled = true; };
  }, [sleeperUser?.user_id]);

  // 3) Fetch ALL leagues' data in parallel (so the overview can render every
  //    league at once). Per-league fetches take ~150-300ms each; running them
  //    in parallel keeps total wall time at the slowest single league rather
  //    than the sum. Each league lands in state independently so the overview
  //    fills in progressively.
  useEffect(() => {
    if(!leagues || leagues.length === 0){
      setAllLeaguesData({});
      setLoadingProgress({done: 0, total: 0});
      return;
    }
    let cancelled = false;
    setLoadingProgress({done: 0, total: leagues.length});
    setAllLeaguesData({});
    leagues.forEach(async lg => {
      try {
        const [data, tps] = await Promise.all([
          sleeperLeague(lg.league_id),
          ppFetchTradedPicks(lg.league_id, PICK_YEAR),
        ]);
        if(cancelled) return;
        setAllLeaguesData(prev => ({...prev, [lg.league_id]: {...data, tradedPicks: tps, _lg: lg}}));
        setLoadingProgress(prev => ({...prev, done: prev.done + 1}));
      } catch(e){
        if(cancelled) return;
        setAllLeaguesData(prev => ({...prev, [lg.league_id]: {error: e.message || 'Fetch failed', _lg: lg}}));
        setLoadingProgress(prev => ({...prev, done: prev.done + 1}));
      }
    });
    return () => { cancelled = true; };
  }, [leagues]);

  const pickLeague = (id) => {
    if(typeof window !== 'undefined'){
      if(id){
        // Entering a specific league. Stash the leagues-list scroll
        // position so we can restore it when the user backs out — Evan has
        // ~55 leagues, scrolling all the way back to a deep card is a
        // chore otherwise. Then scroll to top so the user lands at the
        // 1.01 row inside the league, not mid-page.
        leaguesListScrollRef.current = window.scrollY || window.pageYOffset || 0;
        window.scrollTo({top: 0, behavior: 'smooth'});
      }
    }
    setLeagueId(id || '');
    setExpanded(null);
    setTeamTabs({});
  };

  // Restore the leagues-list scroll position when the user backs out of
  // an inside-league view. Runs in useEffect (after the leagues-list DOM
  // has committed) so the scroll target exists. Skipped on initial mount
  // (ref starts at 0). requestAnimationFrame waits one paint frame so
  // the scrollY:0 from the smooth-scroll-to-top doesn't clobber us.
  useEffect(() => {
    if(leagueId) return;
    if(typeof window === 'undefined') return;
    const y = leaguesListScrollRef.current;
    if(y <= 0) return;
    leaguesListScrollRef.current = 0;
    requestAnimationFrame(() => { window.scrollTo(0, y); });
  }, [leagueId]);

  // Derive the sleeper_player_id Set used by the Session 4 unmatched badge
  // from the full projections array. `null` while loading (badges hide);
  // empty Set on failure (same no-opinion UI).
  const projectedSet = useMemo(() => {
    if(projections == null) return null;
    const s = new Set();
    projections.forEach(p => { if(p?.sleeper_player_id) s.add(String(p.sleeper_player_id)); });
    return s;
  }, [projections]);

  // Derive the by-id record map used by Phase 3's projections-engine scoring
  // path (wired in Commit 3). Empty object until projections land — engine
  // path treats missing records as a 0-value stub. Keys are stringified so
  // they always match the Sleeper roster `players` array's string-or-number
  // entries via `String(pid)`.
  const projectionsById = useMemo(() => {
    const out = {};
    (projections || []).forEach(p => {
      if(p?.sleeper_player_id) out[String(p.sleeper_player_id)] = p;
    });
    return out;
  }, [projections]);

  // Universal positional rank (1 = highest projected player at that position).
  // Used by the position chip across Starting Lineup + Bench rows. Computed
  // once per page load against the full projections array, using a fixed
  // ranking scoring profile (1 PPR + 4 PT-PTDs + standard) — same numbers
  // every league sees, matching the rank you'd see on most fantasy sites.
  // Per-league relative ranks would be more accurate but would require a
  // separate computation per league view; the user spec called this "once
  // per page load."
  const PP_RANK_SCORING = {
    rec: 1, rec_yd: 0.1, rec_td: 6,
    rush_yd: 0.1, rush_td: 6,
    pass_yd: 0.04, pass_td: 4,
    pass_int: -2, fum_lost: -2,
  };
  const posRankBySleeperId = useMemo(() => {
    const byPos = { QB: [], RB: [], WR: [], TE: [], K: [], DEF: [] };
    (projections || []).forEach(p => {
      if(!p || !p.position || !byPos[p.position]) return;
      const scoring = ppApplyLeagueScoring(p.projected_stats || {}, PP_RANK_SCORING, { position: p.position });
      byPos[p.position].push({ id: String(p.sleeper_player_id), points: scoring.points || 0 });
    });
    const out = {};
    Object.keys(byPos).forEach(pos => {
      byPos[pos].sort((a, b) => b.points - a.points);
      byPos[pos].forEach((entry, idx) => { out[entry.id] = idx + 1; });
    });
    return out;
  }, [projections]);

  // Compute projections for EVERY loaded league, in one pass. Keyed by
  // league_id; each value is the same shape the old single-league `projection`
  // memo returned ({ ranked, leagueAvgPos, ownerByOriginalRoster, league }).
  const allProjections = useMemo(() => {
    const out = {};
    Object.entries(allLeaguesData).forEach(([lgId, data]) => {
      if(!data || data.error || !data.league) return;
      const { league, users, rosters, tradedPicks } = data;
      const rosterPositions = Array.isArray(league?.roster_positions) ? league.roster_positions : [];
      const usersById = {}; (users||[]).forEach(u => { usersById[u.user_id] = u; });
      // Collect ppApplyLeagueScoring warnings across the league for the
      // unmodeled-scoring surface. Same scoring_settings → identical
      // warnings per player, so a Set dedupes naturally.
      const leagueWarnings = new Set();
      const teams = (rosters||[]).map(r => {
        const u = r.owner_id ? usersById[r.owner_id] : null;
        const rawPlayerLines = (r.players || []).map(pid => {
          const sid = String(pid);
          // Session 4 transparency badge inputs — inProjections is null
          // while projections data is loading (badge stays hidden); team
          // comes from Sleeper player DB.
          const inProjections = projectedSet ? projectedSet.has(sid) : null;
          const sleeperTeam = sleeperPlayersData[sid]?.team || '';
          const proj = projectionsById[sid];
          if(!proj || !proj.projected_stats){
            // No projection — fall back to Sleeper for name + position so
            // the player still renders in the lineup with adjValue=0 and
            // the transparency badge surfaces next to the name.
            return {
              sleeperId: sid,
              name: sleeperPlayersData[sid]?.full_name || null,
              pos: sleeperPlayersData[sid]?.position || null,
              baseValue: 0,
              adjValue: 0,
              team: sleeperTeam,
              inProjections,
              posRank: null,
            };
          }
          const scoring = ppApplyLeagueScoring(proj.projected_stats, league.scoring_settings, { position: proj.position });
          (scoring.warnings || []).forEach(w => leagueWarnings.add(w));
          const baseValue = scoring.points;
          return {
            sleeperId: sid,
            name: sleeperPlayersData[sid]?.full_name || null,
            pos: proj.position,
            baseValue,
            adjValue: baseValue,
            team: sleeperTeam,
            inProjections,
            posRank: posRankBySleeperId[sid] || null,
          };
        });
        // Flex-aware starter selection + bye-week bench contribution math
        // (PIECE 4). Stamps each player with isStarter/lineupSlot/
        // posDepthRank/bye/byeInRegSeason and computes adjValue:
        //   starter: baseValue × starterGames / 17
        //   bench (covers): baseValue / 17
        //   bench (uncovered or K/DEF/unknown-team): 0
        // The league object is passed for league.settings.playoff_week_start.
        const { playerLines, lineup } = ppApplyLineupAndBench(rawPlayerLines, rosterPositions, league);
        const totalValue = playerLines.reduce((s, p) => s + (p.adjValue||0), 0);
        const posTotals = { QB:0, RB:0, WR:0, TE:0 };
        playerLines.forEach(p => { if(posTotals[p.pos] != null) posTotals[p.pos] += p.adjValue; });
        return {
          roster_id: r.roster_id,
          owner_id: r.owner_id,
          co_owners: r.co_owners || [],
          username: u?.username || null,
          display_name: u?.display_name || u?.username || `Roster ${r.roster_id}`,
          avatar: u?.avatar || null,
          teamName: u?.metadata?.team_name || u?.display_name || u?.username || `Roster ${r.roster_id}`,
          playerLines, posTotals, totalValue, lineup,
        };
      });
      const leagueAvgPos = { QB:0, RB:0, WR:0, TE:0 };
      if(teams.length){
        ['QB','RB','WR','TE'].forEach(pos => {
          leagueAvgPos[pos] = teams.reduce((s, t) => s + (t.posTotals[pos]||0), 0) / teams.length;
        });
      }
      // Per-team posAvgPPG/posRank were used by an earlier draft of the
      // Position Group Strength block. The redesigned block uses
      // t.posTotals[pos] + leagueAvgPos[pos] instead, so they're gone.
      // ownerByOriginalRoster maps an original 1st-round-pick holder to
      // its CURRENT owner. tradedPicks now contains all rounds for the
      // target year (the leagues-list pick breakdown needs them), so we
      // filter here to round 1 only — otherwise a 2nd-round trade for the
      // same roster would clobber the 1st-round mapping that drives the
      // inside-league chart highlight + the "Pick traded to" header copy.
      const ownerByOriginalRoster = {};
      teams.forEach(t => { ownerByOriginalRoster[t.roster_id] = t.roster_id; });
      (tradedPicks || []).forEach(tp => {
        if(tp.round !== PICK_ROUND) return;
        if(tp.roster_id != null && tp.owner_id != null) ownerByOriginalRoster[tp.roster_id] = tp.owner_id;
      });
      const ranked = ppComputeProjections(teams);
      // Stamp per-team slot + tier probability distributions onto the
      // ranked rows. Cheap to compute once per league here (12 teams × 12
      // slots = 144 floats × 12 teams in a 12-team league) and lets the
      // per-team card render its TIER PROBABILITY + MOST LIKELY SLOTS
      // sections without re-walking the league inside the card.
      // ppComputeSlotProbabilities expects a strictly ascending-by-PF
      // ordering — ppComputeProjections already returns that, but the
      // rank/slot tagging means the underlying team objects need to be
      // re-sorted ascending again for the math step.
      const sortedForProb = [...ranked].sort((a, b) => a.totalValue - b.totalValue);
      // Monte Carlo: seed deterministically from (league_id × UTC day) so
      // refreshes within the same day are stable, but the projector
      // naturally re-randomizes daily as new data lands. sigma/trials come
      // from pick_projector_settings (defaults if unset).
      const slotProbsByIdx = ppComputeSlotProbabilities(sortedForProb, {
        sigmaFrac: settings?.pp_mc_sigma_frac ?? PP_MC_SIGMA_FRAC_DEFAULT,
        trials:    settings?.pp_mc_trials      ?? PP_MC_TRIALS_DEFAULT,
        seed:      ppDailySeed(lgId),
      });
      const probsByRosterId = {};
      sortedForProb.forEach((team, idx) => { probsByRosterId[team.roster_id] = slotProbsByIdx[idx]; });
      ranked.forEach(team => {
        const probs = probsByRosterId[team.roster_id] || [];
        team.slotProbs = probs;
        team.tierProbs = ppTierProbabilities(probs);
        // Pass team.slot + team.slotDisplayRange so the row mirrors the
        // card's BIG pick label. slotDisplayRange (added 2026-05-18)
        // walks through close-zone gaps and only stops at daylight, so
        // the MOST LIKELY SLOTS row shows every slot in the user-visible
        // range — wider than the merge group's slotRange, which still
        // drives confidence labeling separately.
        team.topLikelySlots = ppTopLikelySlots(probs, team.slot, team.slotDisplayRange);
      });
      out[lgId] = { ranked, leagueAvgPos, ownerByOriginalRoster, league, tradedPicks: tradedPicks || [], warnings: [...leagueWarnings].sort() };
    });
    return out;
  }, [allLeaguesData, settings, projectedSet, sleeperPlayersData, projectionsById, posRankBySleeperId]);

  // The drilled-in projection (when leagueId is set).
  const projection = leagueId ? allProjections[leagueId] : null;

  // Per-league summary for the overview — one row per league showing MY
  // projected slot, my roster's rank, and who currently owns my 2027 1st.
  //
  // Sort key: best possible slot represented by the displayed label.
  //   - High confidence  → just the slot (e.g. "1.05" → 5)
  //   - Medium / Low     → the LOWER bound of the range (e.g. "1.04–1.06" → 4)
  // Tiebreaker: when two leagues share the same best-possible slot, the
  // confident High row sorts BEFORE the uncertain range — confident is a
  // strictly better expected outcome at that slot. This puts a confident 1.12
  // dead last (best=12), with any "1.11–1.12" range above it (best=11).
  const mySummaries = useMemo(() => {
    if(!sleeperUser?.user_id || !leagues) return [];
    const uid = sleeperUser.user_id;
    const list = [];
    Object.entries(allProjections).forEach(([lgId, proj]) => {
      const lg = (leagues || []).find(l => l.league_id === lgId);
      if(!lg) return;
      const myRow = proj.ranked.find(t => t.owner_id === uid || (t.co_owners||[]).includes(uid));
      if(!myRow) return;
      const ownerRid = proj.ownerByOriginalRoster[myRow.roster_id];
      const ownerInfo = ownerRid != null ? proj.ranked.find(t => t.roster_id === ownerRid) : null;
      list.push({
        lg, lgId, myRow, ownerInfo,
        isTraded: ownerRid !== myRow.roster_id,
        totalTeams: proj.ranked.length,
        ranked: proj.ranked,
        ownerByOriginalRoster: proj.ownerByOriginalRoster,
        // tradedPicks + draftRounds power the bottom-left "2027 Picks by
        // Round" breakdown. tradedPicks is the raw Sleeper /traded_picks
        // response (covers every round, not just 1st). draftRounds falls
        // back to 5 when the league setting isn't surfaced (standard
        // dynasty default).
        tradedPicks: proj.tradedPicks || [],
        draftRounds: proj.league?.settings?.draft_rounds || 5,
      });
    });
    // Best-case slot for sorting the leagues list. Under per-league
    // confidence, a singleton can have any confidence (slot set,
    // slotRange null), and a merged group has slot null + slotRange set —
    // so the cardinal flag is whether the row is a merged group, not
    // its confidence label.
    const bestSlot = (row) => row.slotRange == null ? row.slot : row.slotRange[0];
    list.sort((a, b) => {
      const aBest = bestSlot(a.myRow);
      const bBest = bestSlot(b.myRow);
      if(aBest !== bBest) return aBest - bBest;
      // Same best-possible slot → singletons (specific slot) sort before
      // merged ranges. Under per-league confidence, "singleton vs range"
      // is independent of the HIGH/MEDIUM/LOW chip — gate on slotRange.
      const aIsRange = a.myRow.slotRange != null;
      const bIsRange = b.myRow.slotRange != null;
      if(!aIsRange && bIsRange) return -1;
      if(!bIsRange && aIsRange) return 1;
      // Same shape → fall back to underlying rank for stability.
      return a.myRow.rank - b.myRow.rank;
    });
    return list;
  }, [allProjections, leagues, sleeperUser?.user_id]);

  // Compact league-format chip text — used in overview cards + drilled-in header.
  const ppLeagueFormat = (lg) => {
    const ss = lg?.scoring_settings || {};
    const rp = lg?.roster_positions || [];
    const sf = rp.includes('SUPER_FLEX');
    const ppr = ss.rec === 1 ? '1.0 PPR' : ss.rec === 0.5 ? '0.5 PPR' : '0 PPR';
    const tep = parseFloat(ss.bonus_rec_te ?? 0) || 0;
    const ptd = ss.pass_td ?? 5;
    const teams = lg.total_rosters || 12;
    const tepStr = tep > 0 ? ` · ${tep} TEP` : '';
    return `${sf?'SF':'1QB'} · ${ppr}${tepStr} · ${ptd}pt PT · ${teams}-team`;
  };
  // Filtered view of mySummaries used by the leagues-list overview cards.
  // Substring-matches the search input (case-insensitive) against each
  // league's name AND its settings text — so "Play For Keeps" filters by
  // name, "SF" filters to Superflex leagues, "12-team" filters by size,
  // etc. Empty / whitespace-only search returns the full list. Lives as
  // a separate useMemo so re-typing in the search box doesn't recompute
  // the underlying summaries (which depend on projection data).
  const filteredSummaries = useMemo(() => {
    const q = (leagueSearch || '').trim().toLowerCase();
    if(q === '') return mySummaries;
    return mySummaries.filter(s => {
      const haystack = `${s.lg?.name || ''} ${ppLeagueFormat(s.lg)}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [mySummaries, leagueSearch]);


  // Singletons render as a plain string ("1.01"); merged groups render
  // as JSX with the en-dash de-emphasized (smaller font, muted color,
  // tighter margin) so it reads as a range separator rather than a
  // strong connector gluing the two pick numbers together. The dash
  // scales relative to its parent's font-size, so it looks right at
  // both the 32px leagues-list pick badge and the 28px per-team card
  // slot label.
  const ppSlotLabel = (row) => {
    // The displayed range walks through close-zone gaps and only stops
    // at daylight (>8% of spread) or league edges — this is the
    // pick-range the user actually sees on the card. Falls back to the
    // merge group (slotRange) for any legacy caller that hasn't been
    // updated to provide slotDisplayRange.
    const range = row.slotDisplayRange !== undefined ? row.slotDisplayRange : row.slotRange;
    if(range == null){
      // Singleton: row.slot is the team's own slot; if for some reason
      // the row only has a slotRange (merge group) and no slot field,
      // fall back to slotRange[0].
      const s = row.slot != null ? row.slot : (row.slotRange ? row.slotRange[0] : 1);
      return `1.${String(s).padStart(2,'0')}`;
    }
    const lo = String(range[0]).padStart(2,'0');
    const hi = String(range[1]).padStart(2,'0');
    // En-dash inherits the champagne accent color from the parent and
    // matches the surrounding numbers' weight, so the range reads as
    // one continuous gold unit (e.g. "1.04–1.06") instead of two
    // numbers separated by a muted gray hyphen. Slight inset margin so
    // the dash isn't crammed into the digits, and a tiny baseline
    // raise so it optically centers against the figure tops.
    return (
      <>
        1.{lo}
        <span style={{color:'inherit',fontWeight:'inherit',margin:'0 0.06em',verticalAlign:'0.06em'}}>–</span>
        1.{hi}
      </>
    );
  };

  // ---- Render ----
  const accentChip = (text, color) => (
    <span style={{display:'inline-flex',alignItems:'center',padding:'3px 8px',background:'var(--bg-pill)',border:`1px solid ${color}`,borderRadius:10,color,fontWeight:800,fontSize:10,letterSpacing:0.6,textTransform:'uppercase'}}>{text}</span>
  );

  // Shared Zone 2 renderers. Both the leagues-list summary card and the
  // inside-league per-team card display tier probability + most-likely-
  // slot info using identical pill + text styling so the two views feel
  // like one continuous product. The math (tierProbs, topLikelySlots)
  // is computed once in allProjections and stamped on every team row.

  // Three side-by-side tier pills (EARLY / MID / LATE) with the dominant
  // pill in champagne gold, non-dominant non-zero pills neutral, and
  // 0% pills heavily muted. flex:1 0 0 on each pill makes them share the
  // row equally so they line up cleanly on mobile (~106px each at 360px
  // viewport, plenty for "EARLY 100%" content).
  const renderTierPills = (tierProbs) => {
    const tp = tierProbs || { early: 0, mid: 0, late: 0 };
    const tiers = [
      { key: 'early', label: 'EARLY', pct: tp.early },
      { key: 'mid',   label: 'MID',   pct: tp.mid },
      { key: 'late',  label: 'LATE',  pct: tp.late },
    ];
    // Dominant tier = highest %, ties broken EARLY > MID > LATE by array order.
    const dominant = tiers.reduce((best, cur) => cur.pct > best.pct ? cur : best, tiers[0]);
    return (
      <div data-pp-tier-pills="1"
           data-pp-tier-dominant={dominant.pct > 0 ? dominant.key : 'none'}
           style={{display:'flex',gap:6,alignItems:'stretch'}}>
        {tiers.map(tier => {
          const isDominant = tier.key === dominant.key && tier.pct > 0;
          const isZero = tier.pct === 0;
          const bg = isDominant
            ? 'color-mix(in srgb, var(--accent-gold) 24%, var(--bg-elevated))'
            : 'var(--bg-elevated)';
          const color = isDominant
            ? 'var(--accent-gold-text)'
            : isZero
              ? 'var(--text-muted)'
              : 'var(--text-primary)';
          const border = isDominant
            ? '1px solid color-mix(in srgb, var(--accent-gold) 40%, transparent)'
            : '1px solid var(--border-hairline)';
          return (
            <span key={tier.key}
                  data-pp-tier={tier.key}
                  data-pp-tier-pct={tier.pct}
                  style={{display:'inline-flex',alignItems:'baseline',justifyContent:'center',flex:'1 1 0',minWidth:0,padding:'5px 8px',borderRadius:8,background:bg,color,border,fontWeight:isDominant?800:600,letterSpacing:0.3,fontVariantNumeric:'tabular-nums',opacity:isZero?0.45:1,whiteSpace:'nowrap',gap:5}}>
              <span style={{fontSize:10,letterSpacing:0.6,fontWeight:isDominant?800:700}}>{tier.label}</span>
              <span style={{fontSize:15,fontWeight:isDominant?900:700,lineHeight:1}}>{tier.pct}%</span>
            </span>
          );
        })}
      </div>
    );
  };

  // "MOST LIKELY SLOTS" line. Each entry uses the dot-separator format
  // "1.05 · 30%" and stays as one unit (whiteSpace:nowrap on the entry).
  // The row is a wrapping flex container — when the entries exceed one
  // line on a narrow viewport, they spill to a second line naturally
  // instead of horizontal-scrolling or ellipsis-truncating. Spacing
  // between entries comes from columnGap, not an explicit "·" separator
  // (the separator-on-a-new-line orphan problem disappears).
  const renderLikelySlots = (topLikelySlots) => {
    const slots = topLikelySlots || [];
    if(slots.length === 0) return null;
    const fmt = (s) => `1.${String(s).padStart(2,'0')}`;
    // Compute the dominant pct so the highest-probability slot can
    // render in champagne gold while the others sit in muted gray. If
    // multiple slots tie for the max (e.g. two near-tied teams in a
    // tight cluster), all winners get the gold treatment — visually
    // honest and lets the user see the tie at a glance.
    const maxPct = slots.reduce((m, s) => Math.max(m, s.pct), 0);
    return (
      <div data-pp-likely-slots="1"
           style={{display:'flex',flexWrap:'wrap',columnGap:16,rowGap:6,alignItems:'baseline',fontWeight:800,color:'var(--text-primary)',letterSpacing:0.3,fontVariantNumeric:'tabular-nums'}}>
        {slots.map((s, i) => {
          const isMax = s.pct === maxPct && maxPct > 0;
          return (
            <span key={s.slot}
                  data-pp-likely-slot-rank={i+1}
                  data-pp-likely-slot={s.slot}
                  data-pp-likely-slot-pct={s.pct}
                  data-pp-likely-slot-max={isMax ? '1' : '0'}
                  style={{display:'inline-flex',alignItems:'baseline',whiteSpace:'nowrap',gap:6}}>
              <span style={{fontSize:18,fontWeight:700,color:'var(--text-primary)'}}>{fmt(s.slot)}</span>
              <span style={{fontSize:22,fontWeight:900,color:isMax ? 'var(--accent-gold-text)' : 'var(--text-secondary)'}}>{s.pct}%</span>
            </span>
          );
        })}
      </div>
    );
  };

  // Full Zone 2 block — tier pills + most likely slots, each with their
  // muted uppercase label. Shared by both card types.
  const renderProbabilityZone = (tierProbs, topLikelySlots, opts) => {
    const o = opts || {};
    return (
      <div data-pp-probability-zone="1"
           style={{marginTop:o.marginTop != null ? o.marginTop : 6,paddingTop:o.paddingTop != null ? o.paddingTop : 6,borderTop:o.borderTop===false ? 'none' : '1px solid var(--border-hairline)'}}>
        <div style={{fontSize:9,color:'var(--text-muted)',fontWeight:700,letterSpacing:0.8,textTransform:'uppercase',marginBottom:3}}>Tier Probability</div>
        {renderTierPills(tierProbs)}
        {topLikelySlots && topLikelySlots.length > 0 && (
          <>
            <div style={{fontSize:10,color:'var(--text-muted)',fontWeight:800,letterSpacing:0.9,textTransform:'uppercase',marginTop:6,marginBottom:3}}>Most Likely Slots</div>
            {renderLikelySlots(topLikelySlots)}
          </>
        )}
      </div>
    );
  };

  // Phase 1 Session 4 transparency badge. Renders inline next to a player
  // name when that player has no row in player_projections. Compact chip
  // (smaller than accentChip) so it fits the tight lineup/bench rows
  // without restacking. Reuses --accent-violet (FA) and --warning (Deep
  // Depth or Injury) which already exist in css/styles.css.
  const ppUnmatchedBadge = (player) => {
    const reason = ppUnmatchedReason(player?.inProjections, player?.team);
    if(!reason) return null;
    const isFa = reason === 'FA';
    const color = isFa ? 'var(--accent-violet)' : 'var(--warning)';
    return (
      <span
        data-pp-unmatched-badge={isFa ? 'fa' : 'deep_depth'}
        data-pp-unmatched-reason={reason}
        title="Projection unavailable. Will auto-resolve when source data updates."
        style={{display:'inline-flex',alignItems:'center',marginLeft:6,padding:'1px 6px',background:'var(--bg-pill)',border:`1px solid ${color}`,borderRadius:8,color,fontWeight:800,fontSize:9,letterSpacing:0.5,textTransform:'uppercase',whiteSpace:'nowrap',verticalAlign:'middle'}}>
        {reason}
      </span>
    );
  };

  const UserRow = ({ rosterInfo, label }) => {
    if(!rosterInfo) return null;
    const av = ppAvatarUrl(rosterInfo.avatar);
    return (
      <div style={{display:'flex',alignItems:'center',gap:8}}>
        {label && <span style={{fontSize:11,color:'var(--text-muted)',fontWeight:700,letterSpacing:0.5}}>{label}</span>}
        {av ? (
          <img src={av} alt="" style={{width:22,height:22,borderRadius:'50%',objectFit:'cover',background:'var(--bg-pill)'}} onError={e=>e.target.style.display='none'}/>
        ) : (
          <div style={{width:22,height:22,borderRadius:'50%',background:'var(--bg-pill)',display:'flex',alignItems:'center',justifyContent:'center',color:'var(--text-secondary)',fontSize:10,fontWeight:800}}>
            {(rosterInfo.display_name||'?')[0]?.toUpperCase()}
          </div>
        )}
        <span style={{fontSize:13,color:'var(--text-primary)',fontWeight:700,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{rosterInfo.display_name}</span>
      </div>
    );
  };

  const renderBoard = () => {
    if(!projection) return null;
    const { ranked, leagueAvgPos, ownerByOriginalRoster } = projection;
    // Lineup classification was computed in the scoring loop; renderBoard
    // reads each team's t.lineup / t.playerLines stamps and doesn't need
    // league.roster_positions anymore.
    const teamByRosterId = {}; ranked.forEach(t => { teamByRosterId[t.roster_id] = t; });
    const uid = sleeperUser?.user_id;
    // Bar-height normalization for the league distribution chart. Bars
    // are linearly proportional across the league's actual PF range —
    // raw = (pf - leagueMin) / spread × 100 — with a 5% floor so the
    // very bottom bar doesn't visually disappear. The lower floor (was
    // 10%) lets the second-lowest team render at its actual percent
    // instead of also flooring, so two teams 40 pts apart in a 500-pt
    // spread read as "almost identical but perceptibly different"
    // instead of identical. Spread === 0 (single team or all-tied)
    // falls back to 100% so every bar reads as fully present.
    const leaguePfValues = ranked.map(r => r.totalValue || 0);
    const leagueMinPf = leaguePfValues.length ? Math.min(...leaguePfValues) : 0;
    const leagueMaxPf = leaguePfValues.length ? Math.max(...leaguePfValues) : 0;
    const leaguePfSpread = leagueMaxPf - leagueMinPf;
    const pmpBarPercent = (pf) => {
      if(leaguePfSpread <= 0) return 100;
      const raw = ((pf - leagueMinPf) / leaguePfSpread) * 100;
      return Math.max(5, Math.min(100, raw));
    };
    return (
      <div style={{display:'flex',flexDirection:'column',gap:10}}>
        {ranked.map((t, i) => {
          const slotLabel = ppSlotLabel(t);
          const ownerRid = ownerByOriginalRoster[t.roster_id];
          const ownerInfo = ownerRid != null ? teamByRosterId[ownerRid] : null;
          const isTraded = ownerRid !== t.roster_id;
          // Per-card tab selection (2026-05-20 tabs redesign). 'graph' by
          // default; switches to 'team' when user taps the Team tab. Tab
          // selection is the ONLY thing that toggles below-the-tab-bar
          // content now — there is no more "tap-to-expand" state.
          const activeTab = getTeamTab(t.roster_id);
          // Highlight the linked user's own roster so they spot it instantly
          // when drilling into a league.
          const isMe = uid && (t.owner_id === uid || (t.co_owners||[]).includes(uid));
          const cardBorder = isMe ? 'var(--accent-gold)' : (i===0 ? 'var(--accent-gold)' : 'var(--border)');
          const cardShadow = isMe ? '0 0 0 2px rgba(var(--accent-gold-rgb),0.45)' : (i===0 ? '0 0 0 1px rgba(var(--accent-gold-rgb),0.3)' : 'none');
          // Card-level totals (computed once per card so the PROJ MAX PF block
          // can render in the always-visible top row even while collapsed).
          const cardLines = t.playerLines || [];
          const cardStartersTotal = cardLines.filter(p => p && p.isStarter).reduce((s, p) => s + (p.adjValue || 0), 0);
          const cardBenchContribution = cardLines.filter(p => p && !p.isStarter).reduce((s, p) => s + (p.adjValue || 0), 0);
          const cardTeamTotal = cardStartersTotal + cardBenchContribution;
          // Small avatar component shared by the inline user pill (next to the
          // slot label) — matches UserRow's 22px avatar but is composed
          // inline so we control the gap to the slot label precisely.
          const ownerAv = ppAvatarUrl(t.avatar);
          return (
            <React.Fragment key={t.roster_id}>
            <div data-pp-row={t.roster_id}
                 data-pp-slot={slotLabel}
                 data-pp-confidence={t.confidence}
                 data-pp-is-me={isMe ? '1' : '0'}
                 style={{background:'var(--bg-card)',border:`1px solid ${cardBorder}`,borderRadius:12,padding:'8px 12px',boxShadow:cardShadow}}>
              {/* No onClick wrapper — the team card is no longer a single
                  tap target. The tab bar below Zone 5 is the only
                  interactive element on the card; the rest of the body
                  is informational only.

                  2026-05-20 compression pass — Zones 1→7 top to bottom:
                    1. Header row: league logo + name (L) / pick range (R)
                    2. Sleeper owner row: 26px avi + username (L) /
                       "Pick is yours" or "Traded" (R) — was a giant
                       centered band, now an inline sub-header
                    3. PROJ MAX PF + Rookie side-by-side (2-col flex)
                    4. Tier-probability pills (tighter font + height)
                    5. MOST LIKELY SLOTS inline single-line row
                    6. Tab bar (left-aligned, gap 24px, no default tab)
                    7. Tab content area (placeholder / chart / roster) */}

              {/* TOP 2-COLUMN BLOCK (2026-05-20 polish pass):
                    LEFT  ~50% — league row, sleeper row, ownership tag
                    RIGHT ~50% — pick range, rookie chip
                  Both columns vertically aligned at the top, related
                  info now lives in its own column instead of being
                  spread across rows. PMP block moves to its own full-
                  width section directly below. */}
              <div data-pp-team-card-top="1"
                   style={{display:'grid',gridTemplateColumns:'minmax(0,1fr) minmax(0,1fr)',columnGap:12,alignItems:'start'}}>

                {/* LEFT COLUMN — league row, sleeper row, ownership tag */}
                <div data-pp-team-card-left="1" style={{minWidth:0}}>
                  {/* Row 1: league logo + name */}
                  <div data-pp-card-league-row="1"
                       style={{display:'flex',alignItems:'center',gap:7,minWidth:0}}>
                    {projection?.league && (() => {
                      const lgAv = ppAvatarUrl(projection.league.avatar);
                      const lgName = projection.league.name || '';
                      const lgInit = (lgName[0] || '?').toUpperCase();
                      return (
                        <>
                          {lgAv ? (
                            <img src={lgAv} alt="" style={{width:22,height:22,borderRadius:6,objectFit:'cover',background:'var(--bg-pill)',border:'1px solid var(--border-hairline)',flexShrink:0}} onError={e=>e.target.style.display='none'}/>
                          ) : (
                            <div style={{width:22,height:22,borderRadius:6,background:'color-mix(in srgb, var(--accent-gold) 12%, var(--bg-elevated))',color:'var(--accent-gold-text)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:10,fontWeight:800,border:'1px solid var(--border-hairline)',flexShrink:0}}>{lgInit}</div>
                          )}
                          <span style={{fontSize:13,fontWeight:500,color:'var(--text-bright)',letterSpacing:0.1,lineHeight:1.2,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',minWidth:0}}>{lgName}</span>
                        </>
                      );
                    })()}
                  </div>

                  {/* Row 2: sleeper avi + username — 24px avatar, 6px gap */}
                  <div data-pp-card-user="1"
                       style={{display:'flex',alignItems:'center',gap:6,marginTop:6,minWidth:0}}>
                    {ownerAv ? (
                      <img src={ownerAv} alt="" style={{width:24,height:24,borderRadius:'50%',objectFit:'cover',background:'var(--bg-pill)',border:'1px solid var(--border-strong)',flexShrink:0}} onError={e=>e.target.style.display='none'}/>
                    ) : (
                      <div style={{width:24,height:24,borderRadius:'50%',background:'var(--bg-pill)',display:'flex',alignItems:'center',justifyContent:'center',color:'var(--text-secondary)',fontSize:11,fontWeight:900,border:'1px solid var(--border-strong)',flexShrink:0}}>
                        {(t.display_name || '?')[0]?.toUpperCase()}
                      </div>
                    )}
                    <span style={{flex:1,minWidth:0,fontSize:12,fontWeight:500,color:'var(--text-primary)',letterSpacing:0.2,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{t.display_name}</span>
                  </div>

                  {/* Row 3: ownership tag — two states (2026-05-20 rebuild).
                      The check is "is the signed-in user the current
                      owner of THIS team's natural 2027 1st-round pick?"
                        - Yes → "PICK IS YOURS" green
                        - No  → "PICK BELONGS TO @{owner1}[, @{owner2}…]"
                                muted. Owner usernames are deduplicated;
                                with the current data model each team has
                                exactly one natural 2027 1st so the list
                                is almost always one entry, but the code
                                renders any unique-owner set defensively
                                so multi-pick-range ownership (if ever
                                introduced) just works.
                      Signed-out viewers always fall to the muted branch
                      (uid is falsy, so pickIsMine is false). */}
                  {(() => {
                    const pickOwners = new Set();
                    if(ownerInfo){
                      const handle = ownerInfo.username || ownerInfo.display_name;
                      if(handle) pickOwners.add(handle);
                    }
                    const pickIsMine = !!(uid && ownerInfo && (
                      ownerInfo.owner_id === uid || (ownerInfo.co_owners || []).includes(uid)
                    ));
                    if(pickIsMine){
                      return (
                        <div data-pp-team-ownership="yours"
                             style={{marginTop:3,fontSize:9,fontWeight:700,letterSpacing:0.5,textTransform:'uppercase',whiteSpace:'nowrap',color:'var(--success)'}}>
                          Pick is yours
                        </div>
                      );
                    }
                    if(pickOwners.size === 0) return null;
                    const list = [...pickOwners];
                    return (
                      <div data-pp-team-ownership="other"
                           data-pp-pick-owner-count={list.length}
                           style={{marginTop:3,fontSize:9,fontWeight:700,letterSpacing:0.5,textTransform:'uppercase',color:'var(--text-muted)',lineHeight:1.3}}>
                        Pick belongs to {list.map((h, idx) => (
                          <React.Fragment key={h}>
                            {idx > 0 && ', '}
                            <span style={{color:'var(--text-secondary)',fontWeight:800}}>@{h}</span>
                          </React.Fragment>
                        ))}
                      </div>
                    );
                  })()}

                  {/* Row 4: PROJ MAX PF block — lives in the left column
                      below the ownership tag so the left stack reads as
                      one coherent identity-plus-projection block. Right
                      column (pick range + rookie chip) ends higher than
                      this; asymmetric heights are intentional. */}
                  <div data-pp-totals="1" data-pp-team-total-wrap="1"
                       style={{marginTop:8}}>
                    <div style={{fontSize:9,color:'var(--text-muted)',fontWeight:700,letterSpacing:0.5,textTransform:'uppercase',marginBottom:2}}>Proj Max PF</div>
                    <div data-pp-team-total style={{fontSize:22,fontWeight:600,color:'var(--accent-gold-text)',lineHeight:1,fontVariantNumeric:'tabular-nums'}}>{Math.round(cardTeamTotal).toLocaleString()}</div>
                    <div style={{fontSize:9,color:'var(--text-muted)',marginTop:3,fontVariantNumeric:'tabular-nums'}}>
                      <span data-pp-starters-total>Starters {Math.round(cardStartersTotal).toLocaleString()}</span>
                      <span style={{margin:'0 4px'}}>·</span>
                      <span data-pp-bench-contribution>Bench {Math.round(cardBenchContribution).toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                {/* RIGHT COLUMN — pick range + rookie chip. Right-aligned
                    so the column reads as a coherent block hugging the
                    card's right edge. */}
                <div data-pp-team-card-right="1"
                     style={{minWidth:0,display:'flex',flexDirection:'column',alignItems:'flex-end',gap:4}}>
                  {/* Row 1: pick range */}
                  <div data-pp-slot-label
                       style={{fontSize:20,fontWeight:600,color:'var(--accent-gold-text)',letterSpacing:0.4,fontFamily:"'Inter',sans-serif",lineHeight:1,fontVariantNumeric:'tabular-nums',whiteSpace:'nowrap'}}>
                    {slotLabel}
                  </div>

                  {/* Row 2: rookie chip vertically stacked to match the
                      leagues-list card — photo on top, name below,
                      position·school sub-line below that. All three
                      lines center-aligned within the chip; the chip
                      itself stays anchored to the right edge of this
                      column via the parent's alignItems:'flex-end'. */}
                  {i < 12 && (() => {
                    const rookie = effectiveRookies[i] || null;
                    if(!rookieBoardLoaded && !rookie) return null;
                    if(rookie){
                      return (
                        <div data-pp-rookie-row={i} data-pp-rookie-id={rookie.id || ''}
                             className="pp-card-rookie pp-card-rookie-tucked"
                             style={{display:'flex',flexDirection:'column',alignItems:'center',gap:3,textAlign:'center',minWidth:0,maxWidth:'100%'}}>
                          <div data-pp-rookie-avatar="1" style={{width:40,height:48,flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center'}}>
                            {rookie.photo_url
                              ? <img src={rookie.photo_url} alt="" loading="lazy"
                                     onError={e => {
                                       const img = e.target;
                                       const fallback = document.createElement('div');
                                       fallback.textContent = (rookie.name || '?')[0]?.toUpperCase() || '?';
                                       fallback.style.cssText = 'width:40px;height:48px;display:flex;align-items:center;justify-content:center;background:color-mix(in srgb, var(--accent-gold) 12%, var(--bg-elevated));color:var(--accent-gold-text);font-size:17px;font-weight:900;border-radius:6px;';
                                       img.replaceWith(fallback);
                                     }}
                                     style={{width:40,height:48,objectFit:'contain',display:'block'}}/>
                              : <div style={{width:40,height:48,display:'flex',alignItems:'center',justifyContent:'center',background:'color-mix(in srgb, var(--accent-gold) 12%, var(--bg-elevated))',color:'var(--accent-gold-text)',fontSize:17,fontWeight:900,borderRadius:6}}>
                                  {(rookie.name || '?')[0]?.toUpperCase() || '?'}
                                </div>}
                          </div>
                          <div data-pp-rookie-name="1" style={{fontSize:11,fontWeight:500,color:'var(--text-primary)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',lineHeight:1.2,maxWidth:'100%'}}>{rookie.name || 'Unnamed rookie'}</div>
                          <div style={{fontSize:9,color:'var(--text-muted)',fontWeight:500,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',lineHeight:1.2,maxWidth:'100%'}}>
                            <span data-pp-rookie-position="1">{rookie.position || '—'}</span>
                            {rookie.school ? <span> · <span data-pp-rookie-school="1">{rookie.school}</span></span> : null}
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div data-pp-rookie-row={i} data-pp-rookie-placeholder="1"
                           className="pp-card-rookie pp-card-rookie-tucked"
                           style={{display:'flex',flexDirection:'column',alignItems:'center',gap:3,opacity:0.6}}>
                        <div style={{width:40,height:48,borderRadius:6,background:'var(--bg-pill)',color:'var(--text-muted)',fontSize:17,fontWeight:900,display:'flex',alignItems:'center',justifyContent:'center',border:'1px dashed var(--border-hairline)'}}>?</div>
                        <div style={{fontSize:9,color:'var(--text-muted)',fontWeight:700,letterSpacing:0.4,textTransform:'uppercase'}}>Unranked</div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* PROJ MAX PF block moved 2026-05-20 into the left
                  column of the 2-col top block (sits below PICK IS
                  YOURS) so the left stack reads as one coherent
                  identity-plus-projection unit. Right column ends
                  higher; asymmetric is intentional. */}

              {/* Tier-probability pills lived here previously; moved to
                  the leagues-list card per Evan's 2026-05-20 split:
                  league cards surface the EARLY/MID/LATE distribution,
                  team cards surface the per-slot percentages only. */}

              {/* MOST LIKELY SLOTS inline row. Format:
                  "SLOTS · 1.01 67% · 1.02 31% · 1.03 2%" on a single
                  line (wraps to 2 lines only if needed). Dominant slot
                  pct lights up in champagne gold; others in muted text. */}
              {t.topLikelySlots && t.topLikelySlots.length > 0 && (() => {
                const slots = t.topLikelySlots;
                const maxPct = slots.reduce((m, s) => Math.max(m, s.pct), 0);
                const fmt = (s) => `1.${String(s).padStart(2,'0')}`;
                return (
                  <div data-pp-likely-slots="1"
                       style={{display:'flex',justifyContent:'center',alignItems:'baseline',flexWrap:'wrap',columnGap:8,rowGap:4,marginTop:10,marginBottom:6,fontVariantNumeric:'tabular-nums'}}>
                    <span style={{fontSize:9,color:'var(--text-muted)',fontWeight:700,letterSpacing:0.6,textTransform:'uppercase'}}>Slots</span>
                    {slots.map((s, idx) => {
                      const isMax = s.pct === maxPct && maxPct > 0;
                      return (
                        <React.Fragment key={'slot_'+s.slot}>
                          <span aria-hidden="true" style={{fontSize:9,color:'var(--text-muted)'}}>·</span>
                          <span data-pp-likely-slot-rank={idx+1}
                                data-pp-likely-slot={s.slot}
                                data-pp-likely-slot-pct={s.pct}
                                data-pp-likely-slot-max={isMax ? '1' : '0'}
                                style={{display:'inline-flex',alignItems:'baseline',gap:4,whiteSpace:'nowrap'}}>
                            <span style={{fontSize:11,color:'var(--text-primary)',fontWeight:500}}>{fmt(s.slot)}</span>
                            <span style={{fontSize:11,color:isMax ? 'var(--accent-gold-text)' : 'var(--text-muted)',fontWeight:isMax ? 700 : 500}}>{s.pct}%</span>
                          </span>
                        </React.Fragment>
                      );
                    })}
                  </div>
                );
              })()}
              {/* Tab bar — two separate outlined pills with a 12px gap
                  so each reads unambiguously as its own button. Every
                  pill carries a 1.5px champagne gold border regardless
                  of state, so the boundary between Graph and Team is
                  always visible:
                    - Inactive: dark bg (#1f1f1f), muted text (#8a8478),
                      champagne-gold-tinted border
                    - Active:   solid champagne gold fill, dark text
                      (#1a1a1a), saturated gold border, inset
                      highlight + drop shadow so it reads "pressed"
                  Tapping the active pill deselects (Zone 7 placeholder
                  returns). */}
              <div data-pp-team-tabs={t.roster_id}
                   role="tablist"
                   aria-label="Team detail tabs"
                   style={{margin:'2px 12px 4px',display:'flex',gap:12}}>
                {['graph','team'].map(tabKey => {
                  const isActive = activeTab === tabKey;
                  const label = tabKey === 'graph' ? 'Graph' : 'Team';
                  return (
                    <button key={tabKey} type="button"
                            role="tab"
                            aria-selected={isActive}
                            aria-controls={`pp-team-tabpanel-${t.roster_id}-${tabKey}`}
                            data-pp-team-tab={tabKey}
                            data-pp-team-tab-active={isActive ? '1' : '0'}
                            onClick={()=>setTeamTab(t.roster_id, tabKey)}
                            style={{flex:'1 1 0',height:36,display:'flex',alignItems:'center',justifyContent:'center',background:isActive ? 'var(--accent-gold)' : '#1f1f1f',color:isActive ? '#1a1a1a' : 'var(--accent-gold-text)',border:isActive ? '2px solid var(--accent-gold)' : '2px solid color-mix(in srgb, var(--accent-gold) 75%, transparent)',borderRadius:10,cursor:'pointer',fontSize:12,fontWeight:isActive ? 600 : 600,letterSpacing:0.4,fontFamily:'inherit',padding:'0 14px',transition:'background-color 140ms ease, color 140ms ease, border-color 140ms ease',boxShadow:isActive ? 'inset 0 1px 0 rgba(255,255,255,0.18), 0 1px 2px rgba(0,0,0,0.25)' : '0 0 0 1px rgba(0,0,0,0.45)'}}>
                      {label}
                    </button>
                  );
                })}
              </div>
              {/* ZONE 7 — Tab content. Three states:
                    - activeTab === null  → "Select a tab to view" placeholder
                    - activeTab === 'graph' → chart + ownership row
                    - activeTab === 'team'  → roster panel */}
              {!activeTab && (
                <div data-pp-team-tab-placeholder="1"
                     role="status"
                     style={{padding:'4px 0 2px',textAlign:'center',fontSize:11,color:'#6a6660',letterSpacing:0.3,fontStyle:'normal'}}>
                  Select a tab to view
                </div>
              )}
              {activeTab === 'graph' && (
              <div role="tabpanel"
                   id={`pp-team-tabpanel-${t.roster_id}-graph`}
                   aria-labelledby={`pp-team-tab-${t.roster_id}-graph`}
                   data-pp-team-tabpanel="graph">
              {ranked.length === 0 ? (
                <div data-pp-graph-empty="1" style={{padding:'20px 0',textAlign:'center',color:'var(--text-muted)',fontSize:12,fontStyle:'italic'}}>
                  No chart data
                </div>
              ) : (<>
              {/* League distribution — vertical bar chart, one column per
                  pick in the league. Bars are ordered left-to-right by pick
                  number (1.01 leftmost, last pick rightmost), which equals
                  ranked-ascending order (lowest PF gets the highest pick).
                  Bar height = the ORIGINAL roster's Proj Max PF normalized
                  to league min/max with a 10% floor so the shortest bar
                  stays visible. Avatar below each column = the CURRENT
                  owner of that pick (post-trade), so users can see who'll
                  actually use each slot.
                  Highlighting: bars + avatars belonging to picks THIS card's
                  team currently owns get the champagne gold treatment. The
                  rule is purely "current owner === card's roster_id" — the
                  signed-in user is irrelevant. A team that traded away all
                  their picks renders an all-muted chart (no highlights,
                  looks intentional). A team that owns multiple picks (via
                  trades) highlights all of them. Theme-aware via
                  --border-hairline (track + separator), --text-muted (other
                  teams), and --accent-gold (current team). */}
              <div data-pp-distribution="1"
                   data-pp-distribution-count={ranked.length}
                   data-pp-distribution-me={t.roster_id}
                   role="presentation"
                   style={{marginTop:8,paddingTop:12,borderTop:'1px solid var(--border-hairline)'}}>
                {(() => {
                  const N = ranked.length;
                  // Auto-tune avatar size against the number of bars so 16-
                  // team leagues don't overflow mobile width. 12-team default
                  // is 18px; 16-team drops to 14px; 8/10-team stays at 18px.
                  const avatarSize = N >= 16 ? 14 : N >= 14 ? 16 : 18;
                  return (
                    <>
                      <div style={{display:'grid',gridTemplateColumns:`repeat(${N}, 1fr)`,gap:3,alignItems:'flex-end',height:140,overflow:'visible'}}>
                        {ranked.map((teamN, dIdx) => {
                          const pct = pmpBarPercent(teamN.totalValue);
                          const currentOwnerRid = ownerByOriginalRoster[teamN.roster_id];
                          const isMine = currentOwnerRid === t.roster_id;
                          const fillColor = isMine ? 'var(--accent-gold)' : 'var(--text-muted)';
                          return (
                            <div key={'dcol_'+teamN.roster_id}
                                 data-pp-distribution-col={dIdx + 1}
                                 data-pp-distribution-bar={dIdx + 1}
                                 data-pp-distribution-bar-current={isMine ? '1' : '0'}
                                 data-pp-distribution-bar-pct={pct.toFixed(1)}
                                 data-pp-distribution-original-rid={teamN.roster_id}
                                 data-pp-distribution-owner-rid={currentOwnerRid}
                                 style={{position:'relative',height:pct+'%',background:fillColor,borderTopLeftRadius:3,borderTopRightRadius:3,minWidth:0}}>
                              {/* Proj Max PF value, absolute-positioned just
                                  above the bar's top edge so the label tracks
                                  the bar height. Color matches the bar (gold
                                  for the current team, muted for the rest)
                                  so the visual hierarchy stays consistent —
                                  scannable values without competing with the
                                  bars themselves. */}
                              <div data-pp-distribution-bar-value="1"
                                   style={{position:'absolute',bottom:'100%',left:'50%',transform:'translateX(-50%)',marginBottom:2,fontSize:8,fontWeight:800,letterSpacing:0.2,color:fillColor,lineHeight:1,whiteSpace:'nowrap',fontVariantNumeric:'tabular-nums',pointerEvents:'none'}}>
                                {Math.round(teamN.totalValue).toLocaleString()}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {/* DEV ONLY — gap labels showing the % of league
                          spread between each pair of adjacent bars. Each
                          label is positioned at the boundary between two
                          bars (column i+1's right edge with translateX
                          50% centering). The number maps 1:1 to the
                          confidence thresholds (merge ≤4%, daylight >8%)
                          so it's easy to eyeball why a particular pair
                          is reading the way it is. NOT pushed to preview. */}
                      {N > 1 && (
                        <div data-pp-distribution-gaps="1" style={{display:'grid',gridTemplateColumns:`repeat(${N}, 1fr)`,alignItems:'center',height:12,marginTop:3,overflow:'visible'}}>
                          {Array.from({length:N-1}, (_, i) => {
                            const gap = (ranked[i+1].totalValue || 0) - (ranked[i].totalValue || 0);
                            const pctOfSpread = leaguePfSpread > 0 ? (gap / leaguePfSpread * 100) : 0;
                            return (
                              <div key={'dgap_'+i}
                                   data-pp-distribution-gap={i+1}
                                   data-pp-distribution-gap-pct={pctOfSpread.toFixed(2)}
                                   style={{gridColumn:i+1,justifySelf:'end',transform:'translateX(50%)',fontSize:7,fontWeight:700,color:'var(--text-muted)',whiteSpace:'nowrap',lineHeight:1,fontVariantNumeric:'tabular-nums',pointerEvents:'none'}}>
                                {pctOfSpread.toFixed(1)}%
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <div style={{display:'grid',gridTemplateColumns:`repeat(${N}, 1fr)`,gap:3,marginTop:6}}>
                        {ranked.map((teamN, dIdx) => {
                          const currentOwnerRid = ownerByOriginalRoster[teamN.roster_id];
                          const ownerTeam = teamByRosterId[currentOwnerRid] || teamN;
                          const ownerAv = ppAvatarUrl(ownerTeam.avatar);
                          const initial = (ownerTeam.display_name || '?')[0]?.toUpperCase();
                          return (
                            <div key={'davatar_'+teamN.roster_id}
                                 data-pp-distribution-avatar-col={dIdx + 1}
                                 data-pp-distribution-owner-rid={currentOwnerRid}
                                 style={{display:'flex',justifyContent:'center',alignItems:'center',minWidth:0}}>
                              {ownerAv ? (
                                <img src={ownerAv} alt=""
                                     loading="lazy"
                                     onError={e => {
                                       // Sleeper avatar fetch failed — swap in a
                                       // div placeholder with the owner's initial.
                                       // Plain DOM swap keeps the click target
                                       // intact and avoids a React re-render.
                                       const img = e.target;
                                       const fallback = document.createElement('div');
                                       fallback.textContent = initial || '?';
                                       fallback.style.cssText = img.style.cssText + ';display:flex;align-items:center;justify-content:center;background:var(--bg-pill);color:var(--text-secondary);font-size:9px;font-weight:800;';
                                       img.replaceWith(fallback);
                                     }}
                                     style={{width:avatarSize,height:avatarSize,borderRadius:'50%',objectFit:'cover',background:'var(--bg-pill)'}}/>
                              ) : (
                                <div style={{width:avatarSize,height:avatarSize,borderRadius:'50%',background:'var(--bg-pill)',display:'flex',alignItems:'center',justifyContent:'center',color:'var(--text-secondary)',fontSize:Math.max(8, Math.floor(avatarSize*0.5)),fontWeight:800}}>
                                  {initial || '?'}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {/* Pick slot labels — adds slot context to the bar
                          chart so each column reads "1.01 / 1.02 / …" at
                          the bottom. Same grid template as the bars +
                          avatars so columns align top-to-bottom.
                          The slot belonging to THIS card's team
                          (t.rank — the team's raw pre-MC projected slot,
                          equal to the 1-indexed ascending-PF position)
                          gets a champagne-gold treatment as a "you are
                          here" orientation marker. Independent of the
                          gold-bar owned-pick highlight above: a team
                          that traded their 2027 first still gets their
                          slot label highlighted here so users can
                          instantly tell which team's card they're
                          viewing even when the bar above is muted. */}
                      <div data-pp-distribution-slots="1" style={{display:'grid',gridTemplateColumns:`repeat(${N}, 1fr)`,gap:3,marginTop:4}}>
                        {ranked.map((teamN, dIdx) => {
                          const slotNum = `1.${String(dIdx + 1).padStart(2,'0')}`;
                          const isCurrentSlot = (dIdx + 1) === t.rank;
                          return (
                            <div key={'dslot_'+teamN.roster_id}
                                 data-pp-distribution-slot-col={dIdx + 1}
                                 data-pp-distribution-slot-label={slotNum}
                                 data-pp-distribution-slot-is-current={isCurrentSlot ? '1' : '0'}
                                 style={{textAlign:'center',fontSize:isCurrentSlot ? 10 : 8,fontWeight:isCurrentSlot ? 900 : 700,color:isCurrentSlot ? 'var(--accent-gold-text)' : 'var(--text-muted)',letterSpacing:0.3,lineHeight:1,fontVariantNumeric:'tabular-nums',minWidth:0,whiteSpace:'nowrap'}}>
                              {slotNum}
                            </div>
                          );
                        })}
                      </div>
                    </>
                  );
                })()}
              </div>
              {/* Pick ownership row — lives in the Graph tab below the
                  bar chart. Surfaces "Original owner" or "Traded to
                  <UserRow>" so the user can see who'll actually pick at
                  this team's slot. */}
              <div style={{marginTop:6,padding:'4px 10px',background:'var(--bg-elevated)',borderRadius:8,display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                <span style={{fontSize:10,color:'var(--text-muted)',fontWeight:800,letterSpacing:0.6,textTransform:'uppercase'}}>2027 1st belongs to</span>
                {isTraded && ownerInfo ? (
                  <UserRow rosterInfo={ownerInfo}/>
                ) : (
                  <span style={{fontSize:12,color:'var(--text-secondary)',fontStyle:'italic'}}>Original owner</span>
                )}
                {isTraded && <span data-pp-traded="1" style={{fontSize:10,color:'var(--accent-gold-text)',fontWeight:700,letterSpacing:0.5,textTransform:'uppercase'}}>· traded</span>}
              </div>
              </>)}
              </div>
              )}
              {/* Team tab — starting lineup + bench contributors + bench
                  not-counted + position group strength. Identical
                  rendering to the previous "tap to expand" panel; only
                  the gating moved from isExpanded to activeTab='team'. */}
              {activeTab === 'team' && (
                <div role="tabpanel"
                     id={`pp-team-tabpanel-${t.roster_id}-team`}
                     aria-labelledby={`pp-team-tab-${t.roster_id}-team`}
                     data-pp-team-tabpanel="team"
                     data-pp-expanded="1" style={{marginTop:0}}>
                  {(!Array.isArray(t.lineup) || t.lineup.length === 0) && (!t.playerLines || t.playerLines.length === 0) ? (
                    <div data-pp-team-tab-empty="1" style={{padding:'20px 0',textAlign:'center',color:'var(--text-muted)',fontSize:12,fontStyle:'italic'}}>
                      No roster data
                    </div>
                  ) : (<>
                  {(() => {
                    // PIECE 4 — bye-week-based bench math display.
                    // Fields used (stamped by ppApplyLineupAndBench):
                    //   isStarter, lineupSlot, posDepthRank, bye,
                    //   byeInRegSeason, starterGames, coversStarterId/Name/Bye,
                    //   adjValue.
                    // adjValue = baseValue × starterGames / 17 (starters)
                    //          = baseValue / 17                (bench covering a bye)
                    //          = 0                              (bench uncovered, K/DEF, unknown-team)
                    const lineup = Array.isArray(t.lineup) ? t.lineup : [];
                    const lines  = t.playerLines || [];
                    // startersTotal / benchContribution / teamTotal are now
                    // computed once at the card level (cardStartersTotal et al.)
                    // for the PROJ MAX PF block in the always-visible header.
                    // The expanded panel only needs benchAll for the
                    // counted/not-counted classifier below.
                    const benchAll = lines.filter(p => p && !p.isStarter);
                    // Counted bench: covers a starter bye (adjValue > 0).
                    // Not Counted: every other bench player at QB/RB/WR/TE
                    // (uncovered — either no starter bye left, or their own
                    // bye conflicts). K/DEF dropped from both sections per
                    // spec (Phase 3a integrates them).
                    const SKILL_POS = ['QB', 'RB', 'WR', 'TE'];
                    const benchByPos = { QB: [], RB: [], WR: [], TE: [] };
                    const notCountedByPos = { QB: [], RB: [], WR: [], TE: [] };
                    benchAll.forEach(p => {
                      if(!p || !SKILL_POS.includes(p.pos)) return;
                      if((p.adjValue || 0) > 0){
                        benchByPos[p.pos].push(p);
                      } else {
                        notCountedByPos[p.pos].push(p);
                      }
                    });
                    SKILL_POS.forEach(pos => {
                      benchByPos[pos].sort((a, b) => (b.baseValue || 0) - (a.baseValue || 0));
                      notCountedByPos[pos].sort((a, b) => (b.baseValue || 0) - (a.baseValue || 0));
                    });
                    const countedCount    = SKILL_POS.reduce((s, pos) => s + benchByPos[pos].length, 0);
                    const notCountedCount = SKILL_POS.reduce((s, pos) => s + notCountedByPos[pos].length, 0);
                    // Color-coded position pill used as the leading column
                    // on Starting Lineup, Bench (Counted), and Bench (Not
                    // Counted) rows. Includes the player's positional rank
                    // (e.g. "QB27") when available. For FLEX/SF starters,
                    // pass the player's actual position (RB/WR/TE/QB), not
                    // the slot key.
                    const posChip = (pos, rank) => {
                      const c = POS_COLORS[pos] || 'var(--text-muted)';
                      const label = rank ? `${pos}${rank}` : pos;
                      // minWidth comes from the .pp-pos-chip CSS class so the
                      // mobile media query can override it. Inline styles
                      // still own color + background since those are
                      // per-position and computed at render.
                      return (
                        <span data-pp-pos-chip={pos}
                              data-pp-pos-rank={rank || ''}
                              className="pp-pos-chip"
                              style={{display:'inline-flex',alignItems:'center',justifyContent:'center',padding:'2px 6px',background:`color-mix(in srgb, ${c} 18%, var(--bg-elevated))`,color:c,border:`1px solid color-mix(in srgb, ${c} 40%, transparent)`,borderRadius:4,fontSize:10,fontWeight:800,letterSpacing:0.5,flexShrink:0,fontVariantNumeric:'tabular-nums'}}>
                          {label}
                        </span>
                      );
                    };
                    // PF formatter — direct rounded contribution to team total.
                    // For starters this is baseValue × starterGames / 17.
                    // For bench it's (baseValue / 17) × byes_covered. Both are
                    // already stamped on the player line as adjValue, so we
                    // just round + locale-format. '—' for zero (uncovered
                    // bench, K/DEF, unknown-team starters).
                    const pfLabel = (adjValue) => {
                      const v = adjValue || 0;
                      return v > 0 ? Math.round(v).toLocaleString() : '—';
                    };
                    // Shared muted-badge style. Used by BYE badge + positional
                    // rank badge so they read as the same visual family — small
                    // outlined pill, muted text. Differs only in marginLeft so
                    // the standalone (grid-cell) usage can set its own margin.
                    const badgeBaseStyle = {display:'inline-flex',alignItems:'center',padding:'1px 5px',fontSize:9,fontWeight:700,color:'var(--text-muted)',border:'1px solid var(--border)',borderRadius:3,letterSpacing:0.5,verticalAlign:'middle',whiteSpace:'nowrap'};
                    const byeBadgeStyle = {...badgeBaseStyle};
                    const posRankBadgeStyle = {...badgeBaseStyle};
                    // Tiny inline "BYE 9" badge — muted styling. Returns
                    // null when bye is missing (FA / unknown team). Keeps a
                    // marginLeft for cases where it's appended inline next to
                    // a name string.
                    const byeBadge = (p) => {
                      if(!p || p.bye == null) return null;
                      return (
                        <span data-pp-bye-badge={p.bye}
                              title={`NFL bye week ${p.bye}`}
                              style={{...byeBadgeStyle,marginLeft:6}}>
                          BYE {p.bye}
                        </span>
                      );
                    };
                    // Positional-rank badge — e.g. "QB13". Same visual weight
                    // as BYE badge. Inline placement (after a starter's name),
                    // so it carries a marginLeft.
                    const posRankBadge = (pos, rank) => {
                      if(!pos || !rank) return null;
                      const label = `${pos}${rank}`;
                      return (
                        <span data-pp-pos-rank-badge={label}
                              title={`${pos} #${rank} by projected fantasy points`}
                              style={{...posRankBadgeStyle,marginLeft:6}}>
                          {label}
                        </span>
                      );
                    };
                    // Both Starting Lineup and Bench Contributors share the
                    // .pp-row-grid CSS class — 5 columns (chip | name+rank |
                    // BYE | Weeks | PF) sized to align horizontally across
                    // both sections. The class lives in styles.css so a
                    // mobile media query can shrink fixed widths at ≤540px
                    // (otherwise the name column starves on iPhone widths).
                    const colHeaderBase = {fontSize:9,color:'var(--text-muted)',fontWeight:700,letterSpacing:0.8,textTransform:'uppercase',padding:'2px 0 4px',borderBottom:'1px solid var(--border-hairline)'};
                    return (
                      <>
                        {/* PROJ MAX PF block lives in the always-visible top
                            row now (data-pp-team-total-wrap on the .pp-card-pmp
                            container above). The expanded panel jumps straight
                            to Starting Lineup. */}

                        {/* STARTING LINEUP — 4-column grid (chip | name+rank |
                            BYE | PF). Starters play every available week so
                            there's no Weeks column; the freed space goes to
                            the name column so player names render in full
                            without horizontal scroll on mobile. PF still
                            right-aligns to the row's right edge, matching the
                            bench grid's PF position. */}
                        <div style={{marginBottom:14}} data-pp-starting-lineup="1">
                          <div style={{fontSize:11,color:'var(--accent-gold-text)',fontWeight:900,letterSpacing:1,textTransform:'uppercase',marginBottom:8}}>Starting lineup</div>
                          {lineup.length === 0 ? (
                            <div style={{fontSize:12,color:'var(--text-muted)',fontStyle:'italic',padding:'4px 0'}}>League has no recognized starter slots.</div>
                          ) : (
                            <div data-pp-starter-grid="1" className="pp-row-grid-starter">
                              {/* Header row. PLAYER sits above the NAME column
                                  (col 2) so it visually labels the names, not
                                  the chip column. */}
                              <div data-pp-col-header="chip" style={colHeaderBase}/>
                              <div data-pp-col-header="player" style={colHeaderBase}>Player</div>
                              <div data-pp-col-header="bye" style={{...colHeaderBase,textAlign:'center'}}>BYE</div>
                              <div data-pp-col-header="pf" style={{...colHeaderBase,textAlign:'right'}}>PF</div>
                              {lineup.map((row, idx) => {
                                const isLast = idx === lineup.length - 1;
                                const borderStyle = isLast ? 'none' : '1px solid var(--border-hairline)';
                                const cellStyle = {padding:'6px 0',fontSize:13,color:'var(--text-primary)',borderBottom:borderStyle,display:'flex',alignItems:'center',minWidth:0};
                                if(!row.player){
                                  return (
                                    <div key={'sl_'+idx} data-pp-lineup-slot={row.slotKey} style={{display:'contents'}}>
                                      <div style={cellStyle}/>
                                      <div style={{...cellStyle,color:'var(--text-muted)',fontStyle:'italic'}}>— empty</div>
                                      <div style={{...cellStyle,justifyContent:'center',color:'var(--text-muted)'}}>—</div>
                                      <div style={{...cellStyle,justifyContent:'flex-end',color:'var(--text-muted)'}}>—</div>
                                    </div>
                                  );
                                }
                                const p = row.player;
                                return (
                                  <div key={'sl_'+idx} data-pp-lineup-slot={row.slotKey} data-pp-lineup-pos={p.pos} style={{display:'contents'}}>
                                    <div data-pp-starter-cell="chip" style={cellStyle}>
                                      {posChip(p.pos)}
                                    </div>
                                    <div data-pp-starter-cell="player" style={{...cellStyle,gap:6}}>
                                      <span data-pp-row-name="1" style={{minWidth:0,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                                        {p.name || `Player #${p.sleeperId}`}
                                        {ppUnmatchedBadge(p)}
                                      </span>
                                      {p.posRank ? posRankBadge(p.pos, p.posRank) : null}
                                    </div>
                                    <div data-pp-starter-cell="bye" style={{...cellStyle,justifyContent:'center'}}>
                                      {p.bye != null ? <span data-pp-bye-badge={p.bye} style={byeBadgeStyle}>BYE {p.bye}</span> : <span style={{color:'var(--text-muted)'}}>—</span>}
                                    </div>
                                    <div data-pp-starter-cell="pf" data-pp-pf-cell="starter" style={{...cellStyle,justifyContent:'flex-end',color:'var(--accent-gold-text)',fontWeight:800,fontVariantNumeric:'tabular-nums'}}>
                                      {pfLabel(p.adjValue)}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {/* BENCH CONTRIBUTORS — players whose bye coverage adds
                            adjValue > 0 to the team total. Renamed from
                            "Bench (Counted)"; count suffix dropped per
                            redesign. Same 5-column grid as Starting Lineup so
                            BYE / Weeks / PF align horizontally between the
                            two sections. Chip is color-only (no rank inside)
                            for visual parity with starter chips; the player's
                            universal positional rank lives in a posRank badge
                            after the name, mirroring the starter row. */}
                        <div style={{marginBottom:14}} data-pp-bench-counted="1">
                          <div style={{fontSize:11,color:'var(--accent-gold-text)',fontWeight:900,letterSpacing:1,textTransform:'uppercase',marginBottom:8}}>
                            Bench Contributors
                          </div>
                          {countedCount === 0 ? (
                            <div style={{fontSize:12,color:'var(--text-muted)',fontStyle:'italic',padding:'4px 0'}}>No bench contributors.</div>
                          ) : (
                            <div data-pp-bench-grid="1" className="pp-row-grid-bench">
                              <div data-pp-col-header="chip" style={colHeaderBase}/>
                              <div data-pp-col-header="player" style={colHeaderBase}>Player</div>
                              <div data-pp-col-header="bye" style={{...colHeaderBase,textAlign:'center'}}>BYE</div>
                              <div data-pp-col-header="pf" style={{...colHeaderBase,textAlign:'right'}}>PF</div>
                              {SKILL_POS.flatMap(pos => benchByPos[pos].map((p, idx) => {
                                const covers = Array.isArray(p.coversStarters) ? p.coversStarters : [];
                                const phrase = (c) => `${c.name} bye in week ${c.bye}`;
                                let titleStr = '';
                                if(covers.length === 1) titleStr = `Covers ${phrase(covers[0])}`;
                                else if(covers.length === 2) titleStr = `Covers ${phrase(covers[0])} AND ${phrase(covers[1])}`;
                                else if(covers.length >= 3){
                                  const head = covers.slice(0, -1).map(phrase).join(', ');
                                  const tail = phrase(covers[covers.length - 1]);
                                  titleStr = `Covers ${head}, and ${tail}`;
                                }
                                const benchRank = idx + 1;
                                const weeksList = covers.map(c => c.bye).sort((a,b) => a - b);
                                const weeksStr = weeksList.join(', ');
                                const cellStyle = {padding:'6px 0',fontSize:13,color:'var(--text-primary)',borderBottom:'1px solid var(--border-hairline)',display:'flex',alignItems:'center',minWidth:0};
                                return (
                                  <div key={p.sleeperId+'_row'}
                                       data-pp-bench-counted-row={p.sleeperId}
                                       data-pp-bench-pos={pos}
                                       data-pp-bench-rank={benchRank}
                                       data-pp-covers-count={covers.length}
                                       data-pp-covers-starter-ids={covers.map(c => c.sleeperId).join(',')}
                                       data-pp-weeks-covered={weeksStr}
                                       style={{display:'contents'}}>
                                    <div data-pp-bench-cell="chip" title={titleStr || undefined} style={cellStyle}>
                                      {posChip(pos)}
                                    </div>
                                    {/* Player cell now stacks vertically:
                                        line 1 = name + posRank badge (full
                                        column width, no truncation), line 2
                                        = "Weeks 7, 10" sub-label in muted
                                        text. Dropping the standalone Weeks
                                        column frees enough horizontal space
                                        on mobile that long names no longer
                                        ellipsis to "Sam …". */}
                                    <div data-pp-bench-cell="player" title={titleStr || undefined} style={{...cellStyle,flexDirection:'column',alignItems:'flex-start',justifyContent:'center',gap:2}}>
                                      <div style={{display:'flex',alignItems:'center',gap:6,minWidth:0,width:'100%'}}>
                                        <span data-pp-row-name="1" style={{minWidth:0,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                                          {p.name || `Player #${p.sleeperId}`}
                                          {ppUnmatchedBadge(p)}
                                        </span>
                                        {p.posRank ? posRankBadge(pos, p.posRank) : null}
                                      </div>
                                      {weeksStr && (
                                        <div data-pp-bench-weeks-sub="1" style={{fontSize:10,color:'var(--text-muted)',fontWeight:600,letterSpacing:0.2,fontVariantNumeric:'tabular-nums',lineHeight:1.25,minWidth:0,maxWidth:'100%',whiteSpace:'normal'}}>
                                          Weeks Contributed to Max PF · <span style={{color:'var(--text-secondary)',fontWeight:700}}>{weeksStr}</span>
                                        </div>
                                      )}
                                    </div>
                                    <div data-pp-bench-cell="bye" style={{...cellStyle,justifyContent:'center'}}>
                                      {p.bye != null ? <span data-pp-bye-badge={p.bye} style={byeBadgeStyle}>BYE {p.bye}</span> : <span style={{color:'var(--text-muted)'}}>—</span>}
                                    </div>
                                    <div data-pp-bench-cell="pf" data-pp-pf-cell="bench" style={{...cellStyle,justifyContent:'flex-end',color:'var(--accent-gold-text)',fontWeight:800,fontVariantNumeric:'tabular-nums'}}>
                                      {pfLabel(p.adjValue)}
                                    </div>
                                  </div>
                                );
                              }))}
                            </div>
                          )}
                        </div>

                        {/* BENCH (NOT CONTRIBUTING) — collapsed-by-default
                            section for bench players whose bye conflicts with
                            their position's starter (no eligible cover, so 0
                            PF). Renamed from "Bench (Not Counted)" for clarity
                            and symmetry with "Bench Contributors". Count
                            suffix kept here since the qualifier is meaningful
                            on a hidden-by-default section. */}
                        {notCountedCount > 0 && (
                          <details data-pp-bench-not-counted="1" data-pp-bench-not-counted-count={notCountedCount}
                                   style={{marginBottom:14,background:'var(--bg-elevated)',border:'1px dashed var(--border)',borderRadius:8,padding:'8px 12px'}}>
                            <summary style={{cursor:'pointer',fontSize:11,color:'var(--text-muted)',fontWeight:800,letterSpacing:1,textTransform:'uppercase',outline:'none'}}>
                              Bench (Not Contributing)
                              <span style={{marginLeft:6,fontWeight:700}}>· {notCountedCount}</span>
                            </summary>
                            <div style={{fontSize:11,color:'var(--text-secondary)',fontStyle:'italic',marginTop:8,marginBottom:8,lineHeight:1.5}}>
                              Players not covering any starter byes.
                            </div>
                            {SKILL_POS.flatMap(pos => notCountedByPos[pos].map((p, idx) => (
                              <div key={p.sleeperId+'_nc_'+idx}
                                   data-pp-bench-not-counted-row={p.sleeperId}
                                   data-pp-bench-pos={pos}
                                   style={{display:'flex',alignItems:'center',gap:10,padding:'4px 0',fontSize:12,color:'var(--text-secondary)',borderBottom:'1px solid var(--border-hairline)'}}>
                                {posChip(pos)}
                                <span style={{flex:1,minWidth:0,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                                  {p.name || `Player #${p.sleeperId}`}
                                  {p.posRank ? posRankBadge(pos, p.posRank) : null}
                                  {byeBadge(p)}
                                  {ppUnmatchedBadge(p)}
                                </span>
                              </div>
                            )))}
                          </details>
                        )}
                      </>
                    );
                  })()}
                  {/* Position Group Strength — total + arrow + % vs league
                      avg. Per-position big number is the sum of contributed
                      Proj Max PF at that position on this team (t.posTotals
                      [pos]); the comparison is vs the league average of that
                      same per-position total. A small "Total PF by position"
                      subtitle clarifies what the big numbers represent — a
                      bare "565" carries no context without the label. */}
                  <div style={{marginBottom:14}} data-pp-position-strength="1">
                    <div style={{fontSize:11,color:'var(--accent-gold-text)',fontWeight:900,letterSpacing:1,textTransform:'uppercase',textAlign:'center'}}>Position group strength</div>
                    <div data-pp-posstrength-subtitle style={{fontSize:9,color:'var(--text-muted)',fontWeight:700,letterSpacing:0.8,textTransform:'uppercase',marginTop:2,marginBottom:8,textAlign:'center'}}>Total PF by position</div>
                    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(120px,1fr))',gap:8}}>
                      {['QB','RB','WR','TE'].map(pos => {
                        const teamPos = (t.posTotals && t.posTotals[pos]) || 0;
                        const lgPos = leagueAvgPos[pos] || 0;
                        const diff = lgPos > 0 ? (teamPos - lgPos) / lgPos : 0;
                        const over = diff > 0.005, under = diff < -0.005;
                        const arrow = over ? '▲' : under ? '▼' : '–';
                        const color = over ? 'var(--success)' : under ? 'var(--danger)' : 'var(--text-secondary)';
                        const pctTxt = `${Math.abs(Math.round(diff*100))}%`;
                        const c = POS_COLORS[pos] || 'var(--text-muted)';
                        return (
                          <div key={pos}
                               data-pp-posstrength-pos={pos}
                               data-pp-posstrength-total={Math.round(teamPos)}
                               data-pp-posstrength-league-avg={Math.round(lgPos)}
                               data-pp-posstrength-diff-pct={Math.round(diff*100)}
                               style={{padding:10,background:'var(--bg-elevated)',borderRadius:8,textAlign:'center',display:'flex',flexDirection:'column',alignItems:'center',gap:4}}>
                            <span style={{display:'inline-flex',alignItems:'center',justifyContent:'center',minWidth:44,padding:'2px 6px',background:`color-mix(in srgb, ${c} 18%, var(--bg-elevated))`,color:c,border:`1px solid color-mix(in srgb, ${c} 40%, transparent)`,borderRadius:4,fontSize:10,fontWeight:800,letterSpacing:0.5}}>
                              {pos}
                            </span>
                            <div style={{fontSize:18,fontWeight:900,color:'var(--text-primary)',fontVariantNumeric:'tabular-nums',display:'inline-flex',alignItems:'baseline',gap:4}}>
                              <span>{Math.round(teamPos).toLocaleString()}</span>
                              <span style={{fontSize:10,color:'var(--text-muted)',fontWeight:700,letterSpacing:0.4,textTransform:'uppercase'}}>pts</span>
                            </div>
                            <div style={{fontSize:11,color,fontWeight:700,letterSpacing:0.3,fontVariantNumeric:'tabular-nums'}}>{arrow} {pctTxt}</div>
                            <div style={{fontSize:9,color:'var(--text-muted)',fontWeight:600,letterSpacing:0.4,textTransform:'uppercase'}}>vs league avg</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  </>)}
                </div>
              )}
            </div>
            {/* Inline "← All leagues" between cards — keeps the exit
                always within thumb's reach while scrolling a long
                ranking. Skipped after the last card because the bottom
                button immediately below renderBoard() already covers
                that position. Visual weight matches the top/bottom
                buttons (text-secondary, border-strong, same font sizing)
                so it's findable at a glance; hairline rules on either
                side give it a divider rhythm without making it the
                primary action. Padding is generous enough on phones
                that it clears the ≥40px tap-target rule. */}
            {i < ranked.length - 1 && (
              <div data-pp-back-inline="1" style={{display:'flex',alignItems:'center',justifyContent:'center',gap:10,padding:'4px 0'}}>
                <div style={{flex:1,height:1,background:'var(--border-strong)'}} aria-hidden="true"/>
                <button type="button" onClick={()=>pickLeague('')}
                        data-pp-back-to-overview-inline="1"
                        style={{display:'inline-flex',alignItems:'center',gap:6,padding:'8px 16px',background:'var(--bg-elevated)',border:'1px solid var(--border-strong)',borderRadius:14,color:'var(--text-secondary)',cursor:'pointer',fontSize:12,fontWeight:800,letterSpacing:0.4,fontFamily:'inherit',whiteSpace:'nowrap'}}>
                  ← All leagues
                </button>
                <div style={{flex:1,height:1,background:'var(--border-strong)'}} aria-hidden="true"/>
              </div>
            )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  return (
    <div style={{background:'var(--bg-page)',minHeight:'100vh',color:'var(--text-primary)',fontFamily:"'Inter','Segoe UI',sans-serif"}}>
      <MasterToolbar currentTab="pick"/>
      <div style={{maxWidth:900,margin:'0 auto',padding:'24px 16px 60px'}}>
        <div style={{marginBottom:20}}>
          <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap',marginBottom:6}}>
            <h1 style={{fontSize:24,fontWeight:900,color:'var(--accent-gold-text)',letterSpacing:1.5,margin:0}}>🔮 2027 Pick Projector</h1>
            <HowItWorksButton title="2027 Pick Projector">
              <p>Every team in your league gets a season-long fantasy projection built from each player's projected stats — scored using <em>your league's</em> exact scoring settings, not a one-size-fits-all average. Starters contribute their full projected season. Bench depth contributes based on how often it'd actually fill in during a starter's bye week. Sum the whole roster and you get a team's <strong>Proj Max PF</strong>: the engine's read on how strong the roster looks heading into the upcoming season.</p>
              <p style={{marginTop:10}}>Teams are then ranked ascending — worst Proj Max PF goes 1.01, best gets the last pick. From there, a simulation engine plays out thousands of plausible season variations, because real fantasy outcomes are noisy: close-PF teams swap order often, daylight-apart teams almost never do. That simulation produces the <strong>tier probabilities</strong>, the <strong>most-likely-slot percentages</strong>, and the <strong>visible pick range</strong> on each card.</p>
              <p style={{marginTop:10,fontSize:12,color:'var(--text-muted)',fontStyle:'italic'}}>Projections refresh regularly throughout the season; the simulation re-rolls daily.</p>
              <div data-pp-howitworks-custom-ranks="1"
                   style={{marginTop:18,paddingTop:14,borderTop:'1px solid var(--border-hairline)'}}>
                <div style={{fontSize:11,fontWeight:800,color:'var(--text-muted)',letterSpacing:1.5,textTransform:'uppercase',marginBottom:10}}>
                  Set your own 2027 rookie order
                </div>
                <p style={{fontSize:12,color:'var(--text-secondary)',lineHeight:1.55,margin:0}}>
                  The rookie photo next to each pick slot comes from PFK's default 2027 first-round board. To project <em>your own</em> draft order instead — so each card shows the rookie <strong style={{color:'var(--text-primary)'}}>you'd</strong> take in that slot — open the <a href="/?tab=pfk2027" style={{color:'var(--accent-gold-text)',textDecoration:'underline',textDecorationStyle:'dotted',textUnderlineOffset:2,fontWeight:800}}>2027 Rookie Ranks</a> tab, drag rookies into your preferred order, and save. Pick Projector picks it up automatically the next time you open it.
                </p>
              </div>
              {(() => {
                // Phase 3a refinement: hardcoded plain-English section for the
                // two scoring families dynasty users actually care about as
                // unmodeled — 100+ yard bonuses and PPFD. The earlier raw-
                // warnings list surfaced developer-log noise (st_td,
                // unrecognized-key strings) that confused more than it
                // informed. Tradeoff Evan chose knowingly: total transparency
                // is sacrificed for a cleaner UX. The engine's full warnings
                // array is still emitted by ppApplyLeagueScoring (and stored
                // on projection.warnings) for future audit need.
                const ss = projection?.league?.scoring_settings || {};
                const hasYd = ppLeagueHasYardageBonuses(ss);
                const hasFd = ppLeagueHasPpfd(ss);
                if(!hasYd && !hasFd) return null;
                return (
                  <div data-pp-warnings-info-section="1"
                       data-pp-unmodeled-yd={hasYd ? '1' : '0'}
                       data-pp-unmodeled-fd={hasFd ? '1' : '0'}
                       style={{marginTop:18,paddingTop:14,borderTop:'1px solid var(--border-hairline)'}}>
                    <div style={{fontSize:11,fontWeight:800,color:'var(--text-muted)',letterSpacing:1.5,textTransform:'uppercase',marginBottom:10}}>
                      Scoring rules not modeled
                    </div>
                    <p style={{fontSize:12,color:'var(--text-secondary)',marginBottom:10,lineHeight:1.55,marginTop:0}}>
                      Some common scoring rules in this league aren't yet modeled in our projections:
                    </p>
                    <ul style={{margin:0,paddingLeft:18,fontSize:12,color:'var(--text-secondary)',lineHeight:1.55}}>
                      {hasYd && <li style={{marginBottom:6}}>100+ yard bonuses</li>}
                      {hasFd && <li style={{marginBottom:6}}>PPFD (points per first down)</li>}
                    </ul>
                    <p style={{fontSize:12,color:'var(--text-secondary)',marginTop:10,lineHeight:1.55,marginBottom:0}}>
                      Players in these scoring settings may be slightly under-credited across the board, but team rankings within the league remain accurate.
                    </p>
                  </div>
                );
              })()}
            </HowItWorksButton>
          </div>
          {/* Onboarding disclaimer paragraph — only renders on the
              leagues-list view. Once the user has drilled into a team
              detail page they already know what the tool is, and on
              phones the trailing italicized "Click How it works for
              more info." can wrap such that just "info." sits on its
              own line above the back button, looking like stray text. */}
          {!leagueId && (
            <p style={{fontSize:13,color:'var(--text-secondary)',marginTop:6,lineHeight:1.55,maxWidth:680}}>
              Treat this as a planning tool, not a prediction. Real seasons get messy in ways no model can see — injuries, breakouts, schedule luck, a single Week 1 ankle injury that reshuffles a whole position group. The numbers give you a rough read on your league's <strong style={{color:'var(--text-primary)'}}>2027 1st-round pick economy</strong> so you can think clearly about trades and contention windows — not a guarantee on any specific slot. <em>Click <strong>How it works</strong> for more info.</em>
            </p>
          )}
          {/* "Using your 2027 ranks" indicator. Only renders when the
              user has a saved row in user_rookie_ranks_2027 — anonymous
              visitors and signed-in users without a saved order both
              see nothing here. The Edit link jumps straight to the
              2027 Rookie Ranks editor in case they want to tweak. */}
          {usingCustomRanks && (
            <div data-pp-using-custom-ranks="1"
                 style={{marginTop:10,display:'inline-flex',alignItems:'center',gap:8,padding:'5px 12px',background:'color-mix(in srgb, var(--accent-gold) 12%, var(--bg-elevated))',border:'1px solid color-mix(in srgb, var(--accent-gold) 40%, transparent)',borderRadius:14,fontSize:11,fontWeight:700,color:'var(--accent-gold-text)',letterSpacing:0.3}}>
              <span aria-hidden="true">🏆</span>
              <span>Using your 2027 ranks</span>
              <a href="/?tab=pfk2027"
                 data-pp-using-custom-edit="1"
                 style={{color:'var(--accent-gold-text)',textDecoration:'underline',textDecorationStyle:'dotted',textUnderlineOffset:2,fontWeight:800}}>
                Edit
              </a>
            </div>
          )}
        </div>

        {!sleeperUser && (
          <div style={{padding:'12px 16px',background:'var(--bg-elevated)',border:'1px dashed rgba(var(--accent-gold-rgb),0.27)',borderRadius:8,color:'var(--text-secondary)',fontSize:13,maxWidth:520,marginBottom:16}}>
            💡 Link your Sleeper account in the toolbar above — Pick Projector will then load your dynasty leagues.
          </div>
        )}

        {sleeperUser && (
          <>
            {/* DRILLED-IN: full board for one league + back button */}
            {leagueId && (
              <>
                {/* Team-detail page header — slimmed (2026-05-20) to just
                    the "← All leagues" back button. The league name and
                    settings line used to live here too, but the league
                    name is now in Zone 1 of every team card, and the
                    "scoring rules not modeled" disclosure still surfaces
                    in the bottom "How this works" footer + the How It
                    Works popover so no info is lost. */}
                <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:14}}>
                  <button type="button" onClick={()=>pickLeague('')}
                          data-pp-back-to-overview="1"
                          style={{display:'inline-flex',alignItems:'center',gap:6,padding:'6px 12px',background:'transparent',border:'1px solid var(--border-strong)',borderRadius:14,color:'var(--text-secondary)',cursor:'pointer',fontSize:11,fontWeight:800,letterSpacing:0.4,fontFamily:'inherit'}}>
                    ← All leagues
                  </button>
                </div>
                {!projection && <div style={{padding:30,textAlign:'center',color:'var(--text-secondary)'}}>Loading league…</div>}
                {projection && renderBoard()}
                {/* Bottom "All leagues" button — duplicate of the top one so
                    users who scroll to the end of a long ranking can jump back
                    to the league picker without scrolling to the top first. */}
                {projection && (
                  <div style={{marginTop:18,display:'flex',justifyContent:'center'}}>
                    <button type="button" onClick={()=>pickLeague('')}
                            data-pp-back-to-overview-bottom="1"
                            style={{display:'inline-flex',alignItems:'center',gap:6,padding:'8px 16px',background:'transparent',border:'1px solid var(--border-strong)',borderRadius:14,color:'var(--text-secondary)',cursor:'pointer',fontSize:12,fontWeight:800,letterSpacing:0.4,fontFamily:'inherit'}}>
                      ← All leagues
                    </button>
                  </div>
                )}
                {projection && (
                  <div style={{marginTop:24,padding:14,background:'var(--bg-elevated)',borderRadius:10,fontSize:12,color:'var(--text-secondary)',lineHeight:1.6,letterSpacing:0.1}}>
                    <div style={{fontWeight:800,color:'var(--text-secondary)',marginBottom:6,letterSpacing:0.5,textTransform:'uppercase',fontSize:11}}>How this works</div>
                    <p style={{margin:0}}>
                      Every team in your league gets a season-long fantasy projection built from each player's projected stats — scored using <em>your league's</em> exact scoring settings, not a one-size-fits-all average. Starters contribute their full projected season. Bench depth contributes based on how often it'd actually fill in during a starter's bye week. Sum the whole roster and you get a team's <strong style={{color:'var(--text-primary)'}}>Proj Max PF</strong>: the engine's read on how strong the roster looks heading into the upcoming season.
                    </p>
                    <p style={{margin:'10px 0 0'}}>
                      Teams are then ranked ascending — worst Proj Max PF goes 1.01, best gets the last pick. From there, a simulation engine plays out thousands of plausible season variations, because real fantasy outcomes are noisy: close-PF teams swap order often, daylight-apart teams almost never do. That simulation produces the <strong style={{color:'var(--text-primary)'}}>tier probabilities</strong>, the <strong style={{color:'var(--text-primary)'}}>most-likely-slot percentages</strong>, and the <strong style={{color:'var(--text-primary)'}}>visible pick range</strong> on each card.
                    </p>
                    <p style={{margin:'10px 0 0',fontSize:11,color:'var(--text-muted)',fontStyle:'italic'}}>
                      Projections refresh regularly throughout the season; the simulation re-rolls daily.
                    </p>
                  </div>
                )}
              </>
            )}

            {/* OVERVIEW: one card per league, my projected slot in each */}
            {!leagueId && (
              <>
                {leagues === null && <div style={{fontSize:12,color:'var(--text-secondary)',padding:20,textAlign:'center'}}>Loading your dynasty leagues…</div>}
                {leagues && leagues.length === 0 && <div style={{fontSize:13,color:'var(--text-secondary)',padding:20,textAlign:'center'}}>No dynasty leagues found for {new Date().getFullYear()}.</div>}
                {leagues && leagues.length > 0 && (
                  <>
                    {/* Search bar — filters the leagues list by name (and
                        settings text, so "SF" or "12-team" also work). Lives
                        above the YOUR DYNASTY LEAGUES header. Sized to a
                        max 400px on desktop, full-width on mobile. fontSize
                        is 16px specifically so iOS Safari doesn't zoom the
                        viewport when the input focuses. */}
                    <div data-pp-league-search-wrap="1" style={{marginBottom:14,maxWidth:400}}>
                      <div style={{position:'relative',width:'100%'}}>
                        {/* Magnifying-glass affordance icon, left side */}
                        <span aria-hidden="true"
                              style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',color:'var(--text-muted)',display:'inline-flex',alignItems:'center',pointerEvents:'none'}}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="7"/>
                            <path d="m20 20-3.5-3.5"/>
                          </svg>
                        </span>
                        <input type="search"
                               data-pp-league-search="1"
                               value={leagueSearch}
                               onChange={e => setLeagueSearch(e.target.value)}
                               placeholder="Search your leagues..."
                               aria-label="Search your dynasty leagues"
                               style={{width:'100%',padding:'10px 36px 10px 34px',background:'var(--bg-input)',border:'1px solid var(--border-strong)',borderRadius:8,color:'var(--text-bright)',fontSize:16,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}/>
                        {leagueSearch && (
                          <button type="button"
                                  onClick={() => setLeagueSearch('')}
                                  aria-label="Clear search"
                                  data-pp-league-search-clear="1"
                                  style={{position:'absolute',right:0,top:0,bottom:0,width:44,minWidth:44,display:'inline-flex',alignItems:'center',justifyContent:'center',background:'transparent',border:'none',color:'var(--text-muted)',cursor:'pointer',padding:0,fontSize:18,lineHeight:1,fontFamily:'inherit'}}>
                            <span aria-hidden="true" style={{display:'inline-flex',alignItems:'center',justifyContent:'center',width:20,height:20,borderRadius:'50%',background:'var(--bg-pill)',fontSize:14,fontWeight:700}}>×</span>
                          </button>
                        )}
                      </div>
                    </div>
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12,flexWrap:'wrap',gap:10}}>
                      <div data-pp-leagues-header-count="1" style={{fontSize:11,color:'var(--text-secondary)',fontWeight:800,letterSpacing:1.5,textTransform:'uppercase'}}>
                        Your dynasty leagues ({leagueSearch.trim() && filteredSummaries.length !== mySummaries.length ? `${filteredSummaries.length} of ${leagues.length}` : leagues.length})
                      </div>
                      {loadingProgress.total > 0 && loadingProgress.done < loadingProgress.total && (
                        <div data-pp-loading-progress="1" style={{fontSize:11,color:'var(--text-muted)'}}>
                          Loading {loadingProgress.done} of {loadingProgress.total}…
                        </div>
                      )}
                    </div>
                    {/* Empty state while everything's still loading */}
                    {mySummaries.length === 0 && loadingProgress.done === 0 && (
                      <div style={{padding:30,textAlign:'center',color:'var(--text-secondary)',fontSize:13}}>Computing projections for {loadingProgress.total} leagues…</div>
                    )}
                    {/* No-roster state — finished loading but user isn't on any roster */}
                    {mySummaries.length === 0 && loadingProgress.done === loadingProgress.total && loadingProgress.total > 0 && (
                      <div style={{padding:20,fontSize:13,color:'var(--text-secondary)',background:'var(--bg-elevated)',borderRadius:8}}>
                        Loaded {loadingProgress.total} league{loadingProgress.total===1?'':'s'} but couldn't find your roster in any of them. Make sure your linked Sleeper account is the one that owns your teams.
                      </div>
                    )}
                    {/* Search returned no results — distinct from the
                        loading and no-roster empty states above. */}
                    {mySummaries.length > 0 && filteredSummaries.length === 0 && leagueSearch.trim() && (
                      <div data-pp-league-search-empty="1"
                           style={{padding:20,fontSize:13,color:'var(--text-secondary)',background:'var(--bg-elevated)',borderRadius:8,textAlign:'center'}}>
                        No leagues match &ldquo;<strong style={{color:'var(--text-primary)'}}>{leagueSearch}</strong>&rdquo;.
                      </div>
                    )}
                    {/* The cards themselves */}
                    {filteredSummaries.length > 0 && (
                      <div style={{display:'flex',flexDirection:'column',gap:10}}>
                        {filteredSummaries.map(s => {
                          // Per-league summary data needed for the redesigned card:
                          //   slotLabel      — formatted pick label (1.01 or 1.02-1.03)
                          //   conf           — High/Medium/Low (per-league confidence)
                          //   confColor      — green/amber/muted CSS variable
                          //   topPickIdx     — 1-indexed best-case slot (1, 2, or 3 → tier accent)
                          //   isTopTier      — true iff topPickIdx ≤ 3 (lottery cards)
                          //   leagueAv       — Sleeper league avatar URL (or null)
                          //   rookie         — published-board rookie for the top of this user's pick range
                          //   ownedByRound   — { [round]: count } of 2027 picks this user currently owns
                          //   ownedRounds    — sorted ascending list of rounds the user owns picks in
                          //   totalOwned     — total count across all rounds
                          const slotLabel = ppSlotLabel(s.myRow);
                          const conf = s.myRow.confidence;
                          const confColor = ppConfidenceColor(conf);
                          const topPickIdx = s.myRow.slotRange == null ? s.myRow.slot : s.myRow.slotRange[0];
                          const isTopTier = topPickIdx <= 3;
                          const leagueAv = s.lg.avatar ? `https://sleepercdn.com/avatars/${s.lg.avatar}` : null;
                          const rookie = effectiveRookies[topPickIdx - 1] || null;
                          const rookieInitial = ((rookie?.name || '?')[0] || '?').toUpperCase();
                          const leagueInitial = ((s.lg.name || '?')[0] || '?').toUpperCase();
                          // Tier-probability entries for the 3-pill row on
                          // the compressed leagues-list card. EARLY/MID/LATE
                          // percentages sum to 100. Dominant pill is whichever
                          // has the highest pct (ties broken EARLY > MID >
                          // LATE by array order); the dominant pill gets
                          // champagne fill and the other two stay neutral.
                          const _tp = s.myRow.tierProbs || { early: 0, mid: 0, late: 0 };
                          const _tierEntries = [
                            { key: 'early', label: 'EARLY', pct: _tp.early },
                            { key: 'mid',   label: 'MID',   pct: _tp.mid },
                            { key: 'late',  label: 'LATE',  pct: _tp.late },
                          ];
                          const dominantTier = _tierEntries.reduce((best, cur) => cur.pct > best.pct ? cur : best, _tierEntries[0]);
                          return (
                            <button key={s.lgId} type="button"
                                    onClick={()=>pickLeague(s.lgId)}
                                    data-pp-summary-card={s.lgId}
                                    data-pp-summary-tier={isTopTier ? 'top' : 'mid'}
                                    className={`pp-summary-card ${isTopTier ? 'pp-summary-card-top-pick' : ''}`}>
                              {/* COMPRESSED CARD (2026-05-20 redesign).
                                  Visual-only compression — tapping the card
                                  still drills into the team / inside-league
                                  view via pickLeague(s.lgId). Target height
                                  ~110-130px (down from ~280-300px) so 6+
                                  leagues fit on iPhone at 390px.

                                  Two columns:
                                    LEFT  (3fr) — logo+name row, pick range
                                                  headline, dominant tier
                                                  chip, ownership indicator
                                    RIGHT (2fr) — rookie photo + name +
                                                  position·school

                                  Removed (vs prior premium card): league
                                  settings line, all 3 tier-probability
                                  pills, MOST LIKELY SLOTS row, 2027 PICKS
                                  BY ROUND footer, "Tap to expand" hint. */}
                              <div data-pp-summary-card-header="1"
                                   style={{display:'grid',gridTemplateColumns:'minmax(0,3fr) minmax(0,2fr)',gap:12,alignItems:'center'}}>
                                {/* LEFT COLUMN — vertical stack */}
                                <div data-pp-summary-card-left="1" style={{minWidth:0,display:'flex',flexDirection:'column',gap:6}}>
                                  {/* Row A — league logo + name */}
                                  <div data-pp-summary-card-league-row="1"
                                       style={{display:'flex',alignItems:'center',gap:6,minWidth:0}}>
                                    <div data-pp-summary-league-avatar="1" style={{flexShrink:0,width:18,height:18}}>
                                      {leagueAv ? (
                                        <img src={leagueAv} alt="" loading="lazy"
                                             onError={e => {
                                               const img = e.target;
                                               const fallback = document.createElement('div');
                                               fallback.textContent = leagueInitial;
                                               fallback.style.cssText = 'width:18px;height:18px;border-radius:50%;background:color-mix(in srgb, var(--accent-gold) 12%, var(--bg-elevated));color:var(--accent-gold-text);display:flex;align-items:center;justify-content:center;font-size:9px;font-weight:800;border:1px solid var(--border-hairline);';
                                               img.replaceWith(fallback);
                                             }}
                                             style={{width:18,height:18,borderRadius:'50%',objectFit:'cover',background:'var(--bg-pill)',border:'1px solid var(--border-hairline)'}}/>
                                      ) : (
                                        <div style={{width:18,height:18,borderRadius:'50%',background:'color-mix(in srgb, var(--accent-gold) 12%, var(--bg-elevated))',color:'var(--accent-gold-text)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:9,fontWeight:800,border:'1px solid var(--border-hairline)'}}>
                                          {leagueInitial}
                                        </div>
                                      )}
                                    </div>
                                    <span data-pp-summary-league-name="1"
                                          style={{fontSize:13,fontWeight:500,color:'var(--text-bright)',letterSpacing:0.1,lineHeight:1.2,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',minWidth:0}}>
                                      {s.lg.name}
                                    </span>
                                  </div>

                                  {/* Row B — pick range headline */}
                                  <div data-pp-summary-slot-label="1"
                                       style={{fontSize:22,fontWeight:600,color:'var(--accent-gold-text)',letterSpacing:0.4,lineHeight:1,fontFamily:"'Inter',sans-serif",fontVariantNumeric:'tabular-nums',whiteSpace:'nowrap'}}>
                                    {slotLabel}
                                  </div>

                                  {/* Row C — TIER PROBABILITY pills (full
                                      3-pill row with %). Dominant pill gets
                                      champagne fill + bolder text; others
                                      stay neutral. 0%-buckets render at half
                                      opacity in muted text. */}
                                  <div data-pp-summary-tier-pills="1"
                                       data-pp-summary-tier-dominant={dominantTier.pct > 0 ? dominantTier.key : 'none'}
                                       style={{display:'flex',gap:4}}>
                                    {_tierEntries.map(tier => {
                                      const isDom = tier.key === dominantTier.key && tier.pct > 0;
                                      const isZero = tier.pct === 0;
                                      return (
                                        <span key={tier.key}
                                              data-pp-summary-tier={tier.key}
                                              data-pp-summary-tier-pct={tier.pct}
                                              style={{flex:'1 1 0',minWidth:0,display:'inline-flex',alignItems:'center',justifyContent:'center',gap:4,padding:'3px 6px',minHeight:20,borderRadius:6,background:isDom ? 'color-mix(in srgb, var(--accent-gold) 24%, var(--bg-elevated))' : 'var(--bg-elevated)',color:isDom ? 'var(--accent-gold-text)' : (isZero ? 'var(--text-muted)' : 'var(--text-primary)'),border:isDom ? '1px solid color-mix(in srgb, var(--accent-gold) 40%, transparent)' : '1px solid var(--border-hairline)',fontWeight:isDom?800:600,fontSize:9,letterSpacing:0.4,opacity:isZero?0.5:1,whiteSpace:'nowrap',fontVariantNumeric:'tabular-nums'}}>
                                          <span style={{fontWeight:isDom?800:700,letterSpacing:0.5}}>{tier.label}</span>
                                          <span style={{fontSize:10,fontWeight:isDom?900:700}}>{tier.pct}%</span>
                                        </span>
                                      );
                                    })}
                                  </div>

                                  {/* Row D — ownership indicator (2026-05-20
                                      rebuild). Same two-state logic as the
                                      team card: "is the signed-in user the
                                      current owner of this pick?".
                                        - Yes → "PICK IS YOURS" green
                                        - No  → "PICK BELONGS TO @{owner}"
                                                muted. Single-entry list in
                                                practice (one natural 2027
                                                1st per roster); rendered
                                                via the same dedup'd Set
                                                pattern as the team card. */}
                                  {(() => {
                                    const pickOwners = new Set();
                                    if(s.ownerInfo){
                                      const handle = s.ownerInfo.username || s.ownerInfo.display_name;
                                      if(handle) pickOwners.add(handle);
                                    }
                                    const myUid = sleeperUser?.user_id;
                                    const pickIsMine = !!(myUid && s.ownerInfo && (
                                      s.ownerInfo.owner_id === myUid || (s.ownerInfo.co_owners || []).includes(myUid)
                                    ));
                                    if(pickIsMine){
                                      return (
                                        <div data-pp-summary-ownership="1"
                                             data-pp-summary-ownership-state="yours"
                                             style={{fontSize:9,lineHeight:1.3,letterSpacing:0.5,textTransform:'uppercase',whiteSpace:'nowrap',color:'var(--success)',fontWeight:700}}>
                                          Pick is yours
                                        </div>
                                      );
                                    }
                                    if(pickOwners.size === 0) return null;
                                    const list = [...pickOwners];
                                    return (
                                      <div data-pp-summary-ownership="1"
                                           data-pp-summary-ownership-state="other"
                                           data-pp-pick-owner-count={list.length}
                                           style={{fontSize:9,lineHeight:1.3,letterSpacing:0.5,textTransform:'uppercase',color:'var(--text-muted)',fontWeight:700}}>
                                        Pick belongs to {list.map((h, idx) => (
                                          <React.Fragment key={h}>
                                            {idx > 0 && ', '}
                                            <span style={{color:'var(--text-secondary)',fontWeight:800}}>@{h}</span>
                                          </React.Fragment>
                                        ))}
                                      </div>
                                    );
                                  })()}
                                </div>

                                {/* RIGHT COLUMN — rookie chip centered.
                                    Placeholder block when rookie is null
                                    keeps card height consistent across
                                    rows (don't collapse the column). */}
                                <div data-pp-summary-card-rookie-hero="1"
                                     data-pp-summary-rookie-id={rookie?.id || ''}
                                     style={{textAlign:'center',minWidth:0,display:'flex',flexDirection:'column',alignItems:'center',gap:3}}>
                                  <div data-pp-summary-rookie-avatar="1"
                                       style={{width:50,height:60,display:'flex',alignItems:'center',justifyContent:'center'}}>
                                    {!rookieBoardLoaded && !rookie ? (
                                      <div style={{width:'100%',height:'100%'}} aria-hidden="true"/>
                                    ) : rookie?.photo_url ? (
                                      <img src={rookie.photo_url} alt="" loading="lazy"
                                           onError={e => {
                                             const img = e.target;
                                             const fallback = document.createElement('div');
                                             fallback.textContent = rookieInitial;
                                             fallback.style.cssText = 'width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:color-mix(in srgb, var(--accent-gold) 12%, var(--bg-elevated));color:var(--accent-gold-text);font-size:20px;font-weight:900;border-radius:6px;';
                                             img.replaceWith(fallback);
                                           }}
                                           style={{width:'100%',height:'100%',objectFit:'contain',display:'block'}}/>
                                    ) : (
                                      <div style={{width:'100%',height:'100%',display:'flex',alignItems:'center',justifyContent:'center',background:'color-mix(in srgb, var(--accent-gold) 12%, var(--bg-elevated))',color:'var(--accent-gold-text)',fontSize:20,fontWeight:900,borderRadius:6}}>
                                        {rookieInitial}
                                      </div>
                                    )}
                                  </div>
                                  <div data-pp-summary-rookie-name="1"
                                       style={{fontSize:11,fontWeight:500,color:'var(--text-primary)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',lineHeight:1.2,maxWidth:'100%'}}>
                                    {rookie?.name || (rookieBoardLoaded ? 'Unranked' : '\u00A0')}
                                  </div>
                                  <div style={{fontSize:9,color:'var(--text-muted)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',lineHeight:1.2,maxWidth:'100%'}}>
                                    {rookie?.position && <span data-pp-summary-rookie-position="1">{rookie.position}</span>}
                                    {rookie?.position && rookie?.school && <span style={{margin:'0 4px'}}>·</span>}
                                    {rookie?.school && <span data-pp-summary-rookie-school="1">{rookie.school}</span>}
                                    {!rookie && rookieBoardLoaded && <span style={{fontStyle:'italic'}}>Rookie TBD</span>}
                                    {!rookie && !rookieBoardLoaded && <span>&nbsp;</span>}
                                  </div>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
      <PFKFooter/>
    </div>
  );
}

// ============================================================================
// AdminUnderdogAdp — CSV upload + needs_review queue
// ============================================================================
function AdminUnderdogAdpInner(){
  const [adp, setAdp] = useState([]);
  const [map, setMap] = useState([]);
  const [csvText, setCsvText] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [sleeperPlayersData, setSleeperPlayersData] = useState(null);
  const [needsReviewSearch, setNeedsReviewSearch] = useState({}); // udPlayerId → search query
  const [needsReviewMatches, setNeedsReviewMatches] = useState({}); // udPlayerId → [{pid,name,pos,team}]
  const [latestRun, setLatestRun] = useState(null);
  const [runningNow, setRunningNow] = useState(false);
  const [runMsg, setRunMsg] = useState('');

  const fetchLatestRun = async () => {
    if(!sb) return;
    const { data, error } = await sb.from('scraper_runs')
      .select('*')
      .eq('scraper_name', 'refresh-underdog-adp')
      .order('started_at', { ascending: false })
      .limit(1);
    if(error || !data || !data.length){ setLatestRun(null); return; }
    setLatestRun(data[0]);
  };

  const refresh = async () => {
    const [a, m] = await Promise.all([ppFetchAdp(), ppFetchMap()]);
    setAdp(a); setMap(m);
  };
  useEffect(() => {
    refresh();
    fetchLatestRun();
    sleeperPlayers().then(setSleeperPlayersData).catch(()=>{});
  }, []);

  const handleRunNow = async () => {
    if(!sb){ setRunMsg('Supabase not loaded'); return; }
    setRunningNow(true);
    setRunMsg('Starting scrape…');
    try {
      const { data: sessionData } = await sb.auth.getSession();
      const token = sessionData?.session?.access_token;
      if(!token){ setRunMsg('Sign in first'); setRunningNow(false); return; }
      const res = await fetch('/api/admin/refresh-underdog-adp', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = await res.json().catch(() => ({}));
      if(!res.ok){
        setRunMsg(`Failed (${res.status}): ${body.error || res.statusText}`);
      } else if(body.ok === false && body.error === 'already_running'){
        setRunMsg('A scrape is already running — try again shortly.');
      } else if(body.ok){
        setRunMsg(`Done — scraped ${body.rows_scraped}, matched ${body.rows_matched}, history ${body.rows_history_logged}.`);
      } else {
        setRunMsg('Done — see Last scrape panel for details.');
      }
      await fetchLatestRun();
      await refresh();
    } catch (e) {
      setRunMsg('Error: ' + (e.message || e));
    } finally {
      setRunningNow(false);
    }
  };

  const slugify = (s) => (s||'').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g,'').replace(/[^a-z0-9]/g,'');

  const handleParse = async () => {
    if(!csvText.trim()){ setMsg('Paste CSV first'); return; }
    if(!window.Papa){ setMsg('PapaParse not loaded — refresh the page'); return; }
    if(!sleeperPlayersData){ setMsg('Sleeper player data still loading — try again in a sec'); return; }
    setBusy(true);
    setMsg('Parsing…');
    try {
      const parsed = window.Papa.parse(csvText.trim(), { header: true, skipEmptyLines: true, transformHeader: h => h.trim().toLowerCase() });
      const rows = parsed.data || [];
      if(!rows.length){ setMsg('No rows parsed'); setBusy(false); return; }
      // Validate columns
      const required = ['adp','name','position','team'];
      const missing = required.filter(c => !(c in rows[0]));
      if(missing.length){ setMsg(`Missing columns: ${missing.join(', ')}`); setBusy(false); return; }
      // Build adp + map rows
      const adpRows = [];
      const mapRows = [];
      const seenIds = new Set();
      for(const r of rows){
        const name = (r.name||'').trim();
        const pos = (r.position||'').trim().toUpperCase();
        const team = (r.team||'').trim().toUpperCase();
        const adpVal = parseFloat(r.adp);
        if(!name || !pos || !isFinite(adpVal)) continue;
        let upid = (r.underdog_player_id||'').trim();
        if(!upid) upid = `${slugify(name)}-${pos}-${team}`;
        if(seenIds.has(upid)) continue;
        seenIds.add(upid);
        adpRows.push({ underdog_player_id: upid, name, position: pos, team, adp: adpVal, updated_at: new Date().toISOString() });
        const match = ppFuzzyMatch({ name, position: pos, team }, sleeperPlayersData);
        mapRows.push({
          underdog_player_id: upid,
          sleeper_player_id: match.sleeper_player_id,
          match_confidence: match.confidence,
        });
      }
      setMsg(`Parsed ${adpRows.length} rows. Auto-matched ${mapRows.filter(m => m.match_confidence === 'auto').length}, ${mapRows.filter(m => m.match_confidence === 'needs_review').length} need review. Uploading…`);
      // Upsert in batches of 100 to keep PostgREST happy
      const chunk = 100;
      for(let i = 0; i < adpRows.length; i += chunk){
        const slice = adpRows.slice(i, i + chunk);
        const { error } = await sb.from('underdog_adp').upsert(slice, { onConflict: 'underdog_player_id' });
        if(error){ setMsg('Upload failed: ' + error.message); setBusy(false); return; }
      }
      for(let i = 0; i < mapRows.length; i += chunk){
        const slice = mapRows.slice(i, i + chunk);
        const { error } = await sb.from('underdog_sleeper_map').upsert(slice, { onConflict: 'underdog_player_id' });
        if(error){ setMsg('Map upload failed: ' + error.message); setBusy(false); return; }
      }
      setMsg(`Uploaded ${adpRows.length} ADP rows. Reloading…`);
      await refresh();
      setCsvText('');
    } catch(e){
      setMsg('Error: ' + (e.message||e));
    } finally {
      setBusy(false);
    }
  };

  const adpById = useMemo(() => { const o={}; adp.forEach(a => { o[a.underdog_player_id] = a; }); return o; }, [adp]);
  const needsReview = useMemo(() => map.filter(m => m.match_confidence === 'needs_review' && adpById[m.underdog_player_id]).slice(0, 100), [map, adpById]);

  const searchSleeperPlayers = (upid, q) => {
    setNeedsReviewSearch(prev => ({ ...prev, [upid]: q }));
    if(!sleeperPlayersData || !q || q.length < 2){
      setNeedsReviewMatches(prev => ({ ...prev, [upid]: [] }));
      return;
    }
    const norm = normDraftName(q);
    const out = [];
    for(const pid in sleeperPlayersData){
      const p = sleeperPlayersData[pid];
      if(!p) continue;
      const fn = p.full_name || `${p.first_name||''} ${p.last_name||''}`.trim();
      if(!fn) continue;
      if(normDraftName(fn).includes(norm)){
        out.push({ pid, name: fn, pos: p.position||'', team: p.team||'' });
        if(out.length >= 8) break;
      }
    }
    setNeedsReviewMatches(prev => ({ ...prev, [upid]: out }));
  };

  const assignManual = async (upid, pid) => {
    const { error } = await sb.from('underdog_sleeper_map').upsert({
      underdog_player_id: upid, sleeper_player_id: pid, match_confidence: 'manual',
    }, { onConflict: 'underdog_player_id' });
    if(error){ setMsg('Assign failed: ' + error.message); return; }
    await refresh();
    setNeedsReviewSearch(prev => { const x = {...prev}; delete x[upid]; return x; });
    setNeedsReviewMatches(prev => { const x = {...prev}; delete x[upid]; return x; });
  };

  return (
    <div style={{background:'var(--bg-page)',minHeight:'100vh',color:'var(--text-primary)',fontFamily:"'Inter','Segoe UI',sans-serif"}}>
      <MasterToolbar/>
      <div style={{maxWidth:1000,margin:'0 auto',padding:'24px 16px 60px'}}>
        <div style={{marginBottom:18,display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
          <h1 style={{fontSize:22,fontWeight:900,color:'var(--accent-gold-text)',letterSpacing:1.5,flex:1}}>🛠 Admin · Underdog ADP</h1>
        </div>

        <div style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:12,padding:16,marginBottom:18}} data-pp-last-scrape-card="1">
          <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:10,flexWrap:'wrap'}}>
            <div style={{fontSize:13,fontWeight:800,color:'var(--text-bright)',flex:1,minWidth:120}}>Last scrape</div>
            <button onClick={handleRunNow} disabled={runningNow}
                    data-pp-run-now="1"
                    style={{padding:'10px 16px',minHeight:40,background:runningNow?'var(--bg-pill)':'var(--accent-gold)',color:runningNow?'var(--text-secondary)':'#000',border:'none',borderRadius:6,fontWeight:900,fontSize:12,cursor:runningNow?'wait':'pointer',letterSpacing:1}}>
              {runningNow ? 'RUNNING…' : 'RUN NOW'}
            </button>
          </div>
          {runMsg && <div style={{fontSize:12,color:'var(--text-secondary)',marginBottom:8}} data-pp-run-msg="1">{runMsg}</div>}
          {latestRun ? (
            <div style={{fontSize:12,color:'var(--text-secondary)',lineHeight:1.7}} data-pp-last-run="1">
              <div>When: <strong style={{color:'var(--text-bright)'}}>{new Date(latestRun.started_at).toLocaleString()}</strong></div>
              <div>Status: <strong style={{color: latestRun.status === 'success' ? 'var(--success)' : latestRun.status === 'failed' ? 'var(--error-strong)' : 'var(--warning)'}}>{(latestRun.status||'').toUpperCase()}</strong></div>
              <div>Scraped: <strong style={{color:'var(--text-bright)'}}>{latestRun.rows_scraped}</strong> · Matched: <strong style={{color:'var(--success)'}}>{latestRun.rows_matched}</strong> · Needs review: <strong style={{color:'var(--warning)'}}>{latestRun.rows_unmatched}</strong> · History: <strong style={{color:'var(--accent-gold-text)'}}>{latestRun.rows_history_logged}</strong></div>
              {latestRun.error_message && <div style={{color:'var(--error-strong)',marginTop:6,wordBreak:'break-word'}}>Error: {latestRun.error_message}</div>}
            </div>
          ) : (
            <div style={{fontSize:12,color:'var(--text-secondary)'}}>No scrape has run yet.</div>
          )}
        </div>

        <div style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:12,padding:16,marginBottom:18}}>
          <div style={{fontSize:13,fontWeight:800,color:'var(--text-bright)',marginBottom:8}}>CSV upload</div>
          <div style={{fontSize:12,color:'var(--text-secondary)',marginBottom:10,lineHeight:1.5}}>
            Header row required: <code style={{color:'var(--accent-gold-text)'}}>adp,name,position,team,underdog_player_id</code> (last column optional — auto-generated if blank).
          </div>
          <textarea value={csvText} onChange={e=>setCsvText(e.target.value)}
                    placeholder="adp,name,position,team,underdog_player_id&#10;1.2,Ja'Marr Chase,WR,CIN,&#10;2.4,Bijan Robinson,RB,ATL,"
                    data-pp-csv-input="1"
                    style={{width:'100%',minHeight:180,padding:10,background:'var(--bg-input)',border:'1px solid var(--border-strong)',borderRadius:6,color:'var(--text-bright)',fontFamily:'monospace',fontSize:12,resize:'vertical'}}/>
          <div style={{marginTop:10,display:'flex',gap:10,alignItems:'center'}}>
            <button onClick={handleParse} disabled={busy}
                    data-pp-csv-submit="1"
                    style={{padding:'8px 16px',background:'var(--accent-gold)',color:'#000',border:'none',borderRadius:6,fontWeight:900,fontSize:12,cursor:busy?'wait':'pointer',letterSpacing:1}}>
              {busy ? 'WORKING…' : 'PARSE + UPLOAD'}
            </button>
            {msg && <span style={{fontSize:12,color:'var(--text-secondary)'}}>{msg}</span>}
          </div>
        </div>

        <div style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:12,padding:16,marginBottom:18}}>
          <div style={{fontSize:13,fontWeight:800,color:'var(--text-bright)',marginBottom:8}}>Database state</div>
          <div style={{fontSize:12,color:'var(--text-secondary)'}}>
            ADP rows: <strong style={{color:'var(--text-bright)'}}>{adp.length}</strong> · Auto-matched: <strong style={{color:'var(--success)'}}>{map.filter(m=>m.match_confidence==='auto').length}</strong> · Manual: <strong style={{color:'var(--accent-gold-text)'}}>{map.filter(m=>m.match_confidence==='manual').length}</strong> · Needs review: <strong style={{color:'var(--warning)'}}>{map.filter(m=>m.match_confidence==='needs_review').length}</strong>
          </div>
        </div>

        <div style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:12,padding:16}}>
          <div style={{fontSize:13,fontWeight:800,color:'var(--text-bright)',marginBottom:10}}>Needs review queue ({needsReview.length})</div>
          {needsReview.length === 0 && <div style={{fontSize:12,color:'var(--text-secondary)',padding:'10px 0'}}>Nothing to review — all entries matched or manually assigned.</div>}
          {needsReview.map(m => {
            const ud = adpById[m.underdog_player_id];
            const matches = needsReviewMatches[m.underdog_player_id] || [];
            return (
              <div key={m.underdog_player_id} style={{padding:'10px 0',borderBottom:'1px solid var(--border-hairline)'}}>
                <div style={{fontSize:13,color:'var(--text-primary)',fontWeight:700}}>{ud.name} <span style={{color:'var(--text-muted)',fontSize:11,fontWeight:500}}>{ud.position} · {ud.team} · ADP {ud.adp}</span></div>
                <div style={{display:'flex',gap:6,marginTop:6,flexWrap:'wrap'}}>
                  <input value={needsReviewSearch[m.underdog_player_id]||''}
                         onChange={e=>searchSleeperPlayers(m.underdog_player_id, e.target.value)}
                         placeholder="Search Sleeper player…"
                         style={{flex:1,minWidth:200,padding:6,background:'var(--bg-input)',border:'1px solid var(--border-strong)',borderRadius:4,color:'var(--text-bright)',fontSize:12}}/>
                </div>
                {matches.length > 0 && (
                  <div style={{marginTop:6,display:'flex',flexDirection:'column',gap:4}}>
                    {matches.map(c => (
                      <button key={c.pid} onClick={()=>assignManual(m.underdog_player_id, c.pid)}
                              style={{textAlign:'left',padding:'5px 8px',background:'var(--bg-elevated)',border:'1px solid var(--border)',borderRadius:4,color:'var(--text-primary)',fontSize:12,cursor:'pointer'}}>
                        {c.name} <span style={{color:'var(--text-muted)'}}>{c.pos} · {c.team || '?'} · #{c.pid}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <PFKFooter/>
    </div>
  );
}
function AdminUnderdogAdp(){
  return <AdminGate><AdminUnderdogAdpInner/></AdminGate>;
}


// ============================================================================
// 2027 ROOKIE BOARD — admin tool at /admin/rookie-board-2027 + data layer that
// Pick Projector reads to attach a rookie chip to each projected pick 1.01–1.12.
//
// One-time Supabase setup (paste into the SQL editor as Evan, then create the
// Storage bucket via the Dashboard — both linked in the comment below). The
// tables follow the same RLS pattern as underdog_adp / pick_projector_settings:
// public read, Evan-only write.
//
//   CREATE TABLE rookie_board_2027 (
//     id          uuid PRIMARY KEY DEFAULT gen_random_uuid(),
//     state       text NOT NULL UNIQUE CHECK (state IN ('draft','published')),
//     rookies     jsonb NOT NULL DEFAULT '[]'::jsonb,
//     updated_by  text,
//     updated_at  timestamptz NOT NULL DEFAULT now()
//   );
//   ALTER TABLE rookie_board_2027 ENABLE ROW LEVEL SECURITY;
//   CREATE POLICY "Public read" ON rookie_board_2027 FOR SELECT USING (true);
//   CREATE POLICY "Evan write"  ON rookie_board_2027 FOR ALL
//     USING (auth.email() = 'ejohnson2621@gmail.com')
//     WITH CHECK (auth.email() = 'ejohnson2621@gmail.com');
//   INSERT INTO rookie_board_2027 (state, rookies) VALUES ('draft','[]'::jsonb), ('published','[]'::jsonb);
//
//   CREATE TABLE rookie_board_2027_history (
//     id           bigserial PRIMARY KEY,
//     rookies      jsonb NOT NULL,
//     published_by text,
//     published_at timestamptz NOT NULL DEFAULT now()
//   );
//   ALTER TABLE rookie_board_2027_history ENABLE ROW LEVEL SECURITY;
//   CREATE POLICY "Public read"  ON rookie_board_2027_history FOR SELECT USING (true);
//   CREATE POLICY "Evan insert"  ON rookie_board_2027_history FOR INSERT
//     WITH CHECK (auth.email() = 'ejohnson2621@gmail.com');
//
//   Supabase Storage → New bucket: `rookie-photos-2027`, mark as PUBLIC.
//   Default policies (authenticated insert, public read) are fine.
// ============================================================================

const ROOKIE_BOARD_TABLE = 'rookie_board_2027';
const ROOKIE_BOARD_HISTORY_TABLE = 'rookie_board_2027_history';
const ROOKIE_PHOTO_BUCKET = 'rookie-photos-2027';
const ROOKIE_POSITIONS = ['QB','RB','WR','TE'];
// Note: an earlier ROOKIE_DISPLAY_CAP constant (24) used to clip the admin
// list to the top 24 rows behind a "+ Show N more" button. Removed 2026-05-20
// — Evan ranks 26+ rookies and never wanted the bottom of the list buried
// behind a click. Now all rookies render unconditionally.
const ROOKIE_PHOTO_MAX_WIDTH = 400;

const fetchRookieBoardRow = async (state) => {
  if(!sb) return null;
  try{
    const { data, error } = await sb.from(ROOKIE_BOARD_TABLE).select('*').eq('state', state).maybeSingle();
    if(error) return null;
    return data || null;
  }catch{ return null; }
};
const fetchPublishedRookies2027 = async () => {
  const row = await fetchRookieBoardRow('published');
  return Array.isArray(row?.rookies) ? row.rookies : [];
};

const saveDraftRookies2027 = async (rookies) => {
  if(!sb) return { error: { message: 'Supabase not loaded' } };
  const { data: userData } = await sb.auth.getUser();
  const email = userData?.user?.email || 'PFK Staff';
  const { data: existing } = await sb.from(ROOKIE_BOARD_TABLE).select('id').eq('state', 'draft').maybeSingle();
  if(existing){
    const { error } = await sb.from(ROOKIE_BOARD_TABLE)
      .update({ rookies, updated_by: email, updated_at: new Date().toISOString() })
      .eq('id', existing.id);
    return { error };
  }
  const { error } = await sb.from(ROOKIE_BOARD_TABLE).insert({ state: 'draft', rookies, updated_by: email });
  return { error };
};

const publishRookies2027 = async (rookies) => {
  // Copy draft → published, then append a history snapshot. History errors are
  // non-fatal for the user-facing flow but surfaced via the return value.
  if(!sb) return { error: { message: 'Supabase not loaded' } };
  const { data: userData } = await sb.auth.getUser();
  const email = userData?.user?.email || 'PFK Staff';
  const { data: existing } = await sb.from(ROOKIE_BOARD_TABLE).select('id').eq('state', 'published').maybeSingle();
  if(existing){
    const { error } = await sb.from(ROOKIE_BOARD_TABLE)
      .update({ rookies, updated_by: email, updated_at: new Date().toISOString() })
      .eq('id', existing.id);
    if(error) return { error };
  } else {
    const { error } = await sb.from(ROOKIE_BOARD_TABLE).insert({ state: 'published', rookies, updated_by: email });
    if(error) return { error };
  }
  const { error: histErr } = await sb.from(ROOKIE_BOARD_HISTORY_TABLE).insert({ rookies, published_by: email });
  return { error: histErr };
};

// ----- Pure helpers (exposed on window for the Playwright pure spec) --------

const rbNewId = () => 'rk_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);

const rbReorder = (arr, from, to) => {
  if(!Array.isArray(arr)) return [];
  if(from < 0 || from >= arr.length || to < 0 || to >= arr.length) return arr.slice();
  const next = arr.slice();
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

const rbNormalizeRookie = (raw, idx, errors) => {
  if(!raw || typeof raw !== 'object'){
    errors.push(`Row ${idx+1}: invalid shape`);
    return null;
  }
  const name = (raw.name || '').toString().trim();
  const school = (raw.school || '').toString().trim();
  const position = (raw.position || '').toString().toUpperCase().trim();
  if(!name){ errors.push(`Row ${idx+1}: missing name`); return null; }
  if(!ROOKIE_POSITIONS.includes(position)){
    errors.push(`Row ${idx+1} (${name}): position must be one of ${ROOKIE_POSITIONS.join('/')}`);
    return null;
  }
  return {
    id: rbNewId(),
    name,
    school,
    position,
    photo_url: typeof raw.photo_url === 'string' ? raw.photo_url : '',
  };
};

// Parse bulk-import text. Accepts a JSON array of {name,school,position} OR a
// CSV/TSV with optional header. Returns { rookies, errors }; errors collects
// per-row problems instead of aborting the whole import.
const rbParseBulk = (text) => {
  const errors = [];
  const out = [];
  if(typeof text !== 'string') return { rookies: out, errors: ['Input must be a string'] };
  const trimmed = text.trim();
  if(!trimmed) return { rookies: out, errors: [] };
  if(trimmed.startsWith('[') || trimmed.startsWith('{')){
    try{
      const parsed = JSON.parse(trimmed);
      const arr = Array.isArray(parsed) ? parsed : [parsed];
      arr.forEach((item, idx) => { const r = rbNormalizeRookie(item, idx, errors); if(r) out.push(r); });
      return { rookies: out, errors };
    }catch(e){
      errors.push('JSON parse error: ' + e.message);
      return { rookies: out, errors };
    }
  }
  const splitCsv = (line) => {
    const cells = []; let cur = ''; let inQ = false;
    for(let i = 0; i < line.length; i++){
      const ch = line[i];
      if(ch === '"'){ inQ = !inQ; continue; }
      if((ch === ',' || ch === '\t') && !inQ){ cells.push(cur.trim()); cur = ''; continue; }
      cur += ch;
    }
    cells.push(cur.trim());
    return cells;
  };
  const lines = trimmed.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  let headerMap;
  const firstCells = splitCsv(lines[0]).map(c => c.toLowerCase());
  if(firstCells.includes('name')){
    headerMap = {};
    firstCells.forEach((c, i) => { headerMap[c] = i; });
    lines.shift();
  } else {
    headerMap = { name: 0, school: 1, position: 2 };
  }
  lines.forEach((line, idx) => {
    const cells = splitCsv(line);
    const raw = {
      name:     cells[headerMap.name],
      school:   cells[headerMap.school],
      position: cells[headerMap.position],
    };
    const r = rbNormalizeRookie(raw, idx, errors);
    if(r) out.push(r);
  });
  return { rookies: out, errors };
};

// Client-side resize: File → canvas (max 400px wide) → JPEG blob. Keeps photos
// small without depending on a server-side resize step.
const rbResizePhoto = (file, maxWidth) => new Promise((resolve, reject) => {
  if(!file) return reject(new Error('No file provided'));
  const max = parseInt(maxWidth, 10) || ROOKIE_PHOTO_MAX_WIDTH;
  const reader = new FileReader();
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      const scale = img.width > max ? max / img.width : 1;
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('Canvas toBlob returned null')), 'image/jpeg', 0.88);
    };
    img.onerror = () => reject(new Error('Image decode failed'));
    img.src = reader.result;
  };
  reader.onerror = () => reject(new Error('FileReader failed'));
  reader.readAsDataURL(file);
});

const rbUploadPhoto = async (rookieId, file) => {
  if(!sb) return { error: 'Supabase not loaded' };
  if(!file) return { error: 'No file selected' };
  let blob;
  try{ blob = await rbResizePhoto(file, ROOKIE_PHOTO_MAX_WIDTH); }
  catch(e){ return { error: 'Resize failed: ' + e.message }; }
  const extMatch = (file.name || '').toLowerCase().match(/\.(jpe?g|png|webp|gif)$/);
  const ext = extMatch ? extMatch[1].replace('jpeg','jpg') : 'jpg';
  const filename = `${rookieId}-${Date.now()}.${ext}`;
  const { error: upErr } = await sb.storage.from(ROOKIE_PHOTO_BUCKET).upload(filename, blob, {
    cacheControl: '3600', upsert: false, contentType: blob.type || 'image/jpeg',
  });
  if(upErr) return { error: upErr.message || String(upErr) };
  const { data: urlData } = sb.storage.from(ROOKIE_PHOTO_BUCKET).getPublicUrl(filename);
  return { url: urlData?.publicUrl };
};

if(typeof window !== 'undefined'){
  window.PFK_RB_REORDER = rbReorder;
  window.PFK_RB_PARSE_BULK = rbParseBulk;
}

// ----- Admin component ------------------------------------------------------

function AdminRookieBoard2027Inner(){
  const [draft, setDraft] = useState([]);
  const [published, setPublished] = useState([]);
  const [publishedAt, setPublishedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkErrors, setBulkErrors] = useState([]);
  // showAll/ROOKIE_DISPLAY_CAP removed 2026-05-20 — admin always shows the
  // full draft list. No state needed.
  const [dragIdx, setDragIdx] = useState(null);
  // Active tab — 'rookies' (default) or 'projections'. Initialized from
  // URL hash on mount; writing the hash from setTab() keeps the URL in
  // sync. No hash = rookies tab. Unknown hash falls through to default.
  // Phase 5: legacy 'overrides' tab removed (universal value overrides
  // are conceptually broken in projections-engine land — see Phase 6+
  // "Projection override capability" for the per-stat replacement).
  const [activeTab, setActiveTab] = useState(() => {
    if(typeof window === 'undefined') return 'rookies';
    const h = (window.location.hash || '').replace(/^#/, '').toLowerCase();
    if(h === 'projections') return 'projections';
    return 'rookies';
  });
  const switchTab = (next) => {
    setActiveTab(next);
    if(typeof window === 'undefined') return;
    // Hash mirrors the tab key, except 'rookies' is the default and gets a
    // clean URL with no hash.
    const newHash = next === 'rookies' ? '' : '#' + next;
    const url = window.location.pathname + window.location.search + newHash;
    window.history.replaceState(null, '', url);
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      const [d, p] = await Promise.all([fetchRookieBoardRow('draft'), fetchRookieBoardRow('published')]);
      if(!alive) return;
      setDraft(Array.isArray(d?.rookies) ? d.rookies : []);
      setPublished(Array.isArray(p?.rookies) ? p.rookies : []);
      setPublishedAt(p?.updated_at || null);
      setLoading(false);
    })();
    return () => { alive = false; };
  }, []);

  const persist = async (next) => {
    setBusy(true); setMsg('Saving draft…');
    const { error } = await saveDraftRookies2027(next);
    setBusy(false);
    if(error){ setMsg('Save error: ' + (error.message || error)); return false; }
    setMsg('Draft saved');
    setTimeout(() => setMsg(m => m === 'Draft saved' ? '' : m), 1500);
    return true;
  };

  const onAddBlank = async () => {
    const next = [...draft, { id: rbNewId(), name: '', school: '', position: 'WR', photo_url: '' }];
    setDraft(next);
    await persist(next);
  };

  const onChangeField = (idx, field, value) => {
    setDraft(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };
  const onCommitField = async () => { await persist(draft); };

  const onDelete = async (idx) => {
    const next = draft.filter((_, i) => i !== idx);
    setDraft(next);
    await persist(next);
  };

  const onReorder = async (from, to) => {
    if(from === to || from == null || to == null) return;
    const next = rbReorder(draft, from, to);
    setDraft(next);
    await persist(next);
  };

  const onUploadPhoto = async (idx, file) => {
    if(!file) return;
    const rookie = draft[idx];
    setBusy(true); setMsg(`Uploading photo for ${rookie.name || 'rookie'}…`);
    const { url, error } = await rbUploadPhoto(rookie.id, file);
    setBusy(false);
    if(error){ setMsg('Upload error: ' + error); return; }
    const next = draft.map((r, i) => i === idx ? { ...r, photo_url: url } : r);
    setDraft(next);
    await persist(next);
  };

  // Paste-a-URL path. Skips the upload + rbResizePhoto pipeline entirely so
  // pre-cut sources like ESPN's player headshots (a.espncdn.com — already
  // transparent PNGs) end up on the public projector verbatim, no
  // alpha-flatten round-trip. Accepts any direct http(s) image URL.
  const onPastePhotoUrl = async (idx) => {
    const rookie = draft[idx];
    const current = rookie?.photo_url || '';
    const url = window.prompt(
      'Paste a direct image URL (e.g. ESPN headshot: https://a.espncdn.com/i/headshots/college-football/players/full/12345.png).\n\nLeave blank and OK to remove the current photo.',
      current
    );
    if(url === null) return; // user cancelled
    const trimmed = url.trim();
    // Allow empty string to clear the photo, otherwise require http(s)
    if(trimmed && !/^https?:\/\//i.test(trimmed)){
      setMsg('Photo URL must start with http:// or https://');
      setTimeout(() => setMsg(m => m && m.startsWith('Photo URL') ? '' : m), 2500);
      return;
    }
    const next = draft.map((r, i) => i === idx ? { ...r, photo_url: trimmed } : r);
    setDraft(next);
    setMsg(trimmed ? `Saved URL for ${rookie.name || 'rookie'}` : `Cleared photo for ${rookie.name || 'rookie'}`);
    await persist(next);
  };

  const onBulkImport = async () => {
    const { rookies, errors } = rbParseBulk(bulkText);
    setBulkErrors(errors);
    if(!rookies.length) return;
    if(!window.confirm(`Replace current draft (${draft.length} rookies) with ${rookies.length} parsed rows?`)) return;
    setDraft(rookies);
    setBulkText('');
    await persist(rookies);
  };

  const onSubmit = async () => {
    const topN = Math.min(12, draft.length);
    if(!window.confirm(`Publish all ${draft.length} ranked rookies? Top ${topN} will surface on Pick Projector.`)) return;
    setBusy(true); setMsg('Publishing…');
    const { error } = await publishRookies2027(draft);
    setBusy(false);
    if(error){ setMsg('Publish error: ' + (error.message || error)); return; }
    setPublished(draft.slice());
    setPublishedAt(new Date().toISOString());
    setMsg('Published');
    setTimeout(() => setMsg(m => m === 'Published' ? '' : m), 2000);
  };

  const onRevertDraft = async () => {
    if(!published.length){ return; }
    if(!window.confirm('Discard the current draft and revert to the last published board?')) return;
    setDraft(published.slice());
    await persist(published.slice());
  };

  if(loading) return <div style={{padding:40,textAlign:'center',color:'var(--text-secondary)'}}>Loading…</div>;

  // The full draft list always renders — no caps, no "show more" toggle.

  return (
    <div style={{background:'var(--bg-page)',minHeight:'100vh',color:'var(--text-primary)',fontFamily:"'Inter','Segoe UI',sans-serif"}}>
      <MasterToolbar/>
      <div style={{maxWidth:1100,margin:'0 auto',padding:'24px 16px 60px'}} data-rb-admin="1">
        <div style={{marginBottom:18,display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
          <h1 style={{fontSize:22,fontWeight:900,color:'var(--accent-gold-text)',letterSpacing:1.5,flex:1}}>👑 Admin · 2027 Rookie Board</h1>
        </div>

        {/* Tab switcher — same pill-style pattern as App's pfk/custom toggle
            (js/app.js:3949). Active tab persists via the URL hash so a page
            refresh keeps you on the same view. Both tab bodies stay mounted
            (display:none on the inactive one) so React state inside each is
            preserved across switches. */}
        <div style={{display:'inline-flex',gap:4,padding:4,background:'var(--bg-elevated)',border:'1px solid var(--border)',borderRadius:8,marginBottom:14}} data-rb-tabs="1">
          <button onClick={()=>switchTab('rookies')}
                  data-rb-tab="rookies"
                  data-rb-tab-active={activeTab==='rookies' ? '1' : '0'}
                  style={{padding:'6px 14px',borderRadius:5,border:'none',background:activeTab==='rookies'?'var(--accent-gold)':'transparent',color:activeTab==='rookies'?'#000':'var(--text-muted)',fontWeight:800,fontSize:12,cursor:'pointer',letterSpacing:0.5}}>👑 Rookie Board</button>
          <button onClick={()=>switchTab('projections')}
                  data-rb-tab="projections"
                  data-rb-tab-active={activeTab==='projections' ? '1' : '0'}
                  style={{padding:'6px 14px',borderRadius:5,border:'none',background:activeTab==='projections'?'var(--accent-gold)':'transparent',color:activeTab==='projections'?'#000':'var(--text-muted)',fontWeight:800,fontSize:12,cursor:'pointer',letterSpacing:0.5}}>📊 Projections</button>
        </div>

        {/* TAB 1 — Rookie Board (original content) */}
        <div style={{display:activeTab==='rookies'?'block':'none'}} data-rb-tab-panel="rookies">
        <div style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:12,padding:14,marginBottom:14,fontSize:13,color:'var(--text-secondary)',lineHeight:1.55}}>
          The top <strong>12</strong> ranked rookies surface on Pick Projector next to projected picks 1.01–1.12. Rookies ranked 13+ stay here for your planning. Drag the ⋮⋮ handle to reorder. Field edits autosave to the draft; nothing surfaces publicly until you hit <strong>SUBMIT</strong>.
          <div style={{marginTop:6,fontSize:11,color:'var(--text-muted)'}}>
            <strong>📷 file</strong> uploads + resizes to our bucket (may flatten alpha). <strong>🔗 URL</strong> just stores the link — best for ESPN headshots (<code>a.espncdn.com</code>, already transparent).
          </div>
          {publishedAt && <div style={{marginTop:6,fontSize:11,color:'var(--text-muted)'}} data-rb-published-at="1">Last published {new Date(publishedAt).toLocaleString()} · {published.length} rookies</div>}
        </div>

        <details style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:12,padding:14,marginBottom:14}}>
          <summary style={{cursor:'pointer',fontSize:13,fontWeight:800,color:'var(--text-bright)',letterSpacing:0.5}}>📥 Bulk import (CSV / JSON)</summary>
          <div style={{marginTop:10,fontSize:12,color:'var(--text-secondary)',lineHeight:1.55,marginBottom:8}}>
            Paste rows like <code>Name,School,POS</code> (one per line) or a JSON array of <code>{`{name,school,position}`}</code>. Position must be QB/RB/WR/TE. Replaces the entire current draft.
          </div>
          <textarea value={bulkText} onChange={e=>setBulkText(e.target.value)}
                    data-rb-bulk-input="1"
                    placeholder={`name,school,position\nDrake Maye,UNC,QB\nKaleb Johnson,Iowa,RB`}
                    style={{width:'100%',minHeight:120,padding:10,background:'var(--bg-input)',border:'1px solid var(--border-strong)',borderRadius:6,color:'var(--text-bright)',fontFamily:'monospace',fontSize:12,resize:'vertical'}}/>
          <div style={{marginTop:8,display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
            <button onClick={onBulkImport} data-rb-bulk-import="1"
                    style={{padding:'7px 14px',background:'var(--accent-violet)',color:'#fff',border:'none',borderRadius:6,fontWeight:800,fontSize:12,cursor:'pointer',letterSpacing:0.5}}>
              REPLACE DRAFT
            </button>
            {bulkErrors.length > 0 && (
              <span style={{fontSize:11,color:'var(--danger)'}} data-rb-bulk-errors="1">{bulkErrors.length} parse errors</span>
            )}
          </div>
          {bulkErrors.length > 0 && (
            <ul style={{marginTop:8,paddingLeft:18,fontSize:11,color:'var(--danger)'}}>
              {bulkErrors.slice(0,8).map((e,i)=><li key={i}>{e}</li>)}
            </ul>
          )}
        </details>

        <div style={{display:'flex',flexDirection:'column',gap:6,marginBottom:14}}>
          {draft.map((r, idx) => {
            const isTop12 = idx < 12;
            const handleDragStart = () => setDragIdx(idx);
            const handleDragOver = (e) => { e.preventDefault(); };
            const handleDrop = () => { if(dragIdx != null) onReorder(dragIdx, idx); setDragIdx(null); };
            return (
              <div key={r.id} data-rb-row={idx} data-rb-rookie-id={r.id}
                   onDragOver={handleDragOver} onDrop={handleDrop}
                   style={{display:'flex',gap:8,alignItems:'center',padding:8,background:isTop12?'var(--bg-card)':'var(--bg-elevated)',border:'1px solid '+(isTop12?'var(--border)':'var(--border-hairline)'),borderRadius:8,opacity:isTop12?1:0.85}}>
                <div draggable onDragStart={handleDragStart} onDragEnd={()=>setDragIdx(null)}
                     title="Drag to reorder" data-rb-drag-handle="1"
                     style={{cursor:'grab',padding:'4px 6px',color:'var(--text-muted)',fontWeight:900,userSelect:'none',fontSize:14}}>⋮⋮</div>
                <div style={{minWidth:30,textAlign:'center',fontSize:14,fontWeight:900,color:isTop12?'var(--accent-gold-text)':'var(--text-muted)'}} data-rb-rank="1">{idx+1}</div>
                <div style={{width:42,height:42,borderRadius:6,background:'var(--bg-pill)',overflow:'hidden',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center'}}>
                  {r.photo_url ? <img src={r.photo_url} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}} onError={e=>{ e.target.style.display='none'; }}/> : <span style={{fontSize:18,color:'var(--text-muted)'}}>?</span>}
                </div>
                <input value={r.name||''} onChange={e=>onChangeField(idx,'name',e.target.value)} onBlur={onCommitField}
                       placeholder="Name" data-rb-field-name="1"
                       style={{flex:2,minWidth:120,padding:6,background:'var(--bg-input)',border:'1px solid var(--border-strong)',borderRadius:5,color:'var(--text-bright)',fontSize:13}}/>
                <input value={r.school||''} onChange={e=>onChangeField(idx,'school',e.target.value)} onBlur={onCommitField}
                       placeholder="School" data-rb-field-school="1"
                       style={{flex:2,minWidth:100,padding:6,background:'var(--bg-input)',border:'1px solid var(--border-strong)',borderRadius:5,color:'var(--text-bright)',fontSize:13}}/>
                <select value={r.position||'WR'} onChange={e=>{ onChangeField(idx,'position',e.target.value); }} onBlur={onCommitField}
                        data-rb-field-position="1"
                        style={{padding:6,background:'var(--bg-input)',border:'1px solid var(--border-strong)',borderRadius:5,color:'var(--text-bright)',fontSize:13,fontWeight:700}}>
                  {ROOKIE_POSITIONS.map(p=><option key={p} value={p}>{p}</option>)}
                </select>
                <label data-rb-photo-upload="1" title="Upload a file"
                       style={{padding:'5px 8px',background:'transparent',border:'1px solid var(--border-outline)',borderRadius:5,color:'var(--text-secondary)',fontSize:11,fontWeight:700,cursor:'pointer'}}>
                  📷
                  <input type="file" accept="image/*" style={{display:'none'}} onChange={e=>onUploadPhoto(idx, e.target.files?.[0])}/>
                </label>
                {/* Paste-a-URL button — preferred path for ESPN/Sleeper
                    headshots so transparent PNGs reach the projector
                    unmodified (no rbResizePhoto alpha-flatten). */}
                <button onClick={()=>onPastePhotoUrl(idx)} data-rb-photo-url="1"
                        title="Paste an image URL (ESPN, Sleeper, etc.)"
                        style={{padding:'5px 8px',background:'transparent',border:'1px solid var(--border-outline)',borderRadius:5,color:'var(--text-secondary)',fontSize:11,fontWeight:700,cursor:'pointer'}}>
                  🔗
                </button>
                <button onClick={()=>onDelete(idx)} data-rb-delete="1" title="Remove"
                        style={{padding:'5px 8px',background:'transparent',border:'1px solid var(--danger)',borderRadius:5,color:'var(--danger)',fontSize:11,fontWeight:800,cursor:'pointer'}}>×</button>
              </div>
            );
          })}
        </div>


        <div style={{display:'flex',gap:10,flexWrap:'wrap',marginBottom:14,alignItems:'center'}}>
          <button onClick={onAddBlank} data-rb-add="1"
                  style={{padding:'8px 14px',background:'transparent',border:'1px solid var(--accent-gold)',borderRadius:6,color:'var(--accent-gold-text)',fontWeight:800,fontSize:12,cursor:'pointer',letterSpacing:0.5}}>
            + ADD ROOKIE
          </button>
          <button onClick={onRevertDraft} data-rb-revert="1" disabled={!published.length}
                  style={{padding:'8px 14px',background:'transparent',border:'1px solid var(--border-outline)',borderRadius:6,color:'var(--text-secondary)',fontWeight:700,fontSize:12,cursor:published.length?'pointer':'not-allowed',letterSpacing:0.5,opacity:published.length?1:0.5}}>
            ⤺ Revert draft to published
          </button>
          <div style={{flex:1}}/>
          <button onClick={onSubmit} disabled={busy} data-rb-submit="1"
                  style={{padding:'10px 22px',background:'var(--accent-gold)',color:'#000',border:'none',borderRadius:6,fontWeight:900,fontSize:13,cursor:busy?'wait':'pointer',letterSpacing:1.2}}>
            🚀 SUBMIT — PUBLISH TOP 12
          </button>
        </div>

        {msg && <div data-rb-msg="1" style={{padding:'8px 12px',background:'var(--bg-elevated)',border:'1px solid var(--border)',borderRadius:8,fontSize:12,color:msg.toLowerCase().includes('error')?'var(--danger)':'var(--text-secondary)'}}>{msg}</div>}
        </div>{/* end TAB 1 panel */}

        {/* TAB 2 — Projections (Phase 1 FFA-based projection upload + display).
            Lands here as the Session 3 build of the rebuild plan; eventually
            feeds the in-PP scoring swap in Phase 3. Inner component renders
            no page chrome — parent provides MasterToolbar / PFKFooter. */}
        <div style={{display:activeTab==='projections'?'block':'none'}} data-rb-tab-panel="projections">
          <AdminProjectionsInner/>
        </div>
      </div>
      <PFKFooter/>
    </div>
  );
}
function AdminRookieBoard2027(){
  return <AdminGate><AdminRookieBoard2027Inner/></AdminGate>;
}

// =============================================================================
// AdminProjectionsInner — Phase 1 FFA-based projection upload + display tab
// inside /admin/rookie-board-2027 (URL hash #projections). Reads the freshest
// (season, period) group from player_projections (table populated by the
// CSV upload flow added in Session 3 Step 4). Renders metadata strip + source-
// count histogram + paginated/sortable/searchable table. See
// PICK_PROJECTOR_REBUILD_PHASE_0.md § Data Pipeline for the broader design.
// =============================================================================

// Format a stat value for table display. Rounds to 0 decimals; returns "—"
// for missing/zero. Lives outside the component so it doesn't re-bind per render.
function ppProjFormatStat(stats, key){
  const v = stats && stats[key];
  if(v == null || v === 0 || v === '0') return '—';
  const n = parseFloat(v);
  if(!isFinite(n) || n === 0) return '—';
  if(n >= 100) return Math.round(n).toLocaleString();
  return n.toFixed(0);
}

// JS port of R's normalize_name() (~/pfk_projections/lib/normalize.R). Used by
// the projections upload to derive ffa_name_normalized when the CSV row
// doesn't already carry it. MUST stay byte-identical to the R version —
// player_id_overrides queries by this key, and a JS/R divergence would
// silently break manual overrides.
function ppNormalizeName(s){
  if(s == null) return '';
  let r = String(s);
  // 1. Curly apostrophes -> straight (R does the same first)
  r = r.replace(/[\u2018\u2019\u02BC]/g, "'");
  // 2. Strip diacritics: NFD-normalize, drop combining marks
  r = r.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  // 3. Lowercase (after diacritic strip, matching R order)
  r = r.toLowerCase();
  // 4. Hyphens -> spaces
  r = r.replace(/-/g, ' ');
  // 5. Strip periods
  r = r.replace(/\./g, '');
  // 6. Strip suffix tokens (jr|sr|iii|iv|ii|v) at end, optionally with comma.
  //    Order matters: 'iii' before 'ii' so the longer match wins under PCRE
  //    leftmost-first semantics (matches the R regex ordering).
  r = r.replace(/\s*,?\s*(iii|iv|ii|jr|sr|v)\s*$/, '');
  // 7. Strip remaining punctuation — R uses POSIX [[:punct:]];
  //    this regex covers the same ASCII range.
  r = r.replace(/[!"#$%&'()*+,./:;<=>?@[\\\]^_`{|}~]/g, '');
  // 8. Collapse multiple internal spaces
  r = r.replace(/\s+/g, ' ');
  // 9. Trim leading/trailing whitespace
  return r.trim();
}

// Stat columns the upload pulls out of each CSV row into a JSONB blob.
// Mirrors CSV_STAT_COLS in ~/pfk_projections/lib/aggregate.R. Adding a column
// here means adding it on the R side too.
const PP_PROJ_STAT_COLUMNS = [
  'games',
  'pass_att', 'pass_comp', 'pass_yds', 'pass_tds', 'pass_int',
  'rush_att', 'rush_yds', 'rush_tds',
  'rec_tgt', 'rec', 'rec_yds', 'rec_tds',
  'fumbles_lost',
  'two_pts', 'rush_fd', 'rec_fd', 'kr_td', 'pr_td', 'fum_all',
  'fg', 'fg_att', 'fg_0019', 'fg_2029', 'fg_3039', 'fg_4049', 'fg_50',
  'xp', 'xp_att',
  'dst_int', 'dst_safety', 'dst_sacks', 'dst_fum_rec', 'dst_fum_force',
  'dst_td', 'dst_pts_allowed',
];

// Build the projected_stats JSONB object from a parsed CSV row. Missing /
// non-numeric values are dropped — the resulting object only contains
// { key: number } entries.
function ppProjBuildStats(row){
  const stats = {};
  for(const col of PP_PROJ_STAT_COLUMNS){
    const v = row[col];
    if(v == null || v === '') continue;
    const n = parseFloat(v);
    if(isFinite(n)) stats[col] = n;
  }
  return stats;
}

// Convert one parsed CSV row into the upsert/insert payloads for the two
// destination tables. Exactly one of (mapped, unmatched) is non-null based
// on whether the row carries a sleeper_player_id.
function ppProjRowToPayloads(row, updatedBy, nowIso){
  const stats = ppProjBuildStats(row);
  const season = parseInt(row.season, 10);
  const period = row.period || 'preseason';
  const sourceCount = parseInt(row.source_count, 10) || 0;
  const mapConf = (row.map_confidence != null && row.map_confidence !== '')
                  ? parseInt(row.map_confidence, 10) : null;
  const mapMeth = row.map_method || null;
  const generatedAt = row.generated_at || null;
  const team = row.team || null;
  const sid = (row.sleeper_player_id || '').trim();
  if(sid){
    return {
      mapped: {
        sleeper_player_id: sid,
        ffa_name: row.ffa_name,
        position: row.position,
        team: team,
        season: season,
        period: period,
        source_count: sourceCount,
        projected_stats: stats,
        map_confidence: mapConf,
        map_method: mapMeth,
        generated_at: generatedAt,
        updated_by: updatedBy,
        updated_at: nowIso,
      },
      unmatched: null,
    };
  }
  return {
    mapped: null,
    unmatched: {
      ffa_name: row.ffa_name,
      ffa_name_normalized: row.ffa_name_normalized || ppNormalizeName(row.ffa_name),
      ffa_position: row.position,
      ffa_team: team,
      source_count: sourceCount,
      projected_stats: stats,
      map_confidence: mapConf,
      map_method: mapMeth,
      generated_at: generatedAt,
      season: season,
      period: period,
      updated_by: updatedBy,
    },
  };
}

// ===========================================================================
// Phase 2: Scoring conversion engine
// ===========================================================================
// ppApplyLeagueScoring(projectedStats, scoringSettings, opts) → { points, warnings }
//
// Converts an FFA-derived projected_stats blob + a Sleeper league's
// scoring_settings into projected season fantasy points for one player.
//
// Inputs:
//   projectedStats  — the JSONB blob from player_projections.projected_stats
//                     (built by ppProjBuildStats from the R-pipeline CSV).
//   scoringSettings — league.scoring_settings as returned by Sleeper's API.
//   opts.position   — 'QB'|'RB'|'WR'|'TE'|'K'|'DEF' (gates TEP/PPR-by-pos
//                     bonuses, 2pt position split, PA-tier matching, etc.)
//   opts.games      — projected games this season (defaults to
//                     projectedStats.games or 17 if missing)
//
// Returns: { points: number, warnings: string[] }
//   warnings surfaces every scoring category we recognized but cannot model
//   (threshold bonuses, first-down bonuses, IDP, etc.) so the caller can
//   show transparency badges to users.
//
// Phase 2 scope (per PICK_PROJECTOR_REBUILD_PHASE_0.md):
//   - NOT called from Pick Projector yet (that's Phase 3).
//   - Unit-tested via tests/pick-projector-scoring.spec.js against the
//     deployed dev bundle (same pattern as pick-projector-pure.spec.js).
//   - Threshold bonuses (bonus_pass_yd_300, etc.) and long-TD bonuses
//     (pass_td_40p, etc.) deliberately NOT modeled — they need per-game
//     stat projections we don't have. See Phase 6+ deferred features.

// Direct Sleeper-key → FFA-stat-key mappings (multiply coefficient × stat).
// Position-agnostic at the function level; the FFA blob's natural shape
// self-gates (a QB blob has no dst_int, so `int * (stats.dst_int || 0)` = 0).
const PP_SCORING_DIRECT_MAP = {
  pass_yd:   'pass_yds',
  pass_td:   'pass_tds',
  pass_int:  'pass_int',
  pass_att:  'pass_att',
  pass_cmp:  'pass_comp',
  rush_yd:   'rush_yds',
  rush_td:   'rush_tds',
  rush_att:  'rush_att',
  rec:       'rec',
  rec_yd:    'rec_yds',
  rec_td:    'rec_tds',
  rec_tgt:   'rec_tgt',
  fum_lost:  'fumbles_lost',
  // DST (only meaningful when position === 'DEF', self-gated by blob shape)
  def_td:    'dst_td',
  int:       'dst_int',
  safe:      'dst_safety',
  sack:      'dst_sacks',
  fum_rec:   'dst_fum_rec',
  ff:        'dst_fum_force',
  // Kicker (only meaningful when position === 'K', self-gated by blob shape)
  xpm:       'xp',
  fgm:       'fg',
  fgm_0_19:  'fg_0019',
  fgm_20_29: 'fg_2029',
  fgm_30_39: 'fg_3039',
  fgm_40_49: 'fg_4049',
  fgm_50p:   'fg_50',
};

// Position-gated reception bonuses: bonus only fires when the player is the
// position the league configured the bonus for. bonus_rec_te is the most
// common (this is what kills the legacy TE rank curve — TEP gets baked in
// mathematically via per-position scoring rules, no curve-tuning needed).
const PP_SCORING_POSITION_GATED = {
  bonus_rec_te: { gate: 'TE', ffaKey: 'rec' },
  bonus_rec_rb: { gate: 'RB', ffaKey: 'rec' },
  bonus_rec_wr: { gate: 'WR', ffaKey: 'rec' },
};

// Derived keys: function takes the FFA blob and returns a synthesized value.
// pass_inc derives from pass_att - pass_comp; same for xpmiss/fgmiss.
const PP_SCORING_DERIVED = {
  pass_inc: (s) => Math.max(0, (s.pass_att || 0) - (s.pass_comp || 0)),
  xpmiss:   (s) => Math.max(0, (s.xp_att   || 0) - (s.xp       || 0)),
  fgmiss:   (s) => Math.max(0, (s.fg_att   || 0) - (s.fg       || 0)),
};

// DST points-allowed tier table (Sleeper's standard PA brackets). Estimator:
// season dst_pts_allowed / games = per-game average → match to tier → award
// games × coefficient. Imperfect (a team averaging 18 will sometimes give
// up 35), but symmetric across the league. Documented as such — phase 2 is
// the floor of DST modeling, not the ceiling.
const PP_SCORING_PA_TIERS = [
  [0,  0,         'pts_allow_0'],
  [1,  6,         'pts_allow_1_6'],
  [7,  13,        'pts_allow_7_13'],
  [14, 20,        'pts_allow_14_20'],
  [21, 27,        'pts_allow_21_27'],
  [28, 34,        'pts_allow_28_34'],
  [35, Infinity,  'pts_allow_35p'],
];

// Recognized-but-intentionally-unsupported keys. Each yields a warning when
// it appears with nonzero coefficient. Reasons documented per-key.
const PP_SCORING_UNSUPPORTED = {
  rush_fd:    'First-down bonus (rush_fd) not modeled — FFA does not project first-down counts. RBs slightly under-credited.',
  rec_fd:     'First-down bonus (rec_fd) not modeled — FFA does not project first-down counts. Pass-catchers slightly under-credited.',
  pass_fd:    'First-down bonus (pass_fd) not modeled — FFA does not project first-down counts. QBs slightly under-credited.',
  pass_sack:  'Sacks-taken (pass_sack) not in FFA projection schema. QBs slightly under-credited.',
  kr_td:      'Kick-return TDs (kr_td) not in FFA projection schema. Affected players slightly under-credited.',
  pr_td:      'Punt-return TDs (pr_td) not in FFA projection schema. Affected players slightly under-credited.',
  fum:        'All-fumbles (fum) not in FFA projection schema — Phase 2 falls back to fum_lost only. Minor under-count.',
  fum_rec_td: 'Offensive fumble-recovery TDs (fum_rec_td) not in FFA projection schema. Rare event, not modeled.',
  blk_kick:   'DST blocked-kicks (blk_kick) not in FFA projection schema. DSTs slightly under-credited.',
  def_st_td:  'Special-teams TDs (def_st_td) not separately projected — may overlap dst_td. Not modeled in Phase 2 to avoid double-count.',
  def_pr_td:  'Defensive punt-return TDs (def_pr_td) not in FFA projection schema. Rare event, not modeled.',
  def_kr_td:  'Defensive kick-return TDs (def_kr_td) not in FFA projection schema. Rare event, not modeled.',
};

// Threshold-bonus + long-TD pattern: deferred entirely from Phase 2 (per
// Phase 2 design Q1). Need per-game stat projections to model honestly;
// Phase 6+ will revisit when nflverse historical data is wired in. Until
// then, warnings array is the transparency mechanism.
function ppScoringIsThresholdBonus(key){
  // Yardage thresholds: bonus_pass_yd_300, bonus_rush_yd_100, bonus_rec_yd_200, etc.
  if(/^bonus_(pass|rush|rec)_(yd|att|cmp)_\d+/.test(key)) return true;
  // Long-TD bonuses: pass_td_40p, rush_td_50p, rec_td_40p, etc.
  if(/^(pass|rush|rec)_td_\d+p$/.test(key)) return true;
  return false;
}

// FFA gives a single combined two_pts total per player. Sleeper splits it
// into pass_2pt / rush_2pt / rec_2pt. Phase 2 design Q3: route the player's
// two_pts to whichever Sleeper key matches their primary position.
const PP_SCORING_TWO_PT_GATE = {
  pass_2pt: ['QB'],
  rush_2pt: ['RB'],
  rec_2pt:  ['WR', 'TE'],
};

function ppApplyLeagueScoring(projectedStats, scoringSettings, opts){
  const stats    = projectedStats || {};
  const settings = scoringSettings || {};
  const position = (opts && opts.position) ? String(opts.position).toUpperCase() : '';
  const games    = (opts && opts.games) || stats.games || 17;
  let points = 0;
  const warnings = [];

  for(const [key, rawCoef] of Object.entries(settings)){
    const coef = parseFloat(rawCoef);
    if(!isFinite(coef) || coef === 0) continue;

    if(PP_SCORING_DIRECT_MAP[key] != null){
      points += coef * (stats[PP_SCORING_DIRECT_MAP[key]] || 0);
      continue;
    }
    if(PP_SCORING_DERIVED[key]){
      points += coef * PP_SCORING_DERIVED[key](stats);
      continue;
    }
    if(PP_SCORING_POSITION_GATED[key]){
      const g = PP_SCORING_POSITION_GATED[key];
      if(position === g.gate) points += coef * (stats[g.ffaKey] || 0);
      continue;
    }
    if(PP_SCORING_TWO_PT_GATE[key]){
      if(PP_SCORING_TWO_PT_GATE[key].includes(position)){
        points += coef * (stats.two_pts || 0);
      }
      continue;
    }
    if(/^pts_allow_/.test(key)){
      if(position !== 'DEF') continue;
      const totalPa = stats.dst_pts_allowed;
      if(totalPa == null || !games) continue;
      const perGame = totalPa / games;
      const tier = PP_SCORING_PA_TIERS.find(([lo, hi]) => perGame >= lo && perGame <= hi);
      if(tier && tier[2] === key) points += coef * games;
      continue;
    }
    if(ppScoringIsThresholdBonus(key)){
      warnings.push(`Threshold bonus ${key} (coefficient ${coef}) not modeled in Phase 2; affected players slightly under-credited in this league.`);
      continue;
    }
    if(PP_SCORING_UNSUPPORTED[key]){
      warnings.push(PP_SCORING_UNSUPPORTED[key]);
      continue;
    }
    if(/^idp_/.test(key)){
      warnings.push(`IDP scoring (${key}, coefficient ${coef}) not supported in Phase 2 — IDP scrape arrives in Phase 4. Affected players score 0 here.`);
      continue;
    }
    warnings.push(`Unrecognized Sleeper scoring key "${key}" (coefficient ${coef}); not modeled in Phase 2.`);
  }
  return { points, warnings };
}

// Phase 2 exports — defined here (not in the line-11806 PFK_PP_* block) because
// the consts above are declared after that block and would be in TDZ.
if(typeof window !== 'undefined'){
  window.PFK_PP_APPLY_LEAGUE_SCORING = ppApplyLeagueScoring;
  window.PFK_PP_SCORING_DIRECT_MAP = PP_SCORING_DIRECT_MAP;
  window.PFK_PP_SCORING_POSITION_GATED = PP_SCORING_POSITION_GATED;
  window.PFK_PP_SCORING_DERIVED = PP_SCORING_DERIVED;
  window.PFK_PP_SCORING_PA_TIERS = PP_SCORING_PA_TIERS;
  window.PFK_PP_SCORING_UNSUPPORTED = PP_SCORING_UNSUPPORTED;
  window.PFK_PP_SCORING_TWO_PT_GATE = PP_SCORING_TWO_PT_GATE;
  window.PFK_PP_SCORING_IS_THRESHOLD_BONUS = ppScoringIsThresholdBonus;
}

function AdminProjectionsInner(){
  const [rows, setRows] = useState(null);             // null = loading, [] = loaded empty
  const [latestMeta, setLatestMeta] = useState(null); // {season, period, generatedAt}
  const [unmatchedCount, setUnmatchedCount] = useState(0);
  const [errMsg, setErrMsg] = useState('');

  // Table state
  const [posFilter,   setPosFilter]   = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey,     setSortKey]     = useState('name');
  const [sortDir,     setSortDir]     = useState('asc');
  const [page,        setPage]        = useState(0);

  // Upload state — set as the user moves through file pick → parse → confirm.
  // parseResult shape: { mapped:[], unmatched:[], errors:[], tierBreakdown:{}, sourceFileName:'' }
  const [selectedFile,  setSelectedFile]   = useState(null);
  const [parseResult,   setParseResult]    = useState(null);
  const [uploading,     setUploading]      = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [uploadStatus,  setUploadStatus]   = useState(null);   // {ok:bool, msg:string}|null

  // Click-to-expand row state. Stores the player_projections.id of the currently
  // expanded row, or null. Only one row expanded at a time to keep the table
  // visually scannable; click the same row again to collapse.
  const [expandedRowId, setExpandedRowId] = useState(null);

  // Diagnostic panel — full list of rows from player_projections_unmatched
  // (the "current scrape's mapping gap") plus local search/filter state.
  const [unmatchedRows,    setUnmatchedRows]    = useState([]);
  const [unmatchedSearch,  setUnmatchedSearch]  = useState('');
  const [unmatchedPosFltr, setUnmatchedPosFltr] = useState('ALL');

  const PAGE_SIZE = 50;

  // Fetch the freshest (season, period) group from player_projections + the
  // total count from player_projections_unmatched. Callable on demand so the
  // upload flow can re-trigger it after a successful write.
  const fetchProjections = async () => {
    if(!sb){ setErrMsg('Supabase not loaded'); setRows([]); return; }
    setErrMsg('');
    const { data: latestArr, error: latestErr } = await sb
      .from('player_projections')
      .select('season, period, generated_at')
      .order('generated_at', { ascending: false })
      .limit(1);
    if(latestErr){ setErrMsg('Fetch error: ' + (latestErr.message || latestErr)); setRows([]); return; }

    // Unmatched rows — full fetch for the diagnostic panel + use the array
    // length as the count for the metadata strip. Single query instead of a
    // separate head count.
    const { data: uRows, error: uErr } = await sb
      .from('player_projections_unmatched')
      .select('*')
      .order('ffa_position', { ascending: true })
      .order('ffa_name', { ascending: true });
    if(!uErr){
      setUnmatchedRows(uRows || []);
      setUnmatchedCount((uRows || []).length);
    }

    if(!latestArr || !latestArr.length){ setRows([]); setLatestMeta(null); return; }
    const latest = latestArr[0];
    const { data: groupRows, error: rowsErr } = await sb
      .from('player_projections')
      .select('*')
      .eq('season', latest.season)
      .eq('period', latest.period);
    if(rowsErr){ setErrMsg('Fetch error: ' + (rowsErr.message || rowsErr)); setRows([]); return; }
    setRows(groupRows || []);
    setLatestMeta({
      season:      latest.season,
      period:      latest.period,
      generatedAt: latest.generated_at,
    });
  };

  useEffect(() => { fetchProjections(); }, []);

  // ----- Upload handlers ----------------------------------------------------
  // File picker → immediate parse + preview. On success, parseResult populates
  // and the upload card shows the breakdown + Confirm button. On error, the
  // file is kept selected but errors render inline so the user can fix.
  const onFileSelected = async (file) => {
    if(!file){ return; }
    setUploadStatus(null);
    setSelectedFile(file);
    setParseResult(null);
    if(!window.Papa){
      setParseResult({ rows: [], mapped: [], unmatched: [], errors: ['PapaParse not loaded — refresh the page'], tierBreakdown: {}, sourceFileName: file.name });
      return;
    }
    // Get session to populate updated_by on each row at parse time so the
    // confirm step doesn't need to re-fetch.
    const { data: sessData } = await sb.auth.getSession();
    const updatedBy = sessData?.session?.user?.id;
    if(!updatedBy){
      setParseResult({ rows: [], mapped: [], unmatched: [], errors: ['Sign in required — admin session expired'], tierBreakdown: {}, sourceFileName: file.name });
      return;
    }
    window.Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: h => h.trim().toLowerCase(),
      complete: (results) => {
        const rows = results.data || [];
        const errors = [];
        if(!rows.length){
          errors.push('No rows parsed from file');
        }
        const REQUIRED = ['ffa_name','position','season','period','source_count','map_confidence','map_method','generated_at'];
        const first = rows[0] || {};
        const missing = REQUIRED.filter(c => !(c in first));
        if(missing.length){
          errors.push('Missing required columns: ' + missing.join(', '));
        }
        if(errors.length){
          setParseResult({ rows, mapped: [], unmatched: [], errors, tierBreakdown: {}, sourceFileName: file.name });
          return;
        }
        const nowIso = new Date().toISOString();
        const mapped = [], unmatched = [], tier = {};
        for(const r of rows){
          const meth = r.map_method || 'unknown';
          tier[meth] = (tier[meth] || 0) + 1;
          const payloads = ppProjRowToPayloads(r, updatedBy, nowIso);
          if(payloads.mapped) mapped.push(payloads.mapped);
          else if(payloads.unmatched) unmatched.push(payloads.unmatched);
        }
        setParseResult({ rows, mapped, unmatched, errors: [], tierBreakdown: tier, sourceFileName: file.name });
      },
      error: (err) => {
        setParseResult({ rows: [], mapped: [], unmatched: [], errors: ['Parse error: ' + (err.message || err)], tierBreakdown: {}, sourceFileName: file.name });
      },
    });
  };

  const onCancelUpload = () => {
    setSelectedFile(null);
    setParseResult(null);
    setUploadStatus(null);
    setUploadProgress('');
  };

  // Confirm → execute: upsert mapped → DELETE unmatched table → INSERT
  // unmatched. Sequential, non-atomic per the Phase 1 design (recoverable
  // via re-upload). Chunked at 100 rows. See migration runbook design notes
  // for the additive-then-prune alternative if we ever need stronger safety.
  //
  // On failure: captures the raw supabase-js error object (message, code,
  // details, hint, status) along with the phase + chunk + row-range context.
  // Surfaces all of it in the toast AND logs to console — diagnosis-grade
  // signal, not just "something went wrong."
  const onConfirmUpload = async () => {
    if(!parseResult || parseResult.errors.length || uploading) return;
    setUploading(true);
    setUploadStatus(null);
    const { mapped, unmatched } = parseResult;
    const CHUNK = 100;
    console.info('[pp-upload] start', { mappedRows: mapped.length, unmatchedRows: unmatched.length, chunkSize: CHUNK });
    try {
      // 1. Mapped: upsert in chunks
      for(let i = 0; i < mapped.length; i += CHUNK){
        const slice = mapped.slice(i, i + CHUNK);
        const chunkNum = (i / CHUNK) + 1;
        const totalChunks = Math.ceil(mapped.length / CHUNK);
        const rowStart = i;
        const rowEnd = i + slice.length - 1;
        console.info('[pp-upload] mapped chunk', chunkNum, 'of', totalChunks, '— rows', rowStart, '-', rowEnd);
        setUploadProgress(`Uploading mapped rows… ${Math.min(i + CHUNK, mapped.length)} / ${mapped.length}`);
        const t0 = performance.now();
        const { error } = await sb.from('player_projections')
          .upsert(slice, { onConflict: 'sleeper_player_id,season,period' });
        const ms = Math.round(performance.now() - t0);
        if(error){
          console.error('[pp-upload] mapped chunk', chunkNum, 'FAILED after', ms, 'ms — raw error:', error);
          throw { phase: 'mapped-upsert', chunkNum, totalChunks, rowStart, rowEnd, durationMs: ms, rawError: error };
        }
        console.info('[pp-upload] mapped chunk', chunkNum, 'OK in', ms, 'ms');
      }
      // 2. Unmatched: truncate-then-insert (per Phase 1 design)
      console.info('[pp-upload] truncating player_projections_unmatched');
      setUploadProgress('Clearing previous unmatched rows…');
      const { error: delErr } = await sb.from('player_projections_unmatched')
        .delete().neq('id', -1);
      if(delErr){
        console.error('[pp-upload] truncate FAILED — raw error:', delErr);
        throw { phase: 'unmatched-truncate', rawError: delErr };
      }
      // 3. Unmatched: insert new
      for(let i = 0; i < unmatched.length; i += CHUNK){
        const slice = unmatched.slice(i, i + CHUNK);
        const chunkNum = (i / CHUNK) + 1;
        const totalChunks = Math.ceil(unmatched.length / CHUNK);
        const rowStart = i;
        const rowEnd = i + slice.length - 1;
        console.info('[pp-upload] unmatched chunk', chunkNum, 'of', totalChunks, '— rows', rowStart, '-', rowEnd);
        setUploadProgress(`Uploading unmatched rows… ${Math.min(i + CHUNK, unmatched.length)} / ${unmatched.length}`);
        const t0 = performance.now();
        const { error } = await sb.from('player_projections_unmatched').insert(slice);
        const ms = Math.round(performance.now() - t0);
        if(error){
          console.error('[pp-upload] unmatched chunk', chunkNum, 'FAILED after', ms, 'ms — raw error:', error);
          throw { phase: 'unmatched-insert', chunkNum, totalChunks, rowStart, rowEnd, durationMs: ms, rawError: error };
        }
        console.info('[pp-upload] unmatched chunk', chunkNum, 'OK in', ms, 'ms');
      }
      console.info('[pp-upload] complete', { mapped: mapped.length, unmatched: unmatched.length });
      setUploadStatus({ ok: true, msg: `Uploaded ${mapped.length} mapped + ${unmatched.length} unmatched rows.` });
      setSelectedFile(null);
      setParseResult(null);
      setUploadProgress('');
      await fetchProjections();
    } catch(e){
      // Capture full context. supabase-js errors expose message/code/details/
      // hint/status — we keep them all in rawError so the toast renders the
      // verbatim diagnosis surface.
      const status = { ok: false };
      if(e && typeof e === 'object' && 'phase' in e){
        status.phase       = e.phase;
        status.chunkNum    = e.chunkNum;
        status.totalChunks = e.totalChunks;
        status.rowStart    = e.rowStart;
        status.rowEnd      = e.rowEnd;
        status.durationMs  = e.durationMs;
        status.rawError    = e.rawError;
      } else {
        // Non-supabase exception (parse bug, network throw, etc.)
        status.phase    = 'unknown';
        status.rawError = { message: String((e && e.message) || e) };
      }
      console.error('[pp-upload] aborted —', status);
      setUploadStatus(status);
      setUploadProgress('');
    } finally {
      setUploading(false);
    }
  };

  // Header click → toggle dir on same key, switch key + reset to asc otherwise.
  const onSort = (k) => {
    if(sortKey === k) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir('asc'); }
    setPage(0);
  };
  const sortArrow = (k) => sortKey === k ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  // Source-count histogram over ALL loaded rows (unaffected by the search /
  // position filter so the strip stays stable). Mapped count == rows.length
  // since every row in player_projections has a sleeper_player_id by schema
  // (NOT NULL); unmatched count lives in unmatchedCount state from a separate
  // head query on player_projections_unmatched.
  const aggregates = useMemo(() => {
    if(!Array.isArray(rows)) return null;
    const sourceHist = {};
    rows.forEach(r => {
      sourceHist[r.source_count] = (sourceHist[r.source_count] || 0) + 1;
    });
    return { sourceHist, total: rows.length };
  }, [rows]);

  // Filter + sort + paginate.
  const visible = useMemo(() => {
    if(!Array.isArray(rows)) return { items: [], total: 0, totalPages: 1, clampedPage: 0 };
    let f = rows;
    if(posFilter !== 'ALL') f = f.filter(r => r.position === posFilter);
    if(searchQuery.trim()){
      const q = searchQuery.trim().toLowerCase();
      f = f.filter(r => (r.ffa_name || '').toLowerCase().includes(q));
    }
    f = [...f].sort((a, b) => {
      const isString = (sortKey === 'name' || sortKey === 'position' || sortKey === 'team' || sortKey === 'map_method');
      if(isString){
        const av = (a[sortKey === 'name' ? 'ffa_name' : sortKey] || '') + '';
        const bv = (b[sortKey === 'name' ? 'ffa_name' : sortKey] || '') + '';
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      let av, bv;
      if(sortKey.startsWith('stat:')){
        const k = sortKey.slice(5);
        av = parseFloat((a.projected_stats||{})[k]) || 0;
        bv = parseFloat((b.projected_stats||{})[k]) || 0;
      } else {
        av = parseFloat(a[sortKey]) || 0;
        bv = parseFloat(b[sortKey]) || 0;
      }
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    const total = f.length;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const clampedPage = Math.min(page, totalPages - 1);
    const items = f.slice(clampedPage * PAGE_SIZE, (clampedPage + 1) * PAGE_SIZE);
    return { items, total, totalPages, clampedPage };
  }, [rows, posFilter, searchQuery, sortKey, sortDir, page]);

  const SORT_COLS = [
    { k:'name',             label:'Name',     align:'left'  },
    { k:'position',         label:'Pos',      align:'left'  },
    { k:'team',             label:'Team',     align:'left'  },
    { k:'stat:games',       label:'G',        align:'right' },
    { k:'stat:pass_yds',    label:'Pass Yds', align:'right' },
    { k:'stat:pass_att',    label:'Pass Att', align:'right' },
    { k:'stat:pass_tds',    label:'Pass TD',  align:'right' },
    { k:'stat:rush_yds',    label:'Rush Yds', align:'right' },
    { k:'stat:rush_att',    label:'Carries',  align:'right' },
    { k:'stat:rush_tds',    label:'Rush TD',  align:'right' },
    { k:'stat:rec',         label:'Rec',      align:'right' },
    { k:'stat:rec_tgt',     label:'Targets',  align:'right' },
    { k:'stat:rec_yds',     label:'Rec Yds',  align:'right' },
    { k:'stat:rec_tds',     label:'Rec TD',   align:'right' },
    { k:'source_count',     label:'Src',      align:'right' },
    { k:'map_confidence',   label:'Conf',     align:'right' },
  ];

  return (
    <div data-pp-projections-tab="1">
      {errMsg && (
        <div style={{background:'var(--bg-card)', border:'1px solid var(--danger)', borderRadius:8, padding:12, marginBottom:14, color:'var(--danger)', fontSize:12}} data-pp-err="1">
          {errMsg}
        </div>
      )}

      {/* Upload card — always visible at top. Three states:
          (a) idle: file picker; (b) parsed: preview + Confirm/Cancel;
          (c) uploading: progress msg. Post-success status persists until next
          file pick. */}
      <div style={{background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:12, padding:14, marginBottom:14}} data-pp-upload-card="1">
        <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:10, flexWrap:'wrap'}}>
          <div style={{fontSize:13, fontWeight:800, color:'var(--text-bright)', letterSpacing:0.5}}>📤 Upload projections CSV</div>
          {!parseResult && !uploading && (
            <div style={{fontSize:11, color:'var(--text-muted)'}}>Generated by <code style={{color:'var(--accent-gold-text)'}}>~/pfk_projections/scrape.R</code></div>
          )}
        </div>

        {!parseResult && !uploading && (
          <input type="file" accept=".csv,text/csv"
                 onChange={e => onFileSelected(e.target.files?.[0])}
                 data-pp-upload-input="1"
                 style={{padding:'8px', minHeight:40, background:'var(--bg-input)', border:'1px solid var(--border-strong)', borderRadius:6, color:'var(--text-primary)', fontSize:12, cursor:'pointer', width:'100%', maxWidth:480}}/>
        )}

        {/* Status toast — ALWAYS renders when set, regardless of parseResult
            or uploading state. On failure, surfaces the verbatim supabase-js
            error (message/code/details/hint/status) plus phase + chunk + row
            range so the operator can diagnose without opening the console.
            Persists until dismissed or until the next upload starts. */}
        {uploadStatus && (
          <div data-pp-upload-status="1"
               data-pp-upload-status-ok={uploadStatus.ok ? '1' : '0'}
               style={{marginTop:10, padding:'12px 14px', background:'var(--bg-pill)', border:`1px solid ${uploadStatus.ok ? 'var(--success)' : 'var(--danger)'}`, borderRadius:8, color: uploadStatus.ok ? 'var(--success)' : 'var(--danger)', fontSize:12}}>
            <div style={{fontWeight:800}}>
              {uploadStatus.ok
                ? '✓ ' + uploadStatus.msg
                : '✗ Upload failed' +
                  (uploadStatus.phase ? ` (${uploadStatus.phase}` : '') +
                  (uploadStatus.chunkNum ? ` chunk ${uploadStatus.chunkNum}/${uploadStatus.totalChunks}` : '') +
                  (uploadStatus.rowStart != null ? `, rows ${uploadStatus.rowStart}–${uploadStatus.rowEnd}` : '') +
                  (uploadStatus.durationMs != null ? `, ${uploadStatus.durationMs}ms` : '') +
                  (uploadStatus.phase ? ')' : '')
              }
            </div>
            {!uploadStatus.ok && uploadStatus.rawError && (
              <div style={{marginTop:8}} data-pp-upload-status-detail="1">
                <div style={{fontSize:11, color:'var(--text-bright)', fontWeight:700, marginBottom:4}}>
                  Raw error from supabase-js:
                </div>
                <pre style={{margin:0, padding:'8px 10px', background:'var(--bg-input)', border:'1px solid var(--border-hairline)', borderRadius:6, color:'var(--text-bright)', fontSize:11, fontFamily:'monospace', overflowX:'auto', whiteSpace:'pre-wrap', wordBreak:'break-word'}}>{JSON.stringify(uploadStatus.rawError, null, 2)}</pre>
                <div style={{marginTop:6, fontSize:11, color:'var(--text-muted)', lineHeight:1.5}}>
                  Persists until you dismiss or start a new upload. Copy the JSON above into chat to diagnose. The same payload is also in <code style={{color:'var(--accent-gold-text)'}}>console.error('[pp-upload] … FAILED …')</code> if you want to screenshot it.
                </div>
                <button onClick={() => setUploadStatus(null)}
                        data-pp-upload-status-dismiss="1"
                        style={{marginTop:10, padding:'6px 12px', background:'transparent', border:'1px solid var(--border-outline)', borderRadius:6, color:'var(--text-secondary)', fontSize:11, fontWeight:700, cursor:'pointer'}}>
                  Dismiss
                </button>
              </div>
            )}
          </div>
        )}

        {parseResult && (
          <div style={{marginTop:6, padding:'10px 12px', background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:8}} data-pp-upload-preview="1">
            <div style={{fontSize:12, color:'var(--text-secondary)', marginBottom:6}}>
              <strong style={{color:'var(--text-bright)'}}>{parseResult.sourceFileName}</strong> — {parseResult.rows.length} row{parseResult.rows.length === 1 ? '' : 's'} · <span style={{color:'var(--success)'}}>{parseResult.mapped.length} mapped</span> · <span style={{color:'var(--warning)'}}>{parseResult.unmatched.length} unmatched</span>
            </div>

            {Object.keys(parseResult.tierBreakdown).length > 0 && (
              <div style={{fontSize:11, color:'var(--text-muted)', marginBottom:8}} data-pp-upload-tiers="1">
                Tier breakdown: {Object.entries(parseResult.tierBreakdown).map(([k,v]) => `${k}=${v}`).join(' · ')}
              </div>
            )}

            {parseResult.errors.length > 0 && (
              <div style={{padding:'8px 10px', background:'rgba(255,80,80,0.08)', border:'1px solid var(--danger)', borderRadius:6, color:'var(--danger)', fontSize:11, marginBottom:8}} data-pp-upload-errors="1">
                {parseResult.errors.map((e, i) => <div key={i}>• {e}</div>)}
              </div>
            )}

            {!uploading && (
              <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
                {parseResult.errors.length === 0 && (
                  <button onClick={onConfirmUpload}
                          data-pp-upload-confirm="1"
                          style={{padding:'8px 14px', minHeight:40, background:'var(--accent-gold)', color:'#000', border:'none', borderRadius:6, fontWeight:900, fontSize:12, cursor:'pointer', letterSpacing:0.5}}>
                    CONFIRM UPLOAD
                  </button>
                )}
                <button onClick={onCancelUpload}
                        data-pp-upload-cancel="1"
                        style={{padding:'8px 14px', minHeight:40, background:'transparent', border:'1px solid var(--border-outline)', borderRadius:6, color:'var(--text-secondary)', fontWeight:700, fontSize:12, cursor:'pointer'}}>
                  {parseResult.errors.length > 0 ? 'Choose another file' : 'Cancel'}
                </button>
              </div>
            )}

            {uploading && (
              <div style={{fontSize:12, color:'var(--text-secondary)', display:'flex', alignItems:'center', gap:8}} data-pp-upload-progress="1">
                <span>⏳</span> {uploadProgress || 'Working…'}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Body — loading vs empty vs populated, all conditional inline */}
      {rows === null && (
        <div style={{padding:40, textAlign:'center', color:'var(--text-secondary)'}} data-pp-loading="1">
          Loading projections…
        </div>
      )}

      {rows !== null && !rows.length && !errMsg && (
        <div style={{background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:12, padding:24, textAlign:'center', color:'var(--text-secondary)'}} data-pp-empty="1">
          <div style={{fontSize:14, marginBottom:8, color:'var(--text-bright)', fontWeight:800}}>No projections yet</div>
          <div style={{fontSize:12, lineHeight:1.6}}>
            Upload a CSV above to populate this view. Generate the CSV by running <code style={{color:'var(--accent-gold-text)'}}>Rscript ~/pfk_projections/scrape.R</code>.
          </div>
        </div>
      )}

      {/* Last-upload metadata strip */}
      {Array.isArray(rows) && rows.length > 0 && latestMeta && aggregates && (
        <div style={{background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:12, padding:16, marginBottom:14}} data-pp-meta="1">
          <div style={{display:'flex', alignItems:'flex-start', flexWrap:'wrap', gap:18, fontSize:13}}>
            <div style={{flex:'1 1 180px', minWidth:160}}>
              <div style={{fontSize:11, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:1, marginBottom:4}}>Last upload</div>
              <div style={{color:'var(--text-bright)', fontWeight:700}} data-pp-meta-season>
                {latestMeta.season} · {latestMeta.period}
              </div>
              <div style={{color:'var(--text-secondary)', fontSize:11, marginTop:2}} data-pp-meta-generated>
                Generated {latestMeta.generatedAt ? new Date(latestMeta.generatedAt).toLocaleString() : '—'}
              </div>
            </div>
            <div style={{flex:'1 1 160px'}}>
              <div style={{fontSize:11, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:1, marginBottom:4}}>Rows</div>
              <div style={{color:'var(--text-bright)', fontWeight:700}} data-pp-meta-total>{aggregates.total}</div>
              <div style={{fontSize:11, marginTop:2}}>
                <span style={{color:'var(--success)'}} data-pp-meta-mapped>{rows.length} mapped</span>
                <span style={{color:'var(--text-muted)'}}> · </span>
                <span style={{color: unmatchedCount > 0 ? 'var(--warning)' : 'var(--text-muted)'}} data-pp-meta-unmatched>{unmatchedCount} unmatched</span>
              </div>
            </div>
            <div style={{flex:'2 1 260px'}}>
              <div style={{fontSize:11, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:1, marginBottom:4}}>Source-count histogram</div>
              <div style={{display:'flex', gap:6, flexWrap:'wrap', fontSize:11}} data-pp-source-hist="1">
                {Object.keys(aggregates.sourceHist).sort((a, b) => parseInt(a, 10) - parseInt(b, 10)).map(k => (
                  <div key={k} data-pp-source-bucket={k}
                       style={{padding:'4px 10px', background:'var(--bg-pill)', border:'1px solid var(--border-hairline)', borderRadius:14, color:'var(--text-secondary)'}}>
                    <strong style={{color:'var(--text-bright)'}}>{k} src:</strong> {aggregates.sourceHist[k]}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filter + search bar — only when there's data to filter */}
      {Array.isArray(rows) && rows.length > 0 && (
      <div style={{background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:12, padding:12, marginBottom:14, display:'flex', gap:10, flexWrap:'wrap', alignItems:'center'}}>
        <input
          value={searchQuery}
          onChange={e => { setSearchQuery(e.target.value); setPage(0); }}
          placeholder="Search by name…"
          data-pp-search="1"
          style={{flex:'2 1 200px', minWidth:160, padding:'8px 10px', minHeight:40, background:'var(--bg-input)', border:'1px solid var(--border-strong)', borderRadius:6, color:'var(--text-bright)', fontSize:13}}
        />
        <select
          value={posFilter}
          onChange={e => { setPosFilter(e.target.value); setPage(0); }}
          data-pp-pos-filter="1"
          style={{padding:'8px 10px', minHeight:40, background:'var(--bg-input)', border:'1px solid var(--border-strong)', borderRadius:6, color:'var(--text-bright)', fontSize:13, fontWeight:700}}
        >
          {['ALL','QB','RB','WR','TE','FB','K','DEF'].map(p => (
            <option key={p} value={p}>{p === 'ALL' ? 'All positions' : p}</option>
          ))}
        </select>
        <div style={{flex:'1 1 120px', textAlign:'right', fontSize:12, color:'var(--text-secondary)'}}>
          Showing {visible.items.length} of {visible.total} row{visible.total === 1 ? '' : 's'}
        </div>
      </div>
      )}

      {/* Projections table — only when there's data */}
      {Array.isArray(rows) && rows.length > 0 && (
      <div style={{background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:12, padding:0, overflowX:'auto'}} data-pp-table-wrapper="1">
        <table style={{width:'100%', borderCollapse:'collapse', fontSize:12, color:'var(--text-primary)'}}>
          <thead>
            <tr style={{background:'var(--bg-elevated)', borderBottom:'1px solid var(--border)'}}>
              {SORT_COLS.map(col => (
                <th key={col.k}
                    onClick={() => onSort(col.k)}
                    data-pp-col={col.k}
                    style={{padding:'10px 10px', minHeight:40, textAlign:col.align, cursor:'pointer', userSelect:'none', fontSize:11, fontWeight:800, color:'var(--text-bright)', textTransform:'uppercase', letterSpacing:0.5, whiteSpace:'nowrap'}}>
                  {col.label}{sortArrow(col.k)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.items.flatMap(r => {
              const lowConf = !r.sleeper_player_id || (r.map_confidence ?? 0) < 80;
              const isExpanded = expandedRowId === r.id;
              const dataRow = (
                <tr key={r.id} data-pp-row={r.id} data-pp-row-expanded={isExpanded ? '1' : '0'}
                    onClick={() => setExpandedRowId(isExpanded ? null : r.id)}
                    style={{borderTop:'1px solid var(--border-hairline)', background: lowConf ? 'rgba(255,80,80,0.04)' : 'transparent', cursor:'pointer'}}>
                  <td style={{padding:'10px'}}>
                    <span style={{color:'var(--text-muted)', marginRight:6, display:'inline-block', width:10, fontSize:10}}>{isExpanded ? '▾' : '▸'}</span>
                    {r.ffa_name}
                  </td>
                  <td style={{padding:'10px', color:'var(--text-muted)', fontSize:11}}>{r.position}</td>
                  <td style={{padding:'10px', color:'var(--text-muted)', fontSize:11}}>{r.team || '—'}</td>
                  <td style={{padding:'10px', textAlign:'right', fontVariantNumeric:'tabular-nums'}}>{ppProjFormatStat(r.projected_stats, 'games')}</td>
                  <td style={{padding:'10px', textAlign:'right', fontVariantNumeric:'tabular-nums'}}>{ppProjFormatStat(r.projected_stats, 'pass_yds')}</td>
                  <td style={{padding:'10px', textAlign:'right', fontVariantNumeric:'tabular-nums'}}>{ppProjFormatStat(r.projected_stats, 'pass_att')}</td>
                  <td style={{padding:'10px', textAlign:'right', fontVariantNumeric:'tabular-nums'}}>{ppProjFormatStat(r.projected_stats, 'pass_tds')}</td>
                  <td style={{padding:'10px', textAlign:'right', fontVariantNumeric:'tabular-nums'}}>{ppProjFormatStat(r.projected_stats, 'rush_yds')}</td>
                  <td style={{padding:'10px', textAlign:'right', fontVariantNumeric:'tabular-nums'}}>{ppProjFormatStat(r.projected_stats, 'rush_att')}</td>
                  <td style={{padding:'10px', textAlign:'right', fontVariantNumeric:'tabular-nums'}}>{ppProjFormatStat(r.projected_stats, 'rush_tds')}</td>
                  <td style={{padding:'10px', textAlign:'right', fontVariantNumeric:'tabular-nums'}}>{ppProjFormatStat(r.projected_stats, 'rec')}</td>
                  <td style={{padding:'10px', textAlign:'right', fontVariantNumeric:'tabular-nums'}}>{ppProjFormatStat(r.projected_stats, 'rec_tgt')}</td>
                  <td style={{padding:'10px', textAlign:'right', fontVariantNumeric:'tabular-nums'}}>{ppProjFormatStat(r.projected_stats, 'rec_yds')}</td>
                  <td style={{padding:'10px', textAlign:'right', fontVariantNumeric:'tabular-nums'}}>{ppProjFormatStat(r.projected_stats, 'rec_tds')}</td>
                  <td style={{padding:'10px', textAlign:'right', color:'var(--text-secondary)'}}>{r.source_count ?? 0}</td>
                  <td style={{padding:'10px', textAlign:'right', color: lowConf ? 'var(--warning)' : 'var(--success)', fontWeight:700}}>{r.map_confidence ?? '—'}</td>
                </tr>
              );
              if(!isExpanded) return [dataRow];
              // Expanded panel: full projected_stats JSONB pretty-printed +
              // mapping metadata. Two-column grid for stats so the table
              // expansion stays visually compact.
              const statEntries = Object.entries(r.projected_stats || {})
                .filter(([, v]) => v != null && v !== 0)
                .sort((a, b) => a[0].localeCompare(b[0]));
              const expandedRow = (
                <tr key={r.id + '_expanded'} data-pp-row-detail={r.id}
                    style={{background:'var(--bg-elevated)', borderTop:'1px solid var(--border-hairline)'}}>
                  <td colSpan={SORT_COLS.length} style={{padding:'14px 18px'}}>
                    <div style={{display:'grid', gridTemplateColumns:'minmax(0,1fr) minmax(0,1fr)', gap:18, fontSize:12}}>
                      <div>
                        <div style={{fontSize:10, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:1, marginBottom:6, fontWeight:800}}>
                          Projected stats ({statEntries.length} non-zero)
                        </div>
                        {statEntries.length === 0 ? (
                          <div style={{color:'var(--text-muted)', fontStyle:'italic'}}>all-zero (typical for FA / deep depth)</div>
                        ) : (
                          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(150px, 1fr))', gap:'4px 12px', fontVariantNumeric:'tabular-nums'}}>
                            {statEntries.map(([k, v]) => (
                              <div key={k} style={{display:'flex', justifyContent:'space-between', gap:8, color:'var(--text-secondary)'}}>
                                <span style={{color:'var(--text-muted)'}}>{k}</span>
                                <span style={{color:'var(--text-bright)'}}>{typeof v === 'number' && v % 1 !== 0 ? v.toFixed(1) : v}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div>
                        <div style={{fontSize:10, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:1, marginBottom:6, fontWeight:800}}>
                          Mapping
                        </div>
                        <div style={{display:'grid', gridTemplateColumns:'auto 1fr', gap:'4px 14px', alignItems:'baseline', color:'var(--text-secondary)'}}>
                          <span style={{color:'var(--text-muted)'}}>Sleeper ID</span>
                          <span style={{color:'var(--text-bright)', fontFamily:'monospace'}}>{r.sleeper_player_id}</span>
                          <span style={{color:'var(--text-muted)'}}>Method</span>
                          <span style={{color:'var(--text-bright)'}}>{r.map_method || '—'}</span>
                          <span style={{color:'var(--text-muted)'}}>Confidence</span>
                          <span style={{color:'var(--text-bright)'}}>{r.map_confidence ?? '—'}</span>
                          <span style={{color:'var(--text-muted)'}}>Sources</span>
                          <span style={{color:'var(--text-bright)'}}>{r.source_count ?? 0}</span>
                          <span style={{color:'var(--text-muted)'}}>Season · Period</span>
                          <span style={{color:'var(--text-bright)'}}>{r.season} · {r.period}</span>
                          <span style={{color:'var(--text-muted)'}}>Generated</span>
                          <span style={{color:'var(--text-bright)'}}>{r.generated_at ? new Date(r.generated_at).toLocaleString() : '—'}</span>
                          <span style={{color:'var(--text-muted)'}}>Updated</span>
                          <span style={{color:'var(--text-bright)'}}>{r.updated_at ? new Date(r.updated_at).toLocaleString() : '—'}</span>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              );
              return [dataRow, expandedRow];
            })}
            {!visible.items.length && (
              <tr>
                <td colSpan={SORT_COLS.length} style={{padding:24, textAlign:'center', color:'var(--text-secondary)', fontSize:12}}>
                  No rows match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      )}

      {/* Pagination — only when there's data + more than one page */}
      {Array.isArray(rows) && rows.length > 0 && visible.totalPages > 1 && (
        <div style={{display:'flex', gap:8, justifyContent:'center', alignItems:'center', marginTop:14}} data-pp-pagination="1">
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={visible.clampedPage === 0}
                  data-pp-pagination-prev="1"
                  style={{padding:'8px 14px', minHeight:40, background:'var(--bg-elevated)', border:'1px solid var(--border-outline)', borderRadius:6, color: visible.clampedPage === 0 ? 'var(--text-muted)' : 'var(--text-primary)', fontSize:12, fontWeight:700, cursor: visible.clampedPage === 0 ? 'not-allowed' : 'pointer'}}>
            ← Prev
          </button>
          <div style={{fontSize:12, color:'var(--text-secondary)', minWidth:120, textAlign:'center'}} data-pp-pagination-status>
            Page {visible.clampedPage + 1} of {visible.totalPages}
          </div>
          <button onClick={() => setPage(p => Math.min(visible.totalPages - 1, p + 1))} disabled={visible.clampedPage >= visible.totalPages - 1}
                  data-pp-pagination-next="1"
                  style={{padding:'8px 14px', minHeight:40, background:'var(--bg-elevated)', border:'1px solid var(--border-outline)', borderRadius:6, color: visible.clampedPage >= visible.totalPages - 1 ? 'var(--text-muted)' : 'var(--text-primary)', fontSize:12, fontWeight:700, cursor: visible.clampedPage >= visible.totalPages - 1 ? 'not-allowed' : 'pointer'}}>
            Next →
          </button>
        </div>
      )}

      {/* Diagnostic listing — read-only inventory of player_projections_unmatched.
          Triage path is SQL into player_id_overrides (revealed via the inline
          snippet). Intentionally NOT a workflow tool — per the Phase 1 design,
          most unmatched rows are FAs / May-window rookies / injured starters
          that auto-resolve on the next scrape. */}
      <AdminProjectionsDiagnostic
        unmatchedRows={unmatchedRows}
        unmatchedSearch={unmatchedSearch}
        setUnmatchedSearch={setUnmatchedSearch}
        unmatchedPosFltr={unmatchedPosFltr}
        setUnmatchedPosFltr={setUnmatchedPosFltr}
      />
    </div>
  );
}

// =============================================================================
// AdminProjectionsDiagnostic — sub-component of AdminProjectionsInner. Renders
// the unmatched-rows listing below the main projections table. Split out so
// the parent component stays readable; no own data fetch (parent passes the
// already-loaded array). Read-only by design — the "fix" is to insert into
// player_id_overrides via SQL (snippet revealed inline).
// =============================================================================
function AdminProjectionsDiagnostic({ unmatchedRows, unmatchedSearch, setUnmatchedSearch, unmatchedPosFltr, setUnmatchedPosFltr }){
  const filtered = useMemo(() => {
    let f = unmatchedRows;
    if(unmatchedPosFltr !== 'ALL') f = f.filter(r => r.ffa_position === unmatchedPosFltr);
    if(unmatchedSearch.trim()){
      const q = unmatchedSearch.trim().toLowerCase();
      f = f.filter(r => (r.ffa_name || '').toLowerCase().includes(q));
    }
    return f;
  }, [unmatchedRows, unmatchedSearch, unmatchedPosFltr]);

  // Reason heuristic — drives the badge category and matches the transparency-
  // badge logic that lands in the Pick Projector UI (Phase 1 § Transparency
  // badge in Phase 0 doc). team='' is the Sleeper convention for free agents.
  const reasonFor = (r) => {
    const t = (r.ffa_team || '').trim();
    if(!t) return { label: 'FA', color: 'var(--accent-violet)' };
    return { label: 'Deep Depth or Injury', color: 'var(--warning)' };
  };

  return (
    <div style={{marginTop:18, background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:12, padding:16}} data-pp-diagnostic-panel="1">
      <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:10, flexWrap:'wrap'}}>
        <div style={{fontSize:13, fontWeight:800, color:'var(--text-bright)', letterSpacing:0.5}}>
          🔍 Unmatched ({unmatchedRows.length})
        </div>
        <div style={{flex:1, minWidth:200, fontSize:11, color:'var(--text-muted)', lineHeight:1.5}}>
          Players appear here when FFA's sources don't yet project them — typically free agents, May-window rookies, or injured/benched starters. Resolves automatically on the next scrape once the player appears in source output.
        </div>
      </div>

      <details style={{marginBottom:12, padding:'8px 12px', background:'var(--bg-elevated)', border:'1px solid var(--border-hairline)', borderRadius:8}} data-pp-override-howto="1">
        <summary style={{cursor:'pointer', fontSize:12, fontWeight:700, color:'var(--accent-gold-text)'}}>
          How to manually override mapping (SQL)
        </summary>
        <div style={{marginTop:8, fontSize:11, color:'var(--text-secondary)', lineHeight:1.6}}>
          For each row below that you want to lock to a specific Sleeper player_id, paste this in the <a href="https://supabase.com/dashboard/project/ymwoabgesjqrojurdxmv/sql" target="_blank" rel="noopener" style={{color:'var(--accent-gold-text)'}}>Supabase SQL editor</a> with the actual values filled in:
        </div>
        <pre style={{marginTop:8, padding:'10px 12px', background:'var(--bg-input)', border:'1px solid var(--border-hairline)', borderRadius:6, color:'var(--text-bright)', fontSize:11, fontFamily:'monospace', overflowX:'auto', whiteSpace:'pre-wrap'}}>{`-- Replace <sleeper_player_id> with the target ID from sleeper.app/players
insert into player_id_overrides
  (ffa_name_normalized, ffa_position, ffa_team, sleeper_player_id, reason, updated_by)
values
  ('<ffa_name_normalized from row below>',
   '<QB|RB|WR|TE|FB|K|DEF>',
   '<NFL team abbrev or NULL>',
   '<sleeper_player_id>',
   '<why you are overriding>',
   'c9f6451b-9d7f-4c21-bc5d-4680b07fa089');`}</pre>
        <div style={{marginTop:8, fontSize:11, color:'var(--text-muted)', lineHeight:1.5}}>
          The next scrape's mapper checks this table first; manual overrides always win over the auto-mapping heuristic.
        </div>
      </details>

      {unmatchedRows.length === 0 ? (
        <div style={{padding:'20px 12px', textAlign:'center', color:'var(--text-secondary)', fontSize:12}} data-pp-diagnostic-empty="1">
          All FFA rows mapped — nothing in the unmatched table for the current scrape.
        </div>
      ) : (
        <>
          <div style={{display:'flex', gap:10, flexWrap:'wrap', alignItems:'center', marginBottom:10}}>
            <input
              value={unmatchedSearch}
              onChange={e => setUnmatchedSearch(e.target.value)}
              placeholder="Search unmatched by name…"
              data-pp-diagnostic-search="1"
              style={{flex:'2 1 200px', minWidth:160, padding:'8px 10px', minHeight:40, background:'var(--bg-input)', border:'1px solid var(--border-strong)', borderRadius:6, color:'var(--text-bright)', fontSize:13}}
            />
            <select
              value={unmatchedPosFltr}
              onChange={e => setUnmatchedPosFltr(e.target.value)}
              data-pp-diagnostic-pos-filter="1"
              style={{padding:'8px 10px', minHeight:40, background:'var(--bg-input)', border:'1px solid var(--border-strong)', borderRadius:6, color:'var(--text-bright)', fontSize:13, fontWeight:700}}
            >
              {['ALL','QB','RB','WR','TE','FB','K','DEF'].map(p => (
                <option key={p} value={p}>{p === 'ALL' ? 'All positions' : p}</option>
              ))}
            </select>
            <div style={{flex:'1 1 100px', textAlign:'right', fontSize:12, color:'var(--text-secondary)'}}>
              {filtered.length} of {unmatchedRows.length}
            </div>
          </div>

          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%', borderCollapse:'collapse', fontSize:12, color:'var(--text-primary)'}}>
              <thead>
                <tr style={{background:'var(--bg-elevated)', borderBottom:'1px solid var(--border)'}}>
                  {['Name','Normalized','Pos','Team','Reason','Src','Conf','Method'].map(h => (
                    <th key={h} style={{padding:'8px 10px', textAlign: h === 'Src' || h === 'Conf' ? 'right' : 'left', fontSize:10, fontWeight:800, color:'var(--text-bright)', textTransform:'uppercase', letterSpacing:0.5, whiteSpace:'nowrap'}}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => {
                  const reason = reasonFor(r);
                  return (
                    <tr key={r.id} data-pp-diagnostic-row={r.id} style={{borderTop:'1px solid var(--border-hairline)'}}>
                      <td style={{padding:'8px 10px'}}>{r.ffa_name}</td>
                      <td style={{padding:'8px 10px', fontFamily:'monospace', color:'var(--text-muted)', fontSize:11}}>{r.ffa_name_normalized}</td>
                      <td style={{padding:'8px 10px', color:'var(--text-muted)', fontSize:11}}>{r.ffa_position}</td>
                      <td style={{padding:'8px 10px', color:'var(--text-muted)', fontSize:11}}>{r.ffa_team || '—'}</td>
                      <td style={{padding:'8px 10px'}}>
                        <span style={{padding:'2px 8px', background:'var(--bg-pill)', border:`1px solid ${reason.color}`, borderRadius:10, color:reason.color, fontSize:10, fontWeight:700, whiteSpace:'nowrap'}}>
                          {reason.label}
                        </span>
                      </td>
                      <td style={{padding:'8px 10px', textAlign:'right', color:'var(--text-secondary)'}}>{r.source_count ?? 0}</td>
                      <td style={{padding:'8px 10px', textAlign:'right', color:'var(--text-muted)'}}>{r.map_confidence ?? '—'}</td>
                      <td style={{padding:'8px 10px', color:'var(--text-muted)', fontSize:11, fontFamily:'monospace'}}>{r.map_method || '—'}</td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={8} style={{padding:18, textAlign:'center', color:'var(--text-secondary)', fontSize:12}}>
                      No unmatched rows match the current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}


const path = window.location.pathname.replace(/\/$/,'');
const isAdminRoute = path === '/admin';
const isAdminUnderdogAdpRoute = path === '/admin/underdog-adp';
const isAdminRookieBoard2027Route = path === '/admin/rookie-board-2027';
const isPickProjectorRoute = path === '/pick-projector';
const dispersalMatch = path.match(/^\/dispersal(?:\/([0-9a-f-]+))?$/i);
const lookupMatch = path.match(/^\/lookup(?:\/([^/]+))?$/i);
const isTradeFinderRoute = path === '/trade-finder';
const isDeadRoute = path === '/zolty' || path === '/compare';
// Root path defaults to Sleeper Snapshot (Evan 2026-05-02 — promoted to
// home page). If the visitor has linked their Sleeper account, we auto-fill
// the page with their own snapshot instead of dropping them on the search
// form. The legacy App (Rookie Ranks / Trade Polls) still renders at root
// when a relevant query param is present — `?tab=pfk`, `?tab=polls`, etc.
// for direct nav, or `?signin=1` for cross-page sign-in links.
const _rootParams = new URLSearchParams(window.location.search);
const rootGoesToApp = path === '' && (_rootParams.has('tab') || _rootParams.has('signin'));
const rootGoesToLookup = path === '' && !rootGoesToApp;
let rootLookupIdentifier = null;
if(rootGoesToLookup){
  try{
    const linked = JSON.parse(localStorage.getItem('pfk_sleeper_user') || 'null');
    if(linked && typeof linked === 'object' && linked.username) rootLookupIdentifier = linked.username;
  }catch(e){}
}
function NotFoundPage(){
  return (
    <div style={{background:'var(--bg-page)',minHeight:'100vh',color:'var(--text-primary)',fontFamily:"'Inter','Segoe UI',sans-serif",display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
      <div style={{textAlign:'center',maxWidth:480}}>
        <div style={{fontSize:96,fontWeight:900,color:'var(--wordmark-color)',letterSpacing:6}}>404</div>
        <div style={{fontSize:18,color:'var(--text-secondary)',marginTop:8,letterSpacing:1}}>PAGE NOT FOUND</div>
        <a href="/" style={{display:'inline-block',marginTop:24,padding:'10px 24px',background:'var(--accent-gold)',color:'#000',textDecoration:'none',borderRadius:8,fontWeight:900,letterSpacing:1.5,fontSize:14}}>Back to Play For Keeps</a>
      </div>
    </div>
  );
}
ReactDOM.render(
  isAdminUnderdogAdpRoute ? <AdminUnderdogAdp/>
  : isAdminRookieBoard2027Route ? <AdminRookieBoard2027/>
  : isPickProjectorRoute ? <PickProjectorApp/>
  : isAdminRoute ? <AdminApp/>
  : dispersalMatch ? <DispersalApp draftId={dispersalMatch[1] || null}/>
  : lookupMatch ? <LookupApp identifier={lookupMatch[1] ? decodeURIComponent(lookupMatch[1]) : null}/>
  : isTradeFinderRoute ? <TradeFinderApp/>
  : rootGoesToLookup ? <LookupApp identifier={rootLookupIdentifier}/>
  : isDeadRoute ? <NotFoundPage/>
  : <App/>,
  document.getElementById("root")
);
